import React, { useState, useEffect, useRef, memo, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import googleAdsLogo from './logos/google-ads.png';
import microsoftAdsLogo from './logos/microsoft-ads.png';
import metaAdsLogo from './logos/meta-ads.png';
import pinterestAdsLogo from './logos/pinterest-ads.png';
import tiktokAdsLogo from './logos/tiktok-ads.png';
import pianoLogo from './logos/piano-analytics.png';
import chatgptAdsLogo from './logos/chatgpt-ads.png';
import awinLogo from './logos/awin.png';
import prestashopLogo from './logos/prestashop.png';
import PrestashopDashboardView from './components/PrestashopDashboardView';
import { CLIENT_TOOLS_CATALOGUE, outilsDuCatalogue } from './constants/clientTools';
import { CATEGORIES_OUTILS, grouperParSousCategorie } from './constants/toolCategories';
import { compteAutorise, ensembleAutorise, identitesDuCompte } from './utils/accountMatching';
import { libelleRefusConnecteur } from './utils/libelleRefusConnecteur';
import { messageErreur } from './utils/messageErreur';
import { useBulleAuSurvol } from './components/BulleSurvol';
import { normaliserSitesDeMesure, fabriquerResolveurDeRattachement } from './utils/sitesDeMesure';
import matomoLogo from './logos/matomo.png';
import { buildAccountName } from './utils/accountNaming';
import { adresseDeVignette } from './utils/adresseDeVignette';
import googleAnalyticsLogo from './logos/google-analytics.svg';
import claudeLogo from './logos/claude.svg';
import searchConsoleLogo from './logos/google-search-console.png';
import tagManagerLogo from './logos/google-tag-manager.png';
import merchantLogo from './logos/google-merchant.png';
import axios from 'axios';
import html2pdf from 'html2pdf.js';
import {
  LogOut, Link, Brain, AlertCircle, Loader, X,
  History, Search, FileText, Eye, EyeOff,
  Trash2, Database, Calendar, CheckCircle, Users,
  BarChart3, BarChart2, Bot, Wrench, ShoppingCart, Download, StickyNote, Folder, ChevronRight, ChevronLeft, PanelLeftClose, PanelLeft, Globe,
  Sparkles, Tag, TrendingUp, TrendingDown, Minus, Smartphone, Activity, Clock, LayoutDashboard, ArrowLeft, Check, MessageSquare,
  Cpu, Copy, ExternalLink, Monitor, Settings, Plug, Package, Building2, Bell, Building, UserX,
  AlertTriangle, Compass, Key, RefreshCw, Zap, Youtube, Target, GitCompareArrows, ChevronDown, ChevronUp, ChevronsUpDown, CreditCard, HelpCircle, MapPin, Wallet, ClipboardList, Split, Store, Layers, Plus, Upload,
  // `Users` est déjà pris par la liste des membres : l'icône d'une personne seule est celle du profil.
  User as UserIcon,
  // `Radar` entrerait en collision avec le composant `RadarView` importé plus bas s'il gardait
  // son nom nu dans un fichier de cette taille : on le préfixe une fois pour toutes.
  Radar as RadarIcon
} from 'lucide-react';
import { ComposedChart, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Legend, Line, Bar, BarChart, PieChart, Pie, Cell, LineChart, AreaChart, Area } from 'recharts';
import HomePage from './components/HomePage';
import { aujourdhuiIso, calculateComparisonDates, calculatePreviousPeriod, dateLocale, getDateRangeFromPeriod, jourIso } from './utils/periodUtils';
import HelpCenter from './components/HelpCenter';
import TicketsView from './components/TicketsView';
import CampaignsTable from './components/CampaignsTable';
import AdGroupsTable from './components/AdGroupsTable';
import AssetGroupsTable from './components/AssetGroupsTable';
import KeywordsTable from './components/KeywordsTable';
import DetailedQualityScoreTable from './components/DetailedQualityScoreTable';
import QualityScoreBreakdown from './components/QualityScoreBreakdown';
import MatchTypeBreakdown from './components/MatchTypeBreakdown';
import CpcTierBreakdown from './components/CpcTierBreakdown';
import SearchTermsCpcTierBreakdown from './components/SearchTermsCpcTierBreakdown';
import DevicesTable from './components/DevicesTable';
import WeekdaysTable from './components/WeekdaysTable';
import LandingPagesTableModular from './components/LandingPagesTableModular';
import NetworksTableModular from './components/NetworksTableModular';
import CombinedSearchTermsTable from './components/CombinedSearchTermsTable';
import MicrosoftSearchTermsTable from './components/microsoft-ads/SearchTermsTable';
import { NetworksTable as MicrosoftNetworksTable, ExecutiveSummary as MicrosoftExecutiveSummary } from './components/microsoft-ads';
import NgramAnalysisTable from './components/NgramAnalysisTable';
// import TopFlopProductsTable from './components/TopFlopProductsTable'; // OBSOLÈTE - Remplacé par TopFlopProductsTableModular
import TopFlopProductsTableModular from './components/TopFlopProductsTableModular';
import TopFlopCategoriesTableModular from './components/TopFlopCategoriesTableModular';
import TopFlopItemGroupsTableModular from './components/TopFlopItemGroupsTableModular';
import GeographyTableModular from './components/GeographyTableModular';
import AgeRangesTableModular from './components/AgeRangesTableModular';
import GenderTableModular from './components/GenderTableModular';
import AudiencesTableModular from './components/AudiencesTableModular';
import PerformanceMaxProductsTableModular from './components/PerformanceMaxProductsTableModular';
import PerformanceMaxDevicesTableModular from './components/PerformanceMaxDevicesTableModular';
import PerformanceMaxPlacementsTableModular from './components/PerformanceMaxPlacementsTableModular';
import PerformanceMaxVideosTable from './components/PerformanceMaxVideosTable';
import PerformanceMaxNetworksTable from './components/PerformanceMaxNetworksTable';
import YoutubeVideosTable from './components/YoutubeVideosTable';
import YoutubeResultsChart from './components/YoutubeResultsChart';
import TopFlopSearchTermsTable from './components/TopFlopSearchTermsTable';

import CompetitionDashboard from './components/CompetitionDashboard';
import CrossSalesDashboard from './components/CrossSalesDashboard';
import ProductsInsightsDashboard from './components/ProductsInsightsDashboard';
import DashboardLoadingScreen from './components/DashboardLoadingScreen';
import PerformanceChart from './components/PerformanceChart';
import ArtifactRenderer from './components/Artifacts';
import ChatPanel from './components/ChatPanel';
import './App.css';
import './CampaignTypesCompact.css';
import './search-terms-styles.css';
import './tables-style.css';
import './styles/compteur.css';
import './styles/connecteurs.css';
import './styles/comptes.css';
import './styles/listes.css';
import AdminPanel from './AdminPanel';
import './AdminPanel.css';
import ToolsView from './ToolsView';
import DiagnosticView from './components/DiagnosticView';
import RadarView from './components/RadarView';
import BudgetsView from './components/BudgetsView';
import ChoixDashboardAnalytics from './components/ChoixDashboardAnalytics';
import DashboardSurMesureView from './components/sur-mesure/DashboardSurMesureView';
import ListeComptes from './components/library/ListeComptes';
import VueGenerationDashboard from './components/sur-mesure/VueGenerationDashboard';
import ClientsView from './components/ClientsView';
import SitesView from './components/SitesView';
import XmlFeedsManager from './components/XmlFeedsManager';
import NotificationsView from './components/NotificationsView';
import AlertNotificationsBell from './components/AlertNotificationsBell';
import NotesModal from './components/NotesModal';
import NotesArchive from './components/NotesArchive';
import NotesCalendar from './components/NotesCalendar';
import NotesImport from './components/NotesImport';
import LayoutMockup from './components/LayoutMockup';
import TeamView from './components/TeamView';
import MatomoDashboardView from './components/MatomoDashboardView';
import GA4DashboardView from './components/GA4DashboardView';
import PianoDashboardView from './components/PianoDashboardView';
import ChatGPTAdsDashboardView from './components/ChatGPTAdsDashboardView';
import AwinDashboardView from './components/AwinDashboardView';
import ConsolidatedDashboardView from './components/ConsolidatedDashboardView';
import JoinTeam from './components/auth/JoinTeam';
import OrganizationView from './components/OrganizationView';
import FicheAccesClient from './components/organisation/FicheAccesClient';
import StatisticsView from './components/StatisticsView';
import SubscriptionPage from './components/SubscriptionPage';
import JoinOrganization from './components/auth/JoinOrganization';
import ParcoursInitial from './components/onboarding/ParcoursInitial';
import VisiteGuidee from './components/onboarding/VisiteGuidee';
import FormulaireChatGptAds from './components/connectors/FormulaireChatGptAds';
import FormulaireAwin from './components/connectors/FormulaireAwin';
import FormulairePrestashop from './components/connectors/FormulairePrestashop';
import FormulairePiano from './components/connectors/FormulairePiano';
import FormulaireMatomo from './components/connectors/FormulaireMatomo';
import OrganizationSelector from './components/OrganizationSelector';
import { isMetricSelected, setCurrentActiveAccount, useSelectedMetrics, getCosClassName, calculateCosClassName, getCosThreshold, getCpaClassName, useComponentMetrics, isModuleVisible, setModuleVisibility, useHiddenModules, getMetricLabel, initMetricsSync, panierMoyenPublicitaire } from './utils/metricsUtils';
import HiddenModulesModal from './components/HiddenModulesModal';
import ModuleVisibilityToggle from './components/ModuleVisibilityToggle';
import { useClientModuleVisibility } from './hooks/useClientModuleVisibility';
import MetricsConfigModal from './components/MetricsConfigModal';
import { GenderTable, PlacementTable, DeviceTable, AgeTable, AdSetTable, AdsTable, EngagementDetailsTable, WeekdaysTable as FacebookWeekdaysTable, PlatformTable } from './components/facebook-ads';
import NoDataMessage from './components/NoDataMessage';
import { LoginForm, RegisterForm, ForgotPassword, ResetPassword, VerifyEmail, McpAuth } from './components/auth';
import LegalPage from './components/auth/LegalPage';
import './components/auth/AuthStyles.css';
import NomProfilModal from './components/NomProfilModal';
import config from './config';
import { KanbanView } from './components/kanban';
/*
 * La règle de rattachement, partagée avec le Gestionnaire. Les écrans Comptes et Connecteurs
 * s'en servent pour dire ce qu'un client possède ; la recopier ferait diverger les trois.
 */
import { resolveursDeRattachement, predicatDeRattachement, connecteursDesComptes, comptesDesSites } from './utils/perimetreClient';

import { calculateVariation, makeRenderVariation, variationColor } from './utils/variationUtils';
import { PartialDataBanner, PartialDataNotice } from './components/PartialDataNotice';
import CampaignTypesSection from './components/CampaignTypesSection';
/**
 * La synchronisation des campagnes vit dans `utils/synchroCampagnes.js`.
 *
 * Elle était un `const` de module non exporté ici même : les dashboards Awin et ChatGPT Ads, qui
 * sont des fichiers séparés, ne pouvaient pas s'en servir — leurs campagnes n'arrivaient donc
 * jamais en base, et le panneau « Labels et objectifs » s'y serait ouvert sur une table vide.
 * L'alias garde les appels existants inchangés.
 */
import { synchroniserCampagnes as syncCampaignsInBackground } from './utils/synchroCampagnes';
import { lignesDuBloc } from './utils/lignesDuDashboard';
import AnalyseIA from './components/AnalyseIA';
import { useCampagnesEtiquetees } from './hooks/useCampagnesEtiquetees';
import {
  libellePlateforme, DASHBOARD_PAR_TYPE, ECRAN_PAR_TYPE, CMS, IDS_DASHBOARDS_DE_PLATEFORME
} from './constants/plateformes';

/**
 * Les écrans dont la barre latérale ne porte que la Navigation.
 *
 * ⚠️ **Une liste, et non cinq.** Période, Sites, CMS, Analytics et Publicité posent la même
 * question — « cet écran lit-il les données d'un compte ? » — et chacune la recopiait en
 * clair, seize `currentView !== '…'` à la suite. Relevées côte à côte, les cinq coïncidaient
 * déjà à une exception près : un écran de réglage ajouté ensuite manquait donc aux cinq, et
 * c'est ce qui est arrivé à la fiche d'accès client, qui s'ouvrait sous un sélecteur de
 * période et deux familles de dashboards ne gouvernant rien de ce qu'elle affiche.
 *
 * Ce sont des écrans de **réglage ou d'inventaire** : ils ne portent ni période, ni compte.
 */
const ECRANS_SANS_CONTEXTE_DE_DONNEES = [
  'tools', 'diagnostic', 'analyze', 'history', 'mcp', 'manager', 'organization', 'acces-client',
  'notifications', 'statistics', 'connectors', 'library', 'sites', 'clients', 'pricing',
  'admin', 'help'
];

/**
 * Les logos des plateformes, par `LibraryItem.type`.
 *
 * Sert la section Plateformes de Radar, dont les entrées viennent du feed et non d'une liste
 * écrite ici : une plateforme sans logo prend une icône générique plutôt que de disparaître.
 */
const LOGOS_PLATEFORMES = {
  GOOGLE_ADS: googleAdsLogo,
  MICROSOFT_ADS: microsoftAdsLogo,
  FACEBOOK_ADS: metaAdsLogo,
  PINTEREST_ADS: pinterestAdsLogo,
  TIKTOK_ADS: tiktokAdsLogo,
  OPENAI_ADS: chatgptAdsLogo,
  AWIN: awinLogo,
  PRESTASHOP: prestashopLogo,
  GOOGLE_ANALYTICS: googleAnalyticsLogo,
  PIANO_ANALYTICS: pianoLogo,
  MATOMO: matomoLogo,
  GOOGLE_MERCHANT_API: merchantLogo,
  GOOGLE_SEARCH_CONSOLE: searchConsoleLogo,
  GOOGLE_TAG_MANAGER: tagManagerLogo
};

/**
 * Les connecteurs que l'écran MCP présente, et le type de source que chacun désigne.
 *
 * ⚠️ **`types` est le vocabulaire du catalogue MCP, pas celui de `LibraryItem`.** Les deux
 * diffèrent sur deux entrées et coïncident sur les treize autres, ce qui rend l'écart invisible
 * treize fois sur quinze : un compte Meta est enregistré `FACEBOOK_ADS` et servi `META_ADS`, un
 * compte Analytics `GOOGLE_ANALYTICS` et servi `GOOGLE_ANALYTICS_4` (`TYPE_DE_SCHEMA`, côté
 * serveur). C'est le type **servi** qui est écrit ici, puisque c'est lui que rend
 * `GET /api/mcp/sources` — s'en écarter grise un connecteur pourtant branché, sans rien signaler.
 *
 * Un tableau et non une chaîne, parce qu'un connecteur peut recouvrir deux types : Matomo
 * s'enregistre sous `MATOMO` ou `MATOMO_OAUTH` selon le mode de branchement, et les deux mènent
 * à la même instance.
 *
 * `OPEN_METEO` est lisible par le MCP et n'est **pas** listé ici : c'est une source publique,
 * qu'aucun client ne « branche ».
 */
const CONNECTEURS_MCP = [
  { name: 'Google Ads', icon: googleAdsLogo, types: ['GOOGLE_ADS'] },
  { name: 'Microsoft Ads', icon: microsoftAdsLogo, types: ['MICROSOFT_ADS'] },
  { name: 'Meta Ads', icon: metaAdsLogo, types: ['META_ADS'] },
  { name: 'Pinterest Ads', icon: pinterestAdsLogo, types: ['PINTEREST_ADS'] },
  { name: 'TikTok Ads', icon: tiktokAdsLogo, types: ['TIKTOK_ADS'] },
  { name: 'ChatGPT Ads', icon: chatgptAdsLogo, types: ['OPENAI_ADS'] },
  { name: 'Awin', icon: awinLogo, types: ['AWIN'] },
  { name: 'Google Search Console', icon: searchConsoleLogo, types: ['GOOGLE_SEARCH_CONSOLE'] },
  { name: 'Google Merchant Center', icon: merchantLogo, types: ['GOOGLE_MERCHANT_API'] },
  // Le SVG local, et non le CDN tiers qui servait cette icône : une image servie par un tiers
  // manque dès qu'il tombe, et c'est une page de présentation. Le logo est importé plus haut
  // dans ce fichier.
  { name: 'Google Analytics 4', icon: googleAnalyticsLogo, types: ['GOOGLE_ANALYTICS_4'] },
  { name: 'Google Tag Manager', icon: tagManagerLogo, types: ['GOOGLE_TAG_MANAGER'] },
  { name: 'Matomo', icon: matomoLogo, types: ['MATOMO', 'MATOMO_OAUTH'] },
  { name: 'Piano', icon: pianoLogo, types: ['PIANO_ANALYTICS'] },
  { name: 'PrestaShop', icon: prestashopLogo, types: ['PRESTASHOP'] }
];

/**
 * Les connecteurs dans l'ordre d'affichage : **les branchés d'abord**.
 *
 * Ce qu'un utilisateur vient chercher sur cet écran, c'est ce dont ce client dispose. Laisser
 * les quatorze cartes dans l'ordre du catalogue oblige à balayer toute la grille pour trouver
 * six entrées allumées — et sur un client qui n'a qu'une régie, cette seule carte peut se
 * retrouver en bas à droite.
 *
 * ⚠️ **Deux filtres plutôt qu'un tri**, et ce n'est pas un détail de style : l'ordre du
 * catalogue est conservé **à l'intérieur** de chaque groupe. Un comparateur qui ne distinguerait
 * que branché/non branché laisserait l'implémentation du tri décider du reste, et les cartes se
 * réarrangeraient entre deux clients sans qu'aucune règle ne l'explique.
 *
 * ⚠️ **Rien n'est réordonné tant qu'on ne sait pas.** `typesServis` à `null` — aucun client
 * filtré, réponse pas encore reçue, requête refusée — rend le catalogue dans son ordre d'origine :
 * réordonner sur une ignorance ferait danser la liste au chargement, et surtout promouvrait des
 * connecteurs au titre d'un verdict que personne n'a rendu.
 *
 * @param {Set<string>|null} typesServis les types que le MCP sert pour le client filtré
 * @returns {{name: string, icon: string, types: string[], branche: boolean|null}[]}
 */
function ordonnerConnecteursMcp(typesServis) {
  const juges = CONNECTEURS_MCP.map(connecteur => ({
    ...connecteur,
    branche: typesServis
      ? connecteur.types.some(type => typesServis.has(type))
      : null
  }));

  if (!typesServis) return juges;

  return [
    ...juges.filter(connecteur => connecteur.branche === true),
    ...juges.filter(connecteur => connecteur.branche === false)
  ];
}

const API_URL = config.API_URL;

/**
 * L'écran courant, déclaré au serveur à chaque requête.
 *
 * ## Pourquoi le client l'annonce
 *
 * Le serveur ne peut pas le déduire : `POST /api/dashboard/components/keywords` ne dit pas de
 * quelle plateforme il s'agit — l'information est dans `sources`, sous forme d'identifiants de
 * comptes qu'il faudrait résoudre en base à chaque appel. Et le fragment d'URL, qui la porte,
 * n'est jamais transmis : les navigateurs le retirent du `Referer`.
 *
 * ## Pourquoi ici et pas dans chaque appel
 *
 * `axios.defaults` vaut pour toutes les requêtes de l'application, celles qu'on écrira comprises.
 * C'est exactement ce qui manquait à `trackActivity` côté serveur, posé à la main dans 28
 * fichiers de routes sur 135 : une instrumentation répartie s'oublie, et l'oubli ne se voit pas —
 * il rend simplement un écran muet dans les statistiques d'usage.
 *
 * Le hash fait foi plutôt que les états React : il est la source de vérité de la navigation, il
 * survit au rechargement, et il n'oblige pas à brancher cette déclaration sur une dizaine de
 * `setState` dispersés.
 */
const ecranDepuisLeHash = () => {
  const hash = (window.location.hash || '').split('?')[0].replace(/^#/, '');
  if (!hash) return null;

  const [vue, premier] = hash.split('/');

  // `dashboard:google`, `outils:backlinks`, `analyse` — la famille, puis l'écran.
  if (vue === 'dashboard') return `dashboard:${premier || 'global'}`;
  if (vue === 'tools') return premier ? `outils:${premier}` : 'outils:accueil';
  if (vue === 'analyze' || vue === 'analyser') return 'analyse';

  // Les autres vues — accueil, radar, comptes, admin — ne sont pas des écrans d'analyse : la
  // présence suffit à dire qu'on y est passé, et les compter comme des actes gonflerait les
  // totaux au rythme de la navigation.
  return null;
};

/** Le type de `LibraryItem` que sert chaque onglet de dashboard. */
const TYPE_DE_COMPTE_PAR_ONGLET = {
  'google': 'GOOGLE_ADS',
  'microsoft': 'MICROSOFT_ADS',
  'facebook-ads': 'FACEBOOK_ADS',
  'pinterest-ads': 'PINTEREST_ADS',
  'tiktok-ads': 'TIKTOK_ADS',
  'chatgpt-ads': 'OPENAI_ADS',
  'awin': 'AWIN'
};

/**
 * L'onglet de dashboard d'un type de compte — l'inverse exact de la table ci-dessus.
 *
 * ⚠️ **Dérivé, jamais recopié.** Le dépôt porte déjà cette correspondance **deux fois** :
 * `TYPE_DE_COMPTE_PAR_ONGLET` ici et `TYPE_LIBRAIRIE_PAR_PLATEFORME` quatre-vingts lignes plus
 * bas, à l'identique. En ajouter une troisième, écrite dans l'autre sens, garantirait qu'elles
 * divergent au prochain connecteur — et la divergence serait muette : un compte s'ouvrirait sur
 * le dashboard d'une autre régie.
 *
 * Il sert à savoir si un type de compte **a** un dashboard de plateforme, ce qui décide s'il peut
 * porter un dashboard sur mesure : sans porte pour l'ouvrir, un dashboard hébergé n'existe pas.
 */
const ONGLET_PAR_TYPE_DE_COMPTE = Object.fromEntries(
  Object.entries(TYPE_DE_COMPTE_PAR_ONGLET).map(([onglet, type]) => [type, onglet])
);

/**
 * Les onglets de dashboard qui savent ouvrir un compte précis.
 *
 * Arriver ici depuis « Voir » sur une ligne de compte dépose l'identifiant dans
 * `preselectedAccountId` ; l'effet de nettoyage plus bas l'efface dès qu'on est sur un onglet
 * qui ne saurait qu'en faire. La liste ne tenait que les trois premières régies : TikTok,
 * ChatGPT Ads, Awin et Pinterest voyaient donc leur présélection effacée à l'arrivée, et
 * s'ouvraient sur leur sélecteur de compte alors qu'un compte avait été demandé.
 *
 * Toute régie ajoutée ici doit figurer dans cette liste, sans quoi son « Voir » retombera
 * silencieusement sur le sélecteur.
 */
const ONGLETS_AVEC_PRESELECTION = new Set([
  'google',
  'microsoft',
  'facebook-ads',
  'pinterest-ads',
  'tiktok-ads',
  'chatgpt-ads',
  'awin'
]);

/**
 * Réception du lien d'accès client — `/magic-link/:token`.
 *
 * **Ce chemin n'existait nulle part.** L'email d'invitation y envoie depuis toujours
 * (`emailService.js`), mais aucune route ne le traitait : la réécriture attrape-tout de Vercel
 * servait `index.html`, l'application se montait, et rien ne lisait le chemin. Le client
 * arrivait sur l'écran de connexion sans être connecté et sans explication — le lien ne menait
 * donc nulle part, quelle que soit sa durée de validité.
 *
 * La vérification appartient au serveur, qui consomme le jeton puis redirige vers
 * `/?token=…&user=…` — ce que l'application sait déjà lire. On se contente de l'y conduire.
 *
 * **Au niveau module, et non dans un effet** : rien de ce que l'application affiche ne sert
 * ici, et la monter pour la quitter aussitôt ferait clignoter un écran de connexion que le
 * client n'a pas demandé. `replace` plutôt que `href` pour que le bouton « précédent » ne
 * ramène pas sur un jeton déjà consommé.
 *
 * Le jeton est hexadécimal (`crypto.randomBytes`), mais le défaut du modèle est un `cuid` :
 * les deux formes sont acceptées.
 */
const lienAccesClient = window.location.pathname.match(/^\/magic-link\/([A-Za-z0-9]+)\/?$/);
if (lienAccesClient) {
  window.location.replace(`${API_URL}/api/auth/magic-link/verify/${lienAccesClient[1]}`);
}


// Couleurs pour les graphiques
const COLORS = ['#8884d8', '#82ca9d', '#ffc658', '#ff7c7c', '#8dd1e1', '#d084d0'];

// Plateformes reconnues dans l'URL, sous la forme #dashboard/<plateforme>/<modèle>.
// Ces valeurs sont celles d'`activeTab` : deux listes en dur disaient « facebook »
// au lieu de « facebook-ads » et ignoraient Pinterest et TikTok, si bien qu'un
// rechargement sur l'un de ces trois dashboards retombait sur Google Ads.
const DASHBOARD_PLATFORMS = ['google', 'microsoft', 'facebook-ads', 'pinterest-ads', 'tiktok-ads', 'chatgpt-ads', 'awin'];

/**
 * Le type de `LibraryItem` correspondant à chaque plateforme de dashboard.
 *
 * Tenu ici, à côté de `DASHBOARD_PLATFORMS` dont il couvre exactement les valeurs, et non
 * dans le corps du composant : le sélecteur de comptes de la barre latérale y déroulait une
 * chaîne de ternaires dont le dernier repli était `'FACEBOOK_ADS'`. Sur la page de choix de
 * la plateforme — où `currentDashboardType` vaut `'selection'` —, ce repli s'appliquait, et
 * un menu intitulé « Tous les comptes » ne listait que les comptes Meta.
 *
 * Une table rend l'absence de correspondance explicite : pas de plateforme, pas de
 * sélecteur.
 */
const TYPE_LIBRAIRIE_PAR_PLATEFORME = {
  google: 'GOOGLE_ADS',
  microsoft: 'MICROSOFT_ADS',
  'facebook-ads': 'FACEBOOK_ADS',
  'pinterest-ads': 'PINTEREST_ADS',
  'tiktok-ads': 'TIKTOK_ADS',
  'chatgpt-ads': 'OPENAI_ADS',
  awin: 'AWIN'
};

// Vues atteignables directement par l'URL : bouton précédent, lien collé dans la barre
// d'adresse, favori. Une seule liste, partagée par les deux gestionnaires de navigation.
/**
 * La dernière période choisie, restituée d'une visite à l'autre.
 *
 * **L'URL garde la priorité.** Un lien partagé porte sa période dans son hash : la mémoire ne
 * doit pas la contredire, sinon le destinataire verrait autre chose que l'expéditeur.
 *
 * Une période personnalisée est refusée sans ses deux dates : « CUSTOM » seul ne désigne
 * aucun intervalle, et le tableau retomberait sur un repli silencieux.
 *
 * Toute erreur de lecture rend `null` : un réglage illisible — mode privé, quota, valeur
 * écrite par une version antérieure — repart du défaut plutôt que de bloquer le chargement.
 */
const CLE_PERIODE = 'kokpeet_periode';

const periodeMemorisee = () => {
  try {
    const brut = localStorage.getItem(CLE_PERIODE);
    if (!brut) return null;
    const { period, startDate, endDate } = JSON.parse(brut);
    if (!period) return null;
    if (period === 'CUSTOM' && !(startDate && endDate)) return null;
    return { period, startDate: startDate || '', endDate: endDate || '' };
  } catch (e) {
    return null;
  }
};

/**
 * Les vues de réglage, celles que la barre latérale sert avec la section « Navigation ».
 *
 * ⚠️ **Une seule liste, parce que deux ont divergé.** La section Notes énumérait ces mêmes vues
 * pour s'y masquer, en les recopiant : quatre y manquaient — Connecteurs, Bibliothèque, Sites,
 * Clients —, et « Notes » s'affichait sous « Navigation » comme une entrée de navigation de
 * plus. Relevé à l'écran le 29 octobre 2026, sur l'écran Connecteurs. Rien ne casse quand une
 * vue est ajoutée ici sans l'être là : une entrée paraît, simplement, au mauvais endroit.
 */
const VUES_DE_NAVIGATION = [
  'manager', 'organization', 'notifications', 'statistics', 'pricing',
  // La fiche d'un accès client est un enfant d'`organization` : la barre latérale doit lui
  // servir la même section « Navigation », et non les filtres d'un dashboard.
  'acces-client',
  'connectors', 'library', 'sites', 'clients', 'admin'
];

/**
 * L'entrée de navigation qu'une vue **enfant** doit laisser allumée.
 *
 * ⚠️ **Un écran peut en habiter un autre.** La fiche d'un accès client est un écran
 * d'Organisation — son fil d'Ariane le dit, son bouton de retour y ramène — mais `currentView`
 * vaut `acces-client` : chaque bouton comparant `currentView` à son propre identifiant,
 * l'entrée « Organisation » s'éteignait dès qu'on descendait d'un cran, et la barre latérale
 * ne disait plus où l'on est.
 *
 * Une table, et non un test de plus dans le bouton concerné : les cinq entrées posent la même
 * question, et le prochain sous-écran éteindrait la sienne de la même façon.
 */
const ENTREE_DE_NAVIGATION_PARENTE = {
  'acces-client': 'organization'
};

/**
 * Les autres vues dont la barre latérale est **contextuelle** : Notes n'y a pas sa place non
 * plus. Radar ne doit porter que ce qui filtre le feed — une entrée qui emmène ailleurs au
 * milieu de filtres se lit comme un filtre de plus.
 */
const VUES_SANS_NOTES = [...VUES_DE_NAVIGATION, 'help', 'mcp', 'tools', 'radar'];

const VUES_ROUTABLES = [
  'home', 'radar', 'dashboard', 'analyze', 'connectors', 'library', 'history', 'tools', 'admin',
  // La fiche d'un accès client y figure : son organisation et son email tiennent dans l'URL,
  // elle est donc du côté de `dashboard-sur-mesure` et non de `choix-dashboard`.
  'acces-client',
  'xml-feeds', 'results', 'clients', 'sites', 'team', 'organization',
  'diagnostic', 'notifications', 'mcp', 'pricing', 'statistics', 'manager', 'tickets', 'help',
  'ga4-dashboard', 'matomo-dashboard', 'piano-dashboard', 'prestashop-dashboard', 'consolide-dashboard', 'budgets',
  // `dashboard-sur-mesure` y figure, lui : tout ce dont il a besoin tient dans l'URL — son
  // identifiant, sous `?dashboard=`. C'est la différence avec `choix-dashboard` juste dessous,
  // dont le contenu dépend d'une ligne cliquée que rien ne porte.
  'dashboard-sur-mesure',
  // `choix-dashboard` n'y figure volontairement pas : son contenu dépend de la ligne cliquée,
  // que l'URL ne porte pas. La relire n'ouvrirait qu'un écran vide ; on retombe donc sur
  // l'accueil, d'où le choix se refait en un clic. `setCurrentViewWithHistory` n'en dépend pas
  // — il pousse le hash sans consulter cette liste —, la navigation reste donc intacte.
  // Les trois vues Notes écrivent bien leur hash — `setCurrentViewWithHistory` le fait pour
  // toutes — mais leur absence d'ici empêchait de le relire : recharger
  // `#notes-archive?client=...` retombait sur l'accueil, et le lien n'était pas partageable.
  // Toute vue posée par `setCurrentViewWithHistory` doit figurer dans cette liste.
  'notes-archive', 'notes-calendrier', 'notes-import'
];

/**
 * Fabrique le test « ce site appartient-il à ce rattachement ? » pour Matomo et Piano.
 *
 * ## Le piège que les deux partagent
 *
 * Leurs identifiants de site ne sont uniques **que dans une instance** : deux instances
 * numérotent à partir de 1, si bien que l'`idSite` 2 désigne « Espace Locataires » chez l'une
 * et « Paris en Scène » chez l'autre. Le format récent le règle en portant la connexion —
 * `matomo-{connectionId}-{idSite}` — mais les rattachements anciens ne gardent que le numéro,
 * et l'accepter tel quel faisait remonter les sites de **plusieurs clients** dans le sélecteur.
 *
 * Le numéro nu n'est donc retenu que lorsqu'il ne désigne qu'un seul site du parc. Ambigu, il
 * ne désigne rien : afficher le site d'un autre client est plus grave que n'en afficher aucun,
 * et le second se voit tout de suite. Le cas est journalisé, faute de quoi le sélecteur se
 * viderait sans que rien n'en donne la raison.
 *
 * Google Analytics n'en a pas besoin : ses identifiants de propriété sont uniques chez Google,
 * et son format de rattachement ne porte d'ailleurs aucune connexion.
 */
const fabriquerTestDeRattachement = (prefixe, sites, champsNumero) => {
  const resolveur = fabriquerResolveurDeRattachement(prefixe, sites, champsNumero);

  return (site, accountIds) => {
    if (!Array.isArray(accountIds)) return false;

    // Un numéro nu ambigu ne désigne rien, et il faut le dire : sans ce relevé, le sélecteur se
    // viderait sans qu'aucune trace n'en donne la raison.
    if (resolveur.estAmbigu(site) && accountIds.some(a => String(a) === resolveur.numeroDe(site))) {
      console.warn(
        `⚠️ [${prefixe.toUpperCase()}-FILTER] Rattachement ambigu « ${resolveur.numeroDe(site)} » : ce `
        + "numéro existe sur plusieurs instances. Le site n'est pas retenu — réassociez-le depuis "
        + "la bibliothèque pour enregistrer l'instance."
      );
    }

    return accountIds.some(accountId => resolveur.correspond(site, accountId));
  };
};

/**
 * Place le menu « Associer à un site » là où il tient.
 *
 * Il s'ouvrait toujours vers le bas. Sur la dernière carte de la page — Matomo est la dernière
 * section de la bibliothèque —, il sortait de la fenêtre, et rien ne pouvait défiler pour aller
 * l'y chercher : un menu en position absolue n'allonge pas le document.
 *
 * Il bascule donc au-dessus du bouton quand la place manque en dessous, et sa hauteur est bornée
 * à l'espace réellement disponible. Mesuré à l'ouverture plutôt que deviné en CSS : seul le
 * navigateur sait ce qui reste sous la carte.
 *
 * Appelé en `ref` : React l'invoque au montage du menu, c'est-à-dire au moment où il s'ouvre.
 */
const placerMenuDAssociation = (noeud) => {
  if (!noeud) return;

  noeud.classList.remove('vers-le-haut');
  noeud.style.maxHeight = '';

  // Le bouton, et non le conteneur : celui-ci englobe le menu, et sa hauteur fausserait la mesure.
  const bouton = noeud.previousElementSibling;
  if (!bouton) return;

  const MARGE = 12;
  const HAUTEUR_MINIMALE = 140;
  const ancre = bouton.getBoundingClientRect();
  const enDessous = window.innerHeight - ancre.bottom - MARGE;
  const auDessus = ancre.top - MARGE;
  const souhaitee = noeud.scrollHeight;

  if (souhaitee > enDessous && auDessus > enDessous) {
    noeud.classList.add('vers-le-haut');
    noeud.style.maxHeight = `${Math.max(HAUTEUR_MINIMALE, Math.min(souhaitee, auDessus))}px`;
  } else {
    noeud.style.maxHeight = `${Math.max(HAUTEUR_MINIMALE, Math.min(souhaitee, enDessous))}px`;
  }
};

// Fonction utilitaire pour formater les nombres avec séparateur de milliers français
const formatNumber = (num, decimals = 0) => {
  if (num == null || isNaN(num)) return '0';
  return new Intl.NumberFormat('fr-FR', {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals
  }).format(num);
};

// Afficheur des variations des cartes KPI
const renderVariation = makeRenderVariation({ fontSize: '11px', style: { fontWeight: '500' } });

// Composant pour les cartes KPI avec mini-graphique et évolution
/**
 * Une carte de KPI : la valeur, son titre, la courbe de la période, et la comparaison.
 *
 * ## Une seule définition, après quatre
 *
 * Les dashboards Meta, Pinterest et TikTok en portaient chacun une copie locale qui masquait
 * celle-ci — même nom, dans la portée du composant. Elles avaient divergé sur huit points :
 * bandeau de comparaison affiché ou non, hauteur de la carte, liste des métriques dont la hausse
 * est mauvaise, casse du libellé, plancher du mini-graphique… Chaque correction devait donc être
 * refaite trois fois, et l'était parfois sur la mauvaise copie.
 *
 * Ce qui était propre à chacune passe désormais par une prop : `color` pour la couleur de la
 * régie, `comparisonLabel` pour « période précédente » ou « année précédente »,
 * `invertVariation` pour le sens de lecture d'une hausse.
 *
 * ## Deux règles d'affichage qui portent tout
 *
 * **Le bandeau de comparaison n'apparaît que s'il a quelque chose à dire.** Sans valeur
 * précédente, il s'affichait quand même sur trois des quatre copies : une barre grise vide, qui
 * se lit comme un chargement resté en plan.
 *
 * **La carte remplit sa case de grille.** Sans cela, une carte sans courbe s'arrête à son
 * contenu et paraît tronquée à côté de ses voisines. Le bandeau, poussé en bas, s'aligne alors
 * d'une carte à l'autre.
 */
function KPICard({
  title,
  value,
  unit = '',
  color = '#8884d8',
  currentPeriodData = [],
  previousValue = null,
  formatter = formatNumber,
  comparisonLabel = 'période précédente',
  invertVariation = false,
  className = ''
}) {
  // Transformer les données numériques en objets pour le graphique
  const chartData = currentPeriodData.map((val, index) => ({ value: val || 0, index }));

  /**
   * Y a-t-il de quoi comparer ?
   *
   * `previousValue` absent veut dire « pas de comparaison demandée », et non « zéro ». Une
   * valeur nulle, elle, est une vraie mesure : le bandeau s'affiche, mais sans pourcentage,
   * un écart relatif à zéro n'existant pas.
   */
  const aUneComparaison = previousValue !== null && previousValue !== undefined;
  const change = aUneComparaison && previousValue !== 0
    ? ((value - previousValue) / previousValue) * 100
    : null;

  /**
   * Métriques dont la hausse est mauvaise, quand l'appelant ne le précise pas.
   *
   * Ce repli par le titre reste fragile : il casse au moindre changement de libellé. La prop
   * `invertVariation` est le signal fiable, à préférer côté appelant. `CPM` vient des copies
   * Pinterest et TikTok, qui l'avaient et que celle-ci ignorait.
   */
  const INVERTED_TITLES = ['Coût', 'CPA', 'COS', 'CPC', 'CPM', 'Coût/Inter.', 'Coût/Réact.'];

  // Une hausse est bonne, sauf pour les métriques inversées : les coûts, essentiellement.
  const couleurEvolution = () => variationColor(change, invertVariation || INVERTED_TITLES.includes(title));
  const flecheEvolution = () => (change > 0 ? '↑' : change < 0 ? '↓' : '→');

  return (
    <div className={`insight-card ${className}`.trim()} style={{
      background: 'white',
      padding: '16px',
      borderRadius: '8px',
      textAlign: 'center',
      position: 'relative',
      // La grille étire ses cases : la carte doit les remplir, sinon les hauteurs se déchirent.
      height: '100%',
      display: 'flex',
      flexDirection: 'column',
      boxSizing: 'border-box'
    }}>
      {/* Valeur principale */}
      <div style={{ fontSize: '22px', fontWeight: 'bold', color, marginBottom: '4px' }}>
        {typeof value === 'number' ? formatter(value) : value}{unit}
      </div>

      {/*
        Titre. `textTransform` et `letterSpacing` explicites : `App.css` met en majuscules le
        `div:last-child` de `.insight-card` pour styler le bandeau de comparaison. Sur une carte
        sans courbe ni comparaison, le titre devenait ce dernier enfant et héritait des
        majuscules. L'apparence d'un titre ne doit pas dépendre de sa position dans le DOM.
      */}
      <div style={{
        fontSize: '15px', color: '#6b7280', marginBottom: '8px', fontWeight: '500',
        textTransform: 'none', letterSpacing: 'normal'
      }}>
        {title}
      </div>

      {/* Mini-graphique, seulement si la métrique a une série à montrer */}
      {chartData.length > 0 && (
        <div style={{ height: '30px', marginBottom: '8px' }}>
          <ResponsiveContainer width="100%" height="100%" minWidth={1} minHeight={1}>
            <LineChart data={chartData}>
              <Line
                type="monotone"
                dataKey="value"
                stroke={color}
                strokeWidth={2}
                dot={false}
                connectNulls={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* Évolution et valeur de la période de comparaison, sur fond crème */}
      {aUneComparaison && (
        <div style={{
          backgroundColor: '#FAF9F5',
          padding: '8px',
          borderRadius: '6px',
          // `auto` et non une marge fixe : le bandeau descend au bas de la carte, ce qui
          // l'aligne avec celui des voisines même quand les contenus diffèrent.
          marginTop: 'auto'
        }}>
          {change !== null ? (
            <div
              className="kpi-evolution-text"
              style={{ fontSize: '14px', color: couleurEvolution(), fontWeight: '600', lineHeight: '1.2' }}
            >
              <span style={{ fontSize: '14px' }}>
                {flecheEvolution()} {Math.abs(change).toFixed(1)}%
              </span>
            </div>
          ) : (
            // Période précédente à zéro : aucun écart relatif n'existe, et un « 0 % » mentirait.
            <div style={{ fontSize: '14px', color: '#9ca3af', fontWeight: '600', lineHeight: '1.2' }}>
              <span style={{ fontSize: '14px' }}>-</span>
            </div>
          )}
          <div style={{
            fontSize: '11px', color: '#9ca3af', fontStyle: 'italic',
            fontWeight: 'normal', marginTop: '2px'
          }}>
            {comparisonLabel.toUpperCase()} : {formatter(previousValue)}{unit}
          </div>
        </div>
      )}
    </div>
  );
}


/**
 * Les vues que l'application sait afficher.
 *
 * Sert le repli « Vue inconnue », qui n'a de sens que si cette liste est complète.
 *
 * ⚠️ **Elle était écrite dans le JSX, et elle avait dérivé.** Six vues rendues n'y figuraient
 * pas — `help`, `join-organization`, `join-team`, `legal`, `mcp-auth`, `mockup`. Le défaut est
 * visible mais incompréhensible : la vue s'affiche **et** le bandeau « Vue inconnue » s'affiche
 * au-dessus, puisque le repli ignore que quelque chose d'autre a été rendu.
 *
 * ⚠️ **Ajouter une vue, c'est l'ajouter ici.** `backend/scripts/test-vues-connues.js` dérive du
 * fichier la liste réelle des `currentView === '…'` et refuse l'écart : c'est lui qui rend cette
 * liste tenable, pas la vigilance.
 */
const VUES_CONNUES = [
  'acces-client',
  'admin',
  'analyze',
  'budgets',
  'choix-dashboard',
  'clients',
  'connectors',
  'consolide-dashboard',
  'dashboard',
  'dashboard-sur-mesure',
  'diagnostic',
  'ga4-dashboard',
  'generation-dashboard',
  'help',
  'history',
  'home',
  'join-organization',
  'join-team',
  'legal',
  'library',
  'manager',
  'matomo-dashboard',
  'mcp',
  'mcp-auth',
  'mockup',
  'notes-archive',
  'notes-calendrier',
  'notes-import',
  'notifications',
  'organization',
  'piano-dashboard',
  'prestashop-dashboard',
  'pricing',
  'radar',
  'results',
  'sites',
  'statistics',
  'team',
  'tickets',
  'tools',
  'xml-feeds',
];

/**
 * L'accès client désigné par le hash, ou `null`.
 *
 * Écrit une fois et appelé de trois endroits — le montage, `popstate`, `hashchange` : trois
 * lectures recopiées divergeraient, et le bouton « précédent » rouvrirait alors une autre fiche
 * que le rechargement.
 *
 * ⚠️ **`URLSearchParams` décode `+` en espace.** `jean+kokpit@ex.fr` écrit brut dans le hash
 * revient en `jean kokpit@ex.fr`, et la fiche répond 404 sur un email qui existe.
 * `encodeURIComponent` à l'écriture n'est donc pas une précaution mais la condition de
 * correction — voir `mettreAJourUrlFicheAcces`.
 */
function lireFicheAccesDuHash(hash = window.location.hash) {
  const parties = hash.split('?');
  if (parties[0].replace(/^#/, '').split('/')[0] !== 'acces-client') return null;
  const parametres = new URLSearchParams(parties[1] || '');
  const email = parametres.get('email');
  const org = parametres.get('org');
  return email && org ? { org, email } : null;
}

function App() {
  /*
   * L'écran courant, posé sur toutes les requêtes sortantes.
   *
   * Sur `popstate` et `hashchange` : la navigation de cette application passe par les deux —
   * `pushState` pour les changements d'onglet, le hash pour les retours arrière du navigateur.
   * N'écouter que l'un des deux laisserait l'en-tête figé sur l'écran précédent, et les
   * statistiques attribueraient l'activité au mauvais endroit.
   */
  useEffect(() => {
    const declarer = () => {
      const ecran = ecranDepuisLeHash();
      if (ecran) axios.defaults.headers.common['X-Kokpit-Ecran'] = ecran;
      else delete axios.defaults.headers.common['X-Kokpit-Ecran'];
    };

    declarer();
    window.addEventListener('hashchange', declarer);
    window.addEventListener('popstate', declarer);
    return () => {
      window.removeEventListener('hashchange', declarer);
      window.removeEventListener('popstate', declarer);
    };
  }, []);

  const [user, setUser] = useState(null);

  /*
   * ⚠️ **Déclaré tout en haut, juste après `user`, et c'est nécessaire.**
   *
   * Cette source unique est lue par des listes calculées très tôt — `cmsDisponibles` dès la
   * ligne 1660 — et par l'effet des gardes de rôle. Un tableau de dépendances est évalué
   * **pendant le rendu** : posée plus bas, elle y était lue dans sa zone morte et
   * l'application ne montait plus — « Cannot access 'plateformeOuverte' before
   * initialization ». Le dépôt porte déjà deux fois cet avertissement, sur
   * `ouvrirChoixDashboard` et sur l'effet qui réécrit l'URL des vues analytics.
   *
   * ⚠️ Et le `build` ne l'attrape pas : c'est une faute d'exécution, pas de compilation.
   */
  /**
   * Les dashboards de plateforme fermés à cet accès client.
   *
   * ⚠️ **Un état à part de `clientTools`, et non une entrée de plus dans sa liste.** Celle-ci
   * énumère des **retraits**, celle-là des **autorisations** : les confondre les ferait lire
   * avec la même règle, donc à l'envers pour l'une des deux. Vide pour une agence, qui n'est
   * jamais filtrée.
   *
   * ⚠️ **`null` veut dire « pas encore su », et ce n'est pas la liste vide.** Les deux
   * s'écrivaient `[]`, et la liste énumérant des retraits, « rien n'est retiré » ouvre tout :
   * l'accueil d'un client montrait donc, le temps d'un aller-retour, les plateformes que son
   * agence lui a fermées — puis les retirait sous ses yeux. Ce n'est pas qu'un défaut de
   * fluidité : c'est lui apprendre des noms de régies qui ne le regardent pas. Le troisième
   * état est le seul moyen de distinguer l'absence de mesure d'une mesure nulle.
   */
  const [dashboardsRetires, setDashboardsRetires] = useState(null);

  /**
   * Ce client peut-il ouvrir ce dashboard de plateforme ?
   *
   * ⚠️ **La source unique du filtrage par plateforme**, à brancher partout où l'écran décide de
   * proposer un dashboard. Il n'en existait aucune : la liste des plateformes proposées était
   * décidée à six endroits, depuis trois sources de données différentes.
   *
   * ⚠️ **Tant qu'on ne sait pas, on ne propose rien** — comme les droits d'outil, et à
   * l'inverse de ce que faisait la première version. Elle ouvrait pendant le chargement, au
   * motif que masquer ferait clignoter la navigation : c'est l'autre sens du clignotement qui
   * s'est produit, et c'est le mauvais. Une interface qui se **remplit** est ordinaire ; une
   * interface qui se **vide** laisse voir ce qu'elle devait cacher, puis se reprend.
   *
   * ⚠️ **Masquer une affordance et refuser un accès ne se décident pas pareil.** Ici on masque,
   * donc le doute ferme. L'effet des gardes de rôle, lui, **redirige** : il sort tôt sur
   * `clientToolsLoaded`, posé par le `finally` de `fetchClientTools` — donc après cet état —,
   * sans quoi un client arrivant par un lien direct sur un dashboard ouvert serait renvoyé à
   * l'accueil avant même que la réponse arrive. Ne pas intervertir ces deux défauts.
   *
   * @param {string} identifiantEcran `google`, `ga4-dashboard`… tel que le rend `ECRAN_PAR_TYPE`
   */
  const plateformeOuverte = useCallback((identifiantEcran) => {
    if (user?.role !== 'CLIENT') return true;
    if (!identifiantEcran) return true;
    if (dashboardsRetires === null) return false;
    return !dashboardsRetires.includes(identifiantEcran);
  }, [user, dashboardsRetires]);

  /** Le même verdict, à partir du type de compte plutôt que de l'écran. */
  const plateformeOuvertePourType = useCallback((type) => (
    plateformeOuverte(ECRAN_PAR_TYPE[type])
  ), [plateformeOuverte]);

  const [token, setToken] = useState(localStorage.getItem('kokpeet_token'));
  // État pour éviter le flash du login pendant la vérification du token
  const [isVerifyingAuth, setIsVerifyingAuth] = useState(() => !!localStorage.getItem('kokpeet_token'));
  const [userSubscription, setUserSubscription] = useState(null);
  // État pour le token d'invitation d'équipe (ancien système)
  const [invitationToken, setInvitationToken] = useState(() => {
    // Vérifier si on est sur la route /join-team/:token
    const joinTeamMatch = window.location.pathname.match(/^\/join-team\/([a-zA-Z0-9]+)$/);
    return joinTeamMatch ? joinTeamMatch[1] : null;
  });

  // État pour le token d'invitation d'organisation (nouveau système)
  const [orgInvitationToken, setOrgInvitationToken] = useState(() => {
    // Vérifier si on est sur la route /join-organization/:token
    const joinOrgMatch = window.location.pathname.match(/^\/join-organization\/([a-zA-Z0-9]+)$/);
    return joinOrgMatch ? joinOrgMatch[1] : null;
  });

  // État pour les paramètres MCP Auth (Claude Desktop connector)
  const [mcpAuthParams, setMcpAuthParams] = useState(() => {
    if (window.location.pathname === '/mcp-auth') {
      const params = new URLSearchParams(window.location.search);
      return {
        mcpCallback: params.get('mcp_callback'),
        scope: params.get('scope')
      };
    }
    return null;
  });

  // État pour l'account ID initial du diagnostic (depuis URL email)
  const [diagnosticInitialAccountId, setDiagnosticInitialAccountId] = useState(() => {
    // Vérifier si on arrive depuis un lien email #diagnostic?account=xxx
    const hash = window.location.hash;
    if (hash.startsWith('#diagnostic?') || hash.startsWith('#diagnostic/?')) {
      const queryPart = hash.includes('?') ? hash.split('?')[1] : '';
      const params = new URLSearchParams(queryPart);
      const accountId = params.get('account');
      console.log('[APP] Hash diagnostic détecté:', hash, '-> accountId:', accountId);
      return accountId || null;
    }
    return null;
  });

  const [currentView, setCurrentView] = useState(() => {
    // Pages légales publiques
    if (window.location.pathname === '/privacy' || window.location.pathname === '/terms') {
      return 'legal';
    }
    // Vérifier si on est sur la route /mcp-auth (Claude Desktop connector)
    if (window.location.pathname === '/mcp-auth') {
      return 'mcp-auth';
    }
    // Vérifier si on est sur la route /join-team/:token
    if (window.location.pathname.match(/^\/join-team\/[a-zA-Z0-9]+$/)) {
      return 'join-team';
    }
    // Vérifier si on est sur la route /join-organization/:token
    if (window.location.pathname.match(/^\/join-organization\/[a-zA-Z0-9]+$/)) {
      return 'join-organization';
    }
    // Vérifier si on est sur la route des flux XML
    if (window.location.pathname === '/tools/xml-feeds') {
      return 'xml-feeds';
    }
    // Vérifier si on accède à la maquette UX
    if (window.location.pathname === '/mockup' || window.location.hash === '#mockup') {
      return 'mockup';
    }
    // Vérifier si on arrive depuis un lien email diagnostic
    if (window.location.hash.startsWith('#diagnostic')) {
      return 'diagnostic';
    }
    // Vérifier si on arrive depuis un lien email notifications
    if (window.location.hash === '#notifications') {
      return 'notifications';
    }
    // Vérifier si on arrive sur le dashboard (ex: retour OAuth)
    if (window.location.hash.startsWith('#dashboard')) {
      return 'dashboard';
    }
    // Vérifier si on arrive sur les dashboards analytics (GA4, Matomo, Piano)
    if (window.location.hash.startsWith('#ga4-dashboard')) {
      return 'ga4-dashboard';
    }
    if (window.location.hash.startsWith('#matomo-dashboard')) {
      return 'matomo-dashboard';
    }
    if (window.location.hash.startsWith('#piano-dashboard')) {
      return 'piano-dashboard';
    }
    if (window.location.hash.startsWith('#prestashop-dashboard')) {
      return 'prestashop-dashboard';
    }
    // Vérifier si on arrive sur le gestionnaire de données
    if (window.location.hash === '#manager') {
      return 'manager';
    }
    // Vérifier si on arrive sur les connecteurs
    if (window.location.hash === '#connectors') {
      return 'connectors';
    }
    // Vérifier si on arrive sur le centre d'aide
    if (window.location.hash === '#help') {
      return 'help';
    }
    // Vérifier si on arrive sur la page MCP
    if (window.location.hash === '#mcp' || window.location.hash.startsWith('#mcp?') || window.location.hash.startsWith('#mcp/')) {
      return 'mcp';
    }
    // Vérifier si on arrive sur la vue admin (avec ou sans onglet/ticket)
    if (window.location.hash === '#admin' || window.location.hash.startsWith('#admin/')) {
      return 'admin';
    }
    // Vérifier si on arrive sur les tickets
    if (window.location.hash === '#tickets' || window.location.hash.startsWith('#tickets/')) {
      return 'tickets';
    }
    // Vérifier si on arrive sur les outils
    if (window.location.hash === '#tools' || window.location.hash.startsWith('#tools/')) {
      return 'tools';
    }
    /**
     * Toute autre vue routable, lue dans le hash.
     *
     * Les cas ci-dessus sont ceux qui demandent un traitement particulier — une route de
     * chemin, ou un hash porteur de paramètres à restaurer. Les autres n'en demandent aucun,
     * et les énumérer un à un avait une conséquence silencieuse : une vue absente de la liste
     * retombait sur `home`. Actualiser la page sur `#statistics`, `#library` ou
     * `#organization` renvoyait donc à l'accueil, alors que le bouton précédent, lui, y
     * revenait — les deux gestionnaires de navigation consultent `VUES_ROUTABLES` depuis
     * toujours ; seul ce calcul-ci ne le faisait pas.
     *
     * Même lecture que les deux autres : on retient le premier segment, sans le `?` ni ce qui
     * suit une barre oblique.
     */
    const vueDuHash = window.location.hash.replace(/^#/, '').split('?')[0].split('/')[0];
    if (vueDuHash && VUES_ROUTABLES.includes(vueDuHash)) {
      return vueDuHash;
    }

    // Commencer par la page d'accueil par défaut, ou dashboards pour les clients
    // Vue par défaut identique pour tous les utilisateurs
    return 'home';
  }); // home, dashboard, analyze, connectors, library, history, xml-feeds, mockup, team, join-team, diagnostic
  const [previousViewBeforeTickets, setPreviousViewBeforeTickets] = useState(null); // Dernière page avant d'aller sur Support
  const [previousViewBeforeNotes, setPreviousViewBeforeNotes] = useState(null); // Dernière page avant d'aller sur Notes
  const [isInitialLogin, setIsInitialLogin] = useState(!sessionStorage.getItem('kokpeet_autoRouted'));
  const [showUserMenu, setShowUserMenu] = useState(false);
  /**
   * Demande du nom : ouverte d'elle-même à la première connexion d'un compte qui n'en a pas,
   * ou à la demande depuis le menu du profil.
   *
   * `premiereFois` change le ton — une question plutôt qu'un réglage — et retire les sorties
   * discrètes : la croix et le clic extérieur. « Plus tard » reste, et le dit.
   */
  const [demandeNom, setDemandeNom] = useState(null);
  // La question n'est posée qu'une fois par session : refusée, elle ne doit pas resurgir au
  // moindre changement d'écran.
  const nomDejaDemande = useRef(false);

  /**
   * Un compte sans nom se voit poser la question à son arrivée.
   *
   * Le cas ne concerne en pratique que les accès clients ouverts par lien d'invitation : ils
   * n'ont jamais traversé Google, qui fournit d'ordinaire le nom et la photo. Sans cette
   * demande, leur nom resterait vide à jamais — rien d'autre ne le réclame.
   *
   * Le délai laisse l'écran s'afficher d'abord : une modale qui précède le tableau de bord
   * accueille mal, et la question se comprend mieux devant ce qu'elle sert à nommer.
   */
  useEffect(() => {
    if (!user || nomDejaDemande.current) return;
    if (user.name) return;
    // Un compte Google n'est pas concerné : son nom vient du profil Google et y est relu à
    // chaque connexion. Lui demander de le saisir reviendrait à lui faire écrire quelque chose
    // que la connexion suivante effacerait.
    if (user.googleId) return;

    nomDejaDemande.current = true;
    const minuterie = setTimeout(() => setDemandeNom({ premiereFois: true }), 1200);
    return () => clearTimeout(minuterie);
  }, [user]);
  const [currentDashboardType, setCurrentDashboardType] = useState(() => {
    // Extraire le type depuis le hash si on est sur le dashboard
    // Format: #dashboard/google/global, #dashboard/facebook-ads/global, etc.
    if (window.location.hash.startsWith('#dashboard/')) {
      const hashSegments = window.location.hash.split('?')[0].replace('#', '').split('/');
      const type = hashSegments[1];
      if (DASHBOARD_PLATFORMS.includes(type)) {
        return type;
      }
    }
    // `#dashboard` sans plateforme est une page à part entière : le choix de la
    // plateforme. Répondre 'google' ici ouvrait le dashboard Google Ads et réécrivait
    // l'URL, si bien qu'un simple rechargement changeait de page.
    if (window.location.hash === '#dashboard' || window.location.hash.startsWith('#dashboard?')) {
      return 'selection';
    }
    return 'google';
  });
  const [showAIButton, setShowAIButton] = useState(false);
  
  // États pour le filtre client (USER role uniquement)
  const [userClients, setUserClients] = useState([]);
  // Outils ouverts au compte connecté. Pour une agence, le catalogue complet ; pour un
  // accès client, les seuls outils que son agence lui a accordés en lecture seule.
  const [clientTools, setClientTools] = useState([]);
  const [clientToolsLoaded, setClientToolsLoaded] = useState(false);
  /**
   * Les dashboards sur mesure lisibles, sans leur HTML.
   *
   * Chargés avec les droits clients, et pour la même raison : l'écran de choix du dashboard doit
   * savoir s'il a un choix à faire **avant** d'être affiché. La liste est déjà bornée par le
   * serveur — périmètre, droit client et `visibleClient` compris.
   */
  const [dashboardsSurMesure, setDashboardsSurMesure] = useState([]);
  const [dashboardsSurMesureCharges, setDashboardsSurMesureCharges] = useState(false);
  /**
   * L'accès client dont la fiche est ouverte, pour la vue `acces-client`.
   *
   * ⚠️ **La clé est l'email, et l'organisation voyage avec.** C'est l'email qui porte le droit
   * (`ClientToolAccess` est unique sur organisation + email + outil) : une invitation en attente
   * n'a pas encore de compte, et un même email peut être client de deux agences avec deux jeux
   * de droits. Sans l'organisation, un lien rechargé montrerait les droits de la première
   * organisation de la liste — c'est-à-dire, pour un membre de deux agences, ceux de l'autre.
   *
   * Relu du hash au montage, comme le dashboard sur mesure juste en dessous : l'adresse est
   * partageable et survit à un rechargement.
   */
  const [ficheAccesOuverte, setFicheAccesOuverte] = useState(() => lireFicheAccesDuHash());

  /**
   * L'onglet ouvert de l'écran Organisation, lu du hash — `#organization/clients`.
   *
   * Il existe pour que le « ← Accès clients » de la fiche ramène sur l'onglet d'où l'on vient.
   * Sans lui, le retour retombait sur « Membres » : l'onglet était un état local du composant,
   * que rien dans l'adresse ne portait.
   */
  const [ongletOrganisation, setOngletOrganisation] = useState(() => {
    const premier = window.location.hash.replace(/^#/, '').split('?')[0].split('/');
    return premier[0] === 'organization' ? (premier[1] || null) : null;
  });

  /**
   * L'identifiant du dashboard sur mesure ouvert, pour la vue `dashboard-sur-mesure`.
   *
   * Relu du hash au montage, pour que l'adresse soit partageable et survive à un rechargement :
   * la vue figure dans `VUES_ROUTABLES`, et sans cet identifiant elle s'ouvrirait vide.
   */
  const [dashboardSurMesureOuvert, setDashboardSurMesureOuvert] = useState(() => {
    const parties = window.location.hash.split('?');
    if (parties[0].replace(/^#/, '').split('/')[0] !== 'dashboard-sur-mesure') return null;
    return new URLSearchParams(parties[1] || '').get('dashboard') || null;
  });

  /**
   * La version affichée d'un dashboard sur mesure, ou `null` pour celle qui est publiée.
   *
   * C'est ce qui permet de prévisualiser un **candidat** — une version enregistrée mais non
   * promue. Elle vit sur une fiche réelle, lit donc de vraies données, et se regarde sans que le
   * client voie quoi que ce soit changer.
   */
  const [versionSurMesureOuverte, setVersionSurMesureOuverte] = useState(() => {
    const parties = window.location.hash.split('?');
    if (parties[0].replace(/^#/, '').split('/')[0] !== 'dashboard-sur-mesure') return null;
    return new URLSearchParams(parties[1] || '').get('version') || null;
  });
  /*
   * Le compte pour lequel on génère un dashboard sur mesure.
   *
   * ⚠️ Il n'est **pas** relu de l'URL, contrairement à `dashboardSurMesureOuvert` : une
   * conversation qui coûte quarante centimes le tour ne doit pas se rouvrir sur un simple
   * rechargement.
   *
   * ⚠️ Il peut ne porter qu'un `{ type }` : la vue demande alors sur quel compte générer, au lieu
   * que la porte d'entrée s'évapore — c'est le défaut qui rendait la génération introuvable sur
   * l'écran d'une solution de mesure atteinte sans compte précis.
   */
  const [generationSurMesure, setGenerationSurMesure] = useState(null);

  const [selectedClientId, setSelectedClientId] = useState(() => {
    // Restaurer le client depuis l'URL au chargement
    const hashParts = window.location.hash.split('?');
    if (hashParts.length > 1) {
      const params = new URLSearchParams(hashParts[1]);
      return params.get('client') || null;
    }
    return null;
  });
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [clientSearchFilter, setClientSearchFilter] = useState('');
  const [showDashboardModelDropdown, setShowDashboardModelDropdown] = useState(false);
  const [authError, setAuthError] = useState(null); // État pour gérer les erreurs d'authentification
  const [inaccessibleAccounts, setInaccessibleAccounts] = useState([]); // Comptes Google Ads avec accès révoqué

  // État pour l'organisation active
  const [activeOrganizationId, setActiveOrganizationId] = useState(null);
  const [userOrganizations, setUserOrganizations] = useState([]);
  const [organizationsLoaded, setOrganizationsLoaded] = useState(false);

  // État pour bloquer le rendu de HomePage pendant le chargement initial
  const [initialDataLoaded, setInitialDataLoaded] = useState(false);

  // États pour les notes
  const [isNotesModalOpen, setIsNotesModalOpen] = useState(false);
  const [editingNote, setEditingNote] = useState(null);

  // États pour les préférences de notifications
  const [notificationPrefs, setNotificationPrefs] = useState({ emailAlertsActive: true });
  const [notificationPrefsLoading, setNotificationPrefsLoading] = useState(false);
  const [userSites, setUserSites] = useState([]);
  const [notesContext, setNotesContext] = useState({});
  const [allAdAccounts, setAllAdAccounts] = useState([]);
  const [dashboardNotes, setDashboardNotes] = useState([]);

  // État pour synchroniser le compte sélectionné dans la sidebar
  const [sidebarSelectedAccountId, setSidebarSelectedAccountId] = useState('');

  // Helper pour extraire les paramètres de l'URL au chargement
  const getUrlParams = () => {
    const hashParts = window.location.hash.split('?');
    if (hashParts.length > 1) {
      return new URLSearchParams(hashParts[1]);
    }
    return new URLSearchParams();
  };

  /**
   * La période d'ouverture : celle de l'URL, sinon la dernière retenue, sinon sept jours.
   *
   * Les trois valeurs sont lues ensemble et non chacune de son côté : une période venue de
   * l'URL accompagnée de dates venues de la mémoire décrirait un intervalle que personne n'a
   * choisi.
   */
  const periodeAuChargement = () => {
    const params = getUrlParams();
    if (params.get('period')) {
      return {
        period: params.get('period'),
        startDate: params.get('startDate') || '',
        endDate: params.get('endDate') || ''
      };
    }
    return periodeMemorisee() || { period: 'LAST_7_DAYS', startDate: '', endDate: '' };
  };

  // État global pour la période (partagé entre sidebar, HomePage et dashboards)
  const [selectedPeriod, setSelectedPeriod] = useState(() => periodeAuChargement().period);
  const [customStartDate, setCustomStartDate] = useState(() => periodeAuChargement().startDate);
  const [customEndDate, setCustomEndDate] = useState(() => periodeAuChargement().endDate);
  /**
   * La période retenue, pour la prochaine visite.
   *
   * Écrite à chaque changement plutôt qu'à la fermeture : rien ne garantit qu'un onglet soit
   * fermé proprement, et `beforeunload` ne se déclenche pas sur mobile.
   *
   * Un échec d'écriture — navigation privée, quota — est ignoré : le réglage ne survivra pas
   * à la visite, ce qui est exactement le comportement d'avant.
   */
  useEffect(() => {
    try {
      localStorage.setItem(CLE_PERIODE, JSON.stringify({
        period: selectedPeriod,
        startDate: customStartDate,
        endDate: customEndDate
      }));
    } catch (e) {
      // Sans conséquence : la période reste celle de la session en cours.
    }
  }, [selectedPeriod, customStartDate, customEndDate]);

  /**
   * La même période, enregistrée sur le compte — c'est elle qui suit d'un appareil à l'autre.
   *
   * **Le stockage local n'est pas remplacé, il est doublé.** Lui répond avant le premier
   * rendu ; la base n'arrive qu'après l'authentification. S'en remettre à elle seule ferait
   * ouvrir la page sur la période par défaut, puis basculer — en rechargeant au passage tous
   * les tableaux.
   *
   * L'écriture est différée d'une seconde : parcourir le menu des périodes déclenche autant
   * de changements d'état que de survols, et chacun aurait été un appel.
   *
   * Un échec est passé sous silence. La préférence vaut ce qu'elle vaut : la perdre n'empêche
   * rien, et une alerte sur un réglage de confort serait plus gênante que l'oubli lui-même.
   */
  useEffect(() => {
    if (!token) return;
    if (selectedPeriod === 'CUSTOM' && !(customStartDate && customEndDate)) return;

    const minuteur = setTimeout(() => {
      axios.put(`${API_URL}/api/preferences/periode`, {
        period: selectedPeriod,
        startDate: customStartDate,
        endDate: customEndDate
      }, { headers: { Authorization: `Bearer ${token}` } })
        .catch(() => {});
    }, 1000);

    return () => clearTimeout(minuteur);
  }, [token, selectedPeriod, customStartDate, customEndDate]);

  // États temporaires pour les dates personnalisées (avant validation)
  const [tempCustomStartDate, setTempCustomStartDate] = useState('');
  const [tempCustomEndDate, setTempCustomEndDate] = useState('');
  // État pour la période prédéfinie temporaire (avant validation)
  const [tempSelectedPeriod, setTempSelectedPeriod] = useState('');
  // État pour contrôler l'ouverture du menu période (clic au lieu de hover)
  const [isPeriodMenuOpen, setIsPeriodMenuOpen] = useState(false);
  // Ref pour le conteneur du menu période (pour détecter les clics extérieurs)
  const periodMenuRef = React.useRef(null);

  // États pour la comparaison intégrée au sélecteur de période
  // États temporaires (avant validation avec bouton Appliquer)
  const [tempCompareEnabled, setTempCompareEnabled] = useState(false);
  const [tempComparisonType, setTempComparisonType] = useState('previous_period'); // 'previous_period', 'previous_year', 'custom'
  const [tempComparisonStartDate, setTempComparisonStartDate] = useState('');
  const [tempComparisonEndDate, setTempComparisonEndDate] = useState('');
  // États persistants (après validation) - restaurés depuis l'URL
  const [globalCompareEnabled, setGlobalCompareEnabled] = useState(() => {
    const params = getUrlParams();
    return params.get('compare') === 'true';
  });
  const [globalComparisonType, setGlobalComparisonType] = useState(() => {
    const params = getUrlParams();
    return params.get('compareType') || 'previous_period';
  });
  const [comparisonStartDate, setComparisonStartDate] = useState(() => {
    const params = getUrlParams();
    return params.get('compareStart') || '';
  });
  const [comparisonEndDate, setComparisonEndDate] = useState(() => {
    const params = getUrlParams();
    return params.get('compareEnd') || '';
  });
  // État pour savoir quel champ de date est actif dans le calendrier
  // Valeurs: 'mainStart', 'mainEnd', 'compStart', 'compEnd', null (mode sélection plage N)
  const [activeCalendarField, setActiveCalendarField] = useState(null);

  // Fonction helper pour formater une date en YYYY-MM-DD
  const formatDateYMD = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;

  // Fonction pour calculer les dates d'une période
  const getPeriodDates = (periodValue) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let startDate, endDate;

    switch (periodValue) {
      case 'TODAY':
        startDate = endDate = formatDateYMD(today);
        break;
      case 'YESTERDAY':
        const yesterday = new Date(today);
        yesterday.setDate(yesterday.getDate() - 1);
        startDate = endDate = formatDateYMD(yesterday);
        break;
      case 'LAST_7_DAYS':
        const y7 = new Date(today);
        y7.setDate(y7.getDate() - 1); // Hier
        endDate = formatDateYMD(y7);
        const d7 = new Date(y7);
        d7.setDate(d7.getDate() - 6);
        startDate = formatDateYMD(d7);
        break;
      case 'LAST_14_DAYS':
        const y14 = new Date(today);
        y14.setDate(y14.getDate() - 1); // Hier
        endDate = formatDateYMD(y14);
        const d14 = new Date(y14);
        d14.setDate(d14.getDate() - 13);
        startDate = formatDateYMD(d14);
        break;
      case 'LAST_30_DAYS':
        const y30 = new Date(today);
        y30.setDate(y30.getDate() - 1); // Hier
        endDate = formatDateYMD(y30);
        const d30 = new Date(y30);
        d30.setDate(d30.getDate() - 29);
        startDate = formatDateYMD(d30);
        break;
      case 'THIS_MONTH':
        startDate = formatDateYMD(new Date(now.getFullYear(), now.getMonth(), 1));
        endDate = formatDateYMD(today);
        break;
      case 'LAST_MONTH':
        startDate = formatDateYMD(new Date(now.getFullYear(), now.getMonth() - 1, 1));
        endDate = formatDateYMD(new Date(now.getFullYear(), now.getMonth(), 0));
        break;
      case 'LAST_90_DAYS':
        const y90 = new Date(today);
        y90.setDate(y90.getDate() - 1); // Hier
        endDate = formatDateYMD(y90);
        const d90 = new Date(y90);
        d90.setDate(d90.getDate() - 89);
        startDate = formatDateYMD(d90);
        break;
      case 'THIS_YEAR':
        startDate = formatDateYMD(new Date(now.getFullYear(), 0, 1));
        endDate = formatDateYMD(today);
        break;
      case 'LAST_YEAR':
        startDate = formatDateYMD(new Date(now.getFullYear() - 1, 0, 1));
        endDate = formatDateYMD(new Date(now.getFullYear() - 1, 11, 31));
        break;
      default:
        startDate = endDate = formatDateYMD(today);
    }
    return { startDate, endDate };
  };

  // Fonction pour obtenir le label de la période sélectionnée
  const getPeriodLabel = () => {
    const periodLabels = {
      'TODAY': "Aujourd'hui",
      'YESTERDAY': 'Hier',
      'LAST_7_DAYS': '7 derniers jours',
      'LAST_14_DAYS': '14 derniers jours',
      'LAST_30_DAYS': '30 derniers jours',
      'THIS_MONTH': 'Ce mois',
      'LAST_MONTH': 'Mois dernier',
      'LAST_90_DAYS': '90 derniers jours',
      'THIS_YEAR': 'Année en cours',
      'LAST_YEAR': 'Année dernière',
    };
    if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
      const formatDisplayDate = (dateStr) => {
        const [y, m, d] = dateStr.split('-');
        return `${d}/${m}/${y}`;
      };
      return `${formatDisplayDate(customStartDate)} - ${formatDisplayDate(customEndDate)}`;
    }
    return periodLabels[selectedPeriod] || '7 derniers jours';
  };

  // Fonction pour obtenir le label de la période de comparaison
  const getComparisonLabel = () => {
    if (!globalCompareEnabled || !comparisonStartDate || !comparisonEndDate) return null;
    const formatDisplayDate = (dateStr) => {
      const [y, m, d] = dateStr.split('-');
      return `${d}/${m}/${y}`;
    };
    // Si les dates sont identiques, afficher une seule date
    if (comparisonStartDate === comparisonEndDate) {
      return `vs ${formatDisplayDate(comparisonStartDate)}`;
    }
    return `vs ${formatDisplayDate(comparisonStartDate)} - ${formatDisplayDate(comparisonEndDate)}`;
  };

  // Fonction pour ouvrir le menu période et initialiser les dates
  const openPeriodMenu = () => {
    if (!isPeriodMenuOpen) {
      // Initialiser les valeurs temporaires avec la période actuelle
      if (selectedPeriod === 'CUSTOM') {
        setTempCustomStartDate(customStartDate);
        setTempCustomEndDate(customEndDate);
        setTempSelectedPeriod('CUSTOM');
      } else {
        const { startDate, endDate } = getPeriodDates(selectedPeriod);
        setTempCustomStartDate(startDate);
        setTempCustomEndDate(endDate);
        setTempSelectedPeriod(selectedPeriod);
      }
      // Initialiser les valeurs temporaires de comparaison
      setTempCompareEnabled(globalCompareEnabled);
      setTempComparisonType(globalComparisonType);
      setTempComparisonStartDate(comparisonStartDate);
      setTempComparisonEndDate(comparisonEndDate);
      // Réinitialiser le champ calendrier actif
      setActiveCalendarField(null);
    }
    setIsPeriodMenuOpen(!isPeriodMenuOpen);
  };

  // Fermer le menu période au clic extérieur
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (isPeriodMenuOpen && periodMenuRef.current && !periodMenuRef.current.contains(event.target)) {
        // Juste fermer le menu - les valeurs seront réinitialisées à la prochaine ouverture
        setIsPeriodMenuOpen(false);
        setActiveCalendarField(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isPeriodMenuOpen]);

  // Détecter si des changements ont été faits dans le sélecteur de période
  const hasPeriodChanges = React.useMemo(() => {
    // Changements de période
    if (tempSelectedPeriod && tempSelectedPeriod !== selectedPeriod) return true;
    if (!tempSelectedPeriod && selectedPeriod !== 'CUSTOM') {
      // On a sélectionné des dates personnalisées différentes
      const { startDate, endDate } = getPeriodDates(selectedPeriod);
      if (tempCustomStartDate !== startDate || tempCustomEndDate !== endDate) return true;
    }
    if (selectedPeriod === 'CUSTOM') {
      if (tempCustomStartDate !== customStartDate || tempCustomEndDate !== customEndDate) return true;
    }
    // Changements de comparaison
    if (tempCompareEnabled !== globalCompareEnabled) return true;
    if (tempCompareEnabled) {
      if (tempComparisonType !== globalComparisonType) return true;
      if (tempComparisonStartDate !== comparisonStartDate || tempComparisonEndDate !== comparisonEndDate) return true;
    }
    return false;
  }, [tempSelectedPeriod, selectedPeriod, tempCustomStartDate, tempCustomEndDate, customStartDate, customEndDate, tempCompareEnabled, globalCompareEnabled, tempComparisonType, globalComparisonType, tempComparisonStartDate, tempComparisonEndDate, comparisonStartDate, comparisonEndDate]);

  // États pour la comparaison de période (dashboard Shopping/Produits)
  // Type de comparaison restauré depuis l'URL. Les états par plateforme sont
  // synchronisés au clic sur « Appliquer » ; un rechargement ne repassait pas par
  // là et les laissait vides, si bien que les données se chargeaient sans
  // comparaison alors que le sélecteur, lui, l'affichait toujours active.
  const getInitialComparisonType = () => {
    const params = getUrlParams();
    return params.get('compare') === 'true'
      ? (params.get('compareType') || 'previous_period')
      : '';
  };

  const [comparisonType, setComparisonType] = useState(getInitialComparisonType); // '', 'previous_period', 'previous_year'
  const compareEnabled = comparisonType !== ''; // Actif dès qu'une période est sélectionnée

  // États pour la comparaison de période (dashboard Google Analytics 4)
  const [ga4ComparisonType, setGA4ComparisonType] = useState(getInitialComparisonType); // '', 'previous_period', 'previous_year'
  const ga4CompareEnabled = ga4ComparisonType !== '';

  // États pour la comparaison de période (dashboard Facebook Ads)
  const [fbComparisonType, setFbComparisonType] = useState(getInitialComparisonType); // '', 'previous_period', 'previous_year'
  const fbCompareEnabled = fbComparisonType !== '';

  // États pour la comparaison de période (dashboard Matomo)
  const [matomoComparisonType, setMatomoComparisonType] = useState(getInitialComparisonType); // '', 'previous_period', 'previous_year'
  const matomoCompareEnabled = matomoComparisonType !== '';

  // États pour la comparaison de période (dashboard Piano Analytics)
  const [pianoComparisonType, setPianoComparisonType] = useState(getInitialComparisonType); // '', 'previous_period', 'previous_year'
  const pianoCompareEnabled = pianoComparisonType !== '';

  // États pour la comparaison de période (dashboard Google Ads global)
  const [gadsComparisonType, setGadsComparisonType] = useState(getInitialComparisonType); // '', 'previous_period', 'previous_year'
  const gadsCompareEnabled = gadsComparisonType !== '';

  // États pour la comparaison de période (dashboard Microsoft Ads)
  const [msadsComparisonType, setMsadsComparisonType] = useState(getInitialComparisonType); // '', 'previous_period', 'previous_year'
  const msadsCompareEnabled = msadsComparisonType !== '';

  // `compareStart` / `compareEnd` ne sont écrits dans l'URL qu'en mode personnalisé.
  // Après un rechargement en « période précédente » ou « année précédente », ces
  // dates doivent donc être recalculées, faute de quoi l'étiquette « vs … » de la
  // barre latérale reste vide (elle exige les deux dates, cf. getComparisonLabel).
  useEffect(() => {
    if (!globalCompareEnabled || globalComparisonType === 'custom') return;

    const range = selectedPeriod === 'CUSTOM'
      ? { startDate: customStartDate, endDate: customEndDate }
      : getPeriodDates(selectedPeriod);
    if (!range || !range.startDate || !range.endDate) return;

    const comparison = calculateComparisonDates(range.startDate, range.endDate, globalComparisonType);
    if (!comparison) return;
    if (comparison.startDate !== comparisonStartDate) setComparisonStartDate(comparison.startDate);
    if (comparison.endDate !== comparisonEndDate) setComparisonEndDate(comparison.endDate);
  }, [globalCompareEnabled, globalComparisonType, selectedPeriod, customStartDate, customEndDate,
      comparisonStartDate, comparisonEndDate]);

  // État global pour le site sélectionné (restauré depuis l'URL)
  const [selectedSiteId, setSelectedSiteId] = useState(() => {
    const params = getUrlParams();
    return params.get('site') || 'all';
  });

  // État pour le fil d'Ariane (compte et modèle)
  const [breadcrumbAccountName, setBreadcrumbAccountName] = useState('');
  const [breadcrumbDashboardModel, setBreadcrumbDashboardModel] = useState(() => {
    // Extraire le modèle depuis le hash si on est sur le dashboard
    // Format: #dashboard/google/global ou #dashboard/google/search-terms
    if (window.location.hash.startsWith('#dashboard/')) {
      const hashSegments = window.location.hash.split('?')[0].replace('#', '').split('/');
      const model = hashSegments[2];
      if (model && ['global', 'comparatif', 'keywords', 'search-terms', 'landing-pages', 'performance-max', 'shopping', 'products-insights', 'youtube-ads', 'competition', 'cross-sales'].includes(model)) {
        return model;
      }
    }
    return 'global';
  });

  // État pour la sidebar rétractable
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarManuallyToggled, setSidebarManuallyToggled] = useState(false);

  /*
   * Le panneau d'analyse est-il ouvert ?
   *
   * La barre latérale s'y replie, pour lui rendre la largeur qu'il prend — sans toucher au
   * réglage de l'utilisateur. Le bouton « Analyse IA », lui, se cache depuis le dashboard, qui
   * tient déjà son propre état : il n'avait pas besoin de remonter jusqu'ici.
   *
   * **Pourquoi un second état et non `setSidebarCollapsed`.** Le panneau appelait d'abord ce
   * setter, et la barre se rouvrait aussitôt : l'effet de redimensionnement ci-dessous dépend de
   * `sidebarCollapsed` et appelle `handleResize()` dès qu'il rejoue, lequel déplie toute barre
   * repliée au-dessus de 1 200 px. Le panneau repliait, l'effet dépliait, dans le même tour.
   *
   * Un drapeau distinct lève la contradiction : la barre *paraît* repliée tant que le panneau est
   * ouvert, mais `sidebarCollapsed` — la préférence de l'utilisateur — n'est jamais modifié. À la
   * fermeture, elle revient d'elle-même à ce qu'il avait choisi, sans qu'on ait à mémoriser quoi
   * que ce soit.
   *
   * Exposé sur `window` comme l'est déjà le bouton d'analyse : ces composants sont voisins dans
   * le même fichier mais séparés par dix mille lignes, et les relier par un contexte dédié
   * demanderait de traverser toute la hiérarchie pour un seul booléen.
   */
  const [analyseOuverte, setAnalyseOuverte] = useState(false);

  useEffect(() => {
    window.setSidebarCollapsed = setSidebarCollapsed;
    window.sidebarRepliee = sidebarCollapsed;
    window.setAnalyseOuverte = setAnalyseOuverte;
  }, [sidebarCollapsed]);

  // État pour le panneau d'aide flottant
  const [isHelpPanelOpen, setIsHelpPanelOpen] = useState(false);

  // État pour l'outil actif dans la sidebar
  const [activeToolId, setActiveToolId] = useState(null);

  // État pour l'historique des diagnostics dans la sidebar
  const [diagnosticHistory, setDiagnosticHistory] = useState([]);
  const [diagnosticSelectedAccountId, setDiagnosticSelectedAccountId] = useState(null);
  const [diagnosticSelectedScanId, setDiagnosticSelectedScanId] = useState(null);
  const [diagnosticSubView, setDiagnosticSubView] = useState(null); // null = normal, 'checks' = gestion des checks

  // État pour l'onglet abonnement actif
  const [subscriptionTab, setSubscriptionTab] = useState('subscription');

  // État pour l'onglet admin actif (et ticket initial depuis URL)
  const [adminTab, setAdminTab] = useState(() => {
    // Si on arrive depuis un lien email ticket admin ou la page tickets
    if (window.location.hash.startsWith('#admin/tickets')) {
      return 'tickets';
    }
    // Extraire l'onglet depuis l'URL #admin/:tab
    const match = window.location.hash.match(/^#admin\/([a-zA-Z-]+)/);
    if (match) {
      return match[1];
    }
    return 'dashboard';
  });
  const [adminInitialTicketId, setAdminInitialTicketId] = useState(() => {
    // Extraire l'ID du ticket depuis le hash #admin/tickets/:id
    const match = window.location.hash.match(/^#admin\/tickets\/([a-zA-Z0-9-]+)$/);
    return match ? match[1] : null;
  });
  const [adminSelectedTicketId, setAdminSelectedTicketId] = useState(() => {
    // Extraire l'ID du ticket depuis le hash #admin/tickets/:id
    const match = window.location.hash.match(/^#admin\/tickets\/([a-zA-Z0-9-]+)$/);
    return match ? match[1] : null;
  });
  const [ticketsInitialTicketId] = useState(() => {
    // Extraire l'ID du ticket depuis le hash #tickets/:id (pour les utilisateurs)
    const match = window.location.hash.match(/^#tickets\/([a-zA-Z0-9-]+)$/);
    return match ? match[1] : null;
  });
  const [ticketsSelectedTicketId, setTicketsSelectedTicketId] = useState(() => {
    // Extraire l'ID du ticket depuis le hash #tickets/:id (pour les utilisateurs)
    const match = window.location.hash.match(/^#tickets\/([a-zA-Z0-9-]+)$/);
    return match ? match[1] : null;
  });

  // États pour le dashboard Matomo Analytics (restauré depuis l'URL)
  const [matomoSelectedSiteId, setMatomoSelectedSiteId] = useState(() => {
    const params = getUrlParams();
    return params.get('matomoSite') || null;
  });
  const [matomoSites, setMatomoSites] = useState([]);
  const [matomoDashboardData, setMatomoDashboardData] = useState(null);
  const [matomoLoading, setMatomoLoading] = useState(false);
  const [matomoGranularity, setMatomoGranularity] = useState('day');

  /*
   * La boutique PrestaShop du dashboard. Restaurée depuis l'URL comme ses voisines, pour qu'un
   * lien partagé rouvre la même boutique.
   */
  const [prestashopBoutiqueId, setPrestashopBoutiqueId] = useState(() => {
    const params = getUrlParams();
    return params.get('boutique') || null;
  });
  const [prestashopBoutiques, setPrestashopBoutiques] = useState([]);
  const [prestashopGranularity, setPrestashopGranularity] = useState('day');

  // États pour le dashboard Google Analytics 4 (restauré depuis l'URL)
  const [ga4SelectedPropertyId, setGA4SelectedPropertyId] = useState(() => {
    const params = getUrlParams();
    return params.get('property') || null;
  });
  const [ga4Properties, setGA4Properties] = useState([]);

  // Propriété analytique ouverte par la page d'accueil dans le dashboard consolidé.
  // `null` laisse la vue faire sa propre présélection (la propriété du site courant,
  // ou la seule disponible) plutôt que de rouvrir celle de la visite précédente.
  const [consolideSelectedPropertyId, setConsolideSelectedPropertyId] = useState(null);

  /**
   * Base de comparaison du dashboard consolidé.
   *
   * Portée ici et non dans la vue, pour la même raison que la période : changer de propriété
   * remonte le composant, et le réglage serait reperdu à chaque fois. `null` vaut « année
   * précédente, activée » — ce que le comparatif faisait avant d'être réglable.
   *
   * Elle ne rejoint pas `globalComparisonType` : celui-là pilote les dashboards de régie, avec
   * ses trois bases et son propre vocabulaire. Le consolidé en a deux de plus — période
   * précédente et opération commerciale — et les mêler obligerait les autres écrans à savoir
   * quoi faire d'une base qu'ils ne savent pas calculer.
   */
  const [consolideComparaison, setConsolideComparaison] = useState(null);

  /**
   * La ligne d'accueil dont on veut ouvrir un dashboard : `{ typeSource, siteId, nom }`.
   *
   * L'accueil ne tranche plus entre le dashboard de la solution et le consolidé — il portait
   * pour cela deux tableaux qui lisaient les mêmes chiffres par le même appel. Le choix se fait
   * maintenant sur un écran, après le clic, et cet état est ce qu'on lui transmet.
   */
  const [choixDashboard, setChoixDashboard] = useState(null);


  /**
   * Solution de mesure sur laquelle le dashboard consolidé est ouvert.
   *
   * On entre dans le consolidé **par une solution** — la barre latérale et le tableau de la
   * page d'accueil en nomment une —, et le sélecteur de propriété doit s'y tenir : proposer
   * les propriétés des trois sources après avoir cliqué « Piano Analytics » ferait ouvrir un
   * tableau qui n'a rien à voir avec l'entrée choisie. `null` les propose toutes, ce qui
   * reste le comportement d'une arrivée directe par l'URL.
   */
  const [consolideSelectedSource, setConsolideSelectedSource] = useState(null);
  const [ga4DashboardData, setGA4DashboardData] = useState(null);
  const [ga4Loading, setGA4Loading] = useState(false);
  const [ga4Granularity, setGA4Granularity] = useState('day');

  // États pour le dashboard Piano Analytics (restauré depuis l'URL)
  const [pianoSelectedSiteId, setPianoSelectedSiteId] = useState(() => {
    const params = getUrlParams();
    return params.get('pianoSite') || null;
  });
  const [pianoSites, setPianoSites] = useState([]);
  const [pianoDashboardData, setPianoDashboardData] = useState(null);
  const [pianoLoading, setPianoLoading] = useState(false);
  const [pianoGranularity, setPianoGranularity] = useState('day');

  // États pour les filtres de la vue Analyser
  const [analyzeFilterSite, setAnalyzeFilterSite] = useState('all');
  const [analyzeFilterAnalytics, setAnalyzeFilterAnalytics] = useState({ ga4: true, matomo: true });
  const [analyzeFilterPlatforms, setAnalyzeFilterPlatforms] = useState({ google: true, microsoft: true, facebook: true });
  const [analyzeFilterAccount, setAnalyzeFilterAccount] = useState('all');

  // Réinitialiser les filtres Analyser quand le client change
  useEffect(() => {
    setAnalyzeFilterSite('all');
    setAnalyzeFilterAccount('all');
  }, [selectedClientId]);

  // Filtrer les sites Matomo selon le client/site sélectionné
  /**
   * Les boutiques retenues par le filtre client/site en cours.
   *
   * ⚠️ **On ne compare que sur `id`, l'identifiant du compte.** `sourceId` vaut `1` chez presque
   * tout le monde — c'est l'`id_shop` d'une installation monoboutique — et `Site.accountIds`
   * contient encore des numéros nus hérités d'autres connecteurs. Comparer dessus ferait
   * apparaître sous un site toutes les boutiques du parc dont l'`id_shop` vaut 1.
   */
  const filteredPrestashopBoutiques = useMemo(() => {
    if (!prestashopBoutiques || prestashopBoutiques.length === 0) return [];

    const estClient = user && user.role === 'CLIENT';
    if (!selectedClientId && (!selectedSiteId || selectedSiteId === 'all') && !estClient) {
      return prestashopBoutiques;
    }

    const sitesRetenus = (userSites || []).filter(site => {
      if (selectedSiteId && selectedSiteId !== 'all') return site.id === selectedSiteId;
      if (selectedClientId) return site.clientId === selectedClientId;
      return true;
    });

    const autorises = new Set(sitesRetenus.flatMap(site => (site.accountIds || []).map(String)));
    return prestashopBoutiques.filter(b => autorises.has(String(b.id)));
  }, [prestashopBoutiques, selectedClientId, selectedSiteId, userSites, user]);

  /**
   * Les CMS qui ont au moins une boutique visible, avec leur logo et leurs comptes.
   *
   * ⚠️ **La barre latérale et l'écran de choix parcourent cette liste**, au lieu d'énumérer les
   * CMS un par un. Ajouter Shopify demande donc une entrée dans `CMS`, un logo ici, et une liste
   * de comptes — et la famille apparaît aux deux endroits sans qu'on y pense. C'est ce que les
   * trois solutions de mesure n'ont pas, et ce qui fait qu'y ajouter la quatrième demande de
   * retrouver une douzaine d'énumérations.
   */
  const cmsDisponibles = useMemo(() => {
    const logos = { PRESTASHOP: prestashopLogo };
    const comptes = { PRESTASHOP: filteredPrestashopBoutiques };
    return CMS
      .map(cms => ({ ...cms, logo: logos[cms.cle] || null, comptes: comptes[cms.cle] || [] }))
      .filter(cms => cms.comptes.length > 0)
      // ⚠️ Filtré à la source plutôt qu'à chacun de ses usages : la liste sert la barre
      // latérale, l'écran de choix et leurs `map` internes, et un garde par usage s'oublierait
      // sur le prochain.
      .filter(cms => plateformeOuverte(ECRAN_PAR_TYPE[cms.cle]));
  }, [filteredPrestashopBoutiques, plateformeOuverte]);

  const filteredMatomoSites = useMemo(() => {
    if (!matomoSites || matomoSites.length === 0) return [];

    // Debug: afficher les données pour diagnostiquer
    console.log('🔍 [MATOMO-FILTER] matomoSites:', JSON.stringify(matomoSites.map(s => ({ id: s.id, sourceId: s.sourceId, idSite: s.idSite, name: s.name })), null, 2));
    console.log('🔍 [MATOMO-FILTER] selectedClientId:', selectedClientId);
    console.log('🔍 [MATOMO-FILTER] selectedSiteId:', selectedSiteId);
    console.log('🔍 [MATOMO-FILTER] userSites:', JSON.stringify(userSites.map(s => ({ id: s.id, name: s.name, clientId: s.clientId, accountIds: s.accountIds })), null, 2));

    // Vérifier si l'utilisateur est un CLIENT
    const isClientUser = user && user.role === 'CLIENT';

    // Si aucun filtre client et pas un utilisateur CLIENT, retourner tous les sites
    if (!selectedClientId && (!selectedSiteId || selectedSiteId === 'all') && !isClientUser) {
      return matomoSites;
    }

    // Récupérer les sites utilisateur filtrés par client
    const clientSites = selectedClientId
      ? userSites.filter(site => site.clientId === selectedClientId)
      : userSites;

    console.log('🔍 [MATOMO-FILTER] clientSites:', JSON.stringify(clientSites.map(s => ({ name: s.name, accountIds: s.accountIds })), null, 2));

    const isMatomoSiteInAccountIds = fabriquerTestDeRattachement(
      'matomo', matomoSites, ['sourceId', 'idSite']
    );

    // Si un site spécifique est sélectionné
    if (selectedSiteId && selectedSiteId !== 'all') {
      const selectedUserSite = clientSites.find(s => (s._id || s.id) === selectedSiteId);
      if (selectedUserSite && selectedUserSite.accountIds) {
        return matomoSites.filter(matomoSite =>
          isMatomoSiteInAccountIds(matomoSite, selectedUserSite.accountIds)
        );
      }
      return [];
    }

    // Si seulement un client est sélectionné
    const allAccountIds = clientSites.reduce((acc, site) => {
      if (site.accountIds && Array.isArray(site.accountIds)) {
        return [...acc, ...site.accountIds];
      }
      return acc;
    }, []);

    if (allAccountIds.length === 0) return [];

    return matomoSites.filter(matomoSite =>
      isMatomoSiteInAccountIds(matomoSite, allAccountIds)
    );
  }, [matomoSites, selectedClientId, selectedSiteId, userSites, user]);

  // Filtrer les propriétés GA4 selon le client/site sélectionné
  const filteredGA4Properties = useMemo(() => {
    if (!ga4Properties || ga4Properties.length === 0) return [];

    // Debug: afficher les données pour diagnostiquer
    console.log('🔍 [GA4-FILTER] ga4Properties:', JSON.stringify(ga4Properties.map(p => ({ id: p.id, sourceId: p.sourceId, propertyId: p.propertyId, name: p.name })), null, 2));
    console.log('🔍 [GA4-FILTER] selectedClientId:', selectedClientId);
    console.log('🔍 [GA4-FILTER] selectedSiteId:', selectedSiteId);
    console.log('🔍 [GA4-FILTER] userSites:', JSON.stringify(userSites.map(s => ({ id: s.id, name: s.name, clientId: s.clientId, accountIds: s.accountIds })), null, 2));

    // Vérifier si l'utilisateur est un CLIENT
    const isClientUser = user && user.role === 'CLIENT';

    // Si aucun filtre client et pas un utilisateur CLIENT, retourner toutes les propriétés
    if (!selectedClientId && (!selectedSiteId || selectedSiteId === 'all') && !isClientUser) {
      return ga4Properties;
    }

    // Récupérer les sites utilisateur filtrés par client
    const clientSites = selectedClientId
      ? userSites.filter(site => site.clientId === selectedClientId)
      : userSites;

    console.log('🔍 [GA4-FILTER] clientSites:', JSON.stringify(clientSites.map(s => ({ name: s.name, accountIds: s.accountIds })), null, 2));

    // Fonction pour vérifier si une propriété GA4 est associée
    const isGA4PropertyInAccountIds = (ga4Property, accountIds) => {
      if (!accountIds || !Array.isArray(accountIds)) return false;

      return accountIds.some(accountId => {
        const accountIdStr = String(accountId);
        return (
          accountIdStr === ga4Property.id ||
          accountIdStr === ga4Property.sourceId ||
          accountIdStr === String(ga4Property.sourceId) ||
          accountIdStr === ga4Property.propertyId ||
          accountIdStr === String(ga4Property.propertyId)
        );
      });
    };

    // Si un site spécifique est sélectionné
    if (selectedSiteId && selectedSiteId !== 'all') {
      const selectedUserSite = clientSites.find(s => (s._id || s.id) === selectedSiteId);
      if (selectedUserSite && selectedUserSite.accountIds) {
        return ga4Properties.filter(ga4Property =>
          isGA4PropertyInAccountIds(ga4Property, selectedUserSite.accountIds)
        );
      }
      return [];
    }

    // Si seulement un client est sélectionné
    const allAccountIds = clientSites.reduce((acc, site) => {
      if (site.accountIds && Array.isArray(site.accountIds)) {
        return [...acc, ...site.accountIds];
      }
      return acc;
    }, []);

    console.log('🔍 [GA4-FILTER] allAccountIds:', allAccountIds);

    if (allAccountIds.length === 0) {
      console.log('🔍 [GA4-FILTER] Aucun accountId trouvé, retour tableau vide');
      return [];
    }

    const filtered = ga4Properties.filter(ga4Property =>
      isGA4PropertyInAccountIds(ga4Property, allAccountIds)
    );
    console.log('🔍 [GA4-FILTER] Propriétés GA4 filtrées:', filtered.length);
    return filtered;
  }, [ga4Properties, selectedClientId, selectedSiteId, userSites, user]);

  // Filtrer les sites Piano selon le client/site sélectionné
  const filteredPianoSites = useMemo(() => {
    console.log('🎹 [Piano-Filter] pianoSites:', pianoSites?.length || 0, pianoSites);
    console.log('🎹 [Piano-Filter] selectedSiteId:', selectedSiteId, 'selectedClientId:', selectedClientId);

    if (!pianoSites || pianoSites.length === 0) return [];

    // Vérifier si l'utilisateur est un CLIENT
    const isClientUser = user && user.role === 'CLIENT';

    // Si aucun filtre client et pas un utilisateur CLIENT, retourner tous les sites
    if (!selectedClientId && (!selectedSiteId || selectedSiteId === 'all') && !isClientUser) {
      console.log('🎹 [Piano-Filter] Pas de filtre, retourne tous:', pianoSites.length);
      return pianoSites;
    }

    // Récupérer les sites utilisateur filtrés par client
    const clientSites = selectedClientId
      ? userSites.filter(site => site.clientId === selectedClientId)
      : userSites;

    // Fonction pour vérifier si un site Piano est associé
    // Même piège que Matomo, même correctif : voir `fabriquerTestDeRattachement`.
    const isPianoSiteInAccountIds = fabriquerTestDeRattachement(
      'piano', pianoSites, ['sourceId', 'siteId']
    );

    // Si un site spécifique est sélectionné
    if (selectedSiteId && selectedSiteId !== 'all') {
      const selectedUserSite = clientSites.find(s => (s._id || s.id) === selectedSiteId);
      console.log('🎹 [Piano-Filter] Site sélectionné:', selectedUserSite?.name, 'accountIds:', selectedUserSite?.accountIds);
      if (selectedUserSite && selectedUserSite.accountIds) {
        const filtered = pianoSites.filter(pianoSite => {
          const connId = pianoSite.connectionId || 'default';
          const siteId = pianoSite.sourceId || pianoSite.siteId || pianoSite.id;
          const expectedId = `piano-${connId}-${siteId}`;
          console.log('🎹 [Piano-Filter] Comparing:', expectedId, 'vs accountIds');
          return isPianoSiteInAccountIds(pianoSite, selectedUserSite.accountIds);
        });
        console.log('🎹 [Piano-Filter] Résultat filtrage:', filtered.length);
        return filtered;
      }
      console.log('🎹 [Piano-Filter] Pas de site ou accountIds vide');
      return [];
    }

    // Si seulement un client est sélectionné
    const allAccountIds = clientSites.reduce((acc, site) => {
      if (site.accountIds && Array.isArray(site.accountIds)) {
        return [...acc, ...site.accountIds];
      }
      return acc;
    }, []);

    if (allAccountIds.length === 0) return [];

    return pianoSites.filter(pianoSite =>
      isPianoSiteInAccountIds(pianoSite, allAccountIds)
    );
  }, [pianoSites, selectedClientId, selectedSiteId, userSites, user]);

  // Sauvegarder le hash au montage si l'utilisateur n'est pas connecté
  // Cela permet de restaurer la navigation après login (liens email)
  useEffect(() => {
    const savedToken = localStorage.getItem('kokpeet_token');
    const currentHash = window.location.hash;

    // Si pas de token et qu'il y a un hash pertinent, le sauvegarder
    if (!savedToken && currentHash && currentHash !== '#') {
      // Ne pas écraser si déjà sauvegardé
      if (!localStorage.getItem('kokpeet_returnHash')) {
        console.log('[AUTH] Sauvegarde automatique du hash au montage:', currentHash);
        localStorage.setItem('kokpeet_returnHash', currentHash);
      }
    }
  }, []); // Exécuté une seule fois au montage

  // Refs pour tracker le site et client précédents (Matomo)
  const matomoPrevSiteIdRef = useRef(selectedSiteId);
  const matomoPrevClientIdRef = useRef(selectedClientId);

  // Effacer les données Matomo quand le site/client change
  useEffect(() => {
    const siteChanged = matomoPrevSiteIdRef.current !== selectedSiteId;
    const clientChanged = matomoPrevClientIdRef.current !== selectedClientId;
    matomoPrevSiteIdRef.current = selectedSiteId;
    matomoPrevClientIdRef.current = selectedClientId;

    if (siteChanged || clientChanged) {
      console.log('🔄 [MATOMO] Changement de site/client détecté:', { selectedSiteId, selectedClientId });
      setMatomoDashboardData(null);

      // Vérifier si le site Matomo sélectionné est toujours valide
      if (matomoSelectedSiteId && filteredMatomoSites.length > 0) {
        const currentSiteStillValid = filteredMatomoSites.some(site => site.id === matomoSelectedSiteId);
        if (!currentSiteStillValid) {
          console.log('🔄 [MATOMO] Réinitialisation du site sélectionné (non valide pour ce client)');
          setMatomoSelectedSiteId(null);
        }
      }
    }
  }, [selectedClientId, selectedSiteId, matomoSelectedSiteId, filteredMatomoSites]);

  // Refs pour tracker le site et client précédents (GA4)
  const ga4PrevSiteIdRef = useRef(selectedSiteId);
  const ga4PrevClientIdRef = useRef(selectedClientId);

  // Effacer les données GA4 quand le site/client change
  useEffect(() => {
    const siteChanged = ga4PrevSiteIdRef.current !== selectedSiteId;
    const clientChanged = ga4PrevClientIdRef.current !== selectedClientId;
    ga4PrevSiteIdRef.current = selectedSiteId;
    ga4PrevClientIdRef.current = selectedClientId;

    if (siteChanged || clientChanged) {
      console.log('🔄 [GA4] Changement de site/client détecté:', { selectedSiteId, selectedClientId });
      setGA4DashboardData(null);

      // Vérifier si la propriété GA4 sélectionnée est toujours valide
      if (ga4SelectedPropertyId && filteredGA4Properties.length > 0) {
        const currentPropertyStillValid = filteredGA4Properties.some(prop => prop.id === ga4SelectedPropertyId);
        if (!currentPropertyStillValid) {
          console.log('🔄 [GA4] Réinitialisation de la propriété sélectionnée (non valide pour ce client)');
          setGA4SelectedPropertyId(null);
        }
      }
    }
  }, [selectedClientId, selectedSiteId, ga4SelectedPropertyId, filteredGA4Properties]);

  // Écouter les changements de compte dans Diagnostic pour mettre à jour l'historique
  useEffect(() => {
    const handleDiagnosticAccountChange = async (event) => {
      const { accountId } = event.detail;
      setDiagnosticSelectedAccountId(accountId);

      if (accountId && token) {
        try {
          const response = await fetch(`/api/diagnostic/${accountId}/history?limit=10`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const data = await response.json();
          if (data.success) {
            setDiagnosticHistory(data.data || []);
          }
        } catch (err) {
          console.error('Erreur chargement historique diagnostic:', err);
        }
      } else {
        setDiagnosticHistory([]);
      }
    };

    window.addEventListener('diagnosticAccountChange', handleDiagnosticAccountChange);
    return () => window.removeEventListener('diagnosticAccountChange', handleDiagnosticAccountChange);
  }, [token]);

  // Réinitialiser l'historique diagnostic quand on quitte la vue
  useEffect(() => {
    const isInDiagnosticMode = currentView === 'diagnostic' ||
      (currentView === 'tools' && window.location.hash.includes('#tools/diagnostic'));

    if (!isInDiagnosticMode) {
      setDiagnosticHistory([]);
      setDiagnosticSelectedAccountId(null);
    }
  }, [currentView]);

  // Auto-collapse sidebar quand la fenêtre est redimensionnée
  useEffect(() => {
    const COLLAPSE_THRESHOLD = 1200; // px

    const handleResize = () => {
      // Ne pas auto-collapse si l'utilisateur a manuellement toggle
      if (sidebarManuallyToggled) return;
      // Ni défaire le repli imposé par le panneau d'analyse : cet effet rejoue à chaque
      // changement de `sidebarCollapsed`, et dépliait la barre dans le tour suivant.
      if (analyseOuverte) return;

      if (window.innerWidth < COLLAPSE_THRESHOLD && !sidebarCollapsed) {
        setSidebarCollapsed(true);
      } else if (window.innerWidth >= COLLAPSE_THRESHOLD && sidebarCollapsed) {
        setSidebarCollapsed(false);
      }
    };

    // Check initial size
    handleResize();

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [sidebarCollapsed, sidebarManuallyToggled, analyseOuverte]);

  // Réinitialiser le compte sélectionné et le breadcrumb quand on change de plateforme
  useEffect(() => {
    setSidebarSelectedAccountId('');
    setBreadcrumbAccountName('');
    setBreadcrumbDashboardModel('global');
  }, [currentDashboardType]);

  // Réinitialiser les données utilisateur quand l'utilisateur change (login avec un autre compte)
  const prevUserIdRef = useRef(user?.id);
  useEffect(() => {
    const prevUserId = prevUserIdRef.current;
    const currentUserId = user?.id;

    // Si l'utilisateur a changé (et qu'il y avait un utilisateur avant)
    if (prevUserId && currentUserId && prevUserId !== currentUserId) {
      console.log('🔄 [USER CHANGE] Réinitialisation des données - ancien:', prevUserId, '-> nouveau:', currentUserId);
      setMatomoSites([]);
      setGA4Properties([]);
      setPianoSites([]);
      setUserSites([]);
      setAllAdAccounts([]);
      setUserOrganizations([]);
      setOrganizationsLoaded(false);
    }

    prevUserIdRef.current = currentUserId;
  }, [user?.id]);

  // Réinitialiser le breadcrumb quand on change de site
  useEffect(() => {
    if (selectedSiteId) {
      setSidebarSelectedAccountId('');
      setBreadcrumbAccountName('');
      console.log('🔄 [BREADCRUMB] Réinitialisation lors du changement de site');
    }
  }, [selectedSiteId]);

  // Réinitialiser le compte sélectionné quand on change de client.
  // Le premier passage de l'effet n'est pas un changement de client : s'y fier
  // effaçait le paramètre `account` de l'URL à chaque rechargement de page.
  const previousClientIdRef = useRef(undefined);
  useEffect(() => {
    if (previousClientIdRef.current === undefined) {
      previousClientIdRef.current = selectedClientId;
      return;
    }
    if (previousClientIdRef.current === selectedClientId) return;
    previousClientIdRef.current = selectedClientId;

    setSidebarSelectedAccountId('');
    setBreadcrumbAccountName('');
    // Réinitialiser aussi le compte présélectionné
    if (window.setPreselectedAccountId) {
      window.setPreselectedAccountId(null);
    }
    // Nettoyer le localStorage de navigation
    localStorage.removeItem('kokpeet_selectedAccount');
    localStorage.removeItem('kokpeet_targetTab');
    // Nettoyer le paramètre account de l'URL (cas legacy)
    const hash = window.location.hash;
    if (hash.includes('account=')) {
      const [hashPath, queryString] = hash.split('?');
      if (queryString) {
        const params = new URLSearchParams(queryString);
        params.delete('account');
        const newQuery = params.toString();
        const newHash = newQuery ? `${hashPath}?${newQuery}` : hashPath;
        window.history.replaceState(null, '', newHash);
      }
    }
  }, [selectedClientId]);

  // Écouter les sélections de compte depuis le dashboard pour synchroniser la sidebar et le breadcrumb
  useEffect(() => {
    const handleAccountSelected = (event) => {
      const { accountId, accountName } = event.detail;
      setSidebarSelectedAccountId(accountId || '');
      setBreadcrumbAccountName(accountName || '');
    };
    window.addEventListener('accountSelected', handleAccountSelected);
    return () => window.removeEventListener('accountSelected', handleAccountSelected);
  }, []);

  // Écouter les ouvertures d'outils pour mettre à jour l'état actif
  useEffect(() => {
    const handleOpenTool = (event) => {
      const { toolId } = event.detail;
      setActiveToolId(toolId || null);
    };
    window.addEventListener('openTool', handleOpenTool);
    return () => window.removeEventListener('openTool', handleOpenTool);
  }, []);

  // Réinitialiser l'outil actif quand on quitte la vue tools
  useEffect(() => {
    if (currentView !== 'tools') {
      setActiveToolId(null);
    }
  }, [currentView]);

  // Synchroniser diagnosticSelectedAccountId depuis l'URL quand dans tools/diagnostic
  useEffect(() => {
    const syncDiagnosticAccountFromUrl = () => {
      const hash = window.location.hash;
      if (currentView === 'tools' && hash.includes('#tools/diagnostic')) {
        const params = new URLSearchParams(hash.split('?')[1] || '');
        const accountId = params.get('account');
        if (accountId && accountId !== diagnosticSelectedAccountId) {
          setDiagnosticSelectedAccountId(accountId);
        } else if (!accountId && diagnosticSelectedAccountId) {
          setDiagnosticSelectedAccountId(null);
        }
      }
    };

    syncDiagnosticAccountFromUrl();
    window.addEventListener('hashchange', syncDiagnosticAccountFromUrl);
    return () => window.removeEventListener('hashchange', syncDiagnosticAccountFromUrl);
  }, [currentView, diagnosticSelectedAccountId]);

  // Écouter les changements de modèle de dashboard pour le breadcrumb
  useEffect(() => {
    const handleModelChange = (event) => {
      const { model } = event.detail;
      setBreadcrumbDashboardModel(model || 'global');
    };
    window.addEventListener('dashboardModelChange', handleModelChange);
    return () => window.removeEventListener('dashboardModelChange', handleModelChange);
  }, []);

  // Écouter l'événement changeView pour changer de vue depuis les composants enfants
  useEffect(() => {
    const handleChangeView = (event) => {
      const view = event.detail;
      if (view) {
        setCurrentView(view);
      }
    };
    window.addEventListener('changeView', handleChangeView);
    return () => window.removeEventListener('changeView', handleChangeView);
  }, []);

  // Écouter l'événement openConversation pour ouvrir une conversation existante
  useEffect(() => {
    const handleOpenConversation = (event) => {
      const { conversationId, accountId, accountName } = event.detail;
      if (conversationId) {
        // Stocker les données de la conversation à ouvrir
        setOpenConversationData({
          conversationId,
          accountId,
          accountName
        });
        // Naviguer vers la vue analyze avec le chat ouvert
        setCurrentView('analyze');
      }
    };
    window.addEventListener('openConversation', handleOpenConversation);
    return () => window.removeEventListener('openConversation', handleOpenConversation);
  }, []);

  // Fonction pour changer de client avec gestion de l'historique
  const setSelectedClientIdWithHistory = useCallback((clientId, skipHistory = false) => {
    setSelectedClientId(clientId);
    // Réinitialiser le site sélectionné quand on change de client
    setSelectedSiteId('all');
    // Garder la vue actuelle (ne pas rediriger vers home)
    // currentView est déjà défini, on ne le change pas
    if (!skipHistory) {
      const clientParam = clientId ? `?client=${clientId}` : '';
      // Utiliser la vue actuelle depuis le hash ou 'home' par défaut
      const currentHash = window.location.hash.split('?')[0].replace('#', '') || 'home';
      const historyState = { clientId, view: currentHash };
      window.history.pushState(historyState, '', `#${currentHash}${clientParam}`);
    }
  }, []);

  // Écouter le bouton back/forward pour restaurer le client
  useEffect(() => {
    const handlePopStateForClient = (event) => {
      if (event.state && event.state.clientId !== undefined) {
        setSelectedClientId(event.state.clientId);
      } else {
        // Vérifier dans l'URL si un client est spécifié
        const urlParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
        const clientFromUrl = urlParams.get('client');
        setSelectedClientId(clientFromUrl || null);
      }
    };
    window.addEventListener('popstate', handlePopStateForClient);
    return () => window.removeEventListener('popstate', handlePopStateForClient);
  }, []);
  
  // Optimisations de performance pour le dropdown utilisateur
  const isNotClientUser = useMemo(() => user?.role !== 'CLIENT', [user?.role]);
  const canAccessClients = useMemo(() =>
    user?.role === 'ADMIN' || ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription?.plan),
    [user?.role, userSubscription?.plan]
  );

  // Vérifie si l'utilisateur est membre d'une organisation (accès Tools/Diagnostic)
  const hasOrganization = userOrganizations.length > 0;

  /**
   * Qui voit le serveur MCP : les abonnés au titre de leur plan, et les accès clients à qui
   * leur agence l'a ouvert — eux n'ont pas d'abonnement, la condition de plan les exclurait.
   *
   * Dérivée une fois et consommée deux : le bouton et la vue portaient la même condition,
   * recopiée. Les laisser diverger, c'est un bouton qui mène à une page blanche.
   */
  const mcpOuvert = useMemo(() => Boolean(
    user && (
      user.role === 'ADMIN'
      || clientTools.includes('mcp')
      || ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription?.plan)
    )
  ), [user, clientTools, userSubscription?.plan]);

  /**
   * Les types de source que le MCP sert réellement pour le client filtré.
   *
   * ⚠️ **`null` n'est pas un ensemble vide.** « On ne sait pas encore » — aucun client filtré,
   * requête en vol, requête refusée — ne se traite pas comme « ce client n'a rien » : le second
   * grise, le premier ne doit rien dire. Une liste de connecteurs qui s'éteindrait pendant son
   * chargement affirmerait un manque qu'aucune réponse n'a établi, et le repli du `catch`
   * reste donc « on ne sait pas » plutôt que « rien n'est branché ».
   *
   * La source est `GET /api/mcp/sources`, et pas un décompte reconstitué ici : c'est la **même**
   * porte que le connecteur interroge, donc le même verdict. Elle seule sait lire les trois
   * écritures d'un rattachement (`utils/rattachementComptes.js`) et tient compte d'un compte
   * débrayé du MCP (`actifMcp`) — un calcul maison divergerait sans que rien ne le signale.
   */
  const [typesMcpDuClient, setTypesMcpDuClient] = useState(null);

  useEffect(() => {
    if (!token || !mcpOuvert || currentView !== 'mcp' || !selectedClientId) {
      setTypesMcpDuClient(null);
      return;
    }

    let abandonne = false;
    // `compact` : seuls les types nous intéressent, et la forme complète porte les métadonnées
    // de chaque source — plusieurs dizaines de clés pour un conteneur Tag Manager.
    axios.get(`${API_URL}/api/mcp/sources`, {
      headers: { Authorization: `Bearer ${token}` },
      params: { client: selectedClientId, compact: true }
    })
      .then(reponse => {
        if (abandonne) return;
        const sources = reponse.data?.sources;
        if (!Array.isArray(sources)) return;
        setTypesMcpDuClient(new Set(sources.map(source => source.type)));
      })
      .catch(erreur => {
        if (abandonne) return;
        console.error('❌ [MCP] Sources du client illisibles:', erreur);
        setTypesMcpDuClient(null);
      });

    return () => { abandonne = true; };
  }, [token, mcpOuvert, currentView, selectedClientId]);

  /** Le catalogue jugé et rangé : les connecteurs branchés en tête. */
  const connecteursMcpOrdonnes = useMemo(
    () => ordonnerConnecteursMcp(typesMcpDuClient),
    [typesMcpDuClient]
  );

  /**
   * Radar est réservé aux administrateurs Kokpit, le temps du rodage.
   *
   * `user.role === 'ADMIN'` et rien d'autre : le rôle porté dans une organisation ne compte
   * pas ici. Un OWNER d'agence qui n'est pas administrateur Kokpit ne voit donc pas Radar, pas
   * plus qu'un membre. L'écran s'ouvrira aux organisations quand ses recommandations auront été
   * éprouvées sur le parc — d'ici là, mieux vaut qu'une reco douteuse soit vue par ceux qui
   * peuvent la corriger.
   *
   * Dérivée une fois et consommée deux, pour la raison donnée au-dessus de `mcpOuvert` : un
   * bouton et une vue qui divergent, c'est un onglet qui mène à une page blanche.
   */
  const radarOuvert = useMemo(() => Boolean(user && user.role === 'ADMIN'), [user]);

  /*
   * Le filtre de plateforme de Radar vit ici, et non dans `RadarView`.
   *
   * La barre latérale et les pastilles de l'écran commandent le **même** filtre : il faut donc
   * qu'un seul état les porte, sinon l'une resterait allumée quand l'autre se relâche. Radar
   * remonte les plateformes qu'il a réellement vues (`plateformesRadar`) — la barre n'en propose
   * aucune qui viderait la liste, contrairement aux plateformes *connectées* de la section de
   * navigation juste en dessous.
   */
  const [plateformesRadar, setPlateformesRadar] = useState({});
  const [plateformeRadar, setPlateformeRadar] = useState(null);

  /* Stable : `RadarView` la met dans les dépendances de son chargement. */
  const noterPlateformesRadar = useCallback((presentes) => {
    setPlateformesRadar(actuelles => {
      const memes = Object.keys(presentes).length === Object.keys(actuelles).length
        && Object.keys(presentes).every(cle => actuelles[cle] === presentes[cle]);
      return memes ? actuelles : presentes;
    });
  }, []);

  /**
   * Budgets est fermé aux accès clients, et à eux seuls.
   *
   * Un budget mis face à une dépense laisse deviner ce qui reste sur l'enveloppe : c'est une
   * information d'agence. Les membres d'organisation y entrent, contrairement à Radar — l'écran
   * ne porte aucune recommandation à éprouver, seulement des chiffres déjà connus de ceux qui
   * les ont saisis.
   *
   * Dérivée une fois et consommée trois — le bouton, la redirection d'URL et le rendu —, pour la
   * raison donnée au-dessus de `mcpOuvert`.
   */
  const budgetsOuvert = useMemo(() => Boolean(user && user.role !== 'CLIENT'), [user]);

  // Vérifie si l'utilisateur a un abonnement valide (SOLO, TEAM, AGENCY, SCALE)
  const hasValidSubscription = useMemo(() => {
    if (!user) return false;
    if (user.role === 'ADMIN') return true; // Les admins ont toujours accès
    if (user.role === 'CLIENT') return true; // Les clients ont accès via leur invitation
    // Membres d'organisation : vérifient si l'org a un plan valide
    if (userOrganizations.length > 0 && userOrganizations.some(org => ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(org.plan))) {
      return true;
    }
    // Essai gratuit en cours : ouvert à la création du compte, sans carte. Le huitième jour,
    // `trialEndsAt` est dépassé, la condition redevient fausse et la redirection vers les
    // formules reprend d'elle-même — rien à planifier. Voir backend/services/essaiGratuit.js.
    if (userSubscription?.status === 'TRIALING' && userSubscription.trialEndsAt
        && new Date(userSubscription.trialEndsAt).getTime() > Date.now()) {
      return true;
    }
    // Vérifie l'abonnement personnel
    return userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan) && userSubscription.status === 'ACTIVE';
  }, [user, userSubscription, userOrganizations]);

  // Vérifie si l'utilisateur est owner d'une organisation (ou n'appartient à aucune org)
  const isOrgOwnerOrNoOrg = useMemo(() => {
    return userOrganizations.length === 0 || userOrganizations.some(org => org.role === 'OWNER');
  }, [userOrganizations]);

  // Redirection vers pricing si pas d'abonnement valide
  useEffect(() => {
    // Attendre que les organisations soient chargées avant de décider
    if (!organizationsLoaded) return;

    /**
     * Le parcours d'installation n'est pas concerné.
     *
     * Sans cette garde, un compte en cours d'installation était happé vers l'écran des
     * formules au premier rendu — `userSubscription` arrive d'un appel réseau, et vaut `null`
     * le temps qu'il réponde, ce qui suffit à rendre `hasValidSubscription` faux. Le parcours
     * s'effaçait alors au retour de connexion d'un connecteur, juste avant l'étape où l'on
     * choisit son compte.
     *
     * C'est aussi ce qui rend sûre l'exception `pricing` du verrou plus bas : la vue ne peut
     * plus y arriver d'elle-même pendant le parcours, seulement sur demande explicite.
     */
    if (user && user.role !== 'CLIENT' && user.onboardingTermineAt === null) return;

    // Ne pas rediriger si on est sur une page d'invitation ou d'aide
    const excludedViews = ['pricing', 'home', 'join-organization', 'join-team', 'help', 'legal'];
    if (user && !hasValidSubscription && !excludedViews.includes(currentView)) {
      // Si l'utilisateur est membre d'une organisation (mais pas owner), rediriger vers home, pas pricing
      const isOrgMemberNotOwner = userOrganizations.length > 0 && !userOrganizations.some(org => org.role === 'OWNER');
      if (isOrgMemberNotOwner) {
        console.log('[AUTH] Membre d\'organisation sans droits billing - redirection vers home');
        setCurrentView('home');
        window.history.replaceState({}, document.title, window.location.pathname + '#home');
      } else {
        console.log('[AUTH] Pas d\'abonnement valide - redirection vers pricing');
        setCurrentView('pricing');
        // L'écran Abonnement porte désormais les formules : plus d'onglet « Plans » distinct.
        setSubscriptionTab('subscription');
        window.history.replaceState({}, document.title, window.location.pathname + '#pricing');
      }
    }
  }, [user, hasValidSubscription, currentView, organizationsLoaded, userOrganizations]);

  // Protéger les pages réservées aux owners - rediriger les membres non-owner
  useEffect(() => {
    // Attendre aussi les outils accordés : sans cela, un accès client qui arrive
    // directement sur #tools est renvoyé vers #home avant que ses droits soient connus.
    if (!organizationsLoaded || !clientToolsLoaded || !user) return;

    // Pages accessibles uniquement aux owners (pas aux membres)
    const ownerOnlyViews = ['pricing'];
    const isOrgMemberNotOwner = userOrganizations.length > 0 && !userOrganizations.some(org => org.role === 'OWNER');

    if (isOrgMemberNotOwner && ownerOnlyViews.includes(currentView)) {
      console.log('[AUTH] Accès refusé à', currentView, '- membre sans droits owner');
      setCurrentView('home');
      window.history.replaceState({}, document.title, window.location.pathname + '#home');
    }

    // Pages accessibles aux ADMINS ou membres d'organisations
    /**
     * Radar sort de `orgMemberViews` : il est réservé aux administrateurs, pas ouvert aux
     * membres d'organisation. Une liste à part plutôt qu'une condition ajoutée dans la
     * précédente — deux règles différentes, deux listes.
     */
    if (user.role !== 'ADMIN' && currentView === 'radar') {
      console.log('[AUTH] Accès refusé à radar - réservé aux administrateurs');
      setCurrentView('home');
      window.history.replaceState({}, document.title, window.location.pathname + '#home');
    }

    // Budgets : fermé aux seuls accès clients. Sans ce garde, un client ouvrant l'URL resterait
    // sur une zone vide plutôt que d'être renvoyé — l'API lui répondant 403.
    if (user.role === 'CLIENT' && currentView === 'budgets') {
      console.log('[AUTH] Accès refusé à budgets - fermé aux accès client');
      setCurrentView('home');
      window.history.replaceState({}, document.title, window.location.pathname + '#home');
    }

    const orgMemberViews = ['diagnostic', 'tools'];
    const canAccessOrgViews = user.role === 'ADMIN' || userOrganizations.length > 0;

    // Un accès client n'est membre d'aucune organisation : c'est le droit par outil, et
    // lui seul, qui lui ouvre la vue Outils. Le diagnostic lui reste fermé.
    // Compter les droits du **catalogue**, et non tous les droits accordés : le consolidé et
    // le MCP s'accordent ici mais s'ouvrent ailleurs. Un client n'ayant qu'eux se voyait offrir
    // un onglet Outils que la barre latérale, elle, remplissait avec rien.
    const outilsOuvertsAuClient = currentView === 'tools' && outilsDuCatalogue(clientTools).length > 0;

    if (!canAccessOrgViews && !outilsOuvertsAuClient && orgMemberViews.includes(currentView)) {
      console.log('[AUTH] Accès refusé à', currentView, '- pas admin ni membre d\'organisation');
      setCurrentView('home');
      window.history.replaceState({}, document.title, window.location.pathname + '#home');
    }

    // Les Notes sont fermées aux accès client, comme le dit déjà leur condition de rendu.
    // Sans ce garde, un client ouvrant l'URL n'aurait pas été renvoyé : il serait resté sur
    // une zone de contenu vide, l'écran n'ayant rien à afficher pour lui.
    //
    // Le plan d'abonnement n'est pas retesté ici : la redirection d'abonnement, plus haut,
    // couvre déjà le cas et attend, elle, que les données soient chargées. Le refaire à partir
    // de `userSubscription` — nul tant qu'il n'est pas revenu — renverrait un utilisateur
    // légitime vers l'accueil pendant le chargement.
    const vuesNotes = ['notes-archive', 'notes-calendrier', 'notes-import'];
    if (user.role === 'CLIENT' && vuesNotes.includes(currentView)) {
      console.log('[AUTH] Accès refusé à', currentView, '- les notes sont fermées aux accès client');
      setCurrentView('home');
      window.history.replaceState({}, document.title, window.location.pathname + '#home');
    }

    /*
     * La fiche d'un accès client est un écran d'agence : elle décide ce qu'un client voit.
     *
     * Sans ce garde, un client — ou un membre sans organisation — qui taperait l'adresse
     * resterait devant une zone de contenu vide, l'écran n'ayant rien à lui afficher. Le
     * serveur refuse déjà la route en 403 ; un bandeau rouge se lit comme une panne alors que
     * rien n'est cassé.
     */
    if (!canAccessOrgViews && currentView === 'acces-client') {
      console.log('[AUTH] Accès refusé à acces-client - pas admin ni membre d\'organisation');
      setCurrentView('home');
      window.history.replaceState({}, document.title, window.location.pathname + '#home');
    }

    /*
     * Un dashboard de plateforme fermé n'est pas atteignable par l'URL non plus.
     *
     * ⚠️ **Masquer les entrées ne suffit pas** : une adresse recopiée, un favori, le bouton
     * précédent y mènent encore, et l'écran resterait vide pendant que le serveur refuse ses
     * requêtes en 403 — ce qui se lit comme une panne alors que c'est un réglage. C'est la
     * règle des deux barrières, appliquée à la navigation.
     *
     * La vue `dashboard` porte sa plateforme dans `currentDashboardType` ; les quatre vues de
     * mesure sont elles-mêmes l'identifiant.
     */
    const ecranDeDashboard = currentView === 'dashboard'
      ? (currentDashboardType !== 'selection' ? currentDashboardType : null)
      : (IDS_DASHBOARDS_DE_PLATEFORME.includes(currentView) ? currentView : null);

    if (ecranDeDashboard && !plateformeOuverte(ecranDeDashboard)) {
      console.log('[AUTH] Accès refusé à', ecranDeDashboard, '- ce dashboard a été fermé par l\'agence');
      setCurrentView('home');
      window.history.replaceState({}, document.title, window.location.pathname + '#home');
    }

    // Page admin accessible uniquement aux ADMINS
    if (user.role !== 'ADMIN' && currentView === 'admin') {
      console.log('[AUTH] Accès refusé à admin - pas admin');
      setCurrentView('home');
      window.history.replaceState({}, document.title, window.location.pathname + '#home');
    }
  }, [currentView, organizationsLoaded, userOrganizations, user, clientTools, clientToolsLoaded,
      currentDashboardType, plateformeOuverte]);

  /**
   * Les régies publicitaires que ce périmètre porte réellement.
   *
   * ⚠️ **Vide au départ, et non « tout est là ».** L'état initial affirmait `google`,
   * `microsoft` et `facebook-ads` avant le moindre appel : le bouton « Dashboards » et les
   * entrées de la barre latérale s'affichaient donc à tout le monde, puis disparaissaient
   * quand la réponse arrivait. Sur un accès client qui n'a aucune régie, c'est tout le bloc
   * Publicité qui apparaissait puis s'effaçait — en lui montrant au passage des noms de
   * régies qui ne le regardent pas. Même défaut, et même correction, que `dashboardsRetires`.
   *
   * ⚠️ Le repli du `catch`, lui, ouvre tout : « ne pas savoir » et « ne pas pouvoir savoir »
   * ne se traitent pas pareil — le second dure, et fermer durablement se lirait comme un
   * retrait d'accès.
   */
  const [availableSources, setAvailableSources] = useState({});

  /**
   * ⚠️ Le drapeau est indispensable, parce que « vide » est désormais ambigu.
   *
   * Masquer une affordance dans le doute est sans risque ; **rediriger** ne l'est pas. Le
   * garde de l'écran de choix renverrait à l'accueil un utilisateur légitime arrivé par un
   * lien direct, dans l'intervalle où ses droits sont chargés et ses sources non — deux
   * requêtes distinctes. Il attend donc ce drapeau en plus de `clientToolsLoaded`.
   */
  const [sourcesDisponiblesChargees, setSourcesDisponiblesChargees] = useState(false);
  
  /**
   * Y a-t-il seulement un dashboard de plateforme à proposer ?
   *
   * ⚠️ **Une valeur dérivée, parce que trois endroits se posent la question** : le bouton du
   * menu de tête, l'écran de choix, et le garde ci-dessous. Chacun la recalculait à sa façon,
   * et c'est ainsi que le bouton a survécu à la fermeture de tous les dashboards.
   *
   * Déclarée ici et non avec `plateformeOuverte` : elle dépend d'`availableSources` et des
   * trois listes analytics, qui n'existent qu'à partir d'ici.
   */
  const auMoinsUnDashboardOuvert = useMemo(() => (
    Object.entries(availableSources).some(([cle, actif]) => actif && cle !== 'synthese')
    || (filteredGA4Properties.length > 0 && plateformeOuverte('ga4-dashboard'))
    || (filteredMatomoSites.length > 0 && plateformeOuverte('matomo-dashboard'))
    || (filteredPianoSites.length > 0 && plateformeOuverte('piano-dashboard'))
    || cmsDisponibles.length > 0
  ), [availableSources, filteredGA4Properties, filteredMatomoSites, filteredPianoSites,
      cmsDisponibles, plateformeOuverte]);

  /**
   * La barre latérale porte-t-elle le contexte de lecture — période, site, familles de
   * dashboards ?
   *
   * ⚠️ **Radar n'est pas dans la liste, et c'est délibéré** : il est écarté *en plus*, section
   * par section, parce qu'il garde le sélecteur de **sites** — `RadarView` lit `selectedSiteId`
   * et le passe en paramètre de requête. Les quatre autres familles ne lui servent à rien : il
   * a ses propres filtres de plateforme et de gravité. Le ranger dans la liste commune lui
   * retirerait ce sélecteur sans qu'on s'en aperçoive.
   */
  const sidebarPorteLeContexte = !ECRANS_SANS_CONTEXTE_DE_DONNEES.includes(currentView);

  /** L'entrée de navigation à allumer : celle de la vue, ou celle de son écran parent. */
  const entreeDeNavigationActive = ENTREE_DE_NAVIGATION_PARENTE[currentView] || currentView;

  /**
   * L'écran de choix ne s'ouvre pas s'il n'a rien à proposer.
   *
   * ⚠️ **Masquer le bouton ne ferme pas l'URL.** `#dashboard` tapé, mis en favori ou restauré
   * par le bouton précédent menait encore à « Sélectionnez une plateforme » au-dessus du vide.
   * Et le garde des rôles ne pouvait pas le couvrir : il raisonne sur la plateforme ouverte, or
   * ici il n'y en a aucune — `currentDashboardType` vaut `'selection'`, qui n'en est pas une.
   *
   * ⚠️ Un effet à part, déclaré après `availableSources` : le garde des rôles vit cent lignes
   * plus haut, et y lire cette valeur la prendrait dans sa zone morte.
   */
  useEffect(() => {
    if (currentView !== 'dashboard' || currentDashboardType !== 'selection') return;
    // Tant que les droits ne sont pas chargés, on ne renvoie personne : ne pas savoir n'est
    // pas une raison de fermer un écran qui sera peut-être légitime.
    if (!clientToolsLoaded) return;
    // Et les sources : « aucune régie » ne se distingue pas de « pas encore demandé ».
    if (!sourcesDisponiblesChargees) return;
    if (auMoinsUnDashboardOuvert) return;

    console.log('[AUTH] Écran de choix sans aucune plateforme ouverte - retour à l\'accueil');
    setCurrentView('home');
    window.history.replaceState({}, document.title, window.location.pathname + '#home');
  }, [currentView, currentDashboardType, auMoinsUnDashboardOuvert, clientToolsLoaded,
      sourcesDisponiblesChargees]);

  // Fonction pour obtenir le libellé du dashboard
  const getDashboardLabel = (type) => {
    switch(type) {
      case 'google': return 'Google Ads';
      case 'microsoft': return 'Microsoft Ads';
      case 'facebook-ads': return 'Meta Ads';
      case 'synthese': return 'Synthèse';
      default: return 'Dashboard';
    }
  };
  const [loading, setLoading] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  // État pour ouvrir une conversation existante depuis l'historique
  const [openConversationData, setOpenConversationData] = useState(null);
  /**
   * Erreur générale, affichée notamment par l'écran de connexion.
   *
   * Elle est initialisée depuis l'URL quand le serveur vient d'y déposer un motif de refus.
   * Un lien d'accès expiré ou déjà consommé redirige vers `#login?error=magic_link_error&
   * message=…` — que personne ne lisait : le client retombait sur un écran de connexion muet,
   * sans apprendre que son lien avait une date.
   */
  const [error, setError] = useState(() => {
    const separateur = window.location.hash.indexOf('?');
    if (separateur === -1) return '';

    const params = new URLSearchParams(window.location.hash.slice(separateur + 1));
    if (params.get('error') !== 'magic_link_error') return '';

    return params.get('message') || "Ce lien d'accès n'est plus valide. Demandez-en un nouveau à votre agence.";
  });
  const [success, setSuccess] = useState('');
  const [weatherTesting, setWeatherTesting] = useState(false);
  const [weatherTestResult, setWeatherTestResult] = useState(null);
  const [openMeteoTesting, setOpenMeteoTesting] = useState(false);
  const [openMeteoTestResult, setOpenMeteoTestResult] = useState(null);
  const [connecting, setConnecting] = useState(null);
  const [channelMode, setChannelMode] = useState('measured'); // 'measured' ou 'reconciled'
  const [openMerchantCenterOnLibraryLoad, setOpenMerchantCenterOnLibraryLoad] = useState(false);
  const [openSearchConsoleOnLibraryLoad, setOpenSearchConsoleOnLibraryLoad] = useState(false);
  const [openGoogleAnalyticsOnLibraryLoad, setOpenGoogleAnalyticsOnLibraryLoad] = useState(false);
  const [openAddModalOnLibraryLoad, setOpenAddModalOnLibraryLoad] = useState(false);
  const [openAddConnectorModalOnLoad, setOpenAddConnectorModalOnLoad] = useState(false);
  const [suggestedMerchantIds, setSuggestedMerchantIds] = useState([]); // IDs Merchant liés au compte Google Ads
  const [returnToShoppingAfterMerchantAdd, setReturnToShoppingAfterMerchantAdd] = useState(null); // sourceId du compte Google Ads pour retourner au shopping dashboard
  const [globalConnectors, setGlobalConnectors] = useState([]);
  const [connectorsLoaded, setConnectorsLoaded] = useState(false);
  const [expiredTokensBannerDismissed, setExpiredTokensBannerDismissed] = useState(false);

  /**
   * Les connecteurs dont l'accès ne répond plus.
   *
   * ⚠️ **Ce ne sont pas forcément des « tokens expirés »**, et le bandeau l'affirmait pour tous.
   * Cinq connecteurs du parc s'authentifient par une clé qui n'expire jamais — Matomo, Piano,
   * Awin, ChatGPT Ads, PrestaShop : leur dire « token expiré » envoie l'utilisateur chercher un
   * renouvellement qui n'existe pas, alors que `lastError` porte la vraie cause. Relevé le
   * 26 octobre 2026 sur PrestaShop, où le message réel était un avertissement PHP de la boutique.
   */
  const expiredConnectors = useMemo(() => {
    return globalConnectors.filter(c => c.authValid === false);
  }, [globalConnectors]);

  /** Le libellé du bandeau : la cause réelle quand on la connaît, jamais une cause supposée. */
  const messageConnecteursEnErreur = useMemo(() => {
    if (expiredConnectors.length === 0) return null;
    const nom = (c) => (c.provider || '').replace(/_/g, ' ');

    if (expiredConnectors.length > 1) {
      return `${expiredConnectors.length} connecteurs ne répondent plus `
        + `(${expiredConnectors.map(nom).join(', ')}).`;
    }

    const seul = expiredConnectors[0];
    // Le bandeau est une bande d'une ligne : la cause est bornée, le détail reste sur l'écran
    // Connecteurs, où le bouton mène.
    const cause = (seul.lastError || '').trim();
    return cause
      ? `Le connecteur ${nom(seul)} ne répond plus : ${cause.length > 160 ? `${cause.slice(0, 160)}…` : cause}`
      : `Le connecteur ${nom(seul)} ne répond plus.`;
  }, [expiredConnectors]);

  // Store in window for accessibility in deeply nested components
  window.performanceMaxChannelMode = channelMode;
  window.setPerformanceMaxChannelMode = setChannelMode;
  
  // Handler global pour Merchant Center click
  // Accepte un sourceId optionnel pour détecter les Merchant Centers liés au compte Google Ads
  window.handleMerchantCenterClick = async (googleAdsSourceId = null) => {
    const merchantApiConnector = window.globalConnectors?.find(c => c.provider === 'GOOGLE_MERCHANT_API');
    const merchantConnector = window.globalConnectors?.find(c => c.provider === 'GOOGLE_MERCHANT_API');

    if (!merchantApiConnector && !merchantConnector) {
      // Pas de connecteur Merchant → aller vers les connecteurs
      if (user && user.role === 'CLIENT') {
        setCurrentView('home');
      } else {
        setCurrentView('connectors');
      }
    } else {
      // Si on a un sourceId Google Ads, récupérer les Merchant Centers liés
      if (googleAdsSourceId && token) {
        try {
          console.log('🔗 [MERCHANT-LINK] Récupération des Merchant Centers liés à:', googleAdsSourceId);
          const response = await fetch(`${API_URL}/api/dashboard/linked-merchant-centers`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({ sourceId: googleAdsSourceId })
          });

          if (response.ok) {
            const data = await response.json();
            if (data.success && data.linkedMerchantCenters?.length > 0) {
              // Stocker les IDs des Merchant Centers liés
              const linkedIds = data.linkedMerchantCenters.map(m => String(m.merchantCenterId));
              console.log('🔗 [MERCHANT-LINK] Merchant Centers détectés:', linkedIds);
              setSuggestedMerchantIds(linkedIds);
            } else {
              setSuggestedMerchantIds([]);
            }
          }
        } catch (error) {
          console.error('❌ [MERCHANT-LINK] Erreur:', error);
          setSuggestedMerchantIds([]);
        }
      }

      // Stocker l'ID du compte Google Ads pour revenir au shopping dashboard après ajout
      if (googleAdsSourceId) {
        setReturnToShoppingAfterMerchantAdd(googleAdsSourceId);
      }

      // Aller vers la bibliothèque et ouvrir la modal Merchant API
      setCurrentView('library');
      setOpenMerchantCenterOnLibraryLoad(true);
    }
  };
  window.globalConnectors = globalConnectors;
  
  // Helper pour gérer les erreurs d'authentification
  const handleApiError = async (response, endpointName = 'API') => {
    if (response.status === 401) {
      try {
        const errorData = await response.json();
        if (errorData.error === 'auth_expired' || errorData.error === 'permission_denied') {
          console.log(`🔑 [AUTH] ${errorData.error === 'permission_denied' ? 'Permission refusée' : 'Token expiré'} détecté sur ${endpointName}`, errorData.platform || 'GOOGLE_ADS');
          setAuthError({
            message: errorData.message,
            accountName: errorData.accountName,
            accountId: errorData.accountId,
            requiresReauth: errorData.requiresReauth,
            permissionDenied: errorData.permissionDenied,
            userRole: errorData.userRole,
            platform: errorData.platform || 'GOOGLE_ADS' // Inclure la plateforme du backend
          });
          return { isAuthError: true };
        }
      } catch (parseError) {
        console.error('Erreur parsing réponse 401:', parseError);
      }
    }
    return { isAuthError: false };
  };

  // Fonction pour déclencher la reconnexion
  const handleReconnect = (platform = 'GOOGLE_ADS') => {
    // Rediriger vers l'authentification de la plateforme appropriée
    if (platform === 'FACEBOOK_ADS') {
      window.location.href = `${API_URL}/api/auth/facebook-ads?token=${token}&returnTo=connectors`;
    } else if (platform === 'TIKTOK_ADS') {
      window.location.href = `${API_URL}/api/auth/tiktok-ads?token=${token}&returnTo=connectors`;
    } else if (platform === 'PINTEREST_ADS') {
      window.location.href = `${API_URL}/api/auth/pinterest-ads?token=${token}&returnTo=connectors`;
    } else if (platform === 'MICROSOFT_ADS') {
      window.location.href = `${API_URL}/api/auth/microsoft-ads?token=${token}&returnTo=connectors`;
    } else {
      window.location.href = `${API_URL}/api/auth/google-ads?token=${token}&returnTo=connectors`;
    }
  };
  
  // Vérifier le token au démarrage
  useEffect(() => {
    if (token) {
      verifyToken();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  // Mettre à jour les sources disponibles quand le client sélectionné change
  useEffect(() => {
    if (token) {
      fetchAvailableSources();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClientId, token]);

  // Note: Ne PAS rediriger automatiquement vers une autre source si la source actuelle n'est plus disponible
  // L'utilisateur doit rester sur la plateforme sélectionnée et voir le message "Connecter [plateforme]"
  // au lieu d'être redirigé automatiquement vers une autre plateforme

  // Gérer les clics en dehors des menus déroulants
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Vérifier si le clic est sur un bouton de dropdown
      const isUserDropdownToggle = event.target.closest('.user-dropdown-toggle');
      const isUserDropdownMenu = event.target.closest('.user-dropdown-menu');
      const isClientDropdownToggle = event.target.closest('.client-dropdown-toggle');
      const isDashboardModelDropdown = event.target.closest('.dashboard-model-dropdown');

      // Fermer le menu utilisateur si clic en dehors
      if (showUserMenu && !isUserDropdownToggle && !isUserDropdownMenu) {
        setShowUserMenu(false);
      }

      // Fermer le dropdown client si clic en dehors
      if (showClientDropdown && !isClientDropdownToggle) {
        setShowClientDropdown(false);
        setClientSearchFilter('');
      }

      // Fermer le dropdown dashboard model si clic en dehors
      if (showDashboardModelDropdown && !isDashboardModelDropdown) {
        setShowDashboardModelDropdown(false);
      }
    };

    document.addEventListener('click', handleClickOutside);
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [showUserMenu, showClientDropdown, showDashboardModelDropdown]);

  // Redirection automatique supprimée - CLIENT et utilisateurs normaux ont le même accès

  // Fonction pour charger/rafraîchir les connecteurs globalement
  const refreshGlobalConnectors = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGlobalConnectors(response.data.connectors);
      window.globalConnectors = response.data.connectors;
      setConnectorsLoaded(true);
    } catch (error) {
      console.error('❌ Erreur chargement global des connecteurs:', error);
      setGlobalConnectors([]); // Ensure empty array on error
      setConnectorsLoaded(true); // Still mark as loaded to proceed with routing
    }
  };

  // Charger les connectors globalement au démarrage
  useEffect(() => {
    if (token) {
      setConnectorsLoaded(false); // Reset loading state
      refreshGlobalConnectors();
    }
  }, [token]);

  // Redirection automatique intelligente basée sur les connecteurs
  useEffect(() => {
    if (!token || !connectorsLoaded || !isInitialLogin) return;

    // Ne pas auto-router si un hash de retour est en attente (lien email)
    const pendingReturnHash = localStorage.getItem('kokpeet_returnHash');
    if (pendingReturnHash) {
      console.log('[AUTO-ROUTING] Hash de retour en attente, skip auto-routing:', pendingReturnHash);
      return;
    }

    // Ne pas auto-router si l'URL désigne déjà une destination explicite : lien
    // partagé, favori, ou simple collage dans un onglet neuf. Sans ce garde-fou,
    // #dashboard/google/global?account=... était remplacé par #home, l'auto-routing
    // ne se déclenchant que sur une session sans `kokpeet_autoRouted`.
    const hashView = window.location.hash.replace(/^#/, '').split('?')[0];
    if (hashView && hashView !== 'home') {
      console.log('[AUTO-ROUTING] Destination explicite dans l\'URL, skip auto-routing:', hashView);
      sessionStorage.setItem('kokpeet_autoRouted', 'true');
      setIsInitialLogin(false);
      return;
    }

    // Ne pas rediriger si on est sur une page publique (aide, légal, etc.)
    const publicViews = ['help', 'legal', 'join-team', 'join-organization'];
    if (publicViews.includes(currentView)) {
      sessionStorage.setItem('kokpeet_autoRouted', 'true');
      setIsInitialLogin(false);
      return;
    }

    // Ne pas rediriger si on est déjà sur une vue de ticket
    if (currentView === 'tickets' || (currentView === 'admin' && window.location.hash.includes('tickets'))) {
      console.log('[AUTO-ROUTING] Déjà sur les tickets, skip auto-routing');
      sessionStorage.setItem('kokpeet_autoRouted', 'true');
      setIsInitialLogin(false);
      return;
    }

    // Pas de traitement spécial pour CLIENT - même logique pour tous

    // Connecteurs pertinents pour le dashboard
    const dashboardConnectors = globalConnectors.filter(c =>
      c.provider === 'GOOGLE_ADS' ||
      c.provider === 'MICROSOFT_ADS' ||
      c.provider === 'FACEBOOK_ADS' ||
      (c.provider === 'GOOGLE_MERCHANT_API' && globalConnectors.some(gc => gc.provider === 'GOOGLE_ADS'))
    );

    // Déterminer la vue cible selon les connecteurs et le rôle utilisateur.
    // Les CLIENT vont toujours vers 'home' car ils utilisent les connecteurs de l'admin.
    //
    // Le parcours d'installation n'est plus une destination d'aiguillage : il est décidé au
    // rendu, sur `user.onboardingTermineAt`. Une condition dérivée des connecteurs ne pouvait
    // pas tenir ce rôle — elle devenait fausse au milieu du parcours, dès la source branchée.
    const targetView = (user && user.role === 'CLIENT') ? 'home' : (dashboardConnectors.length === 0 ? 'connectors' : 'home');

    // Rediriger seulement si nécessaire
    if (currentView !== targetView) {
      console.log(`🚀 [AUTO-ROUTING] Redirection vers ${targetView}`);
      setCurrentView(targetView);
    }

    // Marquer le login initial comme terminé pour éviter de futurs déclenchements
    sessionStorage.setItem('kokpeet_autoRouted', 'true');
    setIsInitialLogin(false);
  }, [token, connectorsLoaded, globalConnectors, isInitialLogin, user, currentView]);

  /**
   * Changer d'écran depuis un composant qui n'a pas de quoi le faire.
   *
   * Les outils reçoivent chacun leurs propres props, et la plupart n'ont aucun moyen de
   * demander un changement de vue. Leur passer `setCurrentView` à tous aurait touché une
   * dizaine de signatures pour un cas rare ; un événement laisse l'application seule maîtresse
   * de sa navigation, et le composant émetteur ignorant de la façon dont elle s'opère.
   */
  useEffect(() => {
    const auSignal = (e) => {
      const vue = e?.detail?.vue;
      if (!vue) return;
      setCurrentView(vue);
      window.history.replaceState({}, document.title, window.location.pathname + '#' + vue);
    };
    window.addEventListener('kokpit:naviguer', auSignal);
    return () => window.removeEventListener('kokpit:naviguer', auSignal);
  }, []);

  /**
   * La visite guidée, proposée une seule fois : au premier tableau de bord qui suit
   * l'installation.
   *
   * Trois conditions, et chacune a sa raison :
   * — le parcours d'installation est **terminé**, sinon la personne est encore en train de
   *   brancher ses comptes et n'a rien à visiter ;
   * — elle n'a **jamais vu la proposition** (`visiteGuideeVue`) ;
   * — un compte est **réellement connecté**, faute de quoi la visite désignerait des écrans
   *   vides.
   *
   * ⚠️ **La réponse est consignée à l'affichage, pas à la fermeture.** Une invitation qui
   * revient à chaque connexion est une nuisance : refermer d'un « Plus tard », de la croix ou
   * d'Échap doit suffire à ne plus la revoir. Ce qui n'est acceptable que parce qu'elle reste
   * rejouable depuis Aide et assistance › Ressources, par `relancerVisiteGuidee`.
   *
   * L'état est relu auprès du serveur plutôt que tenu dans le navigateur : c'est une réponse de
   * la personne, qui doit la suivre d'un appareil à l'autre.
   */
  const [visiteProposee, setVisiteProposee] = useState(false);
  /** Rejouée à la demande : on saute l'invitation, qui vient de la demander n'a rien à décider. */
  const [visiteRelancee, setVisiteRelancee] = useState(false);

  useEffect(() => {
    if (!token || !user || user.role === 'CLIENT') return;
    if (user.onboardingTermineAt === null) return;
    if (!connectorsLoaded || globalConnectors.length === 0) return;
    if (visiteProposee) return;

    let annule = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API_URL}/api/onboarding/etat`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (!annule && data?.success && !data.visiteGuideeVue && data.comptes > 0) {
          setVisiteProposee(true);
          // Consigné dès l'affichage : voir le commentaire ci-dessus. L'échec est sans gravité,
          // il ne coûte qu'une proposition de plus à la prochaine session.
          axios.post(`${API_URL}/api/onboarding/visite-guidee/vue`, {}, {
            headers: { Authorization: `Bearer ${token}` }
          }).catch(() => {});
        }
      } catch (e) {
        // Muet : une visite guidée qui ne s'ouvre pas ne doit alerter personne.
      }
    })();
    return () => { annule = true; };
  }, [token, user, connectorsLoaded, globalConnectors, visiteProposee]);

  /** Refermer, d'où que vienne le geste. Rien à consigner : c'est fait à l'affichage. */
  const fermerVisiteGuidee = useCallback(() => {
    setVisiteProposee(false);
    setVisiteRelancee(false);
  }, []);

  /** Rejouer depuis Aide et assistance. */
  const relancerVisiteGuidee = useCallback(() => {
    setIsHelpPanelOpen(false);
    setVisiteProposee(false);
    setVisiteRelancee(true);
  }, []);

  // Gestion du callback d'authentification
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);

    // Gestion des erreurs OAuth (utilisateur non enregistré)
    const authError = urlParams.get('auth_error');
    if (authError === 'subscription_required') {
      setError('Ce compte Google n\'est pas encore enregistré. Cliquez sur "Créez un compte" pour vous inscrire.');
      window.history.replaceState({}, document.title, window.location.pathname);
      return;
    }

    const authToken = urlParams.get('token');
    const googleToken = urlParams.get('googleToken');
    const userData = urlParams.get('user');
    let connectorSuccess = urlParams.get('connector_success');
    let connectorEmail = urlParams.get('connector_email');
    let connectorError = urlParams.get('error');
    let connectorType = urlParams.get('connector');

    // Pour le hash routing, vérifier aussi les params dans le hash
    // URL: http://localhost:3000/#dashboard/google/global?connector_success=...
    let connectorFromHash = false;
    if (!connectorSuccess && window.location.hash.includes('connector_success=')) {
      const hashParts = window.location.hash.split('?');
      if (hashParts.length > 1) {
        const hashParams = new URLSearchParams(hashParts[1]);
        connectorSuccess = hashParams.get('connector_success');
        connectorEmail = hashParams.get('connector_email');
        connectorError = hashParams.get('error');
        connectorType = hashParams.get('connector');
        connectorFromHash = true;
      }
    }
    
    // Gestion de la connexion initiale (login)
    if (authToken && userData && googleToken) {
      try {
        localStorage.setItem('kokpeet_token', authToken);
        localStorage.setItem('google_access_token', googleToken);

        setToken(authToken);
        const parsedUser = JSON.parse(decodeURIComponent(userData));
        setUser(parsedUser);
        setIsVerifyingAuth(false); // Login complet, pas besoin de vérifier

        // Synchroniser les préférences métriques depuis/vers l'API
        initMetricsSync(authToken);

        // Vérifier si une invitation d'organisation est en attente
        const pendingOrgToken = localStorage.getItem('pendingOrgInvitationToken');
        if (pendingOrgToken) {
          setOrgInvitationToken(pendingOrgToken);
          setCurrentView('join-organization');
          setIsInitialLogin(false); // Empêcher l'auto-routing
          window.history.replaceState({}, document.title, window.location.pathname);
          return; // Sortir pour ne pas continuer le flux normal
        }

        // Vérifier si une invitation d'équipe est en attente (ancien système)
        const pendingTeamToken = localStorage.getItem('pendingInvitationToken');
        if (pendingTeamToken) {
          setInvitationToken(pendingTeamToken);
          setCurrentView('join-team');
          setIsInitialLogin(false); // Empêcher l'auto-routing
          window.history.replaceState({}, document.title, window.location.pathname);
          return; // Sortir pour ne pas continuer le flux normal
        }

        // Vérifier si un hash était sauvegardé (lien email avant login)
        const savedHash = localStorage.getItem('kokpeet_returnHash');
        if (savedHash) {
          console.log('[AUTH] Restauration du hash après login:', savedHash);
          // IMPORTANT: Ne pas supprimer le hash immédiatement, il sera supprimé après la configuration

          // Parser le hash pour extraire la vue et les paramètres
          if (savedHash.startsWith('#diagnostic?') || savedHash.startsWith('#diagnostic/?')) {
            const queryPart = savedHash.includes('?') ? savedHash.split('?')[1] : '';
            const params = new URLSearchParams(queryPart);
            const accountId = params.get('account');
            if (accountId) {
              setCurrentView('diagnostic');
              setDiagnosticInitialAccountId(accountId);
              setIsInitialLogin(false); // Empêcher l'auto-routing
              localStorage.removeItem('kokpeet_returnHash'); // Supprimer après config
              window.history.replaceState({}, document.title, window.location.pathname + savedHash);
              return;
            }
          }
          // Gestion spéciale pour les tickets utilisateur #tickets/:id
          if (savedHash.startsWith('#tickets/')) {
            const ticketIdMatch = savedHash.match(/^#tickets\/([a-zA-Z0-9-]+)$/);
            if (ticketIdMatch) {
              const ticketId = ticketIdMatch[1];
              console.log('[AUTH] Restauration ticket utilisateur:', ticketId);
              setCurrentView('tickets');
              setTicketsSelectedTicketId(ticketId);
              setIsInitialLogin(false);
              localStorage.removeItem('kokpeet_returnHash'); // Supprimer après config
              window.history.replaceState({}, document.title, window.location.pathname + savedHash);
              return;
            }
          }

          // Gestion spéciale pour les tickets admin #admin/tickets/:id
          if (savedHash.startsWith('#admin/tickets/')) {
            const ticketIdMatch = savedHash.match(/^#admin\/tickets\/([a-zA-Z0-9-]+)$/);
            if (ticketIdMatch) {
              const ticketId = ticketIdMatch[1];
              console.log('[AUTH] Restauration ticket admin:', ticketId);
              setCurrentView('admin');
              setAdminTab('tickets');
              setAdminSelectedTicketId(ticketId);
              setIsInitialLogin(false);
              localStorage.removeItem('kokpeet_returnHash'); // Supprimer après config
              window.history.replaceState({}, document.title, window.location.pathname + savedHash);
              return;
            }
          }

          // Pour les autres hash, juste restaurer (en nettoyant les query params)
          window.history.replaceState({}, document.title, window.location.pathname + savedHash);
          const hashView = savedHash.replace('#', '').split('?')[0].split('/')[0];
          if (hashView) {
            setCurrentView(hashView);
            setIsInitialLogin(false);
            localStorage.removeItem('kokpeet_returnHash'); // Supprimer après config
            return;
          }
          // Si on arrive ici, supprimer quand même le hash
          localStorage.removeItem('kokpeet_returnHash');
        }

        // Marquer comme login initial pour déclencher l'auto-routing
        setIsInitialLogin(true);
        // La redirection sera gérée par l'useEffect qui vérifie les connecteurs

        // Nettoyer l'URL
        window.history.replaceState({}, document.title, window.location.pathname);
      } catch (error) {
        console.error('❌ Erreur parsing callback:', error);
        setError('Erreur lors de la connexion');
      }
    }
    
    // Gestion du retour de connexion d'un connecteur
    if (connectorSuccess) {
      let message = '';
      if (connectorSuccess === 'google_sheets') {
        message = `✅ Google Sheets connecté avec succès${connectorEmail ? ` (${connectorEmail})` : ''}`;
      } else if (connectorSuccess === 'google_ads') {
        message = `✅ Google Ads connecté avec succès${connectorEmail ? ` (${connectorEmail})` : ''}`;
      } else if (connectorSuccess === 'google_analytics') {
        message = `✅ Google Analytics 4 connecté avec succès${connectorEmail ? ` (${connectorEmail})` : ''} - Sélectionnez les propriétés à ajouter`;
        // Ouvrir automatiquement le sélecteur de propriétés GA4 dans LibraryView
        setOpenGoogleAnalyticsOnLibraryLoad(true);
      } else if (connectorSuccess === 'google_merchant_api') {
        message = `✅ Google Merchant Center connecté avec succès${connectorEmail ? ` (${connectorEmail})` : ''}`;
      } else if (connectorSuccess === 'microsoft_ads') {
        message = `✅ Microsoft Ads connecté avec succès${connectorEmail ? ` (${decodeURIComponent(connectorEmail)})` : ''}`;
      } else if (connectorSuccess === 'facebook_ads') {
        message = `✅ Facebook Ads connecté avec succès${connectorEmail ? ` (${decodeURIComponent(connectorEmail)})` : ''}`;
      } else if (connectorSuccess === 'pinterest_ads') {
        message = `✅ Pinterest Ads connecté avec succès${connectorEmail ? ` (${decodeURIComponent(connectorEmail)})` : ''}`;
      } else if (connectorSuccess === 'tiktok_ads') {
        message = `✅ TikTok Ads connecté avec succès${connectorEmail ? ` (${decodeURIComponent(connectorEmail)})` : ''}`;
      } else if (connectorSuccess === 'google_search_console') {
        message = `✅ Google Search Console connecté avec succès${connectorEmail ? ` (${connectorEmail})` : ''}`;
      } else if (connectorSuccess === 'google_merchant_center') {
        message = `✅ Google Merchant Center connecté avec succès${connectorEmail ? ` (${connectorEmail})` : ''}`;
      } else if (connectorSuccess === 'matomo_oauth') {
        const connectorName = urlParams.get('connector_name') || '';
        message = `✅ Matomo connecté avec succès${connectorName ? ` (${decodeURIComponent(connectorName)})` : ''}`;
      }

      if (message) {
        setSuccess(message);
        // Réinitialiser le state connecting
        setConnecting(null);

        // Si les params viennent du hash (retour depuis dashboard), ne pas changer la vue
        // Sinon, vérifier si un hash de redirection est présent (#manager ou #connectors)
        if (!connectorFromHash) {
          // Pour Google Analytics, rediriger vers library pour sélectionner les propriétés
          if (connectorSuccess === 'google_analytics') {
            setCurrentView('library');
          } else {
            const currentHash = window.location.hash;
            if (currentHash === '#manager') {
              // Rediriger vers le Gestionnaire de données
              setCurrentView('manager');
            } else if (currentHash === '#connectors') {
              // Rediriger vers les Connecteurs
              setCurrentView('connectors');
            } else if (user && user.role === 'CLIENT') {
              setCurrentView('home');
            } else {
              setCurrentView('connectors');
            }
          }
        }

        // Effacer le message après 5 secondes
        setTimeout(() => {
          setSuccess('');
        }, 5000);
      }

      // Nettoyer l'URL - si params dans le hash, nettoyer seulement les params connector
      if (connectorFromHash) {
        // Garder le hash mais enlever les params connector
        const hashParts = window.location.hash.split('?');
        const baseHash = hashParts[0];
        if (hashParts.length > 1) {
          const hashParams = new URLSearchParams(hashParts[1]);
          hashParams.delete('connector_success');
          hashParams.delete('connector_email');
          hashParams.delete('error');
          hashParams.delete('connector');
          const remainingParams = hashParams.toString();
          const newHash = remainingParams ? `${baseHash}?${remainingParams}` : baseHash;
          window.history.replaceState({}, document.title, window.location.pathname + newHash);
        }
      } else {
        // Conserver le hash (ex: #data-manager) si présent
        const currentHash = window.location.hash || '';
        window.history.replaceState({}, document.title, window.location.pathname + currentHash);
      }
    }
    
    // Gestion des erreurs de connexion d'un connecteur
    if (connectorError && connectorType) {
      let errorMessage = 'Erreur lors de la connexion';
      if (connectorType === 'sheets') {
        errorMessage = '❌ Erreur lors de la connexion à Google Sheets';
      } else if (connectorType === 'ads') {
        errorMessage = '❌ Erreur lors de la connexion à Google Ads';
      }
      
      setError(errorMessage);
      setConnecting(null);
      
      // Nettoyer l'URL
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [currentView]);

  // Gestionnaires redondants supprimés - gestion centralisée dans le useEffect global

  // Écouter les changements de type de dashboard pour mettre à jour l'indicateur visuel
  useEffect(() => {
    const handleTabChange = (event) => {
      const { type } = event.detail;
      setCurrentDashboardType(type);
    };

    window.addEventListener('dashboardTypeChange', handleTabChange);

    return () => {
      window.removeEventListener('dashboardTypeChange', handleTabChange);
    };
  }, []);

  // Exposer la fonction pour contrôler le bouton AI depuis les composants dashboard
  useEffect(() => {
    window.setHeaderAIButton = setShowAIButton;
    return () => {
      delete window.setHeaderAIButton;
    };
  }, []);

  const verifyToken = async () => {
    try {
      setInitialDataLoaded(false); // Bloquer HomePage pendant le chargement

      const response = await axios.get(`${API_URL}/api/auth/verify`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUser(response.data.user);
      setIsVerifyingAuth(false); // Token vérifié avec succès

      /**
       * La période enregistrée sur le compte, si elle diffère de celle ouverte.
       *
       * Elle arrive avec l'utilisateur, `verifyToken` rendant la ligne entière : aucun appel
       * supplémentaire n'est nécessaire.
       *
       * **Elle l'emporte sur le stockage local**, parce qu'elle est la préférence du compte
       * et non celle du navigateur : une période changée depuis un autre appareil doit se
       * retrouver ici. Sur le même navigateur les deux concordent presque toujours — le
       * stockage local est écrit à chaque changement, la base une seconde plus tard — et la
       * condition d'égalité évite alors un rechargement inutile des tableaux.
       *
       * **Sauf si l'URL portait une période** : un lien partagé désigne ce qu'il désigne, et
       * une préférence de compte n'a pas à le réécrire.
       */
      const memorisee = response.data.user?.preferencePeriode;
      const urlPorteUnePeriode = Boolean(getUrlParams().get('period'));

      if (memorisee?.period && !urlPorteUnePeriode && memorisee.period !== selectedPeriod) {
        const complete = memorisee.period !== 'CUSTOM'
          || (memorisee.startDate && memorisee.endDate);
        if (complete) {
          setSelectedPeriod(memorisee.period);
          setCustomStartDate(memorisee.startDate || '');
          setCustomEndDate(memorisee.endDate || '');
        }
      }

      // Synchroniser les préférences métriques depuis/vers l'API
      initMetricsSync(token);

      // Charger TOUTES les données initiales en parallèle et attendre qu'elles soient toutes prêtes
      const loadPromises = [fetchUserSubscription()];

      if (response.data.user && (response.data.user.role === 'USER' || response.data.user.role === 'ADMIN' || response.data.user.role === 'CLIENT')) {
        loadPromises.push(fetchUserClients());
        loadPromises.push(fetchUserSites());
        loadPromises.push(fetchAllAdAccounts());
      }

      loadPromises.push(fetchAvailableSources());
      loadPromises.push(fetchUserOrganizations());
      loadPromises.push(fetchInaccessibleAccounts());
      loadPromises.push(fetchClientTools());
      loadPromises.push(fetchDashboardsSurMesure());

      // Attendre que tout soit chargé avant de débloquer HomePage
      await Promise.all(loadPromises);
      setInitialDataLoaded(true);

    } catch (error) {
      console.error('❌ Token invalide:', error);
      localStorage.removeItem('kokpeet_token');
      localStorage.removeItem('google_access_token');
      setToken(null);
      setUser(null);
      setUserSubscription(null);
      setUserClients([]);
      setSelectedClientId(null);
      setInitialDataLoaded(true); // Débloquer même en cas d'erreur
      setIsVerifyingAuth(false); // Vérification terminée (échec)
    }
  };

  const fetchUserSubscription = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/subscription/current`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setUserSubscription(response.data.subscription);
      }
    } catch (error) {
      console.error('❌ Erreur récupération abonnement:', error);
      // Pour les admins, donner accès complet par défaut en cas d'erreur
      if (user && user.role === 'ADMIN') {
        setUserSubscription({
          plan: 'AGENCY',
          status: 'ACTIVE',
          analysisLimit: -1
        });
      } else {
        // En cas d'erreur, considérer comme SOLO par défaut pour les utilisateurs normaux
        setUserSubscription({
          plan: 'SOLO',
          status: 'ACTIVE',
          analysisLimit: 100
        });
      }
    }
  };

  const fetchClientTools = async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/api/client-tools`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setClientTools(response.data?.tools || []);
      setDashboardsRetires(response.data?.dashboardsRetires || []);
    } catch (error) {
      // Un échec ne doit pas ouvrir de droits : on reste sur la liste vide
      console.error('❌ Erreur chargement des outils accordés:', error);
      setClientTools([]);
      /*
       * ⚠️ **Le repli est la liste VIDE ici aussi, et il veut dire l'inverse.**
       *
       * Pour les outils, une liste vide ferme tout — « ne pas savoir n'est pas une
       * autorisation ». Pour les dashboards, elle **ouvre** tout, puisqu'elle énumère les
       * retraits. Les deux replis s'écrivent pareil et ne disent pas la même chose : c'est
       * voulu, et c'est le bon sens dans les deux cas. Fermer tous les dashboards sur un
       * incident réseau ferait croire à un client qu'on lui a tout retiré.
       */
      setDashboardsRetires([]);
    } finally {
      setClientToolsLoaded(true);
    }
  };

  /**
   * Les dashboards sur mesure disponibles, pour tous les comptes du périmètre.
   *
   * Appelé au chargement, comme les droits clients : sans cette liste, l'écran de choix ne peut
   * pas savoir combien de destinations sont ouvertes, et un client sans dashboard verrait une
   * famille vide.
   *
   * Un échec laisse la liste vide — « ne pas savoir n'est pas une autorisation », la règle déjà
   * appliquée aux droits juste au-dessus. Le compte connecté n'ayant pas forcément l'outil, un
   * 403 est un cas normal et non une panne : on ne le journalise pas comme une erreur.
   */
  const fetchDashboardsSurMesure = async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/api/dashboards-sur-mesure`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardsSurMesure(response.data?.dashboards || []);
    } catch (error) {
      if (error.response?.status !== 403) {
        console.error('❌ Erreur chargement des dashboards sur mesure:', error);
      }
      setDashboardsSurMesure([]);
    } finally {
      setDashboardsSurMesureCharges(true);
    }
  };

  const fetchUserClients = async () => {
    if (!token) return;
    
    try {
      const response = await axios.get(`${API_URL}/api/client-management/user-clients`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserClients(response.data);
      
      // Vérifier si le client sélectionné existe encore dans la liste
      if (selectedClientId && !response.data.find(c => c.id === selectedClientId)) {
        console.log('🔄 Client sélectionné non trouvé dans la liste, réinitialisation');
        setSelectedClientId(null);
      }
    } catch (error) {
      console.error('❌ Erreur récupération clients:', error);
      setUserClients([]);
    }
  };

  // Fonction pour récupérer les organisations de l'utilisateur
  const fetchUserOrganizations = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/organizations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setUserOrganizations(response.data.organizations || []);
      }
    } catch (error) {
      console.error('❌ Erreur récupération organisations:', error);
      setUserOrganizations([]);
    } finally {
      setOrganizationsLoaded(true);
    }
  };

  // Fonction pour récupérer les sites de l'utilisateur (pour les notes)
  const fetchUserSites = async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUserSites(response.data.sites || []);
    } catch (error) {
      console.error('❌ Erreur récupération sites:', error);
      setUserSites([]);
    }
  };

  // Fonction pour récupérer tous les comptes publicitaires (pour les notes)
  const fetchAllAdAccounts = async () => {
    if (!token) return;
    try {
      // `usage=dashboards` : un compte débrayé des dashboards ne doit pas apparaître dans le
      // sélecteur. L'écran Comptes, lui, n'envoie aucun usage et les voit tous.
      const response = await axios.get(`${API_URL}/api/library?usage=dashboards`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const library = response.data.library || {};
      const accounts = [];

      console.log('📚 [Notes] Library chargée:', library);

      // Collecter tous les comptes des plateformes publicitaires
      if (library.GOOGLE_ADS && Array.isArray(library.GOOGLE_ADS)) {
        library.GOOGLE_ADS.forEach(item => {
          accounts.push({
            id: item.id,
            sourceId: item.sourceId || item.id,
            // `/api/library` expose `id` = sourceId et l'identifiant de base sous
            // `libraryId` : sans lui, un site enregistré avec des cuid ne reconnaîtrait
            // aucun de ses comptes.
            libraryId: item.libraryId || item.databaseId || null,
            name: item.name,
            type: 'GOOGLE_ADS',
            siteId: item.clientId || null
          });
        });
      }
      if (library.MICROSOFT_ADS && Array.isArray(library.MICROSOFT_ADS)) {
        library.MICROSOFT_ADS.forEach(item => {
          accounts.push({
            id: item.id,
            sourceId: item.sourceId || item.id,
            // `/api/library` expose `id` = sourceId et l'identifiant de base sous
            // `libraryId` : sans lui, un site enregistré avec des cuid ne reconnaîtrait
            // aucun de ses comptes.
            libraryId: item.libraryId || item.databaseId || null,
            name: item.name,
            type: 'MICROSOFT_ADS',
            siteId: item.clientId || null
          });
        });
      }
      if (library.FACEBOOK_ADS && Array.isArray(library.FACEBOOK_ADS)) {
        library.FACEBOOK_ADS.forEach(item => {
          accounts.push({
            id: item.id,
            sourceId: item.sourceId || item.id,
            // `/api/library` expose `id` = sourceId et l'identifiant de base sous
            // `libraryId` : sans lui, un site enregistré avec des cuid ne reconnaîtrait
            // aucun de ses comptes.
            libraryId: item.libraryId || item.databaseId || null,
            name: item.name,
            type: 'FACEBOOK_ADS',
            siteId: item.clientId || null
          });
        });
      }
      if (library.PINTEREST_ADS && Array.isArray(library.PINTEREST_ADS)) {
        library.PINTEREST_ADS.forEach(item => {
          accounts.push({
            id: item.id,
            sourceId: item.sourceId || item.id,
            // `/api/library` expose `id` = sourceId et l'identifiant de base sous
            // `libraryId` : sans lui, un site enregistré avec des cuid ne reconnaîtrait
            // aucun de ses comptes.
            libraryId: item.libraryId || item.databaseId || null,
            name: item.name,
            type: 'PINTEREST_ADS',
            siteId: item.clientId || null
          });
        });
      }
      if (library.TIKTOK_ADS && Array.isArray(library.TIKTOK_ADS)) {
        library.TIKTOK_ADS.forEach(item => {
          accounts.push({
            id: item.id,
            sourceId: item.sourceId || item.id,
            // `/api/library` expose `id` = sourceId et l'identifiant de base sous
            // `libraryId` : sans lui, un site enregistré avec des cuid ne reconnaîtrait
            // aucun de ses comptes.
            libraryId: item.libraryId || item.databaseId || null,
            name: item.name,
            type: 'TIKTOK_ADS',
            siteId: item.clientId || null
          });
        });
      }
      if (library.AWIN && Array.isArray(library.AWIN)) {
        library.AWIN.forEach(item => {
          accounts.push({
            id: item.id,
            sourceId: item.sourceId || item.id,
            libraryId: item.libraryId || item.databaseId || null,
            name: item.name,
            type: 'AWIN',
            siteId: item.clientId || null
          });
        });
      }

      if (library.OPENAI_ADS && Array.isArray(library.OPENAI_ADS)) {
        library.OPENAI_ADS.forEach(item => {
          accounts.push({
            id: item.id,
            sourceId: item.sourceId || item.id,
            // `/api/library` expose `id` = sourceId et l'identifiant de base sous
            // `libraryId` : sans lui, un site enregistré avec des cuid ne reconnaîtrait
            // aucun de ses comptes.
            libraryId: item.libraryId || item.databaseId || null,
            name: item.name,
            type: 'OPENAI_ADS',
            siteId: item.clientId || null
          });
        });
      }

      /**
       * Les solutions de mesure rejoignent la liste des comptes des notes.
       *
       * Une note documente aussi bien un incident de tracking, une reprise de tagging ou un
       * changement de périmètre sur une propriété analytique qu'une opération sur une régie.
       * Les en exclure obligeait à rattacher ces notes au site entier, sans dire de quelle
       * propriété elles parlaient.
       *
       * Même forme que les régies : c'est `type` qui les distingue, et il sert de
       * `platform` à la note.
       */
      for (const type of ['GOOGLE_ANALYTICS', 'PIANO_ANALYTICS', 'MATOMO', 'PRESTASHOP']) {
        if (!Array.isArray(library[type])) continue;
        library[type].forEach(item => {
          accounts.push({
            id: item.id,
            sourceId: item.sourceId || item.id,
            libraryId: item.libraryId || item.databaseId || null,
            name: item.name,
            type,
            siteId: item.clientId || null
          });
        });
      }

      console.log('📚 [Notes] Comptes collectés (régies et mesure) :', accounts);
      setAllAdAccounts(accounts);

      // Stocker également les sites Matomo pour la sidebar (ou vider si plus aucun)
      if (library.MATOMO && Array.isArray(library.MATOMO) && library.MATOMO.length > 0) {
        console.log('📚 [Matomo] Sites Matomo chargés depuis library:', library.MATOMO.length);
        // `/api/library` met le numéro de site dans `id` ; le tableau de bord, lui, y met
        // l'identifiant de base. Les deux écrivent dans cet état — sans cette normalisation,
        // `site.id` change de nature selon celui qui a écrit en dernier.
        setMatomoSites(normaliserSitesDeMesure(library.MATOMO));
        // Les boutiques arrivent par le même chemin : `/api/library` groupe sur `item.type`,
        // donc la clé existe sans qu'on ait rien à ajouter côté serveur.
        setPrestashopBoutiques(Array.isArray(library.PRESTASHOP) ? library.PRESTASHOP : []);
      } else {
        setMatomoSites([]);
      }

      // Stocker également les propriétés GA4 pour la sidebar (ou vider si plus aucune)
      if (library.GOOGLE_ANALYTICS && Array.isArray(library.GOOGLE_ANALYTICS) && library.GOOGLE_ANALYTICS.length > 0) {
        console.log('📚 [GA4] Propriétés GA4 chargées depuis library:', library.GOOGLE_ANALYTICS.length);
        setGA4Properties(library.GOOGLE_ANALYTICS);
      } else {
        setGA4Properties([]);
      }

      // Stocker également les sites Piano Analytics pour la sidebar (ou vider si plus aucun)
      if (library.PIANO_ANALYTICS && Array.isArray(library.PIANO_ANALYTICS) && library.PIANO_ANALYTICS.length > 0) {
        console.log('📚 [Piano] Sites Piano chargés depuis library:', library.PIANO_ANALYTICS.length);
        // Reformater pour avoir la même structure que l'endpoint /piano/sites
        const formattedPianoSites = library.PIANO_ANALYTICS.map(item => ({
          id: item.databaseId || item.libraryId || item.id,
          sourceId: item.sourceId,
          name: item.name,
          connectionId: item.connectionId,
          connectionName: item.connectionName,
          siteId: item.metadata?.siteId || item.sourceId
        }));
        setPianoSites(formattedPianoSites);
      } else {
        setPianoSites([]);
      }

      // Notifier la HomePage qu'elle doit rafraîchir ses données
      window.dispatchEvent(new CustomEvent('homepageDataChange'));
    } catch (error) {
      console.error('❌ Erreur récupération comptes publicitaires:', error);
      setAllAdAccounts([]);
    }
  };

  // Fonctions pour les préférences de notifications (mémorisées pour éviter les boucles infinies)
  const fetchNotificationPrefs = useCallback(async () => {
    if (!token) return;
    try {
      const response = await axios.get(`${API_URL}/api/diagnostic/preferences`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.success) {
        setNotificationPrefs(response.data.data);
      }
    } catch (error) {
      console.error('❌ Erreur récupération préférences notifications:', error);
    }
  }, [token]);

  const updateNotificationPrefs = useCallback(async (emailAlertsActive) => {
    if (!token) return;
    setNotificationPrefsLoading(true);
    try {
      const response = await axios.put(`${API_URL}/api/diagnostic/preferences`,
        { emailAlertsActive },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data.success) {
        setNotificationPrefs(response.data.data);
      }
    } catch (error) {
      console.error('❌ Erreur mise à jour préférences notifications:', error);
    } finally {
      setNotificationPrefsLoading(false);
    }
  }, [token]);

  // Fonction pour récupérer les notes du dashboard (mémorisée pour éviter les boucles infinies)
  const fetchDashboardNotes = useCallback(async (platform, accountId, startDate, endDate) => {
    console.log('📝 [Notes] fetchDashboardNotes appelé:', { platform, accountId, startDate, endDate });
    if (!token) return;
    try {
      const params = new URLSearchParams();
      if (platform) params.append('platform', platform);
      if (accountId) params.append('accountId', accountId);
      if (startDate) params.append('startDate', startDate);
      if (endDate) params.append('endDate', endDate);
      if (selectedClientId) params.append('clientId', selectedClientId);

      const response = await axios.get(`${API_URL}/api/notes?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log('📝 [Notes] Notes du dashboard chargées:', response.data.notes);
        setDashboardNotes(response.data.notes || []);
      }
    } catch (error) {
      console.error('❌ Erreur récupération notes dashboard:', error);
      setDashboardNotes([]);
    }
  }, [token, selectedClientId]);

  /**
   * Notes des dashboards analytiques.
   *
   * Les dashboards de régies chargent les leurs chacun de leur côté ; les trois solutions de
   * mesure n'en chargeaient aucune, faute d'afficher des notes. Un seul effet suffit ici :
   * la propriété affichée découle de la vue courante.
   */
  useEffect(() => {
    const parVue = {
      'ga4-dashboard': ['GOOGLE_ANALYTICS', ga4SelectedPropertyId],
      'matomo-dashboard': ['MATOMO', matomoSelectedSiteId],
      'piano-dashboard': ['PIANO_ANALYTICS', pianoSelectedSiteId]
    };
    const choix = parVue[currentView];
    if (!choix || !choix[1]) return;

    /**
     * Les sélecteurs retiennent l'identifiant **interne** de la propriété, tandis qu'une note
     * porte l'identifiant de la **source** — le numéro de propriété GA4, l'identifiant de site
     * Piano ou Matomo. Interroger l'API avec le premier ne ramenait jamais rien.
     *
     * `allAdAccounts` porte les deux, on s'en sert de table de correspondance ; à défaut on
     * envoie la valeur telle quelle, faute de mieux.
     */
    const compte = allAdAccounts.find(a => a.libraryId === choix[1] || a.id === choix[1] || a.sourceId === choix[1]);
    fetchDashboardNotes(choix[0], compte?.sourceId || compte?.id || choix[1], customStartDate, customEndDate);
  }, [currentView, ga4SelectedPropertyId, matomoSelectedSiteId, pianoSelectedSiteId,
    allAdAccounts, customStartDate, customEndDate, fetchDashboardNotes]);

  // Fonction pour récupérer les sources de données disponibles
  const fetchAvailableSources = async () => {
    if (!token) return;
    
    try {
      const params = {};
      if (selectedClientId) {
        params.clientId = selectedClientId;
      }
      
      const response = await axios.get(`${API_URL}/api/synthese/available-sources`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      if (response.data.success) {
        setAvailableSources(response.data.data.availableSources);
        console.log('🎯 Sources disponibles mises à jour:', response.data.data.availableSources);
      }
    } catch (error) {
      console.error('❌ Erreur récupération sources disponibles:', error);
      // En cas d'erreur, afficher toutes les sources par défaut
      setAvailableSources({
        google: true,
        microsoft: true,
        'facebook-ads': true,
        'pinterest-ads': true,
        'tiktok-ads': true,
        'chatgpt-ads': true,
            'awin': true,
        synthese: true
      });
    } finally {
      setSourcesDisponiblesChargees(true);
    }
  };

  // Récupérer les comptes inaccessibles (accès révoqué)
  const fetchInaccessibleAccounts = async () => {
    if (!token) return;

    try {
      const response = await axios.get(`${API_URL}/api/dashboard/account-access-status`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success && response.data.data.inaccessibleAccounts) {
        setInaccessibleAccounts(response.data.data.inaccessibleAccounts);
        if (response.data.data.count > 0) {
          console.log(`⚠️ [ACCESS] ${response.data.data.count} compte(s) inaccessible(s) détecté(s):`, response.data.data.inaccessibleAccounts);
        }
      }
    } catch (error) {
      console.error('❌ Erreur récupération comptes inaccessibles:', error);
    }
  };

  // Vérifier si l'utilisateur peut accéder à l'analyse IA (tous les plans payants)
  const canAccessAnalyzer = React.useCallback(() => {
    // Les admins ont toujours accès complet
    if (user && user.role === 'ADMIN') return true;

    if (!userSubscription) return false;
    return ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan);
  }, [user, userSubscription]);
  
  // Mémoriser l'accès pour éviter les re-calculs
  const hasAnalyzerAccess = React.useMemo(() => canAccessAnalyzer(), [canAccessAnalyzer]);

  /**
   * Qui peut lancer une analyse IA sur un dashboard.
   *
   * ## Deux régimes, et un seul endroit où les écrire
   *
   * Un **membre d'organisation** est jugé sur son plan, comme avant. Un **accès client** n'a pas
   * de plan — le sien porte `analysisLimit: 0` — mais une permission, accordée depuis
   * Organisation › Accès clients et transportée dans `clientTools`.
   *
   * ## Pourquoi un calcul commun
   *
   * Sept dashboards posent ce bouton. Cinq d'entre eux ne passaient **rien** : `autorise` valant
   * `true` par défaut, un accès client voyait le bouton, ouvrait le panneau, posait sa question —
   * et récoltait un 403 du serveur, qui lui, a toujours tranché. Une règle écrite une fois et
   * passée partout évite que le huitième dashboard répète l'oubli.
   *
   * La règle du serveur est la même, dans `aiAnalysisMiddleware` : elle fait foi, celle-ci
   * n'épargne qu'un aller-retour inutile.
   */
  const analyseAutorisee = React.useMemo(() => (
    user?.role === 'CLIENT'
      ? (clientTools || []).includes('analyse-ia')
      : hasAnalyzerAccess
  ), [user, clientTools, hasAnalyzerAccess]);

  const handleGoogleLogin = () => {
    setLoading(true);
    // Sauvegarder le hash actuel pour le restaurer après login
    if (window.location.hash) {
      localStorage.setItem('kokpeet_returnHash', window.location.hash);
      console.log('[AUTH] Hash sauvegardé pour après login:', window.location.hash);
    }
    window.location.href = `${API_URL}/api/auth/google`;
  };

  const handleLogout = () => {
    localStorage.removeItem('kokpeet_token');
    localStorage.removeItem('google_access_token');
    // Supprimer aussi la vue courante pour forcer le routage automatique à la prochaine connexion
    localStorage.removeItem('kokpeet_currentView');
    // Et la période retenue : elle appartient à celui qui vient de partir, pas au suivant.
    localStorage.removeItem(CLE_PERIODE);
    setToken(null);
    setUser(null);
    setCurrentView('home'); // Retour par défaut vers home
    setAnalysisResult(null);
    // Réinitialiser les données utilisateur pour éviter l'affichage de données du compte précédent
    setMatomoSites([]);
    setGA4Properties([]);
    setPianoSites([]);
    setUserSites([]);
    setAllAdAccounts([]);
    setUserOrganizations([]);
    setOrganizationsLoaded(false);
  };
  
  const handleViewChange = (view, skipHistory = false) => {
    setCurrentView(view);
    setError('');
    setSuccess('');

    // Réinitialiser la sous-vue diagnostic quand on change de vue ou qu'on revient sur diagnostic
    setDiagnosticSubView(null);

    // Ajouter à l'historique du navigateur pour que le bouton back fonctionne
    if (!skipHistory) {
      const clientParam = selectedClientId ? `?client=${selectedClientId}` : '';
      window.history.pushState({ view, clientId: selectedClientId }, '', `#${view}${clientParam}`);
    }
  };

  // Écouter le bouton back/forward du navigateur
  useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.view) {
        // Naviguer vers la vue précédente sans re-pousser dans l'historique
        setCurrentView(event.state.view);
        // Restaurer aussi le client si présent
        if (event.state.clientId !== undefined) {
          setSelectedClientId(event.state.clientId);
        }
        // Une entrée d'historique `#dashboard` sans plateforme désigne l'écran de
        // choix : sans cela le bouton précédent affichait l'URL de la sélection tout en
        // laissant le dashboard de la plateforme à l'écran.
        if (event.state.view === 'dashboard' && !event.state.dashboardType) {
          setCurrentDashboardType('selection');
        }
        // Restaurer le type de dashboard si présent
        if (event.state.view === 'dashboard' && event.state.dashboardType) {
          setCurrentDashboardType(event.state.dashboardType);
          // Déclencher la génération du dashboard
          setTimeout(() => {
            window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: event.state.dashboardType } }));
            window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: event.state.dashboardModel || 'global' } }));
            window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: event.state.dashboardType } }));
          }, 100);
        }
      } else {
        // Si pas d'état, essayer de lire depuis le hash ou aller à home
        const hashParts = window.location.hash.split('?');
        const hash = hashParts[0].replace('#', '');
        const hashSegments = hash.split('/');
        const viewFromHash = hashSegments[0];
        if (viewFromHash && VUES_ROUTABLES.includes(viewFromHash)) {
          setCurrentView(viewFromHash);

          // Si c'est un dashboard, restaurer aussi le type et le modèle
          // Format attendu: dashboard/google/global ou dashboard/microsoft/global
          if (viewFromHash === 'dashboard' && hashSegments.length >= 2) {
            const dashboardType = hashSegments[1]; // google, microsoft, facebook-ads, pinterest-ads, tiktok-ads
            if (DASHBOARD_PLATFORMS.includes(dashboardType)) {
              setCurrentDashboardType(dashboardType);
              // Déclencher la génération du dashboard
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: dashboardType } }));
                window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: dashboardType } }));
              }, 100);
            }
          }
        }
        // Restaurer le client depuis l'URL si présent
        const urlParams = new URLSearchParams(hashParts[1] || '');
        const clientFromUrl = urlParams.get('client');
        if (clientFromUrl) {
          setSelectedClientId(clientFromUrl);
        }
        // Restaurer l'account pour diagnostic depuis l'URL
        const accountFromUrl = urlParams.get('account');
        if (viewFromHash === 'diagnostic' && accountFromUrl) {
          console.log('[APP] PopState: diagnostic account détecté:', accountFromUrl);
          setDiagnosticInitialAccountId(accountFromUrl);
        }
        // Et la fiche d'un accès client, qui s'ouvrirait vide sans son email.
        if (viewFromHash === 'acces-client') {
          setFicheAccesOuverte(lireFicheAccesDuHash());
        }
        // Et le dashboard sur mesure, qui s'ouvrirait vide sans son identifiant.
        if (viewFromHash === 'dashboard-sur-mesure') {
          setDashboardSurMesureOuvert(urlParams.get('dashboard') || null);
          setVersionSurMesureOuverte(urlParams.get('version') || null);
        }
      }
    };

    window.addEventListener('popstate', handlePopState);

    // Gérer aussi hashchange pour les liens directs (ex: email)
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash.startsWith('#diagnostic?') || hash.startsWith('#diagnostic/?')) {
        const queryPart = hash.includes('?') ? hash.split('?')[1] : '';
        const params = new URLSearchParams(queryPart);
        const accountId = params.get('account');
        if (accountId) {
          console.log('[APP] HashChange: diagnostic account détecté:', accountId);
          setCurrentView('diagnostic');
          setDiagnosticInitialAccountId(accountId);
        }
      }
      // Gérer le hash #help pour le centre d'aide
      if (hash === '#help') {
        setCurrentView('help');
        return;
      }

      // Toute autre vue connue : un hash saisi à la main ou un lien interne ne
      // rechargent pas la page, ils n'émettent qu'un `hashchange`. Sans ce traitement la
      // vue courante ne bougeait pas, et l'effet qui synchronise l'URL de la page
      // d'accueil réécrivait aussitôt l'adresse — d'où l'impression d'être renvoyé sur
      // #home?period=… en tapant #dashboard ou #mcp.
      const segments = hash.replace(/^#/, '').split('?')[0].split('/');
      const vue = segments[0];
      if (vue && VUES_ROUTABLES.includes(vue)) {
        setCurrentView(vue);

        /*
         * ⚠️ Un dashboard sur mesure doit relire **ses paramètres**, pas seulement sa vue.
         *
         * Le lien « Prévisualiser » de l'onglet Versions ne recharge pas la page : il change le
         * hash, ce qui n'émet qu'un `hashchange`. Sans cette lecture, la vue basculait bien mais
         * `?dashboard=` et `?version=` restaient à leur valeur d'avant — on cliquait sur une
         * version et l'on voyait la précédente, sans aucun signe que quelque chose avait échoué.
         */
        if (vue === 'dashboard-sur-mesure') {
          const p = new URLSearchParams(hash.includes('?') ? hash.split('?')[1] : '');
          setDashboardSurMesureOuvert(p.get('dashboard') || null);
          setVersionSurMesureOuverte(p.get('version') || null);
        }

        // Même raison pour la fiche d'un accès client : la vue basculerait et l'on verrait
        // l'accès d'avant, sans qu'aucun signe ne dise que quelque chose a échoué.
        if (vue === 'acces-client') {
          setFicheAccesOuverte(lireFicheAccesDuHash(hash));
        }

        /*
         * L'onglet de l'écran Organisation vit dans le hash, en sous-chemin.
         *
         * ⚠️ **En sous-chemin, et non en paramètre** : la query de `#organization` est possédée
         * par trois écritures qui ne connaissent que `client=` et reconstruisent
         * `#${view}${clientParam}` — un `?onglet=` y serait effacé au premier changement
         * d'écran, en silence. Le sous-chemin survit, les lecteurs ne retenant que le premier
         * segment.
         */
        if (vue === 'organization') {
          setOngletOrganisation(segments[1] || null);
        }

        // Dashboard : restaurer aussi la plateforme, comme le fait le bouton précédent
        if (vue === 'dashboard') {
          if (DASHBOARD_PLATFORMS.includes(segments[1])) {
            setCurrentDashboardType(segments[1]);
            setTimeout(() => {
              window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: segments[1] } }));
              window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: segments[2] || 'global' } }));
              window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: segments[1] } }));
            }, 100);
          } else {
            // `#dashboard` nu : retour à l'écran de choix de la plateforme
            setCurrentDashboardType('selection');
          }
        }
      }
    };
    window.addEventListener('hashchange', handleHashChange);

    // Initialiser l'état de l'historique avec la vue courante et le client
    // Ne pas écraser si on vient de naviguer vers un dashboard avec account
    // Ne pas écraser si on est sur #tools/xxx (préserver l'outil actif)
    console.log('🔍 [EFFECT] currentView:', currentView, 'hash:', window.location.hash, 'state:', window.history.state);
    const currentHash = window.location.hash;
    const isToolsWithSubpath = currentHash.startsWith('#tools/') && currentHash.length > 7;
    /*
     * ⚠️ **La fiche d'un accès client porte ses paramètres dans l'URL, et cet effet les jette.**
     *
     * Sur un chargement neuf — un lien collé, un F5 — `window.history.state` est nul, donc la
     * condition ci-dessous est vraie et l'URL est réécrite en `#${currentView}${clientParam}` :
     * `?org=` et `?email=` disparaissent, et la fiche s'ouvre vide sur une adresse qui était
     * juste. C'est pour cela que `#tools/…` a son exemption ; `dashboard-sur-mesure`, lui, n'y
     * survit que **par accident**, parce que `updateAnalyticsUrl` réécrit ses paramètres juste
     * après. On ne compte pas sur l'accident : l'exemption est nommée, et
     * `mettreAJourUrlFicheAcces` tient l'adresse ensuite.
     */
    const estUneFicheAcces = currentView === 'acces-client';
    /*
     * ⚠️ **Et l'onglet d'Organisation est un sous-chemin, qui se fait écraser de la même façon.**
     *
     * Relevé à l'écran : ouvrir `#organization/clients` journalisait « Écrasement URL vers
     * #organization » — le `/clients` parti. `mettreAJourUrlFicheAcces`, déclaré plus bas, le
     * rétablissait dans la même passe, si bien que le résultat était juste **par le seul effet
     * de l'ordre de déclaration**. C'est précisément ce dont `dashboard-sur-mesure` dépend
     * déjà, et qu'on s'est refusé à reproduire : l'exemption est nommée.
     */
    const estUnOngletOrganisation = currentHash.startsWith('#organization/');
    // Lire réellement les paramètres : `account` n'est pas toujours en tête de la
    // query (updateUrlWithAccount écrit client, puis site, puis account), et un
    // test sur la chaîne « ?account= » laissait alors écraser toute l'URL.
    const currentHashParams = new URLSearchParams(currentHash.split('?')[1] || '');
    if (!window.history.state?.view && !currentHashParams.has('account')
        && !isToolsWithSubpath && !estUneFicheAcces && !estUnOngletOrganisation) {
      const clientParam = selectedClientId ? `?client=${selectedClientId}` : '';
      console.log('🔍 [EFFECT] Écrasement URL vers:', `#${currentView}${clientParam}`);
      // Inclure pathname pour s'assurer que les query params sont nettoyés
      window.history.replaceState({ view: currentView, clientId: selectedClientId }, '', `${window.location.pathname}#${currentView}${clientParam}`);
    }

    return () => {
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [currentView, selectedClientId]);

  /**
   * Tient l'adresse de la fiche d'un accès client, et celle de l'onglet d'Organisation.
   *
   * ⚠️ **Déclaré après l'effet qui réécrit l'URL, jamais avant.** Les deux tournent dans la même
   * passe, dans l'ordre de déclaration : placé plus haut, son `replaceState` serait aussitôt
   * écrasé par celui de l'autre, qui ne connaît que `client=`. C'est l'avertissement déjà écrit
   * au-dessus de l'effet qui ramène au choix d'une solution de mesure.
   *
   * `replaceState` et non `pushState` : l'entrée d'historique est posée par
   * `ouvrirFicheAccesClient`. En ajouter une seconde ici obligerait à cliquer deux fois sur
   * « précédent » pour sortir d'une fiche.
   */
  useEffect(() => {
    if (currentView === 'acces-client' && ficheAccesOuverte) {
      const adresse = `#acces-client?org=${encodeURIComponent(ficheAccesOuverte.org)}`
        + `&email=${encodeURIComponent(ficheAccesOuverte.email)}`;
      if (window.location.hash !== adresse) {
        window.history.replaceState(
          { view: 'acces-client', ...ficheAccesOuverte }, '', `${window.location.pathname}${adresse}`);
      }
    }

    if (currentView === 'organization' && ongletOrganisation) {
      const adresse = `#organization/${ongletOrganisation}`;
      if (window.location.hash !== adresse) {
        window.history.replaceState(
          { view: 'organization', onglet: ongletOrganisation }, '',
          `${window.location.pathname}${adresse}`);
      }
    }
  }, [currentView, ficheAccesOuverte, ongletOrganisation]);

  // Wrapper de setCurrentView qui pousse dans l'historique (pour les composants enfants)
  const setCurrentViewWithHistory = useCallback((view) => {
    setCurrentView(view);
    const clientParam = selectedClientId ? `?client=${selectedClientId}` : '';
    window.history.pushState({ view, clientId: selectedClientId }, '', `#${view}${clientParam}`);
  }, [selectedClientId]);

  /**
   * Ouvre la fiche d'un accès client, depuis sa ligne dans l'onglet « Accès clients ».
   *
   * Déclaré ici, après `setCurrentViewWithHistory` : le citer en dépendance plus haut le lirait
   * pendant le rendu, et la zone morte de `const` rendrait la fonction indéfinie — le piège que
   * raconte déjà le commentaire d'`ouvrirChoixDashboard`.
   *
   * ⚠️ L'email est encodé, sans exception : un `+` non encodé revient en espace et la fiche
   * répondrait 404 sur un accès qui existe.
   */
  const ouvrirFicheAccesClient = useCallback((orgId, email) => {
    setFicheAccesOuverte({ org: orgId, email });
    setCurrentView('acces-client');
    window.history.pushState(
      { view: 'acces-client', org: orgId, email },
      '',
      `#acces-client?org=${encodeURIComponent(orgId)}&email=${encodeURIComponent(email)}`
    );
  }, []);

  /** Revient de la fiche à l'onglet « Accès clients », et non à « Membres ». */
  const revenirAuxAccesClients = useCallback(() => {
    setFicheAccesOuverte(null);
    setOngletOrganisation('clients');
    setCurrentView('organization');
    window.history.pushState({ view: 'organization' }, '', '#organization/clients');
  }, []);

  /**
   * Ouvre l'un des écrans de notes depuis les onglets de la modale.
   *
   * La modale sait présenter les trois façons d'entrer des notes, mais pas naviguer : deux de
   * ses onglets sont des vues. Elle les délègue ici, d'où qu'elle ait été ouverte — le menu
   * latéral comme l'archive.
   */
  const ouvrirVueNotes = useCallback((vue) => {
    setIsNotesModalOpen(false);
    setEditingNote(null);
    // « La vue d'avant les notes » : passer d'un écran de notes à un autre ne l'écrase pas,
    // sinon le dashboard d'où l'on vient serait perdu et la sidebar s'y replierait.
    setPreviousViewBeforeNotes(prev => (
      ['notes-archive', 'notes-calendrier', 'notes-import'].includes(currentView) ? prev : currentView
    ));
    setCurrentViewWithHistory(vue);
  }, [currentView, setCurrentViewWithHistory]);

  /**
   * Ouvre le dashboard consolidé sur une solution de mesure, éventuellement sur l'une de
   * ses propriétés.
   *
   * La propriété est explicitement remise à zéro quand elle n'est pas fournie : sans cela,
   * choisir « Piano Analytics » après une visite en GA4 rouvrirait la propriété GA4 de la
   * fois précédente, et le tableau ne correspondrait plus à l'entrée cliquée.
   */
  const ouvrirConsolide = useCallback((typeSource, propertyId = null) => {
    setConsolideSelectedSource(typeSource || null);
    setConsolideSelectedPropertyId(propertyId);
    setCurrentViewWithHistory('consolide-dashboard');
  }, [setCurrentViewWithHistory]);

  /**
   * Ouvre le dashboard d'une solution de mesure, cadré sur la propriété cliquée.
   *
   * Extrait pour être appelé de deux endroits : l'écran de choix, et le raccourci ci-dessous
   * quand il n'y a pas de choix à faire.
   */
  const ouvrirDashboardAnalytics = useCallback((typeSource, siteId = null) => {
    /*
     * Les régies n'ont pas de vue dédiée : elles partagent l'écran `dashboard`, qui se règle par
     * `currentDashboardType` et lit le compte dans `localStorage` — c'est le même chemin que
     * `navigateToDashboardWithAccount`, qu'on ne peut pas appeler d'ici : il est déclaré deux
     * cents lignes plus bas, et le citer en dépendance le lirait pendant le rendu.
     */
    const onglet = ONGLET_PAR_TYPE_DE_COMPTE[typeSource];
    if (onglet) {
      if (siteId) {
        localStorage.setItem('kokpeet_selectedAccount', JSON.stringify({
          accountId: siteId, accountType: typeSource
        }));
        localStorage.setItem('kokpeet_targetTab', onglet);
      }
      setCurrentDashboardType(onglet);
      setCurrentViewWithHistory('dashboard');
      return;
    }

    if (typeSource === 'MATOMO') {
      if (siteId) setMatomoSelectedSiteId(siteId);
      setCurrentViewWithHistory('matomo-dashboard');
    } else if (typeSource === 'PIANO_ANALYTICS') {
      if (siteId) setPianoSelectedSiteId(siteId);
      setCurrentViewWithHistory('piano-dashboard');
    } else if (typeSource === 'PRESTASHOP') {
      /*
       * ⚠️ **Une branche explicite, et non le `else`.** Celui-ci ouvre Google Analytics : sans
       * cette ligne, un clic sur une boutique ouvrirait le dashboard GA4 en lui injectant un
       * identifiant de boutique comme propriété. C'est précisément le défaut que le commentaire
       * de `DASHBOARD_PAR_TYPE` décrit comme « le `default` de ce genre de switch ».
       */
      if (siteId) setPrestashopBoutiqueId(siteId);
      setCurrentViewWithHistory('prestashop-dashboard');
    } else {
      if (siteId) setGA4SelectedPropertyId(siteId);
      setCurrentViewWithHistory('ga4-dashboard');
    }
  }, [setCurrentViewWithHistory]);

  /** Ouvre un dashboard sur mesure. */
  const ouvrirDashboardSurMesure = useCallback((id, versionId = null) => {
    setDashboardSurMesureOuvert(id);
    // Sans cette remise à zéro, ouvrir un dashboard depuis l'écran de choix après avoir
    // prévisualisé un candidat montrerait encore le candidat — en se présentant comme la
    // production.
    setVersionSurMesureOuverte(versionId);
    setCurrentViewWithHistory('dashboard-sur-mesure');
  }, [setCurrentViewWithHistory]);

  /**
   * Les comptes que la sélection courante laisse voir, par identifiant interne — ou `null`
   * quand rien n'est sélectionné, c'est-à-dire quand tout le périmètre est ouvert.
   *
   * ⚠️ **La liste des dashboards sur mesure est chargée une fois, pour tout le périmètre**, et
   * elle ignore le client choisi dans l'en-tête — c'est la bonne borne pour le serveur, qui ne
   * connaît pas ce sélecteur. Sans ce tamis, l'écran de choix atteint depuis la barre latérale,
   * qui ne désigne aucun compte, proposait à un client le dashboard écrit pour un autre.
   *
   * ⚠️ **Les trois solutions de mesure ne se rapprochent pas comme les régies.** Un
   * rattachement s'écrit sous trois formes, dont la forme qualifiée `matomo-{connexion}-{numéro}`
   * que `compteAutorise` ne connaît pas : on réemploie donc les listes déjà bornées plus haut
   * plutôt que de recopier la comparaison — recopiée, elle déclarerait orphelin ce qui est
   * rattaché. Les régies, elles, passent par `ensembleAutorise` / `compteAutorise`.
   */
  const comptesDuPerimetreCourant = useMemo(() => {
    const estAccesClient = user?.role === 'CLIENT';
    // Exactement la porte des trois listes filtrées : rien de sélectionné, rien à borner.
    if (!selectedClientId && (!selectedSiteId || selectedSiteId === 'all') && !estAccesClient) {
      return null;
    }

    const cles = new Set();
    /*
     * L'identifiant **interne** seulement. Un `sourceId` ne distingue pas deux instances — le
     * site Matomo n° 3 existe chez plusieurs clients — et le retenir ouvrirait ici la porte
     * qu'on vient de fermer.
     */
    const retenir = (compte) => {
      const interne = compte?.databaseId || compte?.libraryId || compte?.id;
      if (interne) cles.add(String(interne));
    };

    filteredGA4Properties.forEach(retenir);
    filteredMatomoSites.forEach(retenir);
    filteredPianoSites.forEach(retenir);

    const sitesDuPerimetre = selectedSiteId && selectedSiteId !== 'all'
      ? userSites.filter(site => (site._id || site.id) === selectedSiteId)
      : (selectedClientId ? userSites.filter(site => site.clientId === selectedClientId) : userSites);
    const autorises = ensembleAutorise(
      sitesDuPerimetre.flatMap(site => (Array.isArray(site.accountIds) ? site.accountIds : []))
    );
    allAdAccounts
      .filter(compte => !['GOOGLE_ANALYTICS', 'MATOMO', 'PIANO_ANALYTICS'].includes(compte.type))
      .filter(compte => compteAutorise(compte, autorises))
      .forEach(retenir);

    return cles;
  }, [
    user, selectedClientId, selectedSiteId, userSites, allAdAccounts,
    filteredGA4Properties, filteredMatomoSites, filteredPianoSites
  ]);

  /**
   * Les dashboards sur mesure d'un compte de solution de mesure.
   *
   * ⚠️ **Les deux écritures d'identifiant comptent.** Le `siteId` que reçoit cet écran n'a pas la
   * même nature selon la solution : `normaliserSitesDeMesure` fait de `id` l'identifiant interne
   * pour Matomo et Piano, mais la liste GA4 est poussée brute, où `id` est l'identifiant de
   * propriété et `databaseId` l'identifiant interne. Une fiche, elle, ne porte que l'identifiant
   * interne — d'où la comparaison avec le compte résolu que le serveur joint à chaque ligne.
   *
   * Sans `siteId` — la barre latérale, qui mène à une solution sans désigner de compte —, on
   * rend les dashboards de la solution **qui sont dans le périmètre courant**, et non ceux de
   * toute l'organisation : voir `comptesDuPerimetreCourant` juste au-dessus.
   */
  /**
   * Cette fiche est-elle dans le périmètre courant ?
   *
   * ⚠️ **Un dashboard sur mesure est attaché à un COMPTE, pas à un client** : il porte un
   * `libraryItemId`, et le lien au client passe par compte → site → client. Rien ne le borne
   * donc tout seul — c'est ce tamis, et lui seul, qui tient compte du client choisi dans
   * l'en-tête. La liste arrive du serveur bornée au périmètre entier, parce que le serveur
   * ignore ce sélecteur.
   *
   * Extrait parce que deux écrans se posent la question : l'écran de choix et l'accueil.
   * L'accueil ne le faisait pas, et montrait les mêmes fiches quel que soit le client choisi.
   */
  const dansLePerimetreCourant = useCallback((d) => (
    Boolean(d?.actif)
    && (!comptesDuPerimetreCourant || comptesDuPerimetreCourant.has(String(d.libraryItemId)))
  ), [comptesDuPerimetreCourant]);

  /** Les fiches du périmètre courant, tous comptes confondus — ce que l'accueil propose. */
  const dashboardsSurMesureDuPerimetre = useMemo(
    () => dashboardsSurMesure.filter(dansLePerimetreCourant),
    [dashboardsSurMesure, dansLePerimetreCourant]
  );

  const dashboardsSurMesurePour = useCallback((typeSource, siteId = null) => {
    return dashboardsSurMesure.filter((d) => {
      /*
       * ⚠️ On compare le type **du compte**, pas celui de la fiche.
       *
       * `d.sourceType` est un nom de `SOURCE_SCHEMAS` — celui que le serveur passe à `getData` —,
       * alors que `typeSource` vient d'ici et porte le vocabulaire de la bibliothèque. Les deux
       * diffèrent sur Google Analytics (`GOOGLE_ANALYTICS` / `GOOGLE_ANALYTICS_4`) et sur Meta :
       * comparés, ils ne coïncideraient jamais, et un dashboard Google Analytics ne serait jamais
       * proposé dans l'écran de choix. `d.compte.type` est du même vocabulaire que `typeSource`,
       * et ça évite de recopier ici la table de traduction du serveur.
       *
       * Un dashboard dont le compte a disparu ne s'affiche pas : il ne pourrait pas s'ouvrir.
       */
      if (!d.compte || d.compte.type !== typeSource) return false;
      if (!d.actif) return false;
      // Même tamis que l'accueil : deux lectures du périmètre divergeraient.
      if (!siteId) return dansLePerimetreCourant(d);
      const cle = String(siteId);
      return d.libraryItemId === cle
        || d.compte?.id === cle
        || (d.compte?.sourceId != null && String(d.compte.sourceId) === cle);
    });
  }, [dashboardsSurMesure, dansLePerimetreCourant]);

  /**
   * Les destinations réellement ouvertes pour une ligne donnée.
   *
   * Le test portait sur le seul droit `consolidated-ga4` : « sans le droit, la page de choix
   * n'offrirait qu'une porte ouverte et une porte murée ». Avec un troisième type de dashboard,
   * cette lecture devient fausse **dans les deux sens** — un client sans le consolidé mais avec un
   * dashboard sur mesure a bien un choix, et on l'envoyait droit au dashboard de sa solution de
   * mesure sans qu'il apprenne jamais que l'autre existe.
   *
   * Le repli « ne pas savoir n'est pas une autorisation » est conservé, et pour les deux sources :
   * ni les droits ni la liste des dashboards ne comptent tant qu'ils ne sont pas chargés.
   */
  const destinationsDuChoix = useCallback((typeSource, siteId = null) => {
    /*
     * ⚠️ **Le dashboard de la plateforme elle-même n'est plus inconditionnel.** Il l'était —
     * `['analytics']` en dur —, parce qu'aucun droit ne le gouvernait. Depuis qu'une agence
     * peut le fermer, l'offrir quand même mènerait à un écran que le serveur refuse en 403,
     * ce qui se lit comme une panne alors que c'est un réglage.
     */
    const ouvertes = plateformeOuvertePourType(typeSource) ? ['analytics'] : [];
    /*
     * ⚠️ Le consolidé croise une **audience** avec les régies : il n'a aucun sens sur un compte
     * de régie, qui est déjà l'un des deux termes du croisement. Sans cette borne, une ligne
     * Google Ads portant un dashboard sur mesure proposerait « Dashboard consolidé » — une carte
     * qui mène à un écran vide.
     */
    /*
     * ⚠️ **Une liste blanche, et non la négation des régies.** `!ONGLET_PAR_TYPE_DE_COMPTE[…]`
     * rangeait parmi les solutions de mesure tout ce qui n'est pas une régie — donc une boutique
     * PrestaShop, qui n'en est pas une. La carte « Dashboard consolidé » s'ouvrait alors avec
     * `consolideSelectedSource = 'PRESTASHOP'`, que le consolidé ne sait pas croiser : une carte
     * qui mène à un écran vide, exactement ce que la borne existait pour empêcher.
     */
    const SOLUTIONS_DE_MESURE = ['GOOGLE_ANALYTICS', 'MATOMO', 'MATOMO_OAUTH', 'PIANO_ANALYTICS'];
    const estUneSolutionDeMesure = SOLUTIONS_DE_MESURE.includes(typeSource);
    if (estUneSolutionDeMesure && clientToolsLoaded && clientTools.includes('consolidated-ga4')) {
      ouvertes.push('consolide');
    }
    if (dashboardsSurMesureCharges && dashboardsSurMesurePour(typeSource, siteId).length > 0) {
      ouvertes.push('sur-mesure');
    }
    return ouvertes;
  }, [clientTools, clientToolsLoaded, dashboardsSurMesureCharges, dashboardsSurMesurePour,
      plateformeOuvertePourType]);

  /**
   * Ouvre le choix du dashboard pour une ligne de l'accueil — **s'il y a un choix**.
   *
   * Une seule destination ouverte, et l'écran de choix n'offrirait qu'une porte ouverte et des
   * portes murées : on mène alors directement à cette destination. Un accès client ne doit pas
   * même apprendre l'existence de ce qui ne lui est pas ouvert.
   *
   * Déclaré ici et non avec son état, à côté de `ouvrirConsolide` : tous deux referment sur
   * `setCurrentViewWithHistory`, défini quelques lignes plus haut. Placé avant, le `useCallback`
   * lisait une liaison encore dans sa zone morte et l'application ne montait plus.
   */
  const ouvrirChoixDashboard = useCallback((typeSource, siteId = null, nom = null, origine = 'home') => {
    const ouvertes = destinationsDuChoix(typeSource, siteId);

    /*
     * ⚠️ **Aucune destination : on ne va nulle part.** Le cas est né avec le droit par
     * plateforme — un client à qui l'agence a fermé ce dashboard, et qui n'a ni consolidé ni
     * sur-mesure. Avant lui, la liste contenait toujours au moins « analytics », et le
     * raccourci ci-dessous pouvait s'y fier.
     */
    if (ouvertes.length === 0) return;

    /*
     * ⚠️ **Une seule destination : encore faut-il savoir laquelle.** Ce raccourci ouvrait le
     * dashboard de plateforme sans regarder — ce qui était juste tant qu'« analytics » était
     * inconditionnelle, et devient faux depuis qu'elle peut manquer : un client n'ayant que le
     * consolidé aurait été envoyé droit sur l'écran qu'on venait de lui fermer.
     */
    if (ouvertes.length === 1) {
      if (ouvertes[0] === 'analytics') {
        ouvrirDashboardAnalytics(typeSource, siteId);
        return;
      }
      if (ouvertes[0] === 'consolide') {
        ouvrirConsolide(typeSource, siteId);
        return;
      }
      // Un dashboard sur mesure seul : l'écran de choix le présente, parce qu'il peut y en
      // avoir plusieurs et que rien ici ne sait lequel ouvrir.
    }
    // `origine` est mémorisée parce que deux écrans y mènent désormais : l'accueil et le
    // dashboard. Un « Retour à l'accueil » servi à qui vient du dashboard le déplacerait sans
    // qu'il l'ait demandé — et le bouton mentirait sur ce qu'il fait.
    setChoixDashboard({ typeSource, siteId, nom, origine });
    setCurrentViewWithHistory('choix-dashboard');
  }, [setCurrentViewWithHistory, ouvrirDashboardAnalytics, ouvrirConsolide, destinationsDuChoix]);

  // Fonction pour mettre à jour l'URL des vues analytics (GA4, Matomo, Piano)
  const updateAnalyticsUrl = useCallback(() => {
    const params = new URLSearchParams();
    if (selectedClientId) params.set('client', selectedClientId);
    if (selectedSiteId && selectedSiteId !== 'all') params.set('site', selectedSiteId);
    // Ajouter le compte/propriété selon la vue (avant period pour un ordre logique)
    if (currentView === 'ga4-dashboard' && ga4SelectedPropertyId) {
      params.set('property', ga4SelectedPropertyId);
    } else if (currentView === 'matomo-dashboard' && matomoSelectedSiteId) {
      params.set('matomoSite', matomoSelectedSiteId);
    } else if (currentView === 'piano-dashboard' && pianoSelectedSiteId) {
      params.set('pianoSite', pianoSelectedSiteId);
    } else if (currentView === 'dashboard-sur-mesure' && dashboardSurMesureOuvert) {
      params.set('dashboard', dashboardSurMesureOuvert);
      // La version voyage dans l'URL : un aperçu de candidat doit pouvoir se recopier et se
      // rouvrir, sinon il n'est pas montrable à un collègue.
      if (versionSurMesureOuverte) params.set('version', versionSurMesureOuverte);
    }
    if (selectedPeriod) params.set('period', selectedPeriod);
    if (selectedPeriod === 'CUSTOM' && customStartDate) params.set('startDate', customStartDate);
    if (selectedPeriod === 'CUSTOM' && customEndDate) params.set('endDate', customEndDate);
    if (globalCompareEnabled) {
      params.set('compare', 'true');
      params.set('compareType', globalComparisonType);
      if (globalComparisonType === 'custom' && comparisonStartDate) params.set('compareStart', comparisonStartDate);
      if (globalComparisonType === 'custom' && comparisonEndDate) params.set('compareEnd', comparisonEndDate);
    }
    const queryString = params.toString();
    window.history.replaceState(
      { view: currentView, clientId: selectedClientId },
      '',
      `#${currentView}${queryString ? '?' + queryString : ''}`
    );
  }, [currentView, selectedClientId, selectedSiteId, selectedPeriod, customStartDate, customEndDate, globalCompareEnabled, globalComparisonType, comparisonStartDate, comparisonEndDate, ga4SelectedPropertyId, matomoSelectedSiteId, pianoSelectedSiteId, dashboardSurMesureOuvert, versionSurMesureOuverte]);

  // Mettre à jour l'URL quand les paramètres analytics changent
  useEffect(() => {
    if (['ga4-dashboard', 'matomo-dashboard', 'piano-dashboard', 'dashboard-sur-mesure'].includes(currentView)) {
      updateAnalyticsUrl();
    }
  }, [currentView, selectedClientId, selectedSiteId, selectedPeriod, customStartDate, customEndDate, globalCompareEnabled, globalComparisonType, comparisonStartDate, comparisonEndDate, ga4SelectedPropertyId, matomoSelectedSiteId, pianoSelectedSiteId, dashboardSurMesureOuvert, versionSurMesureOuverte, updateAnalyticsUrl]);

  /**
   * Changer de client ne doit pas laisser un dashboard que le client suivant n'a pas.
   *
   * La vue est délibérément conservée au changement de client — on veut relire le même écran
   * pour le client suivant. Mais le client suivant n'a pas forcément la solution de mesure :
   * on restait alors sur le dashboard Piano devant « Aucun site Piano Analytics associé »,
   * sans rien pour en sortir, la section Analytics de la barre latérale ayant disparu du même
   * coup. On revient donc à l'écran de choix du dashboard — le même geste que le fil d'Ariane
   * de ces trois vues, parce que c'est le seul écran qui nomme ce que le nouveau client a.
   *
   * ⚠️ **La liste complète de la solution sert de preuve de chargement.** Les sites Piano sont
   * rapatriés par la vue elle-même : sans ce garde-fou, un changement de client pendant le
   * chargement lirait une liste filtrée vide et renverrait sur la sélection un client qui a
   * bien un compte Piano. Il écarte du même coup le cas « aucune solution connectée », où
   * l'écran propose d'ajouter un connecteur — là, il a quelque chose à dire.
   *
   * ⚠️ **Les dashboards de régie ne sont pas concernés, et c'est un choix antérieur** : voir la
   * note au-dessus de l'effet qui rafraîchit `availableSources`. On y laisse l'utilisateur sur
   * la plateforme choisie, devant son « Connecter … » ; et il est déjà sur la vue `dashboard`,
   * donc la destination demandée ici ne le déplacerait pas.
   *
   * ⚠️ **Après l'effet qui réécrit l'URL des vues analytics, jamais avant.** Les deux tournent
   * dans la même passe, dans l'ordre où ils sont déclarés : placé plus haut, le `pushState`
   * d'ici serait aussitôt écrasé par le `replaceState` de l'autre, qui lit encore la vue d'avant
   * — l'adresse annoncerait `#piano-dashboard` avec l'écran de choix affiché.
   */
  const clientPrecedentPourDashboard = useRef(selectedClientId);
  useEffect(() => {
    const clientChange = clientPrecedentPourDashboard.current !== selectedClientId;
    clientPrecedentPourDashboard.current = selectedClientId;
    if (!clientChange) return;

    // [toutes les propriétés de la solution, celles du client] — la vue en désigne une paire.
    const paire = {
      'ga4-dashboard': [ga4Properties, filteredGA4Properties],
      'matomo-dashboard': [matomoSites, filteredMatomoSites],
      'piano-dashboard': [pianoSites, filteredPianoSites]
    }[currentView];

    if (!paire || paire[0].length === 0 || paire[1].length > 0) return;

    console.log('[DASHBOARD] Client sans compte pour', currentView, '→ retour à la sélection');
    setCurrentDashboardType('selection');
    setCurrentViewWithHistory('dashboard');
  }, [selectedClientId, currentView, ga4Properties, filteredGA4Properties,
    matomoSites, filteredMatomoSites, pianoSites, filteredPianoSites,
    setCurrentViewWithHistory]);

  // Fonction pour mettre à jour l'URL de la homepage avec client, site, période et comparaison
  const updateHomeUrl = useCallback((siteId) => {
    const params = new URLSearchParams();
    if (selectedClientId) params.set('client', selectedClientId);
    const effectiveSiteId = siteId !== undefined ? siteId : selectedSiteId;
    if (effectiveSiteId && effectiveSiteId !== 'all') params.set('site', effectiveSiteId);
    if (selectedPeriod) params.set('period', selectedPeriod);
    if (selectedPeriod === 'CUSTOM' && customStartDate) params.set('startDate', customStartDate);
    if (selectedPeriod === 'CUSTOM' && customEndDate) params.set('endDate', customEndDate);
    if (globalCompareEnabled) {
      params.set('compare', 'true');
      params.set('compareType', globalComparisonType);
      if (globalComparisonType === 'custom' && comparisonStartDate) params.set('compareStart', comparisonStartDate);
      if (globalComparisonType === 'custom' && comparisonEndDate) params.set('compareEnd', comparisonEndDate);
    }
    const queryString = params.toString();
    window.history.replaceState(
      { view: currentView, clientId: selectedClientId, siteId: effectiveSiteId },
      '',
      `#${currentView}${queryString ? '?' + queryString : ''}`
    );
  }, [currentView, selectedClientId, selectedSiteId, selectedPeriod, customStartDate, customEndDate, globalCompareEnabled, globalComparisonType, comparisonStartDate, comparisonEndDate]);

  // Mettre à jour l'URL de la homepage quand les paramètres changent
  useEffect(() => {
    if (currentView === 'home') {
      updateHomeUrl();
    }
  }, [currentView, selectedClientId, selectedSiteId, selectedPeriod, customStartDate, customEndDate, globalCompareEnabled, globalComparisonType, comparisonStartDate, comparisonEndDate, updateHomeUrl]);

  // Mettre à jour l'URL de la vue admin quand l'onglet ou le ticket change
  useEffect(() => {
    if (currentView === 'admin') {
      let url = '#admin';
      if (adminTab && adminTab !== 'dashboard') {
        url = `#admin/${adminTab}`;
      }
      if (adminTab === 'tickets' && adminSelectedTicketId) {
        url = `#admin/tickets/${adminSelectedTicketId}`;
      }
      window.history.replaceState({ view: 'admin', adminTab, ticketId: adminSelectedTicketId }, '', url);
    }
  }, [currentView, adminTab, adminSelectedTicketId]);

  // Callback pour la sélection de ticket dans AdminPanel
  const handleAdminTicketSelect = useCallback((ticketId) => {
    setAdminSelectedTicketId(ticketId);
  }, []);

  // Mettre à jour l'URL de la vue tickets quand le ticket sélectionné change
  useEffect(() => {
    if (currentView === 'tickets') {
      const url = ticketsSelectedTicketId ? `#tickets/${ticketsSelectedTicketId}` : '#tickets';
      window.history.replaceState({ view: 'tickets', ticketId: ticketsSelectedTicketId }, '', url);
    }
  }, [currentView, ticketsSelectedTicketId]);

  // Callback pour la sélection de ticket dans TicketsView
  const handleUserTicketSelect = useCallback((ticketId) => {
    setTicketsSelectedTicketId(ticketId);
  }, []);

  // Navigation vers dashboard avec account (pour HomePage)
  const navigateToDashboardWithAccount = useCallback((dashboardType, accountId) => {
    setCurrentDashboardType(dashboardType);
    setCurrentView('dashboard');

    // Stocker l'accountId dans localStorage (même mécanisme que SyntheseView)
    if (accountId) {
      const navigationData = {
        accountId: accountId,
        // Le `GOOGLE_ADS` par défaut de la version précédente écrasait le type que l'appelant
        // venait d'écrire : une ligne TikTok repartait d'ici annoncée comme un compte Google.
        accountType: TYPE_DE_COMPTE_PAR_ONGLET[dashboardType] || 'GOOGLE_ADS'
      };
      localStorage.setItem('kokpeet_selectedAccount', JSON.stringify(navigationData));
      localStorage.setItem('kokpeet_targetTab', dashboardType);
    }

    // URL sans le paramètre account (évite les bugs de persistance au changement de client)
    const params = new URLSearchParams();
    if (selectedClientId) params.set('client', selectedClientId);
    const queryString = params.toString();
    const newHash = `#dashboard/${dashboardType}/global${queryString ? '?' + queryString : ''}`;

    window.history.pushState({
      view: 'dashboard',
      dashboardType,
      dashboardModel: 'global',
      clientId: selectedClientId
    }, '', newHash);
  }, [selectedClientId]);

  // Navigation vers diagnostic avec account (pour HomePage widgets)
  const navigateToDiagnosticWithAccount = useCallback((accountId) => {
    if (accountId) {
      setDiagnosticInitialAccountId(accountId);
      // Dispatcher l'événement pour que la sidebar charge l'historique
      window.dispatchEvent(new CustomEvent('diagnosticAccountChange', {
        detail: { accountId }
      }));
    }

    const params = new URLSearchParams();
    if (selectedClientId) params.set('client', selectedClientId);
    if (accountId) params.set('account', accountId);
    const queryString = params.toString();
    const newHash = `#diagnostic${queryString ? '?' + queryString : ''}`;

    // D'abord pousser l'URL, puis changer la vue
    window.history.pushState({
      view: 'diagnostic',
      clientId: selectedClientId,
      accountId
    }, '', newHash);

    setCurrentView('diagnostic');
  }, [selectedClientId]);

  // Test du connecteur OpenWeatherMap
  const testOpenWeatherMap = async () => {
    setWeatherTesting(true);
    setWeatherTestResult(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/connectors/openweathermap/test`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setWeatherTestResult({
        success: true,
        message: `✅ Connecteur fonctionnel ! ${response.data.citiesAvailable} disponibles`,
        details: response.data
      });
    } catch (error) {
      setWeatherTestResult({
        success: false,
        message: '❌ Erreur de connexion au service météo',
        details: error.response?.data || error.message
      });
    }
    
    setWeatherTesting(false);
  };

  // Test du connecteur Open-Meteo
  const testOpenMeteo = async () => {
    setOpenMeteoTesting(true);
    setOpenMeteoTestResult(null);
    
    try {
      const response = await axios.get(`${API_URL}/api/connectors/open-meteo/test`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      setOpenMeteoTestResult({
        success: true,
        message: `✅ Connecteur fonctionnel ! ${response.data.citiesAvailable}`,
        details: response.data
      });
    } catch (error) {
      setOpenMeteoTestResult({
        success: false,
        message: '❌ Erreur de connexion au service Open-Meteo',
        details: error.response?.data || error.message
      });
    }
    
    setOpenMeteoTesting(false);
  };

  // Fonction pour activer/désactiver les connecteurs météo
  const handleWeatherConnector = async (provider, action) => {
    try {
      const response = await axios.post(`${API_URL}/api/connectors/weather/${action}`, {
        provider: provider
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        // Recharger la liste des connecteurs
        const connectorsResponse = await axios.get(`${API_URL}/api/connectors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setGlobalConnectors(connectorsResponse.data.connectors);
        window.globalConnectors = connectorsResponse.data.connectors;
        
        setSuccess(`Connecteur ${provider === 'OPENWEATHERMAP' ? 'OpenWeatherMap' : 'Open-Meteo'} ${action === 'activate' ? 'activé' : 'désactivé'} avec succès`);
      }
    } catch (error) {
      console.error('Erreur connecteur météo:', error);
      setError(`Erreur lors de ${action === 'activate' ? 'l\'activation' : 'la désactivation'} du connecteur`);
    }
  };

  const handleAnalyze = async (selectedItems, question) => {
    if (selectedItems.length === 0 || !question.trim()) {
      setError('Veuillez sélectionner au moins un élément et poser une question');
      return;
    }

    setError('');
    setSuccess('');

    // Extraire les infos du compte sélectionné
    const selectedAccount = selectedItems[0];
    const accountMetadata = typeof selectedAccount.metadata === 'string'
      ? JSON.parse(selectedAccount.metadata)
      : selectedAccount.metadata;

    // Préparer les données pour le streaming
    // Note: item.id est le sourceId, item.libraryId est l'ID de base de données
    const streamingParams = {
      libraryItemIds: selectedItems.map(item => item.libraryId || item.databaseId || item.id),
      query: question.trim(),
      accountId: accountMetadata?.customerId || selectedAccount.sourceId?.replace(/-/g, '') || '',
      accountName: selectedAccount.sourceName || selectedAccount.name || 'Compte Google Ads',
      streamingMode: true,
      chatOpen: true  // Ouvrir le chat immédiatement
    };

    console.log('[ANALYZE] Streaming params:', streamingParams);

    // Stocker les params pour le ChatPanel - NE PAS changer de vue
    setAnalysisResult(streamingParams);
    // On reste sur la page Analyser, le ChatPanel s'ouvre via les props
  };

  // Page d'authentification MCP (Claude Desktop connector)
  if (currentView === 'mcp-auth' && mcpAuthParams) {
    return (
      <McpAuth
        mcpCallback={mcpAuthParams.mcpCallback}
        scope={mcpAuthParams.scope}
      />
    );
  }

  // Pages légales publiques (accessible sans connexion)
  if (currentView === 'legal') {
    return <LegalPage />;
  }

  // Centre d'aide (accessible sans connexion, avant le loading screen)
  if (currentView === 'help') {
    return <HelpCenter />;
  }

  // Page d'invitation d'équipe (accessible sans être connecté) - ancien système
  if (currentView === 'join-team' && invitationToken) {
    return (
      <JoinTeam
        token={token}
        invitationToken={invitationToken}
        onAccepted={() => {
          // Recharger l'app pour mettre à jour les données
          window.location.reload();
        }}
        onNeedLogin={() => {
          // Stocker le token et aller vers le login
          localStorage.setItem('pendingInvitationToken', invitationToken);
          setCurrentView('login');
        }}
      />
    );
  }

  // Page d'invitation d'organisation (accessible sans être connecté) - nouveau système
  if (currentView === 'join-organization' && orgInvitationToken) {
    return (
      <JoinOrganization
        token={token}
        invitationToken={orgInvitationToken}
        onAccepted={() => {
          // Nettoyer l'URL et recharger vers la page d'accueil
          window.location.href = window.location.pathname + '#home';
          window.location.reload();
        }}
        onNeedLogin={() => {
          // Stocker le token et aller vers le login
          localStorage.setItem('pendingOrgInvitationToken', orgInvitationToken);
          setCurrentView('login');
        }}
      />
    );
  }

  /**
   * Écran de chargement pendant la vérification du token (évite le flash du login), et
   * pendant le routage initial (évite le flash de l'accueil).
   *
   * La destination d'une session qui démarre dépend des connecteurs, qui arrivent d'un appel
   * réseau : sans attente, l'accueil s'affichait puis cédait la place à l'écran des connecteurs
   * une fois la réponse arrivée — un saut visible à chaque connexion. `connectorsLoaded` passe
   * à vrai même quand l'appel échoue (`refreshGlobalConnectors`), l'attente ne peut donc pas
   * rester bloquée.
   */
  if ((isVerifyingAuth || (isInitialLogin && !connectorsLoaded)) && token) {
    return (
      <div style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100vh',
        backgroundColor: '#f3efe7',
        color: '#1a1a1a'
      }}>
        <Loader className="spin" size={40} style={{ marginBottom: '16px', color: '#6b7280' }} />
        <span style={{ color: '#6b7280' }}>Chargement...</span>
      </div>
    );
  }

  // Interface non connectée
  if (!user) {
    return (
      <AuthStep
        handleGoogleLogin={handleGoogleLogin}
        loading={loading}
        error={error}
        onLoginSuccess={(userData, authToken) => {
          setUser(userData);
          setToken(authToken);
          localStorage.setItem('kokpeet_token', authToken);

          // Vérifier si une invitation d'équipe est en attente (ancien système)
          const pendingToken = localStorage.getItem('pendingInvitationToken');
          if (pendingToken) {
            setInvitationToken(pendingToken);
            setCurrentView('join-team');
            setIsInitialLogin(false); // Empêcher l'auto-routing
            return;
          }

          // Vérifier si une invitation d'organisation est en attente (nouveau système)
          const pendingOrgToken = localStorage.getItem('pendingOrgInvitationToken');
          if (pendingOrgToken) {
            setOrgInvitationToken(pendingOrgToken);
            setCurrentView('join-organization');
            setIsInitialLogin(false); // Empêcher l'auto-routing
          }
        }}
      />
    );
  }

  // Affichage de la maquette UX (route séparée, sans authentification requise)
  if (currentView === 'mockup') {
    return <LayoutMockup />;
  }

  /**
   * Le parcours d'installation, tant qu'il n'a pas été mené à son terme.
   *
   * ## Un verrou, pas un aiguillage
   *
   * La condition ne regarde **que** `user.onboardingTermineAt`, jamais la vue courante ni les
   * connecteurs. C'est ce qui le rend étanche : l'aiguillage précédent se désactivait dès
   * qu'une URL explicite était présente, si bien qu'un favori sur `#manager` sortait du
   * parcours. Ici, aucune vue n'y échappe.
   *
   * ## Pourquoi `=== null` et non `!`
   *
   * Au retour d'une connexion OAuth, `user` est l'objet réduit que le serveur a mis dans
   * l'URL : si le champ n'y était pas, il vaudrait `undefined`, et un `!` ferait apparaître le
   * parcours à **tout** le monde, le temps que `/api/auth/verify` réponde. `=== null` dit « on
   * sait qu'il est inachevé » ; `undefined` dit « on ne sait pas encore », et on n'affiche rien
   * sur une incertitude. Les deux projections concernées ont été complétées en conséquence
   * (`services/googleAuth.js`, `routes/microsoftAuth.js`).
   *
   * ## La seule échappée : les formules
   *
   * L'essai dure sept jours ; un parcours commencé puis laissé en plan peut donc l'enjamber.
   * Sans l'exception `pricing`, cette personne se retrouverait tenue dans un parcours qu'elle
   * ne veut plus finir, devant une application qu'elle n'a plus le droit d'utiliser et sans
   * pouvoir payer — un cul-de-sac dont même la déconnexion ne la sortirait pas. L'exception ne
   * s'ouvre que sur demande explicite (`#pricing`) : aucune redirection n'y mène d'elle-même,
   * le parcours reste donc obligatoire pour qui le suit.
   *
   * Rendu parmi les retours anticipés, donc sans en-tête ni barre latérale : un compte neuf n'a
   * aucune donnée, et aucun écran de la navigation n'aurait quoi que ce soit à montrer. Placé
   * après le retour `!user`, il a toujours un utilisateur connecté.
   */
  if (user && user.role !== 'CLIENT' && user.onboardingTermineAt === null && currentView !== 'pricing') {
    return (
      <ParcoursInitial
        user={user}
        token={token}
        onLogout={handleLogout}
        onTermine={async (compte) => {
          // Le parcours est clos côté serveur : il reste à le refléter en mémoire, sans quoi
          // le verrou ci-dessus rouvrirait le parcours au rendu suivant.
          setUser((precedent) => ({ ...precedent, onboardingTermineAt: new Date().toISOString() }));
          await refreshGlobalConnectors();
          fetchUserClients();
          fetchUserSites();

          // Trois sorties possibles, parce que les comptes ne se consultent pas tous au même
          // endroit : un onglet du tableau de bord pour les régies, une vue à part pour les
          // solutions de mesure, et l'accueil pour ce qui n'a pas d'écran dédié (Search
          // Console, Merchant Center).
          const onglet = compte?.type ? DASHBOARD_PAR_TYPE[compte.type] : null;
          if (onglet && compte?.sourceId) {
            navigateToDashboardWithAccount(onglet, compte.sourceId);
            return;
          }
          const vue = compte?.type ? ECRAN_PAR_TYPE[compte.type] : null;
          const destination = vue || 'home';
          setCurrentView(destination);
          window.history.replaceState({}, document.title, window.location.pathname + '#' + destination);
        }}
      />
    );
  }

  // Interface principale avec navigation
  return (
    <div className="App">
      {/* La visite guidée, en portail : elle doit couvrir l'écran entier, barre latérale
          comprise, et c'est précisément ce que le gabarit ne permet pas depuis l'intérieur. */}
      {(visiteProposee || visiteRelancee) && (
        <VisiteGuidee
          avecInvitation={!visiteRelancee}
          onTerminer={fermerVisiteGuidee}
          onPlusTard={fermerVisiteGuidee}
        />
      )}
      <style dangerouslySetInnerHTML={{ __html: `
        .product-title-container:hover .product-image-tooltip {
          display: block !important;
        }
        .product-image-tooltip {
          white-space: nowrap;
        }
        .edit-site-btn:hover .edit-icon {
          color: white !important;
        }
        .edit-site-btn:hover {
          background-color: #6c757d !important;
        }
      `}} />

      {/* Bandeau d'alerte : un connecteur dont l'accès ne répond plus. Le libellé vient de
          `messageConnecteursEnErreur` — jamais « token expiré », que cinq connecteurs à clé
          du parc n'ont pas. */}
      {expiredConnectors.length > 0 && !expiredTokensBannerDismissed && user && user.role !== 'CLIENT' && (
        <div style={{
          background: 'linear-gradient(90deg, #dc2626 0%, #b91c1c 100%)',
          color: 'white',
          padding: '12px 20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '12px',
          fontSize: '14px',
          fontWeight: '500',
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          width: '100vw',
          zIndex: 9999,
          boxShadow: '0 2px 8px rgba(0,0,0,0.2)'
        }}>
          <AlertCircle size={20} />
          <span>
            {messageConnecteursEnErreur}
            {' '}Les données ne peuvent pas être récupérées.
          </span>
          <button
            onClick={() => setCurrentViewWithHistory('connectors')}
            style={{
              background: 'white',
              color: '#dc2626',
              border: 'none',
              padding: '6px 16px',
              borderRadius: '6px',
              cursor: 'pointer',
              fontWeight: '600',
              fontSize: '13px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <Link size={14} /> Connecteurs
          </button>
          <button
            onClick={() => setExpiredTokensBannerDismissed(true)}
            style={{
              background: 'transparent',
              border: 'none',
              color: 'white',
              cursor: 'pointer',
              padding: '4px',
              position: 'absolute',
              right: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              opacity: 0.8
            }}
            title="Fermer"
          >
            ✕
          </button>
        </div>
      )}

      {/* Spacer pour compenser le bandeau fixe */}
      {expiredConnectors.length > 0 && !expiredTokensBannerDismissed && user && user.role !== 'CLIENT' && (
        <div style={{ height: '48px' }} />
      )}

      <header className="App-header">
        {/* En-tête avec navigation */}
        <div className="app-header">
          {/* Groupe gauche: logo + filtre client */}
          <div className="header-left-group">
            <div
              className="logo-mini"
              onClick={() => handleViewChange('home')}
              style={{ cursor: 'pointer' }}
            >
              <img src="/kokpit-logo.png" alt="Kokpit" style={{ height: '38px' }} />
            </div>

          </div>

          {/* Navigation */}
          <nav className="main-nav">
            {/* Filtre client pour les utilisateurs USER, ADMIN et CLIENT */}
            {(() => {
              // Pour les CLIENT: afficher seulement s'ils ont accès à plus d'1 client
              // Pour USER/ADMIN: afficher dès qu'il y a au moins 1 client
              const shouldShow = user && userClients.length > 0 && (
                (user.role === 'CLIENT' && userClients.length > 1) ||
                (user.role === 'USER' || user.role === 'ADMIN')
              );
              return shouldShow;
            })() && (
              <div className="client-filter-container">
              <button
                className="nav-btn client-filter-toggle"
                data-visite="filtre-client"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowClientDropdown(!showClientDropdown);
                }}
              >
                <Users size={20} />
                <span>
                  {selectedClientId
                    ? userClients.find(c => c.id === selectedClientId)?.name || 'Client sélectionné'
                    : 'Tous les clients'
                  }
                </span>
                <span className="dropdown-arrow">⌄</span>
              </button>

              {showClientDropdown && (
                <div
                  className="client-dropdown-menu"
                  style={{
                    position: 'absolute',
                    top: '100%',
                    right: '0',
                    background: '#FAF9F5',
                    border: '1px solid rgba(0, 0, 0, 0.1)',
                    borderRadius: '12px',
                    boxShadow: '0 10px 30px rgba(0,0,0,0.12)',
                    zIndex: '9999',
                    width: 'max-content',
                    minWidth: '320px',
                    maxWidth: '580px',
                    marginTop: '8px'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Barre de recherche */}
                  {userClients.length > 5 && (
                    <div style={{ padding: '12px 16px', borderBottom: '1px solid rgba(0, 0, 0, 0.08)' }}>
                      <input
                        type="text"
                        placeholder="Rechercher un client..."
                        value={clientSearchFilter}
                        onChange={(e) => setClientSearchFilter(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: '1px solid rgba(0, 0, 0, 0.1)',
                          borderRadius: '8px',
                          fontSize: '14px',
                          outline: 'none',
                          boxSizing: 'border-box',
                          background: 'white',
                          color: '#1a1a1a',
                          transition: 'border-color 0.2s ease'
                        }}
                        onFocus={(e) => e.target.style.borderColor = 'rgba(0, 0, 0, 0.25)'}
                        onBlur={(e) => e.target.style.borderColor = 'rgba(0, 0, 0, 0.1)'}
                        autoFocus
                      />
                    </div>
                  )}
                  {/* Liste scrollable en grille */}
                  <div style={{ maxHeight: '500px', overflowY: 'auto', padding: '8px' }}>
                    {!clientSearchFilter && (
                      <button
                        onClick={() => {
                          setSelectedClientIdWithHistory(null);
                          setShowClientDropdown(false);
                          setClientSearchFilter('');
                        }}
                        style={{
                          width: '100%',
                          padding: '10px 14px',
                          border: 'none',
                          background: !selectedClientId ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                          textAlign: 'left',
                          cursor: 'pointer',
                          borderRadius: '8px',
                          marginBottom: '8px',
                          fontSize: '14px',
                          fontWeight: '500',
                          color: '#1a1a1a',
                          transition: 'all 0.2s ease'
                        }}
                        onMouseOver={(e) => {
                          if (selectedClientId) e.target.style.background = 'rgba(0, 0, 0, 0.05)';
                        }}
                        onMouseOut={(e) => {
                          e.target.style.background = !selectedClientId ? 'rgba(0, 0, 0, 0.08)' : 'transparent';
                        }}
                      >
                        Tous les clients
                      </button>
                    )}
                    {(() => {
                      const filteredClients = userClients
                        .filter(client =>
                          !clientSearchFilter ||
                          client.name.toLowerCase().includes(clientSearchFilter.toLowerCase())
                        )
                        .sort((a, b) => a.name.localeCompare(b.name, 'fr'));
                      const numColumns = filteredClients.length > 12 ? 3 : 2;
                      return (
                        <div style={{
                          display: 'grid',
                          gridTemplateColumns: `repeat(${numColumns}, 1fr)`,
                          gap: '4px'
                        }}>
                          {filteredClients.map(client => (
                        <button
                          key={client.id}
                          onClick={() => {
                            setSelectedClientIdWithHistory(client.id);
                            setShowClientDropdown(false);
                            setClientSearchFilter('');
                          }}
                          style={{
                            padding: '10px 12px',
                            border: 'none',
                            background: selectedClientId === client.id ? 'rgba(0, 0, 0, 0.08)' : 'transparent',
                            textAlign: 'left',
                            cursor: 'pointer',
                            borderRadius: '8px',
                            fontSize: '14px',
                            color: '#1a1a1a',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            transition: 'all 0.2s ease'
                          }}
                          onMouseOver={(e) => {
                            if (selectedClientId !== client.id) e.target.style.background = 'rgba(0, 0, 0, 0.05)';
                          }}
                          onMouseOut={(e) => {
                            e.target.style.background = selectedClientId === client.id ? 'rgba(0, 0, 0, 0.08)' : 'transparent';
                          }}
                          title={client.name}
                        >
                          {client.name}
                        </button>
                          ))}
                        </div>
                      );
                    })()}
                    {clientSearchFilter && userClients.filter(c => c.name.toLowerCase().includes(clientSearchFilter.toLowerCase())).length === 0 && (
                      <div style={{ padding: '16px', color: '#6b7280', fontSize: '14px', textAlign: 'center' }}>
                        Aucun client trouvé
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
            )}

            {/* Bouton Radar - le feed des actions à entreprendre, avant les dashboards :
                ce qu'il y a à faire se lit avant ce qui s'est passé */}
            {radarOuvert && (
              <button
                onClick={() => handleViewChange('radar')}
                className={`nav-btn ${currentView === 'radar' ? 'active' : ''}`}
                title="Les actions à entreprendre sur vos comptes"
              >
                <RadarIcon size={20} />
                Radar
              </button>
            )}

            {/*
              Bouton Dashboard — affiche la sélection si plusieurs connecteurs, sinon va
              directement au seul disponible.

              ⚠️ **`availableSources` ne connaît que les régies publicitaires.**
              `/api/synthese/available-sources` ne compte que Google Ads, Microsoft, Meta,
              Pinterest, TikTok, ChatGPT Ads et Awin. Un client équipé de GA4, Matomo ou Piano
              mais d'aucune régie rendait donc `false` partout, et le bouton disparaissait — alors
              que la page de sélection, elle, sait très bien présenter ces trois dashboards
              (même condition qu'ici, plus bas dans ce fichier) et que la barre latérale affichait
              « Analytics » juste à côté. Le dashboard était devenu inatteignable pour ces clients.

              Les trois listes analytics sont déjà filtrées par client dans ce composant : on les
              ajoute à la condition plutôt que d'élargir la route, qui sert aussi à d'autres
              écrans où « source » veut bien dire « régie ».
            */}
            {(() => {
              /*
               * ⚠️ **Les trois listes analytics doivent passer le droit, elles aussi.**
               * `availableSources` est filtré par le serveur, mais ces trois-là viennent du
               * navigateur : sans le droit, le bouton restait là une fois tous les dashboards
               * fermés, et menait à un écran de choix qui n'avait rien à proposer.
               */
              const sourcesPublicitaires = Object.entries(availableSources).filter(([_, actif]) => actif);
              const sourcesAnalytics = (filteredGA4Properties.length > 0 && plateformeOuverte('ga4-dashboard') ? 1 : 0)
                + (filteredMatomoSites.length > 0 && plateformeOuverte('matomo-dashboard') ? 1 : 0)
                + (filteredPianoSites.length > 0 && plateformeOuverte('piano-dashboard') ? 1 : 0);
              const total = sourcesPublicitaires.length + sourcesAnalytics;

              /*
               * ⚠️ **Le même verdict que le garde, et non un calcul jumeau.** Le bouton
               * décidait seul de sa présence, et c'est ainsi qu'il a survécu à la fermeture
               * de tous les dashboards : deux calculs d'une même question divergent au
               * premier oubli. `total` reste, parce qu'il décide en plus du raccourci quand
               * une seule source existe — ce que le verdict partagé ne dit pas.
               */
              return auMoinsUnDashboardOuvert && total > 0 && (
              <button
                onClick={() => {
                  // Un raccourci vers l'unique dashboard n'en est un que s'il est vraiment seul :
                  // une régie unique **plus** des propriétés analytics appelle la sélection.
                  if (total === 1 && sourcesPublicitaires.length === 1) {
                    setCurrentDashboardType(sourcesPublicitaires[0][0]);
                    handleViewChange('dashboard');
                  } else {
                    setCurrentDashboardType('selection');
                    handleViewChange('dashboard');
                  }
                }}
                className={`nav-btn ${currentView === 'dashboard' ? 'active' : ''}`}
                title="Aller au dashboard"
              >
                <BarChart3 size={20} />
                Dashboard
              </button>
              );
            })()}

            {/* Bouton Tools - admins (avec sources), membres d'organisations, et accès
                clients à qui au moins un outil a été ouvert en lecture seule */}
            {user && (hasOrganization || outilsDuCatalogue(clientTools).length > 0 || (user.role === 'ADMIN' && Object.values(availableSources).some(source => source))) && (
              <button
                onClick={() => handleViewChange('tools')}
                className={`nav-btn ${currentView === 'tools' ? 'active' : ''}`}
                data-visite="outils"
              >
                <Wrench size={20} />
                Outils
              </button>
            )}

            {/* Bouton MCP - abonnés, et accès clients à qui leur agence l'a ouvert */}
            {mcpOuvert && (
              <button
                onClick={() => handleViewChange('mcp')}
                className={`nav-btn ${currentView === 'mcp' ? 'active' : ''}`}
                data-visite="mcp"
              >
                <Cpu size={20} />
                MCP
              </button>
            )}

            {/* Bouton Analyse — masqué dans le header.
                La vue `analyze` reste montée et joignable par son URL : c'est l'entrée du
                header qu'on retire, pas la fonctionnalité. Remettre `true &&` à la place de
                `false &&` la fait réapparaître, avec ses conditions d'accès intactes —
                abonnés OWNER, pas les membres. */}
            {false && globalConnectors.length > 0 && user && (user.role === 'ADMIN' || (userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan))) && (userOrganizations.length === 0 || userOrganizations.some(org => org.role === 'OWNER')) && (
              <button
                onClick={() => handleViewChange('analyze')}
                className={`nav-btn ${currentView === 'analyze' ? 'active' : ''}`}
              >
                <Bot size={20} />
                Analyser
              </button>
            )}

            {/* Bouton Admin - visible seulement pour les admins */}
            {user && user.role === 'ADMIN' && (
              <button
                onClick={() => handleViewChange('admin')}
                className={`nav-btn ${currentView === 'admin' ? 'active' : ''} admin-nav-btn`}
                style={{
                  backgroundColor: '#1a1a1a',
                  color: 'white',
                  borderRadius: '8px',
                  padding: '8px 16px'
                }}
              >
                <Users size={20} />
                Admin
              </button>
            )}
          </nav>

          {/* Groupe droite: organisation + profil utilisateur */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {/* Alertes de trafic : dans l'en-tête pour être visible quelle que soit la vue.
                Réservé aux comptes d'agence — un client n'a rien à régler ici. */}
            {user && user.role !== 'CLIENT' && (
              <AlertNotificationsBell
                token={token}
                onOpenSettings={() => handleViewChange('notifications')}
                /**
                 * Une alerte mène au compte qu'elle signale.
                 *
                 * La navigation passe par `navigateToDashboardWithAccount`, celle-là même
                 * qu'emploie la page d'accueil : elle pose les clés de `localStorage` que le
                 * dashboard relit pour présélectionner le compte, et écrit l'URL. La
                 * réimplémenter ici aurait donné deux chemins d'ouverture qui divergent.
                 *
                 * L'identifiant transmis est le `sourceId` **tel qu'il est écrit dans la
                 * bibliothèque**, tirets compris : c'est sous cette forme que le dashboard
                 * retrouve son compte, là où l'alerte, elle, ne stocke que la forme normalisée.
                 */
                onOuvrirCompte={(dashboard, cible) => {
                  // Sans dashboard, c'est une alerte d'accès : la bibliothèque est le seul
                  // endroit où l'on répare une connexion, et le dashboard serait vide.
                  if (!dashboard) { handleViewChange('library'); return; }
                  navigateToDashboardWithAccount(dashboard, cible.sourceId);
                }}
              />
            )}

            {/* Sélecteur d'organisation */}
            {user && (user.role === 'ADMIN' || (user.role !== 'CLIENT' && userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan))) && (
              <OrganizationSelector
                token={token}
                activeOrgId={activeOrganizationId}
                onSelectOrg={setActiveOrganizationId}
                onCreateOrg={() => setCurrentViewWithHistory('organization')}
              />
            )}

            {/* Demande du nom — en portail, donc posée ici sans contrainte de mise en page. */}
            <NomProfilModal
              token={token}
              user={user}
              ouvert={Boolean(demandeNom)}
              premiereFois={demandeNom?.premiereFois || false}
              onClose={() => setDemandeNom(null)}
              // L'utilisateur n'est pas conservé localement : il est relu à chaque démarrage
              // par `/api/auth/verify`. Rafraîchir l'état en mémoire suffit donc, le nom étant
              // désormais en base.
              onEnregistre={(maj) => setUser(precedent => ({ ...precedent, ...maj }))}
            />

            {/* Profil utilisateur */}
            <div className="user-profile" style={{ position: 'relative' }}>
            {/* Un accès client créé par lien d'invitation n'a ni photo ni nom : il n'est jamais
                passé par Google. L'`<img>` affichait alors une image cassée, et le bouton du
                menu se réduisait à son chevron. On se rabat sur l'initiale et sur la partie
                locale de l'adresse — c'est ce que la personne reconnaît d'elle-même. */}
            {user.picture ? (
              <img src={user.picture} alt={user.name || user.email} className="user-avatar" />
            ) : (
              <div className="user-avatar-placeholder" aria-hidden="true">
                {(user.name || user.email || '?')[0].toUpperCase()}
              </div>
            )}
            <button 
              className="user-dropdown-toggle"
              onClick={(e) => {
                e.stopPropagation();
                setShowUserMenu(!showUserMenu);
              }}
            >
              <span className="user-name">{user.name || (user.email || '').split('@')[0]}</span>
              <span className="dropdown-arrow">⌄</span>
            </button>
            
            {/* Menu déroulant utilisateur */}
            {showUserMenu && (
              <div
                className="user-dropdown-menu"
                onClick={(e) => e.stopPropagation()}
              >
                {/* Modifier son nom : la seule voie pour un accès client ouvert par lien, qui
                    n'est jamais passé par Google et arrive donc sans nom.

                    **Jamais proposée à un compte Google.** Sa connexion réécrit `name` depuis
                    le profil Google à chaque passage (`services/googleAuth.js`) : une saisie
                    manuelle serait effacée dès la fois suivante, sans que rien ne l'explique.
                    Le nom s'y change chez Google, pas ici. */}
                {user && !user.googleId && (
                  <button
                    onClick={() => {
                      setDemandeNom({ premiereFois: false });
                      setShowUserMenu(false);
                    }}
                    className="dropdown-item"
                  >
                    <UserIcon size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    {user?.name ? 'Modifier mon nom' : 'Renseigner mon nom'}
                  </button>
                )}

                {/* Abonnement - visible uniquement aux OWNER d'organisation ou utilisateurs sans organisation */}
                {user && user.role !== 'CLIENT' && (userOrganizations.length === 0 || userOrganizations.some(org => org.role === 'OWNER')) && (
                  <button
                    onClick={() => {
                      setCurrentViewWithHistory('pricing');
                      setShowUserMenu(false);
                    }}
                    className="dropdown-item"
                  >
                    <CreditCard size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Abonnement
                  </button>
                )}
                {/* Organisation - accessible aux abonnés et ADMIN, uniquement si des connecteurs existent */}
                {globalConnectors.length > 0 && user && (user.role === 'ADMIN' || (user.role !== 'CLIENT' && userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan))) && (
                  <button
                    onClick={() => {
                      setCurrentViewWithHistory('organization');
                      setShowUserMenu(false);
                    }}
                    className="dropdown-item"
                  >
                    <Building size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Organisation
                  </button>
                )}
                {isNotClientUser && (
                  <button
                    onClick={() => {
                      setCurrentViewWithHistory('connectors');
                      setShowUserMenu(false);
                    }}
                    className="dropdown-item"
                  >
                    <Plug size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Connecteurs
                  </button>
                )}
                {/* Statistiques - accessible aux abonnés et ADMIN avec une organisation */}
                {globalConnectors.length > 0 && user && (user.role === 'ADMIN' || (user.role !== 'CLIENT' && userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan) && userOrganizations.length > 0)) && (
                  <button
                    onClick={() => {
                      setCurrentViewWithHistory('statistics');
                      setShowUserMenu(false);
                    }}
                    className="dropdown-item"
                  >
                    <BarChart3 size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Statistiques
                  </button>
                )}
                {/* Notifications - uniquement si des connecteurs existent */}
                {globalConnectors.length > 0 && user && user.role !== 'CLIENT' && (
                  <button
                    onClick={() => {
                      setCurrentViewWithHistory('notifications');
                      setShowUserMenu(false);
                    }}
                    className="dropdown-item"
                  >
                    <Bell size={16} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                    Notifications
                  </button>
                )}
                <button
                  onClick={() => {
                    setShowUserMenu(false);
                    handleLogout();
                  }}
                  className="dropdown-item logout-item"
                >
                  <LogOut size={16} />
                  Déconnexion
                </button>
              </div>
            )}
          </div>
          </div>
        </div>

        {/* Messages d'erreur et de succès */}
        {error && (
          <div className="error-message">
            <AlertCircle size={16} />
            {error}
          </div>
        )}
        {success && (
          <div className="success-message">
            <CheckCircle size={16} />
            {success}
          </div>
        )}

        {/* Layout avec sidebar */}
        <div className="app-layout-with-sidebar">
          {/* Sidebar gauche. Absente du dashboard consolidé : ce tableau se lit en largeur,
              et ses deux seuls réglages — période et propriété — sont posés au-dessus de
              lui. `main-content` étant en `flex: 1`, la place est reprise d'elle-même. */}
          {currentView !== 'consolide-dashboard' && (
          <aside className={`app-sidebar ${sidebarCollapsed || analyseOuverte ? 'collapsed' : ''}`}>
            {/* Bouton toggle sidebar. L'icône suit ce que l'on voit — repli imposé par le
                panneau d'analyse compris —, et non la seule préférence : une barre repliée
                sous un chevron « replier » se lit mal. Le clic, lui, ne touche que la
                préférence. */}
            <button
              className="sidebar-toggle-btn"
              onClick={() => {
                setSidebarCollapsed(!sidebarCollapsed);
                setSidebarManuallyToggled(true);
              }}
              title={sidebarCollapsed || analyseOuverte ? 'Agrandir la sidebar' : 'Réduire la sidebar'}
            >
              {sidebarCollapsed || analyseOuverte ? <PanelLeft size={18} /> : <PanelLeftClose size={18} />}
            </button>
            {/* Section Comptes - masquée car redondante avec la sélection centrée */}

            {/* Section Période - avec menu au clic */}
            {sidebarPorteLeContexte && currentView !== 'radar' && (
              <div ref={periodMenuRef} className={`sidebar-tool-category ${isPeriodMenuOpen ? 'open' : ''}`}>
                <div
                  className="sidebar-section-title"
                  data-visite="periode"
                  onClick={openPeriodMenu}
                  style={{ cursor: 'pointer' }}
                >
                  <Calendar size={16} />
                  <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '2px' }}>
                    <span>{getPeriodLabel()}</span>
                    {getComparisonLabel() && (
                      <span style={{ fontSize: '0.75rem', color: '#f97316', fontWeight: 500 }}>{getComparisonLabel()}</span>
                    )}
                  </div>
                </div>
                <div className="sidebar-tool-menu sidebar-period-menu">
                  <div style={{ display: 'flex', gap: '16px' }}>
                    {/* Colonne gauche - Périodes prédéfinies + Comparaison */}
                    <div style={{ flex: '0 0 200px', borderRight: '1px solid #e5e7eb', paddingRight: '16px', display: 'flex', flexDirection: 'column' }}>
                      <div style={{ flex: 1 }}>
                        {[
                          { value: 'CUSTOM', label: 'Personnalisée' },
                          { value: 'TODAY', label: 'Aujourd\'hui' },
                          { value: 'YESTERDAY', label: 'Hier' },
                          { value: 'LAST_7_DAYS', label: '7 derniers jours' },
                          { value: 'LAST_14_DAYS', label: '14 derniers jours' },
                          { value: 'LAST_30_DAYS', label: '30 derniers jours' },
                          { value: 'THIS_MONTH', label: 'Ce mois' },
                          { value: 'LAST_MONTH', label: 'Mois dernier' },
                          { value: 'LAST_90_DAYS', label: '90 derniers jours' },
                          { value: 'THIS_YEAR', label: 'Année en cours' },
                          { value: 'LAST_YEAR', label: 'Année dernière' },
                        ].map(period => (
                          <button
                            key={period.value}
                            className={`sidebar-tool-menu-item ${(tempSelectedPeriod || selectedPeriod) === period.value ? 'active' : ''}`}
                            onClick={() => {
                              if (period.value === 'CUSTOM') {
                                // Pour Personnalisée, juste sélectionner sans changer les dates
                                setTempSelectedPeriod('CUSTOM');
                              } else {
                                const { startDate, endDate } = getPeriodDates(period.value);
                                setTempSelectedPeriod(period.value);
                                setTempCustomStartDate(startDate);
                                setTempCustomEndDate(endDate);
                                // Recalculer les dates de comparaison si actif
                                if (tempCompareEnabled) {
                                  if (tempComparisonType !== 'custom') {
                                    const compDates = calculateComparisonDates(startDate, endDate, tempComparisonType);
                                    setTempComparisonStartDate(compDates.startDate);
                                    setTempComparisonEndDate(compDates.endDate);
                                  } else {
                                    // Mode custom: vérifier que la date de fin comparaison reste < nouvelle date début N
                                    const newMainStart = new Date(startDate);
                                    if (tempComparisonEndDate && new Date(tempComparisonEndDate) >= newMainStart) {
                                      const compDates = calculateComparisonDates(startDate, endDate, 'previous_period');
                                      setTempComparisonStartDate(compDates.startDate);
                                      setTempComparisonEndDate(compDates.endDate);
                                      setTempComparisonType('previous_period');
                                    }
                                  }
                                }
                              }
                            }}
                            style={{ padding: '8px 12px', fontSize: '0.85rem' }}
                          >
                            <span>{period.label}</span>
                          </button>
                        ))}
                      </div>
                      {/* Séparateur et Toggle Comparer */}
                      <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '12px', marginTop: '12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '8px' }}>
                          <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>Comparer</span>
                          <label className="toggle-switch" style={{ transform: 'scale(0.8)' }}>
                            <input
                              type="checkbox"
                              checked={tempCompareEnabled}
                              onChange={(e) => {
                                setTempCompareEnabled(e.target.checked);
                                if (e.target.checked && tempComparisonType !== 'custom') {
                                  // Calculer les dates de comparaison
                                  const start = tempCustomStartDate || customStartDate || getPeriodDates(tempSelectedPeriod || selectedPeriod).startDate;
                                  const end = tempCustomEndDate || customEndDate || getPeriodDates(tempSelectedPeriod || selectedPeriod).endDate;
                                  const compDates = calculateComparisonDates(start, end, tempComparisonType);
                                  setTempComparisonStartDate(compDates.startDate);
                                  setTempComparisonEndDate(compDates.endDate);
                                }
                              }}
                            />
                            <span className="toggle-slider"></span>
                          </label>
                        </div>
                        {/* Options de comparaison */}
                        {tempCompareEnabled && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                            {[
                              { value: 'previous_period', label: 'Période précédente' },
                              { value: 'previous_year', label: 'Année précédente' },
                              { value: 'custom', label: 'Personnalisée' },
                            ].map(option => (
                              <label
                                key={option.value}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '6px 8px',
                                  borderRadius: '4px',
                                  cursor: 'pointer',
                                  fontSize: '13px',
                                  color: tempComparisonType === option.value ? '#667eea' : '#4b5563',
                                  background: tempComparisonType === option.value ? 'rgba(102, 126, 234, 0.1)' : 'transparent'
                                }}
                                onMouseEnter={(e) => { if (tempComparisonType !== option.value) e.currentTarget.style.background = '#f3f4f6'; }}
                                onMouseLeave={(e) => { if (tempComparisonType !== option.value) e.currentTarget.style.background = 'transparent'; }}
                              >
                                <input
                                  type="radio"
                                  name="comparisonType"
                                  checked={tempComparisonType === option.value}
                                  onChange={() => {
                                    setTempComparisonType(option.value);
                                    if (option.value !== 'custom') {
                                      const start = tempCustomStartDate || customStartDate || getPeriodDates(tempSelectedPeriod || selectedPeriod).startDate;
                                      const end = tempCustomEndDate || customEndDate || getPeriodDates(tempSelectedPeriod || selectedPeriod).endDate;
                                      const compDates = calculateComparisonDates(start, end, option.value);
                                      setTempComparisonStartDate(compDates.startDate);
                                      setTempComparisonEndDate(compDates.endDate);
                                    }
                                  }}
                                  style={{ margin: 0 }}
                                />
                                <span>{option.label}</span>
                              </label>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                    {/* Colonne droite - Calendrier */}
                    <div style={{ flex: 1 }}>
                      {/* Période principale */}
                      <div style={{ marginBottom: tempCompareEnabled ? '12px' : '12px' }}>
                        <div style={{ display: 'flex', gap: '12px' }}>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '11px', color: '#667eea', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Date de début</label>
                            <input
                              type="date"
                              value={tempCustomStartDate || customStartDate}
                              onFocus={() => setActiveCalendarField('mainStart')}
                              onChange={(e) => {
                                setTempCustomStartDate(e.target.value);
                                setTempSelectedPeriod('CUSTOM'); // Passer en mode personnalisé
                                // Recalculer les dates de comparaison si actif
                                if (tempCompareEnabled) {
                                  const end = tempCustomEndDate || customEndDate;
                                  if (tempComparisonType !== 'custom') {
                                    if (end) {
                                      const compDates = calculateComparisonDates(e.target.value, end, tempComparisonType);
                                      setTempComparisonStartDate(compDates.startDate);
                                      setTempComparisonEndDate(compDates.endDate);
                                    }
                                  } else {
                                    // Mode custom: vérifier que la date de fin comparaison reste < nouvelle date début N
                                    const newMainStart = new Date(e.target.value);
                                    if (tempComparisonEndDate && new Date(tempComparisonEndDate) >= newMainStart) {
                                      // Recalculer en mode previous_period par défaut
                                      if (end) {
                                        const compDates = calculateComparisonDates(e.target.value, end, 'previous_period');
                                        setTempComparisonStartDate(compDates.startDate);
                                        setTempComparisonEndDate(compDates.endDate);
                                        setTempComparisonType('previous_period');
                                      }
                                    }
                                  }
                                }
                              }}
                              max={tempCustomEndDate || customEndDate || aujourdhuiIso()}
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: activeCalendarField === 'mainStart' ? '2px solid #667eea' : '1px solid #667eea',
                                fontSize: '13px',
                                boxSizing: 'border-box',
                                outline: 'none'
                              }}
                            />
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', paddingTop: '18px', color: '#9ca3af' }}>-</div>
                          <div style={{ flex: 1 }}>
                            <label style={{ fontSize: '11px', color: '#667eea', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Date de fin</label>
                            <input
                              type="date"
                              value={tempCustomEndDate || customEndDate}
                              onFocus={() => setActiveCalendarField('mainEnd')}
                              onChange={(e) => {
                                setTempCustomEndDate(e.target.value);
                                setTempSelectedPeriod('CUSTOM'); // Passer en mode personnalisé
                                // Recalculer les dates de comparaison si actif
                                if (tempCompareEnabled && tempComparisonType !== 'custom') {
                                  const start = tempCustomStartDate || customStartDate;
                                  if (start) {
                                    const compDates = calculateComparisonDates(start, e.target.value, tempComparisonType);
                                    setTempComparisonStartDate(compDates.startDate);
                                    setTempComparisonEndDate(compDates.endDate);
                                  }
                                }
                              }}
                              min={tempCustomStartDate || customStartDate}
                              max={aujourdhuiIso()}
                              style={{
                                width: '100%',
                                padding: '8px',
                                borderRadius: '6px',
                                border: activeCalendarField === 'mainEnd' ? '2px solid #667eea' : '1px solid #667eea',
                                fontSize: '13px',
                                boxSizing: 'border-box',
                                outline: 'none'
                              }}
                            />
                          </div>
                        </div>
                      </div>
                      {/* Section Comparer à (visible si toggle actif) */}
                      {tempCompareEnabled && (
                        <div style={{ marginBottom: '12px', paddingTop: '8px', borderTop: '1px dashed #e5e7eb' }}>
                          <div style={{ fontSize: '12px', color: '#f97316', fontWeight: '500', marginBottom: '8px' }}>Comparer</div>
                          <div style={{ display: 'flex', gap: '12px' }}>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: '11px', color: '#f97316', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Date de début</label>
                              <input
                                type="date"
                                value={tempComparisonStartDate}
                                onFocus={() => setActiveCalendarField('compStart')}
                                onChange={(e) => {
                                  setTempComparisonStartDate(e.target.value);
                                  setTempComparisonType('custom');
                                }}
                                max={(() => {
                                  // La date de début N-1 doit être <= date de fin N-1, et < date de début N
                                  const mainStart = tempCustomStartDate || customStartDate;
                                  const maxFromMainStart = mainStart ? (() => {
                                    const dayBefore = dateLocale(mainStart);
                                    dayBefore.setDate(dayBefore.getDate() - 1);
                                    return jourIso(dayBefore);
                                  })() : null;
                                  const maxFromCompEnd = tempComparisonEndDate || null;
                                  if (maxFromMainStart && maxFromCompEnd) {
                                    return maxFromMainStart < maxFromCompEnd ? maxFromMainStart : maxFromCompEnd;
                                  }
                                  return maxFromCompEnd || maxFromMainStart || aujourdhuiIso();
                                })()}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '6px',
                                  border: activeCalendarField === 'compStart' ? '2px solid #f97316' : '1px solid #f97316',
                                  fontSize: '13px',
                                  boxSizing: 'border-box',
                                  outline: 'none'
                                }}
                              />
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', paddingTop: '18px', color: '#9ca3af' }}>-</div>
                            <div style={{ flex: 1 }}>
                              <label style={{ fontSize: '11px', color: '#f97316', display: 'block', marginBottom: '4px', fontWeight: '500' }}>Date de fin</label>
                              <input
                                type="date"
                                value={tempComparisonEndDate}
                                onFocus={() => setActiveCalendarField('compEnd')}
                                onChange={(e) => {
                                  setTempComparisonEndDate(e.target.value);
                                  setTempComparisonType('custom');
                                }}
                                min={tempComparisonStartDate}
                                max={(() => {
                                  // La date de fin N-1 doit être strictement inférieure à la date de début N
                                  const mainStart = tempCustomStartDate || customStartDate;
                                  if (mainStart) {
                                    const dayBefore = dateLocale(mainStart);
                                    dayBefore.setDate(dayBefore.getDate() - 1);
                                    return jourIso(dayBefore);
                                  }
                                  return aujourdhuiIso();
                                })()}
                                style={{
                                  width: '100%',
                                  padding: '8px',
                                  borderRadius: '6px',
                                  border: activeCalendarField === 'compEnd' ? '2px solid #f97316' : '1px solid #f97316',
                                  fontSize: '13px',
                                  boxSizing: 'border-box',
                                  outline: 'none'
                                }}
                              />
                            </div>
                          </div>
                        </div>
                      )}
                      {/* Calendrier scrollable avec plusieurs mois */}
                      <div
                        ref={(el) => { if (el) el.scrollTop = el.scrollHeight; }}
                        style={{ maxHeight: '280px', overflowY: 'auto', paddingRight: '8px' }}
                      >
                        {(() => {
                          const today = new Date();
                          const monthNames = ['JANV.', 'FÉVR.', 'MARS', 'AVR.', 'MAI', 'JUIN', 'JUIL.', 'AOÛT', 'SEPT.', 'OCT.', 'NOV.', 'DÉC.'];
                          const dayNames = ['L', 'M', 'M', 'J', 'V', 'S', 'D'];

                          // Générer les mois (36 derniers mois)
                          const months = [];
                          for (let offset = -35; offset <= 0; offset++) {
                            const date = new Date(today.getFullYear(), today.getMonth() + offset, 1);
                            months.push({ month: date.getMonth(), year: date.getFullYear() });
                          }

                          return months.map(({ month, year }, idx) => {
                            const firstDay = new Date(year, month, 1).getDay();
                            const adjustedFirstDay = firstDay === 0 ? 6 : firstDay - 1; // Lundi = 0
                            const daysInMonth = new Date(year, month + 1, 0).getDate();

                            const days = [];
                            // Jours vides avant le premier jour
                            for (let i = 0; i < adjustedFirstDay; i++) {
                              days.push(<div key={`empty-${idx}-${i}`} style={{ width: '24px', height: '24px' }} />);
                            }
                            // Jours du mois
                            for (let day = 1; day <= daysInMonth; day++) {
                              const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                              const isToday = day === today.getDate() && month === today.getMonth() && year === today.getFullYear();
                              const isFuture = new Date(year, month, day) > today;

                              // Période principale (bleu)
                              const displayStart = tempCustomStartDate || customStartDate;
                              const displayEnd = tempCustomEndDate || customEndDate;
                              const isSelected = dateStr === displayStart || dateStr === displayEnd;
                              const isInRange = displayStart && displayEnd && dateStr >= displayStart && dateStr <= displayEnd;

                              // Période de comparaison (orange)
                              const compStart = tempCompareEnabled ? tempComparisonStartDate : '';
                              const compEnd = tempCompareEnabled ? tempComparisonEndDate : '';
                              const isCompSelected = compStart && compEnd && (dateStr === compStart || dateStr === compEnd);
                              const isInCompRange = compStart && compEnd && dateStr >= compStart && dateStr <= compEnd;

                              // Déterminer le style (priorité : principale > comparaison)
                              let bgColor = 'transparent';
                              let textColor = isToday ? '#667eea' : '#374151';
                              let borderRadius = '4px';

                              if (isSelected) {
                                bgColor = '#667eea';
                                textColor = 'white';
                                borderRadius = '50%';
                              } else if (isCompSelected) {
                                bgColor = '#f97316';
                                textColor = 'white';
                                borderRadius = '50%';
                              } else if (isInRange && isInCompRange) {
                                // Les deux périodes se chevauchent
                                bgColor = 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 50%, rgba(249, 115, 22, 0.2) 50%)';
                              } else if (isInRange) {
                                bgColor = 'rgba(102, 126, 234, 0.15)';
                              } else if (isInCompRange) {
                                bgColor = 'rgba(249, 115, 22, 0.15)';
                              }

                              if (isFuture) textColor = '#d1d5db';

                              days.push(
                                <button
                                  key={`day-${idx}-${day}`}
                                  disabled={isFuture}
                                  onClick={() => {
                                    if (isFuture) return;

                                    // Gérer la sélection selon le champ actif
                                    if (activeCalendarField === 'compStart') {
                                      // Sélection date début comparaison
                                      const mainStart = tempCustomStartDate || customStartDate;
                                      // Vérifier que la date est < date début N
                                      if (mainStart && dateStr >= mainStart) return;
                                      setTempComparisonStartDate(dateStr);
                                      setTempComparisonType('custom');
                                      // Si pas de date fin comparaison ou date fin < nouvelle date début, ajuster
                                      if (!tempComparisonEndDate || tempComparisonEndDate < dateStr) {
                                        setTempComparisonEndDate(dateStr);
                                      }
                                      setActiveCalendarField('compEnd'); // Passer à la sélection de fin
                                      return;
                                    }

                                    if (activeCalendarField === 'compEnd') {
                                      // Sélection date fin comparaison
                                      const mainStart = tempCustomStartDate || customStartDate;
                                      // Vérifier que la date est < date début N
                                      if (mainStart && dateStr >= mainStart) return;
                                      // Vérifier que la date est >= date début comparaison
                                      if (tempComparisonStartDate && dateStr < tempComparisonStartDate) {
                                        // Inverser: la nouvelle date devient le début
                                        setTempComparisonStartDate(dateStr);
                                        setTempComparisonEndDate(tempComparisonStartDate);
                                      } else {
                                        setTempComparisonEndDate(dateStr);
                                      }
                                      setTempComparisonType('custom');
                                      setActiveCalendarField(null); // Fin de sélection
                                      return;
                                    }

                                    if (activeCalendarField === 'mainStart') {
                                      // Sélection date début période principale
                                      setTempCustomStartDate(dateStr);
                                      setTempSelectedPeriod('CUSTOM');
                                      // Si pas de date fin ou date fin < nouvelle date début, ajuster
                                      if (!tempCustomEndDate || tempCustomEndDate < dateStr) {
                                        setTempCustomEndDate(dateStr);
                                      }
                                      // Recalculer comparaison si nécessaire
                                      if (tempCompareEnabled && tempComparisonType !== 'custom') {
                                        const end = tempCustomEndDate && tempCustomEndDate >= dateStr ? tempCustomEndDate : dateStr;
                                        const compDates = calculateComparisonDates(dateStr, end, tempComparisonType);
                                        setTempComparisonStartDate(compDates.startDate);
                                        setTempComparisonEndDate(compDates.endDate);
                                      }
                                      setActiveCalendarField('mainEnd'); // Passer à la sélection de fin
                                      return;
                                    }

                                    if (activeCalendarField === 'mainEnd') {
                                      // Sélection date fin période principale
                                      if (tempCustomStartDate && dateStr < tempCustomStartDate) {
                                        // Inverser: la nouvelle date devient le début
                                        setTempCustomStartDate(dateStr);
                                        setTempCustomEndDate(tempCustomStartDate);
                                      } else {
                                        setTempCustomEndDate(dateStr);
                                      }
                                      setTempSelectedPeriod('CUSTOM');
                                      // Recalculer comparaison si nécessaire
                                      if (tempCompareEnabled && tempComparisonType !== 'custom') {
                                        const finalStart = tempCustomStartDate && dateStr < tempCustomStartDate ? dateStr : tempCustomStartDate;
                                        const finalEnd = tempCustomStartDate && dateStr < tempCustomStartDate ? tempCustomStartDate : dateStr;
                                        const compDates = calculateComparisonDates(finalStart, finalEnd, tempComparisonType);
                                        setTempComparisonStartDate(compDates.startDate);
                                        setTempComparisonEndDate(compDates.endDate);
                                      }
                                      setActiveCalendarField(null); // Fin de sélection
                                      return;
                                    }

                                    // Mode par défaut: sélection de plage pour période principale
                                    setTempSelectedPeriod('CUSTOM');
                                    const start = tempCustomStartDate;
                                    if (!start) {
                                      setTempCustomStartDate(dateStr);
                                      setTempCustomEndDate('');
                                    } else {
                                      // Second clic - définir la fin (sans appliquer)
                                      if (dateStr < start) {
                                        setTempCustomStartDate(dateStr);
                                        setTempCustomEndDate(start);
                                      } else {
                                        setTempCustomEndDate(dateStr);
                                      }
                                      // Recalculer les dates de comparaison si actif
                                      if (tempCompareEnabled) {
                                        const finalStart = dateStr < start ? dateStr : start;
                                        const finalEnd = dateStr < start ? start : dateStr;
                                        if (tempComparisonType !== 'custom') {
                                          const compDates = calculateComparisonDates(finalStart, finalEnd, tempComparisonType);
                                          setTempComparisonStartDate(compDates.startDate);
                                          setTempComparisonEndDate(compDates.endDate);
                                        } else {
                                          // Mode custom: vérifier que la date de fin comparaison reste < nouvelle date début N
                                          const newMainStart = new Date(finalStart);
                                          if (tempComparisonEndDate && new Date(tempComparisonEndDate) >= newMainStart) {
                                            const compDates = calculateComparisonDates(finalStart, finalEnd, 'previous_period');
                                            setTempComparisonStartDate(compDates.startDate);
                                            setTempComparisonEndDate(compDates.endDate);
                                            setTempComparisonType('previous_period');
                                          }
                                        }
                                      }
                                    }
                                  }}
                                  style={{
                                    width: '24px',
                                    height: '24px',
                                    border: 'none',
                                    borderRadius: borderRadius,
                                    background: bgColor,
                                    color: textColor,
                                    fontWeight: isToday ? '600' : '400',
                                    fontSize: '11px',
                                    cursor: isFuture ? 'default' : 'pointer',
                                    transition: 'all 0.15s ease'
                                  }}
                                  onMouseEnter={(e) => { if (!isSelected && !isCompSelected && !isFuture) e.target.style.background = '#f3f4f6'; }}
                                  onMouseLeave={(e) => {
                                    if (isFuture) return;
                                    if (isSelected || isCompSelected) return;
                                    if (isInRange && isInCompRange) e.target.style.background = 'linear-gradient(135deg, rgba(102, 126, 234, 0.2) 50%, rgba(249, 115, 22, 0.2) 50%)';
                                    else if (isInRange) e.target.style.background = 'rgba(102, 126, 234, 0.15)';
                                    else if (isInCompRange) e.target.style.background = 'rgba(249, 115, 22, 0.15)';
                                    else e.target.style.background = 'transparent';
                                  }}
                                >
                                  {day}
                                </button>
                              );
                            }

                            return (
                              <div key={`month-${idx}`} style={{ marginBottom: '16px' }}>
                                <div style={{ fontWeight: '600', fontSize: '11px', marginBottom: '6px', color: '#6b7280', letterSpacing: '0.5px' }}>
                                  {monthNames[month]} {year}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px', marginBottom: '2px' }}>
                                  {dayNames.map((d, i) => (
                                    <div key={`header-${idx}-${i}`} style={{ textAlign: 'center', fontSize: '9px', color: '#9ca3af', fontWeight: '500', width: '24px' }}>{d}</div>
                                  ))}
                                </div>
                                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '1px' }}>
                                  {days}
                                </div>
                              </div>
                            );
                          });
                        })()}
                      </div>
                    </div>
                  </div>
                  {/* Légende des couleurs (visible si comparaison active) */}
                  {tempCompareEnabled && (
                    <div style={{ display: 'flex', gap: '16px', marginTop: '8px', paddingTop: '8px', borderTop: '1px solid #f3f4f6' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#6b7280' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#667eea' }}></div>
                        <span>Période</span>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '11px', color: '#6b7280' }}>
                        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#f97316' }}></div>
                        <span>Comparaison</span>
                      </div>
                    </div>
                  )}
                  {/* Boutons Annuler / Appliquer */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', marginTop: '12px', paddingTop: '12px', borderTop: '1px solid #e5e7eb' }}>
                    <button
                      onClick={() => {
                        // Réinitialiser les états temporaires de période
                        setTempCustomStartDate('');
                        setTempCustomEndDate('');
                        setTempSelectedPeriod('');
                        // Réinitialiser les états temporaires de comparaison
                        setTempCompareEnabled(globalCompareEnabled);
                        setTempComparisonType(globalComparisonType);
                        setTempComparisonStartDate(comparisonStartDate);
                        setTempComparisonEndDate(comparisonEndDate);
                        setActiveCalendarField(null);
                        setIsPeriodMenuOpen(false);
                      }}
                      style={{
                        padding: '6px 16px',
                        border: 'none',
                        background: 'none',
                        color: '#667eea',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer'
                      }}
                    >
                      Annuler
                    </button>
                    <button
                      onClick={() => {
                        // Appliquer la période
                        if (tempSelectedPeriod && tempSelectedPeriod !== 'CUSTOM') {
                          // Appliquer une période prédéfinie
                          setSelectedPeriod(tempSelectedPeriod);
                          setCustomStartDate('');
                          setCustomEndDate('');
                          setTempCustomStartDate('');
                          setTempCustomEndDate('');
                          setTempSelectedPeriod('');
                          window.dispatchEvent(new CustomEvent('periodChange', { detail: { period: tempSelectedPeriod } }));
                        } else {
                          // Appliquer une période personnalisée (CUSTOM ou vide)
                          const finalStart = tempCustomStartDate || customStartDate;
                          const finalEnd = tempCustomEndDate || customEndDate;
                          if (finalStart && finalEnd) {
                            setSelectedPeriod('CUSTOM');
                            setCustomStartDate(finalStart);
                            setCustomEndDate(finalEnd);
                            setTempCustomStartDate('');
                            setTempCustomEndDate('');
                            setTempSelectedPeriod('');
                            window.dispatchEvent(new CustomEvent('periodChange', { detail: { period: 'CUSTOM' } }));
                          }
                        }

                        // Appliquer la comparaison
                        setGlobalCompareEnabled(tempCompareEnabled);
                        setGlobalComparisonType(tempComparisonType);
                        setComparisonStartDate(tempComparisonStartDate);
                        setComparisonEndDate(tempComparisonEndDate);

                        // Synchroniser avec tous les états de comparaison par plateforme
                        const legacyType = tempCompareEnabled ? tempComparisonType : '';
                        setComparisonType(legacyType);
                        setGA4ComparisonType(legacyType);
                        setFbComparisonType(legacyType);
                        setMatomoComparisonType(legacyType);
                        setPianoComparisonType(legacyType);
                        setGadsComparisonType(legacyType);
                        setMsadsComparisonType(legacyType);

                        setActiveCalendarField(null);
                        setIsPeriodMenuOpen(false);
                      }}
                      style={{
                        padding: '6px 16px',
                        border: 'none',
                        background: hasPeriodChanges ? '#667eea' : 'none',
                        color: hasPeriodChanges ? 'white' : '#667eea',
                        fontSize: '13px',
                        fontWeight: '500',
                        cursor: 'pointer',
                        borderRadius: '6px',
                        transition: 'all 0.2s ease'
                      }}
                    >
                      Appliquer
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Section Sites - avec menu hover */}
            {sidebarPorteLeContexte && (() => {
              // Filtrer les sites par client sélectionné
              let filteredSites = selectedClientId
                ? userSites.filter(site => site.clientId === selectedClientId)
                : userSites;

              // Si on est sur un dashboard analytics, filtrer les sites qui ont des comptes de ce type
              if (currentView === 'ga4-dashboard' && ga4Properties.length > 0) {
                const ga4SourceIds = new Set(ga4Properties.map(p => p.sourceId || p.id));
                filteredSites = filteredSites.filter(site =>
                  site.accountIds && site.accountIds.some(id => ga4SourceIds.has(id))
                );
              } else if (currentView === 'matomo-dashboard' && matomoSites.length > 0) {
                const matomoSourceIds = new Set(matomoSites.map(s => s.sourceId || s.id));
                filteredSites = filteredSites.filter(site =>
                  site.accountIds && site.accountIds.some(id => matomoSourceIds.has(id))
                );
              } else if (currentView === 'piano-dashboard' && pianoSites.length > 0) {
                const pianoSourceIds = new Set(pianoSites.map(s => s.sourceId || s.id));
                filteredSites = filteredSites.filter(site =>
                  site.accountIds && site.accountIds.some(id => pianoSourceIds.has(id))
                );
              }

              // N'afficher que si plus d'un site disponible
              if (filteredSites.length <= 1) return null;

              // Trouver le nom du site sélectionné
              const selectedSiteName = selectedSiteId === 'all'
                ? 'Tous les sites'
                : filteredSites.find(s => (s._id || s.id) === selectedSiteId)?.name || 'Tous les sites';

              return (
                <div className={`sidebar-tool-category ${selectedSiteId !== 'all' ? 'active' : ''}`}>
                  <div className="sidebar-section-title">
                    <Globe size={16} />
                    <span>{selectedSiteName}</span>
                  </div>
                  <div className="sidebar-tool-menu">
                    <button
                      className={`sidebar-tool-menu-item ${selectedSiteId === 'all' ? 'active' : ''}`}
                      onClick={() => setSelectedSiteId('all')}
                    >
                      <Globe size={18} />
                      <span>Tous les sites</span>
                    </button>
                    {filteredSites.map(site => (
                      <button
                        key={site._id || site.id}
                        className={`sidebar-tool-menu-item ${selectedSiteId === (site._id || site.id) ? 'active' : ''}`}
                        onClick={() => setSelectedSiteId(site._id || site.id)}
                      >
                        <Globe size={18} />
                        <span>{site.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/*
              Section CMS — les boutiques, au-dessus d'Analytics.

              ⚠️ **Une famille à part, et non une entrée de plus sous Analytics.** Une boutique ne
              compte ni session, ni visiteur, ni canal : elle sait ce qui a été encaissé. Les
              ranger ensemble obligerait à deviner, dans une même liste, lesquelles répondent
              « combien de gens sont venus » et lesquelles « combien ont acheté ».

              Elle parcourt `CMS` (`constants/plateformes.js`) : ajouter Shopify ou WooCommerce
              s'y fait en une entrée, sans toucher à ce bloc.
            */}
            {sidebarPorteLeContexte && currentView !== 'radar' && cmsDisponibles.length > 0 && (
              <div className={`sidebar-tool-category ${
                cmsDisponibles.some(cms => currentView === cms.vue) ? 'active' : ''}`}>
                <div className="sidebar-section-title">
                  <Store size={16} />
                  {/* Le titre nomme la boutique ouverte, et retombe sur la famille sinon — même
                      règle que la section Analytics juste en dessous. */}
                  <span>{cmsDisponibles.find(cms => currentView === cms.vue)?.libelle || 'CMS'}</span>
                </div>
                <div className="sidebar-tool-menu">
                  {cmsDisponibles.map(cms => (
                    <button
                      key={cms.cle}
                      className={`sidebar-tool-menu-item ${currentView === cms.vue ? 'active' : ''}`}
                      onClick={() => ouvrirChoixDashboard(
                        cms.cle,
                        // Une seule boutique : l'ouvrir plutôt que demander de la choisir.
                        cms.comptes.length === 1 ? cms.comptes[0].id : null,
                        null,
                        'home'
                      )}
                    >
                      {cms.logo
                        ? <img src={cms.logo} alt={cms.libelle} className="sidebar-platform-logo" />
                        : <Store size={18} />}
                      <span>{cms.libelle}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Section Analytics — les deux dashboards d'une même solution, sous une seule
                entrée. Elle portait auparavant une section « Consolidé » jumelle juste
                au-dessus : mêmes trois solutions, mêmes trois logos, seule la destination
                changeait. Le choix se fait désormais après le clic, dans l'écran dédié, et la
                barre ne pose plus deux fois la même question. */}
            {sidebarPorteLeContexte && currentView !== 'radar' && (
              /*
                ⚠️ **La section entière, et pas seulement ses entrées.** Filtrer les trois
                entrées laissait le titre « Analytics » au-dessus du vide : un en-tête qui
                n'ouvre rien se lit comme un chargement qui a échoué.
              */
              (filteredMatomoSites.length > 0 && plateformeOuverte('matomo-dashboard'))
              || (filteredGA4Properties.length > 0 && plateformeOuverte('ga4-dashboard'))
              || (filteredPianoSites.length > 0 && plateformeOuverte('piano-dashboard'))
            ) && (
              <div className={`sidebar-tool-category ${
                ['ga4-dashboard', 'matomo-dashboard', 'piano-dashboard', 'consolide-dashboard', 'choix-dashboard', 'dashboard-sur-mesure'].includes(currentView)
                /*
                 * ⚠️ La génération n'appartient pas à une catégorie : elle appartient à celle de
                 * **sa source**. Allumer Analytics pendant qu'on écrit un tableau Microsoft Ads
                 * désigne la mauvaise famille, et c'est ce que faisait la version précédente.
                 */
                || (currentView === 'generation-dashboard' && !ONGLET_PAR_TYPE_DE_COMPTE[generationSurMesure?.type])
                  ? 'active' : ''}`}>
                <div className="sidebar-section-title">
                  <BarChart2 size={16} />
                  <span>{currentView === 'ga4-dashboard' ? 'Google Analytics' : currentView === 'matomo-dashboard' ? 'Matomo' : currentView === 'piano-dashboard' ? 'Piano' : currentView === 'consolide-dashboard' ? 'Consolidé' : 'Analytics'}</span>
                </div>
                <div className="sidebar-tool-menu">
                  {/* Plus d'entrée « Tous les… » ici. Les deux qui existaient — « Tous les
                      Analytics » et « Tous les consolidés » — menaient l'une à l'accueil,
                      que le logo de l'en-tête ouvre déjà depuis n'importe quel écran, l'autre
                      au consolidé sans cadrage, qu'on atteint en élargissant le sélecteur une
                      fois dedans. Une section de barre latérale nomme des destinations ; deux
                      de plus vers ce qui est déjà atteignable ne faisaient qu'allonger la
                      liste. À ne pas confondre avec les « Tous les sites » des sections
                      Matomo, GA4 et Piano plus bas : celles-là défont une sélection à
                      l'intérieur d'un dashboard, et restent. */}
                  {filteredGA4Properties.length > 0 && plateformeOuverte('ga4-dashboard') && (
                    <button
                      className={`sidebar-tool-menu-item ${currentView === 'ga4-dashboard' || (currentView === 'consolide-dashboard' && consolideSelectedSource === 'GOOGLE_ANALYTICS') ? 'active' : ''}`}
                      onClick={() => ouvrirChoixDashboard(
                        'GOOGLE_ANALYTICS',
                        filteredGA4Properties.length === 1 ? filteredGA4Properties[0].id : null,
                        null,
                        'home'
                      )}
                    >
                      <img
                        src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png"
                        alt="Google Analytics 4"
                        className="sidebar-platform-logo"
                      />
                      <span>Google Analytics</span>
                    </button>
                  )}
                  {filteredMatomoSites.length > 0 && plateformeOuverte('matomo-dashboard') && (
                    <button
                      className={`sidebar-tool-menu-item ${currentView === 'matomo-dashboard' || (currentView === 'consolide-dashboard' && consolideSelectedSource === 'MATOMO') ? 'active' : ''}`}
                      onClick={() => ouvrirChoixDashboard(
                        'MATOMO',
                        filteredMatomoSites.length === 1 ? filteredMatomoSites[0].id : null,
                        null,
                        'home'
                      )}
                    >
                      <img
                        src={matomoLogo}
                        alt="Matomo"
                        className="sidebar-platform-logo"
                      />
                      <span>Matomo</span>
                    </button>
                  )}
                  {filteredPianoSites.length > 0 && plateformeOuverte('piano-dashboard') && (
                    <button
                      className={`sidebar-tool-menu-item ${currentView === 'piano-dashboard' || (currentView === 'consolide-dashboard' && consolideSelectedSource === 'PIANO_ANALYTICS') ? 'active' : ''}`}
                      onClick={() => ouvrirChoixDashboard(
                        'PIANO_ANALYTICS',
                        filteredPianoSites.length === 1 ? filteredPianoSites[0].id : null,
                        null,
                        'home'
                      )}
                    >
                      <img src={pianoLogo} alt="Piano" className="sidebar-platform-logo" />
                      <span>Piano</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Section Sites Matomo - uniquement sur matomo-dashboard */}
            {currentView === 'matomo-dashboard' && filteredMatomoSites.length > 0 && (
              <div className={`sidebar-tool-category active`}>
                <div className="sidebar-section-title">
                  <Users size={16} />
                  <span>{matomoSelectedSiteId ? filteredMatomoSites.find(s => s.id === matomoSelectedSiteId)?.name || 'Tous les sites' : 'Tous les sites'}</span>
                </div>
                <div className="sidebar-tool-menu">
                  <button
                    className={`sidebar-tool-menu-item ${!matomoSelectedSiteId ? 'active' : ''}`}
                    onClick={() => {
                      setMatomoSelectedSiteId(null);
                      setMatomoDashboardData(null);
                    }}
                  >
                    <span>Tous les sites</span>
                  </button>
                  {filteredMatomoSites
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(site => (
                      <button
                        key={site.id}
                        className={`sidebar-tool-menu-item ${matomoSelectedSiteId === site.id ? 'active' : ''}`}
                        onClick={() => {
                          setMatomoSelectedSiteId(site.id);
                          setMatomoDashboardData(null);
                        }}
                      >
                        <span>{site.name}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Section Dashboard Matomo - masquée car 1 seul dashboard (Global) */}

            {/* Section Propriétés GA4 - uniquement sur ga4-dashboard */}
            {currentView === 'ga4-dashboard' && filteredGA4Properties.length > 0 && (
              <div className={`sidebar-tool-category active`}>
                <div className="sidebar-section-title">
                  <Users size={16} />
                  <span>{ga4SelectedPropertyId ? filteredGA4Properties.find(p => p.id === ga4SelectedPropertyId)?.name || 'Propriétés' : 'Toutes les propriétés'}</span>
                </div>
                <div className="sidebar-tool-menu">
                  <button
                    className={`sidebar-tool-menu-item ${!ga4SelectedPropertyId ? 'active' : ''}`}
                    onClick={() => {
                      setGA4SelectedPropertyId(null);
                      setGA4DashboardData(null);
                    }}
                  >
                    <span>Toutes les propriétés</span>
                  </button>
                  {filteredGA4Properties
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(property => (
                      <button
                        key={property.id}
                        className={`sidebar-tool-menu-item ${ga4SelectedPropertyId === property.id ? 'active' : ''}`}
                        onClick={() => {
                          setGA4SelectedPropertyId(property.id);
                          setGA4DashboardData(null);
                        }}
                      >
                        <span>{property.name}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Section Sites Piano - uniquement sur piano-dashboard */}
            {currentView === 'piano-dashboard' && filteredPianoSites.length > 0 && (
              <div className={`sidebar-tool-category active`}>
                <div className="sidebar-section-title">
                  <Users size={16} />
                  <span>{pianoSelectedSiteId ? filteredPianoSites.find(s => s.id === pianoSelectedSiteId)?.name || 'Tous les sites' : 'Tous les sites'}</span>
                </div>
                <div className="sidebar-tool-menu">
                  <button
                    className={`sidebar-tool-menu-item ${!pianoSelectedSiteId ? 'active' : ''}`}
                    onClick={() => {
                      setPianoSelectedSiteId(null);
                      setPianoDashboardData(null);
                    }}
                  >
                    <span>Tous les sites</span>
                  </button>
                  {filteredPianoSites
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map(site => (
                      <button
                        key={site.id}
                        className={`sidebar-tool-menu-item ${pianoSelectedSiteId === site.id ? 'active' : ''}`}
                        onClick={() => {
                          setPianoSelectedSiteId(site.id);
                          setPianoDashboardData(null);
                        }}
                      >
                        <span>{site.name}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Section Dashboard GA4 - masquée car 1 seul dashboard (Global) */}

            {/* Section Filtres Analyser - sur les vues analyze et history */}
            {(currentView === 'analyze' || currentView === 'history') && (
              <>
                {/* Sites */}
                {(() => {
                  const filteredSites = selectedClientId
                    ? userSites.filter(site => site.clientId === selectedClientId)
                    : userSites;

                  if (filteredSites.length === 0) return null;

                  return (
                    <div className="sidebar-section">
                      <div className="sidebar-section-title">
                        <Globe size={16} />
                        Sites
                      </div>
                      <div className="sidebar-section-content">
                        <select
                          className="sidebar-period-select"
                          value={analyzeFilterSite}
                          onChange={(e) => setAnalyzeFilterSite(e.target.value)}
                        >
                          <option value="all">Tous les sites</option>
                          {filteredSites.map(site => (
                            <option key={site._id || site.id} value={site._id || site.id}>
                              {site.name}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  );
                })()}

                {/* Analytics - masqué pour l'instant car non supporté dans Analyser */}
                {/* TODO: Activer quand GA4 et Matomo seront supportés dans l'analyse */}

                {/* Publicité — le même mot que la rubrique de `#dashboard`, pour que la même
                    famille ne porte pas deux noms. Seuls Google Ads et Meta Ads sont filtrables
                    ici pour l'instant. */}
                {(() => {
                  // Obtenir les accountIds autorisés pour le client sélectionné (via les sites)
                  let allowedAccountIds = null;
                  if (selectedClientId && userSites.length > 0) {
                    const clientSites = userSites.filter(site => site.clientId === selectedClientId);
                    allowedAccountIds = ensembleAutorise(
                      clientSites.flatMap(site => (Array.isArray(site.accountIds) ? site.accountIds : []))
                    );
                  }

                  // Filtrer les comptes par client
                  const clientAccounts = allowedAccountIds
                    ? allAdAccounts.filter(a => compteAutorise(a, allowedAccountIds))
                    : allAdAccounts;

                  const hasGoogleAds = clientAccounts.some(a => a.type === 'GOOGLE_ADS');
                  const hasFacebookAds = clientAccounts.some(a => a.type === 'FACEBOOK_ADS');

                  if (!hasGoogleAds && !hasFacebookAds) return null;

                  return (
                    <div className="sidebar-section">
                      <div className="sidebar-section-title">
                        <Database size={16} />
                        Publicité
                      </div>
                      <div className="sidebar-section-content">
                        {hasGoogleAds && (
                          <button
                            className={`sidebar-platform-item ${analyzeFilterPlatforms.google ? 'active' : ''}`}
                            onClick={() => setAnalyzeFilterPlatforms(prev => ({ ...prev, google: !prev.google }))}
                          >
                            <img src={googleAdsLogo} alt="Google Ads" className="sidebar-platform-logo" />
                            <span>Google Ads</span>
                          </button>
                        )}
                        {hasFacebookAds && (
                          <button
                            className={`sidebar-platform-item ${analyzeFilterPlatforms.facebook ? 'active' : ''}`}
                            onClick={() => setAnalyzeFilterPlatforms(prev => ({ ...prev, facebook: !prev.facebook }))}
                          >
                            <img src={metaAdsLogo} alt="Meta Ads" className="sidebar-platform-logo" />
                            <span>Meta Ads</span>
                          </button>
                        )}
                      </div>
                    </div>
                  );
                })()}

                {/* Comptes */}
                {(() => {
                  // Obtenir les accountIds autorisés selon le client et/ou site sélectionné
                  let allowedAccountIds = null;

                  if (analyzeFilterSite && analyzeFilterSite !== 'all') {
                    // Filtrer par site spécifique
                    const selectedSite = userSites.find(s => (s._id || s.id) === analyzeFilterSite);
                    allowedAccountIds = ensembleAutorise(
                      Array.isArray(selectedSite?.accountIds) ? selectedSite.accountIds : []
                    );
                  } else if (selectedClientId && userSites.length > 0) {
                    // Filtrer par client (via les sites du client)
                    const clientSites = userSites.filter(site => site.clientId === selectedClientId);
                    allowedAccountIds = ensembleAutorise(
                      clientSites.flatMap(site => (Array.isArray(site.accountIds) ? site.accountIds : []))
                    );
                  }

                  // Filtrer les comptes
                  const filteredAccounts = allAdAccounts.filter(a => {
                    // Filtrer par client/site si applicable
                    if (allowedAccountIds && !compteAutorise(a, allowedAccountIds)) return false;

                    // Filtrer par plateforme sélectionnée (uniquement Google et Facebook pour l'instant)
                    if (a.type === 'GOOGLE_ADS' && !analyzeFilterPlatforms.google) return false;
                    if (a.type === 'FACEBOOK_ADS' && !analyzeFilterPlatforms.facebook) return false;
                    // Exclure Microsoft Ads car non supporté dans Analyser
                    if (a.type === 'MICROSOFT_ADS') return false;

                    return true;
                  });

                  if (filteredAccounts.length === 0) return null;

                  return (
                    <div className="sidebar-section">
                      <div className="sidebar-section-title">
                        <Users size={16} />
                        Comptes
                      </div>
                      <div className="sidebar-section-content">
                        <select
                          className="sidebar-period-select"
                          value={analyzeFilterAccount}
                          onChange={(e) => setAnalyzeFilterAccount(e.target.value)}
                        >
                          <option value="all">Tous les comptes</option>
                          {filteredAccounts
                            .sort((a, b) => a.name.localeCompare(b.name))
                            .map(account => (
                              <option key={account.id} value={account.id}>
                                {account.name}
                              </option>
                            ))}
                        </select>
                      </div>
                    </div>
                  );
                })()}
              </>
            )}

            {/*
              * Section Plateformes de Radar : elle **filtre** le feed au lieu de naviguer.
              *
              * C'est une section à part, et non la suivante rendue conditionnelle, pour deux
              * raisons qui tiennent chacune seule :
              *
              * 1. ⚠️ **Elle n'énumère que les plateformes présentes dans le feed**, que Radar
              *    remonte. La section de navigation liste les plateformes *connectées* — sept sur
              *    ce parc, là où Radar n'en porte que deux : cinq entrées auraient vidé la liste
              *    au clic, ce qui se lit comme une panne et non comme un filtre.
              * 2. Les sept boutons d'en dessous ouvrent chacun un dashboard par trois
              *    `dispatchEvent`. Les brancher sur un filtre aurait demandé sept conditions,
              *    toutes à refaire à la plateforme suivante.
              */}
            {currentView === 'radar' && radarOuvert && Object.keys(plateformesRadar).length > 1 && (
              <div className={`sidebar-tool-category ${plateformeRadar ? 'active' : ''}`}>
                <div className="sidebar-section-title">
                  <Database size={16} />
                  {/* « Publicité », comme partout ailleurs. ⚠️ Ce filtre porte sur le `type`
                      du compte, qui pourra un jour couvrir Search Console — le Diagnostic SEO
                      s'y comptera sous `GOOGLE_SEARCH_CONSOLE`. Le libellé sera à revoir ce
                      jour-là ; aujourd'hui le feed ne porte que des régies et Merchant. */}
                  <span>{plateformeRadar ? libellePlateforme(plateformeRadar) : 'Publicité'}</span>
                </div>
                <div className="sidebar-tool-menu">
                  <button
                    className={`sidebar-tool-menu-item ${!plateformeRadar ? 'active' : ''}`}
                    onClick={() => setPlateformeRadar(null)}
                  >
                    <Database size={18} />
                    {/* Rompt le motif « Toutes les <pluriel> » des autres filtres, le nom de
                        la famille étant indénombrable — mais il vaut mieux qu'elle se nomme
                        pareil des deux côtés du menu. */}
                    <span>Toute la publicité</span>
                  </button>
                  {Object.entries(plateformesRadar)
                    .sort((a, b) => libellePlateforme(a[0]).localeCompare(libellePlateforme(b[0])))
                    .map(([type, nombre]) => (
                      <button
                        key={type}
                        className={`sidebar-tool-menu-item ${plateformeRadar === type ? 'active' : ''}`}
                        /* Un second clic relâche, comme sur les pastilles de l'écran. */
                        onClick={() => setPlateformeRadar(actuel => (actuel === type ? null : type))}
                      >
                        {LOGOS_PLATEFORMES[type]
                          ? <img src={LOGOS_PLATEFORMES[type]} alt="" className="sidebar-platform-logo" />
                          : <Database size={18} />}
                        <span>{libellePlateforme(type)}</span>
                        <span className="sidebar-tool-menu-compteur">{nombre}</span>
                      </button>
                    ))}
                </div>
              </div>
            )}

            {/* Section Plateformes - avec menu hover */}
            {sidebarPorteLeContexte && currentView !== 'radar' && (() => {
              // Déterminer les plateformes disponibles pour le client sélectionné
              let availablePlatforms = { google: false, microsoft: false, facebook: false, pinterest: false, tiktok: false, chatgpt: false, awin: false };

              // Vérifier d'abord si on a des comptes publicitaires
              if (!allAdAccounts.some(a => ['GOOGLE_ADS', 'MICROSOFT_ADS', 'FACEBOOK_ADS', 'PINTEREST_ADS', 'TIKTOK_ADS', 'OPENAI_ADS', 'AWIN'].includes(a.type))) {
                return null;
              }

              // Par défaut, toutes les plateformes qui ont des comptes sont disponibles
              allAdAccounts.forEach(account => {
                if (account.type === 'GOOGLE_ADS') availablePlatforms.google = true;
                if (account.type === 'MICROSOFT_ADS') availablePlatforms.microsoft = true;
                if (account.type === 'FACEBOOK_ADS') availablePlatforms.facebook = true;
                if (account.type === 'PINTEREST_ADS') availablePlatforms.pinterest = true;
                if (account.type === 'TIKTOK_ADS') availablePlatforms.tiktok = true;
                if (account.type === 'OPENAI_ADS') availablePlatforms.chatgpt = true;
                if (account.type === 'AWIN') availablePlatforms.awin = true;
              });

              // Filtrer si un site spécifique est sélectionné OU si un client est sélectionné OU si l'utilisateur est un CLIENT
              const isClientUser = user && user.role === 'CLIENT';
              const shouldFilter = (selectedSiteId && selectedSiteId !== 'all') || selectedClientId || isClientUser;

              if (shouldFilter && userSites.length > 0 && allAdAccounts.length > 0) {
                // Obtenir les accountIds autorisés pour le client et/ou le site sélectionné
                const allowedAccountIds = new Set();
                const filteredSitesForPlatforms = userSites.filter(site => {
                  // Si un site spécifique est sélectionné, filtrer uniquement par ce site
                  if (selectedSiteId && selectedSiteId !== 'all') {
                    const siteId = site._id || site.id;
                    return siteId === selectedSiteId;
                  }
                  // Sinon, filtrer par client
                  if (selectedClientId) {
                    return site.clientId === selectedClientId;
                  }
                  // Pour un utilisateur CLIENT, utiliser tous ses sites
                  return true;
                });

                filteredSitesForPlatforms.forEach(site => {
                  if (site.accountIds && Array.isArray(site.accountIds)) {
                    site.accountIds.forEach(id => allowedAccountIds.add(id));
                  }
                });

                // Vérifier quelles plateformes ont des comptes pour ce filtre
                // Si on a des sites filtrés, on doit filtrer les plateformes (même si pas d'accountIds)
                if (filteredSitesForPlatforms.length > 0) {
                  availablePlatforms = { google: false, microsoft: false, facebook: false, pinterest: false, tiktok: false, chatgpt: false, awin: false };
                  if (allowedAccountIds.size > 0) {
                    allAdAccounts.forEach(account => {
                      if (compteAutorise(account, allowedAccountIds)) {
                        if (account.type === 'GOOGLE_ADS') availablePlatforms.google = true;
                        if (account.type === 'MICROSOFT_ADS') availablePlatforms.microsoft = true;
                        if (account.type === 'FACEBOOK_ADS') availablePlatforms.facebook = true;
                        if (account.type === 'PINTEREST_ADS') availablePlatforms.pinterest = true;
                        if (account.type === 'TIKTOK_ADS') availablePlatforms.tiktok = true;
                        if (account.type === 'OPENAI_ADS') availablePlatforms.chatgpt = true;
                if (account.type === 'AWIN') availablePlatforms.awin = true;
                      }
                    });
                  }
                }
              }

              /*
               * ⚠️ **Les dashboards fermés tombent ici, APRÈS les deux calculs.**
               *
               * Ce bloc est calculé deux fois : une fois sur tous les comptes, puis **une
               * seconde fois** quand un périmètre s'applique — un site choisi, un client
               * choisi, ou un accès client. La seconde écrase la première. Filtrer dans le
               * premier `forEach` était donc sans effet précisément pour un accès client,
               * c'est-à-dire le seul cas que ce droit concerne : la sidebar continuait
               * d'afficher les plateformes fermées.
               *
               * ⚠️ **Les clés d'ici ne sont pas celles des écrans** : `facebook` et non
               * `facebook-ads`, `chatgpt` et non `chatgpt-ads`. Une table, parce que les
               * confondre ne casse rien — le filtre ne trouve simplement jamais la plateforme,
               * et ne filtre plus rien.
               */
              const ECRAN_PAR_CLE_SIDEBAR = {
                google: 'google', microsoft: 'microsoft', facebook: 'facebook-ads',
                pinterest: 'pinterest-ads', tiktok: 'tiktok-ads', chatgpt: 'chatgpt-ads',
                awin: 'awin'
              };
              Object.keys(ECRAN_PAR_CLE_SIDEBAR).forEach(cle => {
                if (availablePlatforms[cle] && !plateformeOuverte(ECRAN_PAR_CLE_SIDEBAR[cle])) {
                  availablePlatforms[cle] = false;
                }
              });

              // Si aucune plateforme n'est disponible après filtrage, ne pas afficher la section
              if (!availablePlatforms.google && !availablePlatforms.microsoft && !availablePlatforms.facebook && !availablePlatforms.pinterest && !availablePlatforms.tiktok && !availablePlatforms.chatgpt && !availablePlatforms.awin) {
                return null;
              }

              // Déterminer si une plateforme est active
              const isActive = (currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && ['google', 'microsoft', 'facebook-ads', 'pinterest-ads', 'tiktok-ads', 'chatgpt-ads', 'awin'].includes(currentDashboardType);

              // Nom de la plateforme sélectionnée
              /*
               * Le titre de la famille : le nom de la régie ouverte, ou « Publicité » à défaut.
               *
               * ⚠️ **« Publicité », et non « Plateformes ».** La rubrique de `#dashboard` nomme
               * déjà cette famille ainsi : deux noms pour la même chose obligent le lecteur à
               * deviner qu'il s'agit du même endroit. Le repli est le libellé que l'on voit le
               * plus — il s'affiche sur l'accueil, où aucune régie n'est ouverte.
               *
               * ⚠️ Ne pas confondre avec la section « Plateformes » de Radar, juste au-dessus :
               * celle-là filtre sur le `type` du compte, qui couvre Search Console, Merchant et
               * PrestaShop autant que les régies. « Publicité » y serait faux.
               */
              const platformNames = {
                'google': 'Google Ads',
                'microsoft': 'Microsoft Ads',
                'facebook-ads': 'Meta Ads',
                'pinterest-ads': 'Pinterest Ads',
                'tiktok-ads': 'TikTok Ads',
                'chatgpt-ads': 'ChatGPT Ads',
                'awin': 'Awin'
              };
              const selectedPlatformName = (currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && platformNames[currentDashboardType] ? platformNames[currentDashboardType] : 'Publicité';

              // Une génération qui vise une régie appartient à cette famille-ci, pas à Analytics.
              const genereUneRegie = currentView === 'generation-dashboard'
                && Boolean(ONGLET_PAR_TYPE_DE_COMPTE[generationSurMesure?.type]);

              return (
                <div className={`sidebar-tool-category ${isActive || genereUneRegie ? 'active' : ''}`}>
                  <div className="sidebar-section-title">
                    <Database size={16} />
                    <span>{selectedPlatformName}</span>
                  </div>
                  <div className="sidebar-tool-menu">
                    {/* « Toutes les Plateformes » retirée pour la même raison que son
                        homologue de la section Analytics : elle exécutait le même
                        `setCurrentViewWithHistory('home')`, sans ancre ni défilement vers le
                        tableau qu'elle nommait. Le logo de l'en-tête y mène déjà. */}
                    {availablePlatforms.google && (
                      <button
                        className={`sidebar-tool-menu-item ${(currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && currentDashboardType === 'google' ? 'active' : ''}`}
                        onClick={() => {
                          if (currentView !== 'dashboard') {
                            setCurrentViewWithHistory('dashboard');
                          }
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: 'google' } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: 'google' } }));
                          }, 100);
                        }}
                      >
                        <img src={googleAdsLogo} alt="Google Ads" className="sidebar-platform-logo" />
                        <span>Google Ads</span>
                      </button>
                    )}
                    {availablePlatforms.microsoft && (
                      <button
                        className={`sidebar-tool-menu-item ${(currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && currentDashboardType === 'microsoft' ? 'active' : ''}`}
                        onClick={() => {
                          if (currentView !== 'dashboard') {
                            setCurrentViewWithHistory('dashboard');
                          }
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: 'microsoft' } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: 'microsoft' } }));
                          }, 100);
                        }}
                      >
                        <img src={microsoftAdsLogo} alt="Microsoft Ads" className="sidebar-platform-logo" />
                        <span>Microsoft Ads</span>
                      </button>
                    )}
                    {availablePlatforms.facebook && (
                      <button
                        className={`sidebar-tool-menu-item ${(currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && currentDashboardType === 'facebook-ads' ? 'active' : ''}`}
                        onClick={() => {
                          if (currentView !== 'dashboard') {
                            setCurrentViewWithHistory('dashboard');
                          }
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: 'facebook-ads' } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: 'facebook-ads' } }));
                          }, 100);
                        }}
                      >
                        <img src={metaAdsLogo} alt="Meta Ads" className="sidebar-platform-logo" />
                        <span>Meta Ads</span>
                      </button>
                    )}
                    {availablePlatforms.chatgpt && (
                      <button
                        className={`sidebar-tool-menu-item ${(currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && currentDashboardType === 'chatgpt-ads' ? 'active' : ''}`}
                        onClick={() => {
                          if (currentView !== 'dashboard') {
                            setCurrentViewWithHistory('dashboard');
                          }
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: 'chatgpt-ads' } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: 'chatgpt-ads' } }));
                          }, 100);
                        }}
                      >
                        <img src={chatgptAdsLogo} alt="ChatGPT Ads" className="sidebar-platform-logo" />
                        <span>ChatGPT Ads</span>
                      </button>
                    )}
                    {availablePlatforms.awin && (
                      <button
                        className={`sidebar-tool-menu-item ${(currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && currentDashboardType === 'awin' ? 'active' : ''}`}
                        onClick={() => {
                          if (currentView !== 'dashboard') {
                            setCurrentViewWithHistory('dashboard');
                          }
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: 'awin' } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: 'awin' } }));
                          }, 100);
                        }}
                      >
                        <img src={awinLogo} alt="Awin" className="sidebar-platform-logo" />
                        <span>Awin</span>
                      </button>
                    )}
                    {availablePlatforms.pinterest && (
                      <button
                        className={`sidebar-tool-menu-item ${(currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && currentDashboardType === 'pinterest-ads' ? 'active' : ''}`}
                        onClick={() => {
                          if (currentView !== 'dashboard') {
                            setCurrentViewWithHistory('dashboard');
                          }
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: 'pinterest-ads' } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: 'pinterest-ads' } }));
                          }, 100);
                        }}
                      >
                        <img src={pinterestAdsLogo} alt="Pinterest Ads" className="sidebar-platform-logo" />
                        <span>Pinterest Ads</span>
                      </button>
                    )}
                    {availablePlatforms.tiktok && (
                      <button
                        className={`sidebar-tool-menu-item ${(currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && currentDashboardType === 'tiktok-ads' ? 'active' : ''}`}
                        onClick={() => {
                          if (currentView !== 'dashboard') {
                            setCurrentViewWithHistory('dashboard');
                          }
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: 'tiktok-ads' } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                          setTimeout(() => {
                            window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: 'tiktok-ads' } }));
                          }, 100);
                        }}
                      >
                        <img src={tiktokAdsLogo} alt="TikTok Ads" className="sidebar-platform-logo" />
                        <span>TikTok Ads</span>
                      </button>
                    )}
                    {/**
                      * Budgets ferme la liste, et n'est pas une plateforme de plus.
                      *
                      * Les entrées au-dessus ouvrent chacune le dashboard d'une régie ; celle-ci
                      * ouvre une vue à part, qui les traverse toutes — ce qu'a dépensé chaque
                      * compte de son enveloppe, tous clients confondus. Elle n'émet donc aucun
                      * `dashboardTypeChange` : il n'y a pas d'onglet de dashboard à changer.
                      *
                      * Sa place ici plutôt qu'ailleurs tient à ce qu'on vient y chercher : on
                      * lit un budget en pensant aux régies, pas en pensant aux outils.
                      */}
                    {budgetsOuvert && (
                      <button
                        className={`sidebar-tool-menu-item ${currentView === 'budgets' ? 'active' : ''}`}
                        onClick={() => setCurrentViewWithHistory('budgets')}
                      >
                        <Wallet size={18} />
                        <span>Budgets</span>
                      </button>
                    )}
                  </div>
                </div>
              );
            })()}

            {/* Section Comptes - avec menu hover */}
            {(currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && (() => {
              // Filtrer les comptes par plateforme et par site/client sélectionné
              const platformType = TYPE_LIBRAIRIE_PAR_PLATEFORME[currentDashboardType];

              // Sur la page de choix de la plateforme, il n'y en a aucune : un sélecteur de
              // comptes n'y a pas de sens. Le repli implicite sur Meta y faisait lire
              // « Tous les comptes » au-dessus des seuls comptes Meta.
              if (!platformType) return null;

              // Obtenir les accountIds autorisés pour le site/client sélectionné
              let allowedAccountIds = null;
              if ((selectedSiteId && selectedSiteId !== 'all') || selectedClientId) {
                allowedAccountIds = new Set();
                userSites.filter(site => {
                  if (selectedSiteId && selectedSiteId !== 'all') {
                    return (site._id || site.id) === selectedSiteId;
                  }
                  if (selectedClientId) {
                    return site.clientId === selectedClientId;
                  }
                  return true;
                }).forEach(site => {
                  if (site.accountIds && Array.isArray(site.accountIds)) {
                    site.accountIds.forEach(id => allowedAccountIds.add(id));
                  }
                });
              }

              const filteredAccounts = allAdAccounts
                .filter(account => account.type === platformType)
                .filter(account => !allowedAccountIds || compteAutorise(account, allowedAccountIds))
                .sort((a, b) => a.name.localeCompare(b.name));

              // Ne pas afficher si 1 seul compte
              if (filteredAccounts.length <= 1) return null;

              // Nom du compte sélectionné
              const selectedAccountName = sidebarSelectedAccountId
                ? filteredAccounts.find(a => a.id === sidebarSelectedAccountId)?.name || 'Tous les comptes'
                : 'Tous les comptes';

              return (
                <div className={`sidebar-tool-category ${sidebarSelectedAccountId ? 'active' : ''}`}>
                  <div className="sidebar-section-title">
                    <Users size={16} />
                    <span>{selectedAccountName}</span>
                  </div>
                  <div className="sidebar-tool-menu">
                    <button
                      className={`sidebar-tool-menu-item ${!sidebarSelectedAccountId ? 'active' : ''}`}
                      onClick={() => {
                        setSidebarSelectedAccountId('');
                        setBreadcrumbAccountName('');
                        window.dispatchEvent(new CustomEvent('sidebarAccountChange', {
                          detail: { accountId: '', accountName: '' }
                        }));
                      }}
                    >
                      <Users size={18} />
                      <span>Tous les comptes</span>
                    </button>
                    {filteredAccounts.map(account => (
                      <button
                        key={account.id}
                        className={`sidebar-tool-menu-item ${sidebarSelectedAccountId === account.id ? 'active' : ''}`}
                        onClick={() => {
                          setSidebarSelectedAccountId(account.id);
                          setBreadcrumbAccountName(account.name || '');
                          window.dispatchEvent(new CustomEvent('sidebarAccountChange', {
                            detail: { accountId: account.id, accountName: account.name || '' }
                          }));
                        }}
                      >
                        <Users size={18} />
                        <span>{account.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              );
            })()}

            {/* Section Dashboard - avec menu hover (uniquement sur dashboard Google) */}
            {(currentView === 'dashboard' || (currentView === 'notes-archive' && previousViewBeforeNotes === 'dashboard')) && currentDashboardType === 'google' && (() => {
              const dashboardModelNames = {
                'global': 'Global',
                'comparatif': 'Comparatif',
                'keywords': 'Mots-clés',
                'search-terms': 'Termes de recherche',
                'landing-pages': 'Pages d\'atterrissage',
                'performance-max': 'Performance Max',
                'shopping': 'Produits',
                'products-insights': 'Insights Produits',
                'youtube-ads': 'YouTube Ads',
                'competition': 'Concurrence',
                'cross-sales': 'Ventes Croisées'
              };
              const selectedDashboardName = dashboardModelNames[breadcrumbDashboardModel] || 'Dashboard';

              return (
              <div className={`sidebar-tool-category active`}>
                <div className="sidebar-section-title">
                  <LayoutDashboard size={16} />
                  <span>{selectedDashboardName}</span>
                </div>
                <div className="sidebar-tool-menu">
                  <button
                    className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'global' ? 'active' : ''}`}
                    onClick={() => {
                      if (currentView !== 'dashboard') {
                        setCurrentViewWithHistory('dashboard');
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: currentDashboardType } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                          window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: currentDashboardType } }));
                        }, 100);
                      } else {
                        window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'global' } }));
                      }
                    }}
                  >
                    <LayoutDashboard size={18} />
                    <span>Global</span>
                  </button>
                  <button
                    className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'comparatif' ? 'active' : ''}`}
                    onClick={() => {
                      if (currentView !== 'dashboard') {
                        setCurrentViewWithHistory('dashboard');
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: currentDashboardType } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'comparatif' } }));
                          window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: currentDashboardType } }));
                        }, 100);
                      } else {
                        window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'comparatif' } }));
                      }
                    }}
                  >
                    <GitCompareArrows size={18} />
                    <span>Comparatif</span>
                  </button>
                  <button
                    className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'keywords' ? 'active' : ''}`}
                    onClick={() => {
                      if (currentView !== 'dashboard') {
                        setCurrentViewWithHistory('dashboard');
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: currentDashboardType } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'keywords' } }));
                          window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: currentDashboardType } }));
                        }, 100);
                      } else {
                        window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'keywords' } }));
                      }
                    }}
                  >
                    <Key size={18} />
                    <span>Mots-clés</span>
                  </button>
                  <button
                    className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'search-terms' ? 'active' : ''}`}
                    onClick={() => {
                      if (currentView !== 'dashboard') {
                        setCurrentViewWithHistory('dashboard');
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: currentDashboardType } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'search-terms' } }));
                          window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: currentDashboardType } }));
                        }, 100);
                      } else {
                        window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'search-terms' } }));
                      }
                    }}
                  >
                    <Search size={18} />
                    <span>Termes de recherche</span>
                  </button>
                  <button
                    className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'landing-pages' ? 'active' : ''}`}
                    onClick={() => {
                      if (currentView !== 'dashboard') {
                        setCurrentViewWithHistory('dashboard');
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: currentDashboardType } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'landing-pages' } }));
                          window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: currentDashboardType } }));
                        }, 100);
                      } else {
                        window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'landing-pages' } }));
                      }
                    }}
                  >
                    <FileText size={18} />
                    <span>Pages d'atterrissage</span>
                  </button>
                  <button
                    className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'performance-max' ? 'active' : ''}`}
                    onClick={() => {
                      if (currentView !== 'dashboard') {
                        setCurrentViewWithHistory('dashboard');
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: currentDashboardType } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'performance-max' } }));
                          window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: currentDashboardType } }));
                        }, 100);
                      } else {
                        window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'performance-max' } }));
                      }
                    }}
                  >
                    <Zap size={18} />
                    <span>Performance Max</span>
                  </button>
                  <button
                    className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'shopping' ? 'active' : ''}`}
                    onClick={() => {
                      if (currentView !== 'dashboard') {
                        setCurrentViewWithHistory('dashboard');
                        setTimeout(() => {
                          window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: currentDashboardType } }));
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'shopping' } }));
                          window.dispatchEvent(new CustomEvent('dashboardAutoGenerate', { detail: { platform: currentDashboardType } }));
                        }, 100);
                      } else {
                        window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'shopping' } }));
                      }
                    }}
                  >
                    <Package size={18} />
                    <span>Produits</span>
                  </button>
                  <button
                    className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'products-insights' ? 'active' : ''}`}
                    onClick={() => {
                      if (currentView !== 'dashboard') {
                        setCurrentViewWithHistory('dashboard');
                        setTimeout(() => window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'products-insights' } })), 100);
                      } else {
                        window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'products-insights' } }));
                      }
                    }}
                  >
                    <TrendingUp size={18} />
                    <span>Insights Produits</span>
                  </button>
                  <button
                    className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'youtube-ads' ? 'active' : ''}`}
                    onClick={() => {
                      if (currentView !== 'dashboard') {
                        setCurrentViewWithHistory('dashboard');
                        setTimeout(() => window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'youtube-ads' } })), 100);
                      } else {
                        window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'youtube-ads' } }));
                      }
                    }}
                  >
                    <Youtube size={18} />
                    <span>YouTube Ads</span>
                  </button>
                  {user?.role !== 'CLIENT' && (
                    <button
                      className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'competition' ? 'active' : ''}`}
                      onClick={() => {
                        if (currentView !== 'dashboard') {
                          setCurrentViewWithHistory('dashboard');
                          setTimeout(() => window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'competition' } })), 100);
                        } else {
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'competition' } }));
                        }
                      }}
                    >
                      <Target size={18} />
                      <span>Concurrence</span>
                    </button>
                  )}
                  {user?.role !== 'CLIENT' && config.IS_LOCAL && (
                    <button
                      className={`sidebar-tool-menu-item ${breadcrumbDashboardModel === 'cross-sales' ? 'active' : ''}`}
                      onClick={() => {
                        if (currentView !== 'dashboard') {
                          setCurrentViewWithHistory('dashboard');
                          setTimeout(() => window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'cross-sales' } })), 100);
                        } else {
                          window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'cross-sales' } }));
                        }
                      }}
                    >
                      <ShoppingCart size={18} />
                      <span>Ventes Croisées</span>
                    </button>
                  )}
                </div>
              </div>
              );
            })()}

            {/* Section Outils - uniquement sur tools - avec menus hover */}
            {/* Sidebar réduite pour un accès client : seuls les outils qui lui ont été
                ouverts, sans les catégories de l'agence. */}
            {currentView === 'tools' && user?.role === 'CLIENT' && (
              <div className="sidebar-tool-category">
                <div className="sidebar-section-title">
                  <Wrench size={16} />
                  <span>Outils</span>
                </div>
                <div className="sidebar-tool-menu">
                  {CLIENT_TOOLS_CATALOGUE.filter(tool => clientTools.includes(tool.id)).map(tool => (
                    <button
                      key={tool.id}
                      className={`sidebar-tool-menu-item ${activeToolId === tool.id ? 'active' : ''}`}
                      onClick={() => window.dispatchEvent(new CustomEvent('openTool', { detail: { toolId: tool.id } }))}
                    >
                      <Layers size={18} />
                      <span>{tool.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {currentView === 'tools' && user?.role !== 'CLIENT' && (
              <>
                {/* Les catégories et leur contenu viennent de `constants/toolCategories` :
                    six blocs de JSX quasi identiques vivaient ici, chacun recopiant la liste
                    de ses identifiants deux fois — une pour surligner le titre, une pour
                    surligner l'entrée. Déplacer un outil demandait trois modifications
                    concordantes, que rien ne vérifiait. */}
                {CATEGORIES_OUTILS.map(({ titre, Icon: IconeCategorie, outils }) => {
                  const entrees = outils.filter(o => o.sidebar !== false);
                  // Un outil masqué reste rattaché à l'entrée qui l'ouvre : les deux variantes
                  // par régie des termes à exclure surlignent leur hub, faute de quoi le menu
                  // se viderait de tout repère une fois l'une d'elles ouverte.
                  const idsDeLEntree = (entree) => [
                    entree.id,
                    ...outils.filter(o => o.sidebar === false).map(o => o.id)
                  ];

                  return (
                    <div
                      key={titre}
                      className={`sidebar-tool-category ${outils.some(o => o.id === activeToolId) ? 'active' : ''}`}
                    >
                      <div
                        className="sidebar-section-title"
                        onClick={() => window.dispatchEvent(new CustomEvent('openToolCategory', { detail: { category: titre } }))}
                        style={{ cursor: 'pointer' }}
                        title="Voir les outils de cette catégorie"
                      >
                        <IconeCategorie size={16} />
                        <span>{titre}</span>
                      </div>
                      <div className="sidebar-tool-menu">
                        {grouperParSousCategorie(entrees).map(groupe => {
                          const boutons = groupe.outils.map(entree => {
                            const IconeOutil = entree.Icon;
                            const actif = entree.id === 'negative-keywords-hub'
                              ? idsDeLEntree(entree).includes(activeToolId)
                              : entree.id === activeToolId;

                            return (
                              <button
                                key={entree.id}
                                className={`sidebar-tool-menu-item ${actif ? 'active' : ''}`}
                                onClick={() => window.dispatchEvent(new CustomEvent('openTool', { detail: { toolId: entree.id } }))}
                              >
                                <IconeOutil size={18} />
                                <span>{entree.libelle}</span>
                              </button>
                            );
                          });

                          // Un groupe sans titre n'en porte pas : les outils s'affichent au même
                          // rang que si la sous-catégorie n'existait pas.
                          if (!groupe.titre) return boutons;

                          return (
                            <div key={groupe.titre} className="sidebar-tool-subgroup">
                              <div
                                className="sidebar-tool-subgroup-title"
                                onClick={() => window.dispatchEvent(new CustomEvent('openToolCategory', { detail: { category: groupe.titre } }))}
                                title="Voir les outils de cette sous-catégorie"
                              >
                                {groupe.titre}
                              </div>
                              {boutons}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </>
            )}

            {/* Section Historique Diagnostic - uniquement quand dans diagnostic avec compte sélectionné */}
            {(currentView === 'diagnostic' || (currentView === 'tools' && window.location.hash.includes('#tools/diagnostic'))) && diagnosticSelectedAccountId && (
                  <div className="sidebar-section">
                    <div className="sidebar-section-title">
                      <Clock size={16} />
                      Derniers diagnostics
                    </div>
                    <div className="sidebar-section-content diagnostic-history-list">
                      {diagnosticHistory.length === 0 ? (
                        <div className="diagnostic-history-empty">
                          Aucun historique disponible
                        </div>
                      ) : (
                        diagnosticHistory.map((scan, index) => {
                          const date = new Date(scan.scannedAt);
                          const formattedDate = date.toLocaleDateString('fr-FR', {
                            day: '2-digit',
                            month: '2-digit'
                          });
                          const formattedTime = date.toLocaleTimeString('fr-FR', {
                            hour: '2-digit',
                            minute: '2-digit'
                          });

                          let scoreColor = '#22c55e';
                          if (scan.scoreGlobal < 50) scoreColor = '#ef4444';
                          else if (scan.scoreGlobal < 80) scoreColor = '#f59e0b';

                          const isSelected = diagnosticSelectedScanId === scan.id;
                          const isLatest = index === 0;

                          // Comparer avec le scan précédent (index + 1 car trié du plus récent au plus ancien)
                          const previousScan = diagnosticHistory[index + 1];
                          const scoreDiff = previousScan ? scan.scoreGlobal - previousScan.scoreGlobal : 0;

                          return (
                            <div
                              key={scan.id || index}
                              className={`diagnostic-history-item ${isSelected ? 'active' : ''} ${isLatest && !diagnosticSelectedScanId ? 'active' : ''}`}
                              title={`${scan.triggeredBy === 'cron' ? 'Automatique' : 'Manuel'}${isLatest ? ' (dernier)' : ''}${scoreDiff !== 0 ? ` (${scoreDiff > 0 ? '+' : ''}${scoreDiff})` : ''}`}
                              onClick={() => setDiagnosticSelectedScanId(isLatest ? null : scan.id)}
                              style={{ cursor: 'pointer' }}
                            >
                              <div className="diagnostic-history-date">
                                <span>{formattedDate}</span>
                                <span className="diagnostic-history-time">{formattedTime}</span>
                              </div>
                              <div className="diagnostic-history-trend" style={{ width: '20px', display: 'flex', justifyContent: 'center' }}>
                                {scoreDiff > 0 && <TrendingUp size={16} color="#22c55e" />}
                                {scoreDiff < 0 && <TrendingDown size={16} color="#ef4444" />}
                              </div>
                              <div
                                className="diagnostic-history-score"
                                style={{ color: scoreColor, fontSize: '18px', fontWeight: '600' }}
                              >
                                {scan.scoreGlobal}
                              </div>
                            </div>
                          );
                        })
                      )}
                    </div>
                  </div>
            )}

            {/* Section Plateformes MCP */}
            {currentView === 'mcp' && (
              <div className="sidebar-section">
                <div className="sidebar-section-title">
                  <Database size={16} />
                  Plateformes
                </div>
                <div className="sidebar-section-content">
                  <button className="sidebar-platform-item active">
                    <img src={claudeLogo} alt="Claude" className="sidebar-platform-logo" style={{ width: '20px', height: '20px' }} />
                    <span>Claude</span>
                  </button>
                </div>
              </div>
            )}

            {/* Section Navigation contextuelle - Paramètres, Gestionnaire, Organisation, Notifications, Statistiques, Abonnement, Admin + pages config */}
            {VUES_DE_NAVIGATION.includes(currentView) && (
              <div className="sidebar-section">
                <div className="sidebar-section-title">
                  <Settings size={16} />
                  Navigation
                </div>
                <div className="sidebar-section-content">
                  {(userOrganizations.length === 0 || userOrganizations.some(org => org.role === 'OWNER')) && (
                    <button
                      className={`sidebar-platform-item ${entreeDeNavigationActive === 'pricing' ? 'active' : ''}`}
                      onClick={() => setCurrentViewWithHistory('pricing')}
                    >
                      <CreditCard size={18} />
                      <span>Abonnement</span>
                    </button>
                  )}
                  {globalConnectors.length > 0 && (
                    <button
                      className={`sidebar-platform-item ${entreeDeNavigationActive === 'organization' ? 'active' : ''}`}
                      onClick={() => setCurrentViewWithHistory('organization')}
                    >
                      <Building2 size={18} />
                      <span>Organisation</span>
                    </button>
                  )}
                  <button
                    className={`sidebar-platform-item ${entreeDeNavigationActive === 'manager' ? 'active' : ''}`}
                    onClick={() => setCurrentViewWithHistory('manager')}
                  >
                    <Folder size={18} />
                    <span>Gestionnaire</span>
                  </button>
                  {globalConnectors.length > 0 && user && userOrganizations.length > 0 && (
                    <button
                      className={`sidebar-platform-item ${entreeDeNavigationActive === 'statistics' ? 'active' : ''}`}
                      onClick={() => setCurrentViewWithHistory('statistics')}
                    >
                      <BarChart3 size={18} />
                      <span>Statistiques</span>
                    </button>
                  )}
                  {globalConnectors.length > 0 && user && user.role !== 'CLIENT' && (
                    <button
                      className={`sidebar-platform-item ${entreeDeNavigationActive === 'notifications' ? 'active' : ''}`}
                      onClick={() => setCurrentViewWithHistory('notifications')}
                    >
                      <Bell size={18} />
                      <span>Notifications</span>
                    </button>
                  )}
                </div>
              </div>
            )}

            {/* Section Notes — partout sauf là où la barre latérale sert autre chose.
                Voir `VUES_SANS_NOTES` : la liste des vues de réglage en dérive, de sorte qu'une
                vue ajoutée à « Navigation » ne fasse pas reparaître Notes à côté d'elle. */}
            {!VUES_SANS_NOTES.includes(currentView) && user && user.role !== 'CLIENT' && userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan) && (
              <div className={`sidebar-tool-category ${['notes-archive', 'notes-calendrier', 'notes-import'].includes(currentView) ? 'active' : ''}`}>
                <div
                  className="sidebar-section-title"
                  style={{ cursor: 'pointer' }}
                  onClick={() => { setPreviousViewBeforeNotes(currentView); setCurrentViewWithHistory('notes-archive'); }}
                >
                  <StickyNote size={16} />
                  <span>Notes</span>
                </div>
                <div className="sidebar-tool-menu">
                  <button
                    className="sidebar-tool-menu-item"
                    onClick={() => {
                      const ctx = window.currentNotesContext || {};
                      setNotesContext({
                        clientId: ctx.clientId || selectedClientId || null,
                        siteId: ctx.siteId || (selectedSiteId !== 'all' ? selectedSiteId : null),
                        platform: ctx.platform || null,
                        accountId: ctx.accountId || null,
                        accountName: ctx.accountName || null
                      });
                      setIsNotesModalOpen(true);
                    }}
                  >
                    <Plus size={18} />
                    <span>Ajouter une note</span>
                  </button>
                  {/* Import de masse : quand les notes existent déjà ailleurs — un tableau
                      d'opérations, un journal d'incidents tenu dans Excel — les ressaisir une
                      à une n'a pas de sens. */}
                  <button
                    className={`sidebar-tool-menu-item ${currentView === 'notes-import' ? 'active' : ''}`}
                    onClick={() => {
                      setPreviousViewBeforeNotes(currentView);
                      setCurrentViewWithHistory('notes-import');
                    }}
                  >
                    <Upload size={18} />
                    <span>Importer des notes</span>
                  </button>
                  {/* Saisie en série : le calendrier montre ce qui est déjà documenté sur le
                      mois et permet d'en ajouter plusieurs sans rouvrir de modale. Il reçoit
                      le même contexte, mais l'expose en sélecteurs modifiables —
                      `currentNotesContext` n'est jamais effacé et reste collé au dernier
                      compte visité. */}
                  <button
                    className={`sidebar-tool-menu-item ${currentView === 'notes-calendrier' ? 'active' : ''}`}
                    onClick={() => {
                      setPreviousViewBeforeNotes(currentView);
                      setCurrentViewWithHistory('notes-calendrier');
                    }}
                  >
                    <Calendar size={18} />
                    <span>Ajouter au calendrier</span>
                  </button>
                </div>
              </div>
            )}

          </aside>
          )}

          {/* Contenu principal */}
          <div className="main-content">
            {/* Fil d'Ariane - pour tous les dashboards (y compris la page de sélection) */}
            {currentView === 'dashboard' && (
              <div className="app-breadcrumb">
                {selectedClientId && (
                  <>
                    <span className="breadcrumb-nav-item">
                      <Users size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {userClients.find(c => c.id === selectedClientId)?.name || 'Client'}
                    </span>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                  </>
                )}
                <span
                  className={`breadcrumb-nav-item ${currentDashboardType === 'selection' ? 'active' : ''}`}
                  onClick={() => {
                    setCurrentDashboardType('selection');
                    setCurrentViewWithHistory('dashboard');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <BarChart3 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Dashboard
                </span>
                {currentDashboardType !== 'selection' && (
                  <>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                    <span className={`breadcrumb-nav-item ${!breadcrumbAccountName && !breadcrumbDashboardModel ? 'active' : ''}`}>
                      <img
                        src={currentDashboardType === 'google' ? googleAdsLogo : currentDashboardType === 'microsoft' ? microsoftAdsLogo : currentDashboardType === 'pinterest-ads' ? pinterestAdsLogo : currentDashboardType === 'tiktok-ads' ? tiktokAdsLogo : currentDashboardType === 'chatgpt-ads' ? chatgptAdsLogo : currentDashboardType === 'awin' ? awinLogo : metaAdsLogo}
                        alt=""
                        className="breadcrumb-logo"
                        style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle' }}
                      />
                      {currentDashboardType === 'google' ? 'Google Ads' : currentDashboardType === 'microsoft' ? 'Microsoft Ads' : currentDashboardType === 'pinterest-ads' ? 'Pinterest Ads' : currentDashboardType === 'tiktok-ads' ? 'TikTok Ads' : currentDashboardType === 'chatgpt-ads' ? 'ChatGPT Ads' : currentDashboardType === 'awin' ? 'Awin' : 'Meta Ads'}
                    </span>
                    {breadcrumbAccountName && (
                      <>
                        <ChevronRight size={14} className="breadcrumb-separator" />
                        <span className={`breadcrumb-nav-item ${!breadcrumbDashboardModel ? 'active' : ''}`}>
                          {breadcrumbAccountName}
                        </span>
                      </>
                    )}
                    {breadcrumbDashboardModel && (
                      <>
                        <ChevronRight size={14} className="breadcrumb-separator" />
                        <span className="breadcrumb-nav-item active" style={{ display: 'inline-flex', alignItems: 'center', gap: '6px' }}>
                          {breadcrumbDashboardModel === 'global' && <><LayoutDashboard size={14} /> Global</>}
                          {breadcrumbDashboardModel === 'comparatif' && <><GitCompareArrows size={14} /> Comparatif</>}
                          {breadcrumbDashboardModel === 'keywords' && <><Key size={14} /> Mots-clés</>}
                          {breadcrumbDashboardModel === 'search-terms' && <><Search size={14} /> Termes de recherche</>}
                          {breadcrumbDashboardModel === 'landing-pages' && <><FileText size={14} /> Pages d'atterrissage</>}
                          {breadcrumbDashboardModel === 'performance-max' && <><Zap size={14} /> Performance Max</>}
                          {breadcrumbDashboardModel === 'shopping' && <><Package size={14} /> Produits</>}
                          {breadcrumbDashboardModel === 'products-insights' && <><TrendingUp size={14} /> Insights Produits</>}
                          {breadcrumbDashboardModel === 'youtube-ads' && <><Youtube size={14} /> YouTube Ads</>}
                          {breadcrumbDashboardModel === 'competition' && <><Target size={14} /> Concurrence</>}
                          {breadcrumbDashboardModel === 'cross-sales' && <><ShoppingCart size={14} /> Ventes Croisées</>}
                        </span>
                      </>
                    )}
                  </>
                )}

                {/*
                  Générer un dashboard sur mesure — poussé tout à droite du fil.

                  ⚠️ **Il est ici parce qu'il n'était atteignable nulle part sur une régie.**
                  L'écran de choix ne s'affiche que s'il y a un choix ; pour une régie, le
                  consolidé étant écarté, il n'en restait qu'un — on allait donc droit au
                  dashboard, et la carte « Générer » n'apparaissait qu'une fois un premier
                  dashboard créé, c'est-à-dire quand on n'en avait plus besoin.

                  ⚠️ **Aucun compte n'est transmis, seulement le type** — et aucun type sur la
                  page de sélection, où la vue proposera alors tous les comptes éligibles. Lire
                  ici le compte ouvert supposerait d'aller le chercher dans `localStorage`, où la
                  navigation l'a laissé : une source de vérité de plus, et fausse dès qu'on change
                  de compte sans repasser par l'accueil.

                  ⚠️ Violet tenu, jamais un aplat plein : un fil d'ariane annonce où l'on est, il
                  ne doit pas porter l'élément le plus voyant de la page.
                */}
                {/* `editeurKokpit` comme les deux autres portes : un administrateur, dans
                    Kokpit, est celui d'une organisation **cliente**, et ces appels partent sous
                    notre clé. */}
                {user?.editeurKokpit && (
                  <button
                    type="button"
                    onClick={() => {
                      /*
                       * ⚠️ On transmet **d'où l'on vient** et **le chemin parcouru**, pas seulement
                       * le type. Sans eux, « Retour » ramenait à l'accueil — alors qu'on venait du
                       * dashboard Microsoft Ads — et le fil d'ariane de l'écran suivant repartait
                       * de zéro, comme si l'on avait atterri là par hasard.
                       */
                      const nomDuClient = selectedClientId
                        ? (userClients.find(c => c.id === selectedClientId)?.name || null)
                        : null;
                      const typeVise = TYPE_DE_COMPTE_PAR_ONGLET[currentDashboardType];
                      setGenerationSurMesure({
                        type: typeVise,
                        origine: 'dashboard',
                        // L'onglet à rétablir : revenir sur « Dashboard » sans lui rouvrirait
                        // l'écran de sélection des plateformes, pas celui qu'on regardait.
                        retourDashboardType: currentDashboardType,
                        chemin: [
                          ...(nomDuClient ? [nomDuClient] : []),
                          'Dashboard',
                          ...(typeVise ? [libellePlateforme(typeVise)] : [])
                        ]
                      });
                      setCurrentViewWithHistory('generation-dashboard');
                    }}
                    title="Décrire un tableau de bord, le faire écrire, et l'héberger dans Kokpit"
                    style={{
                      /*
                       * ⚠️ `marginRight` compense le `margin-right: -30px` du fil.
                       *
                       * `.app-breadcrumb` déborde volontairement de son conteneur — `margin: -30px`
                       * et `width: calc(100% + 60px)` — pour que son filet aille d'un bord à
                       * l'autre. Un `margin-left: auto` posait donc le bouton sur **ce** bord-là,
                       * soit seize pixels hors de la fenêtre, où `overflow-x: hidden` le coupait.
                       */
                      marginLeft: 'auto',
                      marginRight: 30,
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: '4px 8px', fontSize: 14, fontWeight: 500,
                      /*
                       * La langue du fil, et rien d'autre : `.breadcrumb-nav-item` est gris,
                       * sans fond ni bordure. Un bouton coloré y serait l'élément le plus voyant
                       * d'une barre dont le rôle est de dire discrètement où l'on est.
                       */
                      color: '#6b7280', background: 'transparent',
                      border: 'none', borderRadius: 6, cursor: 'pointer'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = '#1e293b';
                      e.currentTarget.style.backgroundColor = '#f3efe7';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = '#6b7280';
                      e.currentTarget.style.backgroundColor = 'transparent';
                    }}
                  >
                    <Sparkles size={14} /> Générer un dashboard
                  </button>
                )}
              </div>
            )}

            {/* Fil d'Ariane pour les pages de configuration */}
            {['connectors', 'library', 'sites', 'clients', 'manager'].includes(currentView) && (
              <div className="app-breadcrumb">
                <span
                  className={`breadcrumb-nav-item ${currentView === 'manager' ? 'active' : ''}`}
                  onClick={() => setCurrentViewWithHistory('manager')}
                >
                  <Folder size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Gestionnaire
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${currentView === 'connectors' ? 'active' : ''}`}
                  onClick={() => setCurrentViewWithHistory('connectors')}
                >
                  <Plug size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Connecteurs
                </span>
                {globalConnectors.length > 0 && (
                  <>
                    <span className="breadcrumb-separator">|</span>
                    <span
                      className={`breadcrumb-nav-item ${currentView === 'library' ? 'active' : ''}`}
                      onClick={() => setCurrentViewWithHistory('library')}
                    >
                      <Package size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Comptes
                    </span>
                    <span className="breadcrumb-separator">|</span>
                    <span
                      className={`breadcrumb-nav-item ${currentView === 'sites' ? 'active' : ''}`}
                      onClick={() => setCurrentViewWithHistory('sites')}
                    >
                      <Globe size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Sites
                    </span>
                    <span className="breadcrumb-separator">|</span>
                    <span
                      className={`breadcrumb-nav-item ${currentView === 'clients' ? 'active' : ''}`}
                      onClick={() => setCurrentViewWithHistory('clients')}
                    >
                      <Users size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Clients
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Fil d'Ariane pour les pages Organisation, Notifications, Statistiques, Notes, MCP */}
            {['organization', 'acces-client', 'notifications', 'statistics', 'notes-archive', 'notes-calendrier', 'notes-import', 'mcp'].includes(currentView) && (
              <div className="app-breadcrumb">
                {/* Les trois écrans de notes forment une vraie hiérarchie : l'archive est leur
                    racine. Le fil l'écrit donc en deux éléments, dont le premier ramène à
                    l'archive — un « Notes › Calendrier » d'un seul tenant, et inerte, nommait
                    un chemin qu'on ne pouvait pas remonter. */}
                {['notes-archive', 'notes-calendrier', 'notes-import'].includes(currentView) ? (
                  <>
                    <span
                      className={`breadcrumb-nav-item ${currentView === 'notes-archive' ? 'active' : ''}`}
                      onClick={() => { if (currentView !== 'notes-archive') ouvrirVueNotes('notes-archive'); }}
                      style={{ cursor: currentView === 'notes-archive' ? 'default' : 'pointer' }}
                    >
                      <StickyNote size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Notes
                    </span>
                    {currentView !== 'notes-archive' && (
                      <>
                        <ChevronRight size={14} className="breadcrumb-separator" />
                        <span className="breadcrumb-nav-item active">
                          {currentView === 'notes-calendrier'
                            ? <><Calendar size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Calendrier</>
                            : <><Upload size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Import</>}
                        </span>
                      </>
                    )}
                  </>
                ) : currentView === 'acces-client' ? (
                  /* La fiche d'un accès client est un enfant d'Organisation, et le fil l'écrit
                     en trois éléments dont les deux premiers remontent. Même raison que les
                     notes : un « Organisation › Accès clients › untel » d'un seul tenant, et
                     inerte, nommerait un chemin qu'on ne peut pas remonter. */
                  <>
                    <span
                      className="breadcrumb-nav-item"
                      onClick={() => setCurrentViewWithHistory('organization')}
                    >
                      <Building2 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Organisation
                    </span>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                    <span className="breadcrumb-nav-item" onClick={revenirAuxAccesClients}>
                      <UserX size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      Accès clients
                    </span>
                    {ficheAccesOuverte?.email && (
                      <>
                        <ChevronRight size={14} className="breadcrumb-separator" />
                        <span className="breadcrumb-nav-item active">{ficheAccesOuverte.email}</span>
                      </>
                    )}
                  </>
                ) : (
                  <span className="breadcrumb-nav-item active">
                    {currentView === 'organization' && <><Building2 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Organisation</>}
                    {currentView === 'notifications' && <><Bell size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Notifications</>}
                    {currentView === 'statistics' && <><BarChart3 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />Statistiques</>}
                    {currentView === 'mcp' && <><Cpu size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />MCP Kokpit</>}
                  </span>
                )}
              </div>
            )}

            {/* Fil d'Ariane pour la page Abonnement */}
            {currentView === 'pricing' && (
              <div className="app-breadcrumb">
                <span
                  className={`breadcrumb-nav-item ${subscriptionTab === 'subscription' ? 'active' : ''}`}
                  onClick={() => setSubscriptionTab('subscription')}
                  style={{ cursor: 'pointer' }}
                >
                  <CreditCard size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Abonnement
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${subscriptionTab === 'invoices' ? 'active' : ''}`}
                  onClick={() => setSubscriptionTab('invoices')}
                  style={{ cursor: 'pointer' }}
                >
                  Factures
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${subscriptionTab === 'payment' ? 'active' : ''}`}
                  onClick={() => setSubscriptionTab('payment')}
                  style={{ cursor: 'pointer' }}
                >
                  Moyens de paiement
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${subscriptionTab === 'billing' ? 'active' : ''}`}
                  onClick={() => setSubscriptionTab('billing')}
                  style={{ cursor: 'pointer' }}
                >
                  Facturation
                </span>
              </div>
            )}

            {/* Fil d'Ariane pour Admin */}
            {currentView === 'admin' && (
              <div className="app-breadcrumb">
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'dashboard' ? 'active' : ''}`}
                  onClick={() => setAdminTab('dashboard')}
                  style={{ cursor: 'pointer' }}
                >
                  <BarChart3 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Dashboard
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'customers' ? 'active' : ''}`}
                  onClick={() => setAdminTab('customers')}
                  style={{ cursor: 'pointer' }}
                >
                  Clients
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'beta' ? 'active' : ''}`}
                  onClick={() => setAdminTab('beta')}
                  style={{ cursor: 'pointer' }}
                >
                  Beta
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'cron' ? 'active' : ''}`}
                  onClick={() => setAdminTab('cron')}
                  style={{ cursor: 'pointer' }}
                >
                  CRON
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'action-logs' ? 'active' : ''}`}
                  onClick={() => setAdminTab('action-logs')}
                  style={{ cursor: 'pointer' }}
                >
                  Journal
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'bigquery' ? 'active' : ''}`}
                  onClick={() => setAdminTab('bigquery')}
                  style={{ cursor: 'pointer' }}
                >
                  BigQuery
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'quotas' ? 'active' : ''}`}
                  onClick={() => setAdminTab('quotas')}
                  style={{ cursor: 'pointer' }}
                >
                  Quotas
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'mcp' ? 'active' : ''}`}
                  onClick={() => setAdminTab('mcp')}
                  style={{ cursor: 'pointer' }}
                >
                  MCP
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'tickets' ? 'active' : ''}`}
                  onClick={() => setAdminTab('tickets')}
                  style={{ cursor: 'pointer' }}
                >
                  <MessageSquare size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Tickets
                </span>
                <span className="breadcrumb-separator">|</span>
                <span
                  className={`breadcrumb-nav-item ${adminTab === 'errors' ? 'active' : ''}`}
                  onClick={() => setAdminTab('errors')}
                  style={{ cursor: 'pointer' }}
                >
                  <AlertCircle size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Erreurs
                </span>
              </div>
            )}

            {/* Fil d'Ariane pour Matomo Dashboard */}
            {currentView === 'matomo-dashboard' && (
              <div className="app-breadcrumb">
                {selectedClientId && (
                  <>
                    <span className="breadcrumb-nav-item">
                      <Users size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {userClients.find(c => c.id === selectedClientId)?.name || 'Client'}
                    </span>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                  </>
                )}
                <span
                  className="breadcrumb-nav-item"
                  onClick={() => {
                    setCurrentDashboardType('selection');
                    setCurrentViewWithHistory('dashboard');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <BarChart3 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Dashboard
                </span>
                <ChevronRight size={14} className="breadcrumb-separator" />
                <span className={`breadcrumb-nav-item ${!matomoSelectedSiteId ? 'active' : ''}`}>
                  <img
                    src={matomoLogo}
                    alt=""
                    className="breadcrumb-logo"
                    style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle' }}
                  />
                  Matomo
                </span>
                {matomoSelectedSiteId && (
                  <>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                    <span className="breadcrumb-nav-item active">
                      {filteredMatomoSites.find(s => s.id === matomoSelectedSiteId)?.name || 'Site Matomo'}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Fil d'Ariane pour GA4 Dashboard */}
            {currentView === 'ga4-dashboard' && (
              <div className="app-breadcrumb">
                {selectedClientId && (
                  <>
                    <span className="breadcrumb-nav-item">
                      <Users size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {userClients.find(c => c.id === selectedClientId)?.name || 'Client'}
                    </span>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                  </>
                )}
                <span
                  className="breadcrumb-nav-item"
                  onClick={() => {
                    setCurrentDashboardType('selection');
                    setCurrentViewWithHistory('dashboard');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <BarChart3 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Dashboard
                </span>
                <ChevronRight size={14} className="breadcrumb-separator" />
                <span className={`breadcrumb-nav-item ${!ga4SelectedPropertyId ? 'active' : ''}`}>
                  <img
                    src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png"
                    alt=""
                    className="breadcrumb-logo"
                    style={{ width: 14, height: 14, marginRight: 4, verticalAlign: 'middle' }}
                  />
                  Google Analytics
                </span>
                {ga4SelectedPropertyId && (
                  <>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                    <span className="breadcrumb-nav-item active">
                      {filteredGA4Properties.find(p => p.id === ga4SelectedPropertyId)?.name || 'Propriété Google Analytics'}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Fil d'Ariane pour Piano Dashboard */}
            {currentView === 'piano-dashboard' && (
              <div className="app-breadcrumb">
                {selectedClientId && (
                  <>
                    <span className="breadcrumb-nav-item">
                      <Users size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {userClients.find(c => c.id === selectedClientId)?.name || 'Client'}
                    </span>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                  </>
                )}
                <span
                  className="breadcrumb-nav-item"
                  onClick={() => {
                    setCurrentDashboardType('selection');
                    setCurrentViewWithHistory('dashboard');
                  }}
                  style={{ cursor: 'pointer' }}
                >
                  <BarChart3 size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Dashboard
                </span>
                <ChevronRight size={14} className="breadcrumb-separator" />
                <span className={`breadcrumb-nav-item ${!pianoSelectedSiteId ? 'active' : ''}`}>
                  <img src={pianoLogo} alt="Piano" style={{ width: 14, height: 14, marginRight: 4, borderRadius: '3px', verticalAlign: 'middle' }} />
                  Piano
                </span>
                {pianoSelectedSiteId && (
                  <>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                    <span className="breadcrumb-nav-item active">
                      {filteredPianoSites.find(s => s.id === pianoSelectedSiteId)?.name || 'Site Piano'}
                    </span>
                  </>
                )}
              </div>
            )}

            {/* Fil d'Ariane pour Analyser */}
            {currentView === 'analyze' && (
              <div className="app-breadcrumb">
                {selectedClientId && (
                  <>
                    <span className="breadcrumb-nav-item">
                      <Users size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {userClients.find(c => c.id === selectedClientId)?.name || 'Client'}
                    </span>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                  </>
                )}
                <span className="breadcrumb-nav-item active">
                  <Bot size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Analyser
                </span>
                <ChevronRight size={14} className="breadcrumb-separator" />
                <span
                  className="breadcrumb-nav-item"
                  onClick={() => setCurrentViewWithHistory('history')}
                  style={{ cursor: 'pointer' }}
                >
                  <History size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Historique
                </span>
              </div>
            )}

            {/* Fil d'Ariane pour Historique des analyses */}
            {currentView === 'history' && (
              <div className="app-breadcrumb">
                {selectedClientId && (
                  <>
                    <span className="breadcrumb-nav-item">
                      <Users size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                      {userClients.find(c => c.id === selectedClientId)?.name || 'Client'}
                    </span>
                    <ChevronRight size={14} className="breadcrumb-separator" />
                  </>
                )}
                <span
                  className="breadcrumb-nav-item"
                  onClick={() => setCurrentViewWithHistory('analyze')}
                  style={{ cursor: 'pointer' }}
                >
                  <Bot size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Analyser
                </span>
                <ChevronRight size={14} className="breadcrumb-separator" />
                <span className="breadcrumb-nav-item active">
                  <History size={14} style={{ marginRight: 4, verticalAlign: 'middle' }} />
                  Historique
                </span>
              </div>
            )}

          {isInitialLogin && token && connectorsLoaded && !(user && user.role === 'CLIENT') ? null : (
            <>
          {currentView === 'dashboard' && <DashboardView plateformeOuverte={plateformeOuverte} token={token} setError={setError} setCurrentView={setCurrentViewWithHistory} globalConnectors={globalConnectors} hasAnalyzerAccess={analyseAutorisee} selectedClientId={selectedClientId} user={user} authError={authError} setAuthError={setAuthError} handleApiError={handleApiError} handleReconnect={handleReconnect} currentDashboardType={currentDashboardType} dashboardNotes={dashboardNotes} fetchDashboardNotes={fetchDashboardNotes} clientTools={clientTools} ouvrirChoixDashboard={ouvrirChoixDashboard} selectedPeriod={selectedPeriod} setSelectedPeriod={setSelectedPeriod} customStartDate={customStartDate} setCustomStartDate={setCustomStartDate} customEndDate={customEndDate} setCustomEndDate={setCustomEndDate} selectedSiteId={selectedSiteId} userSites={userSites} availableSources={availableSources} filteredGA4Properties={filteredGA4Properties} filteredMatomoSites={filteredMatomoSites} filteredPianoSites={filteredPianoSites} cmsDisponibles={cmsDisponibles} pianoLogo={pianoLogo} compareEnabled={compareEnabled} comparisonType={comparisonType} fbCompareEnabled={fbCompareEnabled} fbComparisonType={fbComparisonType} gadsComparisonType={gadsComparisonType} setGadsComparisonType={setGadsComparisonType} gadsCompareEnabled={gadsCompareEnabled} msadsComparisonType={msadsComparisonType} msadsCompareEnabled={msadsCompareEnabled} inaccessibleAccounts={inaccessibleAccounts} globalCompareEnabled={globalCompareEnabled} globalComparisonType={globalComparisonType} comparisonStartDate={comparisonStartDate} comparisonEndDate={comparisonEndDate} />}
          
          {currentView === 'analyze' && globalConnectors.length > 0 && !isInitialLogin && (
            (user && (user.role === 'ADMIN' || (userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan)))) ? (
              <AnalyzeView
                onAnalyze={handleAnalyze}
                loading={loading}
                error={error}
                token={token}
                setCurrentView={setCurrentViewWithHistory}
                globalConnectors={globalConnectors}
                selectedClientId={selectedClientId}
                analyzeFilterSite={analyzeFilterSite}
                analyzeFilterAnalytics={analyzeFilterAnalytics}
                analyzeFilterPlatforms={analyzeFilterPlatforms}
                analyzeFilterAccount={analyzeFilterAccount}
                analysisResult={analysisResult}
                setAnalysisResult={setAnalysisResult}
                openConversationData={openConversationData}
                setOpenConversationData={setOpenConversationData}
              />
            ) : (
              <UpgradePromptView 
                userPlan={userSubscription?.plan || 'SOLO'}
                feature="Analyser"
                description="Analysez vos données avec l'IA et obtenez des insights personnalisés"
              />
            )
          )}
          {currentView === 'library' && globalConnectors.length > 0 && (
            <LibraryView
              token={token}
              setSuccess={setSuccess}
              setError={setError}
              inaccessibleAccounts={inaccessibleAccounts}
              /* L'écran Comptes suit le client choisi en en-tête, comme le Gestionnaire. */
              clientFiltre={selectedClientId}
              openMerchantCenterOnLoad={openMerchantCenterOnLibraryLoad}
              setOpenMerchantCenterOnLoad={setOpenMerchantCenterOnLibraryLoad}
              openSearchConsoleOnLoad={openSearchConsoleOnLibraryLoad}
              setOpenSearchConsoleOnLoad={setOpenSearchConsoleOnLibraryLoad}
              openGoogleAnalyticsOnLoad={openGoogleAnalyticsOnLibraryLoad}
              setOpenGoogleAnalyticsOnLoad={setOpenGoogleAnalyticsOnLibraryLoad}
              openAddModalOnLoad={openAddModalOnLibraryLoad}
              setOpenAddModalOnLoad={setOpenAddModalOnLibraryLoad}
              suggestedMerchantIds={suggestedMerchantIds}
              setSuggestedMerchantIds={setSuggestedMerchantIds}
              connectors={globalConnectors}
              setCurrentView={setCurrentViewWithHistory}
              returnToShoppingAfterMerchantAdd={returnToShoppingAfterMerchantAdd}
              setReturnToShoppingAfterMerchantAdd={setReturnToShoppingAfterMerchantAdd}
              onDataChange={() => {
                fetchAllAdAccounts();
                fetchAvailableSources();
              }}
              userSubscription={userSubscription}
              /*
               * ⚠️ `null` pour qui n'est pas l'éditeur, et le bouton disparaît alors de la
               * modale. La règle vit **ici seulement** : la poser aussi dans `ListeComptes`
               * ferait un second endroit à changer, et c'est celui qu'on oublie.
               *
               * ⚠️ **`editeurKokpit`, et non plus `role === 'ADMIN'`** : un administrateur, dans
               * Kokpit, c'est l'administrateur d'une organisation **cliente**, et la génération
               * appelle l'API Claude sous notre clé. Le drapeau vient de `/api/auth/verify` —
               * recopier l'adresse de l'éditeur ici en ferait un second endroit à corriger.
               */
              onGenererDashboard={user?.editeurKokpit
                ? (compte) => {
                  setGenerationSurMesure({
                    ...compte,
                    origine: 'library',
                    chemin: [
                      ...(selectedClientId
                        ? [userClients.find(c => c.id === selectedClientId)?.name].filter(Boolean)
                        : []),
                      'Comptes'
                    ]
                  });
                  setCurrentViewWithHistory('generation-dashboard');
                }
                : null}
            />
          )}

          {/* Vue Manager - Gestionnaire de données unifié */}
          {currentView === 'manager' && user && user.role !== 'CLIENT' && (
            <KanbanView
              token={token}
              /* Le Gestionnaire suit le client choisi en en-tête : ses quatre colonnes se
                 bornent à ce dont ce client dispose. */
              clientFiltre={selectedClientId}
              onConnectorConnect={(fournisseur) => {
                // Liste blanche : `onConnect` est aussi branché sur les cartes de
                // connecteur, qui transmettent un événement — jamais un fournisseur.
                if (['MATOMO', 'PIANO_ANALYTICS', 'OPENAI_ADS', 'AWIN', 'PRESTASHOP'].includes(fournisseur)) {
                  setOpenAddConnectorModalOnLoad(fournisseur);
                }
                setCurrentViewWithHistory('connectors');
              }}
              onConnectorDisconnect={() => setCurrentViewWithHistory('connectors')}
              onDataChange={() => {
                fetchAllAdAccounts();
                fetchAvailableSources();
              }}
            />
          )}

          {currentView === 'home' && (
            initialDataLoaded ? (
              <HomePage
                /* La liste arrive déjà bornée par le serveur — périmètre, droit client et
                   `visibleClient` compris : l'accueil n'a rien à filtrer de plus. */
                dashboardsSurMesure={dashboardsSurMesureDuPerimetre}
                ouvrirDashboardSurMesure={ouvrirDashboardSurMesure}
                token={token}
                user={user}
                userSubscription={userSubscription}
                userOrganizations={userOrganizations}
                selectedClientId={selectedClientId}
                setCurrentView={setCurrentViewWithHistory}
                setCurrentDashboardType={setCurrentDashboardType}
                navigateToDashboardWithAccount={navigateToDashboardWithAccount}
                navigateToDiagnosticWithAccount={navigateToDiagnosticWithAccount}
                selectedPeriod={selectedPeriod}
                setSelectedPeriod={setSelectedPeriod}
                customStartDate={customStartDate}
                setCustomStartDate={setCustomStartDate}
                customEndDate={customEndDate}
                setCustomEndDate={setCustomEndDate}
                selectedSiteId={selectedSiteId}
                userSites={userSites}
                ouvrirChoixDashboard={ouvrirChoixDashboard}
                globalConnectors={globalConnectors}
                setOpenAddModalOnLibraryLoad={setOpenAddModalOnLibraryLoad}
                setOpenAddConnectorModalOnLoad={setOpenAddConnectorModalOnLoad}
                compareEnabled={globalCompareEnabled}
                comparisonType={globalComparisonType}
                comparisonStartDate={comparisonStartDate}
                comparisonEndDate={comparisonEndDate}
              />
            ) : (
              <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                <div style={{ textAlign: 'center' }}>
                  <div className="spinner" style={{ margin: '0 auto 16px', width: '40px', height: '40px', border: '3px solid #e5e7eb', borderTop: '3px solid #6366f1', borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
                  <p style={{ color: '#6b7280' }}>Chargement...</p>
                </div>
              </div>
            )
          )}

          {currentView === 'connectors' && (
            <ConnectorsView
              token={token}
              /* L'écran Connecteurs suit le client choisi en en-tête. Il lui faut pour cela la
                 bibliothèque et les sites, qu'il charge lui-même : un connecteur ne porte rien
                 qui dise de quel client il sert les comptes. */
              clientFiltre={selectedClientId}
              testOpenWeatherMap={testOpenWeatherMap}
              weatherTesting={weatherTesting}
              weatherTestResult={weatherTestResult}
              testOpenMeteo={testOpenMeteo}
              openMeteoTesting={openMeteoTesting}
              openMeteoTestResult={openMeteoTestResult}
              connecting={connecting}
              setConnecting={setConnecting}
              handleWeatherConnector={handleWeatherConnector}
              globalConnectors={globalConnectors}
              userSubscription={userSubscription}
              setPianoSites={setPianoSites}
              openAddConnectorModalOnLoad={openAddConnectorModalOnLoad}
              setOpenAddConnectorModalOnLoad={setOpenAddConnectorModalOnLoad}
              onRefreshLibrary={() => {
                refreshGlobalConnectors();
                fetchAllAdAccounts();
                fetchAvailableSources();
              }}
            />
          )}

          {/* Fallback si aucune vue n'est affichée */}
          {!VUES_CONNUES.includes(currentView) && (
            <div style={{ padding: '20px', textAlign: 'center' }}>
              <h2>Vue inconnue: {currentView}</h2>
              <button onClick={() => setCurrentViewWithHistory('connectors')} className="btn btn-primary">
                Aller aux Connecteurs
              </button>
            </div>
          )}
          {currentView === 'history' && (
            <HistoryView
              token={token}
              selectedClientId={selectedClientId}
              analyzeFilterSite={analyzeFilterSite}
              analyzeFilterPlatforms={analyzeFilterPlatforms}
              analyzeFilterAccount={analyzeFilterAccount}
            />
          )}
          
          {currentView === 'results' && analysisResult && (
            <ResultsView
              analysisResult={analysisResult}
              setAnalysisResult={setAnalysisResult}
              setCurrentView={setCurrentViewWithHistory}
              token={token}
            />
          )}
          
          {currentView === 'tools' && (
            <ToolsView
              token={token}
              user={user}
              clientTools={clientTools}
              selectedClientId={selectedClientId}
              userClients={userClients}
              setCurrentView={setCurrentViewWithHistory}
              setOpenSearchConsoleOnLibraryLoad={setOpenSearchConsoleOnLibraryLoad}
              globalConnectors={globalConnectors}
            />
          )}

          {/* Vue MCP - Model Context Protocol pour Claude */}
          {currentView === 'mcp' && mcpOuvert && (
            <div className="step-content">
              <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                <h2 style={{ margin: 0 }}>
                  <Cpu size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
                  MCP Kokpit
                </h2>
              </div>

              {/* Layout 2 colonnes */}
              <div className="mcp-grid-layout">

                {/* Colonne gauche */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
                  {/* Section: Qu'est-ce qu'un MCP ? */}
                  <div style={{ background: '#f9f7f4', borderRadius: '12px', padding: '24px' }}>
                    <h2 style={{ margin: '0 0 16px 0', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      Qu'est-ce qu'un MCP ?
                    </h2>
                    <p style={{ color: '#4b5563', lineHeight: '1.6', margin: 0 }}>
                      Imaginez pouvoir <strong>dialoguer avec vos données</strong> comme vous le feriez avec un expert.
                      Grâce au <strong>Model Context Protocol (MCP)</strong>, Claude accède directement à vos comptes publicitaires et analytics.<br /><br />
                      Croisez Google Ads avec Google Analytics, comparez vos performances Google Ads et Microsoft Ads,
                      identifiez des corrélations invisibles — le tout en langage naturel, sans export ni tableur.
                    </p>
                  </div>

                  {/* Section: Connecteurs disponibles */}
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }}>
                    <h2 style={{ margin: '0 0 8px 0', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Database size={24} style={{ color: '#111827' }} />
                      Connecteurs disponibles
                    </h2>
                    {/*
                      ⚠️ **Un connecteur grisé sans légende est une énigme.** Rien, sur une carte
                      éteinte, ne dit si c'est le client qui n'a pas la régie ou l'écran qui n'a
                      pas chargé. La phrase nomme le client et dit ce que le gris veut dire ;
                      sans filtre, la liste reste le catalogue de ce que le MCP sait lire, et il
                      n'y a donc rien à annoncer.
                    */}
                    {typesMcpDuClient && (
                      <p style={{ margin: '0 0 16px 0', color: '#6b7280', fontSize: '0.85rem' }}>
                        Pour <strong>{userClients.find(c => c.id === selectedClientId)?.name || 'ce client'}</strong> :
                        les connecteurs branchés viennent en tête ; les grisés qui suivent ne le
                        sont sur aucun de ses comptes.
                      </p>
                    )}
                    {/* La légende porte son propre écart ; sans elle, le titre reste à 20 px de la grille. */}
                    <div className="mcp-connectors-grid" style={{ marginTop: typesMcpDuClient ? 0 : '12px' }}>
                      {connecteursMcpOrdonnes.map(({ name, icon, branche }) => (
                        /*
                         * ⚠️ La clé est le **nom**, jamais l'indice : l'ordre change d'un client
                         * à l'autre, et un indice ferait réemployer la carte d'un connecteur pour
                         * en afficher un autre — avec sa classe d'extinction.
                         */
                        <div
                          key={name}
                          className={branche === false ? 'mcp-connecteur mcp-connecteur-eteint' : 'mcp-connecteur'}
                          title={branche === false ? 'Non branché sur ce client' : undefined}
                        >
                          <img src={icon} alt={name} style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain' }} />
                          <span style={{ fontWeight: '600', fontSize: '0.9rem' }}>{name}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>

                {/* Colonne droite */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>

                  {/* Section: Installation */}
                  <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '12px', padding: '24px' }}>
                    <h2 style={{ margin: '0 0 20px 0', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Download size={24} style={{ color: '#111827' }} />
                      Installation
                    </h2>

                    <ol style={{ color: '#4b5563', paddingLeft: '20px', margin: '0', lineHeight: '2' }}>
                      <li>Ouvrez <strong>Claude</strong> et allez dans <strong>Paramètres</strong></li>
                      <li>Cliquez sur <strong>Connecteurs</strong></li>
                      <li>Cliquez sur <strong>Ajouter un connecteur personnalisé</strong></li>
                      <li>Renseignez les informations suivantes :</li>
                    </ol>

                    <div style={{ background: '#f9fafb', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '16px', margin: '16px 0' }}>
                      <div style={{ marginBottom: '12px' }}>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>Nom</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <code style={{ background: '#e5e7eb', padding: '8px 12px', borderRadius: '6px', fontSize: '0.95rem', flex: 1 }}>Kokpit</code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText('Kokpit');
                              const btn = document.getElementById('copy-mcp-name');
                              if (btn) { btn.textContent = '✓'; setTimeout(() => { btn.textContent = 'Copier'; }, 1500); }
                            }}
                            id="copy-mcp-name"
                            style={{ padding: '8px 12px', background: '#1a1a1a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                          >Copier</button>
                        </div>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontWeight: '600', marginBottom: '4px', color: '#374151' }}>URL du serveur MCP distant</label>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <code style={{ background: '#e5e7eb', padding: '8px 12px', borderRadius: '6px', fontSize: '0.95rem', flex: 1 }}>https://mcp.kokpit.io</code>
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText('https://mcp.kokpit.io');
                              const btn = document.getElementById('copy-mcp-url');
                              if (btn) { btn.textContent = '✓'; setTimeout(() => { btn.textContent = 'Copier'; }, 1500); }
                            }}
                            id="copy-mcp-url"
                            style={{ padding: '8px 12px', background: '#1a1a1a', color: 'white', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}
                          >Copier</button>
                        </div>
                      </div>
                    </div>

                    <ol start={5} style={{ color: '#4b5563', paddingLeft: '20px', margin: '0', lineHeight: '2' }}>
                      <li>Cliquez sur <strong>Ajouter</strong></li>
                      <li>Autorisez l'accès à vos données Kokpit</li>
                    </ol>

                    <p style={{ color: '#6b7280', fontSize: '0.9rem', marginTop: '16px', marginBottom: 0 }}>
                      Seuls les connecteurs connectés dans votre compte Kokpit sont disponibles dans le MCP.
                    </p>
                  </div>

                  {/* Section: Exemples d'utilisation */}
                  <div style={{ background: '#f9f7f4', border: 'none', borderRadius: '12px', padding: '24px' }}>
                    <h2 style={{ margin: '0 0 16px 0', fontSize: '1.25rem', display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <MessageSquare size={24} style={{ color: '#111827' }} />
                      Exemples de requêtes
                    </h2>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
                      {[
                        "Montre-moi les performances de mes campagnes Google Ads ce mois-ci",
                        "Quels sont mes mots-clés avec le meilleur ROAS ?",
                        "Compare le trafic de mon site entre cette semaine et la semaine dernière",
                        "Analyse mes conversions par appareil sur les 30 derniers jours",
                        "Quelles balises tournent sur mon site, et lesquelles posent une conversion ?",
                        "Mes balises Tag Manager déclarent-elles le consentement ?",
                        "La météo a-t-elle un impact sur mes résultats ?"
                      ].map((example, idx) => (
                        <div key={idx} style={{
                          background: 'white',
                          padding: '12px 16px',
                          borderRadius: '8px',
                          border: '1px solid #dbeafe',
                          fontStyle: 'italic',
                          color: '#1e40af'
                        }}>
                          "{example}"
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Vue Diagnostic - Scanner automatique des comptes */}
          {currentView === 'radar' && radarOuvert && (
            <RadarView
              token={token}
              selectedClientId={selectedClientId}
              selectedSiteId={selectedSiteId}
              plateformeExterne={plateformeRadar}
              onPlateformesConnues={noterPlateformesRadar}
              onPlateformeChoisie={setPlateformeRadar}
            />
          )}

          {/* Le choix du dashboard, après un clic sur « Voir » dans l'accueil. Les deux
              destinations partent d'ici pour que l'écran de choix n'ait pas à connaître les
              états de sélection de chaque solution de mesure. */}
          {currentView === 'choix-dashboard' && choixDashboard && (
            <ChoixDashboardAnalytics
              typeSource={choixDashboard.typeSource}
              siteId={choixDashboard.siteId}
              nomDuCompte={choixDashboard.nom}
              onChoisirAnalytics={ouvrirDashboardAnalytics}
              /*
                * ⚠️ `null` plutôt qu'un geste inerte : l'écran ne rend la carte que si on lui
                * câble le clic. Un compte de régie ne doit pas voir « Dashboard consolidé », qui
                * croise une audience avec les régies — il en est déjà l'un des deux termes.
                */
              onChoisirConsolide={destinationsDuChoix(choixDashboard.typeSource, choixDashboard.siteId).includes('consolide')
                ? ((typeSource, siteId) => ouvrirConsolide(typeSource, siteId))
                : null}
              dashboardsSurMesure={dashboardsSurMesurePour(choixDashboard.typeSource, choixDashboard.siteId)}
              onChoisirSurMesure={ouvrirDashboardSurMesure}
              /*
               * ⚠️ **La carte s'affiche même sans compte connu**, et c'est le correctif qui
               * compte : la conditionner à `siteId` la faisait disparaître sur l'écran d'une
               * solution de mesure atteinte sans compte précis — constaté sur Matomo, où la carte
               * n'apparaissait pas. La vue demande alors le compte.
               *
               * `siteId` est ici un **identifiant de compte**, pas de site : `dashboardsSurMesurePour`
               * le compare à `libraryItemId` et à `sourceId`, et la porte du serveur accepte les
               * deux formes. Absent, on ne transmet que le type, qui borne la liste proposée.
               */
              onGenerer={user?.editeurKokpit
                ? () => {
                  setGenerationSurMesure({
                    ...(choixDashboard.siteId
                      ? {
                        id: String(choixDashboard.siteId),
                        name: choixDashboard.nom || '',
                        type: choixDashboard.typeSource
                      }
                      : { type: choixDashboard.typeSource }),
                    origine: 'choix-dashboard',
                    /*
                     * Le chemin tel qu'il a été parcouru. ⚠️ Le nom du compte n'y figure pas :
                     * la vue l'ajoute elle-même en dernier segment, une fois qu'elle l'a résolu
                     * côté serveur — le mettre ici le ferait apparaître deux fois, et sous le
                     * libellé approximatif que l'écran de choix en connaît.
                     */
                    chemin: [
                      ...(selectedClientId
                        ? [userClients.find(c => c.id === selectedClientId)?.name].filter(Boolean)
                        : []),
                      libellePlateforme(choixDashboard.typeSource)
                    ].filter(Boolean)
                  });
                  setCurrentViewWithHistory('generation-dashboard');
                }
                : null}
              onRetour={() => setCurrentViewWithHistory(choixDashboard.origine || 'home')}
              libelleRetour={choixDashboard.origine === 'dashboard' ? 'Retour aux dashboards' : "Retour à l'accueil"}
            />
          )}

          {/* Un dashboard sur mesure : un artefact HTML hébergé, rendu dans une iframe en bac à
              sable. L'hôte ne connaît rien de son contenu — il lui transmet la période du
              sélecteur de Kokpit et relaie ses demandes de données vers la porte du serveur. */}
          {/* La fiche d'un accès client : tous ses droits et ses cartes au même endroit.
              On y entre en cliquant sa ligne dans l'onglet « Accès clients ». */}
          {currentView === 'acces-client' && user && ficheAccesOuverte && (
            <FicheAccesClient
              token={token}
              orgId={ficheAccesOuverte.org}
              email={ficheAccesOuverte.email}
              onRetour={revenirAuxAccesClients}
            />
          )}

          {currentView === 'dashboard-sur-mesure' && dashboardSurMesureOuvert && (
            <DashboardSurMesureView
              token={token}
              dashboardId={dashboardSurMesureOuvert}
              versionId={versionSurMesureOuverte}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onRetour={() => setCurrentViewWithHistory('home')}
            />
          )}

          {/* La génération assistée. Même borne que la route : l'éditeur de Kokpit seulement,
              la facture Claude étant chez lui. `generationSurMesure` peut n'être qu'un
              `{ type }` — la vue demande alors le compte.

              ⚠️ **Un seul montage.** Il y en avait deux, sur la même condition, ne différant que
              par leur `onRetour` : la vue était rendue **deux fois**, donc deux conversations et
              deux additions sur la même facture. D'où `origine`, posée par la porte d'entrée —
              « Retour » doit ramener d'où l'on vient, et c'est ce que les deux montages
              essayaient d'exprimer chacun de son côté.

              ⚠️ La borne est répétée ici et non déduite du montage : une vue atteinte par l'URL
              n'a traversé aucune porte, et c'est la même discipline que Radar — « le bouton, la
              redirection d'URL, et le refus de l'API ». */}
          {currentView === 'generation-dashboard' && generationSurMesure && user?.editeurKokpit && (
            <VueGenerationDashboard
              token={token}
              compte={generationSurMesure}
              selectedClientId={selectedClientId}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              chemin={generationSurMesure.chemin}
              onRetour={() => {
                /*
                 * Revenir d'où l'on vient, et dans l'état où on l'a laissé : on a quitté un
                 * dashboard précis pour décrire un tableau, et l'on veut y retrouver ce qu'on
                 * regardait. Sans `retourDashboardType`, « Dashboard » rouvre l'écran de
                 * sélection des plateformes.
                 */
                if (generationSurMesure.retourDashboardType) {
                  setCurrentDashboardType(generationSurMesure.retourDashboardType);
                }
                setCurrentViewWithHistory(generationSurMesure.origine || 'home');
              }}
            />
          )}

          {/* Vue Budgets — le portefeuille de la consommation budgétaire, compte par compte.
              La garde est la même qu'au bouton : un onglet visible que la vue refuserait mène
              à une page blanche. */}
          {currentView === 'budgets' && budgetsOuvert && (
            <BudgetsView
              token={token}
              selectedClientId={selectedClientId}
              selectedSiteId={selectedSiteId}
            />
          )}

          {currentView === 'diagnostic' && (
            <DiagnosticView
              token={token}
              user={user}
              selectedClientId={selectedClientId}
              userClients={userClients}
              initialAccountId={diagnosticInitialAccountId}
              onInitialAccountUsed={() => setDiagnosticInitialAccountId(null)}
              selectedScanId={diagnosticSelectedScanId}
              onScanIdChange={setDiagnosticSelectedScanId}
              subView={diagnosticSubView}
              onSubViewChange={setDiagnosticSubView}
            />
          )}

          {/* Vue Gestion des Clients - Réservée aux admins, PRO et premium, uniquement si connecteurs */}
          {currentView === 'clients' && user && user.role !== 'CLIENT' && globalConnectors.length > 0 && (
            <ClientsView
              token={token}
              onClientChange={fetchUserClients}
              clientFiltre={selectedClientId}
            />
          )}

          {/* Vue Gestion des Sites - uniquement si connecteurs */}
          {currentView === 'sites' && user && user.role !== 'CLIENT' && globalConnectors.length > 0 && (
            <SitesView
              token={token}
              onSitesChange={fetchUserSites}
              clientFiltre={selectedClientId}
            />
          )}

          {/* Vue Archive des Notes - abonnés uniquement */}
          {/* Calendrier des notes. Même garde que l'archive : l'outil relève des plans
              payants et n'est pas ouvert aux accès clients. */}
          {currentView === 'notes-calendrier' && user && user.role !== 'CLIENT' && userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan) && (
            <NotesCalendar
              token={token}
              clients={userClients}
              sites={userSites}
              accounts={allAdAccounts}
              selectedClientId={selectedClientId}
              selectedSiteId={selectedSiteId}
            />
          )}

          {/* Import de notes. Même garde que l'archive et le calendrier. */}
          {currentView === 'notes-import' && user && user.role !== 'CLIENT' && userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan) && (
            <NotesImport
              token={token}
              clients={userClients}
              sites={userSites}
              accounts={allAdAccounts}
              selectedClientId={selectedClientId}
              selectedSiteId={selectedSiteId}
            />
          )}

          {currentView === 'notes-archive' && user && user.role !== 'CLIENT' && userSubscription && ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription.plan) && (
            <NotesArchive
              token={token}
              onBack={() => setCurrentViewWithHistory('home')}
              clients={userClients}
              sites={userSites}
              accounts={allAdAccounts}
              selectedClientId={selectedClientId}
              onOuvrirVue={ouvrirVueNotes}
            />
          )}

          {currentView === 'team' && user && (
            <TeamView
              token={token}
              user={user}
              userSubscription={userSubscription}
              onBack={() => setCurrentViewWithHistory('home')}
            />
          )}

          {currentView === 'tickets' && user && user.role !== 'CLIENT' && (
            <TicketsView
              token={token}
              user={user}
              userSubscription={userSubscription}
              currentView={currentView}
              previousView={previousViewBeforeTickets}
              selectedClientId={selectedClientId}
              selectedSiteId={selectedSiteId}
              currentDashboardType={currentDashboardType}
              globalConnectors={globalConnectors}
              userOrganizations={userOrganizations}
              clients={userClients}
              initialTicketId={ticketsInitialTicketId}
              onTicketSelect={handleUserTicketSelect}
            />
          )}

          {currentView === 'organization' && user && (
            <OrganizationView
              token={token}
              user={user}
              userSubscription={userSubscription}
              /* L'onglet vient du hash — `#organization/clients` — pour que le retour depuis la
                 fiche d'un accès client retombe sur l'onglet d'où l'on vient, et non sur
                 « Membres ». L'écran le signale ensuite par `onOngletChange`, sans quoi
                 l'adresse resterait sur l'onglet d'arrivée. */
              initialTab={ongletOrganisation}
              onOngletChange={setOngletOrganisation}
              onOuvrirAccesClient={ouvrirFicheAccesClient}
              onBack={() => setCurrentViewWithHistory('home')}
            />
          )}

          {currentView === 'statistics' && user && userOrganizations.length > 0 && (
            <StatisticsView
              token={token}
              user={user}
              userOrganizations={userOrganizations}
            />
          )}

          {currentView === 'pricing' && user && (userOrganizations.length === 0 || userOrganizations.some(org => org.role === 'OWNER')) && (
            <SubscriptionPage
              token={token}
              user={user}
              currentSubscription={userSubscription}
              activeTab={subscriptionTab}
              onBack={() => setCurrentViewWithHistory('home')}
            />
          )}

          {currentView === 'xml-feeds' && (
            <XmlFeedsManager token={token} />
          )}
          {currentView === 'admin' && (
            <AdminPanel
              token={token}
              user={user}
              activeTab={adminTab}
              onTabChange={setAdminTab}
              initialTicketId={adminInitialTicketId}
              onTicketSelect={handleAdminTicketSelect}
            />
          )}
          
          {currentView === 'notifications' && user?.role !== 'CLIENT' && (
            <NotificationsView token={token} user={user} userOrganizations={userOrganizations} />
          )}

          {/* Vue Dashboard Matomo Analytics */}
          {currentView === 'matomo-dashboard' && (
            <MatomoDashboardView
              token={token}
              analyseAutorisee={analyseAutorisee}
              dashboardNotes={dashboardNotes}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              matomoSites={matomoSites}
              filteredMatomoSites={filteredMatomoSites}
              setMatomoSites={setMatomoSites}
              matomoSelectedSiteId={matomoSelectedSiteId}
              setMatomoSelectedSiteId={setMatomoSelectedSiteId}
              matomoDashboardData={matomoDashboardData}
              setMatomoDashboardData={setMatomoDashboardData}
              matomoLoading={matomoLoading}
              setMatomoLoading={setMatomoLoading}
              matomoGranularity={matomoGranularity}
              setMatomoGranularity={setMatomoGranularity}
              KPICard={KPICard}
              formatNumber={formatNumber}
              accountId={matomoSelectedSiteId || 'global'}
              userRole={user?.role || 'USER'}
              compareEnabled={matomoCompareEnabled}
              comparisonType={matomoComparisonType}
            />
          )}

          {/* Vue Dashboard Google Analytics 4 */}
          {currentView === 'ga4-dashboard' && (
            <GA4DashboardView
              token={token}
              analyseAutorisee={analyseAutorisee}
              dashboardNotes={dashboardNotes}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              ga4Properties={ga4Properties}
              filteredGA4Properties={filteredGA4Properties}
              setGA4Properties={setGA4Properties}
              ga4SelectedPropertyId={ga4SelectedPropertyId}
              setGA4SelectedPropertyId={setGA4SelectedPropertyId}
              ga4DashboardData={ga4DashboardData}
              setGA4DashboardData={setGA4DashboardData}
              ga4Loading={ga4Loading}
              setGA4Loading={setGA4Loading}
              ga4Granularity={ga4Granularity}
              setGA4Granularity={setGA4Granularity}
              KPICard={KPICard}
              formatNumber={formatNumber}
              userRole={user?.role}
              compareEnabled={ga4CompareEnabled}
              comparisonType={ga4ComparisonType}
            />
          )}

          {/* Vue Dashboard consolidé : GA4 croisé avec les régies publicitaires.
              Elle s'affiche sans barre latérale et porte son propre sélecteur de période,
              qui écrit dans l'état global comme le fait le menu de la barre latérale. */}
          {currentView === 'consolide-dashboard' && (
            <ConsolidatedDashboardView
              token={token}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              onPeriodChange={(period, debut, fin) => {
                if (period === 'CUSTOM') {
                  // Une borne manquante laisserait le serveur retomber sur 30 jours
                  if (!debut || !fin) return;
                  setSelectedPeriod('CUSTOM');
                  setCustomStartDate(debut);
                  setCustomEndDate(fin);
                } else {
                  setSelectedPeriod(period);
                  setCustomStartDate('');
                  setCustomEndDate('');
                }
                window.dispatchEvent(new CustomEvent('periodChange', { detail: { period } }));
              }}
              selectedClientId={selectedClientId}
              selectedSiteId={selectedSiteId}
              userSites={userSites}
              initialPropertyId={consolideSelectedPropertyId}
              analyseAutorisee={analyseAutorisee}
              sourceMesure={consolideSelectedSource}
              comparaison={consolideComparaison}
              onComparaisonChange={setConsolideComparaison}
            />
          )}

          {/* Vue Dashboard PrestaShop — la boutique, à côté des trois solutions de mesure. */}
          {currentView === 'prestashop-dashboard' && (
            <PrestashopDashboardView
              token={token}
              analyseAutorisee={analyseAutorisee}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              prestashopBoutiques={prestashopBoutiques}
              filteredPrestashopBoutiques={filteredPrestashopBoutiques}
              prestashopBoutiqueId={prestashopBoutiqueId}
              setPrestashopBoutiqueId={setPrestashopBoutiqueId}
              prestashopGranularity={prestashopGranularity}
              setPrestashopGranularity={setPrestashopGranularity}
              userRole={user?.role}
              KPICard={KPICard}
              formatNumber={formatNumber}
            />
          )}

          {/* Vue Dashboard Piano Analytics */}
          {currentView === 'piano-dashboard' && (
            <PianoDashboardView
              token={token}
              analyseAutorisee={analyseAutorisee}
              dashboardNotes={dashboardNotes}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              pianoSites={pianoSites}
              filteredPianoSites={filteredPianoSites}
              setPianoSites={setPianoSites}
              pianoSelectedSiteId={pianoSelectedSiteId}
              setPianoSelectedSiteId={setPianoSelectedSiteId}
              pianoDashboardData={pianoDashboardData}
              setPianoDashboardData={setPianoDashboardData}
              pianoLoading={pianoLoading}
              setPianoLoading={setPianoLoading}
              pianoGranularity={pianoGranularity}
              setPianoGranularity={setPianoGranularity}
              KPICard={KPICard}
              formatNumber={formatNumber}
              accountId={pianoSelectedSiteId || 'global'}
              userRole={user?.role || 'USER'}
              compareEnabled={pianoCompareEnabled}
              comparisonType={pianoComparisonType}
            />
          )}
          {/* Vue results supprimée - les résultats s'affichent dans la vue analyze */}
            </>
          )}
          </div>
        </div>
      </header>

      {/* Modal Notes */}
      <NotesModal
        isOpen={isNotesModalOpen}
        onClose={() => { setIsNotesModalOpen(false); setEditingNote(null); }}
        onSave={(note) => {
          console.log('Note sauvegardée:', note);
          setIsNotesModalOpen(false);
          setEditingNote(null);
        }}
        editingNote={editingNote}
        currentContext={notesContext}
        clients={userClients}
        sites={userSites}
        accounts={allAdAccounts}
        token={token}
        onOuvrirVue={ouvrirVueNotes}
      />

      {/* Bouton d'aide flottant - masqué pour les clients */}
      {user?.role !== 'CLIENT' && ReactDOM.createPortal(
        <>
          {/* Bouton flottant */}
          <button
            className={`help-floating-button ${isHelpPanelOpen ? 'active' : ''}`}
            onClick={() => setIsHelpPanelOpen(!isHelpPanelOpen)}
            title="Aide et assistance"
          >
            {isHelpPanelOpen ? <X size={20} /> : <HelpCircle size={20} />}
          </button>

          {/* Panneau d'aide */}
          {isHelpPanelOpen && (
            <div className="help-panel">
              <div className="help-panel-header">
                <h2>Aide et assistance</h2>
                <button className="help-panel-close" onClick={() => setIsHelpPanelOpen(false)}>
                  <X size={20} />
                </button>
              </div>

              <div className="help-panel-content">
                <div className="help-section">
                  <h3>Guides pour débuter</h3>
                  <div className="help-cards-grid">
                    <a href="/#help" target="_blank" rel="noopener noreferrer" className="help-card">
                      <div className="help-card-icon" style={{ background: '#e0e7ff' }}>
                        <BarChart3 size={20} style={{ color: '#6366f1' }} />
                      </div>
                      <span>Découvrir le Dashboard</span>
                    </a>
                    <a href="/#help" target="_blank" rel="noopener noreferrer" className="help-card">
                      <div className="help-card-icon" style={{ background: '#fef3c7' }}>
                        <Plug size={20} style={{ color: '#f59e0b' }} />
                      </div>
                      <span>Connecter vos comptes</span>
                    </a>
                    <a href="/#help" target="_blank" rel="noopener noreferrer" className="help-card">
                      <div className="help-card-icon" style={{ background: '#d1fae5' }}>
                        <Bot size={20} style={{ color: '#10b981' }} />
                      </div>
                      <span>Utiliser l'IA</span>
                    </a>
                    <a href="/#help" target="_blank" rel="noopener noreferrer" className="help-card">
                      <div className="help-card-icon" style={{ background: '#fee2e2' }}>
                        <Activity size={20} style={{ color: '#ef4444' }} />
                      </div>
                      <span>Lancer un diagnostic</span>
                    </a>
                  </div>
                </div>

                <div className="help-section">
                  <h3>Ressources</h3>
                  <div className="help-links">
                    <a href="/#help" target="_blank" rel="noopener noreferrer" className="help-link">
                      <HelpCircle size={18} />
                      <span>Centre d'aide</span>
                    </a>
                    <a href="https://www.kokpit.io/fr/changelog" target="_blank" rel="noopener noreferrer" className="help-link">
                      <Sparkles size={18} />
                      <span>Nouveautés</span>
                    </a>
                    <button type="button" className="help-link" onClick={relancerVisiteGuidee}>
                      <Compass size={18} />
                      <span>Visite guidée</span>
                    </button>
                  </div>
                </div>

                <div className="help-section help-support-section">
                  <button
                    className="help-contact-button"
                    onClick={() => {
                      setIsHelpPanelOpen(false);
                      if (currentView !== 'tickets') {
                        setPreviousViewBeforeTickets(currentView);
                      }
                      setCurrentView('tickets');
                    }}
                  >
                    <MessageSquare size={18} />
                    <span>Contacter le support</span>
                  </button>
                </div>
              </div>
            </div>
          )}
        </>,
        document.body
      )}

    </div>
  );
}

// Composant: Authentification
function AuthStep({ handleGoogleLogin, loading, error, onLoginSuccess }) {
  const [authMode, setAuthMode] = useState('login'); // 'login', 'register', 'forgot-password', 'reset-password', 'verify-email'
  const [resetToken, setResetToken] = useState(null);
  const [verifyToken, setVerifyToken] = useState(null);

  // Handler pour Google OAuth avec le bon mode (login ou signup)
  const handleGoogleAuth = () => {
    // Sauvegarder le hash pour restaurer après login
    if (window.location.hash) {
      localStorage.setItem('kokpeet_returnHash', window.location.hash);
    }
    // Utiliser le bon endpoint selon le mode
    const endpoint = authMode === 'register' ? '/api/auth/google/signup' : '/api/auth/google';
    window.location.href = `${API_URL}${endpoint}`;
  };

  // Gérer les routes spéciales pour reset-password et verify-email
  useEffect(() => {
    const pathname = window.location.pathname;

    // Route /reset-password/:token
    if (pathname.startsWith('/reset-password/')) {
      const token = pathname.replace('/reset-password/', '');
      if (token) {
        setResetToken(token);
        setAuthMode('reset-password');
      }
    }

    // Route /verify-email/:token
    if (pathname.startsWith('/verify-email/')) {
      const token = pathname.replace('/verify-email/', '');
      if (token) {
        setVerifyToken(token);
        setAuthMode('verify-email');
      }
    }
  }, []);

  // Callback après login réussi par email
  const handleEmailLoginSuccess = (user, token) => {
    // Nettoyer l'URL si nécessaire
    window.history.replaceState({}, document.title, '/');
    if (onLoginSuccess) {
      onLoginSuccess(user, token);
    }
  };

  // Callback après inscription réussie
  const handleRegisterSuccess = () => {
    // L'utilisateur doit vérifier son email, on reste sur la page
  };

  // Callback après reset password réussi
  const handleResetSuccess = () => {
    window.history.replaceState({}, document.title, '/');
    setAuthMode('login');
    setResetToken(null);
  };

  // Callback après vérification email réussie
  const handleVerifySuccess = (user, token) => {
    window.history.replaceState({}, document.title, '/');
    if (onLoginSuccess) {
      onLoginSuccess(user, token);
    }
  };

  // Rendu conditionnel pour les pages spéciales
  if (authMode === 'reset-password' && resetToken) {
    return (
      <div className="auth-split-container">
        <div className="auth-left-panel">
          <div className="auth-left-content">
            <div className="logo-section">
              <div className="logo-main">
                <img src="/kokpit-logo.png" alt="Kokpit" style={{ height: '64px' }} />
              </div>
              <p className="baseline">Le copilote de vos campagnes publicitaires</p>
            </div>
          </div>
        </div>
        <div className="auth-right-panel">
          <div className="auth-right-content">
            <div className="auth-container">
              <ResetPassword token={resetToken} onSuccess={handleResetSuccess} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (authMode === 'verify-email' && verifyToken) {
    return (
      <div className="auth-split-container">
        <div className="auth-left-panel">
          <div className="auth-left-content">
            <div className="logo-section">
              <div className="logo-main">
                <img src="/kokpit-logo.png" alt="Kokpit" style={{ height: '64px' }} />
              </div>
              <p className="baseline">Le copilote de vos campagnes publicitaires</p>
            </div>
          </div>
        </div>
        <div className="auth-right-panel">
          <div className="auth-right-content">
            <div className="auth-container">
              <VerifyEmail token={verifyToken} onSuccess={handleVerifySuccess} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (authMode === 'forgot-password') {
    return (
      <div className="auth-split-container">
        <div className="auth-left-panel">
          <div className="auth-left-content">
            <div className="logo-section">
              <div className="logo-main">
                <img src="/kokpit-logo.png" alt="Kokpit" style={{ height: '64px' }} />
              </div>
              <p className="baseline">Le copilote de vos campagnes publicitaires</p>
            </div>
          </div>
        </div>
        <div className="auth-right-panel">
          <div className="auth-right-content">
            <div className="auth-container">
              <ForgotPassword onBack={() => setAuthMode('login')} />
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="auth-split-container">
      {/* Partie gauche - Fond mauve avec logo et baseline */}
      <div className="auth-left-panel">
        <div className="auth-left-content">
          <div className="logo-section">
            <div className="logo-main">
              <img src="/kokpit-logo.png" alt="Kokpit" style={{ height: '64px' }} />
            </div>
            <p className="baseline">Le copilote de vos campagnes publicitaires</p>
          </div>

          <div className="auth-description-left">
            <ul style={{ listStyle: 'none', padding: 0, fontSize: '1.2rem', lineHeight: '2.2', textAlign: 'left' }}>
              <li style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '12px' }}><BarChart3 size={20} /> Dashboards multi-plateformes</li>
              <li style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '12px' }}><Brain size={20} /> Analyses IA de vos performances</li>
              <li style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '12px' }}><Wrench size={20} /> Outils d'optimisation</li>
              <li style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '12px' }}><Users size={20} /> Gestion multi-clients pour agences</li>
            </ul>
          </div>
        </div>
      </div>

      {/* Partie droite - Formulaires d'authentification */}
      <div className="auth-right-panel">
        <div className="auth-right-content">
          <div className="auth-container">
            {/* Bouton Google OAuth - en premier */}
            <button
              onClick={handleGoogleAuth}
              disabled={loading}
              className="google-login-btn"
            >
              {loading ? (
                <Loader className="spin" size={20} />
              ) : (
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
                </svg>
              )}
              {authMode === 'register' ? "S'inscrire avec Google" : 'Continuer avec Google'}
            </button>

            {error && (
              <div className="error-message" style={{ marginTop: '16px' }}>
                <AlertCircle size={16} />
                {error}
              </div>
            )}

            {/* Divider */}
            <div className="auth-divider">
              <span>ou</span>
            </div>

            {/* Formulaire de connexion */}
            {authMode === 'login' && (
              <LoginForm
                onSuccess={handleEmailLoginSuccess}
                onForgotPassword={() => setAuthMode('forgot-password')}
                onSwitchToRegister={() => setAuthMode('register')}
              />
            )}

            {/* Formulaire d'inscription */}
            {authMode === 'register' && (
              <RegisterForm
                onSuccess={handleRegisterSuccess}
                onSwitchToLogin={() => setAuthMode('login')}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

// Composant: Vue Analyse
function AnalyzeView({ onAnalyze, loading, error, token, setCurrentView, globalConnectors, selectedClientId, analyzeFilterSite, analyzeFilterAnalytics, analyzeFilterPlatforms, analyzeFilterAccount, analysisResult, setAnalysisResult, openConversationData, setOpenConversationData }) {
  const [selectedItems, setSelectedItems] = useState([]);
  const [question, setQuestion] = useState('');
  const [library, setLibrary] = useState([]);
  const [allLibrary, setAllLibrary] = useState([]); // Bibliothèque complète (non filtrée)
  const [loadingLibrary, setLoadingLibrary] = useState(true);

  // État du chat panel - ouvert si analyse en cours OU si on continue une conversation existante
  const chatOpen = analysisResult?.chatOpen === true || openConversationData !== null;
  const setChatOpen = (open) => {
    if (!open) {
      // Fermer le chat - reset analysisResult et openConversationData
      setAnalysisResult(null);
      if (setOpenConversationData) {
        setOpenConversationData(null);
      }
    }
  };

  // Callback quand le streaming est terminé
  const handleStreamComplete = (streamResult) => {
    console.log('[ANALYZE] Stream complete:', streamResult);
    // Garder le chat ouvert mais marquer comme terminé
    setAnalysisResult({
      ...analysisResult,
      ...streamResult,
      streamingMode: false,
      chatOpen: true
    });
  };

  // États pour l'export CSV Facebook Ads
  const [csvStartDate, setCsvStartDate] = useState(() => {
    const date = new Date();
    date.setDate(date.getDate() - 30);
    return jourIso(date);
  });
  const [csvEndDate, setCsvEndDate] = useState(() => {
    return aujourdhuiIso();
  });
  const [exportingCSV, setExportingCSV] = useState(false);
  const [csvExportError, setCsvExportError] = useState(null);

  useEffect(() => {
    loadLibrary();
  }, [selectedClientId]); // Recharger quand le client change

  const loadLibrary = async () => {
    try {
      const params = {};
      if (selectedClientId) {
        params.clientId = selectedClientId;
        console.log('🎯 [ANALYSER] Chargement bibliothèque pour client:', selectedClientId);
      }
      
      const response = await axios.get(`${API_URL}/api/library?usage=outils`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setAllLibrary(response.data.library); // Sauvegarder la bibliothèque complète
      setLibrary(response.data.library);
      
      const itemsCount = Object.values(response.data.library || {}).flat().length;
      console.log('🎯 [ANALYSER] Bibliothèque chargée:', itemsCount, 'items');
      console.log('🎯 [ANALYSER] Structure library:', response.data.library);
      console.log('🎯 [ANALYSER] Paramètres envoyés:', params);
    } catch (error) {
      console.error('❌ Erreur chargement bibliothèque:', error);
    } finally {
      setLoadingLibrary(false);
    }
  };


  // Vider la sélection d'items quand le client ou les filtres changent
  useEffect(() => {
    if (selectedItems.length > 0) {
      setSelectedItems([]);
      console.log('🎯 [ANALYSER] Changement de filtre détecté, reset de la sélection');
    }
  }, [selectedClientId, analyzeFilterSite, analyzeFilterPlatforms, analyzeFilterAccount]);

  const toggleItemSelection = (item) => {
    setSelectedItems(prev => {
      const isSelected = prev.find(selected => selected.id === item.id);
      if (isSelected) {
        return prev.filter(selected => selected.id !== item.id);
      } else {
        return [...prev, item];
      }
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onAnalyze(selectedItems, question);
  };

  // Fonction d'export CSV Facebook Ads
  const handleExportCSV = async () => {
    // Trouver le premier compte Facebook Ads sélectionné
    const facebookAccount = selectedItems.find(item => item.type === 'FACEBOOK_ADS');
    if (!facebookAccount) {
      setCsvExportError('Aucun compte Meta Ads sélectionné');
      return;
    }

    setExportingCSV(true);
    setCsvExportError(null);

    try {
      const response = await axios.post(
        `${API_URL}/api/facebook-ads/export-csv`,
        {
          accountId: facebookAccount.id,
          startDate: csvStartDate,
          endDate: csvEndDate,
          level: 'all'
        },
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      // Créer un lien de téléchargement
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `facebook_ads_export_${csvStartDate}_${csvEndDate}.zip`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      console.log('✅ [ANALYSER] Export CSV téléchargé avec succès');
    } catch (error) {
      console.error('❌ [ANALYSER] Erreur export CSV:', error);

      // Essayer de lire le message d'erreur du blob
      if (error.response?.data instanceof Blob) {
        const text = await error.response.data.text();
        try {
          const json = JSON.parse(text);
          setCsvExportError(json.error || 'Erreur lors de l\'export');
        } catch {
          setCsvExportError('Erreur lors de l\'export CSV');
        }
      } else {
        setCsvExportError(error.response?.data?.error || 'Erreur lors de l\'export CSV');
      }
    } finally {
      setExportingCSV(false);
    }
  };

  // Créer une liste unique de tous les items (éviter les doublons)
  // Filtrer selon les filtres de la sidebar
  const allItems = Object.values(library).flat().filter((item, index, arr) => {
    // Éviter les doublons
    if (arr.findIndex(i => i.id === item.id) !== index) return false;

    // Filtrer par site si sélectionné
    if (analyzeFilterSite && analyzeFilterSite !== 'all') {
      if (item.siteId !== analyzeFilterSite) return false;
    }

    // Filtrer par plateforme Ads selon les filtres de la sidebar
    // Note: Microsoft Ads n'est pas encore supporté dans Analyser
    if (item.type === 'GOOGLE_ADS' && !analyzeFilterPlatforms?.google) return false;
    if (item.type === 'FACEBOOK_ADS' && !analyzeFilterPlatforms?.facebook) return false;

    // Filtrer par Analytics selon les filtres de la sidebar
    // Note: GA4 et Matomo ne sont pas encore supportés dans Analyser
    if (item.type === 'GOOGLE_ANALYTICS') return false;
    if (item.type === 'MATOMO') return false;

    // Ne garder que les types supportés (uniquement Google Ads et Facebook Ads pour l'instant)
    if (!['GOOGLE_ADS', 'FACEBOOK_ADS'].includes(item.type)) return false;

    // Filtrer par compte spécifique si sélectionné
    if (analyzeFilterAccount && analyzeFilterAccount !== 'all' && item.id !== analyzeFilterAccount) return false;

    return true;
  });

  if (loadingLibrary) {
    return (
      <div className="step-content">
        <div className="loading-state">
          <Loader className="spin" size={24} />
          <p>Chargement de votre bibliothèque...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-content">
      <div className="page-header">
        <h2>🤖 Analyse de données</h2>
      </div>
      
      {allItems.length === 0 ? (
        <div className="empty-state">
          <Database size={48} />
          <h3>Aucune source de données</h3>
          <p>Ajoutez des fichiers à votre bibliothèque pour commencer à analyser.</p>
          <button
            onClick={() => setCurrentView('library')}
            className="btn btn-primary"
            style={{
              fontSize: '16px',
              padding: '12px 24px',
              backgroundColor: '#111827',
              borderColor: '#111827'
            }}
          >
            + Ajouter des données
          </button>
        </div>
      ) : (
        <div>
          <div className="analysis-layout">
            <div className="analysis-sources">
              <div className="form-group">
                <label>Sélectionnez vos sources de données :</label>
              <div className="items-grid">
                {allItems.map(item => (
                  <div
                    key={item.id}
                    className={`item-card ${selectedItems.find(s => s.id === item.id) ? 'selected' : ''}`}
                    onClick={() => toggleItemSelection(item)}
                  >
                    <div className="item-header">
                      <img
                        src={item.type === 'GOOGLE_ADS' ? googleAdsLogo : item.type === 'MICROSOFT_ADS' ? microsoftAdsLogo : item.type === 'PINTEREST_ADS' ? pinterestAdsLogo : item.type === 'OPENAI_ADS' ? chatgptAdsLogo : item.type === 'AWIN' ? awinLogo : metaAdsLogo}
                        alt={item.type === 'GOOGLE_ADS' ? 'Google Ads' : item.type === 'MICROSOFT_ADS' ? 'Microsoft Ads' : item.type === 'PINTEREST_ADS' ? 'Pinterest Ads' : item.type === 'OPENAI_ADS' ? 'ChatGPT Ads' : item.type === 'AWIN' ? 'Awin' : 'Meta Ads'}
                        className="item-logo"
                      />
                      <span className="item-name">{item.name}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
            </div>
            
            <div className="analysis-panel">
              <form onSubmit={handleSubmit} className="analysis-form">
              <div className="form-group">
                <label htmlFor="question">Votre question d'analyse :</label>
                <textarea
                  id="question"
                  placeholder="Exemple: Résumez les performances de ventes par mois, ou analysez les corrélations entre ces sources..."
                  value={question}
                  onChange={(e) => setQuestion(e.target.value)}
                  rows={6}
                  required
                />
              </div>

              <button
                type="submit"
                disabled={loading || selectedItems.length === 0 || !question.trim()}
                className="btn btn-primary btn-large"
              >
                {loading ? <Loader className="spin" size={16} /> : null}
                {loading ? 'Analyse en cours...' :
                  selectedItems.length === 0 ? 'Sélectionnez des sources' :
                  'Lancer l\'analyse'
                }
              </button>
            </form>

            {/* Bloc Extraction CSV - visible uniquement si Facebook Ads sélectionné */}
            {selectedItems.some(item => item.type === 'FACEBOOK_ADS') && (
              <div className="csv-export-block">
                <h4><Download size={16} />&nbsp;Extraction de données CSV</h4>
                <p className="csv-export-description">
                  Export complet : Campagnes, Ad Sets, Publicités + ventilations par âge/genre, pays, plateforme, placement, appareil
                </p>

                <div className="csv-date-range">
                  <label>Période d'extraction :</label>
                  <div className="date-inputs">
                    <input
                      type="date"
                      value={csvStartDate}
                      onChange={(e) => setCsvStartDate(e.target.value)}
                      max={csvEndDate}
                    />
                    <span className="date-separator">→</span>
                    <input
                      type="date"
                      value={csvEndDate}
                      onChange={(e) => setCsvEndDate(e.target.value)}
                      min={csvStartDate}
                      max={aujourdhuiIso()}
                    />
                  </div>
                </div>

                {csvExportError && (
                  <div className="csv-export-error">
                    <AlertCircle size={14} />
                    {csvExportError}
                  </div>
                )}

                <button
                  onClick={handleExportCSV}
                  disabled={exportingCSV}
                  className="btn btn-facebook csv-export-btn"
                >
                  {exportingCSV ? (
                    <>
                      <Loader className="spin" size={16} />
                      Export en cours...
                    </>
                  ) : (
                    <>
                      <Download size={16} />
                      Télécharger CSV
                    </>
                  )}
                </button>
              </div>
            )}
          </div>
        </div>
        </div>
      )}

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {/* Pop-in de chargement pour l'analyse */}
      {loading && (
        <div className="modal-overlay ai-loading-overlay">
          <div className="ai-loading-content">
            <div className="ai-loading-animation">
              <div className="ai-brain-icon">🤖</div>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <h3>Analyse en cours...</h3>
            <p>Kokpit AI analyse vos données</p>
            <div className="loading-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
              <div className="loading-details">
                <small>
                  Traitement de : {selectedItems.map(item => item.name).join(', ')}
                </small>
                <small>• Analyse personnalisée</small>
                <small>• Estimation : 15-30 secondes</small>
              </div>
            </div>
            <div className="loading-tips">
              <p><Sparkles size={14} />&nbsp;Le saviez-vous ?</p>
              <p className="tip">Kokpit AI analyse vos tendances de performance, identifie les opportunités d'optimisation et fournit des recommandations personnalisées basées sur vos objectifs.</p>
            </div>
          </div>
        </div>
      )}

      {/* Chat Panel pour le streaming ou pour continuer une conversation existante */}
      <ChatPanel
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        analysisResult={analysisResult?.streamingMode ? null : analysisResult}
        accountId={openConversationData?.accountId || analysisResult?.accountId || ''}
        accountName={openConversationData?.accountName || analysisResult?.accountName || ''}
        token={token}
        initialQuery={analysisResult?.query || ''}
        streamingMode={analysisResult?.streamingMode === true}
        libraryItemIds={analysisResult?.libraryItemIds || []}
        onStreamComplete={handleStreamComplete}
        existingConversationId={openConversationData?.conversationId || null}
      />
    </div>
  );
}

// Plans avec leurs limites
const PLANS_LIB = {
  // Nouveaux plans
  SOLO: { name: 'Solo', seats: 1, accounts: 5 },
  TEAM: { name: 'Team', seats: 3, accounts: 25 },
  AGENCY: { name: 'Agency', seats: 10, accounts: 100 },
  SCALE: { name: 'Scale', seats: -1, accounts: -1 },
  // Anciens plans (mappés vers les équivalents)
  FREE: { name: 'Free', seats: 1, accounts: 5 },
  FREEMIUM: { name: 'Freemium', seats: 1, accounts: 5 },
  PRO: { name: 'Pro', seats: 3, accounts: 25 },
  PREMIUM: { name: 'Premium', seats: 10, accounts: 100 },
  CLIENT: { name: 'Client', seats: 1, accounts: 0 },
  CLIENT_ACCESS: { name: 'Client', seats: 1, accounts: 0 }
};

const getPlanInfoLib = (plan) => {
  if (!plan) return PLANS_LIB.SOLO;
  const normalizedPlan = plan.toUpperCase();
  return PLANS_LIB[normalizedPlan] || PLANS_LIB.SOLO;
};

// Composant: Vue Bibliothèque
function LibraryView({ token, setSuccess, setError, inaccessibleAccounts = [], clientFiltre = null, openMerchantCenterOnLoad, setOpenMerchantCenterOnLoad, openSearchConsoleOnLoad, setOpenSearchConsoleOnLoad, openGoogleAnalyticsOnLoad, setOpenGoogleAnalyticsOnLoad, openAddModalOnLoad, setOpenAddModalOnLoad, suggestedMerchantIds = [], setSuggestedMerchantIds, connectors = [], setCurrentView, returnToShoppingAfterMerchantAdd, setReturnToShoppingAfterMerchantAdd, onDataChange, userSubscription, onGenererDashboard = null }) {
  const [library, setLibrary] = useState({});
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [addModalStep, setAddModalStep] = useState('choose');
  const [sites, setSites] = useState([]);
  const [hoveredAccount, setHoveredAccount] = useState(null);
  const [dropdownOpen, setDropdownOpen] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  

  useEffect(() => {
    loadLibrary();
    loadSites();
  }, []);

  // Fermer la dropdown quand on clique ailleurs
  useEffect(() => {
    const handleClickOutside = () => setDropdownOpen(null);
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  useEffect(() => {
    // Si le flag est activé, ouvrir la modal avec Merchant API présélectionné
    if (openMerchantCenterOnLoad && !loading) {
      setShowAddModal(true);
      // Utiliser merchant-api (nouvelle API) au lieu de merchant-center (ancienne)
      setAddModalStep('merchant-api');
      setOpenMerchantCenterOnLoad(false); // Réinitialiser le flag
    }
  }, [openMerchantCenterOnLoad, loading, setOpenMerchantCenterOnLoad]);

  useEffect(() => {
    // Si le flag est activé, ouvrir la modal avec Search Console présélectionné
    if (openSearchConsoleOnLoad && !loading) {
      setShowAddModal(true);
      setAddModalStep('google-search-console');
      setOpenSearchConsoleOnLoad(false); // Réinitialiser le flag
    }
  }, [openSearchConsoleOnLoad, loading, setOpenSearchConsoleOnLoad]);

  useEffect(() => {
    // Si le flag est activé, ouvrir la modal avec Google Analytics présélectionné
    if (openGoogleAnalyticsOnLoad && !loading) {
      setShowAddModal(true);
      setAddModalStep('google-analytics');
      setOpenGoogleAnalyticsOnLoad(false); // Réinitialiser le flag
    }
  }, [openGoogleAnalyticsOnLoad, loading, setOpenGoogleAnalyticsOnLoad]);

  useEffect(() => {
    // Si le flag est activé, ouvrir la modal d'ajout (step 'choose')
    if (openAddModalOnLoad && !loading) {
      setShowAddModal(true);
      setAddModalStep('choose');
      setOpenAddModalOnLoad(false); // Réinitialiser le flag
    }
  }, [openAddModalOnLoad, loading, setOpenAddModalOnLoad]);

  const loadLibrary = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/library`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLibrary(response.data.library);
    } catch (error) {
      console.error('❌ Erreur chargement bibliothèque:', error);
    } finally {
      setLoading(false);
    }
  };

  /**
   * Les comptes dont l'accès a été refusé, indexés par identifiant de régie.
   *
   * L'écran affichait un compte révoqué exactement comme un compte sain : seul son propre
   * dashboard signalait le refus, au moment de l'interroger. On ne pouvait donc pas savoir
   * d'un coup d'œil lesquels ne répondent plus — c'est pourtant ici qu'on vient faire le ménage.
   *
   * Indexé avec et sans tirets : l'écriture d'un identifiant de régie varie selon l'époque de
   * l'import, et la comparer telle quelle raterait un compte sur deux.
   */
  const accesRefuses = useMemo(() => {
    const index = new Map();
    for (const compte of inaccessibleAccounts || []) {
      const brut = String(compte.accountId);
      index.set(brut, compte);
      index.set(brut.replace(/-/g, ''), compte);
    }
    return index;
  }, [inaccessibleAccounts]);

  const loadSites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSites(response.data.sites || []);
    } catch (error) {
      console.error('❌ Erreur chargement sites:', error);
    }
  };

  /**
   * Associe un compte à un site.
   *
   * `remplaces` porte les écritures anciennes que le nouveau format rend caduques — un numéro
   * de site nu, écrit avant que le rattachement porte la connexion. Les laisser à côté
   * garderait pour toujours une ambiguïté que ce geste vient précisément de lever, puisque
   * l'utilisateur déclare ici quelle instance ce numéro désignait.
   */
  const associateAccountToSite = async (accountId, siteId, remplaces = []) => {
    try {
      const siteToUpdate = sites.find(site => site.id === siteId);
      if (!siteToUpdate) return;

      const aRetirer = new Set(remplaces.map(String));
      const updatedAccountIds = (siteToUpdate.accountIds || []).filter(id => !aRetirer.has(String(id)));
      if (!updatedAccountIds.includes(accountId)) {
        updatedAccountIds.push(accountId);
      }

      await axios.put(`${API_URL}/api/sites/${siteId}`, {
        ...siteToUpdate,
        accountIds: updatedAccountIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(`Compte associé au site ${siteToUpdate.name}`);
      setTimeout(() => setSuccess(''), 3000);
      loadSites(); // Recharger les sites pour mettre à jour l'état
    } catch (error) {
      console.error('❌ Erreur association compte-site:', error);
      setError('Erreur lors de l\'association du compte au site');
    }
  };

  /** `remplaces` : mêmes écritures anciennes, à retirer en même temps que le format récent. */
  const dissociateAccountFromSite = async (accountId, siteId, remplaces = []) => {
    try {
      const siteToUpdate = sites.find(site => site.id === siteId);
      if (!siteToUpdate) return;

      const aRetirer = new Set([String(accountId), ...remplaces.map(String)]);
      const updatedAccountIds = (siteToUpdate.accountIds || []).filter(id => !aRetirer.has(String(id)));

      await axios.put(`${API_URL}/api/sites/${siteId}`, {
        ...siteToUpdate,
        accountIds: updatedAccountIds
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setSuccess(`Compte dissocié du site ${siteToUpdate.name}`);
      setTimeout(() => setSuccess(''), 3000);
      loadSites(); // Recharger les sites pour mettre à jour l'état
    } catch (error) {
      console.error('❌ Erreur dissociation compte-site:', error);
      setError('Erreur lors de la dissociation du compte du site');
    }
  };

  const handleDeleteItem = async (itemId) => {
    if (!window.confirm('Supprimer cet élément de la bibliothèque ?')) return;

    try {
      await axios.delete(`${API_URL}/api/library/${itemId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSuccess('Élément supprimé avec succès');
      loadLibrary();
    } catch (error) {
      console.error('❌ Erreur suppression:', error);
      
      if (error.response?.status === 404) {
        setError('Cet élément a déjà été supprimé ou n\'existe plus');
        // Recharger la bibliothèque pour mettre à jour l'affichage
        loadLibrary();
      } else if (error.response?.status === 401) {
        setError('Vous n\'êtes pas autorisé à supprimer cet élément');
      } else {
        setError('Erreur lors de la suppression de l\'élément. Veuillez réessayer.');
      }
    }
  };


  if (loading) {
    return (
      <div className="step-content">
        <div className="loading-state">
          <Loader className="spin" size={24} />
          <p>Chargement de votre bibliothèque...</p>
        </div>
      </div>
    );
  }

  /**
   * Tous les comptes de la bibliothèque, à plat et portant leur plateforme.
   *
   * ⚠️ **Les résolveurs se bâtissent sur cette liste entière**, et non plateforme par
   * plateforme : c'est la comparaison entre instances qui dit si un numéro de site nu est
   * ambigu. Et ils couvrent Matomo **et Piano** — l'écran n'en connaissait qu'un, si bien
   * qu'un compte Piano rattaché sous sa forme qualifiée s'affichait « Aucun site associé »
   * alors que le Gestionnaire le montrait rattaché.
   */
  const comptesAPlat = Object.entries(library).flatMap(([provider, items]) =>
    (items || []).map(item => ({ ...item, provider }))
  );
  const resolveursDeSites = resolveursDeRattachement(comptesAPlat);

  /**
   * Une ligne de liste par compte, toutes plateformes confondues.
   *
   * Le groupement par fournisseur disparaît au profit d'une colonne « Plateforme » : il coûtait
   * un intertitre par plateforme pour une information qui tient dans une cellule, et interdisait
   * tout tri transversal — voir d'un coup les comptes sans site, par exemple.
   *
   * Tout ce qui relève du **rattachement** est résolu ici et non dans la liste : les trois
   * écritures d'un identifiant, la forme qualifiée des sites Matomo, l'index des accès refusés.
   * Le composant d'affichage reçoit des lignes prêtes ; lui faire refaire ce calcul créerait une
   * seconde vérité, et c'est exactement ce genre de divergence qui faisait afficher « Aucun site
   * associé » sur un compte que le Gestionnaire montrait rattaché.
   */
  const LOGOS_PLATEFORME = {
    GOOGLE_ADS: googleAdsLogo,
    MICROSOFT_ADS: microsoftAdsLogo,
    FACEBOOK_ADS: metaAdsLogo,
    PINTEREST_ADS: pinterestAdsLogo,
    TIKTOK_ADS: tiktokAdsLogo,
    OPENAI_ADS: chatgptAdsLogo,
    AWIN: awinLogo,
    PRESTASHOP: prestashopLogo,
    MATOMO: matomoLogo,
    // Même instance, authentification différente : un connecteur OAuth doit s'afficher
    // comme un connecteur Matomo, pas comme un fournisseur inconnu sans logo.
    MATOMO_OAUTH: matomoLogo,
    PIANO_ANALYTICS: pianoLogo,
    // Le SVG local, et non le CDN qu'employaient les anciennes cartes : une icône servie par un
    // tiers manque dès qu'il tombe, et la ligne retombait sur l'icône générique de base de
    // données — au point qu'une propriété GA4 ne se distinguait plus d'un Google Sheets.
    GOOGLE_ANALYTICS: googleAnalyticsLogo,
    GOOGLE_SEARCH_CONSOLE: searchConsoleLogo,
    GOOGLE_TAG_MANAGER: tagManagerLogo,
    GOOGLE_MERCHANT_API: merchantLogo
  };

  const NOMS_PLATEFORME = {
    GOOGLE_ADS: 'Google Ads',
    MICROSOFT_ADS: 'Microsoft Ads',
    FACEBOOK_ADS: 'Meta Ads',
    PINTEREST_ADS: 'Pinterest Ads',
    TIKTOK_ADS: 'TikTok Ads',
    OPENAI_ADS: 'ChatGPT Ads',
    AWIN: 'Awin',
    PRESTASHOP: 'PrestaShop',
    MATOMO: 'Matomo',
    MATOMO_OAUTH: 'Matomo',
    PIANO_ANALYTICS: 'Piano Analytics',
    GOOGLE_ANALYTICS: 'Google Analytics 4',
    GOOGLE_SEARCH_CONSOLE: 'Search Console',
    GOOGLE_TAG_MANAGER: 'Tag Manager',
    GOOGLE_MERCHANT_API: 'Merchant Center',
    GOOGLE_SHEETS: 'Google Sheets',
    OPENWEATHERMAP: 'OpenWeatherMap',
    OPEN_METEO: 'Open-Meteo'
  };

  /**
   * Seules les sources **globales** échappent au rattachement à un site : la météo décrit une
   * ville, pas le compte d'un client. On énumère l'exception plutôt que la règle — une liste
   * blanche oublie chaque nouveau connecteur, en silence, et le défaut ne se voit qu'à l'usage.
   */
  const SOURCES_GLOBALES = ['OPENWEATHERMAP', 'OPEN_METEO'];

  /**
   * Le résolveur Matomo se construit sur **tous** les sites Matomo du parc : c'est la comparaison
   * entre instances qui dit si un numéro nu est ambigu. Le bâtir par instance conclurait
   * « unique » sur chacune d'elles.
   */
  const toutesLesLignes = comptesAPlat.map(compte => {
    const { provider } = compte;
    const estMatomo = provider === 'MATOMO';
    const resolveur = resolveursDeSites[provider] || null;
    const identites = ensembleAutorise(identitesDuCompte(compte));

    /*
     * ⚠️ Une seule règle de rattachement, partagée avec le Gestionnaire
     * (`utils/perimetreClient.js`). Elle décide à la fois de ce qu'on affiche — « Sites
     * associés » — et de ce que le client filtré laisse voir : deux lectures différentes
     * feraient dire à cet écran l'inverse de ce que sa propre colonne montre.
     */
    const correspond = predicatDeRattachement(compte, resolveursDeSites);
    const sitesAssocies = sites.filter(site => (site.accountIds || []).some(correspond));

    // Le refus est cherché sur toutes les identités du compte, comme le rattachement :
    // l'index porte les deux écritures d'un identifiant de régie.
    const refus = [...identites].map(i => accesRefuses.get(i)).find(Boolean) || null;

    return {
      cle: resolveur ? `${compte.connectionId || 'default'}-${compte.id}` : compte.id,
      item: compte,
      provider,
      logo: LOGOS_PLATEFORME[provider] || null,
      plateforme: NOMS_PLATEFORME[provider]
        || provider.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
      // Le sous-titre dit ce qui distingue deux lignes voisines : l'instance pour Matomo,
      // l'identifiant du compte ailleurs.
      qualificatif: estMatomo
        ? (compte.connectionName || compte.metadata?.instanceUrl || null)
        : (compte.displaySourceId || compte.sourceId || null),
      sitesAssocies,
      refus,
      usagesRestreints: compte.actifDashboards === false
        || compte.actifOutils === false
        || compte.actifMcp === false,
      rattachable: !SOURCES_GLOBALES.includes(provider),
      // L'identifiant à écrire dans le site, selon la plateforme.
      identifiantDAssociation: resolveur
        ? resolveur.identifiantDe(compte)
        : (compte.databaseId || compte.libraryId || compte.sourceId || compte.id),
      // Ce qui lie réellement ce compte à ce site, quelle que soit l'écriture employée à
      // l'époque : n'en retirer qu'une laisserait le rattachement en place. Même prédicat
      // que la lecture — sinon on afficherait un rattachement qu'on ne sait pas défaire.
      identitesPourRetrait: (site) => (site.accountIds || []).filter(correspond)
    };
  });

  /*
   * Le client choisi dans la barre du haut borne la liste **avant** la recherche.
   *
   * Un compte lui appartient dès qu'un de ses sites associés est rattaché à ce client — le
   * rapprochement est déjà fait ligne par ligne, et c'est exactement ce que la colonne
   * « Sites associés » montre.
   */
  const lignesDuPerimetre = clientFiltre
    ? toutesLesLignes.filter(ligne => ligne.sitesAssocies.some(site => site.clientId === clientFiltre))
    : toutesLesLignes;

  // La recherche porte sur ce qui s'affiche, le libellé de la plateforme compris : personne
  // ne tape « OPENAI_ADS », on tape « chat ».
  const requete = searchQuery.trim().toLowerCase();
  const lignesComptes = !requete ? lignesDuPerimetre : lignesDuPerimetre.filter(ligne =>
    ligne.item.name?.toLowerCase().includes(requete)
    || ligne.item.sourceName?.toLowerCase().includes(requete)
    || ligne.item.sourceId?.toLowerCase().includes(requete)
    || (libellePlateforme(ligne.provider) || ligne.provider || '').toLowerCase().includes(requete)
    || ligne.plateforme.toLowerCase().includes(requete)
  );

  const totalComptes = comptesAPlat.length;
  const totalDuPerimetre = lignesDuPerimetre.length;
  const planInfo = getPlanInfoLib(userSubscription?.plan);
  const maxComptes = planInfo.accounts;
  /* Le quota se compte sur tout le compte Kokpit, jamais sur un client : sous un filtre, la
     pastille dit combien de comptes ce client possède, et n'affiche plus le plafond. */
  const quotaAtteint = !clientFiltre && maxComptes !== -1 && totalComptes >= maxComptes;

  return (
    <div className="step-content kc-page">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span><Package size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} /> Comptes</span>
          {/* La pastille passe au rouge quand le quota est atteint : c'est à ce moment-là
              qu'elle cesse d'être une information pour devenir un obstacle. */}
          <span
            className="kok-compteur"
            style={quotaAtteint ? { background: '#fef2f2', borderColor: '#fecaca', color: '#dc2626' } : undefined}
          >
            {lignesComptes.length !== totalDuPerimetre
              ? `${lignesComptes.length} sur ${totalDuPerimetre}`
              : clientFiltre
                ? `${totalDuPerimetre} compte${totalDuPerimetre > 1 ? 's' : ''}`
                : `${totalComptes} / ${maxComptes === -1 ? '∞' : maxComptes}`}
          </span>
        </h2>

        {totalComptes > 0 && (
          <div className="kc-recherche">
            <Search size={16} />
            <input
              type="text"
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            {searchQuery && (
              <button type="button" className="kc-recherche-effacer" onClick={() => setSearchQuery('')} title="Effacer">
                <X size={14} />
              </button>
            )}
          </div>
        )}

        <button
          onClick={() => setShowAddModal(true)}
          className="btn btn-primary btn-small"
          style={{ marginLeft: 'auto', backgroundColor: '#111827', borderColor: '#111827' }}
        >
          + Ajouter un compte
        </button>
      </div>

      <ListeComptes
        lignes={lignesComptes}
        sites={sites}
        token={token}
        onRafraichir={loadLibrary}
        onSupprimer={handleDeleteItem}
        onAssocier={(ligne, site) => associateAccountToSite(ligne.identifiantDAssociation, site.id)}
        onDissocier={(ligne, site) => dissociateAccountFromSite(
          ligne.identifiantDAssociation, site.id, ligne.identitesPourRetrait(site)
        )}
        onGenererDashboard={onGenererDashboard}
      />

      {showAddModal && (
        <AddDataModal
          token={token}
          onClose={() => {
            setShowAddModal(false);
            setAddModalStep('choose'); // Réinitialiser à 'choose' après fermeture
            setSuggestedMerchantIds && setSuggestedMerchantIds([]); // Réinitialiser les suggestions
            setReturnToShoppingAfterMerchantAdd && setReturnToShoppingAfterMerchantAdd(null); // Réinitialiser le retour au shopping
          }}
          onAdded={async () => {
            loadLibrary();
            setShowAddModal(false);
            setAddModalStep('choose'); // Réinitialiser à 'choose' après ajout
            setSuggestedMerchantIds && setSuggestedMerchantIds([]); // Réinitialiser les suggestions

            // Notifier le parent pour rafraîchir les sources disponibles (Dashboard)
            if (onDataChange) {
              // Petit délai pour laisser le backend finaliser
              await new Promise(resolve => setTimeout(resolve, 300));
              onDataChange();
            }

            // Si on doit revenir au shopping dashboard après ajout d'un Merchant Center
            if (returnToShoppingAfterMerchantAdd) {
              const sourceId = returnToShoppingAfterMerchantAdd;
              setReturnToShoppingAfterMerchantAdd && setReturnToShoppingAfterMerchantAdd(null);
              // Naviguer vers le dashboard et déclencher le changement de modèle vers shopping
              setCurrentView('dashboard');
              // Émettre les événements pour configurer le dashboard shopping et recharger les données
              setTimeout(() => {
                window.dispatchEvent(new CustomEvent('dashboardTypeChange', { detail: { type: 'google' } }));
                window.dispatchEvent(new CustomEvent('dashboardModelChange', { detail: { model: 'shopping' } }));
                window.dispatchEvent(new CustomEvent('reloadShoppingDashboard', {
                  detail: { sourceId }
                }));
              }, 100);
            }
          }}
          setSuccess={setSuccess}
          setError={setError}
          initialStep={addModalStep}
          connectors={connectors}
          suggestedMerchantIds={suggestedMerchantIds}
        />
      )}

    </div>
  );
}

// Composant: Modal d'ajout de données
function AddDataModal({ token, onClose, onAdded, setSuccess, setError, initialStep = 'choose', connectors = [], suggestedMerchantIds = [] }) {
  const [step, setStep] = useState(initialStep); // choose, sheets, ads, microsoft-ads, merchant-center

  // Vérifier quels connecteurs sont actifs
  const hasGoogleSheets = connectors.some(c => c.provider === 'GOOGLE_SHEETS');
  const hasGoogleAds = connectors.some(c => c.provider === 'GOOGLE_ADS');
  const hasMicrosoftAds = connectors.some(c => c.provider === 'MICROSOFT_ADS');
  const hasGoogleMerchantApi = connectors.some(c => c.provider === 'GOOGLE_MERCHANT_API');
  const hasFacebookAds = connectors.some(c => c.provider === 'FACEBOOK_ADS');
  const hasPinterestAds = connectors.some(c => c.provider === 'PINTEREST_ADS');
  const hasTikTokAds = connectors.some(c => c.provider === 'TIKTOK_ADS');
  const hasGoogleAnalytics = connectors.some(c => c.provider === 'GOOGLE_ANALYTICS');
  const hasGoogleSearchConsole = connectors.some(c => c.provider === 'GOOGLE_SEARCH_CONSOLE');
  // Servi par `SelecteurGenerique`, qui passe par la route commune
  // `/api/connectors/:id/accounts` plutôt que par une route taillée pour la plateforme —
  // voir `PLATEFORMES_GENERIQUES`.
  const hasOpenAiAds = connectors.some(c => c.provider === 'OPENAI_ADS');
  const hasGoogleTagManager = connectors.some(c => c.provider === 'GOOGLE_TAG_MANAGER');

  return ReactDOM.createPortal(
    <div className="modal-overlay" onClick={onClose} style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 9999
    }}>
      <div className="modal-content" onClick={e => e.stopPropagation()} style={{
        backgroundColor: 'white',
        borderRadius: '12px',
        padding: '0',
        maxWidth: '900px', // Élargi de ~600px à 900px
        width: '90vw',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)'
      }}>
        <div className="modal-header" style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '20px 24px',
          borderBottom: '1px solid #e5e7eb',
          margin: 0
        }}>
          <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: '600', color: '#111827' }}>Ajouter des données</h3>
          <button onClick={onClose} className="btn-close" style={{
            background: 'none',
            border: 'none',
            fontSize: '24px',
            cursor: 'pointer',
            color: '#6b7280',
            padding: '4px',
            borderRadius: '4px'
          }}>×</button>
        </div>

        <div className="modal-body" style={{
          padding: '24px',
          minHeight: '400px',
          overflowX: 'hidden',
          borderRadius: '0 0 15px 15px'
        }}>
          {step === 'choose' && (
            <div className="data-source-choice" style={{ textAlign: 'center' }}>
              <h4 style={{ fontSize: '1.25rem', fontWeight: '500', color: '#374151', marginBottom: '32px' }}>Choisissez votre source de données :</h4>
              
              <div className="source-options" style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                gap: '16px',
                maxWidth: '800px',
                margin: '0 auto'
              }}>
                {hasGoogleSheets && (
                  <button 
                    onClick={() => setStep('sheets')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px',
                      ':hover': {
                        borderColor: '#1a1a1a',
                        backgroundColor: '#f8f5f0'
                      }
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <FileText size={40} style={{ color: '#1a1a1a', marginBottom: '12px' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Google Sheets</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Fichiers et tableaux de calcul</p>
                  </button>
                )}

                {hasGoogleAds && (
                  <button 
                    onClick={() => setStep('ads')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <img src={googleAdsLogo} alt="Google Ads" style={{ width: '40px', height: '40px', marginBottom: '12px' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Google Ads</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Comptes publicitaires Google</p>
                  </button>
                )}

                {hasMicrosoftAds && (
                  <button 
                    onClick={() => setStep('microsoft-ads')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <img src={microsoftAdsLogo} alt="Microsoft Ads" style={{ width: '40px', height: '40px', marginBottom: '12px' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Microsoft Ads</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Comptes publicitaires Microsoft</p>
                  </button>
                )}

                {hasGoogleMerchantApi && (
                  <button
                    onClick={() => setStep('merchant-api')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <ShoppingCart size={40} style={{ color: '#ea4335', marginBottom: '12px' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Merchant Center</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Comptes Google Merchant Center</p>
                  </button>
                )}

                {hasFacebookAds && (
                  <button
                    onClick={() => setStep('facebook-ads')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <img src={metaAdsLogo} alt="Meta Ads" style={{ width: '40px', height: '40px', marginBottom: '12px', objectFit: 'contain' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Meta Ads</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Comptes publicitaires Facebook</p>
                  </button>
                )}

                {hasPinterestAds && (
                  <button
                    onClick={() => setStep('pinterest-ads')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <img src={pinterestAdsLogo} alt="Pinterest Ads" style={{ width: '40px', height: '40px', marginBottom: '12px', objectFit: 'contain' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Pinterest Ads</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Comptes publicitaires Pinterest</p>
                  </button>
                )}

                {hasTikTokAds && (
                  <button
                    onClick={() => setStep('tiktok-ads')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <img src={tiktokAdsLogo} alt="TikTok Ads" style={{ width: '40px', height: '40px', marginBottom: '12px', objectFit: 'contain' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>TikTok Ads</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Comptes publicitaires TikTok</p>
                  </button>
                )}

                {hasGoogleAnalytics && (
                  <button
                    onClick={() => setStep('google-analytics')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png" alt="Google Analytics 4" style={{ width: '40px', height: '40px', marginBottom: '12px', borderRadius: '4px' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Google Analytics 4</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Propriétés Google Analytics</p>
                  </button>
                )}

                {hasOpenAiAds && (
                  <button
                    onClick={() => setStep('openai-ads')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <img src={chatgptAdsLogo} alt="ChatGPT Ads" style={{ width: '40px', height: '40px', marginBottom: '12px', objectFit: 'contain' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>ChatGPT Ads</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Comptes publicitaires ChatGPT</p>
                  </button>
                )}

                {hasGoogleSearchConsole && (
                  <button
                    onClick={() => setStep('google-search-console')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <img src={searchConsoleLogo} alt="Google Search Console" style={{ width: '40px', height: '40px', marginBottom: '12px', borderRadius: '4px' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Search Console</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Sites Google Search Console</p>
                  </button>
                )}

                {hasGoogleTagManager && (
                  <button
                    onClick={() => setStep('google-tag-manager')}
                    className="source-option"
                    style={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      padding: '20px',
                      border: '2px solid #e5e7eb',
                      borderRadius: '12px',
                      backgroundColor: 'white',
                      cursor: 'pointer',
                      transition: 'all 0.2s ease',
                      textAlign: 'center',
                      minHeight: '140px'
                    }}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.borderColor = '#1a1a1a';
                      e.currentTarget.style.backgroundColor = '#f8f5f0';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.borderColor = '#e5e7eb';
                      e.currentTarget.style.backgroundColor = 'white';
                    }}
                  >
                    <img src={tagManagerLogo} alt="Google Tag Manager" style={{ width: '40px', height: '40px', marginBottom: '12px', objectFit: 'contain' }} />
                    <span style={{ fontSize: '1.1rem', fontWeight: '600', color: '#111827', marginBottom: '8px' }}>Tag Manager</span>
                    <p style={{ fontSize: '0.9rem', color: '#6b7280', margin: 0, lineHeight: '1.4' }}>Conteneurs Google Tag Manager</p>
                  </button>
                )}
              </div>
            </div>
          )}

          {step === 'sheets' && (
            <GoogleSheetsSelector 
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
            />
          )}

          {step === 'ads' && (
            <GoogleAdsSelector 
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}

          {step === 'microsoft-ads' && (
            <MicrosoftAdsSelector 
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}

          {step === 'merchant-api' && (
            <MerchantApiSelector
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
              suggestedMerchantIds={suggestedMerchantIds}
            />
          )}

          {step === 'facebook-ads' && (
            <FacebookAdsSelector
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}

          {step === 'pinterest-ads' && (
            <PinterestAdsSelector
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}

          {step === 'tiktok-ads' && (
            <TikTokAdsSelector
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}

          {step === 'google-analytics' && (
            <GoogleAnalyticsSelector
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}

          {step === 'openai-ads' && (
            <SelecteurGenerique
              provider="OPENAI_ADS"
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}

          {step === 'google-search-console' && (
            <GoogleSearchConsoleSelector
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}

          {/*
            * Servi par `SelecteurGenerique` et non par un sélecteur taillé pour la plateforme :
            * la hiérarchie compte GTM → conteneur est aplatie côté backend, une ligne par
            * conteneur, et le compte voyage dans la ligne. Voir `PLATEFORMES_GENERIQUES`.
            */}
          {step === 'google-tag-manager' && (
            <SelecteurGenerique
              provider="GOOGLE_TAG_MANAGER"
              token={token}
              onAdded={onAdded}
              onBack={() => setStep('choose')}
              setSuccess={setSuccess}
              setError={setError}
            />
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}

// Composant: Sélecteur Google Search Console
function GoogleSearchConsoleSelector({ token, onAdded, onBack, setSuccess, setError }) {
  const [sites, setSites] = useState([]);
  const [selectedSites, setSelectedSites] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadSites();
  }, []);

  // Filtrer les sites selon le terme de recherche
  const filteredSites = sites.filter(site => {
    const search = searchTerm.toLowerCase();
    return site.siteUrl?.toLowerCase().includes(search);
  });

  const loadSites = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/library/google-search-console`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setSites(response.data.sites || []);
      }
    } catch (error) {
      console.error('Erreur chargement sites GSC:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleSiteSelection = (site) => {
    setSelectedSites(prev => {
      const isSelected = prev.some(s => s.siteUrl === site.siteUrl);
      if (isSelected) {
        return prev.filter(s => s.siteUrl !== site.siteUrl);
      } else {
        return [...prev, site];
      }
    });
  };

  const selectAllSites = () => {
    setSelectedSites([...filteredSites]);
  };

  const deselectAllSites = () => {
    setSelectedSites([]);
  };

  const isSiteSelected = (site) => {
    return selectedSites.some(s => s.siteUrl === site.siteUrl);
  };

  const saveToLibrary = async () => {
    if (selectedSites.length === 0) return;

    setSaving(true);
    setSavingProgress({ current: 0, total: selectedSites.length });

    let successCount = 0;
    let errors = [];

    try {
      const connectorsResponse = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const gscConnection = connectorsResponse.data.connectors.find(c =>
        c.provider === 'GOOGLE_SEARCH_CONSOLE' && c.isActive
      );

      if (!gscConnection) {
        throw new Error('Connexion Google Search Console non trouvée');
      }

      for (let i = 0; i < selectedSites.length; i++) {
        const site = selectedSites[i];
        setSavingProgress({ current: i + 1, total: selectedSites.length });

        try {
          await axios.post(`${API_URL}/api/library/add`, {
            type: 'GOOGLE_SEARCH_CONSOLE',
            name: site.siteUrl,
            sourceId: site.siteUrl,
            sourceName: site.siteUrl,
            connectionId: gscConnection.id,
            metadata: {
              siteUrl: site.siteUrl,
              permissionLevel: site.permissionLevel
            }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          successCount++;
        } catch (siteError) {
          console.error(`Erreur ajout site ${site.siteUrl}:`, siteError);
          errors.push(`${site.siteUrl}: ${siteError.response?.data?.error || siteError.message}`);
        }
      }

      if (successCount > 0) {
        const message = successCount === selectedSites.length
          ? `${successCount} site(s) ajouté(s) avec succès !`
          : `${successCount}/${selectedSites.length} site(s) ajouté(s).`;

        setSuccess(message);
        setTimeout(() => setSuccess(''), 5000);

        onAdded();
      } else {
        setError(`Aucun site n'a pu être ajouté. Erreurs: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Erreur sauvegarde GSC:', error);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setSavingProgress({ current: 0, total: 0 });
    }
  };

  if (saving) {
    return (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        width: '100vw',
        height: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: '#ffffff',
        zIndex: 9999
      }}>
        <div className="saving-content-fullscreen">
          <div className="saving-spinner">
            <Loader className="spin" size={48} />
          </div>
          <h3>Ajout en cours...</h3>
          <p>Ajout de <strong>{selectedSites.length} site(s)</strong> à votre bibliothèque</p>
          <div className="saving-progress-fullscreen">
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">1</span>
              <span className="step-text-fullscreen">Vérification de la connexion</span>
            </div>
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">2</span>
              <span className="step-text-fullscreen">Enregistrement du site ({savingProgress.current}/{savingProgress.total})</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-selector">
      <div className="selector-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={onBack} className="btn btn-secondary" style={{ backgroundColor: '#f8f5f0', color: '#1a1a1a', border: '1px solid #e5e0d5' }}>← Retour</button>
          <h4 style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            Sélectionner des sites Search Console
          </h4>
        </div>
        <button
          onClick={() => selectedSites.length > 0 && saveToLibrary()}
          className="btn btn-primary"
          disabled={selectedSites.length === 0}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          + Ajouter {selectedSites.length > 0 ? `(${selectedSites.length})` : ''}
        </button>
      </div>

      {loading ? (
        <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
          <p>Chargement des sites Search Console...</p>
        </div>
      ) : sites.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
          <p>Aucun site Search Console disponible.</p>
          <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            Vérifiez que votre compte Google a accès à Google Search Console.
          </p>
        </div>
      ) : (
        <>
          <div className="search-bar" style={{ padding: '16px 0', display: 'flex', alignItems: 'center', gap: '12px' }}>
            <input
              type="text"
              placeholder="Rechercher un site..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                flex: 1,
                padding: '10px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
            <button onClick={selectAllSites} className="btn btn-secondary btn-small">
              Tout sélectionner
            </button>
            <button onClick={deselectAllSites} className="btn btn-secondary btn-small">
              Tout désélectionner
            </button>
          </div>

          <div className="sites-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '4px'
          }}>
            {filteredSites.map((site) => (
              <div
                key={site.siteUrl}
                onClick={() => toggleSiteSelection(site)}
                style={{
                  padding: '16px',
                  border: `2px solid ${isSiteSelected(site) ? '#10B981' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  backgroundColor: isSiteSelected(site) ? '#f0fdf4' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    border: `2px solid ${isSiteSelected(site) ? '#10B981' : '#d1d5db'}`,
                    backgroundColor: isSiteSelected(site) ? '#10B981' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {isSiteSelected(site) && <CheckCircle size={14} style={{ color: 'white' }} />}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: '600', color: '#111827', wordBreak: 'break-all' }}>{site.siteUrl}</div>
                    {site.permissionLevel && (
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>{site.permissionLevel}</div>
                    )}
                  </div>
                  <Globe size={18} style={{ color: '#9ca3af', flexShrink: 0 }} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Composant: Sélecteur Google Sheets
function GoogleSheetsSelector({ token, onAdded, onBack }) {
  const [sheets, setSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [tabs, setTabs] = useState([]);
  const [selectedTab, setSelectedTab] = useState(null);
  const [preview, setPreview] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSheets();
  }, [searchQuery]);

  const loadSheets = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/library/sheets`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { query: searchQuery }
      });
      setSheets(response.data.sheets);
    } catch (error) {
      console.error('❌ Erreur chargement sheets:', error);
    } finally {
      setLoading(false);
    }
  };

  const selectSheet = async (sheet) => {
    setSelectedSheet(sheet);
    setTabs([]);
    setSelectedTab(null);
    setPreview(null);

    try {
      const response = await axios.get(`${API_URL}/api/library/sheets/${sheet.id}/tabs`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTabs(response.data.tabs);
    } catch (error) {
      console.error('❌ Erreur chargement onglets:', error);
    }
  };

  const selectTab = async (tab) => {
    setSelectedTab(tab);
    setPreview(null);

    try {
      const response = await axios.get(`${API_URL}/api/library/sheets/${selectedSheet.id}/preview`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { sheetName: tab.title }
      });
      setPreview(response.data.preview);
    } catch (error) {
      console.error('❌ Erreur prévisualisation:', error);
    }
  };

  const saveToLibrary = async () => {
    if (!selectedSheet || !selectedTab) return;

    setSaving(true);
    try {
      // D'abord récupérer la connexion Google Sheets
      const connectionsResponse = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const googleSheetsConnection = connectionsResponse.data.connectors.find(
        conn => conn.provider === 'GOOGLE_SHEETS'
      );

      if (!googleSheetsConnection) {
        throw new Error('Connexion Google Sheets requise');
      }

      await axios.post(`${API_URL}/api/library/add`, {
        type: 'GOOGLE_SHEETS',
        name: `${selectedSheet.name} - ${selectedTab.title}`,
        sourceId: selectedSheet.id,
        sourceName: selectedSheet.name,
        connectionId: googleSheetsConnection.id,
        metadata: {
          sheetName: selectedTab.title,
          rowCount: selectedTab.rowCount,
          columnCount: selectedTab.columnCount,
          headers: preview?.headers || []
        }
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      onAdded();
    } catch (error) {
      console.error('❌ Erreur sauvegarde:', error);
      alert('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="sheets-selector" style={{ borderRadius: '8px', border: '1px solid #e1e5e9', background: 'white', padding: '20px', overflow: 'hidden' }}>
      <div className="selector-header">
        <button onClick={onBack} className="btn btn-secondary" style={{ backgroundColor: '#f8f5f0', color: '#1a1a1a', border: '1px solid #e5e0d5' }}>← Retour</button>
        <h4>Sélectionner un Google Sheet</h4>
      </div>

      {/* Étape 1: Recherche et sélection du fichier */}
      <div className="search-section">
        <div className="search-input" style={{ maxWidth: '100%', width: '100%', boxSizing: 'border-box', border: '1px solid #ddd', borderRadius: '8px', padding: '10px 15px', display: 'flex', alignItems: 'center', gap: '10px', background: 'white' }}>
          <Search size={16} />
          <input
            type="text"
            placeholder="Rechercher un fichier..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            style={{ width: '100%', boxSizing: 'border-box', border: 'none', outline: 'none', flex: '1', background: 'transparent', fontSize: '14px' }}
          />
        </div>

        {loading ? (
          <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
            <Loader className="spin" size={32} />
            <p>Chargement des fichiers...</p>
          </div>
        ) : sheets.length === 0 ? (
          <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
            <p>Aucun fichier Google Sheets trouvé.</p>
            <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
              Vérifiez que votre compte Google a accès à des fichiers Sheets.
            </p>
          </div>
        ) : (
          <div className="accounts-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px',
            maxHeight: '300px',
            overflowY: 'auto',
            padding: '4px',
            marginTop: '16px'
          }}>
            {sheets.map(sheet => (
              <div
                key={sheet.id}
                onClick={() => selectSheet(sheet)}
                style={{
                  padding: '16px',
                  border: `2px solid ${selectedSheet?.id === sheet.id ? '#34A853' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  backgroundColor: selectedSheet?.id === sheet.id ? '#e6f4ea' : 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    border: `2px solid ${selectedSheet?.id === sheet.id ? '#34A853' : '#d1d5db'}`,
                    backgroundColor: selectedSheet?.id === sheet.id ? '#34A853' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {selectedSheet?.id === sheet.id && <CheckCircle size={14} style={{ color: 'white' }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: '#111827' }}>{sheet.name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>
                      Modifié le {new Date(sheet.modifiedTime).toLocaleDateString()}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Étape 2: Sélection de l'onglet */}
      {selectedSheet && tabs.length > 0 && (
        <div className="tabs-section">
          <h5>Choisissez un onglet :</h5>
          <div className="tabs-list">
            {tabs.map(tab => (
              <div 
                key={tab.id}
                className={`tab-item ${selectedTab?.id === tab.id ? 'selected' : ''}`}
                onClick={() => selectTab(tab)}
              >
                <span className="tab-name">{tab.title}</span>
                <span className="tab-size">{tab.rowCount} lignes</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Étape 3: Prévisualisation */}
      {preview && (
        <div className="preview-section">
          <h5>Aperçu des données :</h5>
          <div style={{ overflowX: 'hidden', marginBottom: '20px' }}>
            <table style={{ tableLayout: 'fixed', width: '100%', borderCollapse: 'collapse', background: 'white', borderRadius: '8px', overflow: 'hidden', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
              <thead>
                <tr>
                  {preview.headers.map(header => (
                    <th key={header} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '12px 15px', textAlign: 'left', borderBottom: '1px solid #f1f3f4', background: '#4CAF50', color: 'white', fontWeight: '600' }}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {preview.sampleData.slice(0, 3).map((row, index) => (
                  <tr key={index}>
                    {preview.headers.map(header => (
                      <td key={header} style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', padding: '12px 15px', textAlign: 'left', borderBottom: '1px solid #f1f3f4', color: '#333' }}>{row[header] || ''}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="preview-actions">
            <button 
              onClick={saveToLibrary}
              disabled={saving}
              className="btn btn-primary"
            >
              {saving ? <Loader className="spin" size={16} /> : '+'}
              {saving ? 'Sauvegarde...' : 'Ajouter à la bibliothèque'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant: Sélecteur Google Ads
function GoogleAdsSelector({ token, onAdded, onBack, setSuccess, setError }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    setError(null); // Effacer les erreurs précédentes avant de charger
    try {
      console.log('🔄 Début du chargement des comptes Google Ads...');
      console.log('📍 API_URL:', API_URL);
      console.log('🔐 Token présent:', !!token);
      
      const response = await axios.get(`${API_URL}/api/library/ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      console.log('✅ Réponse reçue:', response.status);
      console.log('📊 Données reçues:', response.data);
      
      if (response.data.success) {
        setAccounts(response.data.accounts);
        console.log(`✅ ${response.data.accounts.length} comptes Google Ads chargés`);
      } else {
        console.error('❌ Réponse non réussie:', response.data);
        setError(response.data.error || 'Erreur lors du chargement des comptes');
      }
    } catch (error) {
      console.error('❌ Erreur chargement comptes Google Ads:', error);
      console.error('❌ Erreur détaillée:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        headers: error.response?.headers
      });
      
      // Afficher une erreur plus explicite à l'utilisateur
      if (error.response?.status === 401 && error.response?.data?.errorCode === 'TOKEN_EXPIRED') {
        const errorMsg = typeof error.response.data.error === 'string' ? error.response.data.error : 'Token expiré';
        setError(`${errorMsg} Cliquez sur "Connecteurs" pour vous reconnecter.`);
      } else if (error.response?.status === 401) {
        setError('Session expirée. Veuillez vous reconnecter.');
      } else if (error.response?.status === 502) {
        setError('Le serveur est temporairement indisponible. Veuillez réessayer dans quelques instants.');
      } else if (error.response?.status === 404) {
        setError('Endpoint non trouvé. Vérifiez la configuration du serveur.');
      } else if (error.response?.data?.error) {
        // S'assurer que l'erreur est une string
        const errorData = error.response.data.error;
        setError(typeof errorData === 'string' ? errorData : (errorData?.message || 'Erreur inconnue'));
      } else if (error.message === 'Network Error') {
        setError('Erreur réseau. Vérifiez votre connexion internet.');
      } else {
        setError('Erreur lors du chargement des comptes Google Ads');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountSelection = (account) => {
    setSelectedAccounts(prev => {
      const isSelected = prev.some(acc => acc.customerId === account.customerId);
      if (isSelected) {
        return prev.filter(acc => acc.customerId !== account.customerId);
      } else {
        return [...prev, account];
      }
    });
  };

  const selectAllAccounts = () => {
    setSelectedAccounts([...accounts]);
  };

  const deselectAllAccounts = () => {
    setSelectedAccounts([]);
  };

  const isAccountSelected = (account) => {
    return selectedAccounts.some(acc => acc.customerId === account.customerId);
  };

  const saveToLibrary = async () => {
    if (selectedAccounts.length === 0) return;

    setSaving(true);
    setSavingProgress({ current: 0, total: selectedAccounts.length });
    
    let successCount = 0;
    let errors = [];

    try {
      // Récupérer la connexion Google Sheets (réutilisée pour Google Ads)
      const connectionsResponse = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const googleConnection = connectionsResponse.data.connectors.find(
        conn => conn.provider === 'GOOGLE_ADS'
      );

      if (!googleConnection) {
        throw new Error('Connexion Google Ads requise');
      }

      // Traiter chaque compte sélectionné
      for (let i = 0; i < selectedAccounts.length; i++) {
        const account = selectedAccounts[i];
        setSavingProgress({ current: i + 1, total: selectedAccounts.length });
        
        try {
          await axios.post(`${API_URL}/api/library/add`, {
            type: 'GOOGLE_ADS',
            name: `${account.name} (${account.customerId})`,
            sourceId: account.customerId,
            sourceName: account.name,
            connectionId: googleConnection.id,
            metadata: {
              customerId: account.customerId,
              accountName: account.name,
              currency: account.currency,
              timeZone: account.timeZone,
              isTestAccount: account.isTestAccount,
              availableMetrics: preview?.availableMetrics || [],
              totalDays: preview?.totalRows || 30
            }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          successCount++;
        } catch (accountError) {
          console.error(`❌ Erreur ajout compte ${account.name}:`, accountError);
          errors.push(`${account.name}: ${accountError.response?.data?.error || accountError.message}`);
        }
      }

      // Afficher un résumé des résultats
      if (successCount > 0) {
        const message = successCount === selectedAccounts.length 
          ? `${successCount} compte(s) ajouté(s) avec succès !`
          : `${successCount}/${selectedAccounts.length} compte(s) ajouté(s) avec succès.`;
        
        setSuccess(message);
        
        // Masquer automatiquement après 5 secondes
        setTimeout(() => {
          setSuccess('');
        }, 5000);
        
        if (errors.length > 0) {
          console.warn('Erreurs lors de l\'ajout de certains comptes:', errors);
        }
        
        onAdded();
      } else {
        setError(`Aucun compte n'a pu être ajouté. Erreurs: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('❌ Erreur générale sauvegarde Google Ads:', error);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setSavingProgress({ current: 0, total: 0 });
    }
  };


  // Écran d'attente qui occupe toute la pop-in
  if (saving) {
    return (
      <div className="ads-selector saving-fullscreen">
        <div className="saving-content-fullscreen">
          <div className="saving-spinner">
            <Loader className="spin" size={48} />
          </div>
          <h3>Ajout en cours...</h3>
          <p>Ajout de <strong>{selectedAccounts.length + ' compte(s)'}</strong> à votre bibliothèque</p>
          <div className="saving-progress-fullscreen">
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">1</span>
              <span className="step-text-fullscreen">Vérification de la connexion</span>
            </div>
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">2</span>
              <span className="step-text-fullscreen">Enregistrement du compte</span>
            </div>
            <div className="progress-step-fullscreen">
              <span className="step-number-fullscreen">3</span>
              <span className="step-text-fullscreen">Finalisation</span>
            </div>
          </div>
          <p className="saving-note-fullscreen">Veuillez patienter, cette opération peut prendre quelques secondes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-selector">
      <div className="selector-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={onBack} className="btn btn-secondary" style={{ backgroundColor: '#f8f5f0', color: '#1a1a1a', border: '1px solid #e5e0d5' }}>← Retour</button>
          <h4 style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            <img src={googleAdsLogo} alt="Google Ads" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
            Sélectionner un compte Google Ads
          </h4>
        </div>
        <button
          onClick={() => selectedAccounts.length > 0 && saveToLibrary()}
          disabled={selectedAccounts.length === 0 || saving}
          className="btn btn-primary"
          style={{
            fontSize: '14px',
            padding: '10px 20px',
            fontWeight: '600',
            opacity: selectedAccounts.length === 0 ? 0.5 : 1,
            cursor: selectedAccounts.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Ajout en cours...' : `+ Ajouter ${selectedAccounts.length > 0 ? `(${selectedAccounts.length})` : ''}`}
        </button>
      </div>

      {loading ? (
        <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
          <p>Chargement des comptes Google Ads...</p>
        </div>
      ) : accounts.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
          <p>Aucun compte Google Ads disponible.</p>
          <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            Vérifiez que votre compte Google a accès à Google Ads.
          </p>
          <button
            onClick={async () => {
              try {
                console.log('🔍 Exécution du diagnostic Google Ads...');
                const response = await axios.get(`${API_URL}/api/library/ads/debug`, {
                  headers: { Authorization: `Bearer ${token}` }
                });
                console.log('📊 Résultat du diagnostic:', response.data);
                alert(`Diagnostic Google Ads:\n\nStatut: ${response.data.status}\n\nTest de connexion: ${response.data.testResult ?
                  (response.data.testResult.success ?
                    `✅ OK - ${response.data.testResult.accountsFound} compte(s) trouvé(s)` :
                    `❌ Erreur: ${response.data.testResult.error}`) :
                  'Non effectué'}\n\nConsultez la console pour plus de détails.`);
              } catch (error) {
                console.error('❌ Erreur diagnostic:', error);
                alert(`Erreur lors du diagnostic: ${error.message}`);
              }
            }}
            className="btn btn-secondary"
            style={{ marginTop: '10px' }}
          >
            <Search size={14} /> Exécuter le diagnostic
          </button>
        </div>
      ) : (
        <>
          <div className="search-bar" style={{ padding: '16px 0' }}>
            <input
              type="text"
              placeholder="Rechercher un compte..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div className="accounts-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '4px'
          }}>
            {(() => {
              const filteredAccounts = accounts
                .filter(account =>
                  account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  account.customerId?.toString().includes(searchTerm)
                )
                .sort((a, b) => a.name.localeCompare(b.name));

              if (filteredAccounts.length === 0) {
                return (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', gridColumn: '1 / -1' }}>
                    Aucun compte trouvé pour "{searchTerm}"
                  </div>
                );
              }

              return filteredAccounts.map((account, index) => (
                <div
                  key={`${account.customerId}-${index}`}
                  onClick={() => toggleAccountSelection(account)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${isAccountSelected(account) ? '#1a1a1a' : '#e5e7eb'}`,
                    borderRadius: '12px',
                    backgroundColor: isAccountSelected(account) ? '#FAF9F5' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: `2px solid ${isAccountSelected(account) ? '#1a1a1a' : '#d1d5db'}`,
                      backgroundColor: isAccountSelected(account) ? '#1a1a1a' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {isAccountSelected(account) && <CheckCircle size={14} style={{ color: 'white' }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#111827' }}>{account.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>ID: {account.customerId}</div>
                      {account.currency && (
                        <span style={{ fontSize: '10px', backgroundColor: '#e5e7eb', color: '#374151', padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block' }}>
                          {account.currency}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        </>
      )}

      {/* Indicateur de chargement de l'aperçu */}
      {loadingPreview && (
        <div className="loading-preview">
          <Loader className="spin" size={16} />
          <span>Chargement de l'aperçu...</span>
        </div>
      )}

      {/* Étape 2: Actions - Affichée quand un compte est sélectionné */}
      {selectedAccounts.length > 0 && (
        <div className="preview-section" style={{ marginTop: '20px', padding: '15px 20px', backgroundColor: '#f8f5f0', borderRadius: '0 0 15px 15px' }}>
          <div className="preview-actions">
            <button
              onClick={saveToLibrary}
              disabled={saving}
              className="btn btn-primary"
              style={{ fontSize: '16px', padding: '12px 24px', fontWeight: '600' }}
            >
              {saving ? <Loader className="spin" size={16} /> : '+'}
              {saving ? 'Sauvegarde...' : `Ajouter ${selectedAccounts.length} compte(s) à la bibliothèque`}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Composant: Sélecteur Microsoft Ads
function MicrosoftAdsSelector({ token, onAdded, onBack, setSuccess, setError }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/library/microsoft-ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAccounts(response.data.accounts);
    } catch (error) {
      console.error('❌ Erreur chargement comptes Microsoft Ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountSelection = (account) => {
    setSelectedAccounts(prev => {
      const isSelected = prev.some(acc => acc.accountId === account.accountId);
      if (isSelected) {
        return prev.filter(acc => acc.accountId !== account.accountId);
      } else {
        return [...prev, account];
      }
    });
  };

  const selectAllAccounts = () => {
    setSelectedAccounts([...accounts]);
  };

  const deselectAllAccounts = () => {
    setSelectedAccounts([]);
  };

  const isAccountSelected = (account) => {
    return selectedAccounts.some(acc => acc.accountId === account.accountId);
  };

  const saveToLibrary = async () => {
    if (selectedAccounts.length === 0) return;

    setSaving(true);
    setSavingProgress({ current: 0, total: selectedAccounts.length });
    
    let successCount = 0;
    let errors = [];

    try {
      // Récupérer la connexion Microsoft Ads
      const connectionsResponse = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const microsoftConnection = connectionsResponse.data.connectors.find(
        conn => conn.provider === 'MICROSOFT_ADS'
      );

      if (!microsoftConnection) {
        throw new Error('Connexion Microsoft Ads requise');
      }

      // Traiter chaque compte sélectionné
      for (let i = 0; i < selectedAccounts.length; i++) {
        const account = selectedAccounts[i];
        setSavingProgress({ current: i + 1, total: selectedAccounts.length });
        
        try {
          await axios.post(`${API_URL}/api/library/add`, {
            type: 'MICROSOFT_ADS',
            name: `${account.name} (${account.accountId})`,
            sourceId: account.accountId,
            sourceName: account.name,
            connectionId: microsoftConnection.id,
            metadata: {
              accountId: account.accountId,
              accountName: account.name,
              currency: account.currency,
              timeZone: account.timeZone,
              accountMode: account.accountMode,
              accountLifeCycleStatus: account.accountLifeCycleStatus,
              availableMetrics: preview?.availableMetrics || [],
              totalDays: preview?.totalRows || 30
            }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          successCount++;
        } catch (accountError) {
          console.error(`❌ Erreur ajout compte ${account.name}:`, accountError);
          errors.push(`${account.name}: ${accountError.response?.data?.error || accountError.message}`);
        }
      }

      // Afficher un résumé des résultats
      if (successCount > 0) {
        const message = successCount === selectedAccounts.length 
          ? `${successCount} compte(s) ajouté(s) avec succès !`
          : `${successCount}/${selectedAccounts.length} compte(s) ajouté(s) avec succès.`;
        
        setSuccess(message);
        
        // Masquer automatiquement après 5 secondes
        setTimeout(() => {
          setSuccess('');
        }, 5000);
        
        if (errors.length > 0) {
          console.warn('Erreurs lors de l\'ajout de certains comptes:', errors);
        }
        
        onAdded();
      } else {
        setError(`Aucun compte n'a pu être ajouté. Erreurs: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('❌ Erreur générale sauvegarde Microsoft Ads:', error);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setSavingProgress({ current: 0, total: 0 });
    }
  };


  // Écran d'attente qui occupe toute la pop-in
  if (saving) {
    return (
      <div className="ads-selector saving-fullscreen">
        <div className="saving-content-fullscreen">
          <div className="saving-spinner">
            <Loader className="spin" size={48} />
          </div>
          <h3>Ajout en cours...</h3>
          <p>Ajout de <strong>{selectedAccounts.length + ' compte(s)'}</strong> à votre bibliothèque</p>
          <div className="saving-progress-fullscreen">
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">1</span>
              <span className="step-text-fullscreen">Vérification de la connexion</span>
            </div>
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">2</span>
              <span className="step-text-fullscreen">Enregistrement du compte</span>
            </div>
            <div className="progress-step-fullscreen">
              <span className="step-number-fullscreen">3</span>
              <span className="step-text-fullscreen">Finalisation</span>
            </div>
          </div>
          <p className="saving-note-fullscreen">Veuillez patienter, cette opération peut prendre quelques secondes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-selector">
      <div className="selector-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={onBack} className="btn btn-secondary" style={{ backgroundColor: '#f8f5f0', color: '#1a1a1a', border: '1px solid #e5e0d5' }}>← Retour</button>
          <h4 style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            <img src={microsoftAdsLogo} alt="Microsoft Ads" style={{ width: '24px', height: '24px', marginRight: '8px' }} />
            Sélectionner un compte Microsoft Ads
          </h4>
        </div>
        <button
          onClick={() => selectedAccounts.length > 0 && saveToLibrary()}
          disabled={selectedAccounts.length === 0 || saving}
          className="btn btn-primary"
          style={{
            fontSize: '14px',
            padding: '10px 20px',
            fontWeight: '600',
            opacity: selectedAccounts.length === 0 ? 0.5 : 1,
            cursor: selectedAccounts.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Ajout en cours...' : `+ Ajouter ${selectedAccounts.length > 0 ? `(${selectedAccounts.length})` : ''}`}
        </button>
      </div>

      {loading ? (
        <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
          <p>Chargement des comptes Microsoft Ads...</p>
        </div>
      ) : accounts.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
          <p>Aucun compte Microsoft Ads disponible.</p>
          <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            Vérifiez que votre compte a accès à Microsoft Advertising.
          </p>
        </div>
      ) : (
        <>
          <div className="search-bar" style={{ padding: '16px 0' }}>
            <input
              type="text"
              placeholder="Rechercher un compte..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div className="accounts-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '4px'
          }}>
            {(() => {
              const filteredAccounts = accounts
                .filter(account =>
                  account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  account.accountId?.toString().includes(searchTerm)
                )
                .sort((a, b) => a.name.localeCompare(b.name));

              if (filteredAccounts.length === 0) {
                return (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', gridColumn: '1 / -1' }}>
                    Aucun compte trouvé pour "{searchTerm}"
                  </div>
                );
              }

              return filteredAccounts.map((account, index) => (
                <div
                  key={`${account.accountId}-${index}`}
                  onClick={() => toggleAccountSelection(account)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${isAccountSelected(account) ? '#0078D4' : '#e5e7eb'}`,
                    borderRadius: '12px',
                    backgroundColor: isAccountSelected(account) ? '#f0f9ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: `2px solid ${isAccountSelected(account) ? '#0078D4' : '#d1d5db'}`,
                      backgroundColor: isAccountSelected(account) ? '#0078D4' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {isAccountSelected(account) && <CheckCircle size={14} style={{ color: 'white' }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#111827' }}>{account.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>ID: {account.accountId}</div>
                      <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                        {account.currency && (
                          <span style={{ fontSize: '10px', backgroundColor: '#e5e7eb', color: '#374151', padding: '2px 6px', borderRadius: '4px' }}>
                            {account.currency}
                          </span>
                        )}
                        {account.accountLifeCycleStatus === 'Draft' && (
                          <span style={{ fontSize: '10px', backgroundColor: '#fef3c7', color: '#92400e', padding: '2px 6px', borderRadius: '4px' }}>
                            Brouillon
                          </span>
                        )}
                        {account.accountLifeCycleStatus === 'Suspended' && (
                          <span style={{ fontSize: '10px', backgroundColor: '#fee2e2', color: '#991b1b', padding: '2px 6px', borderRadius: '4px' }}>
                            Suspendu
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        </>
      )}
    </div>
  );
}

// Composant: Sélecteur Merchant Center
function MerchantApiSelector({ token, onAdded, onBack, setSuccess, setError, suggestedMerchantIds = [] }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAccounts();
  }, []);

  // Pré-sélectionner les comptes suggérés quand ils sont chargés
  useEffect(() => {
    if (suggestedMerchantIds.length > 0 && accounts.length > 0 && selectedAccounts.length === 0) {
      const suggested = accounts.filter(acc =>
        suggestedMerchantIds.includes(String(acc.id)) && !acc.alreadyInLibrary
      );
      if (suggested.length > 0) {
        setSelectedAccounts(suggested);
        console.log('🔗 [MERCHANT-API] Comptes suggérés pré-sélectionnés:', suggested.map(a => a.id));
      }
    }
  }, [suggestedMerchantIds, accounts]);

  // Vérifier si un compte est suggéré (lié au compte Google Ads)
  const isSuggested = (account) => {
    return suggestedMerchantIds.includes(String(account.id));
  };

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/library/merchant-api`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      let loadedAccounts = response.data.accounts || [];

      // Trier les comptes: suggérés en premier, puis les autres
      if (suggestedMerchantIds.length > 0) {
        loadedAccounts = loadedAccounts.sort((a, b) => {
          const aIsSuggested = suggestedMerchantIds.includes(String(a.id));
          const bIsSuggested = suggestedMerchantIds.includes(String(b.id));
          if (aIsSuggested && !bIsSuggested) return -1;
          if (!aIsSuggested && bIsSuggested) return 1;
          return 0;
        });
      }

      setAccounts(loadedAccounts);
    } catch (error) {
      console.error('❌ Erreur chargement comptes Merchant API:', error);
      setAccounts([]);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountSelection = (account) => {
    setSelectedAccounts(prev => {
      const isSelected = prev.some(acc => acc.id === account.id);
      if (isSelected) {
        return prev.filter(acc => acc.id !== account.id);
      } else {
        return [...prev, account];
      }
    });
  };

  const isAccountSelected = (account) => {
    return selectedAccounts.some(acc => acc.id === account.id);
  };

  const saveToLibrary = async () => {
    if (selectedAccounts.length === 0) return;

    setSaving(true);
    setSavingProgress({ current: 0, total: selectedAccounts.length });

    let successCount = 0;
    let errors = [];

    try {
      const connectionsResponse = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const merchantApiConnection = connectionsResponse.data.connectors.find(
        conn => conn.provider === 'GOOGLE_MERCHANT_API'
      );

      if (!merchantApiConnection) {
        throw new Error('Connexion Google Merchant API requise');
      }

      for (let i = 0; i < selectedAccounts.length; i++) {
        const account = selectedAccounts[i];
        setSavingProgress({ current: i + 1, total: selectedAccounts.length });

        try {
          await axios.post(`${API_URL}/api/library/add`, {
            type: 'GOOGLE_MERCHANT_API',
            name: `${account.name} (${account.id})`,
            sourceId: account.id,
            sourceName: account.name,
            connectionId: merchantApiConnection.id,
            metadata: {
              merchantId: account.id,
              displayName: account.name,
              isAggregator: account.isAggregator
            }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          successCount++;
        } catch (accountError) {
          console.error(`❌ Erreur ajout compte ${account.name}:`, accountError);
          errors.push(`${account.name}: ${accountError.response?.data?.error || accountError.message}`);
        }
      }

      if (successCount > 0) {
        setSuccess(`${successCount} compte(s) Merchant API ajouté(s) avec succès !`);
        setTimeout(() => setSuccess(''), 5000);
        onAdded();
      } else {
        setError(`Aucun compte n'a pu être ajouté. Erreurs: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('❌ Erreur générale sauvegarde Merchant API:', error);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setSavingProgress({ current: 0, total: 0 });
    }
  };

  if (saving) {
    return (
      <div className="ads-selector saving-fullscreen">
        <div className="saving-content-fullscreen">
          <div className="saving-spinner">
            <Loader className="spin" size={48} />
          </div>
          <h3>Ajout en cours...</h3>
          <p>Ajout de <strong>{selectedAccounts.length} compte(s)</strong> à votre bibliothèque</p>
          <div className="saving-progress-fullscreen">
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">1</span>
              <span className="step-text-fullscreen">Vérification</span>
            </div>
            <div className={`progress-step-fullscreen ${savingProgress.current > 0 ? 'active' : ''}`}>
              <span className="step-number-fullscreen">2</span>
              <span className="step-text-fullscreen">Ajout ({savingProgress.current}/{savingProgress.total})</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const filteredAccounts = accounts.filter(account =>
    account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    account.id?.toString().includes(searchTerm)
  );

  return (
    <div className="ads-selector">
      <div className="selector-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={onBack} className="btn btn-secondary" style={{ backgroundColor: '#f8f5f0', color: '#1a1a1a', border: '1px solid #e5e0d5' }}>← Retour</button>
          <h4 style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            <ShoppingCart size={18} /> Sélectionner des comptes Merchant Center
          </h4>
        </div>
        <button
          onClick={() => selectedAccounts.length > 0 && saveToLibrary()}
          className="btn btn-primary"
          disabled={selectedAccounts.length === 0}
          style={{ display: 'flex', alignItems: 'center', gap: '8px' }}
        >
          + Ajouter {selectedAccounts.length > 0 ? `(${selectedAccounts.length})` : ''}
        </button>
      </div>

      {loading ? (
        <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
          <p>Chargement des comptes Merchant API...</p>
        </div>
      ) : accounts.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
          <p>Aucun compte Merchant API disponible.</p>
          <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            Vérifiez que votre compte Google a accès à Google Merchant Center.
          </p>
        </div>
      ) : (
        <>
          <div className="search-bar" style={{ padding: '16px 0' }}>
            <input
              type="text"
              placeholder="Rechercher un compte..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div className="accounts-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '4px'
          }}>
            {filteredAccounts.map(account => (
              <div
                key={account.id}
                onClick={() => !account.alreadyInLibrary && toggleAccountSelection(account)}
                style={{
                  padding: '16px',
                  border: `2px solid ${isSuggested(account) ? '#f59e0b' : isAccountSelected(account) ? '#10B981' : '#e5e7eb'}`,
                  borderRadius: '12px',
                  backgroundColor: account.alreadyInLibrary ? '#f3f4f6' : isAccountSelected(account) ? '#f0fdf4' : isSuggested(account) ? '#fffbeb' : 'white',
                  cursor: account.alreadyInLibrary ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s ease',
                  opacity: account.alreadyInLibrary ? 0.7 : 1,
                  position: 'relative'
                }}
              >
                {/* Badge "Lié à Google Ads" pour les comptes suggérés */}
                {isSuggested(account) && !account.alreadyInLibrary && (
                  <div style={{
                    position: 'absolute',
                    top: '-8px',
                    right: '12px',
                    backgroundColor: '#f59e0b',
                    color: 'white',
                    fontSize: '10px',
                    fontWeight: '600',
                    padding: '2px 8px',
                    borderRadius: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px'
                  }}>
                    <Link size={12} /> Lié à Google Ads
                  </div>
                )}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{
                    width: '20px',
                    height: '20px',
                    borderRadius: '4px',
                    border: `2px solid ${account.alreadyInLibrary ? '#9ca3af' : isAccountSelected(account) ? '#10B981' : '#d1d5db'}`,
                    backgroundColor: account.alreadyInLibrary ? '#9ca3af' : isAccountSelected(account) ? '#10B981' : 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    {(isAccountSelected(account) || account.alreadyInLibrary) && <CheckCircle size={14} style={{ color: 'white' }} />}
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: '600', color: account.alreadyInLibrary ? '#6b7280' : '#111827' }}>{account.name}</div>
                    <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>ID: {account.id}</div>
                    <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap', marginTop: '4px' }}>
                      {account.isAggregator && (
                        <span style={{ fontSize: '10px', backgroundColor: '#1a1a1a', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>
                          Aggregator
                        </span>
                      )}
                      {account.alreadyInLibrary && (
                        <span style={{ fontSize: '10px', backgroundColor: '#6b7280', color: 'white', padding: '2px 6px', borderRadius: '4px' }}>
                          Déjà ajouté
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

// Composant: Sélecteur Facebook Ads
function FacebookAdsSelector({ token, onAdded, onBack, setSuccess, setError }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/library/facebook-ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.data.success) {
        setAccounts(response.data.accounts || []);
      }
    } catch (error) {
      console.error('❌ Erreur chargement comptes Facebook Ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountSelection = (account) => {
    setSelectedAccounts(prev => {
      const isSelected = prev.some(acc => acc.id === account.id);
      if (isSelected) {
        return prev.filter(acc => acc.id !== account.id);
      } else {
        return [...prev, account];
      }
    });
  };

  const selectAllAccounts = () => {
    setSelectedAccounts([...accounts]);
  };

  const deselectAllAccounts = () => {
    setSelectedAccounts([]);
  };

  const isAccountSelected = (account) => {
    return selectedAccounts.some(acc => acc.id === account.id);
  };

  const saveToLibrary = async () => {
    if (selectedAccounts.length === 0) return;

    setSaving(true);
    setSavingProgress({ current: 0, total: selectedAccounts.length });
    
    let successCount = 0;
    let errors = [];

    try {
      // Récupérer les connexions pour trouver la connexion Facebook Ads
      const connectorsResponse = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const facebookConnection = connectorsResponse.data.connectors.find(c => 
        c.provider === 'FACEBOOK_ADS' && c.isActive
      );

      if (!facebookConnection) {
        throw new Error('Connexion Facebook Ads non trouvée');
      }

      // Traiter chaque compte sélectionné
      for (let i = 0; i < selectedAccounts.length; i++) {
        const account = selectedAccounts[i];
        setSavingProgress({ current: i + 1, total: selectedAccounts.length });
        
        try {
          await axios.post(`${API_URL}/api/library/add`, {
            type: 'FACEBOOK_ADS',
            name: `${account.name} (${account.id})`,
            sourceId: account.id,
            sourceName: account.name,
            connectionId: facebookConnection.id,
            metadata: {
              accountId: account.id,
              accountName: account.name,
              currency: account.currency || 'EUR',
              timezone: account.timezone || 'Europe/Paris',
              status: account.status || 'ACTIVE',
              business: account.business,
              totalAccounts: accounts.length
            }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          successCount++;
        } catch (accountError) {
          console.error(`❌ Erreur ajout compte ${account.name}:`, accountError);
          errors.push(`${account.name}: ${accountError.response?.data?.error || accountError.message}`);
        }
      }

      // Afficher un résumé des résultats
      if (successCount > 0) {
        const message = successCount === selectedAccounts.length 
          ? `${successCount} compte(s) ajouté(s) avec succès !`
          : `${successCount}/${selectedAccounts.length} compte(s) ajouté(s) avec succès.`;
        
        setSuccess(message);
        
        // Masquer automatiquement après 5 secondes
        setTimeout(() => {
          setSuccess('');
        }, 5000);
        
        if (errors.length > 0) {
          console.warn('Erreurs lors de l\'ajout de certains comptes:', errors);
        }
        
        onAdded();
      } else {
        setError(`Aucun compte n'a pu être ajouté. Erreurs: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('❌ Erreur générale sauvegarde Facebook Ads:', error);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setSavingProgress({ current: 0, total: 0 });
    }
  };

  // Écran d'attente qui occupe toute la pop-in
  if (saving) {
    return (
      <div className="ads-selector saving-fullscreen">
        <div className="saving-content-fullscreen">
          <div className="saving-spinner">
            <Loader className="spin" size={48} />
          </div>
          <h3>Ajout en cours...</h3>
          <p>Ajout de <strong>{selectedAccounts.length + ' compte(s)'}</strong> à votre bibliothèque</p>
          <div className="saving-progress-fullscreen">
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">1</span>
              <span className="step-text-fullscreen">Vérification de la connexion</span>
            </div>
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">2</span>
              <span className="step-text-fullscreen">Enregistrement du compte</span>
            </div>
            <div className="progress-step-fullscreen">
              <span className="step-number-fullscreen">3</span>
              <span className="step-text-fullscreen">Finalisation</span>
            </div>
          </div>
          <p className="saving-note-fullscreen">Veuillez patienter, cette opération peut prendre quelques secondes...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-selector">
      <div className="selector-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={onBack} className="btn btn-secondary" style={{ backgroundColor: '#f8f5f0', color: '#1a1a1a', border: '1px solid #e5e0d5' }}>← Retour</button>
          <h4 style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            <img src={metaAdsLogo} alt="Meta Ads" style={{ width: '24px', height: '24px', marginRight: '8px', objectFit: 'contain' }} />
            Sélectionner un compte Meta Ads
          </h4>
        </div>
        <button
          onClick={() => selectedAccounts.length > 0 && saveToLibrary()}
          disabled={selectedAccounts.length === 0 || saving}
          className="btn btn-primary"
          style={{
            fontSize: '14px',
            padding: '10px 20px',
            fontWeight: '600',
            opacity: selectedAccounts.length === 0 ? 0.5 : 1,
            cursor: selectedAccounts.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Ajout en cours...' : `+ Ajouter ${selectedAccounts.length > 0 ? `(${selectedAccounts.length})` : ''}`}
        </button>
      </div>

      {loading ? (
        <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
          <p>Chargement des comptes Meta Ads...</p>
        </div>
      ) : accounts.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
          <p>Aucun compte Meta Ads disponible.</p>
          <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            Vérifiez que votre compte a accès à Meta Ads.
          </p>
        </div>
      ) : (
        <>
          <div className="search-bar" style={{ padding: '16px 0' }}>
            <input
              type="text"
              placeholder="Rechercher un compte..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px',
                boxSizing: 'border-box'
              }}
            />
          </div>

          <div className="accounts-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '4px'
          }}>
            {(() => {
              const filteredAccounts = accounts
                .filter(account =>
                  account.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                  account.id?.toString().includes(searchTerm)
                )
                .sort((a, b) => a.name.localeCompare(b.name));

              if (filteredAccounts.length === 0) {
                return (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', gridColumn: '1 / -1' }}>
                    Aucun compte trouvé pour "{searchTerm}"
                  </div>
                );
              }

              return filteredAccounts.map((account, index) => (
                <div
                  key={`${account.id}-${index}`}
                  onClick={() => toggleAccountSelection(account)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${isAccountSelected(account) ? '#1877F2' : '#e5e7eb'}`,
                    borderRadius: '12px',
                    backgroundColor: isAccountSelected(account) ? '#f0f7ff' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: `2px solid ${isAccountSelected(account) ? '#1877F2' : '#d1d5db'}`,
                      backgroundColor: isAccountSelected(account) ? '#1877F2' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {isAccountSelected(account) && <CheckCircle size={14} style={{ color: 'white' }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#111827' }}>{account.name}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>ID: {account.id}</div>
                      <div style={{ display: 'flex', gap: '4px', marginTop: '4px' }}>
                        {account.currency && (
                          <span style={{ fontSize: '10px', backgroundColor: '#e5e7eb', color: '#374151', padding: '2px 6px', borderRadius: '4px' }}>
                            {account.currency}
                          </span>
                        )}
                        {account.status === 'ACTIVE' && (
                          <span style={{ fontSize: '10px', backgroundColor: '#d1fae5', color: '#065f46', padding: '2px 6px', borderRadius: '4px' }}>
                            Actif
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        </>
      )}
    </div>
  );
}

// Composant: Sélecteur Pinterest Ads
function PinterestAdsSelector({ token, onAdded, onBack, setSuccess, setError }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/library/pinterest-ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAccounts(response.data.accounts || []);
      }
    } catch (error) {
      console.error('Erreur chargement comptes Pinterest Ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountSelection = (account) => {
    setSelectedAccounts(prev => {
      const isSelected = prev.some(acc => acc.id === account.id);
      if (isSelected) {
        return prev.filter(acc => acc.id !== account.id);
      } else {
        return [...prev, account];
      }
    });
  };

  const selectAllAccounts = () => {
    setSelectedAccounts([...accounts]);
  };

  const deselectAllAccounts = () => {
    setSelectedAccounts([]);
  };

  const isAccountSelected = (account) => {
    return selectedAccounts.some(acc => acc.id === account.id);
  };

  const saveToLibrary = async () => {
    if (selectedAccounts.length === 0) return;

    setSaving(true);
    setSavingProgress({ current: 0, total: selectedAccounts.length });

    let successCount = 0;
    let errors = [];

    try {
      const connectorsResponse = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const pinterestConnection = connectorsResponse.data.connectors.find(c =>
        c.provider === 'PINTEREST_ADS' && c.isActive
      );

      if (!pinterestConnection) {
        throw new Error('Connexion Pinterest Ads non trouvée');
      }

      for (let i = 0; i < selectedAccounts.length; i++) {
        const account = selectedAccounts[i];
        setSavingProgress({ current: i + 1, total: selectedAccounts.length });

        try {
          await axios.post(`${API_URL}/api/library/add`, {
            type: 'PINTEREST_ADS',
            name: account.name,
            sourceId: account.id,
            sourceName: account.name,
            connectionId: pinterestConnection.id,
            metadata: {
              accountId: account.id,
              accountName: account.name,
              currency: account.currency || 'EUR',
              country: account.country,
              owner: account.owner
            }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          successCount++;
        } catch (accountError) {
          console.error(`Erreur ajout compte ${account.name}:`, accountError);
          errors.push(`${account.name}: ${accountError.response?.data?.error || accountError.message}`);
        }
      }

      if (successCount > 0) {
        const message = successCount === selectedAccounts.length
          ? `${successCount} compte(s) Pinterest Ads ajouté(s) avec succès !`
          : `${successCount}/${selectedAccounts.length} compte(s) ajouté(s) avec succès.`;

        setSuccess(message);
        setTimeout(() => setSuccess(''), 5000);

        onAdded();
      } else {
        setError(`Aucun compte n'a pu être ajouté. Erreurs: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Erreur sauvegarde Pinterest Ads:', error);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setSavingProgress({ current: 0, total: 0 });
    }
  };

  if (saving) {
    return (
      <div className="ads-selector saving-fullscreen">
        <div className="saving-content-fullscreen">
          <div className="saving-spinner">
            <Loader className="spin" size={48} />
          </div>
          <h3>Ajout en cours...</h3>
          <p>Ajout de <strong>{selectedAccounts.length} compte(s)</strong> à votre bibliothèque</p>
          <div className="saving-progress-fullscreen">
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">1</span>
              <span className="step-label-fullscreen">Création des comptes ({savingProgress.current}/{savingProgress.total})</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-selector">
      <div className="selector-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', marginRight: '8px' }}>
          <ChevronLeft size={20} />
        </button>
        <img src={pinterestAdsLogo} alt="Pinterest Ads" style={{ width: '32px', height: '32px', marginRight: '12px', objectFit: 'contain' }} />
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Comptes Pinterest Ads</h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>Sélectionnez les comptes à ajouter</p>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
        </div>
      ) : accounts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <p>Aucun compte Pinterest Ads disponible</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Rechercher un compte..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 36px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9rem' }}
              />
            </div>
            <button onClick={selectAllAccounts} style={{ padding: '8px 12px', fontSize: '0.85rem', background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
              Tout sélectionner
            </button>
            {selectedAccounts.length > 0 && (
              <button onClick={deselectAllAccounts} style={{ padding: '8px 12px', fontSize: '0.85rem', background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', color: '#6b7280', flexShrink: 0, whiteSpace: 'nowrap' }}>
                Désélectionner
              </button>
            )}
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            {accounts.filter(acc =>
              acc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              acc.id?.toString().includes(searchTerm)
            ).map(account => (
              <div
                key={account.id}
                onClick={() => toggleAccountSelection(account)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderBottom: '1px solid #f3f4f6',
                  cursor: 'pointer',
                  backgroundColor: isAccountSelected(account) ? '#f0fdf4' : 'white',
                  transition: 'background-color 0.15s'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  border: isAccountSelected(account) ? 'none' : '2px solid #d1d5db',
                  backgroundColor: isAccountSelected(account) ? '#22c55e' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                  flexShrink: 0
                }}>
                  {isAccountSelected(account) && <Check size={14} color="white" />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', color: '#111827' }}>{account.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{account.id}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
            <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
              {selectedAccounts.length} compte(s) sélectionné(s)
            </span>
            <button
              onClick={saveToLibrary}
              disabled={selectedAccounts.length === 0}
              style={{
                padding: '10px 20px',
                backgroundColor: selectedAccounts.length > 0 ? '#E60023' : '#e5e7eb',
                color: selectedAccounts.length > 0 ? 'white' : '#9ca3af',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: selectedAccounts.length > 0 ? 'pointer' : 'not-allowed'
              }}
            >
              Ajouter à la bibliothèque
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Composant: Sélecteur TikTok Ads
function TikTokAdsSelector({ token, onAdded, onBack, setSuccess, setError }) {
  const [accounts, setAccounts] = useState([]);
  const [selectedAccounts, setSelectedAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadAccounts();
  }, []);

  const loadAccounts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/library/tiktok-ads`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setAccounts(response.data.accounts || []);
      }
    } catch (error) {
      console.error('Erreur chargement comptes TikTok Ads:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleAccountSelection = (account) => {
    setSelectedAccounts(prev => {
      const isSelected = prev.some(acc => acc.id === account.id);
      if (isSelected) {
        return prev.filter(acc => acc.id !== account.id);
      } else {
        return [...prev, account];
      }
    });
  };

  const selectAllAccounts = () => {
    setSelectedAccounts([...accounts]);
  };

  const deselectAllAccounts = () => {
    setSelectedAccounts([]);
  };

  const isAccountSelected = (account) => {
    return selectedAccounts.some(acc => acc.id === account.id);
  };

  const saveToLibrary = async () => {
    if (selectedAccounts.length === 0) return;

    setSaving(true);
    setSavingProgress({ current: 0, total: selectedAccounts.length });

    let successCount = 0;
    let errors = [];

    try {
      const connectorsResponse = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const tiktokConnection = connectorsResponse.data.connectors.find(c =>
        c.provider === 'TIKTOK_ADS' && c.isActive
      );

      if (!tiktokConnection) {
        throw new Error('Connexion TikTok Ads non trouvée');
      }

      for (let i = 0; i < selectedAccounts.length; i++) {
        const account = selectedAccounts[i];
        setSavingProgress({ current: i + 1, total: selectedAccounts.length });

        try {
          await axios.post(`${API_URL}/api/library/add`, {
            type: 'TIKTOK_ADS',
            name: account.name,
            sourceId: account.id,
            sourceName: account.name,
            connectionId: tiktokConnection.id,
            metadata: {
              accountId: account.id,
              accountName: account.name,
              currency: account.currency || 'EUR',
              timezone: account.timezone,
              status: account.status
            }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          successCount++;
        } catch (accountError) {
          console.error(`Erreur ajout compte ${account.name}:`, accountError);
          errors.push(`${account.name}: ${accountError.response?.data?.error || accountError.message}`);
        }
      }

      if (successCount > 0) {
        const message = successCount === selectedAccounts.length
          ? `${successCount} compte(s) TikTok Ads ajouté(s) avec succès !`
          : `${successCount}/${selectedAccounts.length} compte(s) ajouté(s) avec succès.`;

        setSuccess(message);
        setTimeout(() => setSuccess(''), 5000);

        onAdded();
      } else {
        setError(`Aucun compte n'a pu être ajouté. Erreurs: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Erreur sauvegarde TikTok Ads:', error);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setSavingProgress({ current: 0, total: 0 });
    }
  };

  if (saving) {
    return (
      <div className="ads-selector saving-fullscreen">
        <div className="saving-content-fullscreen">
          <div className="saving-spinner">
            <Loader className="spin" size={48} />
          </div>
          <h3>Ajout en cours...</h3>
          <p>Ajout de <strong>{selectedAccounts.length} compte(s)</strong> à votre bibliothèque</p>
          <div className="saving-progress-fullscreen">
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">1</span>
              <span className="step-label-fullscreen">Création des comptes ({savingProgress.current}/{savingProgress.total})</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-selector">
      <div className="selector-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', marginRight: '8px' }}>
          <ChevronLeft size={20} />
        </button>
        <img src={tiktokAdsLogo} alt="TikTok Ads" style={{ width: '32px', height: '32px', marginRight: '12px', objectFit: 'contain' }} />
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Comptes TikTok Ads</h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>Sélectionnez les comptes à ajouter</p>
        </div>
      </div>

      {loading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
        </div>
      ) : accounts.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <p>Aucun compte TikTok Ads disponible</p>
        </div>
      ) : (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af' }} />
              <input
                type="text"
                placeholder="Rechercher un compte..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 36px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9rem' }}
              />
            </div>
            <button onClick={selectAllAccounts} style={{ padding: '8px 12px', fontSize: '0.85rem', background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
              Tout sélectionner
            </button>
            {selectedAccounts.length > 0 && (
              <button onClick={deselectAllAccounts} style={{ padding: '8px 12px', fontSize: '0.85rem', background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', color: '#6b7280', flexShrink: 0, whiteSpace: 'nowrap' }}>
                Désélectionner
              </button>
            )}
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            {accounts.filter(acc =>
              acc.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              acc.id?.toString().includes(searchTerm)
            ).map(account => (
              <div
                key={account.id}
                onClick={() => toggleAccountSelection(account)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderBottom: '1px solid #f3f4f6',
                  cursor: 'pointer',
                  backgroundColor: isAccountSelected(account) ? '#f0fdf4' : 'white',
                  transition: 'background-color 0.15s'
                }}
              >
                <div style={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '4px',
                  border: isAccountSelected(account) ? 'none' : '2px solid #d1d5db',
                  backgroundColor: isAccountSelected(account) ? '#22c55e' : 'white',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: '12px',
                  flexShrink: 0
                }}>
                  {isAccountSelected(account) && <Check size={14} color="white" />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: '500', color: '#111827' }}>{account.name}</div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>{account.id}</div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
            <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
              {selectedAccounts.length} compte(s) sélectionné(s)
            </span>
            <button
              onClick={saveToLibrary}
              disabled={selectedAccounts.length === 0}
              style={{
                padding: '10px 20px',
                backgroundColor: selectedAccounts.length > 0 ? '#000000' : '#e5e7eb',
                color: selectedAccounts.length > 0 ? 'white' : '#9ca3af',
                border: 'none',
                borderRadius: '8px',
                fontWeight: '500',
                cursor: selectedAccounts.length > 0 ? 'pointer' : 'not-allowed'
              }}
            >
              Ajouter à la bibliothèque
            </button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Sélecteur de comptes qui ne connaît aucune plateforme en particulier.
 *
 * ## Pourquoi il existe
 *
 * Les huit sélecteurs voisins de ce fichier sont le même composant recopié huit fois, chacun
 * attaché à une route `/api/library/<plateforme>` taillée pour lui. Conséquence directe :
 * ChatGPT Ads, Awin, Matomo et Piano Analytics n'apparaissaient pas dans « Ajouter des
 * données », faute d'avoir leur copie — alors que le Gestionnaire les importe déjà sans
 * difficulté.
 *
 * Il les importe par `GET /api/connectors/:id/accounts`, une route unique qui sert les douze
 * plateformes. C'est elle qu'on emploie ici : une plateforme de plus se branche en ajoutant
 * une entrée à `PLATEFORMES_GENERIQUES`, pas deux cent cinquante lignes.
 *
 * ⚠️ **Plusieurs connecteurs peuvent servir la même plateforme** — quatre connexions ChatGPT
 * Ads coexistent sur ce compte. Les comptes de chacune sont donc chargés puis réunis, chaque
 * ligne gardant la connexion dont elle vient : c'est elle qui sera enregistrée, et une ligne
 * rattachée à la mauvaise connexion ne se lirait plus.
 */
const PLATEFORMES_GENERIQUES = {
  GOOGLE_TAG_MANAGER: {
    libelle: 'Google Tag Manager',
    sousTitre: 'Conteneurs GTM',
    logo: tagManagerLogo,
    /*
     * ⚠️ **`accountId` n'est pas du confort : sans lui, le conteneur n'est interrogeable par
     * rien.** Tous les chemins de l'API Tag Manager sont préfixés par `accounts/{accountId}/`,
     * et le compte GTM n'est pas déductible du conteneur. La route générique le rend dans la
     * ligne (voir le `case 'GOOGLE_TAG_MANAGER'` de `routes/connectors.js`) ; le backend le
     * complète en dernier recours, parce qu'il y a trois chemins d'ajout.
     *
     * `publicId` (`GTM-XXXXXXX`), lui, est du confort assumé : l'API ne le consomme nulle part,
     * mais c'est la forme que l'humain reconnaît.
     */
    metadata: (compte) => ({
      containerId: String(compte.sourceId || compte.id),
      ...(compte.accountId ? { accountId: String(compte.accountId) } : {}),
      ...(compte.publicId ? { publicId: compte.publicId } : {}),
      ...(compte.containerName ? { containerName: compte.containerName } : {}),
      ...(compte.accountName ? { accountName: compte.accountName } : {}),
      ...(compte.usageContext ? { usageContext: compte.usageContext } : {}),
      ...(compte.tagManagerUrl ? { tagManagerUrl: compte.tagManagerUrl } : {})
    })
  },
  OPENAI_ADS: {
    libelle: 'ChatGPT Ads',
    sousTitre: 'Comptes publicitaires ChatGPT',
    logo: chatgptAdsLogo,
    /*
     * Ce que le backend attend dans `metadata`, construit depuis le compte renvoyé par la
     * route générique. `timeZone` n'est pas décoratif : `_getOpenAiAdsData` s'en sert pour
     * borner ses périodes, et sans lui les journées sont découpées sur Europe/Paris — juste
     * pour ce parc, faux ailleurs, et faux sans que rien ne le montre.
     */
    metadata: (compte) => ({
      accountId: String(compte.sourceId || compte.id),
      ...(compte.timeZone ? { timeZone: compte.timeZone } : {}),
      ...(compte.currency ? { currency: compte.currency } : {})
    })
  }
};

function SelecteurGenerique({ provider, token, onAdded, onBack, setSuccess, setError }) {
  const config = PLATEFORMES_GENERIQUES[provider] || {};
  const [comptes, setComptes] = useState([]);
  const [selection, setSelection] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [enregistrement, setEnregistrement] = useState(false);
  const [progression, setProgression] = useState({ current: 0, total: 0 });
  const [recherche, setRecherche] = useState('');
  const [avertissement, setAvertissement] = useState('');
  /*
   * L'avancement d'un inventaire construit en plusieurs minutes.
   *
   * Seul Google Tag Manager en produit un : lister ses conteneurs coûte un appel par compte GTM,
   * et le quota en tolère une vingtaine par minute. `null` partout ailleurs, et le composant
   * n'affiche alors rien de plus — un sablier suffit quand la liste arrive en une seconde.
   */
  const [inventaire, setInventaire] = useState(null);
  const [actualisation, setActualisation] = useState(0);

  useEffect(() => {
    let annule = false;
    let minuterie = null;

    const charger = async ({ forcer = false, discret = false } = {}) => {
      // `discret` : un rafraîchissement d'avancement ne doit pas faire clignoter la liste déjà
      // affichée en la remplaçant par un sablier.
      if (!discret) setChargement(true);
      setAvertissement('');
      let trouve = null;
      try {
        const reponse = await axios.get(`${API_URL}/api/connectors`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const connexions = (reponse.data.connectors || [])
          .filter(c => c.provider === provider && c.isActive);

        if (connexions.length === 0) {
          if (!annule) { setComptes([]); setChargement(false); }
          return;
        }

        /*
         * Une connexion en panne ne doit pas emporter les autres. On interroge toutes les
         * connexions et on nomme celles qui ont échoué : sans cela, une clé API expirée
         * rendrait la liste entière vide, et l'écran dirait « aucun compte » devant trois
         * connexions saines.
         */
        const resultats = await Promise.allSettled(
          connexions.map(c =>
            axios.get(`${API_URL}/api/connectors/${c.id}/accounts${forcer ? '?refresh=1' : ''}`, {
              headers: { Authorization: `Bearer ${token}` }
            }).then(r => ({ connexion: c, comptes: r.data?.accounts || [], inventaire: r.data?.inventaire || null }))
          )
        );

        // L'inventaire le moins avancé commande l'affichage : tant qu'une connexion construit
        // encore sa liste, elle est incomplète.
        const inventaires = resultats
          .filter(r => r.status === 'fulfilled' && r.value.inventaire)
          .map(r => r.value.inventaire);
        const enCours = inventaires.find(i => i.etat === 'en_cours');
        trouve = enCours || inventaires[0] || null;
        if (!annule) setInventaire(trouve);

        const reunis = [];
        const enEchec = [];
        resultats.forEach((r, i) => {
          if (r.status === 'fulfilled') {
            r.value.comptes.forEach(compte => reunis.push({
              ...compte,
              sourceId: compte.sourceId || compte.id,
              connectionId: r.value.connexion.id,
              connectionName: r.value.connexion.connectionName || r.value.connexion.email || null
            }));
          } else {
            enEchec.push(connexions[i].connectionName || connexions[i].email || 'une connexion');
          }
        });

        if (annule) return;
        setComptes(reunis);
        if (enEchec.length > 0) {
          setAvertissement(
            `${enEchec.length} connexion(s) n'ont pas répondu (${enEchec.join(', ')}). `
            + `Les comptes affichés viennent des autres.`
          );
        }
      } catch (erreur) {
        console.error(`Erreur chargement comptes ${provider}:`, erreur);
        if (!annule) setError(erreur.response?.data?.error || 'Erreur lors du chargement des comptes');
      } finally {
        if (!annule) setChargement(false);
      }
      return trouve;
    };

    /*
     * Tant que l'inventaire se construit, on redemande l'avancement toutes les dix secondes.
     *
     * ⚠️ **En discret** : la liste déjà obtenue reste affichée et se complète. La remplacer par
     * un sablier à chaque sondage ferait clignoter l'écran, et empêcherait de cocher un conteneur
     * déjà visible — alors que c'est précisément l'intérêt d'afficher une liste partielle.
     */
    const sonder = async () => {
      const etat = await charger({ discret: true });
      if (annule) return;
      // ⚠️ On ne reprogramme QUE si la construction est encore en cours. Sans cette condition,
      // le sondage tournerait indéfiniment pour les onze autres fournisseurs, dont la liste
      // arrive en une seconde et ne change plus — une requête toutes les dix secondes, pour rien.
      if (etat?.etat === 'en_cours') minuterie = setTimeout(sonder, 10000);
    };

    charger({ forcer: actualisation > 0 }).then((etat) => {
      if (!annule && etat?.etat === 'en_cours') minuterie = setTimeout(sonder, 10000);
    });

    return () => {
      annule = true;
      if (minuterie) clearTimeout(minuterie);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [provider, token, actualisation]);

  // La clé réunit la connexion et le compte : le même compte accessible par deux connexions
  // fait deux lignes distinctes, et cocher l'une ne doit pas cocher l'autre.
  const cle = (compte) => `${compte.connectionId}|${compte.sourceId}`;
  const estSelectionne = (compte) => selection.some(c => cle(c) === cle(compte));

  const basculer = (compte) => {
    // Un compte déjà en bibliothèque ne se coche pas : `POST /api/library/add` le refuserait
    // par un 409, et l'écran annoncerait un échec pour une situation normale.
    if (compte.alreadyInLibrary) return;
    setSelection(prev => estSelectionne(compte)
      ? prev.filter(c => cle(c) !== cle(compte))
      : [...prev, compte]);
  };

  const filtres = comptes.filter(c => {
    const motif = recherche.toLowerCase();
    return String(c.name || '').toLowerCase().includes(motif)
      || String(c.sourceId || '').toLowerCase().includes(motif);
  });

  const enregistrer = async () => {
    if (selection.length === 0) return;
    setEnregistrement(true);
    setProgression({ current: 0, total: selection.length });

    let reussis = 0;
    const erreurs = [];

    for (let i = 0; i < selection.length; i++) {
      const compte = selection[i];
      setProgression({ current: i + 1, total: selection.length });
      try {
        await axios.post(`${API_URL}/api/library/add`, {
          type: provider,
          name: buildAccountName(provider, compte.name, compte.sourceId),
          sourceId: String(compte.sourceId),
          sourceName: compte.name,
          connectionId: compte.connectionId,
          metadata: config.metadata ? config.metadata(compte) : {}
        }, { headers: { Authorization: `Bearer ${token}` } });
        reussis++;
      } catch (erreur) {
        erreurs.push(`${compte.name}: ${erreur.response?.data?.error || erreur.message}`);
      }
    }

    if (reussis > 0) {
      setSuccess(reussis === selection.length
        ? `${reussis} compte(s) ${config.libelle} ajouté(s) avec succès !`
        : `${reussis}/${selection.length} compte(s) ajouté(s). ${erreurs.join(' · ')}`);
      setTimeout(() => setSuccess(''), 5000);
      onAdded();
    } else {
      setError(`Aucun compte n'a pu être ajouté. ${erreurs.join(' · ')}`);
    }

    setEnregistrement(false);
    setProgression({ current: 0, total: 0 });
  };

  if (enregistrement) {
    return (
      <div className="ads-selector saving-fullscreen">
        <div className="saving-content-fullscreen">
          <div className="saving-spinner"><Loader className="spin" size={48} /></div>
          <h3>Ajout en cours...</h3>
          <p>Ajout de <strong>{selection.length} compte(s)</strong> à votre bibliothèque</p>
          <div className="saving-progress-fullscreen">
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">1</span>
              <span className="step-label-fullscreen">Création des comptes ({progression.current}/{progression.total})</span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-selector">
      <div className="selector-header" style={{ display: 'flex', alignItems: 'center', marginBottom: '1rem' }}>
        <button onClick={onBack} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '8px', marginRight: '8px' }}>
          <ChevronLeft size={20} />
        </button>
        <img src={config.logo} alt={config.libelle} style={{ width: '32px', height: '32px', marginRight: '12px', objectFit: 'contain' }} />
        <div>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: '600' }}>Comptes {config.libelle}</h3>
          <p style={{ margin: 0, fontSize: '0.85rem', color: '#6b7280' }}>Sélectionnez les comptes à ajouter</p>
        </div>
      </div>

      {avertissement && (
        <div style={{ padding: '10px 12px', marginBottom: '12px', backgroundColor: '#fffbeb', border: '1px solid #fde68a', borderRadius: '8px', fontSize: '0.85rem', color: '#92400e' }}>
          {avertissement}
        </div>
      )}

      {/*
        * L'avancement d'un inventaire, et le bouton qui le reconstruit.
        *
        * ⚠️ **Une progression chiffrée, pas un sablier.** Lister les conteneurs d'un compte
        * d'agence demande un appel par compte GTM, et le quota en tolère une vingtaine par
        * minute : la construction dure des minutes. Un sablier sans fin se lit comme une panne ;
        * « 24 / 63 comptes lus » se lit comme une attente. Et la liste partielle reste
        * sélectionnable pendant ce temps.
        */}
      {inventaire && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: '12px',
          padding: '10px 12px', marginBottom: '12px', borderRadius: '8px', fontSize: '0.85rem',
          backgroundColor: inventaire.etat === 'echec' ? '#fef2f2' : '#f0f9ff',
          border: `1px solid ${inventaire.etat === 'echec' ? '#fecaca' : '#bae6fd'}`,
          color: inventaire.etat === 'echec' ? '#991b1b' : '#075985'
        }}>
          {inventaire.etat === 'en_cours' && <Loader className="spin" size={16} />}
          <span style={{ flex: 1 }}>
            {inventaire.etat === 'en_cours' && (
              inventaire.comptesTotal
                ? `Inventaire en cours : ${inventaire.comptesLus} / ${inventaire.comptesTotal} comptes lus. Les conteneurs déjà trouvés sont sélectionnables.`
                : 'Inventaire en cours : lecture des comptes Google Tag Manager…'
            )}
            {inventaire.etat === 'pret' && (
              `${comptes.length} conteneur(s) — liste établie le ${new Date(inventaire.majLe).toLocaleString('fr-FR')}.`
            )}
            {inventaire.etat === 'echec' && (
              `L'inventaire a échoué : ${inventaire.erreur || 'cause inconnue'}.`
            )}
          </span>
          {inventaire.etat !== 'en_cours' && (
            <button
              onClick={() => setActualisation(n => n + 1)}
              style={{
                padding: '5px 10px', border: '1px solid currentColor', borderRadius: '6px',
                background: 'transparent', color: 'inherit', cursor: 'pointer', fontSize: '0.8rem',
                whiteSpace: 'nowrap'
              }}
              title="Relit tous les comptes Google Tag Manager. Plusieurs minutes sur un gros parc."
            >
              Actualiser
            </button>
          )}
        </div>
      )}

      {chargement ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
        </div>
      ) : comptes.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', color: '#6b7280' }}>
          <p>Aucun compte {config.libelle} disponible</p>
          <p style={{ fontSize: '0.85rem' }}>
            Vérifiez que la connexion est active dans l'onglet Connecteurs.
          </p>
        </div>
      ) : (
        <>
          {/*
            * ⚠️ **Trois réglages, et chacun répare un débordement distinct.**
            *
            * 1. `boxSizing: 'border-box'` sur le champ : le projet n'a **aucune** règle
            *    universelle (vérifié dans `App.css`), donc `width: 100%` s'ajoutait aux 48 px de
            *    rembourrage et aux 2 px de bordure — le champ dépassait de 50 px et recouvrait le
            *    bouton, dont on ne lisait plus que « sélectionner ».
            * 2. `minWidth: 0` sur le conteneur `flex: 1` : un élément flexible refuse par défaut
            *    de descendre sous la largeur de son contenu, et pousse ses voisins dehors plutôt
            *    que de se rétrécir.
            * 3. `flexShrink: 0` et `whiteSpace: 'nowrap'` sur les boutons : sans eux, ce sont eux
            *    qui s'écrasent, et un libellé coupé en deux se lit comme un défaut d'affichage.
            */}
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <Search size={16} style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#9ca3af', pointerEvents: 'none' }} />
              <input
                type="text"
                placeholder="Rechercher un compte..."
                value={recherche}
                onChange={(e) => setRecherche(e.target.value)}
                style={{ width: '100%', boxSizing: 'border-box', padding: '10px 12px 10px 36px', border: '1px solid #e5e7eb', borderRadius: '8px', fontSize: '0.9rem' }}
              />
            </div>
            <button onClick={() => setSelection(filtres.filter(c => !c.alreadyInLibrary))} style={{ padding: '8px 12px', fontSize: '0.85rem', background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', flexShrink: 0, whiteSpace: 'nowrap' }}>
              Tout sélectionner
            </button>
            {selection.length > 0 && (
              <button onClick={() => setSelection([])} style={{ padding: '8px 12px', fontSize: '0.85rem', background: 'none', border: '1px solid #e5e7eb', borderRadius: '6px', cursor: 'pointer', color: '#6b7280', flexShrink: 0, whiteSpace: 'nowrap' }}>
                Désélectionner
              </button>
            )}
          </div>

          <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e5e7eb', borderRadius: '8px' }}>
            {filtres.map(compte => (
              <div
                key={cle(compte)}
                onClick={() => basculer(compte)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  padding: '12px 16px',
                  borderBottom: '1px solid #f3f4f6',
                  // Déjà en bibliothèque : la ligne reste visible mais inerte. La masquer
                  // laisserait croire que le compte est inaccessible.
                  cursor: compte.alreadyInLibrary ? 'not-allowed' : 'pointer',
                  backgroundColor: compte.alreadyInLibrary
                    ? '#f9fafb'
                    : (estSelectionne(compte) ? '#f0fdf4' : 'white'),
                  opacity: compte.alreadyInLibrary ? 0.65 : 1,
                  transition: 'background-color 0.15s'
                }}
              >
                <div style={{
                  width: '20px', height: '20px', borderRadius: '4px',
                  border: (estSelectionne(compte) || compte.alreadyInLibrary) ? 'none' : '2px solid #d1d5db',
                  backgroundColor: compte.alreadyInLibrary
                    ? '#9ca3af'
                    : (estSelectionne(compte) ? '#22c55e' : 'white'),
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  marginRight: '12px', flexShrink: 0
                }}>
                  {(estSelectionne(compte) || compte.alreadyInLibrary) && <Check size={14} color="white" />}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <span style={{ fontWeight: '500', color: compte.alreadyInLibrary ? '#6b7280' : '#111827' }}>
                      {compte.name}
                    </span>
                    {compte.alreadyInLibrary && (
                      <span style={{ fontSize: '0.7rem', padding: '2px 8px', borderRadius: '999px', backgroundColor: '#e5e7eb', color: '#4b5563', fontWeight: '500', whiteSpace: 'nowrap' }}>
                        Déjà ajouté
                      </span>
                    )}
                  </div>
                  <div style={{ fontSize: '0.8rem', color: '#6b7280' }}>
                    {compte.sourceId}
                    {/* La connexion d'origine, quand plusieurs servent la plateforme : deux
                        lignes homonymes sinon indiscernables. */}
                    {compte.connectionName && comptes.some(c => c.connectionId !== compte.connectionId)
                      ? ` · ${compte.connectionName}`
                      : ''}
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
            <span style={{ fontSize: '0.9rem', color: '#6b7280' }}>
              {selection.length} compte(s) sélectionné(s)
              {comptes.filter(c => c.alreadyInLibrary).length > 0
                && ` · ${comptes.filter(c => c.alreadyInLibrary).length} déjà en bibliothèque`}
            </span>
            <button
              onClick={enregistrer}
              disabled={selection.length === 0}
              style={{
                padding: '10px 20px',
                backgroundColor: selection.length > 0 ? '#000000' : '#e5e7eb',
                color: selection.length > 0 ? 'white' : '#9ca3af',
                border: 'none', borderRadius: '8px', fontWeight: '500',
                cursor: selection.length > 0 ? 'pointer' : 'not-allowed'
              }}
            >
              Ajouter à la bibliothèque
            </button>
          </div>
        </>
      )}
    </div>
  );
}

// Composant: Sélecteur Google Analytics 4
function GoogleAnalyticsSelector({ token, onAdded, onBack, setSuccess, setError }) {
  const [properties, setProperties] = useState([]);
  const [selectedProperties, setSelectedProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savingProgress, setSavingProgress] = useState({ current: 0, total: 0 });
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadProperties();
  }, []);

  // Filtrer les propriétés selon le terme de recherche
  const filteredProperties = properties.filter(property => {
    const search = searchTerm.toLowerCase();
    return (
      property.propertyName?.toLowerCase().includes(search) ||
      property.propertyId?.toLowerCase().includes(search) ||
      property.accountName?.toLowerCase().includes(search)
    );
  });

  const loadProperties = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/api/library/google-analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        setProperties(response.data.properties || []);
      }
    } catch (error) {
      console.error('Erreur chargement propriétés GA4:', error);
    } finally {
      setLoading(false);
    }
  };

  const togglePropertySelection = (property) => {
    setSelectedProperties(prev => {
      const isSelected = prev.some(p => p.propertyId === property.propertyId);
      if (isSelected) {
        return prev.filter(p => p.propertyId !== property.propertyId);
      } else {
        return [...prev, property];
      }
    });
  };

  const selectAllProperties = () => {
    setSelectedProperties([...filteredProperties]);
  };

  const deselectAllProperties = () => {
    setSelectedProperties([]);
  };

  const isPropertySelected = (property) => {
    return selectedProperties.some(p => p.propertyId === property.propertyId);
  };

  const saveToLibrary = async () => {
    if (selectedProperties.length === 0) return;

    setSaving(true);
    setSavingProgress({ current: 0, total: selectedProperties.length });

    let successCount = 0;
    let errors = [];

    try {
      const connectorsResponse = await axios.get(`${API_URL}/api/connectors`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      const gaConnection = connectorsResponse.data.connectors.find(c =>
        c.provider === 'GOOGLE_ANALYTICS' && c.isActive
      );

      if (!gaConnection) {
        throw new Error('Connexion Google Analytics non trouvée');
      }

      for (let i = 0; i < selectedProperties.length; i++) {
        const property = selectedProperties[i];
        setSavingProgress({ current: i + 1, total: selectedProperties.length });

        try {
          await axios.post(`${API_URL}/api/library/add`, {
            type: 'GOOGLE_ANALYTICS',
            name: property.propertyName,
            sourceId: property.propertyId,
            sourceName: property.propertyName,
            connectionId: gaConnection.id,
            metadata: {
              propertyId: property.propertyId,
              propertyName: property.propertyName,
              accountId: property.accountId,
              accountName: property.accountName
            }
          }, {
            headers: { Authorization: `Bearer ${token}` }
          });

          successCount++;
        } catch (propError) {
          console.error(`Erreur ajout propriété ${property.propertyName}:`, propError);
          errors.push(`${property.propertyName}: ${propError.response?.data?.error || propError.message}`);
        }
      }

      if (successCount > 0) {
        const message = successCount === selectedProperties.length
          ? `${successCount} propriété(s) ajoutée(s) avec succès !`
          : `${successCount}/${selectedProperties.length} propriété(s) ajoutée(s).`;

        setSuccess(message);
        setTimeout(() => setSuccess(''), 5000);

        onAdded();
      } else {
        setError(`Aucune propriété n'a pu être ajoutée. Erreurs: ${errors.join(', ')}`);
      }
    } catch (error) {
      console.error('Erreur sauvegarde GA4:', error);
      setError('Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
      setSavingProgress({ current: 0, total: 0 });
    }
  };

  if (saving) {
    return (
      <div className="ads-selector saving-fullscreen">
        <div className="saving-content-fullscreen">
          <div className="saving-spinner">
            <Loader className="spin" size={48} />
          </div>
          <h3>Ajout en cours...</h3>
          <p>Ajout de <strong>{selectedProperties.length} propriété(s)</strong> à votre bibliothèque</p>
          <div className="saving-progress-fullscreen">
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">1</span>
              <span className="step-text-fullscreen">Vérification de la connexion</span>
            </div>
            <div className="progress-step-fullscreen active">
              <span className="step-number-fullscreen">2</span>
              <span className="step-text-fullscreen">Enregistrement de la propriété</span>
            </div>
            <div className="progress-step-fullscreen">
              <span className="step-number-fullscreen">3</span>
              <span className="step-text-fullscreen">Finalisation</span>
            </div>
          </div>
          <p className="saving-note-fullscreen">Veuillez patienter...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ads-selector">
      <div className="selector-header" style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '12px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
          <button onClick={onBack} className="btn btn-secondary" style={{ backgroundColor: '#f8f5f0', color: '#1a1a1a', border: '1px solid #e5e0d5' }}>← Retour</button>
          <h4 style={{ display: 'flex', alignItems: 'center', margin: 0 }}>
            <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png" alt="Google Analytics 4" style={{ width: '24px', height: '24px', marginRight: '8px', borderRadius: '4px' }} />
            Sélectionner une propriété GA4
          </h4>
        </div>
        <button
          onClick={() => selectedProperties.length > 0 && saveToLibrary()}
          disabled={selectedProperties.length === 0 || saving}
          className="btn btn-primary"
          style={{
            fontSize: '14px',
            padding: '10px 20px',
            fontWeight: '600',
            opacity: selectedProperties.length === 0 ? 0.5 : 1,
            cursor: selectedProperties.length === 0 ? 'not-allowed' : 'pointer'
          }}
        >
          {saving ? 'Ajout en cours...' : `+ Ajouter ${selectedProperties.length > 0 ? `(${selectedProperties.length})` : ''}`}
        </button>
      </div>

      {loading ? (
        <div className="loading-state" style={{ textAlign: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
          <p>Chargement des propriétés Google Analytics 4...</p>
        </div>
      ) : properties.length === 0 ? (
        <div className="empty-state" style={{ textAlign: 'center', padding: '40px' }}>
          <p>Aucune propriété Google Analytics 4 trouvée.</p>
          <p style={{ fontSize: '0.9rem', color: '#6b7280' }}>
            Vérifiez que votre compte Google a accès à des propriétés GA4.
          </p>
        </div>
      ) : (
        <>
          <div className="search-bar" style={{ padding: '16px 0' }}>
            <input
              type="text"
              placeholder="Rechercher une propriété..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                width: '100%',
                padding: '10px 16px',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                fontSize: '14px'
              }}
            />
          </div>

          <div className="accounts-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: '12px',
            maxHeight: '400px',
            overflowY: 'auto',
            padding: '4px'
          }}>
            {(() => {
              if (filteredProperties.length === 0) {
                return (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#6b7280', gridColumn: '1 / -1' }}>
                    Aucune propriété trouvée pour "{searchTerm}"
                  </div>
                );
              }

              return filteredProperties.map(property => (
                <div
                  key={property.propertyId}
                  onClick={() => togglePropertySelection(property)}
                  style={{
                    padding: '16px',
                    border: `2px solid ${isPropertySelected(property) ? '#F9AB00' : '#e5e7eb'}`,
                    borderRadius: '12px',
                    backgroundColor: isPropertySelected(property) ? '#fffbeb' : 'white',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease'
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                      width: '20px',
                      height: '20px',
                      borderRadius: '4px',
                      border: `2px solid ${isPropertySelected(property) ? '#F9AB00' : '#d1d5db'}`,
                      backgroundColor: isPropertySelected(property) ? '#F9AB00' : 'white',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {isPropertySelected(property) && <CheckCircle size={14} style={{ color: 'white' }} />}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: '600', color: '#111827' }}>{property.propertyName}</div>
                      <div style={{ fontSize: '0.85rem', color: '#6b7280' }}>ID: {property.propertyId}</div>
                      {property.accountName && (
                        <span style={{ fontSize: '10px', backgroundColor: '#e5e7eb', color: '#374151', padding: '2px 6px', borderRadius: '4px', marginTop: '4px', display: 'inline-block' }}>
                          {property.accountName}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Identité d'affichage des connecteurs : un seul endroit pour le libellé et la
 * marque, là où la page les répétait dans autant de branches JSX que de
 * fournisseurs. `emoji` ne sert que de repli pour les services sans logo
 * (météo) ; un fournisseur absent de la table retombe sur son identifiant.
 */
const META_CONNECTEURS = {
  GOOGLE_ADS: { libelle: 'Google Ads', logo: googleAdsLogo },
  MICROSOFT_ADS: { libelle: 'Microsoft Ads', logo: microsoftAdsLogo },
  FACEBOOK_ADS: { libelle: 'Meta Ads', logo: metaAdsLogo },
  PINTEREST_ADS: { libelle: 'Pinterest Ads', logo: pinterestAdsLogo },
  TIKTOK_ADS: { libelle: 'TikTok Ads', logo: tiktokAdsLogo },
  OPENAI_ADS: { libelle: 'ChatGPT Ads', logo: chatgptAdsLogo },
  AWIN: { libelle: 'Awin', logo: awinLogo },
  PRESTASHOP: { libelle: 'PrestaShop', logo: prestashopLogo },
  GOOGLE_MERCHANT_API: { libelle: 'Google Merchant Center', logo: merchantLogo },
  GOOGLE_ANALYTICS: { libelle: 'Google Analytics 4', logo: googleAnalyticsLogo },
  GOOGLE_SEARCH_CONSOLE: { libelle: 'Google Search Console', logo: searchConsoleLogo },
  GOOGLE_TAG_MANAGER: { libelle: 'Google Tag Manager', logo: tagManagerLogo },
  MATOMO: { libelle: 'Matomo Analytics', logo: matomoLogo },
  MATOMO_OAUTH: { libelle: 'Matomo Analytics', logo: matomoLogo },
  PIANO_ANALYTICS: { libelle: 'Piano Analytics', logo: pianoLogo },
  GOOGLE_SHEETS: { libelle: 'Google Sheets', emoji: '📊' },
  OPENWEATHERMAP: { libelle: 'OpenWeatherMap', emoji: '🌤️' },
  OPEN_METEO: { libelle: 'Open-Meteo', emoji: '🌦️' }
};

/**
 * Le singulier d'un mot que le serveur rend au pluriel.
 *
 * ⚠️ Le serveur nomme ce qu'une clé ouvre — « sites », « boutiques », « comptes annonceurs » —
 * parce que lui seul le sait. L'écran a besoin du singulier pour compter, et une seconde table
 * de mots côté navigateur divergerait de la première au premier fournisseur ajouté : on dérive.
 * Les trois libellés servis sont réguliers ; un mot qui ne le serait pas se repérerait d'emblée.
 */
const singulier = (mot) => String(mot).replace(/s\b/g, '');

/**
 * Route de ré-authentification par fournisseur. Les huit blocs JSX qui
 * dupliquaient le bouton « Reconnecter » ne différaient que par ce chemin.
 * Un fournisseur absent se répare par son formulaire, pas par OAuth : aucun
 * bouton n'est alors proposé.
 */
const CHEMINS_RECONNEXION = {
  GOOGLE_ADS: 'google-ads',
  MICROSOFT_ADS: 'microsoft-ads',
  FACEBOOK_ADS: 'facebook-ads',
  TIKTOK_ADS: 'tiktok-ads',
  PINTEREST_ADS: 'pinterest-ads',
  GOOGLE_MERCHANT_API: 'google-merchant-api',
  GOOGLE_ANALYTICS: 'google-analytics',
  GOOGLE_SEARCH_CONSOLE: 'google-search-console',
  GOOGLE_TAG_MANAGER: 'google-tag-manager'
};

/** Chaque fournisseur nomme autrement ce qu'il expose : instance, compte, utilisateur ou email. */
function descriptionCompte(connector) {
  if (connector.provider === 'MATOMO') {
    return { qualificatif: 'Instance', valeur: connector.metadata?.instanceUrl || connector.connectionName };
  }
  if (connector.provider === 'PIANO_ANALYTICS') {
    return { qualificatif: 'Compte', valeur: connector.connectionName };
  }
  /*
   * PrestaShop : l'adresse de la boutique, comme Matomo rend son instance. C'est la seule chose
   * qui distingue deux connexions dans la liste — `connectionName` vaut le domaine par défaut,
   * mais l'utilisateur peut l'avoir renommé.
   */
  if (connector.provider === 'PRESTASHOP') {
    return { qualificatif: 'Boutique', valeur: connector.metadata?.baseUrl || connector.connectionName };
  }
  if (connector.provider === 'PINTEREST_ADS' || connector.provider === 'TIKTOK_ADS') {
    return { qualificatif: 'Utilisateur', valeur: connector.connectorEmail || connector.connectionName };
  }
  return { qualificatif: 'Email', valeur: connector.connectorEmail || connector.connectionName };
}

/**
 * Une erreur prime sur le drapeau d'activité : un connecteur actif mais en erreur n'est pas
 * « actif ». `rang` ordonne la colonne État : croissant, les ennuis d'abord — sur un écran
 * de connecteurs, ce qu'on cherche en triant par état, c'est ce qui est cassé, pas la
 * lettre E avant la lettre I.
 */
/**
 * Le libellé dit **quelle** erreur, et non « Erreur ».
 *
 * Le détail vivait dans une rangée rouge sous la ligne — « Token Facebook expiré » — qui coupait
 * le tableau en deux pour un message que la pastille peut porter elle-même. Un jeton expiré se
 * répare en se reconnectant, et le bouton est déjà là, sur la même ligne.
 *
 * ⚠️ Le mot vient de `libelleRefusConnecteur`, que le badge de l'accueil lit aussi : les deux
 * écrans montraient déjà deux verdicts différents pour le même compte.
 */
function etatConnecteur(connector) {
  if (connector.lastError) {
    return {
      classe: 'erreur',
      libelle: libelleRefusConnecteur(connector.lastError),
      rang: 0,
      explication: connector.lastError
    };
  }
  if (!connector.isActive) return { classe: 'inactif', libelle: 'Inactif', rang: 1, explication: null };
  /*
   * ⚠️ **« Non vérifié » n'est pas un état dégradé, c'est l'absence de verdict.**
   *
   * Le serveur rend `verifie: false` quand la sonde n'a pas pu conclure — réseau, 5xx, quota,
   * délai dépassé. Le ranger sous « Actif » serait affirmer qu'une connexion va bien parce
   * qu'on n'a pas pu la joindre : c'est très exactement le défaut qui a fait afficher « Actif »
   * pendant trois semaines sur une clé Awin refusée. Le ranger sous « Erreur » serait l'excès
   * inverse, et ferait défaire un réglage intact.
   *
   * ⚠️ `verifie` **absent** vaut non vérifié, et non l'inverse : un serveur plus ancien que cet
   * écran ne doit pas faire passer pour constaté ce qu'il n'a pas constaté.
   */
  if (connector.verifie !== true) {
    return {
      classe: 'non-verifie',
      libelle: 'Non vérifié',
      rang: 2,
      explication: connector.motifNonVerifie
        ? `La vérification n'a pas abouti : ${connector.motifNonVerifie}`
        : "La vérification n'a pas abouti. L'état affiché est celui d'avant."
    };
  }
  return { classe: 'actif', libelle: 'Actif', rang: 3, explication: null };
}

/** Colonnes triables de la liste, dans leur ordre d'affichage. La colonne Actions ne l'est pas. */
const COLONNES_CONNECTEURS = [
  { cle: 'connecteur', libelle: 'Connecteur' },
  { cle: 'identifiant', libelle: 'Identifiant' },
  { cle: 'date', libelle: 'Connecté le' },
  // La clé reste `statut` : elle sert au tri et au comparateur, et la renommer n'apporterait
  // rien qu'un risque. Seul le libellé change, pour parler comme l'écran Comptes.
  { cle: 'statut', libelle: 'État' }
];

/**
 * Compare deux lignes sur une colonne.
 *
 * Deux règles qui ne vont pas de soi :
 *
 * 1. **Une valeur absente n'est ni la plus petite ni la plus grande** : elle part en fin de
 *    liste dans les deux sens. Les services météo n'ont pas de date de connexion ; les placer
 *    en tête d'un tri « du plus ancien » les ferait passer pour les plus vieux connecteurs.
 * 2. **À égalité, on retombe sur le nom**, toujours croissant. Sans quoi un tri par statut
 *    rendrait trois blocs dont l'ordre interne dépendrait de celui de la réponse de l'API.
 */
function comparerConnecteurs(a, b, colonne, sens) {
  const va = a.tri[colonne];
  const vb = b.tri[colonne];

  const aVide = va === null || va === undefined || va === '';
  const bVide = vb === null || vb === undefined || vb === '';
  if (aVide !== bVide) return aVide ? 1 : -1;

  if (!aVide) {
    const ordre = typeof va === 'number' && typeof vb === 'number'
      ? va - vb
      : String(va).localeCompare(String(vb), 'fr', { sensitivity: 'base' });
    if (ordre !== 0) return sens === 'asc' ? ordre : -ordre;
  }

  return String(a.tri.connecteur).localeCompare(String(b.tri.connecteur), 'fr', { sensitivity: 'base' })
    || String(a.tri.identifiant || '').localeCompare(String(b.tri.identifiant || ''), 'fr', { sensitivity: 'base' });
}

/**
 * Passerelle entre les clés de configuration des métriques et les champs des données
 * comparatives. Le dashboard comparatif expose désormais les mêmes colonnes que le global,
 * mais les deux mondes ne nomment pas les champs pareil : la configuration est en français,
 * les données comparatives portent les noms de l'API.
 *
 * `views` est un comptage, `vtr` et `cpv` des ratios déjà dérivés côté serveur sur des sommes
 * de période — jamais des moyennes de ratios, qui seraient fausses d'une période à l'autre.
 */
/**
 * Métriques dérivées d'un agrégat comparatif, côté affichage.
 *
 * Le tableau « Totaux » du comparatif recalcule ses propres totaux plutôt que de lire ceux
 * du serveur. Les cinq métriques qui ne sont pas servies telles quelles doivent donc être
 * dérivées ici aussi — sur des sommes de période, jamais en moyennant des ratios.
 *
 * Le taux de vue se rapporte aux impressions **vidéo**, d'où `videoImpressions`, transporté
 * à part depuis la collecte : le rapporter aux impressions totales le diviserait par trois.
 */
function derivéesComparatif(m) {
  const conversions = m.conversions || 0;
  const conversionsValue = m.conversionsValue || 0;
  const cost = m.cost || 0;
  const views = m.views || 0;
  const videoImpressions = m.videoImpressions || 0;
  const viewCost = m.viewCost || 0;

  return {
    panierMoyen: conversions > 0 ? conversionsValue / conversions : 0,
    roas: cost > 0 ? (conversionsValue / cost) * 100 : 0,
    roi: cost > 0 ? (conversionsValue - cost) / cost : 0,
    vtr: videoImpressions > 0 ? (views / videoImpressions) * 100 : 0,
    cpv: views > 0 ? viewCost / views : 0
  };
}

/** Icône par axe de segmentation du comparatif, pour coiffer chaque tableau. */
const ICONE_SEGMENT = {
  network: <Globe size={20} />,
  campaign: <Target size={20} />,
  adgroup: <Layers size={20} />,
  device: <Smartphone size={20} />,
  weekday: <Calendar size={20} />,
  geo: <MapPin size={20} />,
  age: <Users size={20} />,
  gender: <UserIcon size={20} />
};

const CORRESPONDANCE_COMPARATIF = {
  impressions:  { key: 'impressions',      label: 'Impressions',      format: 'number' },
  clics:        { key: 'clicks',           label: 'Clics',            format: 'number' },
  cout:         { key: 'cost',             label: 'Coût',             format: 'currency' },
  conv:         { key: 'conversions',      label: 'Conversions',      format: 'conversions' },
  valeur_conv:  { key: 'conversionsValue', label: 'Valeur de Conv.',  format: 'currency' },
  ctr:          { key: 'ctr',              label: 'CTR',              format: 'percentage' },
  cpc:          { key: 'cpc',              label: 'CPC',              format: 'currency' },
  taux_conv:    { key: 'conversionRate',   label: 'Taux Conv.',       format: 'percentage' },
  cpa:          { key: 'cpa',              label: 'CPA',              format: 'currency' },
  cos:          { key: 'cos',              label: 'COS',              format: 'percentage' },
  panier_moyen: { key: 'panierMoyen',      label: 'Panier moyen',     format: 'currency' },
  roas:         { key: 'roas',             label: 'ROAS',             format: 'percentage' },
  roi:          { key: 'roi',              label: 'ROI',              format: 'decimal' },
  views:        { key: 'views',            label: 'Vues',             format: 'number' },
  vtr:          { key: 'vtr',              label: 'Taux de vue',      format: 'percentage' },
  cpv:          { key: 'cpv',              label: 'CPV',              format: 'cpv' }
};

// Composant: Vue Connecteurs
function ConnectorsView({ token, testOpenWeatherMap, weatherTesting, weatherTestResult, testOpenMeteo, openMeteoTesting, openMeteoTestResult, connecting, setConnecting, handleWeatherConnector, globalConnectors, userSubscription, setPianoSites, openAddConnectorModalOnLoad, setOpenAddConnectorModalOnLoad, onRefreshLibrary, clientFiltre = null }) {
  /*
   * Le message exact de la régie, au survol de la pastille d'état. Voir la règle des
   * infobulles dans `.claude/CLAUDE.md` : un `title` natif ne suffit pas, et une bulle écrite
   * à la main serait coupée par l'`overflow: hidden` de la liste.
   */
  const { survoler: survolerEtat, quitter: quitterEtat, bulle: bulleEtat } = useBulleAuSurvol();
  const [connectors, setConnectors] = useState([]);
  /*
   * Le remplacement de clé d'un connecteur à formulaire.
   *
   * ⚠️ **Les champs viennent du serveur** (`connector.champsIdentifiants`), jamais d'une table
   * tenue ici : la forme des identifiants est une affaire de régie, et une liste en double
   * divergerait au premier connecteur ajouté — un champ manquant, donc une clé incomplète,
   * donc un refus que personne ne saurait expliquer.
   */
  const [modaleCle, setModaleCle] = useState({ show: false, connector: null });
  const [valeursCle, setValeursCle] = useState({});
  const [erreurCle, setErreurCle] = useState('');
  const [envoiCle, setEnvoiCle] = useState(false);

  const ouvrirRemplacementCle = (connector) => {
    setValeursCle({});
    setErreurCle('');
    setModaleCle({ show: true, connector });
  };

  const fermerRemplacementCle = () => {
    if (envoiCle) return;
    /*
     * ⚠️ Les valeurs saisies sont effacées à la fermeture : ce sont des secrets, et les garder
     * en mémoire du composant les ferait réapparaître à la réouverture — sur un autre
     * connecteur, le cas échéant.
     */
    setValeursCle({});
    setErreurCle('');
    setModaleCle({ show: false, connector: null });
  };

  /*
   * La récolte des comptes qu'une clé ouvre. Le geste jumeau du remplacement de clé : on
   * répare la clé, puis on récolte.
   *
   * ⚠️ Le résultat **n'est pas un simple « c'est fait »** : il compte ce qui a été ajouté, ce
   * que le quota a écarté, et surtout il nomme les comptes que la clé **n'ouvre plus**. Ce
   * dernier point est la seule chose que le sélecteur « Ajouter des données » ne sait pas dire,
   * et c'est ce qui justifie un second geste à côté de lui.
   */
  const [synchroEnCours, setSynchroEnCours] = useState(null);
  const [resultatSynchro, setResultatSynchro] = useState(null);

  const synchroniserLesComptes = async (connector) => {
    setSynchroEnCours(connector.id);
    setResultatSynchro(null);
    const mot = connector.synchronisationComptes.libelle;
    try {
      const { data } = await axios.post(
        `${API_URL}/api/connectors/${connector.id}/comptes/synchroniser`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );

      const lignes = [];
      lignes.push(data.ajoutes.length === 0
        ? `Aucun nouveau ${singulier(mot)} à ajouter.`
        : `${data.ajoutes.length} ${data.ajoutes.length === 1 ? singulier(mot) : mot} ajouté${data.ajoutes.length === 1 ? '' : 's'} : `
          + data.ajoutes.map(c => c.name).join(', ') + '.');

      if (data.ecartesParLeQuota > 0) {
        lignes.push(`${data.ecartesParLeQuota} n'${data.ecartesParLeQuota === 1 ? 'a' : 'ont'} pas été ajouté${data.ecartesParLeQuota === 1 ? '' : 's'} : `
          + 'le quota de comptes de votre abonnement est atteint.');
      }

      /*
       * ⚠️ **Nommés, jamais désactivés.** Un compte que la clé n'ouvre plus peut l'être pour
       * un droit retiré — mais aussi pour une réponse partielle ou un accès suspendu une heure.
       * Le retirer sur cette foi ferait disparaître ses données d'un dashboard. C'est
       * l'utilisateur qui décide, et la phrase le dit.
       */
      if (data.disparus.length > 0) {
        lignes.push(`${data.disparus.length} ${data.disparus.length === 1 ? singulier(mot) : mot} `
          + `n'${data.disparus.length === 1 ? 'est' : 'sont'} plus ouvert${data.disparus.length === 1 ? '' : 's'} par cette clé : `
          + data.disparus.map(c => c.name).join(', ')
          + '. Rien n\'a été retiré — à vous de voir.');
      }

      setResultatSynchro({ connectorId: connector.id, ton: 'ok', texte: lignes.join('\n') });
      await loadConnectors();
      if (onRefreshLibrary) onRefreshLibrary();
    } catch (erreur) {
      setResultatSynchro({
        connectorId: connector.id,
        ton: 'erreur',
        texte: messageErreur(erreur, 'Les comptes n’ont pas pu être synchronisés.')
      });
    } finally {
      setSynchroEnCours(null);
    }
  };

  const envoyerNouvelleCle = async () => {
    const connector = modaleCle.connector;
    if (!connector) return;
    setEnvoiCle(true);
    setErreurCle('');
    try {
      await axios.put(
        `${API_URL}/api/connectors/${connector.id}/identifiants`,
        valeursCle,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setModaleCle({ show: false, connector: null });
      setValeursCle({});
      await loadConnectors();
      if (onRefreshLibrary) onRefreshLibrary();
    } catch (erreur) {
      /*
       * ⚠️ `texteAffichable` et non `erreur.response.data.error` : une passerelle répond
       * `{ code, message }` là où nos routes rendent une chaîne, et un objet atteignant le
       * rendu démonte l'arbre React — le défaut déjà corrigé dans l'Optimiseur de flux.
       */
      setErreurCle(messageErreur(erreur, 'La clé n’a pas pu être remplacée.'));
    } finally {
      setEnvoiCle(false);
    }
  };
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  /**
   * De quoi rapprocher un connecteur d'un client : les comptes de la bibliothèque et les sites.
   *
   * ⚠️ **Un connecteur ne porte rien qui dise à quel client il sert.** Le lien passe par ses
   * comptes et par les sites qui les rattachent — c'est la même chaîne que dans le
   * Gestionnaire, et elle vit dans `utils/perimetreClient.js`.
   *
   * ⚠️ **Trois états, pas deux.** `null` veut dire « on ne sait pas encore », ou « on n'a pas
   * pu savoir » : aucun filtrage n'est alors appliqué. Un tableau vide au repli du `catch`
   * éteindrait toute la liste après un simple refus, en affirmant que ce client n'a aucun
   * connecteur — une absence qu'aucune réponse n'a établie.
   */
  const [donneesDuPerimetre, setDonneesDuPerimetre] = useState(null);
  const [deleteModal, setDeleteModal] = useState({ show: false, connector: null, libraryItems: [] });
  const [loadingDelete, setLoadingDelete] = useState(false);

  // États pour la modal Matomo
  const [matomoModal, setMatomoModal] = useState({ show: false });

  // États pour la modal Piano Analytics
  const [pianoModal, setPianoModal] = useState({ show: false });

  /**
   * Ouverture des formulaires à saisie. Leur contenu vit désormais dans
   * `components/connectors/`, pour que le parcours d'installation puisse les ouvrir aussi ;
   * il ne reste ici que le drapeau qui les affiche.
   */
  const [awinModal, setAwinModal] = useState({ show: false });
  const [prestashopModal, setPrestashopModal] = useState({ show: false });
  const [openaiModal, setOpenaiModal] = useState({ show: false });

  // État pour la modal d'ajout de connecteur
  const [addConnectorModalOpen, setAddConnectorModalOpen] = useState(false);

  const [rechercheConnecteurs, setRechercheConnecteurs] = useState('');

  // Tri de la liste. Par défaut alphabétique sur le nom de la plateforme : l'API rend les
  // connecteurs dans un ordre qui n'en est pas un, et on cherche un connecteur par son nom.
  const [tri, setTri] = useState({ colonne: 'connecteur', sens: 'asc' });

  const basculerTri = (colonne) => {
    setTri(precedent => precedent.colonne === colonne
      ? { colonne, sens: precedent.sens === 'asc' ? 'desc' : 'asc' }
      : { colonne, sens: 'asc' });
  };

  useEffect(() => {
    loadConnectors();
  }, []);

  // Ouvrir une modale à l'arrivée sur la page.
  //
  // Le drapeau vaut `true` pour la modale générique « ajouter un connecteur », ou le nom
  // d'un fournisseur pour ouvrir directement sa modale. Matomo, Piano et ChatGPT Ads se
  // connectent par formulaire et non par OAuth : depuis le Gestionnaire, on renvoie ici,
  // et sans ce nom l'utilisateur atterrissait sur la page sans qu'aucune modale s'ouvre.
  useEffect(() => {
    if (!openAddConnectorModalOnLoad || loading) return;

    const modalesParFournisseur = {
      MATOMO: () => setMatomoModal({ show: true }),
      PIANO_ANALYTICS: () => setPianoModal({ show: true }),
      OPENAI_ADS: () => setOpenaiModal({ show: true }),
      AWIN: () => setAwinModal({ show: true }),
      PRESTASHOP: () => setPrestashopModal({ show: true })
    };

    const ouvrir = modalesParFournisseur[openAddConnectorModalOnLoad];
    if (ouvrir) ouvrir(); else setAddConnectorModalOpen(true);

    setOpenAddConnectorModalOnLoad(false);
  }, [openAddConnectorModalOnLoad, loading, setOpenAddConnectorModalOnLoad]);

  /*
   * Les trois lectures partent ensemble et l'écran attend qu'elles aient toutes **abouti ou
   * échoué** avant de s'afficher : servies l'une après l'autre, la liste paraîtrait entière
   * puis se réduirait sous les yeux — très exactement le clignotement qu'un écran ne doit pas
   * avoir quand il *retire* des lignes.
   *
   * Seuls les connecteurs sont indispensables. La bibliothèque et les sites ne servent qu'au
   * filtre client : leur échec le désarme, il ne casse pas l'écran.
   */
  const loadConnectors = async () => {
    const entetes = { headers: { Authorization: `Bearer ${token}` } };
    const [reponseConnecteurs, reponseBibliotheque, reponseSites] = await Promise.allSettled([
      axios.get(`${API_URL}/api/connectors`, entetes),
      axios.get(`${API_URL}/api/library`, entetes),
      axios.get(`${API_URL}/api/sites`, entetes)
    ]);

    if (reponseConnecteurs.status === 'fulfilled') {
      setConnectors(reponseConnecteurs.value.data.connectors);
    } else {
      console.error('❌ Erreur chargement connecteurs:', reponseConnecteurs.reason);
      setError('Erreur lors du chargement des connecteurs');
    }

    if (reponseBibliotheque.status === 'fulfilled' && reponseSites.status === 'fulfilled') {
      const bibliotheque = reponseBibliotheque.value.data.library || {};
      setDonneesDuPerimetre({
        comptes: Object.entries(bibliotheque).flatMap(([provider, items]) =>
          (items || []).map(item => ({ ...item, provider }))
        ),
        sites: reponseSites.value.data.sites || []
      });
    } else {
      // On ne sait pas : on ne filtre pas. Voir `donneesDuPerimetre`.
      console.warn('[CONNECTEURS] Périmètre client indisponible, filtre désarmé');
    }

    setLoading(false);
  };

  const handleConnect = async (connectorType) => {
    setConnecting(connectorType);
    
    try {
      if (connectorType === 'GOOGLE_SHEETS') {
        window.location.href = `${API_URL}/api/auth/google-sheets?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'GOOGLE_ADS') {
        window.location.href = `${API_URL}/api/auth/google-ads?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'MICROSOFT_ADS') {
        window.location.href = `${API_URL}/api/auth/microsoft-ads?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'GOOGLE_MERCHANT_API') {
        window.location.href = `${API_URL}/api/auth/google-merchant?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'GOOGLE_MERCHANT_API') {
        window.location.href = `${API_URL}/api/auth/google-merchant-api?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'FACEBOOK_ADS') {
        window.location.href = `${API_URL}/api/auth/facebook-ads?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'PINTEREST_ADS') {
        window.location.href = `${API_URL}/api/auth/pinterest-ads?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'TIKTOK_ADS') {
        window.location.href = `${API_URL}/api/auth/tiktok-ads?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'GOOGLE_ANALYTICS') {
        window.location.href = `${API_URL}/api/auth/google-analytics?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'GOOGLE_SEARCH_CONSOLE') {
        window.location.href = `${API_URL}/api/auth/google-search-console?token=${token}&returnTo=connectors`;
      } else if (connectorType === 'GOOGLE_TAG_MANAGER') {
        window.location.href = `${API_URL}/api/auth/google-tag-manager?token=${token}&returnTo=connectors`;
      }
    } catch (error) {
      console.error('❌ Erreur connexion:', error);
      setError('Erreur lors de la connexion');
    } finally {
      setConnecting(null);
    }
  };

  const handleDisconnect = async (connectorId) => {
    try {
      setLoadingDelete(true);
      // Récupérer les comptes associés au connecteur
      const response = await axios.get(`${API_URL}/api/connectors/${connectorId}/library-items`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const connector = connectors.find(c => c.id === connectorId);
      setDeleteModal({
        show: true,
        connector: connector,
        libraryItems: response.data.libraryItems || []
      });
    } catch (error) {
      console.error('❌ Erreur récupération comptes:', error);
      // Si erreur, procéder avec la suppression simple
      if (window.confirm('Êtes-vous sûr de vouloir supprimer ce connecteur ?')) {
        await performDelete(connectorId);
      }
    } finally {
      setLoadingDelete(false);
    }
  };

  const performDelete = async (connectorId) => {
    try {
      setLoadingDelete(true);
      await axios.delete(`${API_URL}/api/connectors/${connectorId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadConnectors();
      // Rafraîchir la liste des comptes publicitaires pour mettre à jour la sidebar
      if (onRefreshLibrary) onRefreshLibrary();
      setDeleteModal({ show: false, connector: null, libraryItems: [] });
    } catch (error) {
      console.error('❌ Erreur suppression connecteur:', error);
      alert('Erreur lors de la suppression du connecteur');
    } finally {
      setLoadingDelete(false);
    }
  };

  const handleTest = async (connectorId) => {
    try {
      const response = await axios.post(`${API_URL}/api/connectors/${connectorId}/test`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        alert('✅ Test réussi ! Le connecteur fonctionne correctement.');
      } else {
        alert('❌ Test échoué : ' + response.data.error);
      }
    } catch (error) {
      console.error('❌ Erreur test connecteur:', error);
      alert('❌ Erreur lors du test du connecteur');
    }
  };

  // === Fonctions Matomo ===
  // === Fonctions Piano Analytics ===
  /**
   * Valide la clé sans rien enregistrer, et décrit le compte qu'elle ouvre.
   * L'utilisateur voit ainsi quel compte il s'apprête à connecter avant de valider.
   */

  // Déterminer quels connecteurs sont déjà connectés
  const hasGoogleSheets = connectors.some(c => c.provider === 'GOOGLE_SHEETS');
  const hasGoogleAds = connectors.some(c => c.provider === 'GOOGLE_ADS');
  const hasMicrosoftAds = connectors.some(c => c.provider === 'MICROSOFT_ADS');
  const hasGoogleMerchantApi = connectors.some(c => c.provider === 'GOOGLE_MERCHANT_API');
  const hasFacebookAds = connectors.some(c => c.provider === 'FACEBOOK_ADS');
  const hasPinterestAds = connectors.some(c => c.provider === 'PINTEREST_ADS');
  const hasGoogleAnalytics = connectors.some(c => c.provider === 'GOOGLE_ANALYTICS');
  const hasGoogleSearchConsole = connectors.some(c => c.provider === 'GOOGLE_SEARCH_CONSOLE');
  // Pour Matomo, on peut avoir plusieurs connecteurs (1 par instance)
  const matomoConnectors = connectors.filter(c => c.provider === 'MATOMO' || c.provider === 'MATOMO_OAUTH');
  // Pour Piano Analytics, on peut avoir plusieurs connecteurs (1 par compte)
  const pianoConnectors = connectors.filter(c => c.provider === 'PIANO_ANALYTICS');

  // Déterminer si les connecteurs météo sont activés (via globalConnectors)
  const hasOpenWeatherMap = globalConnectors?.some(c => c.provider === 'OPENWEATHERMAP') || false;
  const hasOpenMeteo = globalConnectors?.some(c => c.provider === 'OPEN_METEO') || false;

  // Fonction pour vérifier si un connecteur nécessite un plan payant
  const isPremiumConnector = (provider) => {
    return ['GOOGLE_SHEETS', 'OPENWEATHERMAP', 'OPEN_METEO'].includes(provider);
  };

  // Fonction pour vérifier si l'utilisateur peut accéder à un connecteur
  const canAccessConnector = (provider) => {
    if (!isPremiumConnector(provider)) return true;
    return ['SOLO', 'TEAM', 'AGENCY', 'SCALE'].includes(userSubscription?.plan);
  };

  if (loading) {
    return (
      <div className="step-content">
        <div className="loading-state">
          <Loader className="spin" size={24} />
          <p>Chargement des connecteurs...</p>
        </div>
      </div>
    );
  }

  // Les services météo ne sont pas des DataConnection : ils vivent dans
  // `globalConnectors` et n'ont ni identifiant ni date de connexion. On les
  // ramène à la même forme pour qu'ils tiennent dans la même liste.
  const connecteursVisibles = connectors.filter(connector => canAccessConnector(connector.provider));

  /**
   * Les connexions qui servent le client choisi en en-tête.
   *
   * `null` tant qu'aucun client n'est filtré, ou tant qu'on ne sait pas : on n'écarte alors
   * personne. La chaîne — client → ses sites → les comptes rattachés → leurs connexions —
   * est celle du Gestionnaire, et elle n'est écrite qu'une fois.
   */
  const connecteursDuClient = (clientFiltre && donneesDuPerimetre)
    ? connecteursDesComptes(
      connecteursVisibles,
      comptesDesSites(
        donneesDuPerimetre.comptes,
        donneesDuPerimetre.sites.filter(site => site.clientId === clientFiltre)
      )
    )
    : null;

  /*
   * ⚠️ **Les services météo ne sont rattachés à personne, et restent donc hors du filtre.**
   * Open-Meteo décrit une ville, pas le compte d'un client : les écarter ferait croire qu'un
   * client « n'a pas la météo », alors qu'elle est ouverte à tout le monde.
   */
  const garderDansLePerimetre = (connector) => !connecteursDuClient || connecteursDuClient.has(connector.id);
  const servicesMeteo = [
    hasOpenWeatherMap && canAccessConnector('OPENWEATHERMAP') && {
      provider: 'OPENWEATHERMAP',
      description: 'Accès mondial aux données météorologiques historiques.',
      resultat: weatherTestResult
    },
    hasOpenMeteo && canAccessConnector('OPEN_METEO') && {
      provider: 'OPEN_METEO',
      description: 'Données météorologiques historiques étendues (90 jours).',
      resultat: openMeteoTestResult
    }
  ].filter(Boolean);

  // Connecteurs et services météo se rejoignent dans un seul tableau avant le tri : deux
  // listes triées séparément se seraient réordonnées chacune dans son coin, et la colonne
  // n'aurait ordonné que des moitiés.
  const lignes = [
    ...connecteursVisibles.filter(garderDansLePerimetre).map(connector => {
      const meta = META_CONNECTEURS[connector.provider] || { libelle: connector.provider.replace(/_/g, ' ') };
      const compte = descriptionCompte(connector);
      const etat = etatConnecteur(connector);

      return {
        cle: connector.id,
        genre: 'connecteur',
        connector,
        meta,
        compte,
        etat,
        tri: {
          connecteur: meta.libelle,
          identifiant: compte.valeur || '',
          date: connector.createdAt ? new Date(connector.createdAt).getTime() : null,
          statut: etat.rang
        }
      };
    }),
    ...servicesMeteo.map(service => {
      const meta = META_CONNECTEURS[service.provider];

      return {
        cle: service.provider,
        genre: 'meteo',
        service,
        meta,
        tri: {
          connecteur: meta.libelle,
          identifiant: service.description,
          date: null,
          statut: 2
        }
      };
    })
  ];

  // La recherche porte sur ce qui est affiché — le nom de la plateforme et l'identifiant —
  // et pas sur l'identifiant technique du fournisseur : chercher « meta » doit trouver la
  // ligne nommée Meta Ads, pas échouer parce qu'elle s'appelle FACEBOOK_ADS en base.
  const recherche = rechercheConnecteurs.trim().toLowerCase();
  const lignesAffichees = lignes
    .filter(ligne => !recherche
      || String(ligne.tri.connecteur).toLowerCase().includes(recherche)
      || String(ligne.tri.identifiant).toLowerCase().includes(recherche))
    .sort((a, b) => comparerConnecteurs(a, b, tri.colonne, tri.sens));

  const total = lignes.length;
  const affiches = lignesAffichees.length;
  // Le périmètre a été posé et ne laisse rien : ce n'est ni « aucun connecteur » ni « aucun
  // résultat de recherche », et les deux messages existants répondraient à côté.
  const perimetreVide = Boolean(connecteursDuClient) && total === 0;

  return (
    <div className="step-content">
      <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
        <h2 style={{ margin: 0 }}><Plug size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} /> Connecteurs</h2>
        {total > 0 && (
          <span className="kok-compteur">
            {recherche
              ? `${affiches} sur ${total}`
              : `${total} connecté${total > 1 ? 's' : ''}`}
          </span>
        )}
        {total > 0 && (
          <div className="kc-recherche">
            <Search size={16} />
            <input
              type="text"
              placeholder="Rechercher..."
              value={rechercheConnecteurs}
              onChange={(e) => setRechercheConnecteurs(e.target.value)}
            />
            {rechercheConnecteurs && (
              <button
                type="button"
                className="kc-recherche-effacer"
                title="Effacer la recherche"
                onClick={() => setRechercheConnecteurs('')}
              >
                <X size={14} />
              </button>
            )}
          </div>
        )}
        <button
          onClick={() => setAddConnectorModalOpen(true)}
          className="btn btn-primary btn-small"
          style={{ marginLeft: 'auto', backgroundColor: '#111827', borderColor: '#111827' }}
        >
          + Ajouter un connecteur
        </button>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      <div className="kc-page">
        <div className="kc-liste">
          {perimetreVide ? (
            <div className="kc-vide">
              <Plug size={32} />
              <p className="kc-vide-titre">Aucun connecteur pour ce client</p>
              <p className="kc-vide-texte">
                Aucun compte de ce client n'est branché sur un connecteur. Rattachez-lui des
                comptes depuis le Gestionnaire, ou choisissez « Tous les clients ».
              </p>
            </div>
          ) : total === 0 ? (
            <div className="kc-vide">
              <Plug size={32} />
              <p className="kc-vide-titre">Aucun connecteur</p>
              <p className="kc-vide-texte">
                Connectez une régie ou un outil de mesure pour alimenter vos tableaux de bord.
              </p>
              <button
                onClick={() => setAddConnectorModalOpen(true)}
                className="btn btn-primary btn-small"
                style={{ backgroundColor: '#111827', borderColor: '#111827' }}
              >
                + Ajouter un connecteur
              </button>
            </div>
          ) : affiches === 0 ? (
            // « Aucun résultat » n'est pas « aucun connecteur » : proposer ici d'en ajouter un
            // répondrait à côté de la question, alors qu'il suffit d'effacer la recherche.
            <div className="kc-vide">
              <Search size={32} />
              <p className="kc-vide-titre">Aucun résultat</p>
              <p className="kc-vide-texte">
                Aucun connecteur ne correspond à « {rechercheConnecteurs.trim()} ».
              </p>
              <button
                onClick={() => setRechercheConnecteurs('')}
                className="btn btn-small"
                style={{ border: '1px solid #d1d5db', background: '#fff', color: '#111827' }}
              >
                Effacer la recherche
              </button>
            </div>
          ) : (
            <>
              <div className="kc-grille kc-entete">
                {COLONNES_CONNECTEURS.map(colonne => {
                  const actif = tri.colonne === colonne.cle;

                  return (
                    <button
                      key={colonne.cle}
                      type="button"
                      className={`kc-tri ${actif ? 'actif' : ''}`}
                      onClick={() => basculerTri(colonne.cle)}
                      aria-sort={actif ? (tri.sens === 'asc' ? 'ascending' : 'descending') : 'none'}
                      title={`Trier par ${colonne.libelle.toLowerCase()}`}
                    >
                      {colonne.libelle}
                      {actif
                        ? (tri.sens === 'asc' ? <ChevronUp size={13} /> : <ChevronDown size={13} />)
                        : <ChevronsUpDown size={13} className="kc-tri-repos" />}
                    </button>
                  );
                })}
                <span className="kc-entete-fin">Actions</span>
              </div>

              {lignesAffichees.map(ligne => {
                const { meta } = ligne;

                if (ligne.genre === 'meteo') {
                  const { service } = ligne;
                  const temperature = service.resultat?.details?.testResult?.temperature;

                  return (
                    <div key={ligne.cle} className="kc-grille kc-ligne">
                      <div className="kc-marque">
                        <span className="kc-logo">{meta.emoji}</span>
                        <span className="kc-nom">{meta.libelle}</span>
                      </div>

                      <div className="kc-compte" title={service.description}>
                        <span className="kc-qualificatif">Service</span>
                        {service.description}
                      </div>

                      <div className="kc-date">—</div>

                      <span className="kc-etat actif">Actif</span>

                      <div className="kc-actions">
                        <button
                          className="kc-action danger"
                          title="Supprimer"
                          onClick={() => handleWeatherConnector(service.provider, 'deactivate')}
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>

                      {service.resultat && (
                        <div className={`kc-note ${service.resultat.success ? 'succes' : 'echec'}`}>
                          {service.resultat.message}
                          {temperature && ` · Test Paris : ${temperature}`}
                        </div>
                      )}
                    </div>
                  );
                }

                const { connector, compte, etat } = ligne;
                const cheminReconnexion = CHEMINS_RECONNEXION[connector.provider];
                const aReconnecter = !connector.isActive || connector.lastError;

                return (
                  <div key={ligne.cle} className="kc-grille kc-ligne">
                    <div className="kc-marque">
                      <span className="kc-logo">
                        {meta.logo
                          ? <img src={meta.logo} alt="" />
                          : (meta.emoji || <Plug size={18} />)}
                      </span>
                      <span className="kc-nom" title={meta.libelle}>{meta.libelle}</span>
                    </div>

                    {/* Le qualificatif — « Email », « Instance » — n'est plus affiché : il
                        répétait ce que la valeur montre déjà. Il reste en infobulle, où il
                        lève l'ambiguïté sans coûter une ligne. */}
                    <div className="kc-compte" title={[compte.qualificatif, compte.valeur].filter(Boolean).join(' : ')}>
                      {compte.valeur || '—'}
                    </div>

                    <div className="kc-date">
                      {connector.createdAt ? new Date(connector.createdAt).toLocaleDateString('fr-FR') : '—'}
                    </div>

                    {/* La pastille est enveloppée : une cellule de grille s'étire sur toute la
                        hauteur de sa rangée — c'est ce qui porte le fond et le séparateur — et
                        un `border-radius: 999px` en aurait fait un cercle. */}
                    <div className="kc-cellule-etat">
                      {/* Le message de la régie reste au survol : la pastille dit la nature du
                          problème, la bulle son texte exact — celui qu'un support demandera.
                          ⚠️ `useBulleAuSurvol` et non un `title` natif : c'est le motif unique
                          du projet, et lui seul se place après mesure plutôt que de sortir de
                          l'écran sur la dernière ligne. `ksi-survolable` n'est posé que s'il y
                          a un texte — un curseur « help » qui ne promet rien est pire que pas
                          de curseur. */}
                      <span
                        className={`kc-etat ${etat.classe}${etat.explication ? ' ksi-survolable' : ''}`}
                        onMouseEnter={etat.explication ? survolerEtat(etat.explication) : undefined}
                        onMouseLeave={etat.explication ? quitterEtat : undefined}
                      >
                        {etat.libelle}
                      </span>
                    </div>

                    <div className="kc-actions">
                      {aReconnecter && cheminReconnexion && (
                        <button
                          className="kc-reconnecter"
                          title={`Reconnecter ${meta.libelle}`}
                          onClick={() => {
                            // Repasser par OAuth sans perdre la configuration existante
                            window.location.href = `${API_URL}/api/auth/${cheminReconnexion}?token=${token}&returnTo=connectors`;
                          }}
                        >
                          <Link size={14} />
                          Reconnecter
                        </button>
                      )}
                      {/*
                        * Le pendant de « Reconnecter » pour les connecteurs à clé, qui n'ont
                        * aucun OAuth où repasser. ⚠️ Il **remplace la clé en place** : créer
                        * un second connecteur laisserait les comptes de la bibliothèque
                        * rattachés à l'ancien par `connectionId`, et avec eux les sites, les
                        * dashboards et l'historique.
                        *
                        * ⚠️ **Seulement sur une ligne en erreur, et nommé.** Une première
                        * version le réduisait à une icône partout ailleurs, pour la rotation
                        * préventive d'une clé qui marche — « utile mais rare », disait le
                        * commentaire. Rare, et jamais : sur un parc de vingt-trois connecteurs,
                        * cela faisait vingt-trois clés identiques dans la colonne Actions,
                        * dont aucune ne servait. Une affordance qu'on n'emploie pas n'est pas
                        * neutre : elle occupe la place de celles qui comptent, et sur la seule
                        * ligne où il fallait la voir, elle se confondait avec les vingt-deux
                        * autres.
                        *
                        * Reste le cas de la rotation volontaire : il passe par la suppression
                        * et la reconnexion, comme avant — et qui veut l'éviter commence par
                        * révoquer sa clé chez le fournisseur, ce qui ramène la ligne en erreur
                        * et fait reparaître le bouton.
                        */}
                      {connector.champsIdentifiants && aReconnecter && (
                        <button
                          className="kc-reconnecter"
                          onClick={() => ouvrirRemplacementCle(connector)}
                        >
                          <Key size={14} />
                          Remplacer la clé
                        </button>
                      )}
                      {/*
                        * ⚠️ Toujours une icône, jamais un bouton nommé : ce geste n'est pas la
                        * réparation d'une panne — il n'y a rien à réparer —, et le nommer sur
                        * une ligne en erreur le mettrait au même rang que « Remplacer la clé »,
                        * qui est alors le seul geste utile. On répare d'abord, on récolte après.
                        */}
                      {connector.synchronisationComptes && (
                        <button
                          className="kc-action ksi-survolable"
                          disabled={synchroEnCours === connector.id}
                          onMouseEnter={survolerEtat(
                            `Récolter les ${connector.synchronisationComptes.libelle} que cette clé ouvre, `
                            + 'et voir ceux qu\'elle n\'ouvre plus'
                          )}
                          onMouseLeave={quitterEtat}
                          onClick={() => synchroniserLesComptes(connector)}
                        >
                          <RefreshCw size={15} className={synchroEnCours === connector.id ? 'kc-tourne' : undefined} />
                        </button>
                      )}
                      <button
                        className="kc-action danger"
                        title="Supprimer"
                        onClick={() => handleDisconnect(connector.id)}
                      >
                        <Trash2 size={15} />
                      </button>
                    </div>

                    {/*
                      * ⚠️ Le résultat se pose **sous sa propre ligne**, et non dans un bandeau
                      * en tête de page : il nomme des comptes, et il faut voir de quelle
                      * connexion ils viennent. Un bandeau en haut d'une liste de vingt-trois
                      * connecteurs obligerait à s'en souvenir.
                      *
                      * `pre-line` : chaque constat est sur sa ligne — ajoutés, écartés par le
                      * quota, disparus. Mis bout à bout, ils ne se lisent pas.
                      */}
                    {resultatSynchro?.connectorId === connector.id && (
                      <div className={`kc-synchro-resultat ${resultatSynchro.ton}`}>
                        {resultatSynchro.texte}
                      </div>
                    )}

                  </div>
                );
              })}
            </>
          )}
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      {deleteModal.show && ReactDOM.createPortal(
        <div onClick={() => !loadingDelete && setDeleteModal({ show: false, connector: null, libraryItems: [] })} style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 9999
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            backgroundColor: 'white',
            borderRadius: '0.75rem',
            boxShadow: '0 20px 60px rgba(0, 0, 0, 0.2)',
            overflow: 'hidden',
            maxWidth: '480px',
            width: '90vw',
            maxHeight: '90vh',
            overflowY: 'auto'
          }}>
            {/* Header */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '1rem 1.5rem',
              borderBottom: '1px solid #e5e0d5'
            }}>
              <h3 style={{ fontSize: '1.125rem', fontWeight: '600', color: '#1e293b', display: 'flex', alignItems: 'center', gap: '0.5rem', margin: 0 }}>
                <Trash2 size={20} />
                Confirmer la suppression
              </h3>
              <button
                onClick={() => !loadingDelete && setDeleteModal({ show: false, connector: null, libraryItems: [] })}
                style={{
                  padding: '0.5rem',
                  borderRadius: '0.375rem',
                  border: 'none',
                  backgroundColor: 'transparent',
                  cursor: loadingDelete ? 'not-allowed' : 'pointer',
                  color: '#64748b',
                  display: 'flex'
                }}
                disabled={loadingDelete}
              >
                <X size={20} />
              </button>
            </div>

            {/* Body */}
            <div style={{ padding: '1.5rem' }}>
              {/* Connecteur à supprimer */}
              <div style={{
                backgroundColor: '#f9f7f4',
                border: '2px solid #d4cfc7',
                borderRadius: '0.5rem',
                padding: '1rem',
                marginBottom: '1.5rem'
              }}>
                <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: '#64748b' }}>
                  Connecteur à supprimer :
                </p>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                  <div style={{
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'white',
                    borderRadius: '0.5rem',
                    border: '1px solid #e5e0d5'
                  }}>
                    {deleteModal.connector?.provider === 'GOOGLE_ADS' ? <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px'}} /> :
                     deleteModal.connector?.provider === 'MICROSOFT_ADS' ? <img src={microsoftAdsLogo} alt="Microsoft Ads" style={{width: '24px', height: '24px'}} /> :
                     deleteModal.connector?.provider === 'FACEBOOK_ADS' ? <img src={metaAdsLogo} alt="Meta Ads" style={{width: '24px', height: '24px', objectFit: 'contain'}} /> :
                     deleteModal.connector?.provider === 'MATOMO' ? <img src={matomoLogo} alt="Matomo" style={{width: '24px', height: '24px', borderRadius: '4px'}} /> :
                     <Plug size={20} color="#64748b" />}
                  </div>
                  <div>
                    <div style={{ fontWeight: '600', color: '#1e293b', fontSize: '0.9375rem' }}>
                      {deleteModal.connector?.connectionName || deleteModal.connector?.connectorEmail}
                    </div>
                    <div style={{ color: '#64748b', fontSize: '0.8125rem' }}>
                      {deleteModal.connector?.provider?.replace(/_/g, ' ')}
                    </div>
                  </div>
                </div>
              </div>

              {/* Impact */}
              {deleteModal.libraryItems.length > 0 && (
                <div style={{ marginBottom: '1.5rem' }}>
                  <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: '#64748b' }}>
                    {deleteModal.libraryItems.length} compte(s) associé(s) seront également supprimés :
                  </p>
                  <div style={{
                    backgroundColor: '#f9f7f4',
                    border: '1px solid #e5e0d5',
                    borderRadius: '0.5rem',
                    maxHeight: '150px',
                    overflowY: 'auto'
                  }}>
                    {deleteModal.libraryItems.map((item) => (
                      <div key={item.id} style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        padding: '0.625rem 0.75rem',
                        borderBottom: '1px solid #e5e0d5',
                        fontSize: '0.875rem',
                        color: '#1e293b'
                      }}>
                        <Database size={16} color="#64748b" />
                        {item.name}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Avertissement */}
              <div style={{
                backgroundColor: '#fef2f2',
                border: '1px solid #fecaca',
                borderRadius: '0.5rem',
                padding: '0.75rem 1rem',
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem'
              }}>
                <AlertCircle size={18} color="#dc2626" />
                <span style={{ color: '#dc2626', fontSize: '0.875rem', fontWeight: '500' }}>
                  Cette action est irréversible
                </span>
              </div>
            </div>

            {/* Footer */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              gap: '0.75rem',
              padding: '1rem 1.5rem',
              borderTop: '1px solid #e5e0d5',
              backgroundColor: '#f3efe7'
            }}>
              <button
                onClick={() => setDeleteModal({ show: false, connector: null, libraryItems: [] })}
                disabled={loadingDelete}
                style={{
                  padding: '0.625rem 1.25rem',
                  borderRadius: '0.5rem',
                  border: '1px solid #e5e0d5',
                  backgroundColor: 'white',
                  color: '#64748b',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: loadingDelete ? 'not-allowed' : 'pointer',
                  opacity: loadingDelete ? 0.5 : 1
                }}
              >
                Annuler
              </button>
              <button
                onClick={() => performDelete(deleteModal.connector.id)}
                disabled={loadingDelete}
                style={{
                  padding: '0.625rem 1.25rem',
                  borderRadius: '0.5rem',
                  border: 'none',
                  backgroundColor: '#dc2626',
                  color: 'white',
                  fontSize: '0.875rem',
                  fontWeight: '500',
                  cursor: loadingDelete ? 'not-allowed' : 'pointer',
                  opacity: loadingDelete ? 0.7 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                {loadingDelete ? (
                  <>
                    <Loader size={16} style={{ animation: 'spin 1s linear infinite' }} />
                    Suppression...
                  </>
                ) : (
                  <>
                    <Trash2 size={16} />
                    Supprimer
                  </>
                )}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal de connexion Matomo */}
      {/* Formulaire Matomo — extrait de cet écran pour être aussi ouvert par le parcours
          d'installation. Voir components/connectors/FormulaireMatomo.jsx. */}
      <FormulaireMatomo
        ouvert={matomoModal.show}
        onFermer={() => setMatomoModal({ show: false })}
        token={token}
        onConnecte={async () => {
          await loadConnectors();
          // Rafraîchir les comptes au niveau application : sans cela la barre latérale
          // et les sources disponibles restent celles d'avant la connexion.
          if (onRefreshLibrary) onRefreshLibrary();
        }}
      />

      {/* Modal de connexion Piano Analytics */}
      {/* Modale ChatGPT Ads : saisie de la clé, validation, puis connexion */}
      {/*
        Modale Awin. Portail obligatoire : sans lui, la barre latérale passerait au-dessus.
        Différence avec ChatGPT Ads : un jeton ouvre plusieurs comptes annonceurs, d'où la
        liste à cocher entre le test et la connexion.
      */}
      {/* Formulaire Awin — extrait de cet écran pour être aussi ouvert par le parcours
          d'installation. Voir components/connectors/FormulaireAwin.jsx. */}
      <FormulaireAwin
        ouvert={awinModal.show}
        onFermer={() => setAwinModal({ show: false })}
        token={token}
        onConnecte={async () => {
          await loadConnectors();
          // Sans ce rafraîchissement, la barre latérale et les sources disponibles restent
          // celles d'avant la connexion.
          if (onRefreshLibrary) onRefreshLibrary();
        }}
      />

      {/* Formulaire PrestaShop — même protocole qu'Awin : une clé ouvre N boutiques, et
          l'utilisateur choisit celles qu'il rattache.
          Voir components/connectors/FormulairePrestashop.jsx. */}
      <FormulairePrestashop
        ouvert={prestashopModal.show}
        onFermer={() => setPrestashopModal({ show: false })}
        token={token}
        onConnecte={async () => {
          await loadConnectors();
          // Sans ce rafraîchissement, la barre latérale et les sources disponibles restent
          // celles d'avant la connexion.
          if (onRefreshLibrary) onRefreshLibrary();
        }}
      />

      {/* Formulaire ChatGPT Ads — extrait de cet écran pour être aussi ouvert par le
          parcours d'installation. Voir components/connectors/FormulaireChatGptAds.jsx. */}
      <FormulaireChatGptAds
        ouvert={openaiModal.show}
        onFermer={() => setOpenaiModal({ show: false })}
        token={token}
        onConnecte={async () => {
          await loadConnectors();
          // Rafraîchir les comptes au niveau application : sans cela la barre latérale
          // et les sources disponibles restent celles d'avant la connexion.
          if (onRefreshLibrary) onRefreshLibrary();
        }}
      />

      {/* Formulaire Piano Analytics — extrait de cet écran pour être aussi ouvert par le
          parcours d'installation. Voir components/connectors/FormulairePiano.jsx. */}
      <FormulairePiano
        ouvert={pianoModal.show}
        onFermer={() => setPianoModal({ show: false })}
        token={token}
        onConnecte={async ({ sites }) => {
          await loadConnectors();
          if (onRefreshLibrary) onRefreshLibrary();
          // Les sites relus par le formulaire alimentent la barre latérale, qui resterait
          // sinon celle d'avant la connexion.
          if (sites?.length && setPianoSites) setPianoSites(sites);
        }}
      />

      {/* Modal de sélection de connecteur */}
      {addConnectorModalOpen && ReactDOM.createPortal(
        <div
          onClick={() => setAddConnectorModalOpen(false)}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white',
              borderRadius: '12px',
              padding: '24px',
              width: '90%',
              maxWidth: '600px',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)'
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: '600', color: '#1e293b' }}>
                <Plug size={20} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                Ajouter un connecteur
              </h2>
              <button
                onClick={() => setAddConnectorModalOpen(false)}
                style={{
                  background: 'none',
                  border: 'none',
                  cursor: 'pointer',
                  padding: '4px',
                  color: '#64748b'
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ color: '#64748b', marginBottom: '20px', fontSize: '0.9rem' }}>
              Sélectionnez le type de connecteur que vous souhaitez ajouter.
            </p>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: '12px' }}>
              {/* Google Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  handleConnect('GOOGLE_ADS');
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={googleAdsLogo} alt="Google Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Google Ads</span>
              </button>

              {/* Microsoft Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  handleConnect('MICROSOFT_ADS');
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={microsoftAdsLogo} alt="Microsoft Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Microsoft Ads</span>
              </button>

              {/* Meta Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  handleConnect('FACEBOOK_ADS');
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={metaAdsLogo} alt="Meta Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Meta Ads</span>
              </button>

              {/* Pinterest Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  handleConnect('PINTEREST_ADS');
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={pinterestAdsLogo} alt="Pinterest Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Pinterest Ads</span>
              </button>

              {/* TikTok Ads */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  handleConnect('TIKTOK_ADS');
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={tiktokAdsLogo} alt="TikTok Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>TikTok Ads</span>
              </button>

              {/* Google Analytics */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  handleConnect('GOOGLE_ANALYTICS');
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src="https://cdn.jsdelivr.net/gh/walkxcode/dashboard-icons/png/google-analytics.png" alt="Google Analytics 4" style={{ width: '32px', height: '32px', borderRadius: '4px' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Google Analytics</span>
              </button>

              {/* Google Search Console */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  handleConnect('GOOGLE_SEARCH_CONSOLE');
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={searchConsoleLogo} alt="Google Search Console" style={{ width: '32px', height: '32px', borderRadius: '4px' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b', textAlign: 'center' }}>Search Console</span>
              </button>

              {/* Google Tag Manager */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  handleConnect('GOOGLE_TAG_MANAGER');
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={tagManagerLogo} alt="Google Tag Manager" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b', textAlign: 'center' }}>Tag Manager</span>
              </button>

              {/* Matomo */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  setMatomoModal({ show: true });
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={matomoLogo} alt="Matomo" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Matomo</span>
              </button>

              {/* Piano Analytics */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  setPianoModal({ show: true });
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={pianoLogo} alt="Piano Analytics" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Piano Analytics</span>
              </button>

              {/* ChatGPT Ads — connexion par clé API, une clé donne accès à un seul compte */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  setOpenaiModal({ show: true });
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={chatgptAdsLogo} alt="ChatGPT Ads" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>ChatGPT Ads</span>
              </button>

              {/* Awin — connexion par jeton API, un jeton ouvre plusieurs comptes annonceurs */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  setAwinModal({ show: true });
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={awinLogo} alt="Awin" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>Awin</span>
              </button>

              {/* PrestaShop — connexion par clé du webservice, une clé ouvre N boutiques */}
              <button
                onClick={() => {
                  setAddConnectorModalOpen(false);
                  setPrestashopModal({ show: true });
                }}
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '8px',
                  padding: '16px',
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  background: 'white',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.borderColor = '#3b82f6';
                  e.currentTarget.style.background = '#f8f5f0';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.borderColor = '#e5e7eb';
                  e.currentTarget.style.background = 'white';
                }}
              >
                <img src={prestashopLogo} alt="PrestaShop" style={{ width: '32px', height: '32px', objectFit: 'contain' }} />
                <span style={{ fontSize: '0.85rem', fontWeight: '500', color: '#1e293b' }}>PrestaShop</span>
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
      {/*
        * Remplacement de clé. ⚠️ `createPortal` : la règle du projet pour toute modale — sans
        * lui, la barre latérale passe au-dessus par un z-index qu'aucune valeur locale ne bat.
        */}
      {modaleCle.show && modaleCle.connector && ReactDOM.createPortal(
        <div
          onClick={fermerRemplacementCle}
          style={{
            position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 9999
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: '#ffffff', borderRadius: '12px', padding: '1.75rem',
              width: 'min(480px, calc(100vw - 2rem))',
              boxShadow: '0 20px 45px rgba(15,23,42,0.22)'
            }}
          >
            <h3 style={{ margin: '0 0 0.35rem', fontSize: '1.0625rem', color: '#111827' }}>
              {modaleCle.connector.champsIdentifiants.libelle}
            </h3>
            <p style={{ margin: '0 0 1.25rem', fontSize: '0.8125rem', color: '#6b7280', lineHeight: 1.5 }}>
              {modaleCle.connector.champsIdentifiants.aide}
            </p>

            {/*
              * ⚠️ Ce que la modale promet, et qui est la raison d'être de l'écran : les comptes
              * ne bougent pas. Sans cette phrase, l'utilisateur hésite entre ce bouton et une
              * suppression suivie d'une reconnexion — qui, elle, détacherait tout.
              */}
            <p style={{
              margin: '0 0 1.25rem', padding: '0.625rem 0.75rem', fontSize: '0.8125rem',
              lineHeight: 1.5, color: '#374151', background: '#f9fafb',
              border: '1px solid #e5e7eb', borderRadius: '8px'
            }}>
              Les comptes déjà rattachés à ce connecteur, leurs sites et leur historique sont
              conservés. Seule la clé change.
            </p>

            {modaleCle.connector.champsIdentifiants.champs.map((champ) => (
              <label key={champ.nom} style={{ display: 'block', marginBottom: '0.875rem' }}>
                <span style={{
                  display: 'block', marginBottom: '0.3rem',
                  fontSize: '0.8125rem', fontWeight: 600, color: '#374151'
                }}>
                  {champ.libelle}
                </span>
                <input
                  type={champ.secret ? 'password' : 'text'}
                  autoComplete="off"
                  value={valeursCle[champ.nom] || ''}
                  onChange={(e) => setValeursCle(v => ({ ...v, [champ.nom]: e.target.value }))}
                  disabled={envoiCle}
                  style={{
                    width: '100%', padding: '0.55rem 0.7rem', fontSize: '0.875rem',
                    border: '1px solid #d1d5db', borderRadius: '8px', boxSizing: 'border-box'
                  }}
                />
              </label>
            ))}

            {erreurCle && (
              <p style={{
                margin: '0 0 1rem', padding: '0.625rem 0.75rem', fontSize: '0.8125rem',
                lineHeight: 1.5, color: '#991b1b', background: '#fef2f2',
                border: '1px solid #fecaca', borderRadius: '8px', whiteSpace: 'pre-line'
              }}>
                {erreurCle}
              </p>
            )}

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
              <button
                onClick={fermerRemplacementCle}
                disabled={envoiCle}
                style={{
                  padding: '0.5rem 0.9rem', fontSize: '0.875rem', borderRadius: '8px',
                  border: '1px solid #d1d5db', background: '#ffffff', color: '#374151',
                  cursor: envoiCle ? 'default' : 'pointer'
                }}
              >
                Annuler
              </button>
              <button
                onClick={envoyerNouvelleCle}
                disabled={envoiCle}
                style={{
                  padding: '0.5rem 0.9rem', fontSize: '0.875rem', fontWeight: 600,
                  borderRadius: '8px', border: '1px solid #111827',
                  background: envoiCle ? '#4b5563' : '#111827', color: '#ffffff',
                  cursor: envoiCle ? 'default' : 'pointer'
                }}
              >
                {/* La clé est essayée avant d'être écrite : le libellé le dit, l'attente a une raison. */}
                {envoiCle ? 'Vérification…' : 'Vérifier et remplacer'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* La bulle sort par un portail : sa place dans l'arbre est sans importance. */}
      {bulleEtat}
    </div>
  );
}

// Composant: Vue Historique des Conversations
function HistoryView({ token, selectedClientId, analyzeFilterSite, analyzeFilterPlatforms, analyzeFilterAccount }) {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [pagination, setPagination] = useState({ page: 1, limit: 20, total: 0, totalItems: 0 });

  useEffect(() => {
    loadConversations();
  }, [pagination.page]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/conversations`, {
        headers: { Authorization: `Bearer ${token}` },
        params: {
          page: pagination.page,
          limit: pagination.limit
        }
      });

      setConversations(response.data.conversations || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.pages || 1,
        totalItems: response.data.pagination?.totalItems || response.data.conversations?.length || 0
      }));
    } catch (error) {
      console.error('❌ Erreur chargement conversations:', error);
      setError('Erreur lors du chargement des conversations');
    } finally {
      setLoading(false);
    }
  };

  const deleteConversation = async (conversationId) => {
    if (!window.confirm('Supprimer cette conversation ?')) return;

    try {
      await axios.delete(`${API_URL}/api/conversations/${conversationId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      loadConversations();
    } catch (error) {
      console.error('❌ Erreur suppression conversation:', error);
      alert('Erreur lors de la suppression');
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatRelativeTime = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diff = now - date;

    if (diff < 60000) return "À l'instant";
    if (diff < 3600000) return `Il y a ${Math.floor(diff / 60000)} min`;
    if (diff < 86400000) return `Il y a ${Math.floor(diff / 3600000)} h`;
    if (diff < 604800000) return `Il y a ${Math.floor(diff / 86400000)} j`;

    return formatDate(dateString);
  };

  if (loading) {
    return (
      <div className="step-content">
        <div className="loading-state">
          <Loader className="spin" size={24} />
          <p>Chargement des conversations...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="step-content">
      <div className="page-header">
        <h2>💬 Historique des conversations</h2>
        <div className="header-stats">
          <span className="stat-item">
            {pagination.totalItems} conversation(s) au total
          </span>
        </div>
      </div>

      {error && (
        <div className="error-message">
          <AlertCircle size={16} />
          {error}
        </div>
      )}

      {conversations.length === 0 ? (
        <div className="empty-state">
          <MessageSquare size={48} />
          <h3>Aucune conversation</h3>
          <p>Vos conversations avec l'IA apparaîtront ici une fois que vous aurez lancé une analyse.</p>
          <button
            onClick={() => window.dispatchEvent(new CustomEvent('changeView', { detail: 'analyze' }))}
            className="btn btn-primary"
          >
            + Commencer une analyse
          </button>
        </div>
      ) : (
        <>
          <div className="analyses-list">
            {conversations.map(conversation => (
              <div key={conversation.id} className="analysis-card">
                <div className="analysis-card-header">
                  <div className="analysis-info">
                    <h4 className="analysis-query">
                      {conversation.title || conversation.initialQuery?.substring(0, 60) + '...'}
                    </h4>
                    <div className="analysis-meta-line">
                      <span className="analysis-date">
                        <Calendar size={14} />
                        {formatRelativeTime(conversation.lastMessageAt || conversation.updatedAt)}
                      </span>
                      <span className="analysis-type">
                        <MessageSquare size={14} style={{ marginRight: '4px' }} />
                        {conversation.messageCount || 0} messages
                      </span>
                      <span className="analysis-sources">
                        <img src={googleAdsLogo} alt="Google Ads" style={{width: '16px', height: '16px', marginRight: '4px', verticalAlign: 'middle'}} />
                        {conversation.accountName || conversation.accountId}
                      </span>
                    </div>
                  </div>

                  <div className="analysis-stats">
                    <span className="tokens-count">{conversation.totalTokensUsed || 0} tokens</span>
                    <span className="cost-estimate">
                      {(conversation.totalCost || 0).toFixed(4)}€
                    </span>
                  </div>
                </div>

                <div className="analysis-preview">
                  <p>
                    <strong>Question initiale :</strong> {conversation.initialQuery?.substring(0, 150)}
                    {conversation.initialQuery?.length > 150 && '...'}
                  </p>
                </div>

                <div className="analysis-actions">
                  <button
                    onClick={() => {
                      // Naviguer vers l'Analyser avec cette conversation
                      window.dispatchEvent(new CustomEvent('openConversation', {
                        detail: {
                          conversationId: conversation.id,
                          accountId: conversation.accountId,
                          accountName: conversation.accountName
                        }
                      }));
                    }}
                    className="btn btn-primary btn-small"
                  >
                    <MessageSquare size={14} />
                    Continuer
                  </button>
                  <button
                    onClick={() => deleteConversation(conversation.id)}
                    className="btn btn-danger btn-small"
                  >
                    <Trash2 size={14} />
                    Supprimer
                  </button>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination.total > 1 && (
            <div className="pagination">
              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))}
                disabled={pagination.page === 1}
                className="btn btn-secondary btn-small"
              >
                ← Précédent
              </button>

              <span className="pagination-info">
                Page {pagination.page} sur {pagination.total}
              </span>

              <button
                onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))}
                disabled={pagination.page === pagination.total}
                className="btn btn-secondary btn-small"
              >
                Suivant →
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}



// ===== COMPOSANT DASHBOARD PRINCIPAL AVEC ONGLETS =====

/**
 * Carte de la grille de sélection du dashboard.
 *
 * Le même bloc de style était recopié dix fois, une par plateforme proposée. Il l'était déjà
 * avant la suppression de la section « Consolidé » ; celle-ci a simplement rendu la constante
 * orpheline, et l'occasion de brancher les neuf autres dessus.
 */
const styleCarte = {
  padding: '16px 20px',
  backgroundColor: 'white',
  borderRadius: '12px',
  border: '1px solid #e2e8f0',
  cursor: 'pointer',
  transition: 'all 0.2s ease',
  boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
  display: 'flex',
  alignItems: 'center',
  gap: '12px'
};

function DashboardView({
  token,
  setError,
  setCurrentView,
  globalConnectors,
  hasAnalyzerAccess,
  selectedClientId,
  user,
  authError,
  setAuthError,
  handleApiError,
  handleReconnect,
  currentDashboardType,
  // Le dashboard consolidé se propose ici comme dans la barre latérale : mêmes droits,
  // même navigation.
  clientTools = [],
  // Mène à l'écran de choix plutôt qu'au consolidé : le dashboard n'a plus de section
  // « Consolidé » propre, la question se pose après le clic sur une solution.
  ouvrirChoixDashboard,
  dashboardNotes,
  fetchDashboardNotes,
  selectedPeriod,
  setSelectedPeriod,
  customStartDate,
  setCustomStartDate,
  customEndDate,
  setCustomEndDate,
  selectedSiteId,
  userSites,
  availableSources,
  // La source unique du filtrage par plateforme : l'écran de choix la consulte comme la
  // barre latérale, pour ne pas proposer ce que le serveur refusera.
  plateformeOuverte,
  filteredGA4Properties = [],
  filteredMatomoSites = [],
  filteredPianoSites = [],
  /*
   * ⚠️ Descendue d'`App()` comme ses trois voisines, et non recalculée ici : `CMS` y est résolu
   * une fois, avec les logos et les comptes filtrés. Deux calculs divergeraient au premier CMS
   * ajouté — l'un montrerait Shopify, l'autre non.
   */
  cmsDisponibles = [],
  pianoLogo,
  compareEnabled = false,
  comparisonType = 'previous_period',
  fbCompareEnabled = false,
  fbComparisonType = '',
  gadsComparisonType = '',
  setGadsComparisonType,
  gadsCompareEnabled = false,
  msadsComparisonType = '',
  msadsCompareEnabled = false,
  inaccessibleAccounts = [],
  globalCompareEnabled = false,
  globalComparisonType = 'previous_period',
  comparisonStartDate = '',
  comparisonEndDate = ''
}) {
  const [activeTab, setActiveTab] = useState(currentDashboardType || 'selection');
  const [totalAccountsCount, setTotalAccountsCount] = useState(0);

  // Synchroniser activeTab avec currentDashboardType quand il change depuis l'extérieur
  React.useEffect(() => {
    if (currentDashboardType && currentDashboardType !== activeTab) {
      setActiveTab(currentDashboardType);
    }
  }, [currentDashboardType]);

  // Fonction pour changer d'onglet avec gestion de l'historique
  const setActiveTabWithHistory = React.useCallback((tab, skipHistory = false) => {
    setActiveTab(tab);
    if (!skipHistory) {
      // Préserver le paramètre account de l'URL actuelle
      const currentParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const accountParam = currentParams.get('account');

      const params = new URLSearchParams();
      if (selectedClientId) params.set('client', selectedClientId);
      if (accountParam) params.set('account', accountParam);
      const queryString = params.toString();

      window.history.pushState(
        { dashboardTab: tab, clientId: selectedClientId, accountId: accountParam },
        '',
        `#dashboard/${tab}${queryString ? '?' + queryString : ''}`
      );
    }
  }, [selectedClientId]);

  // Écouter le bouton back/forward du navigateur pour les onglets
  React.useEffect(() => {
    const handlePopState = (event) => {
      if (event.state && event.state.dashboardTab) {
        setActiveTab(event.state.dashboardTab);
      }
    };
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  // Initialiser l'historique avec l'onglet actif au montage du composant
  React.useEffect(() => {
    // Remplacer l'état actuel avec l'onglet dashboard correct
    const currentHash = window.location.hash;
    // Préserver le paramètre account s'il existe
    const currentParams = new URLSearchParams(currentHash.split('?')[1] || '');
    const accountParam = currentParams.get('account');

    // L'écran de sélection n'a pas de plateforme : son adresse est `#dashboard`, et la
    // compléter produirait un `#dashboard/selection/global` qui ne désigne rien.
    const surLaSelection = !activeTab || activeTab === 'selection';

    if (!surLaSelection && (currentHash === '#dashboard' || !currentHash.startsWith('#dashboard/'))) {
      const params = new URLSearchParams();
      if (selectedClientId) params.set('client', selectedClientId);
      if (accountParam) params.set('account', accountParam);
      const queryString = params.toString();
      const newUrl = `#dashboard/${activeTab}/global${queryString ? '?' + queryString : ''}`;
      window.history.replaceState(
        { dashboardTab: activeTab, dashboardModel: 'global', clientId: selectedClientId, accountId: accountParam },
        '',
        newUrl
      );
    }
  }, [selectedClientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compter le nombre total de comptes publicitaires dans la bibliothèque
  useEffect(() => {
    const countAccounts = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API_URL}/api/library`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        
        // Compter les comptes des plateformes publicitaires
        const library = response.data.library || {};
        const googleAdsCount = library.GOOGLE_ADS?.length || 0;
        const microsoftAdsCount = library.MICROSOFT_ADS?.length || 0;
        const facebookAdsCount = library.FACEBOOK_ADS?.length || 0;
        
        const totalCount = googleAdsCount + microsoftAdsCount + facebookAdsCount;
        setTotalAccountsCount(totalCount);
      } catch (error) {
        console.error('Erreur lors du comptage des comptes:', error);
        setTotalAccountsCount(0);
      }
    };
    
    countAccounts();
  }, [token]);
  
  // Rediriger depuis Synthèse si moins de 2 comptes ou si l'utilisateur n'est pas admin
  useEffect(() => {
    if (activeTab === 'synthese' && (totalAccountsCount < 2 || !user || user.role !== 'ADMIN')) {
      setActiveTab('google');
    }
  }, [activeTab, totalAccountsCount, user]);

  // Synchroniser le header avec l'état initial du dashboard
  useEffect(() => {
    const event = new CustomEvent('dashboardTypeChange', {
      detail: { type: activeTab }
    });
    window.dispatchEvent(event);
  }, [activeTab]);
  
  // Fonction locale pour vérifier l'accès analyseur
  const canAccessAnalyzer = () => hasAnalyzerAccess;

  // État partagé pour le modèle de dashboard sélectionné
  const [selectedDashboardModel, setSelectedDashboardModel] = useState(() => {
    // Extraire le modèle depuis le hash si on est sur le dashboard
    // Format: #dashboard/google/global ou #dashboard/google/search-terms
    if (window.location.hash.startsWith('#dashboard/')) {
      const hashSegments = window.location.hash.split('?')[0].replace('#', '').split('/');
      const model = hashSegments[2];
      if (model && ['global', 'comparatif', 'keywords', 'search-terms', 'landing-pages', 'performance-max', 'shopping', 'products-insights', 'youtube-ads', 'competition', 'cross-sales'].includes(model)) {
        return model;
      }
    }
    return 'global';
  });

  // Fonction pour changer de modèle de dashboard avec gestion de l'historique
  const setSelectedDashboardModelWithHistory = React.useCallback((model, skipHistory = false) => {
    setSelectedDashboardModel(model);
    if (!skipHistory) {
      // Récupérer le paramètre account depuis l'URL actuelle
      const currentParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
      const currentAccountId = currentParams.get('account');

      const params = new URLSearchParams();
      if (selectedClientId) params.set('client', selectedClientId);
      if (currentAccountId) params.set('account', currentAccountId);
      const queryString = params.toString();

      window.history.pushState(
        { dashboardTab: activeTab, dashboardModel: model, clientId: selectedClientId, accountId: currentAccountId },
        '',
        `#dashboard/${activeTab}/${model}${queryString ? '?' + queryString : ''}`
      );
    }
  }, [activeTab, selectedClientId]);

  // Fonction pour mettre à jour l'URL avec le compte sélectionné et la période
  const updateUrlWithAccount = React.useCallback((accountId) => {
    const params = new URLSearchParams();
    // Récupérer le compte actuel depuis l'URL si aucun n'est passé
    const currentParams = new URLSearchParams(window.location.hash.split('?')[1] || '');
    const effectiveAccountId = accountId || currentParams.get('account');

    if (selectedClientId) params.set('client', selectedClientId);
    if (selectedSiteId && selectedSiteId !== 'all') params.set('site', selectedSiteId);
    if (effectiveAccountId) params.set('account', effectiveAccountId);
    // Ajouter la période
    if (selectedPeriod) params.set('period', selectedPeriod);
    if (selectedPeriod === 'CUSTOM' && customStartDate) params.set('startDate', customStartDate);
    if (selectedPeriod === 'CUSTOM' && customEndDate) params.set('endDate', customEndDate);
    // Ajouter la comparaison si activée
    if (globalCompareEnabled) {
      params.set('compare', 'true');
      params.set('compareType', globalComparisonType);
      if (globalComparisonType === 'custom' && comparisonStartDate) params.set('compareStart', comparisonStartDate);
      if (globalComparisonType === 'custom' && comparisonEndDate) params.set('compareEnd', comparisonEndDate);
    }
    const queryString = params.toString();
    const newUrl = `#dashboard/${activeTab}/${selectedDashboardModel}${queryString ? '?' + queryString : ''}`;
    window.history.replaceState(
      { dashboardTab: activeTab, dashboardModel: selectedDashboardModel, clientId: selectedClientId, siteId: selectedSiteId, accountId: effectiveAccountId },
      '',
      newUrl
    );
  }, [activeTab, selectedDashboardModel, selectedClientId, selectedSiteId, selectedPeriod, customStartDate, customEndDate, globalCompareEnabled, globalComparisonType, comparisonStartDate, comparisonEndDate]);

  // Exposer updateUrlWithAccount via window pour les dashboards enfants
  React.useEffect(() => {
    window.updateUrlWithAccount = updateUrlWithAccount;
    return () => {
      delete window.updateUrlWithAccount;
    };
  }, [updateUrlWithAccount]);

  // Mettre à jour l'URL quand les paramètres changent (tab, modèle, site, période, comparaison)
  React.useEffect(() => {
    // Ne mettre à jour que si on est sur un dashboard (pas sur la page de sélection)
    if (activeTab && activeTab !== 'selection' && selectedDashboardModel) {
      // Utiliser un petit délai pour s'assurer que window.updateUrlWithAccount est à jour
      setTimeout(() => {
        window.updateUrlWithAccount?.();
      }, 50);
    }
  }, [activeTab, selectedDashboardModel, selectedSiteId, selectedPeriod, customStartDate, customEndDate, globalCompareEnabled, globalComparisonType, comparisonStartDate, comparisonEndDate]);

  // Mettre à jour le listener popstate pour gérer aussi les modèles de dashboard
  React.useEffect(() => {
    const handlePopStateForModel = (event) => {
      if (event.state && event.state.dashboardModel) {
        setSelectedDashboardModel(event.state.dashboardModel);
      }
    };
    window.addEventListener('popstate', handlePopStateForModel);
    return () => window.removeEventListener('popstate', handlePopStateForModel);
  }, []);

  // État pour les données de comparaison de période
  const [previousPeriodData, setPreviousPeriodData] = useState(null);
  
  // États pour le bouton Analyse IA
  const [showAIButton, setShowAIButton] = useState(false);
  const [preselectedAccountId, setPreselectedAccountId] = useState(null);
  const [shouldResetDashboards, setShouldResetDashboards] = useState(false);
  const [preselectionUsed, setPreselectionUsed] = useState(false);
  
  
  // Exposer setPreselectedAccountId via window pour permettre le nettoyage
  useEffect(() => {
    window.setPreselectedAccountId = setPreselectedAccountId;
    return () => {
      delete window.setPreselectedAccountId;
    };
  }, []);

  // Extraire le paramètre account de l'URL au montage (ex: retour OAuth)
  useEffect(() => {
    const hashParts = window.location.hash.split('?');
    if (hashParts.length > 1) {
      const hashParams = new URLSearchParams(hashParts[1]);
      const accountFromUrl = hashParams.get('account');
      if (accountFromUrl) {
        console.log('🎯 DashboardView - Compte détecté dans URL:', accountFromUrl);
        setPreselectedAccountId(accountFromUrl);
      }
    }
  }, []); // Seulement au montage

  // Gérer la navigation depuis le localStorage
  React.useEffect(() => {
    const targetTab = localStorage.getItem('kokpeet_targetTab');
    const accountData = localStorage.getItem('kokpeet_selectedAccount');

    if (targetTab) {
      console.log('🎯 DashboardView - Tab cible détecté:', targetTab);
      // Skip history car navigateToDashboardWithAccount gère déjà l'URL
      setActiveTabWithHistory(targetTab, true);

      // Nettoyer le localStorage après utilisation
      localStorage.removeItem('kokpeet_targetTab');
      
      if (accountData) {
        try {
          const parsedData = JSON.parse(accountData);
          console.log('📅 DashboardView - Données de navigation:', parsedData);
          
          // Stocker l'ID du compte présélectionné
          if (parsedData.accountId) {
            console.log('🎯 DashboardView - Compte présélectionné:', parsedData.accountId);
            setPreselectedAccountId(parsedData.accountId);
          }
          
          // Mettre à jour la période si nécessaire
          if (parsedData.period && parsedData.period !== selectedPeriod) {
            console.log('📅 DashboardView - Mise à jour période:', parsedData.period);
            setSelectedPeriod(parsedData.period);
          }
          if (parsedData.customStartDate !== customStartDate) {
            console.log('📅 DashboardView - Mise à jour date début:', parsedData.customStartDate);
            setCustomStartDate(parsedData.customStartDate || '');
          }
          if (parsedData.customEndDate !== customEndDate) {
            console.log('📅 DashboardView - Mise à jour date fin:', parsedData.customEndDate);
            setCustomEndDate(parsedData.customEndDate || '');
          }
        } catch (e) {
          console.error('❌ Erreur parsing données navigation:', e);
        }
      }
    }
  }, [setActiveTabWithHistory]);

  // Nettoyer complètement quand on quitte Dashboard après une navigation depuis Synthèse
  React.useEffect(() => {
    if (preselectedAccountId && !ONGLETS_AVEC_PRESELECTION.has(activeTab)) {
      console.log('🧹 DashboardView - Nettoyage présélection et reset dashboards');
      setPreselectedAccountId(null);
      // Forcer un reset complet en changeant temporairement l'onglet actif
      const previousTab = activeTab;
      setActiveTab('');
      setTimeout(() => {
        setActiveTab(previousTab);
      }, 0);
    }
  }, [activeTab, preselectedAccountId]);

  // Écouter les événements de navigation forcée
  React.useEffect(() => {
    const handleForceNavigation = (event) => {
      const { targetTab, navigationData } = event.detail;
      console.log('🎯 DashboardView - Événement navigation forcée reçu:', targetTab);

      // Changer l'onglet actif SANS historique (car navigateToDashboardWithAccount gère déjà l'URL)
      setActiveTabWithHistory(targetTab, true);
      
      // Mettre à jour les données si nécessaire
      if (navigationData) {
        console.log('📅 DashboardView - Mise à jour des données depuis événement:', navigationData);
        
        if (navigationData.accountId) {
          setPreselectedAccountId(navigationData.accountId);
        }
        if (navigationData.period && navigationData.period !== selectedPeriod) {
          setSelectedPeriod(navigationData.period);
        }
        if (navigationData.customStartDate !== customStartDate) {
          setCustomStartDate(navigationData.customStartDate || '');
        }
        if (navigationData.customEndDate !== customEndDate) {
          setCustomEndDate(navigationData.customEndDate || '');
        }
      }
    };

    window.addEventListener('forceNavigation', handleForceNavigation);
    
    // Gestionnaire pour les changements de type de dashboard depuis le header
    const handleDashboardTypeChange = (event) => {
      const { type } = event.detail;
      console.log('🎯 DashboardView - Changement de type depuis header:', type);
      setActiveTabWithHistory(type);
    };

    // Gestionnaire pour les changements de période depuis la sidebar
    const handlePeriodChange = (event) => {
      const { period } = event.detail;
      console.log('📅 DashboardView - Changement de période depuis sidebar:', period);
      setSelectedPeriod(period);
    };

    // Gestionnaire pour les changements de compte depuis la sidebar
    const handleAccountChange = (event) => {
      const { accountId } = event.detail;
      console.log('👤 DashboardView - Changement de compte depuis sidebar:', accountId);
      setPreselectedAccountId(accountId);
    };

    // Gestionnaire pour les changements de modèle depuis la sidebar
    const handleDashboardModelChange = (event) => {
      const { model } = event.detail;
      console.log('📋 DashboardView - Changement de modèle depuis sidebar:', model);
      setSelectedDashboardModelWithHistory(model);
    };

    window.addEventListener('dashboardTypeChange', handleDashboardTypeChange);
    window.addEventListener('periodChange', handlePeriodChange);
    window.addEventListener('accountChange', handleAccountChange);
    window.addEventListener('dashboardModelChange', handleDashboardModelChange);

    return () => {
      window.removeEventListener('forceNavigation', handleForceNavigation);
      window.removeEventListener('dashboardTypeChange', handleDashboardTypeChange);
      window.removeEventListener('periodChange', handlePeriodChange);
      window.removeEventListener('accountChange', handleAccountChange);
      window.removeEventListener('dashboardModelChange', handleDashboardModelChange);
    };
  }, [selectedPeriod, customStartDate, customEndDate, setActiveTabWithHistory, setSelectedDashboardModelWithHistory]);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  
  // Fonction pour déclencher l'analyse IA
  const handleAIAnalysis = () => {
    if (activeTab === 'google' && window.googleDashboardAIAnalysis) {
      window.googleDashboardAIAnalysis();
    } else if (activeTab === 'microsoft' && window.microsoftDashboardAIAnalysis) {
      window.microsoftDashboardAIAnalysis();
    } else if (activeTab === 'facebook-ads' && window.facebookDashboardAIAnalysis) {
      window.facebookDashboardAIAnalysis();
    } else if (activeTab === 'synthese' && window.syntheseDashboardAIAnalysis) {
      window.syntheseDashboardAIAnalysis();
    }
  };
  
  // Exposer les états pour les composants enfants
  useEffect(() => {
    window.setDashboardAIButton = setShowAIButton;
    window.setDashboardAIAnalyzing = setIsAnalyzing;
  }, []);
  
  // Réinitialiser le bouton AI quand on change d'onglet
  useEffect(() => {
    setShowAIButton(false);
    setIsAnalyzing(false);
  }, [activeTab]);

  // Fonction helper pour calculer les dates selon la période sélectionnée
  const calculatePeriodDates = (period) => {
    const today = new Date();
    let startDate, endDate;
    
    switch (period) {
      case 'LAST_7_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1); // Hier
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 6); // 7 jours avant
        break;
      case 'LAST_14_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 13);
        break;
      case 'LAST_30_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 29);
        break;
      case 'LAST_60_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 59);
        break;
      case 'LAST_90_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 89);
        break;
      case 'LAST_180_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 179);
        break;
      case 'LAST_365_DAYS':
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        startDate = new Date(endDate);
        startDate.setDate(startDate.getDate() - 364);
        break;
      case 'THIS_MONTH':
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        break;
      case 'LAST_MONTH':
        startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
        endDate = new Date(today.getFullYear(), today.getMonth(), 0);
        break;
      case 'THIS_YEAR':
        startDate = new Date(today.getFullYear(), 0, 1);
        endDate = new Date(today);
        endDate.setDate(endDate.getDate() - 1);
        break;
      case 'LAST_YEAR':
        startDate = new Date(today.getFullYear() - 1, 0, 1);
        endDate = new Date(today.getFullYear() - 1, 11, 31);
        break;
      case 'CUSTOM':
        return null; // Les dates custom sont déjà définies
      default:
        return null;
    }
    
    // Formater les dates en YYYY-MM-DD
    const formatDate = (date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    };
    
    return {
      startDate: formatDate(startDate),
      endDate: formatDate(endDate)
    };
  };
  
  // Fonction helper pour récupérer les données de période précédente/année précédente
  // lightweight: true = uniquement les totaux pour KPICards, false = données complètes (campagnes, devices, etc.)
  const fetchPreviousPeriodData = async (accountId, comparisonTypeParam = 'previous_period', lightweight = false) => {
    if (!accountId) {
      console.log('🔍 AccountId manquant pour période précédente');
      return null;
    }

    // Déterminer les dates de la période principale
    let startDate, endDate;
    if (selectedPeriod === 'CUSTOM') {
      startDate = customStartDate;
      endDate = customEndDate;
      if (!startDate || !endDate) {
        console.log('🔍 Dates personnalisées manquantes pour période précédente');
        return null;
      }
    } else {
      const periodDates = calculatePeriodDates(selectedPeriod);
      if (!periodDates) {
        console.log('🔍 Impossible de calculer les dates pour la période:', selectedPeriod);
        return null;
      }
      startDate = periodDates.startDate;
      endDate = periodDates.endDate;
    }

    // Utiliser le type de comparaison global si disponible
    const effectiveComparisonType = globalComparisonType || comparisonTypeParam;

    // Pour les dates personnalisées, envoyer directement les dates de comparaison
    const isCustomComparison = effectiveComparisonType === 'custom' && comparisonStartDate && comparisonEndDate;

    try {
      console.log('📊 Récupération période comparaison pour:', {
        accountId, startDate, endDate,
        comparisonType: effectiveComparisonType,
        isCustomComparison,
        customComparisonDates: isCustomComparison ? { comparisonStartDate, comparisonEndDate } : null,
        lightweight
      });

      const response = await axios.post(`${API_URL}/api/dashboard/previous-period-comparison`, {
        accountId,
        startDate: startDate,
        endDate: endDate,
        comparisonType: isCustomComparison ? 'custom' : effectiveComparisonType,
        periodType: selectedPeriod,
        // Envoyer les dates de comparaison personnalisées si définies
        ...(isCustomComparison && {
          comparisonStartDate: comparisonStartDate,
          comparisonEndDate: comparisonEndDate
        }),
        lightweight
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.success) {
        console.log(`✅ Données période précédente récupérées (${lightweight ? 'lightweight' : 'complètes'}):`, response.data.data);
        if (response.data.data && response.data.data.period) {
        }
        return response.data.data;
      } else {
        console.log('⚠️ Échec récupération période précédente:', response.data.error);
        return null;
      }
    } catch (error) {
      console.error('❌ Erreur récupération période précédente:', error);
      return null;
    }
  };

  return (
    <div className="step-content">
      {/**
        * Bannière d'erreur d'authentification, **sur le seul onglet concerné**.
        *
        * Elle ne tenait compte que de l'existence de l'erreur, jamais de la plateforme qu'elle
        * vise : une connexion Meta expirée annonçait donc « Authentification Meta Ads expirée »
        * par-dessus le dashboard Google Ads, qui fonctionnait parfaitement. Le bouton de
        * reconnexion y était juste, mais la place était fausse — on lisait un avertissement sur
        * un écran qu'il ne concernait pas, et rien ne permettait de comprendre pourquoi.
        *
        * L'erreur reste en mémoire : revenir sur Meta la fait réapparaître, puisque l'accès est
        * toujours expiré. C'est bien l'affichage qui devait suivre l'onglet, pas l'état qu'il
        * fallait effacer — l'effacer aurait masqué un problème non résolu.
        *
        * `authError.platform` est absent sur les erreurs les plus anciennes, que le reste de ce
        * bloc traite déjà comme du Google Ads : la même lecture est reprise ici, faute de quoi
        * elles ne s'afficheraient plus nulle part.
        */}
      {authError && (authError.platform || 'GOOGLE_ADS') === TYPE_LIBRAIRIE_PAR_PLATEFORME[activeTab] && (
        <div style={{
          backgroundColor: authError.permissionDenied ? '#f8d7da' : '#fff3cd',
          border: `1px solid ${authError.permissionDenied ? '#f5c6cb' : '#ffeaa7'}`,
          borderRadius: '8px',
          padding: '16px',
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            <AlertCircle size={20} style={{ color: authError.permissionDenied ? '#721c24' : '#856404' }} />
            <div>
              <div style={{ fontWeight: '600', color: authError.permissionDenied ? '#721c24' : '#856404', marginBottom: '4px' }}>
                {authError.permissionDenied
                  ? 'Accès au compte refusé'
                  : `Authentification ${authError.platform === 'FACEBOOK_ADS' ? 'Meta Ads' : authError.platform === 'TIKTOK_ADS' ? 'TikTok Ads' : authError.platform === 'PINTEREST_ADS' ? 'Pinterest Ads' : authError.platform === 'MICROSOFT_ADS' ? 'Microsoft Ads' : 'Google Ads'} expirée`}
              </div>
              <div style={{ color: '#6c757d', fontSize: '14px' }}>
                {authError.permissionDenied
                  ? 'Le propriétaire de ce compte publicitaire ne vous a pas accordé les permissions nécessaires. Vous pouvez retirer ce compte de votre bibliothèque.'
                  : (authError.message || `Votre connexion ${authError.platform === 'FACEBOOK_ADS' ? 'Meta Ads' : authError.platform === 'TIKTOK_ADS' ? 'TikTok Ads' : authError.platform === 'PINTEREST_ADS' ? 'Pinterest Ads' : authError.platform === 'MICROSOFT_ADS' ? 'Microsoft Ads' : 'Google Ads'} a expiré. Veuillez vous reconnecter.`)}
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', gap: '8px' }}>
            {authError.permissionDenied && authError.userRole !== 'CLIENT' && (
              <button
                onClick={() => {
                  // Fermer la bannière et rediriger vers la bibliothèque
                  setAuthError(null);
                  setCurrentView('library');
                }}
                style={{
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Gérer ma bibliothèque
              </button>
            )}
            {authError.requiresReauth && authError.userRole !== 'CLIENT' && (
              <button
                onClick={() => handleReconnect(authError.platform || 'GOOGLE_ADS')}
                style={{
                  backgroundColor: '#1a1a1a',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 16px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer'
                }}
              >
                Reconnecter {authError.platform === 'FACEBOOK_ADS' ? 'Facebook' : authError.platform === 'TIKTOK_ADS' ? 'TikTok' : authError.platform === 'PINTEREST_ADS' ? 'Pinterest' : authError.platform === 'MICROSOFT_ADS' ? 'Microsoft' : 'Google'}
              </button>
            )}
          </div>
        </div>
      )}

      {/*
        La bannière des comptes inaccessibles a été retirée d'ici.

        Elle ne prévenait que qui ouvrait un écran — or c'est justement le compte qu'on
        n'ouvre plus qui perd son accès sans qu'on s'en aperçoive. Pire, elle s'affichait
        hors de son contexte : un bandeau « compte Google Ads inaccessible » coiffait le
        dashboard ChatGPT Ads d'un autre client.

        Un accès coupé est un événement daté, pas un état de page : il passe désormais par
        les notifications — e-mail, carte dans Kokpit, cloche — comme les alertes de trafic.
        Voir `services/alerts/AccountAccessAlertService.js`. Le repérage compte par compte
        reste dans la bibliothèque, où l'on vient justement gérer ses comptes.
      */}

      {/* Affichage du dashboard sélectionné */}
      {activeTab === 'google' && (
        <MemoizedDashboardGoogleAds
          token={token}
          setError={setError}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          selectedDashboardModel={selectedDashboardModel}
          setSelectedDashboardModel={setSelectedDashboardModelWithHistory}
          setCurrentView={setCurrentView}
          preselectedAccountId={preselectedAccountId}
          activeTab={activeTab}
          preselectionUsed={preselectionUsed}
          setPreselectionUsed={setPreselectionUsed}
          fetchPreviousPeriodData={fetchPreviousPeriodData}
          authError={authError}
          setAuthError={setAuthError}
          handleApiError={handleApiError}
          globalConnectors={globalConnectors}
          canAccessAnalyzer={hasAnalyzerAccess}
          showAIButton={showAIButton}
          selectedClientId={selectedClientId}
          currentDashboardType={activeTab}
          updateUrlWithAccount={updateUrlWithAccount}
          dashboardNotes={dashboardNotes}
          fetchDashboardNotes={fetchDashboardNotes}
          selectedSiteId={selectedSiteId}
          userSites={userSites}
          pmaxCompareEnabled={compareEnabled}
          pmaxComparisonType={comparisonType}
          gadsComparisonType={gadsComparisonType}
          setGadsComparisonType={setGadsComparisonType}
          gadsCompareEnabled={gadsCompareEnabled}
          userRole={user?.role}
        />
      )}
      {activeTab === 'microsoft' && (
        <DashboardMicrosoftAds
          token={token}
          setError={setError}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          selectedDashboardModel={selectedDashboardModel}
          setSelectedDashboardModel={setSelectedDashboardModelWithHistory}
          preselectedAccountId={preselectedAccountId}
          activeTab={activeTab}
          preselectionUsed={preselectionUsed}
          setPreselectionUsed={setPreselectionUsed}
          fetchPreviousPeriodData={fetchPreviousPeriodData}
          globalConnectors={globalConnectors}
          canAccessAnalyzer={hasAnalyzerAccess}
          selectedClientId={selectedClientId}
          updateUrlWithAccount={updateUrlWithAccount}
          dashboardNotes={dashboardNotes}
          fetchDashboardNotes={fetchDashboardNotes}
          selectedSiteId={selectedSiteId}
          userSites={userSites}
          userRole={user?.role}
          msadsComparisonType={msadsComparisonType}
          msadsCompareEnabled={msadsCompareEnabled}
          comparisonStartDate={comparisonStartDate}
          comparisonEndDate={comparisonEndDate}
        />
      )}
      {activeTab === 'facebook-ads' && (
        <DashboardFacebookAds
          token={token}
          setError={setError}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          selectedDashboardModel={selectedDashboardModel}
          setSelectedDashboardModel={setSelectedDashboardModelWithHistory}
          preselectedAccountId={preselectedAccountId}
          activeTab={activeTab}
          preselectionUsed={preselectionUsed}
          setPreselectionUsed={setPreselectionUsed}
          globalConnectors={globalConnectors}
          canAccessAnalyzer={hasAnalyzerAccess}
          selectedClientId={selectedClientId}
          updateUrlWithAccount={updateUrlWithAccount}
          authError={authError}
          setAuthError={setAuthError}
          handleApiError={handleApiError}
          handleReconnect={handleReconnect}
          dashboardNotes={dashboardNotes}
          fetchDashboardNotes={fetchDashboardNotes}
          selectedSiteId={selectedSiteId}
          userSites={userSites}
          compareEnabled={fbCompareEnabled}
          comparisonType={fbComparisonType}
          userRole={user?.role}
          comparisonStartDate={comparisonStartDate}
          comparisonEndDate={comparisonEndDate}
        />
      )}
      {activeTab === 'awin' && (
        <AwinDashboardView
          token={token}
          analyseAutorisee={canAccessAnalyzer}
          selectedPeriod={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          selectedClientId={selectedClientId}
          selectedSiteId={selectedSiteId}
          userSites={userSites}
          preselectedAccountId={preselectedAccountId}
          compareEnabled={fbCompareEnabled}
          comparisonType={fbComparisonType}
          formatNumber={formatNumber}
          KPICard={KPICard}
          dashboardNotes={dashboardNotes}
          userRole={user?.role}
        />
      )}
      {activeTab === 'chatgpt-ads' && (
        <ChatGPTAdsDashboardView
          token={token}
          analyseAutorisee={canAccessAnalyzer}
          selectedPeriod={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          selectedClientId={selectedClientId}
          selectedSiteId={selectedSiteId}
          userSites={userSites}
          preselectedAccountId={preselectedAccountId}
          compareEnabled={fbCompareEnabled}
          comparisonType={fbComparisonType}
          comparisonStartDate={comparisonStartDate}
          comparisonEndDate={comparisonEndDate}
          formatNumber={formatNumber}
          KPICard={KPICard}
          globalConnectors={globalConnectors}
          dashboardNotes={dashboardNotes}
          userRole={user?.role}
        />
      )}
      {activeTab === 'pinterest-ads' && (
        <DashboardPinterestAds
          token={token}
          setError={setError}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          selectedDashboardModel={selectedDashboardModel}
          setSelectedDashboardModel={setSelectedDashboardModelWithHistory}
          preselectedAccountId={preselectedAccountId}
          activeTab={activeTab}
          preselectionUsed={preselectionUsed}
          setPreselectionUsed={setPreselectionUsed}
          globalConnectors={globalConnectors}
          canAccessAnalyzer={hasAnalyzerAccess}
          selectedClientId={selectedClientId}
          updateUrlWithAccount={updateUrlWithAccount}
          authError={authError}
          setAuthError={setAuthError}
          handleApiError={handleApiError}
          handleReconnect={handleReconnect}
          dashboardNotes={dashboardNotes}
          fetchDashboardNotes={fetchDashboardNotes}
          selectedSiteId={selectedSiteId}
          userSites={userSites}
          compareEnabled={false}
          comparisonType=""
          userRole={user?.role}
          comparisonStartDate={comparisonStartDate}
          comparisonEndDate={comparisonEndDate}
        />
      )}
      {activeTab === 'tiktok-ads' && (
        <DashboardTikTokAds
          token={token}
          setError={setError}
          selectedPeriod={selectedPeriod}
          setSelectedPeriod={setSelectedPeriod}
          customStartDate={customStartDate}
          setCustomStartDate={setCustomStartDate}
          customEndDate={customEndDate}
          setCustomEndDate={setCustomEndDate}
          selectedDashboardModel={selectedDashboardModel}
          setSelectedDashboardModel={setSelectedDashboardModelWithHistory}
          preselectedAccountId={preselectedAccountId}
          activeTab={activeTab}
          preselectionUsed={preselectionUsed}
          setPreselectionUsed={setPreselectionUsed}
          globalConnectors={globalConnectors}
          canAccessAnalyzer={hasAnalyzerAccess}
          selectedClientId={selectedClientId}
          updateUrlWithAccount={updateUrlWithAccount}
          authError={authError}
          setAuthError={setAuthError}
          handleApiError={handleApiError}
          handleReconnect={handleReconnect}
          dashboardNotes={dashboardNotes}
          fetchDashboardNotes={fetchDashboardNotes}
          selectedSiteId={selectedSiteId}
          userSites={userSites}
          compareEnabled={false}
          comparisonType=""
          userRole={user?.role}
          comparisonStartDate={comparisonStartDate}
          comparisonEndDate={comparisonEndDate}
        />
      )}
      {activeTab === 'selection' && (
        <div className="dashboard-content">
          <div className="page-header" style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
            <h2 style={{ margin: 0 }}>
              <BarChart3 size={18} style={{ marginRight: 8, verticalAlign: 'middle' }} />
              Dashboard
            </h2>
          </div>
          <p style={{ margin: '0 0 32px 0', color: '#64748b', fontSize: '1rem' }}>
            Sélectionnez une plateforme pour accéder à son dashboard
          </p>

          {/* La section « Consolidé » a été retirée d'ici, et non déplacée.
             Elle doublait la section Analytics juste en dessous — mêmes logos, mêmes trois
             solutions, seule la destination changeait. C'était poser la question du dashboard
             deux fois avant de savoir de quelle propriété on parle. Les cartes Analytics mènent
             désormais à l'écran de choix, qui la pose une fois, après le clic. */}
          {/*
            Rubrique CMS, au-dessus d'Analytics — même raison que dans la barre latérale : une
            boutique répond à « combien ont acheté », une solution de mesure à « combien sont
            venus ». Elle parcourt `CMS`, donc un CMS de plus n'ajoute rien ici.
          */}
          {cmsDisponibles.length > 0 && (
            <>
              <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                CMS
              </h3>
              <div style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(3, 1fr)',
                gap: '16px',
                marginBottom: '32px'
              }}>
                {cmsDisponibles.map(cms => (
                  <div
                    key={cms.cle}
                    onClick={() => ouvrirChoixDashboard(cms.cle, null, null, 'dashboard')}
                    style={styleCarte}
                    className="dashboard-selection-card"
                  >
                    {cms.logo
                      ? <img src={cms.logo} alt={cms.libelle} style={{ width: '32px', height: '32px', borderRadius: '6px' }} />
                      : <Store size={32} />}
                    <span style={{ fontSize: '0.95rem', fontWeight: '500', color: '#1e293b' }}>
                      {cms.libelle}
                    </span>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Section Analytics — ⚠️ le titre suit ses entrées : filtrer les trois cartes en
              laissant « ANALYTICS » au-dessus du vide donnait un écran qui annonce une section
              et n'en montre aucune, ce qui se lit comme un chargement qui a échoué. */}
          {((filteredGA4Properties.length > 0 && plateformeOuverte('ga4-dashboard'))
            || (filteredMatomoSites.length > 0 && plateformeOuverte('matomo-dashboard'))
            || (filteredPianoSites.length > 0 && plateformeOuverte('piano-dashboard'))) && (
              <>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Analytics
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px',
                  marginBottom: '32px'
                }}>
                  {filteredGA4Properties.length > 0 && plateformeOuverte('ga4-dashboard') && (
                    <div
                      onClick={() => ouvrirChoixDashboard('GOOGLE_ANALYTICS', null, null, 'dashboard')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={googleAnalyticsLogo}
                        alt="Google Analytics"
                        style={{ width: '32px', height: '32px', borderRadius: '6px' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        Google Analytics
                      </span>
                    </div>
                  )}
                  {filteredMatomoSites.length > 0 && plateformeOuverte('matomo-dashboard') && (
                    <div
                      onClick={() => ouvrirChoixDashboard('MATOMO', null, null, 'dashboard')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={matomoLogo}
                        alt="Matomo"
                        style={{ width: '32px', height: '32px', borderRadius: '6px' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        Matomo
                      </span>
                    </div>
                  )}
                  {filteredPianoSites.length > 0 && plateformeOuverte('piano-dashboard') && (
                    <div
                      onClick={() => ouvrirChoixDashboard('PIANO_ANALYTICS', null, null, 'dashboard')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={pianoLogo}
                        alt="Piano Analytics"
                        style={{ width: '32px', height: '32px', borderRadius: '6px' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        Piano Analytics
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}

            {/* Section Publicité */}
            {(availableSources?.google || availableSources?.microsoft || availableSources?.['facebook-ads'] || availableSources?.['pinterest-ads'] || availableSources?.['tiktok-ads'] || availableSources?.['chatgpt-ads'] || availableSources?.['awin']) && (
              <>
                <h3 style={{ margin: '0 0 16px 0', fontSize: '1rem', fontWeight: '600', color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Publicité
                </h3>
                <div style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '16px'
                }}>
                  {availableSources?.google && (
                    <div
                      onClick={() => setActiveTabWithHistory('google')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={googleAdsLogo}
                        alt="Google Ads"
                        style={{ width: '32px', height: '32px', borderRadius: '6px' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        Google Ads
                      </span>
                    </div>
                  )}
                  {availableSources?.microsoft && (
                    <div
                      onClick={() => setActiveTabWithHistory('microsoft')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={microsoftAdsLogo}
                        alt="Microsoft Ads"
                        style={{ width: '32px', height: '32px', borderRadius: '6px' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        Microsoft Ads
                      </span>
                    </div>
                  )}
                  {availableSources?.['facebook-ads'] && (
                    <div
                      onClick={() => setActiveTabWithHistory('facebook-ads')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={metaAdsLogo}
                        alt="Meta Ads"
                        style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        Meta Ads
                      </span>
                    </div>
                  )}
                  {availableSources?.['chatgpt-ads'] && (
                    <div
                      onClick={() => setActiveTabWithHistory('chatgpt-ads')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={chatgptAdsLogo}
                        alt="ChatGPT Ads"
                        style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        ChatGPT Ads
                      </span>
                    </div>
                  )}
                  {availableSources?.['awin'] && (
                    <div
                      onClick={() => setActiveTabWithHistory('awin')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={awinLogo}
                        alt="Awin"
                        style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        Awin
                      </span>
                    </div>
                  )}
                  {availableSources?.['pinterest-ads'] && (
                    <div
                      onClick={() => setActiveTabWithHistory('pinterest-ads')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={pinterestAdsLogo}
                        alt="Pinterest Ads"
                        style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        Pinterest Ads
                      </span>
                    </div>
                  )}
                  {availableSources?.['tiktok-ads'] && (
                    <div
                      onClick={() => setActiveTabWithHistory('tiktok-ads')}
                      style={styleCarte}
                      className="dashboard-selection-card"
                    >
                      <img
                        src={tiktokAdsLogo}
                        alt="TikTok Ads"
                        style={{ width: '32px', height: '32px', borderRadius: '6px', objectFit: 'contain' }}
                      />
                      <span style={{ fontSize: '1rem', fontWeight: '600', color: '#1e293b' }}>
                        TikTok Ads
                      </span>
                    </div>
                  )}
                </div>
              </>
            )}
        </div>
      )}
      {activeTab === 'synthese' && (
        user && user.role === 'ADMIN' ? (
          <SyntheseDashboard
            token={token}
            selectedPeriod={selectedPeriod}
            setSelectedPeriod={setSelectedPeriod}
            customStartDate={customStartDate}
            setCustomStartDate={setCustomStartDate}
            customEndDate={customEndDate}
            setCustomEndDate={setCustomEndDate}
            setCurrentView={setCurrentView}
            fetchPreviousPeriodData={fetchPreviousPeriodData}
            selectedClientId={selectedClientId}
            selectedSiteId={selectedSiteId}
          />
        ) : (
          <div style={{
            padding: '40px',
            textAlign: 'center',
            backgroundColor: '#fef3c7',
            border: '1px solid #f59e0b',
            borderRadius: '8px',
            margin: '20px',
            color: '#92400e'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#92400e', display: 'flex', alignItems: 'center', gap: '8px' }}><Key size={18} /> Accès restreint</h3>
            <p style={{ margin: '0', lineHeight: '1.5' }}>
              Le dashboard "Synthèse des performances par site" est réservé aux administrateurs.
              <br />
              Contactez votre administrateur pour obtenir l'accès à cette fonctionnalité.
            </p>
          </div>
        )
      )}
    </div>
  );
}

// ===== COMPOSANT DASHBOARD SYNTHÈSE =====

function SyntheseDashboard({ token, selectedPeriod, setSelectedPeriod, customStartDate, setCustomStartDate, customEndDate, setCustomEndDate, setCurrentView, fetchPreviousPeriodData, selectedClientId }) {
  const [loading, setLoading] = useState(true);
  const [syntheseData, setSyntheseData] = useState([]);
  const [error, setError] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  
  const [showCustomDates, setShowCustomDates] = useState(false);

  // Fonction pour naviguer vers le dashboard avec un compte présélectionné
  const navigateToDashboard = (account) => {
    console.log('🔗 Navigation vers dashboard pour compte:', account);
    console.log('📅 Période actuelle dans Synthèse:', selectedPeriod);
    console.log('📅 Dates personnalisées:', { customStartDate, customEndDate });
    
    // Stocker les informations du compte et de la période dans le localStorage
    const navigationData = {
      accountId: account.accountId,
      accountType: account.accountType,
      period: selectedPeriod,
      customStartDate: customStartDate,
      customEndDate: customEndDate
    };
    console.log('💾 Données de navigation stockées:', navigationData);
    localStorage.setItem('kokpeet_selectedAccount', JSON.stringify(navigationData));
    
    // Déterminer l'onglet cible selon le type de compte
    const targetTab = account.accountType === 'MICROSOFT_ADS' ? 'microsoft' : 
                     account.accountType === 'FACEBOOK_ADS' ? 'facebook-ads' : 'google';
    localStorage.setItem('kokpeet_targetTab', targetTab);
    console.log('🎯 Tab cible:', targetTab);
    
    // Navigation directe vers le dashboard - déclencher un événement personnalisé
    console.log('🔄 Navigation directe vers dashboard');
    
    // Émettre un événement personnalisé pour forcer la navigation
    window.dispatchEvent(new CustomEvent('forceNavigation', {
      detail: { targetTab, navigationData }
    }));
    
    document.title = `Dashboard ${targetTab.charAt(0).toUpperCase() + targetTab.slice(1)} - Kokpit`;
    window.scrollTo(0, 0);
    console.log('✅ Navigation terminée vers', targetTab);
  };

  // Fonction de chargement des données
  const loadSyntheseData = async () => {
    setLoading(true);
    setError('');
    
    try {
      // Récupérer les données de performance pour tous les comptes
      const params = { period: selectedPeriod };
      if (selectedPeriod === 'CUSTOM') {
        params.customStartDate = customStartDate;
        params.customEndDate = customEndDate;
      }
      if (selectedClientId) {
        params.clientId = selectedClientId;
      }
      
      const performanceResponse = await axios.get(`${API_URL}/api/synthese/performance`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      
      if (performanceResponse.data.success) {
        setSyntheseData(performanceResponse.data.data || []);
      } else {
        throw new Error(performanceResponse.data.error || 'Erreur lors du chargement des données');
      }
    } catch (err) {
      console.error('Erreur synthèse:', err);
      setError(err.message || 'Erreur lors du chargement des données de synthèse');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    console.log('🎯 [SYNTHESE] Rechargement des données - selectedClientId:', selectedClientId);
    loadSyntheseData();
  }, [selectedPeriod, customStartDate, customEndDate, selectedClientId]);

  // Synthèse n'a pas besoin de données de période précédente
  // Les données de synthèse sont déjà agrégées et complètes

  // Fonction de tri
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  // Fonction de formatage des nombres
  const formatNumber = (num, decimals = 0) => {
    if (num === null || num === undefined || isNaN(num)) return '0';
    return new Intl.NumberFormat('fr-FR', {
      minimumFractionDigits: decimals,
      maximumFractionDigits: decimals
    }).format(num);
  };

  // Calculer le total global
  const totals = React.useMemo(() => {
    if (!syntheseData || !Array.isArray(syntheseData)) return {
      impressions: 0,
      clicks: 0,
      cost: 0,
      conversions: 0,
      conversionsValue: 0,
      accountsCount: 0
    };

    let globalTotals = {
      impressions: 0,
      clicks: 0,
      cost: 0,
      conversions: 0,
      conversionsValue: 0,
      accountsCount: 0
    };

    syntheseData.forEach(siteGroup => {
      if (siteGroup.totals) {
        globalTotals.impressions += siteGroup.totals.impressions;
        globalTotals.clicks += siteGroup.totals.clicks;
        globalTotals.cost += siteGroup.totals.cost;
        globalTotals.conversions += siteGroup.totals.conversions;
        globalTotals.conversionsValue += siteGroup.totals.conversionsValue || 0;
        globalTotals.accountsCount += siteGroup.accounts.length;
      }
    });

    return globalTotals;
  }, [syntheseData]);

  // Trier les données selon sortConfig
  const sortedData = React.useMemo(() => {
    if (!syntheseData || !Array.isArray(syntheseData)) return [];
    
    // Fonction helper pour obtenir la valeur de tri d'un compte
    const getSortValue = (item) => {
      if (sortConfig.key === 'cos') {
        return item.conversionsValue > 0 ? (item.cost / item.conversionsValue) * 100 : 0;
      }
      return item[sortConfig.key] || 0;
    };
    
    // Extraire tous les comptes avec leur site d'origine
    const allAccounts = [];
    syntheseData.forEach(siteGroup => {
      siteGroup.accounts.forEach(account => {
        allAccounts.push({
          ...account,
          siteId: siteGroup.siteId,
          siteName: siteGroup.siteName,
          siteDescription: siteGroup.siteDescription
        });
      });
    });
    
    // Trier tous les comptes
    allAccounts.sort((a, b) => {
      const aValue = getSortValue(a);
      const bValue = getSortValue(b);
      
      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
    
    // Reconstruire la structure groupée par site
    const siteMap = new Map();
    
    // D'abord, créer tous les sites (même ceux sans comptes)
    syntheseData.forEach(siteGroup => {
      siteMap.set(siteGroup.siteId || 'no-site', {
        siteId: siteGroup.siteId,
        siteName: siteGroup.siteName,
        siteDescription: siteGroup.siteDescription,
        accounts: [],
        totals: {
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          conversionsValue: 0,
          ctr: 0,
          cpc: 0,
          cpa: 0,
          conversionRate: 0
        }
      });
    });
    
    // Ajouter les comptes triés à leurs sites respectifs
    allAccounts.forEach(account => {
      const siteKey = account.siteId || 'no-site';
      const site = siteMap.get(siteKey);
      if (site) {
        // Retirer les propriétés de site de l'objet compte
        const { siteId, siteName, siteDescription, ...accountData } = account;
        site.accounts.push(accountData);
        
        // Mettre à jour les totaux
        site.totals.impressions += accountData.impressions;
        site.totals.clicks += accountData.clicks;
        site.totals.cost += accountData.cost;
        site.totals.conversions += accountData.conversions;
        site.totals.conversionsValue += accountData.conversionsValue || 0;
      }
    });
    
    // Calculer les métriques dérivées pour chaque site
    siteMap.forEach(site => {
      site.totals.ctr = site.totals.impressions > 0 ? (site.totals.clicks / site.totals.impressions) * 100 : 0;
      site.totals.cpc = site.totals.clicks > 0 ? (site.totals.cost / site.totals.clicks) : 0;
      site.totals.cpa = site.totals.conversions > 0 ? (site.totals.cost / site.totals.conversions) : 0;
      site.totals.conversionRate = site.totals.clicks > 0 ? (site.totals.conversions / site.totals.clicks) * 100 : 0;
    });
    
    // Convertir la Map en array et trier les sites par leur meilleur compte
    const result = Array.from(siteMap.values());
    
    // Trier les sites selon la valeur de leur premier compte (le meilleur selon le tri actuel)
    result.sort((a, b) => {
      // Sites sans comptes vont à la fin
      if (a.accounts.length === 0 && b.accounts.length === 0) return 0;
      if (a.accounts.length === 0) return 1;
      if (b.accounts.length === 0) return -1;
      
      // Comparer selon la valeur du premier compte de chaque site
      const aValue = getSortValue(a.accounts[0]);
      const bValue = getSortValue(b.accounts[0]);
      
      if (sortConfig.direction === 'asc') {
        return aValue - bValue;
      } else {
        return bValue - aValue;
      }
    });
    
    return result;
  }, [syntheseData, sortConfig]);

  // Gérer showCustomDates en fonction de selectedPeriod - doit être avant les returns
  useEffect(() => {
    setShowCustomDates(selectedPeriod === 'CUSTOM');
  }, [selectedPeriod]);

  if (loading) {
    return (
      <div className="dashboard-content">
        <div style={{ textAlign: 'center', padding: '40px' }}>
          <Loader className="spin" size={32} />
          <h3>Chargement de la synthèse...</h3>
          <p>Agrégation des données de tous vos comptes</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="dashboard-content">
        <div style={{ textAlign: 'center', padding: '40px', color: '#dc3545' }}>
          <h3><AlertCircle size={20} />&nbsp;Erreur</h3>
          <p>{error}</p>
          <button onClick={loadSyntheseData} className="btn btn-primary">
            Réessayer
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="dashboard-content">
      {/* Titre et sélecteur */}
      <div style={{ display: 'flex', alignItems: 'center', marginBottom: '24px', width: '100%' }}>
        <h3 style={{ margin: 0, flex: '1', display: 'flex', alignItems: 'center', gap: '8px' }}><TrendingUp size={18} /> Synthèse des performances par site</h3>
        
        {/* Sélecteur de période */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginLeft: 'auto' }}>
          <label htmlFor="synthese-period-select" style={{ whiteSpace: 'nowrap', display: 'flex', alignItems: 'center', gap: '4px' }}><Calendar size={14} /> Période :</label>
          <select
            id="synthese-period-select"
            value={selectedPeriod}
            onChange={(e) => {
              setSelectedPeriod(e.target.value);
              setShowCustomDates(e.target.value === 'CUSTOM');
              
              // Réinitialiser les dates personnalisées quand on change de période
              if (e.target.value !== 'CUSTOM') {
                setCustomStartDate('');
                setCustomEndDate('');
              }
            }}
            className="form-select"
            style={{ width: '180px', minWidth: '180px' }}
          >
            <option value="TODAY">Aujourd'hui</option>
            <option value="YESTERDAY">Hier</option>
            <option value="LAST_7_DAYS">7 derniers jours</option>
            <option value="LAST_14_DAYS">14 derniers jours</option>
            <option value="LAST_30_DAYS">30 derniers jours</option>
            <option value="THIS_MONTH">Ce mois</option>
            <option value="LAST_MONTH">Mois dernier</option>
            <option value="LAST_60_DAYS">60 derniers jours</option>
            <option value="LAST_90_DAYS">90 derniers jours</option>
            <option value="LAST_180_DAYS">180 derniers jours</option>
            <option value="LAST_365_DAYS">365 derniers jours</option>
            <option value="THIS_YEAR">Année en cours</option>
            <option value="LAST_YEAR">Année dernière</option>
            <option value="CUSTOM">Personnalisé</option>
          </select>
        </div>
      </div>

      {!syntheseData || syntheseData.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <h4>Aucune donnée disponible</h4>
          <p>Ajoutez vos comptes dans la Bibliothèque pour voir les résultats.</p>
        </div>
      ) : (
        <div className="campaign-types-table-wrapper">
          <table className="campaign-types-table">
            <thead>
              <tr>
                <th className="sortable">
                  Site / Compte
                </th>
                <th onClick={() => handleSort('impressions')} className="sortable numeric">
                  Impr. {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('ctr')} className="sortable numeric">
                  CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('clicks')} className="sortable numeric">
                  Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('cpc')} className="sortable numeric">
                  CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('cost')} className="sortable numeric">
                  Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('conversionRate')} className="sortable numeric">
                  Taux Conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('conversions')} className="sortable numeric">
                  Conv. {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('cpa')} className="sortable numeric">
                  CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('conversionsValue')} className="sortable numeric">
                  Val. Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
                <th onClick={() => handleSort('cos')} className="sortable numeric">
                  COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                </th>
              </tr>
            </thead>
            <tbody>
              {sortedData.map((siteGroup, siteIndex) => (
                <React.Fragment key={siteGroup.siteId || 'no-site'}>
                  {/* Header du site */}
                  <tr style={{ backgroundColor: '#f0f7ff' }}>
                    <td colSpan="11" style={{ 
                      fontWeight: '600', 
                      padding: '12px 16px',
                      fontSize: '14px'
                    }}>
                      🏢 {siteGroup.siteName}
                      {siteGroup.siteDescription && (
                        <span style={{ 
                          fontWeight: '400', 
                          color: '#666', 
                          marginLeft: '12px',
                          fontSize: '13px'
                        }}>
                          {siteGroup.siteDescription}
                        </span>
                      )}
                    </td>
                  </tr>
                  
                  {/* Comptes du site */}
                  {siteGroup.accounts.length > 0 ? (
                    siteGroup.accounts.map((account, accountIndex) => (
                      <tr key={`${siteGroup.siteId}-${account.accountId}`}>
                        <td style={{ paddingLeft: '32px' }}>
                          <button
                            onClick={() => navigateToDashboard(account)}
                            style={{
                              background: 'none',
                              border: 'none',
                              color: '#1a1a1a',
                              textDecoration: 'underline',
                              cursor: 'pointer',
                              padding: 0,
                              font: 'inherit'
                            }}
                            title={`Ouvrir le dashboard ${account.accountType === 'MICROSOFT_ADS' ? 'Microsoft Ads' : account.accountType === 'FACEBOOK_ADS' ? 'Meta Ads' : 'Google Ads'} pour ce compte`}
                          >
                            {account.accountType === 'MICROSOFT_ADS' ? 'Microsoft Ads' : account.accountType === 'FACEBOOK_ADS' ? 'Meta Ads' : 'Google Ads'} ({account.accountName.split(' (')[0]})
                          </button>
                        </td>
                        <td className="numeric">{formatNumber(account.impressions)}</td>
                        <td className="numeric">{formatNumber(account.ctr, 2)}%</td>
                        <td className="numeric">{formatNumber(account.clicks)}</td>
                        <td className="numeric">€{formatNumber(account.cpc, 2)}</td>
                        <td className="numeric">€{formatNumber(account.cost, 2)}</td>
                        <td className="numeric">{formatNumber(account.conversionRate, 2)}%</td>
                        <td className="numeric">{formatNumber(account.conversions, 1)}</td>
                        <td className="numeric">{account.cpa >= 0 ? `€${formatNumber(account.cpa, 2)}` : '-'}</td>
                        <td className="numeric">€{formatNumber(account.conversionsValue || 0, 2)}</td>
                        <td className={`numeric ${calculateCosClassName(account.cost, account.conversionsValue, 'global')}`}>
                          {account.conversionsValue > 0 && account.cost > 0 
                            ? `${formatNumber((account.cost / account.conversionsValue) * 100, 1)}%`
                            : '-'
                          }
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="11" style={{ 
                        paddingLeft: '32px', 
                        color: '#999',
                        fontStyle: 'italic'
                      }}>
                        Aucun compte Google Ads associé
                      </td>
                    </tr>
                  )}
                  
                  {/* Sous-total du site si des comptes existent */}
                  {siteGroup.accounts.length > 0 && (
                    <tr style={{ 
                      backgroundColor: '#f8f9fa', 
                      fontWeight: '600',
                      borderTop: '2px solid #dee2e6'
                    }}>
                      <td style={{ paddingLeft: '16px' }}>
                        Sous-total
                      </td>
                      <td className="numeric">{formatNumber(siteGroup.totals.impressions)}</td>
                      <td className="numeric">{formatNumber(siteGroup.totals.ctr, 2)}%</td>
                      <td className="numeric">{formatNumber(siteGroup.totals.clicks)}</td>
                      <td className="numeric">€{formatNumber(siteGroup.totals.cpc, 2)}</td>
                      <td className="numeric">€{formatNumber(siteGroup.totals.cost, 2)}</td>
                      <td className="numeric">{formatNumber(siteGroup.totals.conversionRate, 2)}%</td>
                      <td className="numeric">{formatNumber(siteGroup.totals.conversions, 1)}</td>
                      <td className="numeric">{siteGroup.totals.cpa >= 0 ? `€${formatNumber(siteGroup.totals.cpa, 2)}` : '-'}</td>
                      <td className="numeric">€{formatNumber(siteGroup.totals.conversionsValue || 0, 2)}</td>
                      <td className={`numeric ${calculateCosClassName(siteGroup.totals.cost, siteGroup.totals.conversionsValue, 'global')}`}>
                        {siteGroup.totals.conversionsValue > 0 && siteGroup.totals.cost > 0 
                          ? `${formatNumber((siteGroup.totals.cost / siteGroup.totals.conversionsValue) * 100, 1)}%`
                          : '-'
                        }
                      </td>
                    </tr>
                  )}
                  
                  {/* Espace entre les sites */}
                  {siteIndex < sortedData.length - 1 && (
                    <tr style={{ height: '16px' }}>
                      <td colSpan="11" style={{ padding: 0, backgroundColor: '#fff' }}></td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
              
              {/* Total global */}
              <tr style={{ 
                backgroundColor: '#f9fafb', 
                fontWeight: '600',
                borderTop: '2px solid #d1d5db'
              }}>
                <td style={{ padding: '12px 16px' }}>
                  TOTAL GÉNÉRAL
                </td>
                <td className="numeric">{formatNumber(totals.impressions)}</td>
                <td className="numeric">
                  {totals.impressions > 0 
                    ? formatNumber((totals.clicks / totals.impressions) * 100, 2) + '%'
                    : '0%'
                  }
                </td>
                <td className="numeric">{formatNumber(totals.clicks)}</td>
                <td className="numeric">
                  {totals.clicks > 0 
                    ? '€' + formatNumber(totals.cost / totals.clicks, 2)
                    : '€0'
                  }
                </td>
                <td className="numeric">€{formatNumber(totals.cost, 2)}</td>
                <td className="numeric">
                  {totals.clicks > 0 
                    ? formatNumber((totals.conversions / totals.clicks) * 100, 2) + '%'
                    : '0%'
                  }
                </td>
                <td className="numeric">{formatNumber(totals.conversions, 1)}</td>
                <td className="numeric">
                  {totals.conversions > 0 
                    ? '€' + formatNumber(totals.cost / totals.conversions, 2)
                    : '-'
                  }
                </td>
                <td className="numeric">€{formatNumber(totals.conversionsValue || 0, 2)}</td>
                <td className={`numeric ${calculateCosClassName(totals.cost, totals.conversionsValue, 'global')}`}>
                  {totals.conversionsValue > 0 && totals.cost > 0 
                    ? `${formatNumber((totals.cost / totals.conversionsValue) * 100, 1)}%`
                    : '-'
                  }
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

// ===== COMPOSANT DASHBOARD GOOGLE ADS =====

function DashboardGoogleAds({ token, setError, selectedPeriod, setSelectedPeriod, customStartDate, setCustomStartDate, customEndDate, setCustomEndDate, selectedDashboardModel, setSelectedDashboardModel, setCurrentView, preselectedAccountId, activeTab, preselectionUsed, setPreselectionUsed, fetchPreviousPeriodData, authError, setAuthError, handleApiError, globalConnectors, canAccessAnalyzer, showAIButton, currentDashboardType, selectedClientId, updateUrlWithAccount, dashboardNotes, fetchDashboardNotes, selectedSiteId, userSites, pmaxCompareEnabled = false, pmaxComparisonType = 'previous_period', gadsComparisonType = '', setGadsComparisonType, gadsCompareEnabled = false, userRole = 'USER' }) {
  
  
  const [selectedSources, setSelectedSources] = useState([]);
  const [localShowAIButton, setLocalShowAIButton] = useState(false);
  const [autoCharts, setAutoCharts] = useState(null);
  const [granularity, setGranularity] = useState('day');
  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  // OPTIMISATION: campaignTypesData supprimé - utiliser campaignsData.data[0].campaigns
  const [devicesData, setDevicesData] = useState(null);
  const [weekdaysData, setWeekdaysData] = useState(null);
  const [loadingWeekdays, setLoadingWeekdays] = useState(false); // État de chargement pour la section Jours de la semaine
  const [campaignsData, setCampaignsData] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [adGroupsData, setAdGroupsData] = useState(null);
  const [topFlopProductsData, setTopFlopProductsData] = useState(null);
  const [geographyData, setGeographyData] = useState(null);
  // Nouveaux states pour preload des composants (optimisation performance)
  const [networksPreloadData, setNetworksPreloadData] = useState(null);
  const [topFlopCategoriesPreloadData, setTopFlopCategoriesPreloadData] = useState(null);
  const [ageRangesPreloadData, setAgeRangesPreloadData] = useState(null);
  const [genderPreloadData, setGenderPreloadData] = useState(null);
  const [audiencesPreloadData, setAudiencesPreloadData] = useState(null);
  const [searchTermsPreloadData, setSearchTermsPreloadData] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [accountHasNoData, setAccountHasNoData] = useState(false); // True quand le pre-check retourne hasData: false
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [allLibraryItems, setAllLibraryItems] = useState([]);

  const [loadingLibraryItems, setLoadingLibraryItems] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  /**
   * Configuration des métriques du dashboard comparatif. Même catalogue et mêmes templates
   * que le dashboard global : `comparatif` ne porte aucun des préfixes reconnus par
   * `getTemplatesForComponent`, il hérite donc des templates Google Ads par défaut.
   */
  const [afficherConfigComparatif, setAfficherConfigComparatif] = useState(false);
  /**
   * Axe de segmentation du comparatif. « Types de campagne » reste le défaut : c'est la vue
   * historique de cet écran, et la seule dont les sections sont écrites en dur plus bas.
   */
  const [segmentationComparatif, setSegmentationComparatif] = useState('type');
  const metriquesComparatif = useComponentMetrics('comparatif', selectedAccount?.id || 'global');
  const selectedMetrics = useSelectedMetrics(selectedAccount?.id || 'global');
  const [showCustomDates, setShowCustomDates] = useState(false);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // Ref pour garder la valeur la plus récente de libraryItems (pour les event handlers)
  const libraryItemsRef = React.useRef(libraryItems);

  // Comptes absents des réponses. Les routes poursuivent leur boucle quand un compte
  // échoue et répondent quand même « success: true » : sans cette agrégation, des
  // totaux amputés s'afficheraient exactement comme des totaux complets.
  const resolveAccountName = React.useCallback((sourceId) => {
    const clean = String(sourceId).replace(/-/g, '');
    const item = libraryItems.find(
      (i) => String(i.sourceId || i.id).replace(/-/g, '') === clean
    );
    return item?.name || sourceId;
  }, [libraryItems]);

  React.useEffect(() => {
    libraryItemsRef.current = libraryItems;
  }, [libraryItems]);

  // Reset accountHasNoData quand la période change (pour tous les dashboards)
  React.useEffect(() => {
    setAccountHasNoData(false);
  }, [selectedPeriod, customStartDate, customEndDate]);

  // État pour les données de la période précédente et comparaison
  const [previousPeriodData, setPreviousPeriodData] = useState(null);
  const [loadingPreviousPeriod, setLoadingPreviousPeriod] = useState(false);

  // Changer de période de comparaison recharge autant de données qu'un changement de
  // période : on affiche le même écran de chargement plutôt qu'un dashboard figé sur
  // les anciennes variations. Indicateur dérivé plutôt que de forcer loadingCharts,
  // qui entrerait en course avec le chargement principal.
  const gadsLoading = loadingCharts || loadingPreviousPeriod;

  // Ces modèles ont leur propre écran de chargement : l'écran plein ne les couvre pas,
  // et le contenu ne doit donc pas être masqué pour eux.
  const GADS_MODELS_WITH_OWN_LOADER = ['performance-max', 'competition', 'landing-pages', 'products-insights', 'shopping', 'search-terms'];
  const gadsFullScreenLoading = gadsLoading && !GADS_MODELS_WITH_OWN_LOADER.includes(selectedDashboardModel);
  // Note: gadsComparisonType et gadsCompareEnabled sont définis au niveau App pour être accessibles dans la sidebar
  const [showAssetGroups, setShowAssetGroups] = useState(() => isModuleVisible('asset_groups', 'global'));
  const [geoSortConfig, setGeoSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // États pour contrôler la visibilité des sections (persisté via localStorage)
  const [showResults, setShowResults] = useState(() => isModuleVisible('results', 'global'));
  const [showCampaignTypes, setShowCampaignTypes] = useState(() => isModuleVisible('campaign_types', 'global'));
  const [showTopFlopSearchTerms, setShowTopFlopSearchTerms] = useState(() => isModuleVisible('topflop_terms', 'global'));
  const [showCampaignDetails, setShowCampaignDetails] = useState(() => isModuleVisible('campaigns', 'global'));
  const [showAdGroupDetails, setShowAdGroupDetails] = useState(() => isModuleVisible('ad_groups', 'global'));
  const [showDeviceConversions, setShowDeviceConversions] = useState(() => isModuleVisible('devices', 'global'));

  // Calculer le clientId effectif à partir du compte Google Ads sélectionné
  // Fallback: utiliser l'ID du compte Google Ads préfixé si pas de site associé
  const effectiveClientId = React.useMemo(() => {
    const result = (() => {
      if (selectedClientId) return selectedClientId;
      if (!selectedAccount) return null;
      // Essayer plusieurs formats d'ID pour matcher avec accountIds
      const accountId = selectedAccount.id || selectedAccount.sourceId;
      const accountIdClean = accountId?.replace(/-/g, '');
      const accountSite = userSites.find(s =>
        s.accountIds?.includes(accountId) ||
        s.accountIds?.includes(accountIdClean) ||
        s.accountIds?.includes(selectedAccount.sourceId) ||
        s.accountIds?.some(aid => aid.includes(accountId) || accountId?.includes(aid))
      );
      // Fallback: utiliser l'ID du compte Google Ads si pas de site associé
      return accountSite?.clientId || (accountId ? `gads_${accountIdClean || accountId}` : null);
    })();
    return result;
  }, [selectedClientId, selectedAccount, userSites]);

  // Hook pour la visibilité des modules par client (persistée en base de données)
  const {
    isModuleVisible: isClientModuleVisible,
    toggleModuleVisibility: toggleClientModuleVisibility,
    setModuleVisibility: setClientModuleVisibility,
    hiddenModules: clientHiddenModules,
    loading: visibilityLoading
  } = useClientModuleVisibility(effectiveClientId, userRole);

  // State pour le modal des modules masqués (Google Ads)
  const [isHiddenModulesModalOpen, setIsHiddenModulesModalOpen] = useState(false);

  // Fonctions de toggle qui sauvegardent aussi dans localStorage
  const toggleResults = useCallback(() => {
    const newValue = !showResults;
    setShowResults(newValue);
    setModuleVisibility('results', newValue, 'global');
  }, [showResults]);

  const toggleCampaignTypes = useCallback(() => {
    const newValue = !showCampaignTypes;
    setShowCampaignTypes(newValue);
    setModuleVisibility('campaign_types', newValue, 'global');
  }, [showCampaignTypes]);

  const toggleTopFlopSearchTerms = useCallback(() => {
    const newValue = !showTopFlopSearchTerms;
    setShowTopFlopSearchTerms(newValue);
    setModuleVisibility('topflop_terms', newValue, 'global');
  }, [showTopFlopSearchTerms]);

  const toggleCampaignDetails = useCallback(() => {
    const newValue = !showCampaignDetails;
    setShowCampaignDetails(newValue);
    setModuleVisibility('campaigns', newValue, 'global');
  }, [showCampaignDetails]);

  const toggleAdGroupDetails = useCallback(() => {
    const newValue = !showAdGroupDetails;
    setShowAdGroupDetails(newValue);
    setModuleVisibility('ad_groups', newValue, 'global');
  }, [showAdGroupDetails]);

  const toggleDeviceConversions = useCallback(() => {
    const newValue = !showDeviceConversions;
    setShowDeviceConversions(newValue);
    setModuleVisibility('devices', newValue, 'global');
  }, [showDeviceConversions]);

  const toggleAssetGroups = useCallback(() => {
    const newValue = !showAssetGroups;
    setShowAssetGroups(newValue);
    setModuleVisibility('asset_groups', newValue, 'global');
  }, [showAssetGroups]);

  // useEffect pour synchroniser les états de visibilité avec localStorage
  // (quand on restaure un module depuis HiddenModulesModal)
  useEffect(() => {
    const handleVisibilityChange = () => {
      setShowResults(isModuleVisible('results', 'global'));
      setShowCampaignTypes(isModuleVisible('campaign_types', 'global'));
      setShowTopFlopSearchTerms(isModuleVisible('topflop_terms', 'global'));
      setShowCampaignDetails(isModuleVisible('campaigns', 'global'));
      setShowAdGroupDetails(isModuleVisible('ad_groups', 'global'));
      setShowDeviceConversions(isModuleVisible('devices', 'global'));
      setShowAssetGroups(isModuleVisible('asset_groups', 'global'));
    };

    window.addEventListener('moduleVisibilityChanged', handleVisibilityChange);
    return () => {
      window.removeEventListener('moduleVisibilityChanged', handleVisibilityChange);
    };
  }, []);

  // Configuration des métriques pour le composant Types de campagnes
  const campaignTypesMetrics = useComponentMetrics('campaign_types', selectedAccount?.id || 'global');

  // Configuration des métriques pour le composant Shopping Products
  const shoppingProductsMetrics = useComponentMetrics('shopping_products', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est visible dans le tableau Shopping
  const isShoppingMetricVisible = useCallback((metricKey) => {
    return shoppingProductsMetrics.metrics.includes(metricKey);
  }, [shoppingProductsMetrics.metrics]);

  // Configuration des métriques pour le dashboard Performance Max
  const pmaxMetrics = useComponentMetrics('pmax', selectedAccount?.id || 'global');

  // Helper pour vérifier si une métrique est visible dans les tableaux Performance Max
  const isPmaxMetricVisible = useCallback((metricKey) => {
    return pmaxMetrics.metrics.includes(metricKey);
  }, [pmaxMetrics.metrics]);


  // État pour le filtre par campagne
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState(null);
  const [gaCampaignSearchQuery, setGaCampaignSearchQuery] = useState('');
  const [showGaCampaignAutocomplete, setShowGaCampaignAutocomplete] = useState(false);

  // État pour le filtre par labels (campagnes). La **sélection** reste locale au dashboard ;
  // les données viennent du hook partagé (voir plus bas).
  const [selectedLabelsFilter, setSelectedLabelsFilter] = useState([]);
  const [showLabelsDropdown, setShowLabelsDropdown] = useState(false);

  // État pour le filtre par type de campagne
  const [selectedCampaignTypeFilter, setSelectedCampaignTypeFilter] = useState(null);
  
  // Réinitialiser les données quand l'onglet change
  React.useEffect(() => {
    if (activeTab === 'google') {
      setAutoCharts(null);
      setResultsData(null);
      setDevicesData(null);
      setWeekdaysData(null);
      setCampaignsData(null);
      setAdGroupsData(null);
      setTopFlopProductsData(null);
      setGeographyData(null);
      setLoadingResults(false);
      setLoadingCampaigns(false);
      setLoadingCharts(false);
    }
  }, [activeTab]);

  // Ref pour tracker le type de comparaison précédent (éviter le double chargement)
  const prevComparisonTypeRef = useRef(gadsComparisonType);
  const prevPeriodRef = useRef(selectedPeriod);
  const prevDatesRef = useRef({ start: customStartDate, end: customEndDate });
  // Signature du dernier chargement complet réellement effectué. Nécessaire parce
  // que generateAutoDashboard écarte ses données lightweight dès que la comparaison
  // est active : sans cette trace, aucun chargement complet n'avait lieu au montage
  // (comparaison restaurée depuis l'URL) ni au changement de compte.
  const loadedComparisonKeyRef = useRef(null);

  // Recharger les données de comparaison SEULEMENT quand le type de comparaison ou la période change
  // Note: Le chargement initial est déjà fait par generateAutoDashboard en parallèle
  useEffect(() => {
    // Vérifier si c'est un changement de type de comparaison ou de période (pas un changement de compte)
    const typeChanged = prevComparisonTypeRef.current !== gadsComparisonType;
    const periodChanged = prevPeriodRef.current !== selectedPeriod;
    const datesChanged = prevDatesRef.current.start !== customStartDate || prevDatesRef.current.end !== customEndDate;

    // Mettre à jour les refs
    prevComparisonTypeRef.current = gadsComparisonType;
    prevPeriodRef.current = selectedPeriod;
    prevDatesRef.current = { start: customStartDate, end: customEndDate };

    // Quand aucune comparaison n'est active, generateAutoDashboard charge lui-même
    // les totaux lightweight au changement de compte : inutile de doubler.
    // Quand elle est active, il les écarte au contraire (elles n'ont ni campagnes ni
    // segments) et compte sur cet effet — qui doit donc charger aussi au montage et
    // au changement de compte.
    const accountKey = selectedAccount ? (selectedAccount.sourceId || selectedAccount.id) : null;
    const comparisonKey = [accountKey, gadsComparisonType, selectedPeriod, customStartDate, customEndDate].join('|');
    const needsFullLoad = gadsCompareEnabled && accountKey && loadedComparisonKeyRef.current !== comparisonKey;

    if (!typeChanged && !periodChanged && !datesChanged && !needsFullLoad) {
      return;
    }
    if (gadsCompareEnabled && accountKey) {
      loadedComparisonKeyRef.current = comparisonKey;
    }

    const loadComparisonData = async () => {
      if (!selectedAccount) {
        setPreviousPeriodData(null);
        setLoadingPreviousPeriod(false);
        return;
      }

      // Utiliser 'previous_period' par défaut si aucun type n'est sélectionné
      const comparisonTypeToUse = gadsComparisonType || 'previous_period';

      // Mode lightweight (totaux uniquement) si aucune comparaison sélectionnée
      // Mode complet (campagnes, devices, etc.) si comparaison explicitement sélectionnée
      const useLightweight = !gadsComparisonType;

      console.log('🔄 [COMPARISON] Rechargement données comparaison (type/période changé):', comparisonTypeToUse, useLightweight ? '(lightweight)' : '(complet)');

      // Si mode complet (comparaison sélectionnée), afficher loading et effacer les données
      if (!useLightweight) {
        setLoadingPreviousPeriod(true);
        setPreviousPeriodData(null); // Effacer pour déclencher l'état loading des composants
      }

      try {
        const data = await fetchPreviousPeriodData(
          selectedAccount.sourceId || selectedAccount.id,
          comparisonTypeToUse,
          useLightweight
        );
        // Le backend renvoie success:true avec des totaux à zéro et hasData:false
        // quand l'API échoue. La réponse nominale n'expose pas ce champ : on ne rejette
        // donc que le false explicite, jamais l'absence.
        if (data && data.hasData !== false) {
          console.log('✅ [COMPARISON] Données comparaison reçues:', data);
          setPreviousPeriodData(data);
        } else {
          console.log('⚠️ [COMPARISON] Période précédente sans données exploitables');
          setPreviousPeriodData(null);
        }
      } catch (error) {
        console.error('❌ [COMPARISON] Erreur chargement:', error);
        setPreviousPeriodData(null);
      } finally {
        setLoadingPreviousPeriod(false);
      }
    };

    loadComparisonData();
  }, [selectedAccount, gadsComparisonType, selectedPeriod, customStartDate, customEndDate]);

  /**
   * Les labels des campagnes du compte, par le hook partagé.
   *
   * Ce bloc était un `fetch` maison, recopié presque à l'identique dans les dashboards Meta et
   * Microsoft. Le passer au hook n'est pas qu'un rangement : le panneau d'édition, qui vit dans
   * l'en-tête du module Campagnes, prévient les autres instances par un `CustomEvent` après
   * chaque écriture. C'est ce qui fait apparaître le filtre « Labels » aussitôt qu'on pose le
   * premier label — jusque-là il fallait recharger la page, et le dropdown restait masqué
   * puisqu'il n'y avait aucun label à proposer.
   */
  const {
    labelsParCampagne: campaignLabelsMap,
    labelsDisponibles: availableCampaignLabels
  } = useCampagnesEtiquetees({
    accountId: selectedAccount?.sourceId || selectedAccount?.id,
    accountType: 'GOOGLE_ADS',
    token
  });

  // Charger les notes du dashboard quand le compte ou les dates changent
  useEffect(() => {
    if (!selectedAccount || !fetchDashboardNotes) return;
    const accountId = selectedAccount?.sourceId || selectedAccount?.id;
    fetchDashboardNotes('GOOGLE_ADS', accountId, customStartDate, customEndDate);
  }, [selectedAccount, customStartDate, customEndDate, fetchDashboardNotes]);

  // Fonction utilitaire pour vérifier si une campagne correspond aux labels sélectionnés
  const campaignMatchesLabelsFilter = (campaignId) => {
    if (selectedLabelsFilter.length === 0) return true;
    const campaignLabels = campaignLabelsMap[String(campaignId)] || [];
    // Filtre "OU" : la campagne doit avoir AU MOINS UN des labels sélectionnés
    return selectedLabelsFilter.some(label => campaignLabels.includes(label));
  };

  // Calculer les IDs des campagnes filtrées par labels (pour CampaignsTable)
  const labelFilteredCampaignIds = React.useMemo(() => {
    if (selectedLabelsFilter.length === 0) return null;
    const matchingIds = Object.entries(campaignLabelsMap)
      .filter(([campaignId, labels]) =>
        selectedLabelsFilter.some(selectedLabel => labels.includes(selectedLabel))
      )
      .map(([campaignId]) => campaignId);
    return matchingIds.length > 0 ? matchingIds : [];
  }, [selectedLabelsFilter, campaignLabelsMap]);

  /**
   * Les campagnes que les filtres laissent passer, en identifiants.
   *
   * ## Pourquoi passer par des identifiants
   *
   * Les écrans qui lisent leurs propres données — Origine géographique, Audiences, Top & Flop
   * Produits — n'ont pas la liste des campagnes sous la main : ils interrogent une route qui
   * agrège côté Google Ads. Ils envoyaient bien `campaignTypeFilter`, et la route le recevait,
   * le journalisait, et annonçait même `filteredByCampaignType: true` dans sa réponse — sans
   * jamais le transmettre au service, qui ne sait pas le lire. Le filtre était donc accepté,
   * proclamé, et sans effet.
   *
   * Le canal `campaignIds`, lui, fonctionne de bout en bout. Le tableau des campagnes connaît le
   * type de chacune : traduire le filtre en liste d'identifiants ici le fait passer partout, par
   * un chemin déjà éprouvé.
   *
   * ## Une intersection, pas une somme
   *
   * Labels et type se cumulent : une campagne doit satisfaire les deux pour rester. `null`
   * signifie « aucun filtre par identifiant », et se distingue d'un tableau vide, qui signifie
   * « aucune campagne ne convient ».
   */
  const idsCampagnesFiltrees = React.useMemo(() => {
    if (!selectedCampaignTypeFilter) return labelFilteredCampaignIds;

    const campagnes = campaignsData?.data?.[0]?.campaigns || [];
    // Tant que les campagnes ne sont pas chargées, on ne sait pas filtrer : mieux vaut laisser
    // passer que de rendre une liste vide, qui viderait les écrans le temps du chargement.
    if (campagnes.length === 0) return labelFilteredCampaignIds;

    // Même comparaison que les tableaux qui filtrent déjà par type côté navigateur : le code
    // numérique et le libellé sont acceptés, Google Ads employant les deux selon les routes.
    const duType = campagnes
      .filter(campagne => {
        const type = campagne.advertisingChannelType ?? campagne.type;
        return type === selectedCampaignTypeFilter.type ||
               String(type) === String(selectedCampaignTypeFilter.type) ||
               type === selectedCampaignTypeFilter.name ||
               String(type) === String(selectedCampaignTypeFilter.name);
      })
      .map(campagne => String(campagne.id || campagne.campaignId));

    if (!labelFilteredCampaignIds) return duType;

    const autorises = new Set(labelFilteredCampaignIds.map(String));
    return duType.filter(id => autorises.has(id));
  }, [selectedCampaignTypeFilter, labelFilteredCampaignIds, campaignsData]);

  /**
   * Restreint une liste de campagnes à ce que les filtres laissent passer.
   *
   * Sert aux totaux calculés dans le navigateur, et non à une route : le tableau des audiences
   * affiche « Avec audiences / Sans audiences », la seconde ligne étant la différence entre le
   * total du compte et celui des audiences. Le total venait de toutes les campagnes, quelle que
   * soit la sélection — « Sans audiences » annonçait donc le trafic du compte entier sous un
   * en-tête filtré, et pouvait même dépasser le total affiché au-dessus.
   *
   * `id` ou `campaignId` selon la provenance : les deux formes circulent, et la période
   * précédente n'emploie pas toujours la même que la période courante.
   */
  const restreindreAuxCampagnesFiltrees = React.useCallback((campagnes) => {
    if (!Array.isArray(campagnes)) return [];
    if (!idsCampagnesFiltrees) return campagnes;

    const autorises = new Set(idsCampagnesFiltrees.map(String));
    return campagnes.filter(campagne =>
      autorises.has(String(campagne.id)) || autorises.has(String(campagne.campaignId))
    );
  }, [idsCampagnesFiltrees]);

  // Réinitialiser les filtres quand on change de compte ou de modèle de dashboard
  // Note: les filtres persistent lors du changement de période pour permettre la comparaison
  useEffect(() => {
    if (selectedCampaignFilter) {
      console.log('🔄 [CAMPAIGN-FILTER] Réinitialisation du filtre de campagne lors du changement de compte/modèle');
      setSelectedCampaignFilter(null);
    }
    if (selectedLabelsFilter.length > 0) {
      console.log('🔄 [LABELS-FILTER] Réinitialisation du filtre de labels lors du changement de compte/modèle');
      setSelectedLabelsFilter([]);
    }
    if (selectedQualityScoreFilter !== null) {
      console.log('🔄 [QUALITY-FILTER] Réinitialisation du filtre score de qualité lors du changement de compte/modèle');
      setSelectedQualityScoreFilter(null);
    }
    if (selectedMatchTypeFilter !== null) {
      console.log('🔄 [MATCH-TYPE-FILTER] Réinitialisation du filtre type de correspondance lors du changement de compte/modèle');
      setSelectedMatchTypeFilter(null);
    }
    if (selectedCpcTierFilter) {
      console.log('🔄 [CPC-TIER-FILTER] Réinitialisation du filtre tranche de CPC lors du changement de compte/modèle');
      setSelectedCpcTierFilter(null);
    }
    if (selectedCampaignTypeFilter) {
      console.log('🔄 [CAMPAIGN-TYPE-FILTER] Réinitialisation du filtre type de campagne lors du changement de compte/modèle');
      setSelectedCampaignTypeFilter(null);
    }
    // Réinitialiser les filtres Performance Max
    if (pmaxCampaignFilter) {
      console.log('🔄 [PMAX-CAMPAIGN-FILTER] Réinitialisation du filtre campagne PMAX lors du changement de compte/modèle');
      setPmaxCampaignFilter(null);
    }
    if (pmaxLabelsFilter.length > 0) {
      console.log('🔄 [PMAX-LABELS-FILTER] Réinitialisation du filtre labels PMAX lors du changement de compte/modèle');
      setPmaxLabelsFilter([]);
    }
    if (pmaxCampaignSearchQuery) {
      setPmaxCampaignSearchQuery('');
    }
    setShowPmaxCampaignAutocomplete(false);
    setShowPmaxLabelsDropdown(false);

    // Exposer les fonctions pour la suppression des filtres
    window.setSelectedCampaignFilter = setSelectedCampaignFilter;
    window.setSelectedQualityScoreFilter = setSelectedQualityScoreFilter;
    window.setSelectedMatchTypeFilter = setSelectedMatchTypeFilter;
    window.setSelectedCpcTierFilter = setSelectedCpcTierFilter;
  }, [selectedAccount, selectedDashboardModel]);
  const [showWeekdayConversions, setShowWeekdayConversions] = useState(() => isModuleVisible('weekdays', 'global'));
  const [showNetworks, setShowNetworks] = useState(() => isModuleVisible('networks', 'global'));
  const [showGeoConversions, setShowGeoConversions] = useState(true);
  // const [showLandingPages, setShowLandingPages] = useState(true); // Non utilisé dans dashboard global
  const [showSearchTerms, setShowSearchTerms] = useState(true);
  const [showKeywords, setShowKeywords] = useState(true);
  const [showDetailedQualityScore, setShowDetailedQualityScore] = useState(true);
  const [showQualityScoreBreakdown, setShowQualityScoreBreakdown] = useState(true);
  const [selectedQualityScoreFilter, setSelectedQualityScoreFilter] = useState(null);
  const [showMatchTypeBreakdown, setShowMatchTypeBreakdown] = useState(true);
  const [selectedMatchTypeFilter, setSelectedMatchTypeFilter] = useState(null);
  const [showCpcTierBreakdown, setShowCpcTierBreakdown] = useState(true);
  const [showSearchTermsCpcTierBreakdown, setShowSearchTermsCpcTierBreakdown] = useState(true);
  const [selectedCpcTierFilter, setSelectedCpcTierFilter] = useState(null);

  // Fonctions de toggle pour les modules supplémentaires
  const toggleWeekdayConversions = useCallback(() => {
    const newValue = !showWeekdayConversions;
    setShowWeekdayConversions(newValue);
    setModuleVisibility('weekdays', newValue, 'global');
  }, [showWeekdayConversions]);

  const toggleNetworks = useCallback(() => {
    const newValue = !showNetworks;
    setShowNetworks(newValue);
    setModuleVisibility('networks', newValue, 'global');
  }, [showNetworks]);

  // useEffect pour synchroniser weekdays et networks avec localStorage
  useEffect(() => {
    const handleVisibilityChange = () => {
      setShowWeekdayConversions(isModuleVisible('weekdays', 'global'));
      setShowNetworks(isModuleVisible('networks', 'global'));
    };

    window.addEventListener('moduleVisibilityChanged', handleVisibilityChange);
    return () => {
      window.removeEventListener('moduleVisibilityChanged', handleVisibilityChange);
    };
  }, []);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showAiModal, setShowAiModal] = useState(false);
  /**
   * Le panneau d'analyse, et la charge qu'il doit envoyer.
   *
   * La charge est figée au moment du clic plutôt que relue au rendu : le dashboard continue de
   * vivre pendant l'analyse — on peut changer de période, régénérer — et l'analyse doit porter
   * sur ce qui était à l'écran quand on l'a demandée.
   */
  const [panneauAnalyseOuvert, setPanneauAnalyseOuvert] = useState(false);

  /*
   * La barre latérale se replie pendant l'analyse, et revient comme elle était.
   *
   * Le panneau prend un tiers de l'écran ; sans cela, le dashboard se retrouve pris en étau
   * entre deux colonnes et ses graphiques deviennent illisibles. `App` expose son setter comme
   * il le fait déjà pour le bouton d'analyse — c'est le canal en place entre ces deux composants.
   *
   * Rien à mémoriser ici : le drapeau est distinct de la préférence de l'utilisateur, qui
   * reprend d'elle-même à la fermeture (voir `analyseOuverte` dans `App`).
   */
  useEffect(() => {
    const signaler = window.setAnalyseOuverte;
    if (!signaler) return undefined;

    signaler(panneauAnalyseOuvert);
    // Un démontage en panneau ouvert laisserait la barre repliée et le bouton caché.
    return () => signaler(false);
  }, [panneauAnalyseOuvert]);

  /*
   * Rendre au dashboard la largeur que le panneau prend.
   *
   * Le panneau est posé en `position: fixed` dans `body`, par un portail : il est donc hors du
   * flux et recouvrirait le tableau sans que rien ne s'en aperçoive. Le CSS rétrécit `.App` d'un
   * tiers tant que la classe est là, en-tête et barre latérale compris — le panneau s'ouvre
   * ainsi *à côté* du dashboard, et non par-dessus.
   *
   * La classe vit sur `body` et non dans un état React parce que le panneau et la coquille de
   * l'application n'ont aucun ancêtre commun qui ne soit `body` lui-même.
   */
  useEffect(() => {
    document.body.classList.toggle('panneau-analyse-ouvert', panneauAnalyseOuvert);

    /*
     * Le panneau s'ouvre sous l'en-tête, pas par-dessus.
     *
     * La hauteur n'est pas constante : un bandeau de connecteurs expirés ajoute 48 px, et la
     * barre de navigation se replie sur deux lignes en dessous de 1 024 px. On la mesure donc,
     * plutôt que d'inscrire un nombre qui serait faux une fois sur trois.
     *
     * La valeur voyage par une variable CSS : c'est la feuille de style qui décide quoi en
     * faire — décalage du haut ici, hauteur du panneau juste après.
     */
    const mesurer = () => {
      /*
       * On mesure le **haut de la zone de contenu**, pas le bas d'un en-tête.
       *
       * `.App-header` porte mal son nom : il enveloppe aussi `.app-layout-with-sidebar`, donc
       * toute la page — son bas est à 942 px, pas à 106. Le vrai en-tête est `.app-header`, et
       * c'est de toute façon le haut du contenu qui nous intéresse : c'est lui que le panneau
       * doit longer, et c'est le même élément que le CSS rétrécit.
       */
      const contenu = document.querySelector('.app-layout-with-sidebar');
      const entete = document.querySelector('.app-header');
      // Rien n'est ajouté pour le défilement : le panneau est en `position: fixed`, donc ancré
      // au cadre de la fenêtre et non au document.
      const haut = contenu
        ? contenu.getBoundingClientRect().top
        : (entete ? entete.getBoundingClientRect().bottom : 0);
      document.documentElement.style.setProperty('--haut-panneau-analyse', `${Math.max(0, Math.round(haut))}px`);

      // Le bandeau du panneau s'aligne sur la hauteur de l'en-tête. Mesurée elle aussi : la
      // barre de navigation se replie sur deux lignes sous 1 024 px, et un nombre écrit en dur
      // serait faux la moitié du temps.
      if (entete) {
        const hauteur = Math.round(entete.getBoundingClientRect().height);
        document.documentElement.style.setProperty('--hauteur-entete-app', `${hauteur}px`);
      }
    };

    if (!panneauAnalyseOuvert) {
      document.documentElement.style.removeProperty('--haut-panneau-analyse');
      document.documentElement.style.removeProperty('--hauteur-entete-app');
      return () => document.body.classList.remove('panneau-analyse-ouvert');
    }

    mesurer();
    window.addEventListener('resize', mesurer);

    return () => {
      // Un démontage en panneau ouvert — changement d'écran, déconnexion — laisserait
      // l'application rétrécie pour rien.
      document.body.classList.remove('panneau-analyse-ouvert');
      document.documentElement.style.removeProperty('--haut-panneau-analyse');
      document.documentElement.style.removeProperty('--hauteur-entete-app');
      window.removeEventListener('resize', mesurer);
    };
  }, [panneauAnalyseOuvert]);
  const [userBrief, setUserBrief] = useState('');
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');

  // Fonction pour exporter le dashboard en PDF (HTML2PDF)
  const handleExportPDF = async () => {
    try {
      console.log('🔄 Export PDF - Utilisation de html2pdf.js');
      
      // Configuration pour html2pdf
      const opt = {
        margin: 10,
        filename: `Dashboard_GoogleAds_${selectedAccount?.name || 'Export'}_${new Date().toLocaleDateString('fr-FR').replace(/\//g, '-')}.pdf`,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: { 
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff'
        },
        jsPDF: { 
          unit: 'mm', 
          format: 'a4', 
          orientation: 'landscape'
        },
        pagebreak: { mode: ['css', 'legacy'] }
      };

      // Composants à exporter - sélecteurs corrigés
      const componentsToExport = [
        { selector: '.results-section-container .performance-chart-section', title: 'Section Résultats' },
        { selector: '.campaign-types-container', title: 'Types de Campagnes' },
        { selector: '.campaigns-table-container', title: 'Tableau des Campagnes' },
        { selector: '.ad-groups-table-container', title: 'Tableau des Groupes d\'Annonces' },
        { selector: '.devices-data-container', title: 'Données par Appareil' },
        { selector: '.weekdays-data-container', title: 'Données par Jour de la Semaine' },
        { selector: '.top-flop-products-container', title: '📊 Top & Flop Produits' },
        { selector: '.geography-data-container', title: '🌍 Origine géographique' },
        { selector: '.age-ranges-data-container', title: '👥 Tranches d\'âge' },
        { selector: '.gender-data-container', title: '👫 Genre' }
      ];

      // Créer un conteneur temporaire pour organiser le contenu
      const tempContainer = document.createElement('div');
      tempContainer.style.width = '100%';
      tempContainer.style.backgroundColor = 'white';
      tempContainer.style.padding = '20px';
      
      // Page de garde
      const coverPage = document.createElement('div');
      coverPage.style.pageBreakAfter = 'always';
      coverPage.style.textAlign = 'center';
      coverPage.style.padding = '60px 40px';
      coverPage.innerHTML = `
        <h1 style="font-size: 32px; color: #1f2937; margin-bottom: 20px;">Dashboard Google Ads - Export PDF</h1>
        <h2 style="font-size: 18px; color: #6b7280; margin-bottom: 10px;">Compte: ${selectedAccount?.name || 'Non défini'}</h2>
        <h3 style="font-size: 16px; color: #6b7280; margin-bottom: 10px;">
          Période: ${selectedPeriod === 'CUSTOM' 
            ? `${customStartDate} au ${customEndDate}`
            : selectedPeriod.replace('LAST_', '').replace('_DAYS', ' jours')}
        </h3>
        <p style="font-size: 14px; color: #9ca3af;">Export généré le: ${new Date().toLocaleString('fr-FR')}</p>
      `;
      tempContainer.appendChild(coverPage);

      // Exporter chaque composant sur une page séparée
      let pageCount = 1;
      for (const component of componentsToExport) {
        const element = document.querySelector(component.selector);
        
        // Vérifier si l'élément existe, a du contenu visible et des données utiles
        const hasVisibleContent = element && 
                                  element.offsetHeight > 0 && 
                                  element.textContent.trim().length > 50 && // Au moins 50 caractères de contenu
                                  !element.textContent.includes('Aucune donnée disponible') &&
                                  !element.textContent.includes('Pas de données') &&
                                  !element.textContent.includes('0 lignes') &&
                                  element.querySelector('table, .chart-preview, .recharts-wrapper, canvas');
        
        if (hasVisibleContent) {
          pageCount++;
          console.log(`📄 Préparation: ${component.title}`);
          
          // Créer une page pour ce composant avec seulement le titre et le contenu isolé
          const componentPage = document.createElement('div');
          componentPage.style.pageBreakBefore = 'always';
          componentPage.style.pageBreakAfter = 'always';
          componentPage.style.padding = '20px';
          componentPage.style.backgroundColor = 'white';
          componentPage.style.minHeight = '200mm'; // Hauteur minimale pour une page A4 paysage
          
          // Titre
          const title = document.createElement('h2');
          title.style.fontSize = '24px';
          title.style.color = '#1f2937';
          title.style.marginBottom = '20px';
          title.style.paddingBottom = '10px';
          title.style.borderBottom = '2px solid #e5e7eb';
          title.textContent = component.title;
          componentPage.appendChild(title);
          
          // Contenu isolé : cloner seulement l'élément spécifique
          const contentWrapper = document.createElement('div');
          contentWrapper.style.width = '100%';
          contentWrapper.style.overflow = 'hidden';
          
          // Cloner l'élément et nettoyer pour éviter la duplication
          const clonedElement = element.cloneNode(true);
          
          // Supprimer les éléments de navigation/filtre du clone pour éviter la duplication
          const filtersToRemove = clonedElement.querySelectorAll([
            '[style*="sticky"]',
            '.campaign-filter',
            '.filter-banner',
            '.sticky'
          ].join(','));
          filtersToRemove.forEach(filter => filter.remove());
          
          clonedElement.style.width = '100%';
          clonedElement.style.maxWidth = '100%';
          clonedElement.style.position = 'relative';
          clonedElement.style.top = 'auto';
          clonedElement.style.left = 'auto';
          
          contentWrapper.appendChild(clonedElement);
          componentPage.appendChild(contentWrapper);
          
          // Numéro de page
          const pageNumber = document.createElement('div');
          pageNumber.style.textAlign = 'right';
          pageNumber.style.fontSize = '12px';
          pageNumber.style.color = '#9ca3af';
          pageNumber.style.marginTop = '20px';
          pageNumber.textContent = `Page ${pageCount}`;
          componentPage.appendChild(pageNumber);
          
          tempContainer.appendChild(componentPage);
        } else {
          console.log(`⚠️ Élément ignoré (vide): ${component.title}`);
        }
      }

      // Ajouter le conteneur temporaire au body
      document.body.appendChild(tempContainer);
      
      // Générer le PDF
      await html2pdf().set(opt).from(tempContainer).save();
      
      // Nettoyer le conteneur temporaire
      document.body.removeChild(tempContainer);
      
      console.log('✅ Export PDF terminé avec succès');
      
    } catch (error) {
      console.error('❌ Erreur lors de l\'export PDF:', error);
      setError('Erreur lors de l\'export PDF: ' + error.message);
    }
  };

  // OBSOLÈTE - Fonction pour charger les tops et flops produits
  // Last update: 2025-09-26 22:45:00 - Force production refresh for campaign filtering
  // REMPLACÉ PAR: TopFlopProductsTableModular qui gère son propre chargement
  /*
  const loadTopFlopProducts = async (campaignFilter = null) => {
    if (!selectedAccount) {
      console.log('⚠️ [TOP-FLOP-PRODUCTS] Pas de compte sélectionné');
      return;
    }
    
    console.log('🚀 [TOP-FLOP-PRODUCTS] Début chargement pour:', selectedAccount ? '1 compte' : '0 compte');
    console.log('🚀 [TOP-FLOP-PRODUCTS] Filtre campagne:', campaignFilter);
    setLoadingTopFlopProducts(true);
    try {
      const requestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod,
        limit: 10 // 10 tops et 10 flops
      };

      // Ajouter le filtre par campagne si présent
      if (campaignFilter) {
        requestBody.campaignFilter = {
          id: campaignFilter.id,
          name: campaignFilter.name
        };
        console.log('📊 [TOP-FLOP-PRODUCTS] Filtrage par campagne:', requestBody.campaignFilter);
      }

      if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      console.log('📊 [TOP-FLOP-PRODUCTS] Requête:', requestBody);

      const response = await axios.post(`${API_URL}/api/dashboard/top-flop-products`, requestBody, {
        headers: { Authorization: `Bearer ${token}` }
      });

      console.log('📊 [TOP-FLOP-PRODUCTS] Réponse reçue:', response.data);

      if (response.data.success) {
        setTopProducts(response.data.data.topProducts || []);
        setFlopProducts(response.data.data.flopProducts || []);
        console.log('✅ Tops/Flops produits chargés:', {
          tops: response.data.data.topProducts?.length || 0,
          flops: response.data.data.flopProducts?.length || 0,
          topProductsData: response.data.data.topProducts,
          flopProductsData: response.data.data.flopProducts
        });
      } else {
        console.error('❌ API retourne success=false:', response.data);
      }
    } catch (error) {
      console.error('❌ Erreur chargement tops/flops produits:', error);
      console.error('❌ Détails erreur:', error.response?.data || error.message);
      setError('Erreur lors du chargement des tops/flops produits');
    } finally {
      setLoadingTopFlopProducts(false);
    }
  };
  */
  
  // États pour le dashboard comparatif
  const [comparativeData, setComparativeData] = useState(null);
  const [loadingComparative, setLoadingComparative] = useState(false);
  const [comparisonType, setComparisonType] = useState('previous_year_to_date'); // 'previous_year_to_date', 'previous_year' ou 'previous_period'

  // États pour le dashboard Performance Max
  const [performanceMaxData, setPerformanceMaxData] = useState(null);
  const [loadingPerformanceMax, setLoadingPerformanceMax] = useState(false);
  
  // État pour la vue en pourcentage de la répartition par campagnes
  const [showCampaignBreakdownAsPercentage, setShowCampaignBreakdownAsPercentage] = useState(false);
  
  // États pour la section Display Performance Max
  const [performanceMaxDisplayData, setPerformanceMaxDisplayData] = useState(null);
  const [loadingPerformanceMaxDisplay, setLoadingPerformanceMaxDisplay] = useState(false);
  
  // États pour la section combinée des termes de recherche
  const [showCombinedSearchTerms, setShowCombinedSearchTerms] = useState(true);

  // États pour l'analyse n-gram
  const [showNgramAnalysis, setShowNgramAnalysis] = useState(true);
  
  // État pour le filtre par campagne global (dashboard termes de recherche)
  const [searchTermsCampaignFilter, setSearchTermsCampaignFilter] = useState('all');
  const [availableSearchTermsCampaigns, setAvailableSearchTermsCampaigns] = useState([]);
  const [loadingSearchTermsCampaigns, setLoadingSearchTermsCampaigns] = useState(false);
  const [searchTermsCampaignSearchQuery, setSearchTermsCampaignSearchQuery] = useState('');
  const [showSearchTermsCampaignAutocomplete, setShowSearchTermsCampaignAutocomplete] = useState(false);

  // États pour les campagnes des pages de destination (dashboard landing-pages)
  const [landingPagesCampaignFilter, setLandingPagesCampaignFilter] = useState('all');
  const [availableLandingPagesCampaigns, setAvailableLandingPagesCampaigns] = useState([]);
  const [loadingLandingPagesCampaigns, setLoadingLandingPagesCampaigns] = useState(false);
  const [landingPagesCampaignSearchQuery, setLandingPagesCampaignSearchQuery] = useState('');
  const [showLandingPagesCampaignAutocomplete, setShowLandingPagesCampaignAutocomplete] = useState(false);
  
  // États pour les campagnes avec mots-clés (dashboard mots-clés)
  const [availableKeywordsCampaigns, setAvailableKeywordsCampaigns] = useState([]);
  
  // Fonction pour extraire les campagnes uniques depuis les données de mots-clés
  const extractCampaignsFromKeywords = React.useCallback((keywordsData) => {
    if (!keywordsData?.keywords) return [];
    
    const campaignMap = new Map();
    let keywordCount = 0;
    
    keywordsData.keywords.forEach(keyword => {
      // Les mots-clés ont campaignName mais pas forcément campaignId
      if (keyword.campaignName) {
        // Utiliser campaignName comme clé si campaignId n'existe pas
        const campaignKey = keyword.campaignId || keyword.campaignName;
        
        if (!campaignMap.has(campaignKey)) {
          campaignMap.set(campaignKey, {
            id: keyword.campaignId || keyword.campaignName, // Fallback sur campaignName
            name: keyword.campaignName,
            keywordsCount: 0
          });
        }
        campaignMap.get(campaignKey).keywordsCount++;
        keywordCount++;
      }
    });
    
    const campaigns = Array.from(campaignMap.values()).sort((a, b) => a.name.localeCompare(b.name));
    console.log(`🎯 [KEYWORDS-CAMPAIGNS] ${campaigns.length} campagnes extraites depuis ${keywordCount} mots-clés`);
    
    return campaigns;
  }, []);
  
  // Les campagnes sont maintenant extraites directement depuis les données de mots-clés
  // via le callback onCampaignsExtracted du KeywordsTable
  
  // États pour les tops et flops (visibilité persistée via localStorage)
  const [showTopFlopProducts, setShowTopFlopProducts] = useState(() => isModuleVisible('topflop_products', 'global'));
  const [showTopFlopCategories, setShowTopFlopCategories] = useState(() => isModuleVisible('topflop_categories', 'global'));
  const [showTopFlopItemGroups, setShowTopFlopItemGroups] = useState(() => isModuleVisible('topflop_itemgroups', 'global'));
  const [showGeography, setShowGeography] = useState(() => isModuleVisible('geography', 'global'));
  const [showAgeRanges, setShowAgeRanges] = useState(() => isModuleVisible('age_ranges', 'global'));
  const [showGender, setShowGender] = useState(() => isModuleVisible('gender', 'global'));
  const [showAudiences, setShowAudiences] = useState(() => isModuleVisible('audiences', 'global'));

  // Fonctions de toggle pour les modules Top & Flop et démographiques
  const toggleTopFlopProducts = useCallback(() => {
    const newValue = !showTopFlopProducts;
    setShowTopFlopProducts(newValue);
    setModuleVisibility('topflop_products', newValue, 'global');
  }, [showTopFlopProducts]);

  const toggleTopFlopCategories = useCallback(() => {
    const newValue = !showTopFlopCategories;
    setShowTopFlopCategories(newValue);
    setModuleVisibility('topflop_categories', newValue, 'global');
  }, [showTopFlopCategories]);

  const toggleTopFlopItemGroups = useCallback(() => {
    const newValue = !showTopFlopItemGroups;
    setShowTopFlopItemGroups(newValue);
    setModuleVisibility('topflop_itemgroups', newValue, 'global');
  }, [showTopFlopItemGroups]);

  const toggleGeography = useCallback(() => {
    const newValue = !showGeography;
    setShowGeography(newValue);
    setModuleVisibility('geography', newValue, 'global');
  }, [showGeography]);

  const toggleAgeRanges = useCallback(() => {
    const newValue = !showAgeRanges;
    setShowAgeRanges(newValue);
    setModuleVisibility('age_ranges', newValue, 'global');
  }, [showAgeRanges]);

  const toggleGender = useCallback(() => {
    const newValue = !showGender;
    setShowGender(newValue);
    setModuleVisibility('gender', newValue, 'global');
  }, [showGender]);

  const toggleAudiences = useCallback(() => {
    const newValue = !showAudiences;
    setShowAudiences(newValue);
    setModuleVisibility('audiences', newValue, 'global');
  }, [showAudiences]);

  // useEffect pour synchroniser les modules Top & Flop et démographiques avec localStorage
  useEffect(() => {
    const handleVisibilityChange = () => {
      setShowTopFlopProducts(isModuleVisible('topflop_products', 'global'));
      setShowTopFlopCategories(isModuleVisible('topflop_categories', 'global'));
      setShowTopFlopItemGroups(isModuleVisible('topflop_itemgroups', 'global'));
      setShowGeography(isModuleVisible('geography', 'global'));
      setShowAgeRanges(isModuleVisible('age_ranges', 'global'));
      setShowGender(isModuleVisible('gender', 'global'));
      setShowAudiences(isModuleVisible('audiences', 'global'));
    };

    window.addEventListener('moduleVisibilityChanged', handleVisibilityChange);
    return () => {
      window.removeEventListener('moduleVisibilityChanged', handleVisibilityChange);
    };
  }, []);

  // États pour le dashboard Shopping
  const [shoppingDashboardData, setShoppingDashboardData] = useState(null);
  const [loadingShoppingDashboard, setLoadingShoppingDashboard] = useState(false);
  const [shoppingLoadingProgress, setShoppingLoadingProgress] = useState(0);
  
  // États pour le dashboard YouTube Ads
  const [youtubeVideosData, setYoutubeVideosData] = useState(null);
  const [processedYoutubeData, setProcessedYoutubeData] = useState(null);
  const [loadingYoutubeVideos, setLoadingYoutubeVideos] = useState(false);
  const [isYoutubeDataComplete, setIsYoutubeDataComplete] = useState(false); // Flag pour indiquer si toutes les données sont finalisées

  // Debug: Observer les changements d'état de chargement YouTube
  useEffect(() => {
  }, [loadingYoutubeVideos, isYoutubeDataComplete]);

  // Callback memoized pour onDataProcessed
  const handleYoutubeDataProcessed = useCallback((data) => {
    console.log('🎯 [App.js] Réception des données YouTube complètes');
    setProcessedYoutubeData(data);
    setIsYoutubeDataComplete(true);
  }, []);
  const [youtubeLoadingProgress, setYoutubeLoadingProgress] = useState(0);
  
  // État pour la granularité du composant YouTube Results (jour/semaine/mois/trimestre/année)
  const [youtubeGranularity, setYoutubeGranularity] = useState('day');
  
  // États pour les totaux des vidéos Performance Max (pour synchroniser avec Métriques détaillées)
  const [performanceMaxVideoTotals, setPerformanceMaxVideoTotals] = useState(null);
  // État pour les données individuelles des vidéos Performance Max (pour répartition par campagne)
  // État pour les données de répartition par canal Performance Max (depuis l'API)
  const [performanceMaxChannelsData, setPerformanceMaxChannelsData] = useState([]);
  // État pour l'évolution des coûts par canal
  const [channelsEvolutionData, setChannelsEvolutionData] = useState(null);
  const [loadingChannelsEvolution, setLoadingChannelsEvolution] = useState(false);

  const [searchTermsPage, setSearchTermsPage] = useState(1);
  const [searchTermsSortConfig, setSearchTermsSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [placementsPage, setPlacementsPage] = useState(1);
  const [placementsSortConfig, setPlacementsSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [performanceMaxCampaignsPage, setPerformanceMaxCampaignsPage] = useState(1);
  const [performanceMaxCampaignsSortConfig, setPerformanceMaxCampaignsSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // États pour le filtrage dans le dashboard Performance Max
  const [pmaxCampaignFilter, setPmaxCampaignFilter] = useState(null);
  const [pmaxLabelsFilter, setPmaxLabelsFilter] = useState([]);
  const [pmaxCampaignSearchQuery, setPmaxCampaignSearchQuery] = useState('');
  const [showPmaxCampaignAutocomplete, setShowPmaxCampaignAutocomplete] = useState(false);
  const [showPmaxLabelsDropdown, setShowPmaxLabelsDropdown] = useState(false);
  const [topProductsSortConfig, setTopProductsSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [selectedCountryFilter, setSelectedCountryFilter] = useState('all');
  const [selectedProductTypeFilter, setSelectedProductTypeFilter] = useState('all');
  const [selectedLabelFilter, setSelectedLabelFilter] = useState('all');
  const [selectedShoppingCampaignFilter, setSelectedShoppingCampaignFilter] = useState('all');
  // États pour le filtre de recherche produit
  const [productSearchQuery, setProductSearchQuery] = useState('');
  const [showProductAutocomplete, setShowProductAutocomplete] = useState(false);
  const [selectedProductFilter, setSelectedProductFilter] = useState(null);
  
  // Réinitialiser la pagination quand le compte ou la période change
  useEffect(() => {
    setPerformanceMaxCampaignsPage(1);
    setSearchTermsPage(1);
    setPlacementsPage(1);
  }, [selectedAccount, selectedPeriod]);
  
  // États pour la modal de détails produit
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [showProductModal, setShowProductModal] = useState(false);
  const [productCampaignDetails, setProductCampaignDetails] = useState(null);
  const [loadingProductCampaigns, setLoadingProductCampaigns] = useState(false);
  const [productHistoricalData, setProductHistoricalData] = useState(null);
  const [loadingProductHistorical, setLoadingProductHistorical] = useState(false);

  // États pour la modal de détails terme de recherche
  const [selectedSearchTerm, setSelectedSearchTerm] = useState(null);
  const [showSearchTermModal, setShowSearchTermModal] = useState(false);
  const [searchTermCampaignDetails, setSearchTermCampaignDetails] = useState(null);
  const [loadingSearchTermCampaigns, setLoadingSearchTermCampaigns] = useState(false);
  const [searchTermCampaignsError, setSearchTermCampaignsError] = useState(false);

  // États pour la modal de détails n-gram
  const [selectedNgram, setSelectedNgram] = useState(null);
  const [showNgramModal, setShowNgramModal] = useState(false);
  const [ngramSearchTerms, setNgramSearchTerms] = useState([]);
  const [ngramModalSortConfig, setNgramModalSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // États pour la modal de détails type de produit
  const [selectedProductType, setSelectedProductType] = useState(null);
  const [showProductTypeModal, setShowProductTypeModal] = useState(false);
  const [productTypeCampaignDetails, setProductTypeCampaignDetails] = useState(null);
  const [loadingProductTypeCampaigns, setLoadingProductTypeCampaigns] = useState(false);
  const [productTypeCampaignsError, setProductTypeCampaignsError] = useState(false);
  const [productTypeHistoricalData, setProductTypeHistoricalData] = useState(null);
  const [loadingProductTypeHistorical, setLoadingProductTypeHistorical] = useState(false);

  // État pour la modal de configuration des métriques du tableau Shopping
  const [showShoppingMetricsConfig, setShowShoppingMetricsConfig] = useState(false);

  // État pour la modal de configuration des métriques du dashboard Performance Max
  const [showPmaxMetricsConfig, setShowPmaxMetricsConfig] = useState(false);

  // Fonction de test pour la nouvelle requête Performance Max
  const testNewQuery = async (product) => {
    try {
      console.log('🧪 Test nouvelle requête pour produit:', product.itemId);
      
      const response = await fetch(`${API_URL}/api/dashboard/test-product-campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sourceId: selectedAccount.id || selectedAccount,
          productItemId: product.itemId,
          period: selectedPeriod
        })
      });

      const data = await response.json();
      console.log('🧪 Résultat test nouvelle requête:', data);
      
      alert(`Test terminé! Résultats: ${data.data?.results || 0} lignes, Types de campagnes: ${data.data?.campaignTypes?.join(', ') || 'Aucun'}`);
      
    } catch (error) {
      console.error('🧪 Erreur test nouvelle requête:', error);
      alert('Erreur lors du test: ' + error.message);
    }
  };

  // Fonction pour ouvrir la modal de détails produit
  const openProductModal = async (product) => {
    console.log('🎯 Produit sélectionné:', product);
    console.log('🎯 Description présente?', !!product.description);
    setSelectedProduct(product);
    setShowProductModal(true);
    setProductCampaignDetails(null);
    setProductHistoricalData(null);
    setLoadingProductCampaigns(true);
    setLoadingProductHistorical(true);
    
    try {
      // Préparer les filtres actifs
      const activeFilters = {
        country: selectedCountryFilter,
        customLabel: selectedLabelFilter
      };
      
      console.log('🎯 Chargement détails campagnes pour produit:', product.itemId);
      console.log('🎯 Filtres actifs:', activeFilters);
      
      // Requête vers l'API pour récupérer les détails des campagnes
      const response = await fetch(`${API_URL}/api/dashboard/product-campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sourceId: selectedAccount.id,
          productItemId: product.itemId,
          period: selectedPeriod,
          customStartDate: selectedPeriod === 'CUSTOM' ? customStartDate : null,
          customEndDate: selectedPeriod === 'CUSTOM' ? customEndDate : null,
          filters: activeFilters
        })
      });
      
      const data = await response.json();
      console.log('✅ Détails campagnes reçus:', data);
      
      if (data.success) {
        setProductCampaignDetails(data.data);
        
        // Charger les données historiques pour le graphique
        try {
          console.log('📈 Chargement des données historiques pour le produit...');
          const historicalResponse = await fetch(`${API_URL}/api/dashboard/product-historical`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              sourceId: selectedAccount.id,
              productItemId: product.itemId,
              period: selectedPeriod,
              customStartDate: selectedPeriod === 'CUSTOM' ? customStartDate : null,
              customEndDate: selectedPeriod === 'CUSTOM' ? customEndDate : null
            })
          });
          
          const historicalData = await historicalResponse.json();
          
          if (historicalData.success && historicalData.data) {
            // Formater les données pour le graphique
            const formattedData = historicalData.data.map(item => ({
              date: item.date,
              clicks: item.clicks || 0,
              conversions: item.conversions || 0,
              impressions: item.impressions || 0,
              cost: item.cost || 0
            }));
            
            setProductHistoricalData(formattedData);
            console.log('✅ Données historiques chargées:', formattedData.length, 'jours');
          } else {
            console.log('⚠️ Pas de données historiques disponibles');
            setProductHistoricalData([]);
          }
        } catch (historicalError) {
          console.error('❌ Erreur chargement données historiques:', historicalError);
          setProductHistoricalData([]);
        } finally {
          setLoadingProductHistorical(false);
        }
      } else {
        console.error('❌ Erreur récupération campagnes:', data.error);
        setLoadingProductHistorical(false);
      }
      
    } catch (error) {
      console.error('❌ Erreur chargement détails campagnes:', error);
      setLoadingProductHistorical(false);
    } finally {
      setLoadingProductCampaigns(false);
    }
  };
  
  // Fonction pour fermer la modal de détails produit
  const closeProductModal = () => {
    setSelectedProduct(null);
    setShowProductModal(false);
    setProductCampaignDetails(null);
    setProductHistoricalData(null);
    setLoadingProductCampaigns(false);
    setLoadingProductHistorical(false);
  };

  // Fonction pour ouvrir la modal de détails terme de recherche
  const openSearchTermModal = async (searchTerm) => {
    console.log('🎯 Terme de recherche sélectionné:', searchTerm);
    setSelectedSearchTerm(searchTerm);
    setShowSearchTermModal(true);
    setSearchTermCampaignDetails(null);
    setLoadingSearchTermCampaigns(true);
    setSearchTermCampaignsError(false);
    
    try {
      console.log('🎯 Chargement détails campagnes pour terme:', searchTerm.searchTerm);
      
      // Requête vers l'API pour récupérer les détails des campagnes
      const response = await fetch(`${API_URL}/api/dashboard/search-term-campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: selectedAccount.id,
          search_term: searchTerm.searchTerm,
          period: selectedPeriod,
          customStartDate: selectedPeriod === 'CUSTOM' ? customStartDate : null,
          customEndDate: selectedPeriod === 'CUSTOM' ? customEndDate : null
        })
      });
      
      const data = await response.json();
      console.log('✅ Détails campagnes terme reçus:', data);
      
      if (data.success) {
        setSearchTermCampaignDetails(data.campaigns);
      } else {
        console.error('❌ Erreur récupération campagnes terme:', data.error);
        setSearchTermCampaignsError(true);
      }
      
    } catch (error) {
      console.error('❌ Erreur chargement détails campagnes terme:', error);
      setSearchTermCampaignsError(true);
    } finally {
      setLoadingSearchTermCampaigns(false);
    }
  };

  // Fonction pour fermer la modal de détails terme de recherche
  const closeSearchTermModal = () => {
    setSelectedSearchTerm(null);
    setShowSearchTermModal(false);
    setSearchTermCampaignDetails(null);
    setLoadingSearchTermCampaigns(false);
  };

  // Fonction pour récupérer les campagnes disponibles avec termes de recherche
  const fetchAvailableSearchTermsCampaigns = async () => {
    if (!selectedAccount) return;

    setLoadingSearchTermsCampaigns(true);
    console.log('🎯 [FETCH CAMPAIGNS] Récupération des campagnes avec termes de recherche...');

    try {
      const response = await fetch(`${API_URL}/api/dashboard/search-terms-campaigns-list`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          customer_id: selectedAccount.id,
          period: selectedPeriod,
          customStartDate: selectedPeriod === 'CUSTOM' ? customStartDate : null,
          customEndDate: selectedPeriod === 'CUSTOM' ? customEndDate : null,
          minClicks: 1 // Exclure les termes à 0 clics pour optimiser le chargement
        })
      });

      if (!response.ok) {
        console.error('❌ [FETCH CAMPAIGNS] Erreur HTTP:', response.status, response.statusText);
        const errorText = await response.text();
        console.error('❌ [FETCH CAMPAIGNS] Détails erreur:', errorText);
        setAvailableSearchTermsCampaigns([]);
        return;
      }

      const data = await response.json();
      console.log('✅ [FETCH CAMPAIGNS] Réponse API:', data);

      if (data.success) {
        setAvailableSearchTermsCampaigns(data.campaigns || []);
        console.log(`🎯 [FETCH CAMPAIGNS] ${data.campaigns?.length || 0} campagnes trouvées`);
        if (data.campaigns && data.campaigns.length > 0) {
          console.log('🎯 [FETCH CAMPAIGNS] Exemple de campagne:', data.campaigns[0]);
        }
      } else {
        console.error('❌ [FETCH CAMPAIGNS] Erreur API:', data.error);
        setAvailableSearchTermsCampaigns([]);
      }

    } catch (error) {
      console.error('❌ [FETCH CAMPAIGNS] Erreur requête:', error);
      setAvailableSearchTermsCampaigns([]);
    } finally {
      setLoadingSearchTermsCampaigns(false);
    }
  };

  // Charger les campagnes avec termes de recherche quand compte ou période change
  useEffect(() => {
    // Ne pas charger si le compte n'a pas de données sur la période
    if (accountHasNoData) {
      console.log('⚡ [SEARCH-TERMS/LANDING-PAGES] Compte sans données, skip chargement');
      return;
    }

    if (selectedDashboardModel === 'search-terms' && selectedAccount && selectedPeriod) {
      // Réinitialiser le flag de tentative pour forcer le rechargement des termes de recherche
      // quand on arrive sur ce dashboard (ex: venant du dashboard keywords)
      hasAttemptedSearchTermsLoadRef.current = null;
      // Réinitialiser le filtre de campagne quand on change de compte
      setSearchTermsCampaignFilter('all');
      fetchAvailableSearchTermsCampaigns();
    }

    // Pour landing-pages, les campagnes sont chargées par le composant LandingPagesTableModular
    if (selectedDashboardModel === 'landing-pages' && selectedAccount && selectedPeriod) {
      // Réinitialiser le filtre de campagne quand on change de compte
      setLandingPagesCampaignFilter('all');
      setLoadingLandingPagesCampaigns(true);
      setAvailableLandingPagesCampaigns([]);
      setAccountHasNoData(false); // Reset pour permettre le rechargement
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedDashboardModel, accountHasNoData]);

  // Fonction pour ouvrir la modal n-gram
  const openNgramModal = (ngramData, allSearchTermsRaw) => {
    setSelectedNgram(ngramData);
    
    // Fonction de normalisation (identique à celle du tableau n-gram)
    const normalizeText = (text) => {
      return text
        .toLowerCase()
        .normalize('NFD') // Décomposer les caractères accentués
        .replace(/[\u0300-\u036f]/g, '') // Supprimer les diacritiques
        .replace(/[^\w\s]/g, '') // Supprimer la ponctuation
        .replace(/\s+/g, ' ') // Normaliser les espaces
        .trim();
    };
    
    // Trouver tous les termes de recherche contenant ce n-gram
    const ngramWords = ngramData.ngram.toLowerCase().split(' ');
    const termsMap = new Map(); // Pour l'agrégation par terme commun
    
    allSearchTermsRaw.forEach(term => {
      const searchTerm = term.searchTerm || term.term || '';
      const normalizedTermWords = normalizeText(searchTerm);
      let containsAllWords = true;
      
      // Vérifier si le terme contient tous les mots du n-gram comme mots complets
      for (const word of ngramWords) {
        // Utiliser une regex avec limites de mots pour éviter les correspondances partielles
        const wordBoundaryRegex = new RegExp(`\\b${word.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\b`);
        const isCompleteWord = wordBoundaryRegex.test(normalizedTermWords);
        
        if (!isCompleteWord) {
          containsAllWords = false;
          break;
        }
      }
      
      if (containsAllWords) {
        // Agréger par terme de recherche commun
        const termKey = searchTerm.toLowerCase().trim();
        
        if (!termsMap.has(termKey)) {
          termsMap.set(termKey, {
            searchTerm: searchTerm,
            sources: [],
            impressions: 0,
            clicks: 0,
            cost: 0,
            conversions: 0,
            conversionsValue: 0
          });
        }
        
        const aggregated = termsMap.get(termKey);
        
        // Ajouter la source si elle n'existe pas déjà
        if (!aggregated.sources.includes(term.source)) {
          aggregated.sources.push(term.source);
        }
        
        // Agréger les métriques
        aggregated.impressions += term.impressions || 0;
        aggregated.clicks += term.clicks || 0;
        aggregated.cost += term.cost || 0;
        aggregated.conversions += term.conversions || 0;
        aggregated.conversionsValue += term.conversionsValue || 0;
      }
    });
    
    // Convertir le Map en array et calculer les métriques dérivées
    const relatedTerms = Array.from(termsMap.values()).map(term => {
      const ctr = term.impressions > 0 ? (term.clicks / term.impressions * 100) : 0;
      const cpc = term.clicks > 0 ? (term.cost / term.clicks) : 0;
      const conversionRate = term.clicks > 0 ? (term.conversions / term.clicks * 100) : 0;
      const cpa = term.conversions > 0 ? (term.cost / term.conversions) : 0;
      const cos = term.conversionsValue > 0 ? (term.cost / term.conversionsValue * 100) : 0;
      
      return {
        ...term,
        sources: term.sources, // Conserver le tableau des sources pour l'affichage
        ctr,
        cpc,
        conversionRate,
        cpa,
        cos
      };
    });
    
    // Trier les termes par coût décroissant
    relatedTerms.sort((a, b) => b.cost - a.cost);
    
    setNgramSearchTerms(relatedTerms);
    setShowNgramModal(true);
  };

  // Fonction pour fermer la modal n-gram
  const closeNgramModal = () => {
    setSelectedNgram(null);
    setShowNgramModal(false);
    setNgramSearchTerms([]);
    setNgramModalSortConfig({ key: 'cost', direction: 'desc' }); // Reset du tri
  };

  // Fonction de tri pour la modal n-gram
  const handleNgramModalSort = (key) => {
    const direction = ngramModalSortConfig.key === key && ngramModalSortConfig.direction === 'desc' ? 'asc' : 'desc';
    setNgramModalSortConfig({ key, direction });
  };

  // Fonction pour ouvrir la modal de détails type de produit
  const openProductTypeModal = async (productTypeItem) => {
    console.log('🏷️ FONCTION APPELÉE - Ouverture modal type de produit:', productTypeItem);
    
    setSelectedProductType(productTypeItem);
    setShowProductTypeModal(true);
    setLoadingProductTypeCampaigns(true);
    setProductTypeCampaignsError(false);

    try {
      await loadProductTypeCampaigns(productTypeItem);
    } catch (error) {
      console.error('❌ Erreur chargement détails campagnes type de produit:', error);
      setProductTypeCampaignsError(true);
    } finally {
      setLoadingProductTypeCampaigns(false);
    }
  };

  // Fonction pour charger les campagnes associées à un type de produit
  const loadProductTypeCampaigns = async (productTypeItem) => {
    if (!selectedAccount || !selectedAccount.id) {
      console.error('❌ Aucun compte sélectionné pour les campagnes type de produit');
      return;
    }

    try {
      console.log('🔄 Chargement campagnes pour type de produit:', {
        productType: productTypeItem.fullPath,
        country: productTypeItem.country,
        level: productTypeItem.level
      });

      // Filtres actifs pour la recherche de campagnes
      const activeFilters = {
        country: productTypeItem.country !== 'all' ? productTypeItem.country : selectedCountryFilter,
        productType: productTypeItem.fullPath,
        productTypeLevel: productTypeItem.level
      };

      // Solution temporaire : Utiliser les données existantes des produits pour calculer les campagnes
      console.log('🔄 Calcul des campagnes pour type de produit depuis les données existantes');
      
      // Trouver tous les produits du même type dans les données du dashboard
      const filteredProducts = Object.values(shoppingDashboardData || {})
        .flatMap(accountData => accountData.products || [])
        .filter(product => {
          // Vérifier si le produit correspond au type sélectionné selon le niveau
          let productTypeMatches = false;
          
          // D'abord, essayer avec les productTypes (format "Catégorie > Sous-catégorie > ...")
          if (product.productTypes) {
            if (Array.isArray(product.productTypes) && product.productTypes.length > 0) {
              productTypeMatches = product.productTypes.some(pt => 
                pt === productTypeItem.fullPath || pt.startsWith(productTypeItem.fullPath + ' > ')
              );
            } else if (typeof product.productTypes === 'string') {
              productTypeMatches = product.productTypes === productTypeItem.fullPath || 
                                   product.productTypes.startsWith(productTypeItem.fullPath + ' > ');
            }
          }
          
          // Sinon, vérifier avec les segments Google Ads (product_type_l1, l2, etc.)
          if (!productTypeMatches) {
            const levels = productTypeItem.fullPath.split(' > ');
            productTypeMatches = true;
            
            for (let i = 0; i < levels.length; i++) {
              const fieldName = `product_type_l${i + 1}`;
              if (!product[fieldName] || product[fieldName] !== levels[i]) {
                productTypeMatches = false;
                break;
              }
            }
          }
          
          const countryMatches = productTypeItem.country === 'all' || 
                                 (product.feedLabelOrCountry || product.targetCountry || 'FR') === productTypeItem.country;
          
          return productTypeMatches && countryMatches;
        });

      console.log(`📊 Trouvé ${filteredProducts.length} produits pour le type "${productTypeItem.fullPath}"`);

      if (filteredProducts.length === 0) {
        setProductTypeCampaignDetails({ 
          campaigns: [], 
          message: 'Aucun produit trouvé pour ce type dans les données actuelles.' 
        });
        return;
      }

      // Utiliser le nouvel endpoint optimisé pour récupérer toutes les campagnes en une seule requête
      console.log(`🚀 Chargement optimisé des campagnes pour la catégorie: ${productTypeItem.fullPath}`);
      
      const response = await fetch(`${API_URL}/api/dashboard/product-type-campaigns`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sourceId: selectedAccount.id,
          productType: productTypeItem.fullPath, // Envoyer le chemin complet pour le niveau sélectionné
          country: productTypeItem.country === 'all' ? 'FR' : productTypeItem.country,
          period: selectedPeriod,
          customStartDate: selectedPeriod === 'CUSTOM' ? customStartDate : null,
          customEndDate: selectedPeriod === 'CUSTOM' ? customEndDate : null,
          filters: activeFilters
        })
      });

      if (!response.ok) {
        throw new Error(`Erreur API: ${response.status}`);
      }

      const data = await response.json();
      if (!data.success || !data.data || !data.data.campaigns) {
        throw new Error('Données manquantes dans la réponse');
      }

      const aggregatedCampaigns = data.data.campaigns;
      console.log(`✅ ${aggregatedCampaigns.length} campagnes récupérées pour le type de produit en une seule requête`);

      setProductTypeCampaignDetails({
        campaigns: aggregatedCampaigns,
        success: true
      });

      // Charger les données historiques pour le graphique
      try {
        console.log('📈 Chargement des données historiques pour la catégorie...');
        setLoadingProductTypeHistorical(true);
        
        const historicalResponse = await fetch(`${API_URL}/api/dashboard/product-type-historical`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sourceId: selectedAccount.id,
            productType: productTypeItem.fullPath,
            country: productTypeItem.country === 'all' ? 'FR' : productTypeItem.country,
            period: selectedPeriod,
            customStartDate: selectedPeriod === 'CUSTOM' ? customStartDate : null,
            customEndDate: selectedPeriod === 'CUSTOM' ? customEndDate : null
          })
        });
        
        const historicalData = await historicalResponse.json();
        
        if (historicalData.success && historicalData.data) {
          // Formater les données pour le graphique
          const formattedData = historicalData.data.map(item => ({
            date: item.date,
            clicks: item.clicks || 0,
            conversions: item.conversions || 0,
            impressions: item.impressions || 0,
            cost: item.cost || 0
          }));
          
          setProductTypeHistoricalData(formattedData);
          console.log('✅ Données historiques catégorie chargées:', formattedData.length, 'jours');
        } else {
          console.log('⚠️ Pas de données historiques disponibles pour la catégorie');
          setProductTypeHistoricalData([]);
        }
      } catch (historicalError) {
        console.error('❌ Erreur chargement données historiques catégorie:', historicalError);
        setProductTypeHistoricalData([]);
      } finally {
        setLoadingProductTypeHistorical(false);
      }
      
    } catch (error) {
      console.error('❌ Erreur chargement détails campagnes type de produit:', error);
      setProductTypeCampaignsError(true);
      setLoadingProductTypeHistorical(false);
    }
  };

  // Fonction pour fermer la modal de détails type de produit
  const closeProductTypeModal = () => {
    setSelectedProductType(null);
    setShowProductTypeModal(false);
    setProductTypeCampaignDetails(null);
    setProductTypeHistoricalData(null);
    setLoadingProductTypeCampaigns(false);
    setLoadingProductTypeHistorical(false);
    setProductTypeCampaignsError(false);
  };
  
  // Fonction pour décoder les entités HTML et nettoyer les caractères mal encodés
  const decodeHTMLEntities = (text) => {
    if (!text) return text;
    
    // Décoder les entités HTML en utilisant une approche plus complète
    const textarea = document.createElement('textarea');
    textarea.innerHTML = text;
    let decoded = textarea.value;
    
    // Si la méthode textarea n'a pas fonctionné, utiliser des remplacements manuels
    if (decoded === text || decoded.includes('&')) {
      decoded = text
        // Entités HTML courantes
        .replace(/&amp;/g, '&')
        .replace(/&lt;/g, '<')
        .replace(/&gt;/g, '>')
        .replace(/&quot;/g, '"')
        .replace(/&apos;/g, "'")
        .replace(/&#39;/g, "'")
        .replace(/&nbsp;/g, ' ')
        // Entités HTML accentuées françaises
        .replace(/&agrave;/g, 'à')
        .replace(/&aacute;/g, 'á')
        .replace(/&acirc;/g, 'â')
        .replace(/&atilde;/g, 'ã')
        .replace(/&auml;/g, 'ä')
        .replace(/&egrave;/g, 'è')
        .replace(/&eacute;/g, 'é')
        .replace(/&ecirc;/g, 'ê')
        .replace(/&euml;/g, 'ë')
        .replace(/&igrave;/g, 'ì')
        .replace(/&iacute;/g, 'í')
        .replace(/&icirc;/g, 'î')
        .replace(/&iuml;/g, 'ï')
        .replace(/&ograve;/g, 'ò')
        .replace(/&oacute;/g, 'ó')
        .replace(/&ocirc;/g, 'ô')
        .replace(/&otilde;/g, 'õ')
        .replace(/&ouml;/g, 'ö')
        .replace(/&ugrave;/g, 'ù')
        .replace(/&uacute;/g, 'ú')
        .replace(/&ucirc;/g, 'û')
        .replace(/&uuml;/g, 'ü')
        .replace(/&ccedil;/g, 'ç')
        .replace(/&ntilde;/g, 'ñ')
        // Guillemets français
        .replace(/&ldquo;/g, '"')
        .replace(/&rdquo;/g, '"')
        .replace(/&lsquo;/g, "'")
        .replace(/&rsquo;/g, "'")
        // Autres caractères
        .replace(/&hellip;/g, '…')
        .replace(/&ndash;/g, '–')
        .replace(/&mdash;/g, '—');
    }
    
    // Remplacer les caractères mal encodés courants (en complément)
    decoded = decoded
      .replace(/¿/g, "'") // ¿ est souvent une apostrophe mal encodée
      .replace(/â€™/g, "'") // Autre forme d'apostrophe mal encodée
      .replace(/â€œ/g, '"') // Guillemet ouvrant mal encodé
      .replace(/â€/g, '"') // Guillemet fermant mal encodé
      .replace(/â€"/g, '–') // Tiret demi-cadratin
      .replace(/â€"/g, '—') // Tiret cadratin
      .replace(/Â /g, ' ') // Espace insécable mal encodé
      .replace(/Ã©/g, 'é') // é mal encodé
      .replace(/Ã¨/g, 'è') // è mal encodé
      .replace(/Ã /g, 'à') // à mal encodé
      .replace(/Ã§/g, 'ç') // ç mal encodé
      .replace(/Ã´/g, 'ô') // ô mal encodé
      .replace(/Ã®/g, 'î'); // î mal encodé
    
    return decoded;
  };
  
  // Fonction utilitaire pour filtrer les produits selon les filtres appliqués
  const filterProducts = (products) => {
    let filteredProducts = products || [];
    
    // Debug
    console.log('🔍 filterProducts appelé:', {
      totalProducts: products?.length || 0,
      selectedCountryFilter,
      selectedLabelFilter,
      selectedShoppingCampaignFilter,
      selectedProductFilter: selectedProductFilter?.title
    });

    // Appliquer le filtre produit (recherche par nom)
    if (selectedProductFilter) {
      const beforeCount = filteredProducts.length;
      filteredProducts = filteredProducts.filter(product => {
        const productTitle = (product.title || '').toLowerCase();
        const filterTitle = (selectedProductFilter.title || '').toLowerCase();
        return productTitle.includes(filterTitle);
      });
      console.log(`🔎 Filtre produit appliqué: ${beforeCount} → ${filteredProducts.length}`);
    }

    // Appliquer le filtre pays
    if (selectedCountryFilter !== 'all') {
      const beforeCount = filteredProducts.length;
      filteredProducts = filteredProducts.filter(product => {
        const productCountry = product.feedLabelOrCountry || product.targetCountry || 'FR';
        return productCountry === selectedCountryFilter;
      });
      console.log(`🌍 Filtre pays appliqué: ${beforeCount} → ${filteredProducts.length}`);
    }
    
    // Appliquer le filtre type de produit
    if (selectedProductTypeFilter !== 'all') {
      const beforeCount = filteredProducts.length;
      const [level, selectedPath] = selectedProductTypeFilter.split('::');
      
      filteredProducts = filteredProducts.filter(product => {
        // Construire le chemin du produit en fonction du niveau sélectionné
        const levelNum = parseInt(level.replace('L', ''));
        let productPath = '';
        
        switch(levelNum) {
          case 1:
            productPath = product.product_type_l1 || '';
            break;
          case 2:
            productPath = product.product_type_l1 && product.product_type_l2 
              ? `${product.product_type_l1} > ${product.product_type_l2}` 
              : '';
            break;
          case 3:
            productPath = product.product_type_l1 && product.product_type_l2 && product.product_type_l3
              ? `${product.product_type_l1} > ${product.product_type_l2} > ${product.product_type_l3}`
              : '';
            break;
          case 4:
            productPath = product.product_type_l1 && product.product_type_l2 && product.product_type_l3 && product.product_type_l4
              ? `${product.product_type_l1} > ${product.product_type_l2} > ${product.product_type_l3} > ${product.product_type_l4}`
              : '';
            break;
          case 5:
            productPath = product.product_type_l1 && product.product_type_l2 && product.product_type_l3 && product.product_type_l4 && product.product_type_l5
              ? `${product.product_type_l1} > ${product.product_type_l2} > ${product.product_type_l3} > ${product.product_type_l4} > ${product.product_type_l5}`
              : '';
            break;
          default:
            return false;
        }
        
        return productPath === selectedPath;
      });
      
      console.log(`🏷️ Filtre type de produit appliqué: ${beforeCount} → ${filteredProducts.length}`);
    }
    
    // Appliquer le filtre libellé personnalisé
    if (selectedLabelFilter !== 'all') {
      const beforeCount = filteredProducts.length;

      // Debug: afficher quelques produits avec leurs labels
      console.log('🏷️ [DEBUG] Filtre libellé sélectionné:', selectedLabelFilter);
      console.log('🏷️ [DEBUG] Exemples de produits avec leurs labels:', filteredProducts.slice(0, 3).map(p => ({
        title: p.title?.substring(0, 30),
        customLabel0: p.customLabel0,
        customLabel1: p.customLabel1,
        customLabel2: p.customLabel2,
        customLabel3: p.customLabel3,
        customLabel4: p.customLabel4
      })));

      filteredProducts = filteredProducts.filter(product => {
        // Vérifier si le produit a le label sélectionné dans n'importe quel custom label (0-4)
        const hasLabel =
          product.customLabel0 === selectedLabelFilter ||
          product.customLabel1 === selectedLabelFilter ||
          product.customLabel2 === selectedLabelFilter ||
          product.customLabel3 === selectedLabelFilter ||
          product.customLabel4 === selectedLabelFilter ||
          // Aussi vérifier dans customAttributes
          (product.customAttributes && Array.isArray(product.customAttributes) &&
            product.customAttributes.some(attr =>
              attr.name && attr.name.startsWith('custom_label_') && attr.value === selectedLabelFilter
            )
          );
        return hasLabel;
      });
      console.log(`🏷️ Filtre libellé appliqué: ${beforeCount} → ${filteredProducts.length}`);
    }
    
    // Appliquer le filtre campagne
    if (selectedShoppingCampaignFilter !== 'all') {
      const beforeCount = filteredProducts.length;
      filteredProducts = filteredProducts.filter(product => {
        // Vérifier si le produit appartient à la campagne sélectionnée
        // (soit dans campaignId principal, soit dans allCampaigns)
        if (product.campaignId === selectedShoppingCampaignFilter) return true;
        
        if (product.allCampaigns && Array.isArray(product.allCampaigns)) {
          return product.allCampaigns.some(campaign => campaign.id === selectedShoppingCampaignFilter);
        }
        
        return false;
      });
      console.log(`🎯 Filtre campagne appliqué: ${beforeCount} → ${filteredProducts.length}`);
    }
    
    console.log(`📊 Résultat final: ${filteredProducts.length} produits après filtrage`);
    return filteredProducts;
  };
  
  // États pour les campagnes shopping Performance Max
  const [shoppingData, setShoppingData] = useState(null);

  // Comptes absents des réponses, tous modèles confondus. Les routes poursuivent leur
  // boucle quand un compte échoue et répondent quand même « success: true » : sans
  // cette agrégation, des totaux amputés s'afficheraient comme des totaux complets.
  // Placé après toutes les déclarations d'état : les modèles autres que « global »
  // déclarent les leurs plus bas dans le composant.
  const partialBlocks = React.useMemo(() => ([
    { label: 'Campagnes', failedSources: campaignsData?.failedSources },
    { label: "Groupes d'annonces", failedSources: adGroupsData?.failedSources },
    { label: 'Appareils', failedSources: devicesData?.failedSources },
    { label: 'Jours', failedSources: weekdaysData?.failedSources },
    { label: 'Géographie', failedSources: geographyData?.failedSources },
    { label: 'Réseaux', failedSources: networksPreloadData?.failedSources },
    { label: "Tranches d'âge", failedSources: ageRangesPreloadData?.failedSources },
    { label: 'Genre', failedSources: genderPreloadData?.failedSources },
    { label: 'Audiences', failedSources: audiencesPreloadData?.failedSources },
    { label: 'Catégories', failedSources: topFlopCategoriesPreloadData?.failedSources },
    { label: 'Produits', failedSources: topFlopProductsData?.failedSources },
    { label: 'Termes de recherche', failedSources: searchTermsPreloadData?.failedSources },
    { label: 'Comparatif', failedSources: comparativeData?.failedSources },
    { label: 'Performance Max', failedSources: performanceMaxData?.failedSources },
    { label: 'Performance Max — Display', failedSources: performanceMaxDisplayData?.failedSources },
    { label: 'Performance Max — Canaux', failedSources: channelsEvolutionData?.failedSources },
    { label: 'Shopping', failedSources: shoppingDashboardData?.failedSources },
    { label: 'Shopping — Campagnes', failedSources: shoppingData?.failedSources },
    { label: 'YouTube', failedSources: youtubeVideosData?.failedSources }
  ].filter((block) => block.failedSources?.length)), [
    campaignsData, adGroupsData, devicesData, weekdaysData, geographyData, networksPreloadData,
    ageRangesPreloadData, genderPreloadData, audiencesPreloadData,
    topFlopCategoriesPreloadData, topFlopProductsData, searchTermsPreloadData,
    comparativeData, performanceMaxData, performanceMaxDisplayData, channelsEvolutionData,
    shoppingDashboardData, shoppingData, youtubeVideosData
  ]);
  
  // État pour le tooltip d'image produit - supprimé car utilisation de CSS à la place
  
  // États pour la pagination des produits shopping
  const [productsCurrentPage, setProductsCurrentPage] = useState(1);
  const productsItemsPerPage = 15;
  
  // États pour la pagination des marques shopping
  const [brandsCurrentPage, setBrandsCurrentPage] = useState(1);
  const brandsItemsPerPage = 15;
  
  // État pour gérer les niveaux hiérarchiques ouverts/fermés (types de produit)
  const [expandedProductTypes, setExpandedProductTypes] = useState(new Set());
  // État pour afficher les produits au dernier niveau d'une catégorie
  const [expandedCategoryProducts, setExpandedCategoryProducts] = useState(new Set());
  
  // États pour la pagination des libellés shopping
  const [labelsCurrentPage, setLabelsCurrentPage] = useState(1);
  const labelsItemsPerPage = 15;
  
  // États pour le tri des produits shopping
  const [productsSortConfig, setProductsSortConfig] = useState({ field: 'cost', direction: 'desc' });
  
  // Fonction pour gérer le tri des produits
  const handleProductSort = (field) => {
    setProductsSortConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
    setProductsCurrentPage(1); // Réinitialiser à la première page
  };

  // États pour le tri des tableaux d'agrégation shopping
  const [statusSortConfig, setStatusSortConfig] = useState({ field: 'cost', direction: 'desc' });
  const [availabilitySortConfig, setAvailabilitySortConfig] = useState({ field: 'cost', direction: 'desc' });
  const [categorySortConfig, setCategorySortConfig] = useState({ field: 'cost', direction: 'desc' });
  const [brandSortConfig, setBrandSortConfig] = useState({ field: 'cost', direction: 'desc' });
  const [labelSortConfig, setLabelSortConfig] = useState({ field: 'cost', direction: 'desc' });

  // Fonction générique pour gérer le tri des tableaux d'agrégation
  const createSortHandler = (setConfig) => (field) => {
    setConfig(prevConfig => ({
      field,
      direction: prevConfig.field === field && prevConfig.direction === 'desc' ? 'asc' : 'desc'
    }));
  };

  const handleStatusSort = createSortHandler(setStatusSortConfig);
  const handleAvailabilitySort = createSortHandler(setAvailabilitySortConfig);
  const handleCategorySort = createSortHandler(setCategorySortConfig);
  const handleBrandSort = createSortHandler(setBrandSortConfig);
  const handleLabelSort = createSortHandler(setLabelSortConfig);

  // Fonction pour trier les données agrégées
  const sortAggregatedData = (data, sortConfig) => {
    return [...data].sort((a, b) => {
      let aValue, bValue;

      // Calculer les valeurs dérivées si nécessaire
      switch (sortConfig.field) {
        case 'ctr':
          aValue = a.impressions > 0 ? (a.clicks / a.impressions * 100) : 0;
          bValue = b.impressions > 0 ? (b.clicks / b.impressions * 100) : 0;
          break;
        case 'cpc':
          aValue = a.clicks > 0 ? (a.cost / a.clicks) : 0;
          bValue = b.clicks > 0 ? (b.cost / b.clicks) : 0;
          break;
        case 'conversionRate':
          aValue = a.clicks > 0 ? (a.conversions / a.clicks * 100) : 0;
          bValue = b.clicks > 0 ? (b.conversions / b.clicks * 100) : 0;
          break;
        case 'cpa':
          aValue = a.conversions > 0 ? (a.cost / a.conversions) : 0;
          bValue = b.conversions > 0 ? (b.cost / b.conversions) : 0;
          break;
        case 'cos':
          aValue = a.conversionsValue > 0 ? (a.cost / a.conversionsValue * 100) : 0;
          bValue = b.conversionsValue > 0 ? (b.cost / b.conversionsValue * 100) : 0;
          break;
        case 'count':
          aValue = a.count || a.productCount || 0;
          bValue = b.count || b.productCount || 0;
          break;
        case 'label':
          aValue = a.label || '';
          bValue = b.label || '';
          break;
        case 'labelType':
          aValue = a.labelType || '';
          bValue = b.labelType || '';
          break;
        case 'brand':
          aValue = a.brand || '';
          bValue = b.brand || '';
          break;
        default:
          aValue = a[sortConfig.field] || 0;
          bValue = b[sortConfig.field] || 0;
      }

      // Gérer les chaînes de caractères
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        return sortConfig.direction === 'desc'
          ? bValue.localeCompare(aValue)
          : aValue.localeCompare(bValue);
      }

      // Tri numérique
      return sortConfig.direction === 'desc' ? bValue - aValue : aValue - bValue;
    });
  };

  // Réinitialiser la pagination quand les filtres changent (éviter les logs multiples)
  useEffect(() => {
    setProductsCurrentPage(1);
  }, [selectedCountryFilter, selectedLabelFilter, selectedShoppingCampaignFilter, selectedProductFilter]);

  // Présélectionner le compte si fourni depuis la navigation ou la sidebar
  useEffect(() => {
    // Condition: preselectedAccountId est défini ET différent du compte actuellement sélectionné
    const shouldChangeAccount = preselectedAccountId &&
      libraryItems.length > 0 &&
      (!selectedAccount || selectedAccount.id !== preselectedAccountId);

    if (shouldChangeAccount) {
      console.log('🎯 GoogleAds - Changement de compte vers:', preselectedAccountId);

      // Chercher le compte dans la liste
      const targetAccount = libraryItems.find(item => item.id === preselectedAccountId);
      if (targetAccount) {
        console.log('✅ GoogleAds - Compte trouvé:', targetAccount.name);
        setSelectedAccount(targetAccount);
        setCurrentActiveAccount(targetAccount.id);
        setSelectedSources([targetAccount.id]);
        setPreselectionUsed(true);
        // Notifier la sidebar et le breadcrumb pour synchronisation
        window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: targetAccount.id, accountName: targetAccount.name } }));
        // Mettre à jour l'URL avec le compte sélectionné (pour persistance au refresh)
        window.updateUrlWithAccount?.(targetAccount.id);
      } else {
        console.log('⚠️ GoogleAds - Compte non trouvé dans la liste, réinitialisation');
        // Réinitialiser car le compte demandé n'existe pas (changement de client)
        setSelectedAccount(null);
        setCurrentActiveAccount(null);
        setSelectedSources([]);
        setAutoCharts(null);
        setPreselectionUsed(false);
      }
    } else if (!preselectedAccountId && selectedAccount && libraryItems.length > 1) {
      // "Tous les comptes" sélectionné - réinitialiser
      // Ne pas réinitialiser si un seul compte (cas d'auto-sélection)
      console.log('🔄 GoogleAds - Réinitialisation (Tous les comptes)');
      setSelectedAccount(null);
      setCurrentActiveAccount(null);
      setSelectedSources([]);
      setAutoCharts(null);
      setPreselectionUsed(false);
    }
  }, [preselectedAccountId, libraryItems]);
  
  // Générer automatiquement le dashboard lors de la présélection d'un compte
  useEffect(() => {
    // Vérifier que le compte est dans la liste filtrée actuelle (évite la génération pour un ancien client)
    const accountInFilteredList = libraryItems.some(item => item.id === selectedAccount?.id);
    if (selectedAccount && preselectedAccountId && selectedAccount.id === preselectedAccountId && !autoCharts && accountInFilteredList) {
      console.log('🚀 GoogleAds - Génération automatique du dashboard après présélection');
      generateAutoDashboard();
    }
  }, [selectedAccount, preselectedAccountId, autoCharts, libraryItems]);

  // Note: Plus besoin de recharger l'API pour changement de granularité
  // L'agrégation se fait maintenant côté frontend avec les données enrichies
  
  // Ref pour savoir si on avait un preselectedAccountId (venant de Synthèse)
  const hadPreselectedAccountRef = useRef(false);
  
  // Tracker quand on reçoit un preselectedAccountId
  useEffect(() => {
    if (preselectedAccountId !== null) {
      hadPreselectedAccountRef.current = true;
    }
  }, [preselectedAccountId]);
  
  // Réinitialiser l'état seulement si on avait un preselectedAccountId et qu'il a été nettoyé
  useEffect(() => {
    if (preselectedAccountId === null && hadPreselectedAccountRef.current && selectedAccount) {
      console.log('🔄 GoogleAds - Réinitialisation état après nettoyage preselectedAccountId (retour de Synthèse)');
      setSelectedAccount(null);
      setCurrentActiveAccount(null); // Réinitialiser le compte actif global
      setAutoCharts(null);
      setAiInsights(null);
      hadPreselectedAccountRef.current = false; // Reset du flag
    }
  }, [preselectedAccountId, selectedAccount]);

  // Note: Le gestionnaire accountChange a été supprimé car le useEffect sur preselectedAccountId
  // gère maintenant les changements de compte depuis la sidebar de manière plus fiable

  // Gestionnaire pour la génération automatique depuis la sidebar
  useEffect(() => {
    const handleAutoGenerate = (event) => {
      const { platform } = event.detail;
      if (platform !== 'google') return;

      console.log('🚀 GoogleAds - Clic sur plateforme depuis sidebar');

      // Attendre que le filtrage par client soit terminé
      setTimeout(() => {
        // Utiliser la ref pour avoir la valeur la plus récente de libraryItems
        const currentFilteredItems = libraryItemsRef.current;

        console.log('🔍 GoogleAds - Comptes disponibles après filtrage:', currentFilteredItems.length);

        // Si pas de compte sélectionné
        if (!selectedAccount && currentFilteredItems.length > 0) {
          // Si un seul compte disponible, le sélectionner et générer automatiquement
          if (currentFilteredItems.length === 1) {
            const singleAccount = currentFilteredItems[0];
            console.log('📌 GoogleAds - Un seul compte disponible, sélection auto:', singleAccount.name);
            setSelectedAccount(singleAccount);
            setCurrentActiveAccount(singleAccount.id);
            setSelectedSources([singleAccount.id]);
            const accountSite = userSites.find(s => s.accountIds?.includes(singleAccount.id) || s.accountIds?.includes(singleAccount.sourceId));
            window.currentNotesContext = {
              platform: 'GOOGLE_ADS',
              accountId: singleAccount.sourceId || singleAccount.id,
              accountName: singleAccount.name,
              siteId: accountSite?._id || accountSite?.id || null,
              clientId: accountSite?.clientId || null
            };
            // Déclencher la génération
            setTimeout(() => {
              if (window.googleDashboardGenerateAuto) {
                window.googleDashboardGenerateAuto();
              }
            }, 200);
          } else {
            // Plusieurs comptes disponibles, ne pas sélectionner automatiquement
            console.log('📋 GoogleAds - Plusieurs comptes disponibles, attente de sélection manuelle');
          }
        } else if (selectedAccount) {
          // Un compte est déjà sélectionné, générer le dashboard
          setTimeout(() => {
            if (window.googleDashboardGenerateAuto) {
              window.googleDashboardGenerateAuto();
            }
          }, 200);
        }
      }, 400); // Délai pour permettre au filtrage asynchrone de s'exécuter et à la ref d'être mise à jour
    };

    window.addEventListener('dashboardAutoGenerate', handleAutoGenerate);
    return () => {
      window.removeEventListener('dashboardAutoGenerate', handleAutoGenerate);
    };
  }, [selectedAccount]);

  // Gestionnaire pour la sélection de compte depuis la sidebar
  useEffect(() => {
    const handleSidebarAccountChange = (event) => {
      const { accountId, accountName } = event.detail;
      console.log('🔄 GoogleAds - Changement de compte depuis sidebar:', accountId, accountName);

      if (!accountId) {
        // "Tous les comptes" sélectionné
        setSelectedAccount(null);
        setCurrentActiveAccount(null);
        setSelectedSources([]);
        setAutoCharts(null);
        return;
      }

      // Chercher le compte dans la liste
      const targetAccount = libraryItemsRef.current.find(item =>
        item.id === accountId || item.sourceId === accountId
      );

      if (targetAccount) {
        setSelectedAccount(targetAccount);
        setCurrentActiveAccount(targetAccount.id);
        setSelectedSources([targetAccount.id]);
        const targetSite = userSites.find(s => s.accountIds?.includes(targetAccount.id) || s.accountIds?.includes(targetAccount.sourceId));
        window.currentNotesContext = {
          platform: 'GOOGLE_ADS',
          accountId: targetAccount.sourceId || targetAccount.id,
          accountName: targetAccount.name,
          siteId: targetSite?._id || targetSite?.id || null,
          clientId: targetSite?.clientId || null
        };
        // Générer automatiquement le dashboard
        setTimeout(() => {
          if (window.googleDashboardGenerateAuto) {
            window.googleDashboardGenerateAuto();
          }
          // Mettre à jour l'URL après que activeTab soit synchronisé
          window.updateUrlWithAccount?.(targetAccount.id);
        }, 200);
      }
    };

    window.addEventListener('sidebarAccountChange', handleSidebarAccountChange);
    return () => {
      window.removeEventListener('sidebarAccountChange', handleSidebarAccountChange);
    };
  }, []);

  // Logique de réinitialisation simplifiée - plus besoin d'événements complexes

  // Ref pour éviter les chargements multiples des termes de recherche
  const isLoadingSearchTermsRef = useRef(false);
  const hasAttemptedSearchTermsLoadRef = useRef(null);
  const isLoadingLibraryRef = useRef(false);

  const loadLibraryItems = useCallback(async () => {
    if (isLoadingLibraryRef.current) return;
    isLoadingLibraryRef.current = true;
    
    setLoadingLibraryItems(true);
  try {
    const response = await fetch(`${API_URL}/api/library`, {
      headers: { 'Authorization': `Bearer ${token}` }
    });
    const data = await response.json();
    
    
    if (data.success && data.library) {
      // Les comptes Google Ads peuvent être dans GOOGLE_SHEETS ou GOOGLE_ADS selon la connexion
      const googleSheetsItems = data.library.GOOGLE_SHEETS || [];
      const googleAdsItems = data.library.GOOGLE_ADS || [];
      
      // Filtrer pour ne prendre que les items de type GOOGLE_ADS
      const adsItems = [
        ...googleSheetsItems.filter(item => item.type === 'GOOGLE_ADS'),
        ...googleAdsItems.filter(item => item.type === 'GOOGLE_ADS')
      ];
      
      
      // Déduplication basée sur l'ID unique
      const uniqueAdsItems = adsItems.filter((item, index, self) => 
        index === self.findIndex((t) => t.id === item.id)
      );
      
      setAllLibraryItems(uniqueAdsItems); // Garder une copie des comptes non filtrés
      setLibraryItems(uniqueAdsItems);
    } else {
      console.log('Erreur API:', data.error);
      setError(data.error || 'Erreur lors du chargement des comptes Google Ads');
    }
  } catch (error) {
    console.error('Erreur chargement bibliothèque:', error);
    setError('Erreur lors du chargement des comptes Google Ads');
  } finally {
    setLoadingLibraryItems(false);
    isLoadingLibraryRef.current = false;
  }
}, [token]);

  // Charger les éléments de la bibliothèque (Google Ads)
  useEffect(() => {
    if (token) {
      loadLibraryItems();
    }
  }, [token]);

  // Filtrer les comptes par client ou par site (Google Ads)
  const filterAccountsByClient = async (accounts, clientId, siteId) => {
    // Si un site spécifique est sélectionné, filtrer par ce site en utilisant userSites
    if (siteId && siteId !== 'all' && accounts.length) {
      const site = userSites.find(s => (s._id || s.id) === siteId);
      if (site && site.accountIds) {
        const allowedAccountIds = ensembleAutorise(site.accountIds);
        const filteredAccounts = accounts.filter(account => compteAutorise(account, allowedAccountIds));
        return filteredAccounts;
      }
    }

    if (!clientId || !accounts.length) {
      return accounts;
    }

    try {
      // Récupérer les sites associés au client
      const response = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { clientId }
      });

      const sites = response.data.sites || [];

      // Extraire tous les accountIds des sites du client
      const allowedAccountIds = ensembleAutorise(
        sites.flatMap(site => (Array.isArray(site.accountIds) ? site.accountIds : []))
      );

      const filteredAccounts = accounts.filter(account => compteAutorise(account, allowedAccountIds));

      return filteredAccounts;
    } catch (error) {
      console.error('Erreur filtrage comptes par client:', error);
      return accounts;
    }
  };

  // Refs pour tracker le site et client précédents (Google Ads)
  const prevSiteIdRef = useRef(selectedSiteId);
  const prevClientIdRef = useRef(selectedClientId);

  // Recharger la liste des comptes et gérer la sélection quand le site/client change (Google Ads)
  useEffect(() => {
    const siteChanged = prevSiteIdRef.current !== selectedSiteId;
    const clientChanged = prevClientIdRef.current !== selectedClientId;
    prevSiteIdRef.current = selectedSiteId;
    prevClientIdRef.current = selectedClientId;

    const filterAndSelectAccount = async () => {
      if (siteChanged || clientChanged) {
        console.log('🔄 [GOOGLE ADS] Changement de site/client détecté:', { selectedSiteId, selectedClientId });
        // IMPORTANT: Réinitialiser le flag de présélection pour éviter que l'useEffect
        // de réinitialisation ne supprime le compte auto-sélectionné
        hadPreselectedAccountRef.current = false;

        // IMMÉDIAT: Effacer les données du dashboard dès le changement de client
        // pour un feedback visuel instantané (avant le filtrage asynchrone)
        console.log('🔄 [GOOGLE ADS] Effacement immédiat des données (changement client)');
        setAutoCharts(null);
        setResultsData(null);
        setDevicesData(null);
        setWeekdaysData(null);
        setCampaignsData(null);
        setAdGroupsData(null);
        setTopFlopProductsData(null);
        setGeographyData(null);
        setAiInsights(null);
        setShoppingData(null);
        setError('');
        setSelectedAccount(null);
        setCurrentActiveAccount(null);
        setSelectedSources([]);
      }

      if (allLibraryItems.length > 0) {
        console.log('🔍 [GOOGLE ADS] Filtrage des comptes pour site:', selectedSiteId);
        const filteredItems = await filterAccountsByClient(allLibraryItems, selectedClientId, selectedSiteId);
        console.log('🔍 [GOOGLE ADS] Comptes filtrés:', filteredItems.length);
        setLibraryItems(filteredItems);

        // Si un seul compte disponible après filtrage, l'auto-sélectionner
        // (sauf si un compte présélectionné EXISTE dans la liste)
        const preselectedExistsInList = preselectedAccountId && filteredItems.some(item => item.id === preselectedAccountId);
        if (filteredItems.length === 1 && !preselectedExistsInList) {
          const account = filteredItems[0];
          console.log('🎯 [GOOGLE ADS] Auto-sélection du seul compte disponible:', account.name);
          setSelectedAccount(account);
          setCurrentActiveAccount(account.id);
          setSelectedSources([account.id]);
          // Mettre à jour le contexte des notes
          const accountSite = userSites.find(s => s.accountIds?.includes(account.id) || s.accountIds?.includes(account.sourceId));
          window.currentNotesContext = {
            platform: 'GOOGLE_ADS',
            accountId: account.sourceId || account.id,
            accountName: account.name,
            siteId: accountSite?._id || accountSite?.id || null,
            clientId: accountSite?.clientId || null
          };
          // Mettre à jour le breadcrumb
          window.dispatchEvent(new CustomEvent('accountSelected', {
            detail: { accountId: account.id, accountName: account.name }
          }));
        }
      }
    };

    filterAndSelectAccount();
  }, [selectedClientId, selectedSiteId, allLibraryItems]);

  // Note: Le rechargement des données est géré par le useEffect sur selectedAccount (ligne ~14476)
  // qui se déclenche après que la logique de filtrage ci-dessus ait mis à jour le compte sélectionné

  // Charger les données unifiées (Résultats + Campagnes) avec l'endpoint campaigns unifié
  // OPTIMISATION: Un seul appel au lieu de deux endpoints séparés
  const loadResultsSection = async () => {
    if (!selectedAccount) {
      console.log('❌ [RESULTS] Pas de selectedAccount, arrêt');
      return;
    }

    try {
      setLoadingResults(true);
      setLoadingCampaigns(true);

      const baseRequestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod,
        granularity: granularity
      };

      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) return;
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }

      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        baseRequestBody.clientId = selectedClientId;
        console.log('🎯 [DASHBOARD] Filtrage par client:', selectedClientId);
      }

      // Ajouter le filtre par labels si des labels sont sélectionnés
      if (selectedLabelsFilter.length > 0 && Object.keys(campaignLabelsMap).length > 0) {
        const matchingCampaignIds = Object.entries(campaignLabelsMap)
          .filter(([campaignId, labels]) =>
            selectedLabelsFilter.some(selectedLabel => labels.includes(selectedLabel))
          )
          .map(([campaignId]) => campaignId);

        if (matchingCampaignIds.length > 0) {
          baseRequestBody.campaignIds = matchingCampaignIds;
          console.log('🏷️ [RESULTS] Filtrage par labels, campagnes:', matchingCampaignIds.length);
        }
      }

      // Endpoint unifié: campagnes + timeline + KPIs
      const response = await fetch(`${config.API_URL}/api/dashboard/components/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(baseRequestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [RESULTS] Erreur HTTP:', response.status, errorText);
        throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
      }

      const campaignsResult = await response.json();

      console.log('📈 [UNIFIED-DEBUG] Réponse complète:', campaignsResult);
      console.log('📈 [UNIFIED-DEBUG] Charts:', campaignsResult?.charts?.length || 0);
      console.log('📈 [UNIFIED-DEBUG] RawData keys:', campaignsResult?.rawData ? Object.keys(campaignsResult.rawData) : 'null');

      if (campaignsResult.success) {
        // Générer timelineData pour PerformanceChart depuis le endpoint unifié
        const timelineData = {
          success: true,
          charts: campaignsResult.charts || [],
          rawData: campaignsResult.rawData || {}
        };

        if (timelineData.charts && timelineData.charts.length > 0) {
          console.log('✅ [UNIFIED-DEBUG] Charts disponibles:', timelineData.charts.length);
        } else {
          console.log('⚠️ [UNIFIED-DEBUG] Pas de charts, rawData:', !!timelineData.rawData);
        }

        setResultsData(timelineData);
        setCampaignsData(campaignsResult);
        console.log('✅ [UNIFIED] Données results + campaigns chargées avec succès');
      } else {
        console.error('❌ [RESULTS] Erreur unified:', campaignsResult.error);
      }

    } catch (error) {
      console.error('❌ [RESULTS] Erreur chargement section Résultats:', error);
    } finally {
      setLoadingResults(false);
      setLoadingCampaigns(false);
    }
  };

  // OPTIMISATION: loadCampaignsSection délègue à loadResultsSection (endpoint unifié)
  const loadCampaignsSection = async () => {
    // L'endpoint campaigns est maintenant unifié avec timeline
    // loadResultsSection charge les deux
    await loadResultsSection();
  };

  // Charger les données de la section Groupes d'annonces avec le nouvel endpoint
  const loadAdGroupsSection = async () => {
    if (!selectedAccount) {
      return;
    }
    
    try {
      
      const baseRequestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod
      };
      
      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) return;
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }
      
      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        baseRequestBody.clientId = selectedClientId;
        console.log('🎯 [AD-GROUPS] Filtrage par client:', selectedClientId);
      }
      
      const response = await fetch(`${config.API_URL}/api/dashboard/components/ad-groups`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(baseRequestBody)
      });
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const adGroupsResult = await response.json();
      
      if (adGroupsResult.success && adGroupsResult.data) {
        setAdGroupsData(adGroupsResult);
        console.log('✅ [AD-GROUPS] Données groupes d\'annonces chargées avec succès');
      } else {
        console.log('❌ [AD-GROUPS] Échec du chargement:', adGroupsResult.error || 'Réponse invalide');
      }
      
    } catch (error) {
      console.error('❌ [AD-GROUPS] Erreur lors du chargement:', error);
    } finally {
    }
  };

  // OPTIMISATION: loadCampaignTypesSection supprimée - utiliser campaignsData unifié

  // Charger les données de la section Appareils avec le nouvel endpoint
  const loadDevicesSection = async () => {
    if (!selectedAccount) {
      return;
    }


    try {
      const baseRequestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod
      };

      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) {
          console.log('❌ [DEVICES] Dates personnalisées manquantes');
          return;
        }
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }
      
      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        baseRequestBody.clientId = selectedClientId;
        console.log('🎯 [DEVICES] Filtrage par client:', selectedClientId);
      }
      
      const response = await fetch(`${config.API_URL}/api/dashboard/components/devices`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(baseRequestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const devicesResult = await response.json();
      
      if (devicesResult.success) {
        console.log('✅ [DEVICES] Section Appareils chargée avec succès');
        setDevicesData(devicesResult);
      } else {
        console.error('❌ [DEVICES] Erreur:', devicesResult.error);
      }
      
    } catch (error) {
      console.error('❌ [DEVICES] Erreur chargement section Appareils:', error);
    } finally {
    }
  };

  // Charger les données de la section Jours de la semaine avec le nouvel endpoint
  const loadWeekdaysSection = async () => {
    if (!selectedAccount) {
      return;
    }

    setLoadingWeekdays(true);

    try {
      const baseRequestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod
      };

      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) {
          console.log('❌ [WEEKDAYS] Dates personnalisées manquantes');
          return;
        }
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }
      
      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        baseRequestBody.clientId = selectedClientId;
        console.log('🎯 [WEEKDAYS] Filtrage par client:', selectedClientId);
      }
      
      const response = await fetch(`${config.API_URL}/api/dashboard/components/weekdays`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(baseRequestBody)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const weekdaysResult = await response.json();
      
      console.log('🔍 [WEEKDAYS] Réponse reçue:', weekdaysResult);
      
      if (weekdaysResult.success) {
        console.log('✅ [WEEKDAYS] Section Jours de la semaine chargée avec succès');
        console.log('✅ [WEEKDAYS] Structure weekdaysResult:', {
          hasCharts: !!weekdaysResult.charts,
          chartsLength: weekdaysResult.charts?.length,
          hasData: !!weekdaysResult.data,
          dataLength: weekdaysResult.data?.length
        });
        setWeekdaysData(weekdaysResult);
      } else {
        console.error('❌ [WEEKDAYS] Erreur:', weekdaysResult.error);
      }
      
    } catch (error) {
      console.error('❌ [WEEKDAYS] Erreur chargement section Jours de la semaine:', error);
    } finally {
      setLoadingWeekdays(false);
    }
  };

  // Générer le dashboard automatique
  const generateAutoDashboard = async (accountOverride = null) => {
    const accountToUse = accountOverride || selectedAccount;
    if (!accountToUse) {
      setError('Veuillez sélectionner un compte Google Ads');
      return;
    }

    setLoadingCharts(true);
    setLoadingProgress(0);
    setAccountHasNoData(false); // Reset au début du chargement
    setAutoCharts(null); // Reset previous data
    setResultsData(null); // Reset previous results data
    
    // Démarrer la progression synchronisée avec le temps réel
    const startTime = Date.now();
    const estimatedDuration = 5000; // Durée estimée en ms (5 secondes)
    
    const progressTimer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const calculatedProgress = Math.min((elapsed / estimatedDuration) * 90, 90); // Max 90% avant la fin
      setLoadingProgress(calculatedProgress);
    }, 50); // Mise à jour plus fréquente pour plus de fluidité
    
    try {
      console.log('📊 Génération dashboard pour:', accountToUse?.name || 'compte', 'Période:', selectedPeriod);

      const performanceTimes = []; // Collecter tous les temps de performance

      // Étape 1: Préparation de la requête

      // Préparer le body de la requête commun
      const baseRequestBody = {
        sources: [accountToUse?.id],
        period: selectedPeriod,
        granularity: granularity
      };

      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) {
          setError('Veuillez sélectionner une date de début et une date de fin');
          setLoadingCharts(false);
          clearInterval(progressTimer);
          return;
        }
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }
      
      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        baseRequestBody.clientId = selectedClientId;
        console.log('🎯 [DASHBOARD] Filtrage principal par client:', selectedClientId);
      }

      // Ajouter le filtre campagne si une campagne est sélectionnée
      if (selectedCampaignFilter) {
        baseRequestBody.campaignFilter = selectedCampaignFilter;
        console.log('🎯 [DASHBOARD] Filtrage par campagne:', selectedCampaignFilter.name);
      }

      // Ajouter le filtre par labels si des labels sont sélectionnés
      if (selectedLabelsFilter.length > 0 && Object.keys(campaignLabelsMap).length > 0) {
        const matchingCampaignIds = Object.entries(campaignLabelsMap)
          .filter(([campaignId, labels]) =>
            selectedLabelsFilter.some(selectedLabel => labels.includes(selectedLabel))
          )
          .map(([campaignId]) => campaignId);

        if (matchingCampaignIds.length > 0) {
          baseRequestBody.campaignIds = matchingCampaignIds;
          console.log('🏷️ [DASHBOARD] Filtrage par labels, campagnes:', matchingCampaignIds.length);
        }
      }

      // Étape 1.5: Pré-vérification rapide des données
      try {
        const checkDataResponse = await fetch(`${API_URL}/api/dashboard/components/check-data`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            sourceId: accountToUse?.id,
            period: selectedPeriod,
            customStartDate: selectedPeriod === 'CUSTOM' ? customStartDate : undefined,
            customEndDate: selectedPeriod === 'CUSTOM' ? customEndDate : undefined
          })
        });

        const checkDataResult = await checkDataResponse.json();
        console.log('⚡ [CHECK-DATA] Résultat:', checkDataResult);

        // Si pas de données, arrêter le chargement et afficher le message
        if (checkDataResult.success && !checkDataResult.hasData) {
          console.log('⚡ [CHECK-DATA] Aucune donnée sur la période, arrêt du chargement');
          clearInterval(progressTimer);
          setLoadingProgress(100);
          setLoadingCharts(false);
          setAccountHasNoData(true); // Bloquer les appels API supplémentaires (Performance Max, etc.)
          // Mettre resultsData vide pour déclencher NoDataMessage
          setResultsData({ charts: [], rawData: {} });
          setAutoCharts({ charts: [], rawData: {} });
          return;
        }
      } catch (checkError) {
        // En cas d'erreur, continuer le chargement normal
        console.warn('⚠️ [CHECK-DATA] Erreur pré-vérification, continuation:', checkError.message);
      }

      // Étape 2: Lancement des appels parallèles OPTIMISÉ
      
      const requestConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        }
      };

      // OPTIMISATION: Paralléliser les requêtes critiques avec monitoring des performances
      const startTime = performance.now();
      const requestStartTimes = [];

      // Les bornes de la période regardée, servies au point de comparaison. La table des
      // périodes vit dans `utils/periodUtils`, celle que le sélecteur affiche : les copies
      // avaient déjà divergé ailleurs — l'une sérialisait en UTC, une douzaine d'autres
      // s'arrêtaient à aujourd'hui là où une période nommée s'arrête hier.
      let comparisonStartDate, comparisonEndDate;
      if (selectedPeriod === 'CUSTOM') {
        comparisonStartDate = customStartDate;
        comparisonEndDate = customEndDate;
      } else {
        const plage = getDateRangeFromPeriod(selectedPeriod);
        comparisonStartDate = plage.startDate;
        comparisonEndDate = plage.endDate;
      }

      // Mesurer chaque requête individuellement
      const requestPromises = [
        // Dashboard principal
        (async () => {
          const reqStart = performance.now();
          requestStartTimes[0] = reqStart;
          const response = await fetch(`${API_URL}/api/dashboard/charts`, {
            ...requestConfig,
            body: JSON.stringify({
              ...baseRequestBody,
              skipMerchantCenter: true,
              skipGeoData: true
            })
          });

          // Vérifier les erreurs d'authentification
          const authCheck = await handleApiError(response, '/api/dashboard/charts');
          if (authCheck.isAuthError) {
            throw new Error('AUTH_EXPIRED');
          }

          const reqEnd = performance.now();
          const duration = (reqEnd - reqStart).toFixed(0);
          performanceTimes.push({ endpoint: '/api/dashboard/charts', duration: `${duration}ms` });
          return response;
        })(),

        // Période précédente en parallèle pour éviter le blocage (mode lightweight pour KPICards uniquement)
        (async () => {
          const reqStart = performance.now();
          try {
            const response = await fetch(`${API_URL}/api/dashboard/previous-period-comparison`, {
              ...requestConfig,
              body: JSON.stringify({
                accountId: accountToUse.sourceId || accountToUse.id,
                startDate: comparisonStartDate,
                endDate: comparisonEndDate,
                comparisonType: gadsComparisonType || 'previous_period',
                periodType: selectedPeriod, // Pour calculer correctement les périodes calendaires
                lightweight: true, // Mode léger: uniquement les totaux pour les KPICards
                campaignIds: baseRequestBody.campaignIds // Filtre labels si actif
              })
            });
            const reqEnd = performance.now();
            const duration = (reqEnd - reqStart).toFixed(0);
            performanceTimes.push({ endpoint: '/api/dashboard/previous-period-comparison', duration: `${duration}ms` });
            return response;
          } catch (error) {
            const reqEnd = performance.now();
            const duration = (reqEnd - reqStart).toFixed(0);
            performanceTimes.push({ endpoint: '/api/dashboard/previous-period-comparison', duration: `${duration}ms (ERREUR)` });
            console.warn('⚠️ Période précédente non disponible:', error);
            return null; // Ne pas faire échouer le dashboard pour cette erreur
          }
        })(),
        
        // OPTIMISATION: Appel timeline supprimé - données intégrées dans campaigns unifié
        // OPTIMISATION: Appel campaign-types supprimé - données intégrées dans campaigns unifié
        // Voir: backend/src/routes/dashboard/campaigns.js (endpoint unifié campagnes + timeline + types)

        // Endpoint unifié: campagnes + timeline + KPIs + types
        (async () => {
          const reqStart = performance.now();
          requestStartTimes[2] = reqStart;
          const response = await fetch(`${config.API_URL}/api/dashboard/components/campaigns`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          const duration = (reqEnd - reqStart).toFixed(0);
          performanceTimes.push({ endpoint: '/api/dashboard/components/campaigns', duration: `${duration}ms` });
          return response;
        })(),

        (async () => {
          const reqStart = performance.now();
          requestStartTimes[3] = reqStart;
          const response = await fetch(`${config.API_URL}/api/dashboard/components/ad-groups`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          const duration = (reqEnd - reqStart).toFixed(0);
          performanceTimes.push({ endpoint: '/api/dashboard/components/ad-groups', duration: `${duration}ms` });
          return response;
        })(),
        
        // Top/Flop Products
        (async () => {
          const reqStart = performance.now();
          requestStartTimes[5] = reqStart;
          const response = await fetch(`${config.API_URL}/api/dashboard/components/top-flop-products`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          const duration = (reqEnd - reqStart).toFixed(0);
          performanceTimes.push({ endpoint: '/api/dashboard/components/top-flop-products', duration: `${duration}ms` });
          return response;
        })(),
        
        // Geography
        (async () => {
          const reqStart = performance.now();
          requestStartTimes[6] = reqStart;
          const response = await fetch(`${config.API_URL}/api/dashboard/components/geography`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          const duration = (reqEnd - reqStart).toFixed(0);
          performanceTimes.push({ endpoint: '/api/dashboard/components/geography', duration: `${duration}ms` });
          return response;
        })()
      ];
      
      const [
        chartsResponse,
        previousPeriodResponse,
        campaignsResponse,  // Endpoint unifié: campagnes + timeline + KPIs + types
        adGroupsResponse,
        topFlopProductsResponse,
        geographyResponse
      ] = await Promise.all(requestPromises);
      
      const totalParallelTime = performance.now() - startTime;
      performanceTimes.push({ endpoint: 'TOTAL temps parallélisation', duration: `${totalParallelTime.toFixed(0)}ms` });

      // Étape 3: Chargement des données secondaires en parallèle (non-bloquant)
      
      // Lancer les requêtes non-critiques en arrière-plan sans attendre
      Promise.all([
        (async () => {
          const reqStart = performance.now();
          const response = await fetch(`${config.API_URL}/api/dashboard/components/devices`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          const duration = (reqEnd - reqStart).toFixed(0);
          performanceTimes.push({ endpoint: '/api/dashboard/components/devices', duration: `${duration}ms` });
          return response;
        })(),
        (async () => {
          const reqStart = performance.now();
          const response = await fetch(`${config.API_URL}/api/dashboard/components/weekdays`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          const duration = (reqEnd - reqStart).toFixed(0);
          performanceTimes.push({ endpoint: '/api/dashboard/components/weekdays', duration: `${duration}ms` });
          return response;
        })(),
        // Preload des composants modulaires (optimisation performance)
        (async () => {
          const reqStart = performance.now();
          const response = await fetch(`${config.API_URL}/api/dashboard/components/networks`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          performanceTimes.push({ endpoint: '/api/dashboard/components/networks', duration: `${(reqEnd - reqStart).toFixed(0)}ms` });
          return response;
        })(),
        (async () => {
          const reqStart = performance.now();
          const response = await fetch(`${config.API_URL}/api/dashboard/components/top-flop-categories`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          performanceTimes.push({ endpoint: '/api/dashboard/components/top-flop-categories', duration: `${(reqEnd - reqStart).toFixed(0)}ms` });
          return response;
        })(),
        (async () => {
          const reqStart = performance.now();
          const response = await fetch(`${config.API_URL}/api/dashboard/components/age-ranges`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          performanceTimes.push({ endpoint: '/api/dashboard/components/age-ranges', duration: `${(reqEnd - reqStart).toFixed(0)}ms` });
          return response;
        })(),
        (async () => {
          const reqStart = performance.now();
          const response = await fetch(`${config.API_URL}/api/dashboard/components/gender`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          performanceTimes.push({ endpoint: '/api/dashboard/components/gender', duration: `${(reqEnd - reqStart).toFixed(0)}ms` });
          return response;
        })(),
        (async () => {
          const reqStart = performance.now();
          const response = await fetch(`${config.API_URL}/api/dashboard/components/audiences`, {
            ...requestConfig,
            body: JSON.stringify(baseRequestBody)
          });
          const reqEnd = performance.now();
          performanceTimes.push({ endpoint: '/api/dashboard/components/audiences', duration: `${(reqEnd - reqStart).toFixed(0)}ms` });
          return response;
        })(),
        (async () => {
          const reqStart = performance.now();
          const response = await fetch(`${config.API_URL}/api/dashboard/search-terms`, {
            ...requestConfig,
            body: JSON.stringify({ ...baseRequestBody, limit: 10000, minClicks: 1 })
          });
          const reqEnd = performance.now();
          performanceTimes.push({ endpoint: '/api/dashboard/search-terms', duration: `${(reqEnd - reqStart).toFixed(0)}ms` });
          return response;
        })()
      ]).then(async ([devicesResponse, weekdaysResponse, networksResponse, categoriesResponse, ageRangesResponse, genderResponse, audiencesResponse, searchTermsResponse]) => {
        // Traiter les données secondaires de manière asynchrone
        if (devicesResponse.ok) {
          const devicesResult = await devicesResponse.json();
          console.log('🔍 [DEVICES] Réponse reçue:', devicesResult);
          console.log('🔍 [DEVICES] Structure détaillée:', {
            success: devicesResult.success,
            hasCharts: !!devicesResult.charts,
            chartsLength: devicesResult.charts?.length,
            charts: devicesResult.charts,
            hasRawData: !!devicesResult.rawData,
            rawDataKeys: devicesResult.rawData ? Object.keys(devicesResult.rawData) : []
          });
          if (devicesResult.success) {
            console.log('✅ [DEVICES] Définition de devicesData avec:', devicesResult);
            setDevicesData(devicesResult);
          } else {
            console.error('❌ [DEVICES] Réponse unsuccessful:', devicesResult);
          }
        } else {
          console.error('❌ [DEVICES] Requête failed:', devicesResponse.status);
        }
        if (weekdaysResponse.ok) {
          const weekdaysResult = await weekdaysResponse.json();
          console.log('🔍 [WEEKDAYS] Réponse reçue (parallèle):', weekdaysResult);
          if (weekdaysResult.success) {
            console.log('✅ [WEEKDAYS] Définition de weekdaysData avec:', weekdaysResult);
            console.log('✅ [WEEKDAYS] Structure weekdaysResult (parallèle):', {
              hasCharts: !!weekdaysResult.charts,
              chartsLength: weekdaysResult.charts?.length,
              hasData: !!weekdaysResult.data,
              dataLength: weekdaysResult.data?.length
            });
            
            // Vérification de sécurité pour les charts
            if (weekdaysResult.charts && weekdaysResult.charts.length > 0) {
              console.log('✅ [WEEKDAYS] Charts valides trouvés, définition weekdaysData');
              setWeekdaysData(weekdaysResult);
            } else {
              console.log('⚠️ [WEEKDAYS] Aucun chart trouvé dans la réponse');
              console.log('📅 [WEEKDAYS] Réponse complète:', JSON.stringify(weekdaysResult, null, 2));
            }
          } else {
            console.error('❌ [WEEKDAYS] Réponse unsuccessful:', weekdaysResult);
            console.error('❌ [WEEKDAYS] Erreur détails:', weekdaysResult.error || 'Pas de détails d\'erreur');
          }
        } else {
          console.error('❌ [WEEKDAYS] Requête failed:', weekdaysResponse.status);
          const errorText = await weekdaysResponse.text();
          console.error('❌ [WEEKDAYS] Erreur texte:', errorText);
        }

        // Traitement des données préchargées (optimisation performance)
        if (networksResponse?.ok) {
          const networksResult = await networksResponse.json();
          if (networksResult.success) {
            console.log('✅ [PRELOAD] Networks data ready');
            setNetworksPreloadData(networksResult);
          }
        }
        if (categoriesResponse?.ok) {
          const categoriesResult = await categoriesResponse.json();
          if (categoriesResult.success) {
            console.log('✅ [PRELOAD] Top/Flop Categories data ready');
            setTopFlopCategoriesPreloadData(categoriesResult);
          }
        }
        if (ageRangesResponse?.ok) {
          const ageRangesResult = await ageRangesResponse.json();
          if (ageRangesResult.success) {
            console.log('✅ [PRELOAD] Age Ranges data ready');
            setAgeRangesPreloadData(ageRangesResult);
          }
        }
        if (genderResponse?.ok) {
          const genderResult = await genderResponse.json();
          if (genderResult.success) {
            console.log('✅ [PRELOAD] Gender data ready');
            setGenderPreloadData(genderResult);
          }
        }
        if (audiencesResponse?.ok) {
          const audiencesResult = await audiencesResponse.json();
          if (audiencesResult.success) {
            console.log('✅ [PRELOAD] Audiences data ready');
            setAudiencesPreloadData(audiencesResult);
          }
        }
        if (searchTermsResponse?.ok) {
          const searchTermsResult = await searchTermsResponse.json();
          if (searchTermsResult.success) {
            console.log('✅ [PRELOAD] Search Terms data ready');
            setSearchTermsPreloadData(searchTermsResult);
          }
        }
      }).catch(error => {
        console.error('⚠️ Erreur chargement données secondaires:', error);
        // Ne pas bloquer le dashboard principal pour ces erreurs
      });

      // Étape 4: Traitement des réponses critiques en parallèle

      const [
        chartsData,
        previousPeriodData,
        campaignsData,  // Endpoint unifié: contient campaigns + timeline + KPIs + types
        adGroupsData,
        topFlopProductsData,
        geographyData
      ] = await Promise.all([
        chartsResponse.json(),
        previousPeriodResponse ? previousPeriodResponse.json() : Promise.resolve(null),
        campaignsResponse.json(),
        adGroupsResponse.json(),
        topFlopProductsResponse.json(),
        geographyResponse.json()
      ]);

      // OPTIMISATION: Générer timelineData depuis campaignsData unifié (évite 1 requête HTTP)
      // campaignsData contient maintenant: { success, data, charts, rawData }
      // On génère un objet compatible avec l'ancien format timeline pour PerformanceChart
      let timelineData = { success: false, charts: [], rawData: {} };
      if (campaignsData.success) {
        timelineData = {
          success: true,
          charts: campaignsData.charts || [],  // Charts timeline du endpoint unifié
          rawData: campaignsData.rawData || {}  // rawData contient enrichedDailyData + metrics
        };
        console.log('📊 [OPTIM] timelineData généré depuis campaigns unifié:',
          timelineData.charts?.length || 0, 'charts,',
          Object.keys(timelineData.rawData).length, 'sources');
      }

      console.log('📊 Données reçues:', {
        charts: chartsData.success,
        previousPeriod: previousPeriodData?.success ?? 'non-chargé',
        campaigns: campaignsData.success + ' (unifié: campaigns + timeline + types)',
        adGroups: adGroupsData.success,
        topFlopProducts: topFlopProductsData.success,
        geography: geographyData.success
      });

      // Traiter la période précédente si disponible
      // IMPORTANT: Si gadsCompareEnabled est actif, le useEffect dédié charge les données complètes
      // Ne pas écraser avec les données lightweight qui n'incluent pas les campagnes
      if (previousPeriodData?.success) {
        if (gadsCompareEnabled) {
          console.log('⏭️ Données période précédente lightweight ignorées (comparaison activée, useEffect charge données complètes)');
          // Ne pas écraser - le useEffect chargera les données complètes avec campaigns
        } else {
          console.log('✅ Données période précédente intégrées (mode léger):', previousPeriodData.data);
          setPreviousPeriodData(previousPeriodData.data?.hasData !== false ? previousPeriodData.data : null);
        }
      } else if (!gadsCompareEnabled) {
        console.log('⚠️ Période précédente non disponible');
        setPreviousPeriodData(null);
      }
      
      // Étape 5: Fusion et traitement des données

      if (chartsData.success) {
        setAutoCharts(prevCharts => {
          // Dashboard Global : utiliser seulement les données charts (pas de search terms)
          const newRawData = { ...chartsData.rawData };
          
          return {
            ...chartsData,
            rawData: newRawData
          };
        });

        // Traiter les données des sections en parallèle
        if (timelineData.success) {
          console.log('📈 [TIMELINE-MAIN] Données timeline reçues:', {
            success: timelineData.success,
            chartsLength: timelineData.charts?.length || 0,
            charts: timelineData.charts,
            rawDataKeys: timelineData.rawData ? Object.keys(timelineData.rawData) : 'null'
          });
          setResultsData(timelineData);
        }
        // OPTIMISATION: campaignTypes supprimé - utiliser campaignsData.data[0].campaigns pour les types
        if (campaignsData.success && campaignsData.data) {
          setCampaignsData(campaignsData);
          // Sync campagnes en arrière-plan pour labels/objectifs
          // Extraire les campagnes depuis data[0] ou rawData (fallback)
          let allCampaigns = campaignsData.data[0]?.campaigns || [];
          if (allCampaigns.length === 0 && campaignsData.rawData) {
            // Fallback: prendre les campagnes du premier compte dans rawData
            const firstSource = Object.values(campaignsData.rawData)[0];
            allCampaigns = firstSource?.campaigns || [];
          }
          console.log('🔄 [CAMPAIGNS-SYNC] Campagnes extraites:', allCampaigns.length);
          syncCampaignsInBackground(token, accountToUse.sourceId || accountToUse.id, 'GOOGLE_ADS', allCampaigns);
        }
        if (adGroupsData.success && adGroupsData.data) {
          console.log('✅ [AD-GROUPS] Données reçues:', adGroupsData.data.length, 'datasets');
          setAdGroupsData(adGroupsData);
        } else {
          console.log('❌ [AD-GROUPS] Pas de données:', { success: adGroupsData.success, hasData: !!adGroupsData.data, dataLength: adGroupsData.data?.length });
        }
        if (topFlopProductsData.success) {
          setTopFlopProductsData(topFlopProductsData);
        }
        if (geographyData.success) {
          setGeographyData(geographyData);
        }
        
        // Étape 6: Finalisation
        clearInterval(progressTimer);
        setLoadingProgress(100);
        
        setError(''); // Clear any previous errors
        console.log(`✅ Dashboard généré: ${chartsData.charts?.length || 0} graphiques, ${chartsData.dataPoints || 0} points de données`);
        
      } else {
        clearInterval(progressTimer);
        setError(chartsData.error || 'Erreur génération dashboard');
      }
    } catch (error) {
      console.error('Erreur génération dashboard:', error);
      clearInterval(progressTimer);
      setError('Erreur réseau lors de la génération du dashboard');
    } finally {
      setLoadingCharts(false);
      setTimeout(() => setLoadingProgress(0), 500); // Réinitialiser le progrès avec délai
    }
  };

  // Synchroniser le breadcrumb quand un compte est sélectionné (Google Ads)
  useEffect(() => {
    if (selectedAccount) {
      console.log('🔄 [BREADCRUMB] Synchronisation compte Google Ads:', selectedAccount.name);
      window.dispatchEvent(new CustomEvent('accountSelected', {
        detail: { accountId: selectedAccount.id, accountName: selectedAccount.name }
      }));
    }
  }, [selectedAccount]);

  // Générer automatiquement le dashboard quand le compte, la période, le client ou les dates personnalisées changent
  useEffect(() => {

    if (selectedAccount && selectedSources.length > 0 && (selectedDashboardModel === 'global' || selectedDashboardModel === 'keywords')) {
      // Pour période CUSTOM, attendre que les deux dates soient définies
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      // Réinitialiser le flag de tentative quand on change de compte/période/client
      hasAttemptedSearchTermsLoadRef.current = null;
      generateAutoDashboard();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedDashboardModel]);

  // Refs pour tracker les filtres précédents
  const previousLabelsRef = useRef(selectedLabelsFilter);
  const previousCampaignFilterRef = useRef(selectedCampaignFilter);

  // Recharger les données filtrables quand le filtre campagne ou labels change
  useEffect(() => {
    if (!selectedAccount || !token) return;

    // Ne recharger que si on a déjà des données (dashboard déjà chargé)
    if (!autoCharts) return;

    const hadLabels = previousLabelsRef.current.length > 0;
    const hasLabels = selectedLabelsFilter.length > 0;
    const hadCampaignFilter = previousCampaignFilterRef.current !== null;
    const hasCampaignFilter = selectedCampaignFilter !== null;

    previousLabelsRef.current = selectedLabelsFilter;
    previousCampaignFilterRef.current = selectedCampaignFilter;

    // Recharger si: un filtre est actif OU si on vient de désélectionner un filtre
    const filtersCleared = (hadLabels && !hasLabels) || (hadCampaignFilter && !hasCampaignFilter);
    const hasActiveFilters = hasCampaignFilter || hasLabels;

    if (!hasActiveFilters && !filtersCleared) {
      console.log('🏷️ [LABELS-FILTER] Aucun filtre actif et pas de changement, skip reload');
      return;
    }

    // Vérifier que selectedAccount existe
    if (!selectedAccount?.id) {
      console.log('🏷️ [LABELS-FILTER] Pas de compte sélectionné, skip reload');
      return;
    }

    // Afficher l'état de chargement (comme dans Facebook Ads)
    setResultsData(null);
    setLoadingResults(true);

    const reloadFilteredSections = async () => {
      console.log('🎯 [CAMPAIGN-FILTER] Rechargement des sections avec filtre campagne:', selectedCampaignFilter?.name || 'aucun');
      console.log('🏷️ [LABELS-FILTER] Labels sélectionnés:', selectedLabelsFilter);
      console.log('🏷️ [LABELS-FILTER] campaignLabelsMap keys count:', Object.keys(campaignLabelsMap).length);

      const baseRequestBody = {
        sources: [selectedAccount.id],
        period: selectedPeriod,
        granularity: granularity
      };

      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) return;
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }

      if (selectedCampaignFilter) {
        baseRequestBody.campaignFilter = selectedCampaignFilter;
      }

      // Filtrer par labels : récupérer les IDs des campagnes qui ont les labels sélectionnés
      if (selectedLabelsFilter.length > 0 && Object.keys(campaignLabelsMap).length > 0) {
        console.log('🏷️ [LABELS-FILTER] Recherche campagnes avec labels:', selectedLabelsFilter);
        console.log('🏷️ [LABELS-FILTER] Exemple de campaignLabelsMap:', Object.entries(campaignLabelsMap).slice(0, 3));

        const matchingCampaignIds = Object.entries(campaignLabelsMap)
          .filter(([campaignId, labels]) =>
            selectedLabelsFilter.some(selectedLabel => labels.includes(selectedLabel))
          )
          .map(([campaignId]) => campaignId);

        console.log('🏷️ [LABELS-FILTER] Campagnes correspondantes:', matchingCampaignIds);
        console.log('🏷️ [LABELS-FILTER] Nombre de campagnes correspondantes:', matchingCampaignIds.length);

        if (matchingCampaignIds.length > 0) {
          baseRequestBody.campaignIds = matchingCampaignIds;
        } else {
          console.warn('⚠️ [LABELS-FILTER] Aucune campagne ne correspond aux labels sélectionnés!');
        }
      }

      console.log('🏷️ [LABELS-FILTER] baseRequestBody final:', JSON.stringify(baseRequestBody));

      const requestConfig = {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(baseRequestBody)
      };

      /**
       * Les bornes de la période **regardée** : c'est le serveur qui en dérive la précédente.
       *
       * ⚠️ Le calcul était recopié ici, et il sérialisait par `toISOString()` des dates
       * construites à minuit **local** — `new Date(2026, 9, 29)`. À Paris, minuit local tombe
       * la veille en UTC : la fenêtre partait donc du 15 au 28 octobre pour une période qui
       * allait du 16 au 29, et la comparaison portait sur des journées décalées d'un jour.
       */
      let startDate, endDate;

      if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
        startDate = customStartDate;
        endDate = customEndDate;
      } else {
        const plage = getDateRangeFromPeriod(selectedPeriod);
        startDate = plage.startDate;
        endDate = plage.endDate;
      }

      // Préparer le body pour la période précédente
      const previousPeriodBody = {
        accountId: selectedAccount.id,
        startDate,
        endDate,
        periodType: selectedPeriod, // Pour calculer correctement les périodes calendaires
        comparisonType: gadsComparisonType || 'previous_period' // Type de comparaison (période précédente ou année précédente)
      };
      if (selectedCampaignFilter) {
        previousPeriodBody.campaignFilter = selectedCampaignFilter;
      }
      if (baseRequestBody.campaignIds) {
        previousPeriodBody.campaignIds = baseRequestBody.campaignIds;
      }

      // Recharger campaigns (unifié), devices, weekdays et période précédente en parallèle
      try {
        const [campaignsResponse, devicesResponse, weekdaysResponse, previousPeriodResponse] = await Promise.all([
          fetch(`${config.API_URL}/api/dashboard/components/campaigns`, requestConfig),
          fetch(`${config.API_URL}/api/dashboard/components/devices`, requestConfig),
          fetch(`${config.API_URL}/api/dashboard/components/weekdays`, requestConfig),
          fetch(`${config.API_URL}/api/dashboard/previous-period-comparison`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify(previousPeriodBody)
          })
        ]);

        if (campaignsResponse.ok) {
          const campaignsResult = await campaignsResponse.json();
          if (campaignsResult.success) {
            console.log('✅ [CAMPAIGN-FILTER] Campaigns unifié rechargé avec filtre');
            // Générer timelineData depuis campaigns pour PerformanceChart
            const timelineResult = {
              success: true,
              charts: campaignsResult.charts || [],
              rawData: campaignsResult.rawData || {}
            };
            setResultsData(timelineResult);
            setCampaignsData(campaignsResult);
          }
        }

        if (devicesResponse.ok) {
          const devicesResult = await devicesResponse.json();
          if (devicesResult.success) {
            console.log('✅ [CAMPAIGN-FILTER] Devices rechargés avec filtre');
            setDevicesData(devicesResult);
          }
        }

        if (weekdaysResponse.ok) {
          const weekdaysResult = await weekdaysResponse.json();
          if (weekdaysResult.success && weekdaysResult.charts?.length > 0) {
            console.log('✅ [CAMPAIGN-FILTER] Weekdays rechargés avec filtre');
            setWeekdaysData(weekdaysResult);
          }
        }

        if (previousPeriodResponse.ok) {
          const previousPeriodResult = await previousPeriodResponse.json();
          if (previousPeriodResult.success && previousPeriodResult.data?.hasData !== false) {
            console.log('✅ [CAMPAIGN-FILTER] Période précédente rechargée avec filtre');
            setPreviousPeriodData(previousPeriodResult.data);
          } else {
            console.log('⚠️ [CAMPAIGN-FILTER] Période précédente sans données exploitables');
            setPreviousPeriodData(null);
          }
        }

        setLoadingResults(false);
      } catch (error) {
        console.error('❌ [CAMPAIGN-FILTER] Erreur rechargement:', error);
        setLoadingResults(false);
      }
    };

    reloadFilteredSections();
  }, [selectedCampaignFilter, selectedLabelsFilter, campaignLabelsMap, gadsComparisonType]);

  // Période précédente maintenant intégrée dans generateAutoDashboard pour éviter les blocages

  // Réinitialiser les termes de recherche quand la période change (pour forcer le rechargement)
  const previousPeriodRef = useRef(null);
  useEffect(() => {
    // Pour CUSTOM, ne pas effacer tant que les deux dates ne sont pas définies
    if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
      console.log(`🔄 [SEARCH-TERMS] Période CUSTOM sélectionnée, en attente des dates...`);
      return; // Ne pas effacer les données, attendre que l'utilisateur sélectionne les dates
    }

    // Vérifier si la période a changé
    const currentPeriodKey = `${selectedPeriod}_${customStartDate}_${customEndDate}`;
    if (previousPeriodRef.current && previousPeriodRef.current !== currentPeriodKey) {
      console.log(`🔄 [SEARCH-TERMS] Période changée de ${previousPeriodRef.current} vers ${currentPeriodKey}, réinitialisation des données`);
      // Réinitialiser le flag de tentative
      hasAttemptedSearchTermsLoadRef.current = null;
      // Effacer les données de termes de recherche pour forcer le rechargement
      if (selectedAccount && autoCharts?.rawData) {
        setAutoCharts(prevCharts => {
          if (!prevCharts?.rawData) return prevCharts;
          const updatedRawData = { ...prevCharts.rawData };
          const accountKey = selectedAccount.name || selectedAccount.id;
          if (updatedRawData[accountKey]) {
            updatedRawData[accountKey] = {
              ...updatedRawData[accountKey],
              searchTerms: null
            };
          }
          return {
            ...prevCharts,
            rawData: updatedRawData
          };
        });
      }
    }
    previousPeriodRef.current = currentPeriodKey;
  }, [selectedPeriod, customStartDate, customEndDate, selectedAccount]);

  // Auto-charger les termes de recherche si la section est ouverte ET si on n'est PAS dans le dashboard Global
  useEffect(() => {

    // Ne pas charger les termes de recherche dans le dashboard Global
    if (selectedDashboardModel === 'global') {
      return;
    }
    
    
    // Vérifier si on a des données pour le compte sélectionné spécifiquement
    const hasSearchTermsDataForAccount = autoCharts?.rawData && selectedAccount && 
      autoCharts.rawData[selectedAccount.name || selectedAccount.id]?.searchTerms?.searchTerms?.length > 0;
    
    // Vérifier si on a déjà fait une tentative de chargement pour ce compte/période
    const attemptKey = `${selectedAccount?.id}_${selectedPeriod}_${customStartDate}_${customEndDate}`;
    const hasAlreadyAttempted = hasAttemptedSearchTermsLoadRef.current === attemptKey;
    
    
    // Debug: vérifier les conditions
    console.log('🔍 [SEARCH-TERMS-DEBUG] Conditions:', {
      showSearchTerms,
      hasSelectedAccount: !!selectedAccount,
      selectedAccountId: selectedAccount?.id,
      hasSearchTermsDataForAccount,
      isLoadingSearchTermsRef: isLoadingSearchTermsRef.current,
      hasAlreadyAttempted,
      attemptKey,
      autoChartsRawData: !!autoCharts?.rawData
    });

    // Si la section est ouverte, qu'on a un compte sélectionné, mais qu'on n'a pas de données pour ce compte
    if (showSearchTerms && selectedAccount && !hasSearchTermsDataForAccount && !isLoadingSearchTermsRef.current && !hasAlreadyAttempted) {
      // Pour période CUSTOM, attendre que les deux dates soient définies
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      
      // Marquer comme en cours de chargement et enregistrer la tentative
      isLoadingSearchTermsRef.current = true;
      hasAttemptedSearchTermsLoadRef.current = attemptKey;
      loadAllSearchTerms();
    }
  }, [showSearchTerms, selectedAccount, selectedPeriod, customStartDate, customEndDate, autoCharts?.rawData, selectedDashboardModel]);

  // Forcer l'affichage quand les search terms sont chargés (sauf dans le dashboard Global)
  useEffect(() => {
    // Ne pas forcer l'affichage des termes de recherche dans le dashboard Global
    if (selectedDashboardModel === 'global') {
      return;
    }
    
    if (autoCharts?.rawData && selectedAccount) {
      const accountData = autoCharts.rawData[selectedAccount.name || selectedAccount.id];
      if (accountData?.searchTerms?.searchTerms?.length > 0) {
        if (!showSearchTerms) {
          setShowSearchTerms(true);
        }
      }
    }
  }, [autoCharts?.rawData, selectedAccount?.name, showSearchTerms, selectedDashboardModel]);

  // Charger tous les termes de recherche (OPTIMISÉ)
  const loadAllSearchTerms = async () => {
    if (!selectedAccount) {
      console.error('❌ [OPTIMIZED] Aucun compte sélectionné pour les termes de recherche');
      return;
    }

    console.log(`🚀 [OPTIMIZED] Début chargement de tous les termes de recherche...`);
    console.log(`🚀 [OPTIMIZED] Compte sélectionné:`, selectedAccount?.name || 'aucun');
    console.log(`🚀 [OPTIMIZED] Période:`, selectedPeriod);
    
    try {
      // Préparer le body de la requête avec métadonnées de campagne et filtre sur les clics
      const requestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod,
        limit: 300000,
        minClicks: 1 // Exclure les termes à 0 clics pour optimiser le chargement
      };

      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) {
          console.error('Dates personnalisées manquantes');
          return;
        }
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      
      const response = await fetch(`${API_URL}/api/dashboard/search-terms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Erreur ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      // Mettre à jour seulement les termes de recherche dans autoCharts
      if (data.success && data.searchTermsResults) {
        
        setAutoCharts(prevCharts => {
          
          if (!prevCharts) {
            return {
              charts: [],
              rawData: data.searchTermsResults
            };
          }
          
          // Mettre à jour les données des termes de recherche
          const updatedRawData = { ...prevCharts.rawData };
          Object.keys(data.searchTermsResults).forEach(accountName => {
            if (updatedRawData[accountName]) {
              updatedRawData[accountName].searchTerms = data.searchTermsResults[accountName];
            } else {
              updatedRawData[accountName] = { searchTerms: data.searchTermsResults[accountName] };
            }
          });

          return {
            ...prevCharts,
            rawData: updatedRawData
          };
        });

        // Forcer l'affichage de la section des termes de recherche
        if (!showSearchTerms) {
          setShowSearchTerms(true);
        }
      }

    } catch (error) {
      console.error(`❌ [OPTIMIZED] Erreur chargement des termes:`, error);
      setError(`Erreur lors du chargement des termes de recherche`);
    } finally {
      // Toujours remettre le flag à false après le chargement
      isLoadingSearchTermsRef.current = false;
      console.log(`🔄 [OPTIMIZED] Flag isLoadingSearchTermsRef remis à false`);
    }
  };

  // Exposer setLocalShowAIButton pour ce composant
  useEffect(() => {
    window.setDashboardAIButton = setLocalShowAIButton;
    return () => {
      delete window.setDashboardAIButton;
    };
  }, []);

  // Exposer la fonction de génération automatique pour la sidebar
  useEffect(() => {
    window.googleDashboardGenerateAuto = generateAutoDashboard;
    return () => {
      delete window.googleDashboardGenerateAuto;
    };
  }, [generateAutoDashboard]);

  // Exposer la fonction d'analyse IA et gérer la visibilité du bouton
  useEffect(() => {
    window.googleDashboardAIAnalysis = generateAIInsights;
    
    // Gérer la visibilité du bouton
    const shouldShowButton = (autoCharts && autoCharts.charts && autoCharts.charts.length > 0) || 
      (selectedDashboardModel === 'comparatif' && comparativeData && !loadingComparative) ||
      (selectedDashboardModel === 'shopping' && shoppingDashboardData && Object.keys(shoppingDashboardData).length > 0 && !loadingShoppingDashboard);
    setLocalShowAIButton(shouldShowButton);
    
    // Gérer l'état d'analyse
    if (window.setDashboardAIAnalyzing) {
      window.setDashboardAIAnalyzing(loadingInsights);
    }
  }, [autoCharts, selectedDashboardModel, comparativeData, loadingComparative, loadingInsights, shoppingDashboardData, loadingShoppingDashboard]);
  
  // Générer les insights IA (PAYANT)
  const generateAIInsights = async () => {
    // Vérifier l'accès à l'analyse IA
    if (!canAccessAnalyzer) {
      setError('Cette fonctionnalité nécessite un abonnement actif');
      return;
    }
    
    // Vérifier qu'on a des données selon le type de dashboard
    if (selectedDashboardModel === 'comparatif') {
      if (!comparativeData) {
        setError('Générez d\'abord le dashboard comparatif');
        return;
      }
    } else if (selectedDashboardModel === 'shopping') {
      if (!shoppingDashboardData || Object.keys(shoppingDashboardData).length === 0) {
        setError('Générez d\'abord le dashboard Shopping');
        return;
      }
    } else {
      if (!autoCharts || !autoCharts.rawData) {
        setError('Générez d\'abord le dashboard automatique');
        return;
      }
    }

    /*
     * Le panneau s'ouvre tout de suite, et l'analyse part avec lui.
     *
     * La modale demandait d'abord un brief : une question posée avant d'avoir rien vu, à
     * laquelle on répondait le plus souvent en la laissant vide. L'analyse arrive désormais
     * sans qu'on ait à la cadrer, et se précise dans la conversation — c'est là qu'on sait
     * quoi demander.
     */
    setPanneauAnalyseOuvert(true);
  };

  /**
   * Ce qu'on envoie à l'analyse : les données **déjà affichées**, selon le modèle en cours.
   *
   * Reprend à l'identique ce que construisait `submitAIAnalysis`. Les trois modèles n'ont pas
   * la même forme de données, et commenter le mauvais jeu reviendrait à décrire un écran que
   * le lecteur n'a pas sous les yeux.
   */
  /*
   * Les réponses du dashboard enveloppent leur contenu de façons différentes : tantôt
   * `data[0]`, tantôt `data`, tantôt la racine. Cette lecture tolérante évite d'écrire trois
   * chemins par composant — et un `undefined` ne lève pas, il disparaît simplement du JSON.
   */
  const lireBloc = (reponse) => reponse?.data?.[0] ?? reponse?.data ?? reponse;

  /*
   * Les blocs de l'écran, et ceux qui manquent à l'appel.
   *
   * Les composants se chargent en parallèle : ouvrir le panneau pendant le chargement donne une
   * photo incomplète. Plutôt que de la passer pour complète — ce que l'analyse ferait, faute de
   * savoir qu'il manque quelque chose —, on **nomme les absents**. Le modèle peut alors dire
   * « les appareils n'étaient pas chargés » au lieu d'inventer ou de prétendre qu'il faut
   * chercher ailleurs.
   *
   * Les libellés sont ceux de l'écran : ce sont eux que l'utilisateur emploiera dans sa question.
   */
  const composantsDuDashboard = () => {
    const bruts = {
      "Groupes d'annonces": adGroupsData,
      'Appareils': devicesData,
      'Jours de la semaine': weekdaysData,
      'Géographie': geographyData,
      'Réseaux': networksPreloadData,
      "Tranches d'âge": ageRangesPreloadData,
      'Genre': genderPreloadData,
      'Audiences': audiencesPreloadData,
      'Termes de recherche': searchTermsPreloadData,
      'Performance Max': performanceMaxData,
      'Performance Max — Canaux': channelsEvolutionData,
      'YouTube — Vidéos': youtubeVideosData,
      // Top et flop voyagent séparément : le serveur ne retient qu'un tableau par bloc, et les
      // deux listes sont également intéressantes — l'une n'est pas le reste de l'autre.
      'Produits — meilleurs': { lignes: lireBloc(topFlopProductsData)?.topProducts },
      'Produits — moins bons': { lignes: lireBloc(topFlopProductsData)?.flopProducts },
      'Catégories — meilleures': { lignes: lireBloc(topFlopCategoriesPreloadData)?.topCategories },
      'Catégories — moins bonnes': { lignes: lireBloc(topFlopCategoriesPreloadData)?.flopCategories }
    };

    const presents = {};
    const absents = [];
    for (const [libelle, bloc] of Object.entries(bruts)) {
      // Seules les lignes de performance partent : le bloc entier porte en plus des séries
      // jour par jour et des données brutes, soit 4 Mo mesurés pour les treize blocs réunis.
      const reduit = bloc ? lignesDuBloc(bloc) : null;
      if (reduit) presents[libelle] = reduit;
      else absents.push(libelle);
    }
    return { blocs: presents, absents };
  };

  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    let donnees;
    if (selectedDashboardModel === 'comparatif') {
      donnees = {
        sources: [selectedAccount?.id || selectedAccount],
        ...periode,
        comparativeData,
        dashboardType: 'comparatif',
        comparisonType
      };
    } else if (selectedDashboardModel === 'shopping') {
      donnees = {
        sources: [selectedAccount?.id || selectedAccount],
        ...periode,
        shoppingData: shoppingDashboardData,
        dashboardType: 'shopping'
      };
    } else {
      donnees = {
        sources: selectedSources,
        ...periode,
        charts: autoCharts?.charts,
        /*
         * Le détail par campagne, que seuls les `charts` ne portaient pas.
         *
         * Ils ne contiennent que des séries temporelles, agrégées tous comptes confondus : à la
         * question « quelles campagnes sont les plus rentables ? », l'analyse répondait qu'elle
         * n'avait que des totaux — alors que le tableau était à l'écran, juste à côté du panneau.
         */
        campaigns: campaignsData?.data?.[0]?.campaigns || [],
        /*
         * Tous les autres blocs de l'écran.
         *
         * L'analyse ne voyait que les séries temporelles et les campagnes : à « produits les plus
         * vendus », elle répondait n'avoir que du niveau campagne, le tableau affiché à côté
         * d'elle. On lui passe donc l'écran entier.
         *
         * Les blocs partent **tels quels** : aucun des dix-neuf n'a le même format, et c'est le
         * serveur qui sait les réduire, de façon générique (`compacterComposantsDashboard`). Un
         * bloc absent ou encore en chargement vaut `undefined` et disparaît à la sérialisation.
         *
         * Les libellés sont ceux de l'écran — ce sont eux que l'utilisateur emploiera dans sa
         * question, et le modèle doit pouvoir les relier à ce qu'il lit.
         */
        composants: composantsDuDashboard(),
        dashboardType: 'global'
      };
    }

    return {
      dashboardData: donnees,
      dashboardType: selectedDashboardModel,
      accountId: selectedAccount?.sourceId || selectedAccount?.id || null,
      accountName: selectedAccount?.name || null,
      ...periode
    };
  };

  // Soumettre l'analyse avec le brief utilisateur
  const submitAIAnalysis = async () => {
    setShowAiModal(false);
    setLoadingInsights(true);
    
    try {
      // Préparer les données selon le type de dashboard
      let requestBody;
      
      if (selectedDashboardModel === 'comparatif') {
        // Utiliser les données comparatives
        requestBody = {
          sources: [selectedAccount.id || selectedAccount],
          period: selectedPeriod,
          ...(selectedPeriod === 'CUSTOM' && {
            customStartDate: customStartDate,
            customEndDate: customEndDate
          }),
          comparativeData: comparativeData,
          dashboardType: 'comparatif',
          comparisonType: comparisonType,
          userBrief: userBrief
        };
      } else if (selectedDashboardModel === 'shopping') {
        // Utiliser les données Shopping
        requestBody = {
          sources: [selectedAccount.id || selectedAccount],
          period: selectedPeriod,
          ...(selectedPeriod === 'CUSTOM' && {
            customStartDate: customStartDate,
            customEndDate: customEndDate
          }),
          shoppingData: shoppingDashboardData,
          dashboardType: 'shopping',
          userBrief: userBrief
        };
      } else {
        // Utiliser les données globales
        requestBody = {
          sources: selectedSources,
          period: selectedPeriod,
          ...(selectedPeriod === 'CUSTOM' && {
            customStartDate: customStartDate,
            customEndDate: customEndDate
          }),
          charts: autoCharts.charts,
          dashboardType: 'global',
          userBrief: userBrief
        };
      }

      // Utiliser l'API d'analyse dashboard (sans MCP)
      const dashboardAnalysisBody = {
        dashboardData: requestBody,
        dashboardType: selectedDashboardModel,
        userBrief: userBrief,
        period: selectedPeriod,
        ...(selectedPeriod === 'CUSTOM' && {
          customStartDate: customStartDate,
          customEndDate: customEndDate
        })
      };

      const response = await fetch(`${API_URL}/api/analyze-dashboard`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(dashboardAnalysisBody)
      });

      const data = await response.json();
      console.log('🔍 Réponse Analyse Dashboard reçue:', data);

      if (data.success) {
        // Extraire les recommandations depuis l'analyse
        let recommendations = [];

        // Essayer d'extraire les recommandations depuis le texte d'analyse
        if (data.analysis) {
          const recMatch = data.analysis.match(/\*\*💡 Recommandations.*?\*\*([\s\S]*?)(?:\*\*|$)/);
          if (recMatch) {
            recommendations = recMatch[1]
              .split('\n')
              .map(line => line.replace(/^[•\-\d.]+\s*/, '').trim())
              .filter(line => line.length > 0);
          }
        }

        // Fallback si pas de recommandations trouvées
        if (recommendations.length === 0) {
          recommendations = [
            'Consultez l\'analyse détaillée ci-dessus pour les recommandations',
            'Optimisez les campagnes avec le meilleur ROI',
            'Surveillez les tendances et ajustez les enchères en conséquence'
          ];
        }

        const cleanInsights = {
          analysis: data.analysis || 'Analyse terminée avec succès',
          recommendations: recommendations,
          tokensUsed: data.tokensUsed || 0,
          cost: 0,
          approach: 'dashboard_analysis',
          dataTable: null,
          userQuery: userBrief,
          gaqlQuery: null,
          resource: data.result?.execution?.resource || null,
          dataPoints: data.result?.execution?.dataPoints || 0
        };
        
        console.log('🎯 cleanInsights.recommendations après mapping:', cleanInsights.recommendations);
        console.log('🎯 Type de cleanInsights.recommendations:', typeof cleanInsights.recommendations);
        console.log('🎯 Array.isArray(cleanInsights.recommendations):', Array.isArray(cleanInsights.recommendations));
        
        console.log('🔥 [GAQL-TRACE] setAiInsights va être appelé avec:', cleanInsights);
        console.log('🔥 [GAQL-TRACE] cleanInsights.gaqlQuery:', cleanInsights.gaqlQuery);
        console.log('🔥 [GAQL-TRACE] cleanInsights.approach:', cleanInsights.approach);
        
        setAiInsights(cleanInsights);
        
        // DEBUG: Forcer l'affichage d'une section debug
        setTimeout(() => {
          const debugElement = document.createElement('div');
          debugElement.id = 'gaql-debug-temp';
          debugElement.style.cssText = 'position: fixed; top: 10px; right: 10px; z-index: 9999; background: #fef3c7; border: 2px solid #f59e0b; padding: 16px; border-radius: 8px; font-size: 12px; max-width: 400px;';
          debugElement.innerHTML = `
            <h4>🔥 DEBUG GAQL:</h4>
            <pre>gaqlQuery: ${JSON.stringify(cleanInsights.gaqlQuery)}
userQuery: ${JSON.stringify(cleanInsights.userQuery)}  
approach: ${JSON.stringify(cleanInsights.approach)}</pre>
            <button onclick="this.parentElement.remove()" style="margin-top: 8px;">Fermer</button>
          `;
          
          // Supprimer l'ancien s'il existe
          const oldDebug = document.getElementById('gaql-debug-temp');
          if (oldDebug) oldDebug.remove();
          
          document.body.appendChild(debugElement);
        }, 1000);
        
        // Également peupler analysisResult pour que la section "📋 Données échantillon" affiche la GAQL
        const analysisResultForGaql = {
          gaqlQuery: data.result?.execution?.gaqlQuery || null,
          userQuery: data.result?.execution?.userQuery || null,
          resource: data.result?.execution?.resource || null,
          dataPoints: data.result?.execution?.dataPoints || 0,
          approach: data.approach || 'clean_architecture',
          sampleData: cleanInsights.dataTable?.rows || []
        };
        // Note: GAQL query will be displayed via aiInsights.gaqlQuery sections
        // that are already added around lines 9866, 9923, etc.
        
        setError('');
        console.log(`🤖 Clean Architecture - Insights générés: ${cleanInsights.tokensUsed} tokens utilisés`);
        console.log('🎯 aiInsights après adaptation Clean Architecture:', cleanInsights);
        console.log('🔍 [DEBUG-TABLE] dataTable dans cleanInsights:', cleanInsights.dataTable);
        console.log('🔍 [DEBUG-TABLE] data.result structure:', data.result);
        console.log('🔍 [DEBUG-GAQL] aiInsights.gaqlQuery:', cleanInsights.gaqlQuery);
        console.log('🔍 [DEBUG-GAQL] aiInsights.userQuery:', cleanInsights.userQuery);
        console.log('🔍 [DEBUG-GAQL] data.result.execution:', data.result?.execution);
        
        // Force re-render en attendant un tick
        setTimeout(() => {
          console.log('🔄 [FORCE-CHECK] Vérification aiInsights après setTimeout:', cleanInsights.recommendations);
        }, 100);
      } else {
        // `message` d'abord : `error` porte un code (`TOO_MANY_ANALYSES`), qui ne dit rien au
        // lecteur de ce qu'il doit faire.
        setError(data.message || data.error || 'Erreur génération insights avec Clean Architecture');
      }
    } catch (error) {
      console.error('Erreur insights IA:', error);
      setError('Erreur réseau lors de la génération des insights');
    } finally {
      setLoadingInsights(false);
      setUserBrief(''); // Réinitialiser le brief
    }
  };

  // Fonction pour charger les données comparatives
  const loadComparativeData = async () => {
    if (!selectedAccount) return;

    setLoadingComparative(true);
    setComparativeData(null); // Clear previous data immediately
    try {
      const requestBody = {
        sources: [selectedAccount.id || selectedAccount],
        period: selectedPeriod,
        comparisonType: comparisonType,
        // L'axe voyage avec la requête : six des neuf exigent deux appels API de plus, que le
        // serveur ne paie que s'ils sont demandés.
        segmentation: segmentationComparatif
      };

      if (selectedPeriod === 'CUSTOM' && customStartDate && customEndDate) {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      // Ajouter le filtre de campagne si sélectionné
      if (selectedCampaignFilter) {
        requestBody.campaignId = selectedCampaignFilter.id || selectedCampaignFilter.campaignId;
      }

      // Ajouter le filtre de labels si sélectionné (envoyer les IDs de campagnes filtrées)
      if (selectedLabelsFilter && selectedLabelsFilter.length > 0 && Object.keys(campaignLabelsMap).length > 0) {
        // Calculer les IDs de campagnes correspondant aux labels sélectionnés
        const filteredCampaignIds = Object.entries(campaignLabelsMap)
          .filter(([, labels]) => selectedLabelsFilter.some(selectedLabel => labels.includes(selectedLabel)))
          .map(([campaignId]) => campaignId);

        if (filteredCampaignIds.length > 0) {
          requestBody.labelFilteredCampaignIds = filteredCampaignIds;
          console.log(`🏷️ [COMPARATIF] Filtrage par labels: ${selectedLabelsFilter.join(', ')} -> ${filteredCampaignIds.length} campagnes`);
        }
      }

      const response = await fetch(`${API_URL}/api/dashboard/comparative`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      if (data.success) {
        setComparativeData(data.data);
        setError('');
        console.log('📊 Données comparatives chargées:', data.data);
      } else {
        setError(data.error || 'Erreur lors du chargement des données comparatives');
      }
    } catch (error) {
      console.error('Erreur chargement données comparatives:', error);
      setError('Erreur réseau lors du chargement des données comparatives');
    } finally {
      setLoadingComparative(false);
    }
  };

  // Fonction pour charger les données Performance Max
  const loadPerformanceMaxData = async () => {
    // Pour période CUSTOM, attendre que les deux dates soient définies
    if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
      console.log(`⏳ [PERFORMANCE MAX] Attente des dates personnalisées...`);
      return;
    }
    
    setLoadingPerformanceMax(true);
    try {
      const requestBody = {
        sources: [selectedAccount.id || selectedAccount],
        period: selectedPeriod,
        minClicks: 1, // Exclure les termes à 0 clics pour optimiser le chargement
        ...(selectedPeriod === 'CUSTOM' && {
          customStartDate,
          customEndDate
        })
      };

      const response = await fetch(`${API_URL}/api/dashboard/performance-max`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('🔍 RÉPONSE BACKEND COMPLÈTE:', data);
      if (data.success) {
        setPerformanceMaxData(data.data);
        setError('');
        console.log('📊 Données Performance Max chargées:', data.data);
        console.log('📊 Type de performanceMaxData:', typeof data.data);
        console.log('📊 Keys de performanceMaxData:', Object.keys(data.data));
        console.log('📊 Longueur des keys:', Object.keys(data.data).length);
        
        // Log détaillé de la première entrée
        const firstAccount = Object.values(data.data)[0];
        console.log('📊 PREMIER COMPTE DÉTAILLÉ:', firstAccount);
        
        // Les données Performance Max sont chargées
      } else {
        setError(data.error || 'Erreur lors du chargement des données Performance Max');
      }
    } catch (error) {
      console.error('Erreur chargement données Performance Max:', error);
      setError('Erreur réseau lors du chargement des données Performance Max');
    } finally {
      setLoadingPerformanceMax(false);
    }
  };

  // Fonction pour charger les données Display Performance Max
  const loadPerformanceMaxDisplayData = async () => {
    console.log('🎯 [PERFORMANCE-MAX-DISPLAY] ===== DÉBUT CHARGEMENT DISPLAY =====');
    console.log('🎯 [PERFORMANCE-MAX-DISPLAY] selectedAccount:', selectedAccount);
    console.log('🎯 [PERFORMANCE-MAX-DISPLAY] selectedPeriod:', selectedPeriod);
    
    // Vérifier les dates personnalisées
    if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
      console.log(`⏳ [PERFORMANCE-MAX-DISPLAY] Attente des dates personnalisées...`);
      return;
    }
    
    setLoadingPerformanceMaxDisplay(true);
    try {
      const requestBody = {
        sources: [selectedAccount.id || selectedAccount],
        period: selectedPeriod,
        ...(selectedPeriod === 'CUSTOM' && {
          customStartDate,
          customEndDate
        })
      };

      console.log('🎯 [PERFORMANCE-MAX-DISPLAY] Requête:', requestBody);

      const response = await fetch(`${API_URL}/api/dashboard/components/performance-max-display`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      if (data.success) {
        setPerformanceMaxDisplayData(data.data);
        console.log('✅ [PERFORMANCE-MAX-DISPLAY] Données Display chargées:', data.data);
      } else {
        console.error('❌ [PERFORMANCE-MAX-DISPLAY] Erreur:', data.error);
      }
    } catch (error) {
      console.error('❌ [PERFORMANCE-MAX-DISPLAY] Erreur réseau:', error);
    } finally {
      setLoadingPerformanceMaxDisplay(false);
    }
  };

  // Fonction pour charger les données du dashboard Shopping combiné
  const loadShoppingDashboardData = async () => {
    console.log('🛍️ [SHOPPING DASHBOARD] Début chargement données shopping combinées...');
    console.log('🛍️ [SHOPPING DASHBOARD] selectedAccount:', selectedAccount);
    
    setLoadingShoppingDashboard(true);
    setShoppingLoadingProgress(0);
    
    try {
      // Étape 1: Préparation
      await new Promise(resolve => setTimeout(resolve, 300));
      setShoppingLoadingProgress(15);
      
      const requestBody = {
        sources: [selectedAccount.id || selectedAccount],
        period: selectedPeriod,
        ...(selectedPeriod === 'CUSTOM' && {
          customStartDate,
          customEndDate
        }),
        // Paramètres de comparaison
        compareEnabled: pmaxCompareEnabled,
        comparisonType: pmaxComparisonType
      };

      console.log('🛍️ [SHOPPING DASHBOARD] Request body:', requestBody);
      console.log('🛍️ [SHOPPING DASHBOARD] Comparaison:', { pmaxCompareEnabled, pmaxComparisonType });

      // Étape 2: Connexion
      setShoppingLoadingProgress(30);

      // Simuler la progression pendant l'appel API
      let progressValue = 30;
      const progressInterval = setInterval(() => {
        progressValue += 5;
        if (progressValue <= 65) {
          setShoppingLoadingProgress(progressValue);
          if (progressValue >= 45) {
          }
        }
      }, 800);

      const response = await fetch(`${API_URL}/api/dashboard/shopping`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      clearInterval(progressInterval);

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      // Étape 3: Récupération
      setShoppingLoadingProgress(70);

      // Petit délai pour voir le message
      await new Promise(resolve => setTimeout(resolve, 200));

      setShoppingLoadingProgress(80);
      
      const data = await response.json();
      console.log('🛍️ [SHOPPING DASHBOARD] Réponse complète:', data);
      
      // Étape 4: Traitement
      setShoppingLoadingProgress(90);

      if (data.success) {
        // Étape 5: Finalisation
        setShoppingLoadingProgress(100);
        
        // Petite pause pour voir le message de succès
        await new Promise(resolve => setTimeout(resolve, 500));
        
        setShoppingDashboardData(data.data);
        setError('');
        console.log('🛍️ [SHOPPING DASHBOARD] Données chargées:', data.data);
        console.log('🔍 [SHOPPING DASHBOARD] Structure des données pour IA:', JSON.stringify(data.data, null, 2).substring(0, 2000) + '...');
        
        // Réinitialiser après succès
        setTimeout(() => {
          setLoadingShoppingDashboard(false);
          setShoppingLoadingProgress(0);
        }, 100);
      } else {
        setError(data.error || 'Erreur lors du chargement du dashboard Shopping');
        console.error('🛍️ [SHOPPING DASHBOARD] Erreur:', data.error);
      }
    } catch (error) {
      console.error('🛍️ [SHOPPING DASHBOARD] Erreur réseau:', error);
      setError(`Erreur lors du chargement du dashboard Shopping: ${error.message}`);
      setShoppingLoadingProgress(0);
      setTimeout(() => {
        setLoadingShoppingDashboard(false);
      }, 1000);
    }
  };

  // Fonction pour charger les données YouTube Videos
  const loadYoutubeVideosData = async () => {
    console.log('🎥 [YOUTUBE VIDEOS] Début chargement données YouTube Ads...');
    console.log('🎥 [YOUTUBE VIDEOS] selectedAccount:', selectedAccount);
    
    setLoadingYoutubeVideos(true);
    setProcessedYoutubeData(null); // Réinitialiser les données traitées pour afficher l'écran de chargement
    setIsYoutubeDataComplete(false); // Réinitialiser le flag de données complètes
    setYoutubeLoadingProgress(0);

    // Simuler la progression
    const progressTimer = setInterval(() => {
      setYoutubeLoadingProgress(prev => {
        if (prev >= 90) return prev;
        return prev + Math.random() * 15;
      });
    }, 300);
    
    try {
      const requestBody = {
        sources: [selectedAccount.id || selectedAccount],
        period: selectedPeriod,
        ...(selectedPeriod === 'CUSTOM' && {
          customStartDate,
          customEndDate
        })
      };

      console.log('🎥 [YOUTUBE VIDEOS] Request body:', requestBody);
      
      setYoutubeLoadingProgress(30);

      const response = await fetch(`${API_URL}/api/dashboard/components/youtube-videos`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      
      if (response.ok && data.success) {
        setYoutubeLoadingProgress(100);

        // Vérifier si les données contiennent réellement des vidéos
        // Le backend renvoie un tableau d'objets { accountId, accountName, videos: [] }
        let hasActualVideos = false;
        if (Array.isArray(data.data)) {
          // Vérifier si au moins un compte a des vidéos
          hasActualVideos = data.data.some(account =>
            account.videos && Array.isArray(account.videos) && account.videos.length > 0
          );
        }

        console.log('🎥 [YOUTUBE VIDEOS] hasActualVideos:', hasActualVideos, 'data:', data.data);

        if (hasActualVideos) {
          setYoutubeVideosData(data.data);
          setError('');
          console.log('🎥 [YOUTUBE VIDEOS] Données chargées:', data.data);
        } else {
          // Pas de vidéos YouTube pour ce compte
          console.log('🎥 [YOUTUBE VIDEOS] Aucune vidéo YouTube trouvée pour ce compte');
          setYoutubeVideosData(null);
          setError('');
        }

        // Réinitialiser après succès - TOUJOURS arrêter le loading
        setTimeout(() => {
          setLoadingYoutubeVideos(false);
          setYoutubeLoadingProgress(0);
        }, 100);
      } else {
        console.error('🎥 [YOUTUBE VIDEOS] Erreur:', data.error);
        setError(data.error || 'Erreur lors du chargement des données YouTube Ads');
        // Réinitialiser le chargement en cas d'erreur pour afficher l'écran "pas de données"
        setYoutubeVideosData(null);
        setTimeout(() => {
          setLoadingYoutubeVideos(false);
          setYoutubeLoadingProgress(0);
        }, 500);
      }
    } catch (error) {
      console.error('🎥 [YOUTUBE VIDEOS] Erreur réseau:', error);
      setError(`Erreur lors du chargement des données YouTube Ads: ${error.message}`);
      setYoutubeLoadingProgress(0);
      setTimeout(() => {
        setLoadingYoutubeVideos(false);
      }, 1000);
    } finally {
      clearInterval(progressTimer);
    }
  };

  // Fonction pour calculer les totaux des vidéos Performance Max
  const handlePerformanceMaxVideoData = useCallback((videosData) => {
    console.log('🎬 [VIDEO CALLBACK] Callback reçu avec données vidéos:', videosData?.length || 0);
    
    if (!videosData || videosData.length === 0) {
      console.log('🎬 [VIDEO CALLBACK] Aucune donnée vidéo reçue, réinitialisation');
      setPerformanceMaxVideoTotals(null);
      return;
    }
    
    // Stocker les données individuelles des vidéos pour la répartition par campagne

    // Calculer les totaux comme dans YoutubeVideosTable
    const totals = videosData.reduce((acc, video) => ({
      impressions: acc.impressions + (video.impressions || 0),
      views: acc.views + (video.views || 0),
      clicks: acc.clicks + (video.clicks || 0),
      cost: acc.cost + (video.cost || 0),
      conversions: acc.conversions + (video.conversions || 0),
      conversionsValue: acc.conversionsValue + (video.conversionsValue || 0),
      interactions: acc.interactions + (video.interactions || 0)
    }), { impressions: 0, views: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, interactions: 0 });

    console.log('🎬 [VIDEO TOTALS] Totaux calculés pour vidéos Performance Max:', totals);
    console.log('🎬 [VIDEO TOTALS] État performanceMaxVideoTotals avant mise à jour:', performanceMaxVideoTotals);
    
    setPerformanceMaxVideoTotals(totals);
    
    console.log('🎬 [VIDEO TOTALS] setPerformanceMaxVideoTotals appelé avec:', totals);
  }, [performanceMaxVideoTotals]);

  // Fonction pour charger les données shopping Performance Max
  const loadShoppingData = async () => {
    
    // Pour période CUSTOM, attendre que les deux dates soient définies
    if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
      console.log(`⏳ [SHOPPING DATA] Attente des dates personnalisées...`);
      return;
    }
    
    try {
      const requestBody = {
        sources: [selectedAccount.id || selectedAccount],
        period: selectedPeriod,
        ...(selectedPeriod === 'CUSTOM' && {
          customStartDate,
          customEndDate
        })
      };

      const response = await fetch(`${API_URL}/api/dashboard/components/performance-max-products`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('🛒 Réponse API shopping:', data);
      if (data.success && data.data && data.data.campaigns) {
        // Adapter la structure pour correspondre à ce qui est attendu dans la réconciliation
        // L'endpoint performance-max-products retourne { campaigns: [...] } 
        // On doit le transformer en { accountId: { campaigns: [...] } }
        const adaptedData = {
          [selectedAccount.id]: {
            campaigns: data.data.campaigns,
            summary: {
              totalCampaigns: data.data.totalCampaigns || data.data.campaigns.length
            }
          }
        };
        
        setShoppingData(adaptedData);
        setError('');
        console.log('📊 Données shopping chargées et adaptées:', adaptedData);
        console.log('📊 Nombre de campagnes Shopping:', data.data.campaigns.length);
        data.data.campaigns.forEach(campaign => {
          console.log(`🛒 Campagne Shopping: ${campaign.name} - Coût: ${campaign.cost}€`);
        });
      } else {
        setError(data.error || 'Erreur lors du chargement des données shopping');
      }
    } catch (error) {
      console.error('Erreur chargement données shopping:', error);
      setError('Erreur réseau lors du chargement des données shopping');
    } finally {
    }
  };

  // Fonction pour charger les données de répartition par canal Performance Max
  const loadChannelsData = async () => {
    // Pour période CUSTOM, attendre que les deux dates soient définies
    if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
      console.log(`⏳ [CHANNELS DATA] Attente des dates personnalisées...`);
      return;
    }

    try {
      const requestBody = {
        sources: [selectedAccount.id || selectedAccount],
        period: selectedPeriod,
        ...(selectedPeriod === 'CUSTOM' && {
          customStartDate,
          customEndDate
        })
      };

      const response = await fetch(`${API_URL}/api/dashboard/components/performance-max-channels`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('📊 [CHANNELS] Réponse API channels:', data);

      if (data.success && data.data && data.data.channels) {
        // Extraire les campagnes avec leur répartition par canal
        const allCampaigns = data.data.channels.flatMap(source => source.campaigns || []);
        setPerformanceMaxChannelsData(allCampaigns);
        console.log('📊 [CHANNELS] Données channels chargées:', allCampaigns.length, 'campagnes');
      } else {
        console.error('📊 [CHANNELS] Erreur API:', data.error);
        setPerformanceMaxChannelsData([]);
      }
    } catch (error) {
      console.error('📊 [CHANNELS] Erreur chargement données channels:', error);
      setPerformanceMaxChannelsData([]);
    } finally {
    }
  };

  // Fonction pour charger l'évolution des coûts par canal Performance Max
  // Paramètre optionnel campaignIds pour filtrer par campagnes spécifiques
  const loadChannelsEvolutionData = async (campaignIds = null) => {
    if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
      return;
    }

    setLoadingChannelsEvolution(true);
    try {
      const requestBody = {
        sources: [selectedAccount.id || selectedAccount],
        period: selectedPeriod,
        ...(selectedPeriod === 'CUSTOM' && {
          customStartDate,
          customEndDate
        }),
        // Ajouter les campaignIds si un filtre est actif
        ...(campaignIds && campaignIds.length > 0 && { campaignIds })
      };

      console.log('📈 [CHANNELS-EVOLUTION] Requête avec filtrage:', campaignIds?.length || 'aucun filtre');

      const response = await fetch(`${API_URL}/api/dashboard/components/performance-max-channels-evolution`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('📈 [CHANNELS-EVOLUTION] Réponse API:', data);

      if (data.success && data.data) {
        setChannelsEvolutionData(data.data);
        console.log('📈 [CHANNELS-EVOLUTION] Données chargées:', data.data.evolution?.length, 'jours');
      } else {
        console.error('📈 [CHANNELS-EVOLUTION] Erreur API:', data.error);
        setChannelsEvolutionData(null);
      }
    } catch (error) {
      console.error('📈 [CHANNELS-EVOLUTION] Erreur chargement:', error);
      setChannelsEvolutionData(null);
    } finally {
      setLoadingChannelsEvolution(false);
    }
  };

  // Effect pour charger les données comparatives quand le modèle comparatif est sélectionné
  useEffect(() => {
    // Ne pas charger si le compte n'a pas de données sur la période
    if (accountHasNoData) {
      console.log('⚡ [COMPARATIF] Compte sans données, skip chargement');
      return;
    }

    if (selectedDashboardModel === 'comparatif' && selectedAccount) {
      loadComparativeData();
      // Charger aussi les campagnes pour le filtre si pas déjà chargées
      if (!campaignsData) {
        loadCampaignsSection();
      }
    }
    // `segmentationComparatif` déclenche le rechargement : sans lui, choisir « Appareils »
    // laisserait `evolutionAxe` à null et le tableau vide.
  }, [selectedDashboardModel, selectedAccount, selectedPeriod, customStartDate, customEndDate, comparisonType, selectedCampaignFilter, selectedLabelsFilter, accountHasNoData, segmentationComparatif]);

  // Effect pour charger les données Performance Max et Shopping
  useEffect(() => {
    console.log('🔄 [SHOPPING EFFECT] Triggered with:', { selectedDashboardModel, pmaxCompareEnabled, pmaxComparisonType, accountHasNoData });

    // Ne pas charger si le compte n'a pas de données sur la période
    if (accountHasNoData) {
      console.log('⚡ [SHOPPING EFFECT] Compte sans données, skip chargement');
      return;
    }

    if (selectedDashboardModel === 'performance-max' && selectedAccount) {
      loadPerformanceMaxData();
      loadPerformanceMaxDisplayData();
      // Charger aussi les données shopping pour la table shopping
      loadShoppingData();
      // Charger les données de répartition par canal
      loadChannelsData();
      // Charger l'évolution des coûts par canal
      loadChannelsEvolutionData();
    } else if (selectedDashboardModel === 'shopping' && selectedAccount) {
      // Pour période CUSTOM, attendre que les deux dates soient définies
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        console.log(`⏳ [SHOPPING] Attente des dates personnalisées...`);
        return;
      }
      console.log('🛍️ [SHOPPING EFFECT] Loading shopping data with comparison:', { pmaxCompareEnabled, pmaxComparisonType });
      loadShoppingDashboardData();
    } else if (selectedDashboardModel === 'youtube-ads' && selectedAccount) {
      // Pour période CUSTOM, attendre que les deux dates soient définies
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        console.log(`⏳ [YOUTUBE-ADS] Attente des dates personnalisées...`);
        return;
      }
      loadYoutubeVideosData();
    }
  }, [selectedDashboardModel, selectedAccount, selectedPeriod, customStartDate, customEndDate, pmaxCompareEnabled, pmaxComparisonType, accountHasNoData]);

  // Effect pour gérer le retour au shopping dashboard après ajout d'un Merchant Center
  useEffect(() => {
    const handleReloadShoppingDashboard = (event) => {
      const { sourceId } = event.detail || {};
      console.log('🛍️ [SHOPPING RELOAD] Événement reçu, sourceId:', sourceId);

      if (sourceId) {
        // Chercher le compte correspondant dans les libraryItems
        const account = libraryItemsRef.current.find(item => String(item.id) === String(sourceId));
        if (account) {
          console.log('🛍️ [SHOPPING RELOAD] Compte trouvé:', account.name);
          // Forcer le rechargement en vidant les données et en sélectionnant le compte
          setShoppingDashboardData(null);
          setSelectedAccount(account);
        } else {
          // Si pas trouvé, créer un objet minimal avec l'ID
          console.log('🛍️ [SHOPPING RELOAD] Compte non trouvé, utilisation de l\'ID:', sourceId);
          setShoppingDashboardData(null);
          setSelectedAccount({ id: sourceId });
        }
      }
    };

    window.addEventListener('reloadShoppingDashboard', handleReloadShoppingDashboard);
    return () => {
      window.removeEventListener('reloadShoppingDashboard', handleReloadShoppingDashboard);
    };
  }, []);

  // Effect pour recharger l'évolution des coûts quand le filtre PMAX change
  useEffect(() => {
    if (selectedDashboardModel === 'performance-max' && selectedAccount && performanceMaxChannelsData.length > 0) {
      // Calculer les IDs des campagnes filtrées
      let filteredCampaignIds = null;

      if (pmaxCampaignFilter || pmaxLabelsFilter.length > 0) {
        const filterPmaxCampaignForEvolution = (campaign) => {
          const campaignId = (campaign.id || campaign.campaignId || '').toString();
          const campaignName = campaign.name || campaign.campaignName || '';

          if (pmaxCampaignFilter) {
            const filterId = (pmaxCampaignFilter.id || pmaxCampaignFilter.campaignId || '').toString();
            const filterName = pmaxCampaignFilter.name || pmaxCampaignFilter.campaignName || '';
            if (campaignId !== filterId && campaignName !== filterName) return false;
          }

          if (pmaxLabelsFilter.length > 0) {
            const campaignLabels = campaignLabelsMap[campaignId] || [];
            if (!pmaxLabelsFilter.some(label => campaignLabels.includes(label))) return false;
          }

          return true;
        };

        filteredCampaignIds = performanceMaxChannelsData
          .filter(filterPmaxCampaignForEvolution)
          .map(c => c.id || c.campaignId)
          .filter(Boolean);

        console.log('📈 [PMAX-FILTER] Rechargement évolution avec', filteredCampaignIds.length, 'campagnes filtrées');
      }

      // Recharger avec le filtre (ou sans filtre si filteredCampaignIds est null)
      loadChannelsEvolutionData(filteredCampaignIds);
    }
  }, [pmaxCampaignFilter, pmaxLabelsFilter, performanceMaxChannelsData.length]);

  // Effect pour charger les données Performance Max seulement pour le dashboard termes de recherche (pour la section combinée)
  useEffect(() => {
    if (selectedDashboardModel === 'search-terms' && selectedAccount) {
      // Pour période CUSTOM, attendre que les deux dates soient définies
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        console.log(`⏳ [GLOBAL-PERFORMANCE-MAX] Attente des dates personnalisées...`);
        return;
      }
      console.log(`🚀 [GLOBAL-PERFORMANCE-MAX] Chargement automatique des données Performance Max pour section combinée`);
      loadPerformanceMaxData();
    }
  }, [selectedDashboardModel, selectedAccount, selectedPeriod, customStartDate, customEndDate]);

  // Effect pour charger les données Performance Max pour le dashboard Google Ads global (pour TopFlopSearchTermsTable)
  // Différé de 3 secondes pour ne pas bloquer le chargement initial du dashboard
  useEffect(() => {
    let timeoutId = null;

    if (selectedDashboardModel === 'global' && selectedAccount && !accountHasNoData) {
      // Pour période CUSTOM, attendre que les deux dates soient définies
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        console.log(`⏳ [GLOBAL-PERFORMANCE-MAX-GOOGLE-ADS] Attente des dates personnalisées...`);
        return;
      }

      // Différer le chargement de 3 secondes pour prioriser l'affichage initial du dashboard
      console.log(`⏳ [GLOBAL-PERFORMANCE-MAX-GOOGLE-ADS] Chargement différé des données Performance Max (dans 3s)...`);
      timeoutId = setTimeout(() => {
        console.log(`🚀 [GLOBAL-PERFORMANCE-MAX-GOOGLE-ADS] Chargement des données Performance Max pour dashboard Google Ads global`);
        loadPerformanceMaxData();
      }, 3000);
    }

    // Cleanup: annuler le timeout si les dépendances changent
    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [selectedDashboardModel, selectedAccount, selectedPeriod, customStartDate, customEndDate, accountHasNoData]);
  
  // OBSOLÈTE - Effect pour charger les tops et flops produits dans le dashboard global
  // REMPLACÉ PAR: TopFlopProductsTableModular qui gère son propre chargement avec useEffect
  /*
  useEffect(() => {
    if (selectedDashboardModel === 'global' && selectedAccount) {
      // Pour période CUSTOM, attendre que les deux dates soient définies
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        console.log(`⏳ [TOP-FLOP-PRODUCTS] Attente des dates personnalisées...`);
        return;
      }
      console.log(`🚀 [TOP-FLOP-PRODUCTS] Chargement automatique des tops/flops produits`);
      loadTopFlopProducts(selectedCampaignFilter);
    }
  }, [selectedDashboardModel, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedCampaignFilter]);
  */

  // Réinitialiser la pagination des produits, marques et libellés lors du changement de période
  useEffect(() => {
    setProductsCurrentPage(1);
    setBrandsCurrentPage(1);
    setLabelsCurrentPage(1);
    // Vider les données shopping pour forcer le rechargement avec la nouvelle période
    setShoppingDashboardData(null);
    // Réinitialiser les filtres
    setSelectedCountryFilter('all');
    setSelectedLabelFilter('all');
    setSelectedShoppingCampaignFilter('all');
    setSelectedProductFilter(null);
    setProductSearchQuery('');
  }, [selectedPeriod, customStartDate, customEndDate]);

  // Fonction pour rendre un tableau comparatif par type de campagne
  const renderComparativeTable = (data, comparisonType, periodInfo = null) => {
    // Générer des labels de période dynamiques
    const getPeriodLabel = () => {
      if (!periodInfo) return comparisonType === 'previous_year' ? 'Année N-1' : 'Période N-1';
      
      const { period, customStartDate, customEndDate, previousStartDate, previousEndDate } = periodInfo;
      
      // Pour la période précédente
      if (previousStartDate && previousEndDate) {
        const start = new Date(previousStartDate);
        const end = new Date(previousEndDate);
        
        // Si c'est la même année et des dates proches, afficher le format court
        if (start.getFullYear() === end.getFullYear()) {
          if (start.getMonth() === end.getMonth()) {
            return `${start.getDate()}-${end.getDate()} ${start.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}`;
          }
          return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
        }
        return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' })}`;
      }
      
      return comparisonType === 'previous_year' ? 'Année précédente' : 'Période précédente';
    };
    
    const getCurrentPeriodLabel = () => {
      if (!periodInfo) return 'Période N';
      
      const { period, customStartDate, customEndDate } = periodInfo;
      
      // Périodes prédéfinies
      const periodLabels = {
        'TODAY': "Aujourd'hui",
        'YESTERDAY': 'Hier',
        'LAST_7_DAYS': '7 derniers jours',
        'LAST_14_DAYS': '14 derniers jours',
        'LAST_30_DAYS': '30 derniers jours',
        'LAST_60_DAYS': '60 derniers jours',
        'LAST_90_DAYS': '90 derniers jours',
        'LAST_180_DAYS': '180 derniers jours',
        'LAST_365_DAYS': '365 derniers jours',
        'THIS_MONTH': 'Ce mois',
        'LAST_MONTH': 'Mois dernier',
        'THIS_YEAR': 'Année en cours',
        'LAST_YEAR': 'Année dernière'
      };
      
      if (period === 'CUSTOM' && customStartDate && customEndDate) {
        const start = new Date(customStartDate);
        const end = new Date(customEndDate);
        
        if (start.getFullYear() === end.getFullYear() && start.getMonth() === end.getMonth()) {
          return `${start.getDate()}-${end.getDate()} ${start.toLocaleDateString('fr-FR', { month: 'short', year: 'numeric' })}`;
        }
        return `${start.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })} - ${end.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' })}`;
      }
      
      return periodLabels[period] || 'Période actuelle';
    };
    
    const periodLabel = getPeriodLabel();
    const currentPeriodLabel = getCurrentPeriodLabel();
    
    /**
     * Les lignes du comparatif suivent la configuration de l'utilisateur, comme les tableaux
     * du dashboard global. Deux nommages coexistent et doivent être réconciliés ici : la
     * configuration parle en clés françaises (`clics`, `cout`, `conv`…), les données
     * comparatives en clés d'API (`clicks`, `cost`, `conversions`…).
     */
    const metrics = (metriquesComparatif.order || [])
      .filter(cle => metriquesComparatif.metrics.includes(cle) && CORRESPONDANCE_COMPARATIF[cle])
      .map(cle => CORRESPONDANCE_COMPARATIF[cle]);

    const formatValue = (value, format) => {
      if (value === null || value === undefined || isNaN(value)) return '-';

      switch (format) {
        case 'number':
          return Math.round(value).toLocaleString('fr-FR');
        case 'conversions':
          return value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 });
        case 'percentage':
          return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
        case 'currency':
          return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}€`;
        // Un CPV se compte en centimes : deux décimales l'écraseraient à 0,00 €.
        case 'cpv':
          return `${value.toLocaleString('fr-FR', { minimumFractionDigits: 3, maximumFractionDigits: 3 })}€`;
        case 'decimal':
          return value.toLocaleString('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        default:
          return value;
      }
    };

    const formatEvolution = (evolution, metricType = 'normal') => {
      if (evolution === null || evolution === undefined || isNaN(evolution)) return '-';
      const formatted = `${evolution > 0 ? '+' : ''}${evolution.toLocaleString('fr-FR', { minimumFractionDigits: 1, maximumFractionDigits: 1 })}%`;
      
      // Pour les métriques de coût (COS, CPA, Coût, CPC), une hausse est mauvaise (rouge) et une baisse est bonne (vert)
      const isCostMetric = ['cos', 'cpa', 'cost', 'cpc'].includes(metricType.toLowerCase());
      let color;
      
      if (evolution > 0) {
        color = isCostMetric ? '#ef4444' : '#10b981'; // Rouge pour coûts en hausse, vert pour autres métriques en hausse
      } else if (evolution < 0) {
        color = isCostMetric ? '#10b981' : '#ef4444'; // Vert pour coûts en baisse, rouge pour autres métriques en baisse  
      } else {
        color = '#6b7280'; // Gris pour pas de changement
      }
      
      const icon = evolution > 0 ? '↗️' : evolution < 0 ? '↘️' : '→';
      
      return (
        <span style={{ color: color, fontWeight: '600' }}>
          {icon} {formatted}
        </span>
      );
    };

    return (
      <div className="comparative-table" style={{
        marginBottom: '32px',
        backgroundColor: 'white',
        borderRadius: '12px',
        overflow: 'hidden'
      }}>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          tableLayout: 'fixed'
        }}>
          <thead>
            <tr style={{ backgroundColor: '#FAF9F5' }}>
              <th style={{
                padding: '12px 16px',
                textAlign: 'left',
                fontWeight: '600',
                color: '#374151',
                borderBottom: '2px solid #e5e7eb',
                width: '25%',
                maxWidth: '25%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                Métrique
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'right',
                fontWeight: '600',
                color: '#374151',
                borderBottom: '2px solid #e5e7eb',
                width: '25%',
                maxWidth: '25%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {periodLabel}
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'right',
                fontWeight: '600',
                color: '#374151',
                borderBottom: '2px solid #e5e7eb',
                width: '25%',
                maxWidth: '25%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                {currentPeriodLabel}
              </th>
              <th style={{
                padding: '12px 16px',
                textAlign: 'right',
                fontWeight: '600',
                color: '#374151',
                borderBottom: '2px solid #e5e7eb',
                width: '25%',
                maxWidth: '25%',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap'
              }}>
                Évolution (%)
              </th>
            </tr>
          </thead>
          <tbody>
            {metrics.map((metric, index) => (
              <tr key={metric.key} style={{
                backgroundColor: index % 2 === 0 ? 'white' : '#FAF9F5'
              }}>
                <td style={{
                  padding: '12px 16px',
                  color: '#374151',
                  borderBottom: '1px solid #f3f4f6',
                  width: '25%',
                  maxWidth: '25%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {metric.label}
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  color: '#6b7280',
                  borderBottom: '1px solid #f3f4f6',
                  width: '25%',
                  maxWidth: '25%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {formatValue(data.previous[metric.key], metric.format)}
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  fontWeight: '600',
                  color: '#1f2937',
                  borderBottom: '1px solid #f3f4f6',
                  width: '25%',
                  maxWidth: '25%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {formatValue(data.current[metric.key], metric.format)}
                </td>
                <td style={{
                  padding: '12px 16px',
                  textAlign: 'right',
                  borderBottom: '1px solid #f3f4f6',
                  width: '25%',
                  maxWidth: '25%',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>
                  {formatEvolution(data.evolution[metric.key], metric.key)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  // Fonction pour rendre le tableau des totaux
  const renderTotalsTable = (accountData, comparisonType, periodInfo = null) => {
    // Utiliser les totaux calculés par le backend
    const currentTotals = accountData.currentPeriod?.data?.totalMetrics || {
      impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0
    };
    const previousTotals = accountData.previousPeriod?.data?.totalMetrics || {
      impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0
    };

    // Calculer les métriques dérivées
    const currentDerived = {
      ...currentTotals,
      ctr: currentTotals.impressions > 0 ? (currentTotals.clicks / currentTotals.impressions * 100) : 0,
      cpc: currentTotals.clicks > 0 ? (currentTotals.cost / currentTotals.clicks) : 0,
      conversionRate: currentTotals.clicks > 0 ? (currentTotals.conversions / currentTotals.clicks * 100) : 0,
      cpa: currentTotals.conversions > 0 ? (currentTotals.cost / currentTotals.conversions) : 0,
      cos: currentTotals.conversionsValue > 0 ? (currentTotals.cost / currentTotals.conversionsValue * 100) : 0,
      ...derivéesComparatif(currentTotals)
    };

    const previousDerived = {
      ...previousTotals,
      ctr: previousTotals.impressions > 0 ? (previousTotals.clicks / previousTotals.impressions * 100) : 0,
      cpc: previousTotals.clicks > 0 ? (previousTotals.cost / previousTotals.clicks) : 0,
      conversionRate: previousTotals.clicks > 0 ? (previousTotals.conversions / previousTotals.clicks * 100) : 0,
      cpa: previousTotals.conversions > 0 ? (previousTotals.cost / previousTotals.conversions) : 0,
      cos: previousTotals.conversionsValue > 0 ? (previousTotals.cost / previousTotals.conversionsValue * 100) : 0,
      ...derivéesComparatif(previousTotals)
    };

    // Calculer les évolutions
    const evolution = {};
    // Les seize clés configurables, pas seulement les dix d'origine : une métrique cochée
    // sans évolution calculée afficherait un tiret dans la troisième colonne.
    const allKeys = ['impressions', 'clicks', 'cost', 'conversions', 'conversionsValue', 'ctr', 'cpc', 'conversionRate', 'cpa', 'cos',
      'panierMoyen', 'roas', 'roi', 'views', 'vtr', 'cpv'];
    
    
    allKeys.forEach(key => {
      const currentVal = currentDerived[key] || 0;
      const previousVal = previousDerived[key] || 0;
      
      if (previousVal === 0) {
        evolution[key] = currentVal > 0 ? 100 : 0;
      } else {
        evolution[key] = ((currentVal - previousVal) / previousVal) * 100;
      }
      
    });

    const totalsData = {
      current: currentDerived,
      previous: previousDerived,
      evolution: evolution
    };

    return renderComparativeTable(totalsData, comparisonType, periodInfo);
  };

  const handleConnectGoogleAds = () => {
    window.location.href = `${API_URL}/api/auth/google-ads?token=${token}&returnTo=connectors`;
  };

  return (
    <div className="view-content">
      {/* Comptes absents des réponses : valable pour tous les modèles de dashboard */}
      <PartialDataBanner blocks={partialBlocks} resolveName={resolveAccountName} />

      {/* Sélection des comptes Google Ads */}
      <div className="dashboard-sources">
        {(() => {
          if (loadingLibraryItems) {
            return (
              <div className="loading-state">
                <Loader className="spin" size={16} />
                <span>Chargement des comptes Google Ads...</span>
              </div>
            );
          }
          
          if (libraryItems.length === 0) {
            // Vérifier si le connecteur Google Ads est activé
            const hasGoogleAdsConnector = globalConnectors?.some(c => c.provider === 'GOOGLE_ADS' || c.provider === 'GOOGLE') || false;
            
            if (!hasGoogleAdsConnector) {
              // Cas 1: Connecteur pas activé
              return (
                <div className="no-sources">
                  <div className="empty-state">
                    <p>Aucun connecteur Google Ads activé</p>
                    <button 
                      onClick={handleConnectGoogleAds}
                      className="btn btn-primary"
                      style={{
                        fontSize: '16px',
                        padding: '12px 24px'
                      }}
                    >
                      + Connecter
                    </button>
                  </div>
                </div>
              );
            } else {
              // Cas 2: Connecteur activé mais pas de comptes
              return (
                <div className="no-sources">
                  <div className="empty-state">
                    <p>Aucun compte Google Ads connecté</p>
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="btn btn-primary"
                      style={{
                        fontSize: '16px',
                        padding: '12px 24px'
                      }}
                    >
                      + Ajouter un compte
                    </button>
                  </div>
                </div>
              );
            }
          }

          // Cas 3: Plusieurs comptes disponibles mais aucun sélectionné
          if (libraryItems.length > 1 && !selectedAccount) {
            return (
              <div className="account-selection-prompt">
                <div className="account-selection-card">
                  <div className="account-selection-icon">
                    <img src={googleAdsLogo} alt="Google Ads" style={{ width: 48, height: 48 }} />
                  </div>
                  <h2>Sélectionnez un compte Google Ads</h2>
                  <p>{libraryItems.length} comptes disponibles</p>
                  <select
                    className="account-selection-dropdown"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        const account = libraryItems.find(item => item.id === e.target.value);
                        if (account) {
                          setSelectedAccount(account);
                          setCurrentActiveAccount(account.id);
                          setSelectedSources([account.id]);
                          // Synchroniser avec la sidebar et le breadcrumb
                          window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: account.id, accountName: account.name } }));
                          // Mettre à jour l'URL avec le compte sélectionné
                          setTimeout(() => window.updateUrlWithAccount?.(account.id), 100);
                        }
                      }
                    }}
                  >
                    <option value="">Choisir un compte...</option>
                    {[...libraryItems].sort((a, b) => a.name.localeCompare(b.name)).map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          }

          return (
          <>
            {/* Message de chargement si nécessaire (sauf pour Performance Max, Concurrence, Pages d'atterrissage, Products Insights, Shopping et Search Terms qui ont leur propre écran) */}
            {gadsFullScreenLoading && (
              <DashboardLoadingScreen
                platformName="Google Ads"
                progress={loadingProgress}
                features={[
                  { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
                  { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
                  { icon: <img src={googleAdsLogo} alt="Google Ads" style={{width: '28px', height: '28px'}} />, label: 'Analyse des campagnes' }
                ]}
              />
            )}
          </>
          );
        })()}
      </div>

      {/* Dashboard Comparatif */}
      {selectedDashboardModel === 'comparatif' && selectedAccount && !loadingCharts && (
        <div className="comparative-dashboard-section">

          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingComparative && accountHasNoData && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {!accountHasNoData && (
            <>
          {loadingComparative && (
            <DashboardLoadingScreen
              platformName="Google Ads"
              features={[
                { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
                { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
                { icon: <img src={googleAdsLogo} alt="Google Ads" style={{width: '28px', height: '28px'}} />, label: 'Analyse des campagnes' }
              ]}
            />
          )}

          {comparativeData && !loadingComparative && (() => {
            const accountData = Object.values(comparativeData)[0]; // Premier compte
            if (!accountData || !accountData.evolution) return null;

            // Créer l'objet d'informations de période pour les labels dynamiques
            const periodInfo = {
              period: selectedPeriod,
              customStartDate: customStartDate,
              customEndDate: customEndDate,
              previousStartDate: accountData.previousPeriod?.startDate,
              previousEndDate: accountData.previousPeriod?.endDate
            };

            return (
              <div className="comparative-dashboard-content">
                <MetricsConfigModal
                  isOpen={afficherConfigComparatif}
                  onClose={() => setAfficherConfigComparatif(false)}
                  componentId="comparatif"
                  accountId={selectedAccount?.id || 'global'}
                  componentTitle="Dashboard comparatif"
                />

                {/* Sticky Campaign Filter Banner - Dashboard Comparatif */}
                {comparativeData && (
                  <div style={{
                    background: '#FAF9F5',
                    padding: '16px 20px',
                    borderRadius: '8px',
                    marginTop: '-5px',
                    marginBottom: '24px',
                    position: 'sticky',
                    top: '0',
                    zIndex: '1000'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontWeight: '500',
                        color: '#374151',
                        fontSize: '0.95em',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> Filtrer:
                      </span>
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <input
                          type="text"
                          placeholder="Rechercher une campagne..."
                          value={selectedCampaignFilter ? (selectedCampaignFilter.name || selectedCampaignFilter.campaignName) : gaCampaignSearchQuery}
                          onChange={(e) => {
                            if (selectedCampaignFilter) {
                              setSelectedCampaignFilter(null);
                            }
                            setGaCampaignSearchQuery(e.target.value);
                            setShowGaCampaignAutocomplete(true);
                          }}
                          onFocus={() => setShowGaCampaignAutocomplete(true)}
                          onBlur={() => {
                            setTimeout(() => setShowGaCampaignAutocomplete(false), 200);
                          }}
                          style={{
                            padding: '4px 12px',
                            paddingRight: (gaCampaignSearchQuery || selectedCampaignFilter) ? '28px' : '12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: selectedCampaignFilter ? '#FAF9F5' : 'white',
                            color: '#374151',
                            fontSize: '0.9em',
                            width: '400px',
                            height: '35px',
                            boxSizing: 'border-box',
                            outline: 'none'
                          }}
                        />
                        {(gaCampaignSearchQuery || selectedCampaignFilter) && (
                          <button
                            onClick={() => {
                              setGaCampaignSearchQuery('');
                              setSelectedCampaignFilter(null);
                              setShowGaCampaignAutocomplete(false);
                            }}
                            style={{
                              position: 'absolute',
                              right: '6px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: selectedCampaignFilter ? '#374151' : '#9ca3af',
                              padding: '2px',
                              fontSize: '14px',
                              lineHeight: 1,
                              zIndex: 1
                            }}
                            title={selectedCampaignFilter ? "Effacer le filtre campagne" : "Effacer la recherche"}
                          >
                            ✕
                          </button>
                        )}
                        {/* Autocomplete dropdown */}
                        {showGaCampaignAutocomplete && (() => {
                          const searchLower = gaCampaignSearchQuery.toLowerCase().trim();
                          let baseCampaigns = campaignsData?.data?.[0]?.campaigns || [];
                          if (selectedLabelsFilter.length > 0 && Object.keys(campaignLabelsMap).length > 0) {
                            baseCampaigns = baseCampaigns.filter(c => {
                              const campaignId = (c.id || c.campaignId || '').toString();
                              const campaignLabels = campaignLabelsMap[campaignId] || [];
                              return selectedLabelsFilter.some(label => campaignLabels.includes(label));
                            });
                          }
                          const filteredCampaigns = searchLower
                            ? baseCampaigns
                                .filter(c => (c.name || c.campaignName || '').toLowerCase().includes(searchLower))
                                .sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''))
                            : [...baseCampaigns].sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''));

                          if (filteredCampaigns.length === 0) {
                            return (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                marginTop: '4px',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                zIndex: 1001,
                                padding: '12px',
                                color: '#6b7280',
                                fontSize: '0.9em'
                              }}>
                                Aucune campagne trouvée
                              </div>
                            );
                          }

                          return (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              marginTop: '4px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                              zIndex: 1001,
                              maxHeight: '300px',
                              overflowY: 'auto'
                            }}>
                              <div style={{
                                padding: '8px 12px',
                                fontSize: '0.8em',
                                color: '#6b7280',
                                borderBottom: '1px solid #e5e7eb',
                                backgroundColor: '#FAF9F5'
                              }}>
                                {searchLower
                                  ? `${filteredCampaigns.length} campagne(s) trouvée(s)`
                                  : `Toutes les campagnes (${filteredCampaigns.length})`}
                              </div>
                              {filteredCampaigns.map((campaign, index) => (
                                <div
                                  key={campaign.id || campaign.campaignId || index}
                                  onClick={() => {
                                    setSelectedCampaignFilter(campaign);
                                    setGaCampaignSearchQuery('');
                                    setShowGaCampaignAutocomplete(false);
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.9em',
                                    color: '#374151',
                                    borderBottom: index < filteredCampaigns.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    transition: 'background-color 0.15s'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                  {campaign.name || campaign.campaignName}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>
                    </div>
                  </div>
                )}

                {/* Section Insights IA pour Dashboard Comparatif */}
                {aiInsights && (
                  <div className="ai-insights-section">
                    <h3>🤖 Analyse Intelligence Artificielle</h3>
                    <div className="insights-content">
                      <div className="insights-header">
                        <div className="insights-badges">
                          <span className="ai-badge">🤖 CLAUDE AI</span>
                        </div>
                        <div className="insights-meta">
                          <span>Tokens: {aiInsights.tokensUsed}</span>
                          <span>Coût: {aiInsights.cost}€</span>
                          <span>Analyse: 100% personnalisée</span>
                        </div>
                      </div>
                      <div className="insights-text" style={{ whiteSpace: 'pre-wrap' }}>
                        {aiInsights.analysis}
                      </div>

                      {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
                        <div className="recommendations-section">
                          <h4><Sparkles size={16} />&nbsp;Actions Recommandées</h4>
                          <div className="recommendations-list">
                            {aiInsights.recommendations
                              .filter(rec => rec && typeof rec === 'string' && rec.trim().length > 0)
                              .map((rec, index) => (
                              <div key={index} className="recommendation-item">
                                <span className="rec-number">{index + 1}</span>
                                <span className="rec-text">{rec}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="comparative-tables">
                  {/* Totaux - Placé en premier */}
                  <div className="campaign-type-section">
                  <div style={{
                    position: 'relative',
                    marginBottom: '16px'
                  }}>
                    <h4 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      margin: 0,
                      color: '#1f2937',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <BarChart3 size={20} /> Totaux
                    </h4>
                    {/*
                      * Roue crantée calée à droite, sur la ligne du titre « Totaux ». Le
                      * sélecteur de comparaison, juste à gauche, est positionné en absolu sur
                      * la colonne N-1 : sortir du flux ici aussi évite de le décaler.
                      * Masquée pour les accès clients, comme sur tous les autres tableaux.
                      */}
                    <div style={{ position: 'absolute', top: 0, right: 0, display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {/* Axe de segmentation des tableaux affichés sous les totaux */}
                      <select
                        value={segmentationComparatif}
                        onChange={(e) => setSegmentationComparatif(e.target.value)}
                        className="form-select"
                        title="Segmenter les tableaux"
                        style={{
                          fontSize: '15px',
                          padding: '6px 12px',
                          backgroundColor: '#FAF9F5',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          cursor: 'pointer'
                        }}
                      >
                        <option value="type">Types de campagne</option>
                        <option value="network">Réseaux</option>
                        <option value="campaign">Campagnes</option>
                        <option value="adgroup">Groupes d'annonces</option>
                        <option value="device">Appareils</option>
                        <option value="weekday">Jours de la semaine</option>
                        <option value="geo">Origine géographique</option>
                        <option value="age">Tranches d'âge</option>
                        <option value="gender">Genre</option>
                      </select>
                      {userRole !== 'CLIENT' && (
                        <button
                          onClick={() => setAfficherConfigComparatif(true)}
                          className="metrics-settings-btn"
                          title="Configurer les métriques"
                        >
                          <Settings size={16} />
                        </button>
                      )}
                    </div>
                    <div style={{
                      position: 'absolute',
                      top: 0,
                      left: '25%', // Positionné au début de la colonne N-1
                      width: '25%', // Même largeur que la colonne
                      display: 'flex',
                      justifyContent: 'flex-end',
                      paddingRight: '16px'
                    }}>
                      <select
                        value={comparisonType}
                        onChange={(e) => setComparisonType(e.target.value)}
                        className="form-select"
                        style={{
                          fontSize: '15px',
                          padding: '6px 12px',
                          backgroundColor: '#FAF9F5',
                          border: '1px solid #e5e7eb',
                          borderRadius: '6px',
                          cursor: 'pointer',
                          width: '230px'
                        }}
                      >
                        <option value="previous_year_to_date">Année précédente (à date)</option>
                        <option value="previous_year">Année précédente</option>
                        <option value="previous_period">Période précédente</option>
                      </select>
                    </div>
                  </div>
                  {renderTotalsTable(accountData, comparisonType, periodInfo)}
                </div>

                {/* Segmentation par type de campagne — la vue historique de cet écran */}
                {segmentationComparatif === 'type' && (
                  <>
                  {/* Tableau Search */}
                  {(accountData.evolution[2] || accountData.evolution.Search) && (
                    <div className="campaign-type-section">
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Search size={20} /> Search
                      </h4>
                      {renderComparativeTable(accountData.evolution[2] || accountData.evolution.Search, comparisonType, periodInfo)}
                    </div>
                  )}

                  {/* Tableau Shopping (type 4) */}
                  {accountData.evolution[4] && (
                    <div className="campaign-type-section">
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Package size={20} /> Shopping
                      </h4>
                      {renderComparativeTable(accountData.evolution[4], comparisonType, periodInfo)}
                    </div>
                  )}

                  {/* Tableau Performance Max (type 10) */}
                  {accountData.evolution[10] && (
                    <div className="campaign-type-section">
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Zap size={20} /> Performance Max
                      </h4>
                      {renderComparativeTable(accountData.evolution[10], comparisonType, periodInfo)}
                    </div>
                  )}

                  {/* Tableau Display (type 3) */}
                  {accountData.evolution[3] && (
                    <div className="campaign-type-section">
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Monitor size={20} /> Display
                      </h4>
                      {renderComparativeTable(accountData.evolution[3], comparisonType, periodInfo)}
                    </div>
                  )}

                  {/* Tableau Video (type 6) */}
                  {accountData.evolution[6] && (
                    <div className="campaign-type-section">
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Youtube size={20} /> Video
                      </h4>
                      {renderComparativeTable(accountData.evolution[6], comparisonType, periodInfo)}
                    </div>
                  )}
                  </>
                )}

                {/*
                  * Segmentation par réseau ou par campagne. Les deux se lisent dans la même
                  * réponse que les types : basculer ne relance aucun appel. Les segments
                  * arrivent déjà triés par budget décroissant.
                  */}
                {segmentationComparatif !== 'type' && (() => {
                  // Réseaux et campagnes voyagent avec la réponse principale ; les six autres
                  // axes arrivent dans `evolutionAxe`, calculé côté serveur à la demande.
                  const segments = segmentationComparatif === 'network' ? accountData.evolutionParReseau
                    : segmentationComparatif === 'campaign' ? accountData.evolutionParCampagne
                    : accountData.evolutionAxe;
                  const entrees = Object.entries(segments || {});

                  if (entrees.length === 0) {
                    return (
                      <div className="campaign-type-section" style={{ color: '#6b7280', padding: '16px 0' }}>
                        Aucune donnée sur cet axe pour la période sélectionnée.
                      </div>
                    );
                  }

                  return entrees.map(([cle, donnees]) => (
                    <div className="campaign-type-section" key={cle}>
                      <h4 style={{
                        fontSize: '18px',
                        fontWeight: '600',
                        marginBottom: '16px',
                        color: '#1f2937',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        {ICONE_SEGMENT[segmentationComparatif] || <Target size={20} />}
                        {donnees.typeLabel}
                      </h4>
                      {renderComparativeTable(donnees, comparisonType, periodInfo)}
                    </div>
                  ));
                })()}
                </div>
              </div>
            );
          })()}
          </>
          )}
        </div>
      )}

      {/* Section Insights IA pour Dashboard Global */}
      {selectedDashboardModel === 'global' && aiInsights && !gadsFullScreenLoading && (
        <div className="ai-insights-section">
          <h3>🤖 Analyse Intelligence Artificielle</h3>
          <div className="insights-content">
            <div className="insights-header">
              <div className="insights-badges">
                <span className="ai-badge">🤖 CLAUDE AI</span>
              </div>
              <div className="insights-meta">
                <span>Tokens: {aiInsights.tokensUsed}</span>
                <span>Coût: {aiInsights.cost}€</span>
                <span>Analyse: 100% personnalisée</span>
              </div>
            </div>
            <div className="insights-text markdown-content">
              <ReactMarkdown remarkPlugins={[remarkGfm]}>
                {aiInsights.analysis}
              </ReactMarkdown>
            </div>

            {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
              <div className="recommendations-section">
                <h4><Sparkles size={16} />&nbsp;Actions Recommandées</h4>
                <div className="recommendations-list">
                  {aiInsights.recommendations
                    .filter(rec => rec && typeof rec === 'string' && rec.trim().length > 0)
                    .map((rec, index) => (
                    <div key={index} className="recommendation-item">
                      <span className="rec-number">{index + 1}</span>
                      <span className="rec-text">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section Debug GAQL - uniquement pour clean_architecture */}
            {aiInsights.approach === 'clean_architecture' && aiInsights.gaqlQuery && (
              <div className="gaql-section" style={{marginTop: '20px', marginBottom: '20px'}}>
                <h4 style={{fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px'}}><Search size={16} /> Détails de l'analyse</h4>
                <div className="gaql-details" style={{backgroundColor: '#FAF9F5', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                  {aiInsights.userQuery && (
                    <div className="user-query" style={{marginBottom: '16px'}}>
                      <strong style={{color: '#6b7280'}}>Question posée :</strong>
                      <div style={{
                        backgroundColor: '#fff',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '6px',
                        fontSize: '14px',
                        fontStyle: 'italic',
                        color: '#374151',
                        border: '1px solid #d1d5db'
                      }}>
                        "{aiInsights.userQuery || 'User query non disponible - DEBUG'}"
                      </div>
                    </div>
                  )}
                  
                  {/* GAQL Query technique */}
                  <div className="gaql-query" style={{marginBottom: '12px'}}>
                    <strong style={{color: '#6b7280'}}>Requête GAQL générée :</strong>
                    <pre className="gaql-code" style={{
                      backgroundColor: '#1f2937',
                      color: '#e5e7eb',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontFamily: 'Monaco, Consolas, monospace',
                      overflowX: 'auto',
                      marginTop: '8px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all'
                    }}>{aiInsights.gaqlQuery || 'GAQL query non disponible - DEBUG'}</pre>
                  </div>
                  
                  {/* Métadonnées */}
                  <div className="gaql-metadata" style={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    <span><strong>Resource:</strong> {aiInsights.resource}</span>
                    <span><strong>Data Points:</strong> {aiInsights.dataPoints}</span>
                    <span><strong>Architecture:</strong> {aiInsights.approach}</span>
                  </div>
                </div>
              </div>
            )}

            {aiInsights.dataTable && aiInsights.dataTable.headers && aiInsights.dataTable.headers.length > 0 && (
              <div className="data-table-section">
                <h4><BarChart2 size={16} />&nbsp;Données analysées</h4>
                <div className="table-wrapper" style={{overflowX: 'auto', marginTop: '15px'}}>
                  <table className="analysis-data-table" style={{width: '100%', borderCollapse: 'collapse', fontSize: '14px', tableLayout: 'auto'}}>
                    <thead>
                      <tr style={{backgroundColor: '#FAF9F5', borderBottom: '2px solid #e5e7eb'}}>
                        {aiInsights.dataTable.headers.map((header, index) => (
                          <th key={index} style={{padding: '12px 8px', textAlign: 'left', fontWeight: '600', color: '#374151', minWidth: header === 'Impressions' ? '120px' : '80px', width: header === 'Impressions' ? '120px' : 'auto'}}>
                            {header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {aiInsights.dataTable.rows.map((row, rowIndex) => (
                        <tr key={rowIndex} style={{borderBottom: '1px solid #e5e7eb'}}>
                          {row.map((cell, cellIndex) => (
                            <td key={cellIndex} style={{padding: '10px 8px', color: '#6b7280', whiteSpace: 'nowrap', minWidth: aiInsights.dataTable.headers[cellIndex] === 'Impressions' ? '120px' : '80px', width: aiInsights.dataTable.headers[cellIndex] === 'Impressions' ? '120px' : 'auto', overflow: 'visible'}}>
                              {cell}
                            </td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                <div style={{marginTop: '10px', fontSize: '12px', color: '#9ca3af'}}>
                  Affichage de {aiInsights.dataTable.rows.length} lignes sur {aiInsights.dataTable.totalRows} total
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Section Insights IA pour Dashboard Shopping */}
      {selectedDashboardModel === 'shopping' && aiInsights && (
        <div className="ai-insights-section">
          <h3>🤖 Analyse Intelligence Artificielle</h3>
          <div className="insights-content">
            <div className="insights-header">
              <div className="insights-badges">
                <span className="ai-badge">🤖 CLAUDE AI</span>
              </div>
              <div className="insights-meta">
                <span>Tokens: {aiInsights.tokensUsed}</span>
                <span>Coût: {aiInsights.cost}€</span>
                <span>Analyse: 100% personnalisée</span>
              </div>
            </div>
            <div className="insights-text" style={{ whiteSpace: 'pre-wrap' }}>
              {aiInsights.analysis}
            </div>

            {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
              <div className="recommendations-section">
                <h4><Sparkles size={16} />&nbsp;Actions Recommandées</h4>
                <div className="recommendations-list">
                  {aiInsights.recommendations
                    .filter(rec => rec && typeof rec === 'string' && rec.trim().length > 0)
                    .map((rec, index) => (
                    <div key={index} className="recommendation-item">
                      <span className="rec-number">{index + 1}</span>
                      <span className="rec-text">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section GAQL Debug - FORCE DISPLAY FOR TEST */}
            {(aiInsights.gaqlQuery || aiInsights.approach === 'clean_architecture') && (
              <div className="gaql-section" style={{marginTop: '20px', marginBottom: '20px'}}>
                <h4 style={{fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px'}}><Search size={16} /> Détails de l'analyse</h4>
                <div className="gaql-details" style={{backgroundColor: '#FAF9F5', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                  {/* Query utilisateur */}
                  {aiInsights.userQuery && (
                    <div className="user-query" style={{marginBottom: '16px'}}>
                      <strong style={{color: '#6b7280'}}>Question posée :</strong>
                      <div style={{
                        backgroundColor: '#fff',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '6px',
                        fontSize: '14px',
                        fontStyle: 'italic',
                        color: '#374151',
                        border: '1px solid #d1d5db'
                      }}>
                        "{aiInsights.userQuery || 'User query non disponible - DEBUG'}"
                      </div>
                    </div>
                  )}
                  
                  {/* GAQL Query technique */}
                  <div className="gaql-query" style={{marginBottom: '12px'}}>
                    <strong style={{color: '#6b7280'}}>Requête GAQL générée :</strong>
                    <pre className="gaql-code" style={{
                      backgroundColor: '#1f2937',
                      color: '#e5e7eb',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontFamily: 'Monaco, Consolas, monospace',
                      overflowX: 'auto',
                      marginTop: '8px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all'
                    }}>{aiInsights.gaqlQuery || 'GAQL query non disponible - DEBUG'}</pre>
                  </div>
                  
                  {/* Métadonnées */}
                  <div className="gaql-metadata" style={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    <span><strong>Resource:</strong> {aiInsights.resource}</span>
                    <span><strong>Data Points:</strong> {aiInsights.dataPoints}</span>
                    <span><strong>Architecture:</strong> {aiInsights.approach}</span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Dashboard Global */}
      {(() => {
        // Condition simplifiée: afficher si compte sélectionné et mode global
        // Le NoDataMessage sera affiché à l'intérieur si pas de données
        // Masqué pendant le chargement : l'écran plein le remplace au lieu de se superposer
        return libraryItems.length > 0 && selectedDashboardModel === 'global' && !gadsFullScreenLoading;
      })() && (
        <div className="auto-dashboard-section auto-charts-container">

          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingCharts && autoCharts && autoCharts.charts && autoCharts.charts.length === 0 && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {/* Tout le contenu du dashboard - affiché uniquement si données disponibles */}
          {autoCharts && autoCharts.charts && autoCharts.charts.length > 0 && (
            <>
          {/* Sticky Campaign Filter Banner - affiché uniquement si données disponibles */}
          {campaignsData?.data?.[0]?.campaigns?.length > 0 && (
            <div style={{
              background: 'linear-gradient(135deg, #FAF9F6 0%, #F5F4F0 100%)',
              padding: '16px 20px',
              borderRadius: '8px',
              marginTop: '-5px',
              marginBottom: '10px',
              position: 'sticky',
              top: '0',
              zIndex: '1000'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{
                    fontWeight: '500',
                    color: '#374151',
                    fontSize: '0.95em',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> Filtrer:
                  </span>
                  <div style={{ position: 'relative', display: 'inline-block' }}>
                    <input
                      type="text"
                      placeholder="Rechercher une campagne..."
                      value={selectedCampaignFilter ? (selectedCampaignFilter.name || selectedCampaignFilter.campaignName) : gaCampaignSearchQuery}
                      onChange={(e) => {
                        if (selectedCampaignFilter) {
                          setSelectedCampaignFilter(null);
                        }
                        setGaCampaignSearchQuery(e.target.value);
                        setShowGaCampaignAutocomplete(true);
                      }}
                      onFocus={() => setShowGaCampaignAutocomplete(true)}
                      onBlur={() => {
                        // Délai pour permettre le clic sur les options
                        setTimeout(() => setShowGaCampaignAutocomplete(false), 200);
                      }}
                      style={{
                        padding: '4px 12px',
                        paddingRight: (gaCampaignSearchQuery || selectedCampaignFilter) ? '28px' : '12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: selectedCampaignFilter ? '#dbeafe' : 'white',
                        color: '#374151',
                        fontSize: '0.9em',
                        width: '400px',
                        height: '35px',
                        boxSizing: 'border-box',
                        outline: 'none'
                      }}
                    />
                    {(gaCampaignSearchQuery || selectedCampaignFilter) && (
                      <button
                        onClick={() => {
                          setGaCampaignSearchQuery('');
                          setSelectedCampaignFilter(null);
                          setShowGaCampaignAutocomplete(false);
                        }}
                        style={{
                          position: 'absolute',
                          right: '6px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: selectedCampaignFilter ? '#1a1a1a' : '#9ca3af',
                          padding: '2px',
                          fontSize: '14px',
                          lineHeight: 1,
                          zIndex: 1
                        }}
                        title={selectedCampaignFilter ? "Effacer le filtre campagne" : "Effacer la recherche"}
                      >
                        ✕
                      </button>
                    )}
                    {/* Autocomplete dropdown */}
                    {showGaCampaignAutocomplete && (() => {
                      const searchLower = gaCampaignSearchQuery.toLowerCase().trim();
                      // D'abord filtrer par labels si des labels sont sélectionnés
                      let baseCampaigns = campaignsData?.data?.[0]?.campaigns || [];
                      if (selectedLabelsFilter.length > 0 && Object.keys(campaignLabelsMap).length > 0) {
                        baseCampaigns = baseCampaigns.filter(c => {
                          const campaignId = (c.id || c.campaignId || '').toString();
                          const campaignLabels = campaignLabelsMap[campaignId] || [];
                          return selectedLabelsFilter.some(label => campaignLabels.includes(label));
                        });
                      }
                      // Ensuite filtrer par recherche textuelle
                      const filteredCampaigns = searchLower
                        ? baseCampaigns
                            .filter(c => (c.name || c.campaignName || '').toLowerCase().includes(searchLower))
                            .sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''))
                        : [...baseCampaigns].sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''));

                      if (filteredCampaigns.length === 0) {
                        return (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            marginTop: '4px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            zIndex: 1001,
                            padding: '12px',
                            color: '#6b7280',
                            fontSize: '0.9em'
                          }}>
                            Aucune campagne trouvée
                          </div>
                        );
                      }

                      return (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          marginTop: '4px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          zIndex: 1001,
                          maxHeight: '300px',
                          overflowY: 'auto'
                        }}>
                          <div style={{
                            padding: '8px 12px',
                            fontSize: '0.8em',
                            color: '#6b7280',
                            borderBottom: '1px solid #e5e7eb',
                            backgroundColor: '#f9fafb'
                          }}>
                            {searchLower
                              ? `${filteredCampaigns.length} campagne(s) trouvée(s)`
                              : `Toutes les campagnes (${filteredCampaigns.length})`}
                          </div>
                          {filteredCampaigns.map((campaign, index) => (
                            <div
                              key={campaign.id || campaign.campaignId || index}
                              onClick={() => {
                                setSelectedCampaignFilter(campaign);
                                setGaCampaignSearchQuery('');
                                setShowGaCampaignAutocomplete(false);
                              }}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                fontSize: '0.9em',
                                color: '#374151',
                                borderBottom: index < filteredCampaigns.length - 1 ? '1px solid #f3f4f6' : 'none',
                                transition: 'background-color 0.15s'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                            >
                              {campaign.name || campaign.campaignName}
                            </div>
                          ))}
                        </div>
                      );
                    })()}
                  </div>

                  {/* Filtre par labels */}
                  {(() => {
                    // Si une campagne est sélectionnée, ne montrer que ses labels
                    let labelsToDisplay = availableCampaignLabels;
                    if (selectedCampaignFilter) {
                      const campaignId = (selectedCampaignFilter.id || selectedCampaignFilter.campaignId || '').toString();
                      const campaignLabels = campaignLabelsMap[campaignId] || [];
                      // Si la campagne n'a pas de labels, masquer le dropdown
                      if (campaignLabels.length === 0) return null;
                      // Sinon, ne montrer que les labels de cette campagne
                      labelsToDisplay = campaignLabels.filter(l => availableCampaignLabels.includes(l));
                    }
                    if (labelsToDisplay.length === 0) return null;

                    return (
                    <div style={{ position: 'relative', marginLeft: '16px' }}>
                    <button
                      onClick={() => setShowLabelsDropdown(!showLabelsDropdown)}
                      style={{
                        padding: '4px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: selectedLabelsFilter.length > 0 ? '#dbeafe' : 'white',
                        color: '#374151',
                        fontSize: '0.9em',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        minWidth: '140px',
                        height: '35px',
                        boxSizing: 'border-box'
                      }}
                    >
                      <span><Tag size={14} />&nbsp;Labels</span>
                      {selectedLabelsFilter.length > 0 && (
                        <span style={{
                          backgroundColor: '#1a1a1a',
                          color: 'white',
                          borderRadius: '10px',
                          padding: '2px 8px',
                          fontSize: '0.8em',
                          fontWeight: '600'
                        }}>
                          {selectedLabelsFilter.length}
                        </span>
                      )}
                      <span style={{ marginLeft: 'auto' }}>{showLabelsDropdown ? '▲' : '▼'}</span>
                    </button>

                    {showLabelsDropdown && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          marginTop: '4px',
                          backgroundColor: 'white',
                          border: '1px solid #e5e7eb',
                          borderRadius: '8px',
                          boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                          zIndex: 9999,
                          minWidth: '200px',
                          maxHeight: '300px',
                          overflowY: 'auto'
                        }}
                      >
                        {/* Bouton tout effacer */}
                        {selectedLabelsFilter.length > 0 && (
                          <div
                            onClick={() => {
                              setSelectedLabelsFilter([]);
                            }}
                            style={{
                              padding: '8px 12px',
                              cursor: 'pointer',
                              fontSize: '0.85em',
                              color: '#ef4444',
                              borderBottom: '1px solid #e5e7eb',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '6px'
                            }}
                          >
                            ✕ Tout effacer
                          </div>
                        )}
                        {labelsToDisplay.map((label) => (
                          <div
                            key={label}
                            onClick={() => {
                              if (selectedLabelsFilter.includes(label)) {
                                setSelectedLabelsFilter(selectedLabelsFilter.filter(l => l !== label));
                              } else {
                                setSelectedLabelsFilter([...selectedLabelsFilter, label]);
                              }
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              fontSize: '0.9em',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              backgroundColor: selectedLabelsFilter.includes(label) ? '#FAF9F5' : 'transparent',
                              borderBottom: '1px solid #f3f4f6'
                            }}
                            onMouseEnter={(e) => {
                              if (!selectedLabelsFilter.includes(label)) {
                                e.currentTarget.style.backgroundColor = '#f9fafb';
                              }
                            }}
                            onMouseLeave={(e) => {
                              e.currentTarget.style.backgroundColor = selectedLabelsFilter.includes(label) ? '#FAF9F5' : 'transparent';
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedLabelsFilter.includes(label)}
                              onChange={() => {}}
                              style={{
                                width: '16px',
                                height: '16px',
                                cursor: 'pointer',
                                accentColor: '#1a1a1a'
                              }}
                            />
                            <span style={{
                              fontSize: '0.9em'
                            }}>
                              {label}
                            </span>
                          </div>
                        ))}

                        {/* Bouton Valider */}
                        <div
                          onClick={() => setShowLabelsDropdown(false)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                            color: 'white',
                            backgroundColor: '#1a1a1a',
                            borderTop: '1px solid #e5e7eb',
                            textAlign: 'center',
                            fontWeight: '500',
                            marginTop: '4px'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#333333'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#1a1a1a'}
                        >
                          ✓ Valider
                        </div>
                      </div>
                    )}

                    </div>
                    );
                  })()}
                </div>

                {/* Icône PDF Export - Temporairement masqué */}
                {false && (
                  <button
                    onClick={handleExportPDF}
                    style={{
                      background: '#e5e7eb',
                      border: 'none',
                      borderRadius: '8px',
                      color: '#374151',
                      padding: '12px 16px',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px',
                      fontSize: '0.9em',
                      fontWeight: '500',
                      transition: 'all 0.2s ease',
                      boxShadow: 'none'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.background = '#dc2626';
                      e.target.style.color = 'white';
                      e.target.style.transform = 'translateY(-1px)';
                      e.target.style.boxShadow = 'none';
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.background = '#e5e7eb';
                      e.target.style.color = '#374151';
                      e.target.style.transform = 'translateY(0)';
                      e.target.style.boxShadow = 'none';
                    }}
                    title="Exporter le dashboard en PDF"
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
                      <polyline points="14,2 14,8 20,8"/>
                      <line x1="16" y1="13" x2="8" y2="13"/>
                      <line x1="16" y1="17" x2="8" y2="17"/>
                      <polyline points="10,9 9,9 8,9"/>
                    </svg>
                    PDF
                  </button>
                )}
                {selectedCampaignTypeFilter && (
                  <div style={{
                    backgroundColor: '#f3e5f5',
                    color: '#7b1fa2',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: '1px solid #ce93d8'
                  }}>
                    🏷️ {(() => {
                      const typeMapping = {
                        0: 'Non spécifié', 1: 'Inconnu', 2: 'Recherche', 3: 'Display', 4: 'Shopping',
                        5: 'Hôtel', 6: 'Vidéo', 7: 'Multi-canal', 8: 'Local', 9: 'Intelligente',
                        10: 'Performance Max', 11: 'Services locaux', 12: 'Découverte', 13: 'Voyage',
                        14: 'DemandGen', 15: 'App (iOS)', 16: 'App (Android)', 17: 'Autre'
                      };
                      return typeMapping[selectedCampaignTypeFilter.type] || selectedCampaignTypeFilter.name || 'Type de campagne';
                    })()}
                    <button
                      onClick={() => setSelectedCampaignTypeFilter(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#7b1fa2',
                        cursor: 'pointer',
                        padding: '0 2px',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        borderRadius: '2px'
                      }}
                      title="Supprimer le filtre type de campagne"
                      onMouseOver={(e) => e.target.style.backgroundColor = '#ce93d8'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      ×
                    </button>
                  </div>
                )}

                {/*
                  * Bouton Analyse IA, à droite dans le conteneur de filtrage.
                  *
                  * Il disparaît quand le panneau est ouvert : il n'a plus rien à ouvrir, et le
                  * panneau porte déjà sa croix de fermeture. Le laisser aurait fait un second
                  * bouton sans effet à côté de ce qu'il était censé provoquer.
                  */}
                {localShowAIButton && globalConnectors.length > 0 && canAccessAnalyzer && !panneauAnalyseOuvert && (
                  <button
                    onClick={() => {
                      // Déclencher l'analyse IA selon le type de dashboard actuel
                      if (currentDashboardType === 'google' && window.googleDashboardAIAnalysis) {
                        window.googleDashboardAIAnalysis();
                      } else if (currentDashboardType === 'microsoft' && window.microsoftDashboardAIAnalysis) {
                        window.microsoftDashboardAIAnalysis();
                      } else if (currentDashboardType === 'facebook-ads' && window.facebookDashboardAIAnalysis) {
                        window.facebookDashboardAIAnalysis();
                      } else if (currentDashboardType === 'synthese' && window.syntheseDashboardAIAnalysis) {
                        window.syntheseDashboardAIAnalysis();
                      }
                    }}
                    /*
                     * Le bouton reprend la classe des onglets de l'en-tête plutôt que de
                     * recopier leurs couleurs : il suivra d'office une évolution de la charte,
                     * là où un fond écrit en dur ici se serait désaligné au premier changement.
                     * Le dégradé violet qu'il portait n'appartenait à aucune palette du produit.
                     *
                     * Seule la taille est précisée en ligne : `.nav-btn` est dimensionné pour
                     * une barre de navigation, et détonnerait dans une barre de filtres.
                     */
                    className="nav-btn"
                    data-visite="analyse-ia"
                    style={{
                      padding: '8px 16px',
                      gap: '10px',
                      fontSize: '0.9em',
                      fontWeight: '500',
                      whiteSpace: 'nowrap'
                    }}
                    title="Analyse IA du dashboard actuel"
                  >
                    {/* L'icône porte l'accent de la charte ; les onglets voisins ont des icônes
                        Lucide, un émoji y détonnait autant que le dégradé violet d'avant. */}
                    <Sparkles size={16} color="#7c2d12" />
                    Analyse IA
                  </button>
                )}

                {/* Lien Modules masqués */}
                {userRole !== 'CLIENT' && clientHiddenModules.length > 0 && (
                  <div
                    className="hidden-modules-link"
                    onClick={() => setIsHiddenModulesModalOpen(true)}
                    title="Voir les modules masqués pour le client"
                  >
                    <EyeOff size={16} />
                    <span>Modules masqués</span>
                    <span className="hidden-count">{clientHiddenModules.length}</span>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Modal des modules masqués */}
          <HiddenModulesModal
            isOpen={isHiddenModulesModalOpen}
            onClose={() => setIsHiddenModulesModalOpen(false)}
            hiddenModules={clientHiddenModules}
            onRestoreModule={(moduleId) => setClientModuleVisibility(moduleId, true)}
          />

          {/* Section Résultats - Composant modulaire avec chargement prioritaire */}
          <div className="results-section-container">
            {(() => {
              // Utiliser UNIQUEMENT resultsData du nouvel endpoint timeline
            const chartSource = resultsData;
            
            // Afficher le chargement si on attend encore les données
            // loadingCharts = chargement initial, loadingResults = rechargement avec filtres
            if ((loadingResults || loadingCharts) && !chartSource) {
              return (
                <div className="performance-chart-section">
                  <div className="section-header">
                    <h4><TrendingUp size={16} />&nbsp;Résultats</h4>
                  </div>
                  <div className="loading-placeholder" style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    height: '200px',
                    backgroundColor: '#FAF9F5',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    color: '#6b7280'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <Loader size={18} className="spin" style={{ color: '#6b7280' }} />
                      Chargement des résultats...
                    </div>
                  </div>
                </div>
              );
            }
            
            // Ne pas afficher la section si aucun compte n'est sélectionné
            if (!selectedAccount) {
              return null;
            }
            
            // Ne pas afficher cette section s'il n'y a pas de données (NoDataMessage est affiché au niveau dashboard)
            if (!chartSource || !chartSource.charts || chartSource.charts.length === 0) {
              return null;
            }
            
            return (
              <PerformanceChart
                autoCharts={chartSource}
                formatNumber={formatNumber}
                KPICard={KPICard}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                granularity={granularity}
                onGranularityChange={setGranularity}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                notes={dashboardNotes}
                userRole={userRole}
                isVisible={showResults}
                onToggleVisibility={toggleResults}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                moduleId="results"
                selectedAccount={selectedAccount}
              />
            );
          })()}


          </div>


          {/* Pages de destination supprimées du dashboard global */}

          {/* Section Types de campagnes - composant partagé avec le dashboard Microsoft Ads */}
          {selectedDashboardModel === 'global' && campaignsData?.success && resultsData?.charts?.length > 0 && !gadsLoading && (() => {
            const allCampaignsData = campaignsData?.data?.[0]?.campaigns || [];
            if (allCampaignsData.length === 0 && !loadingCampaigns) return null;

            // Google Ads encode le type de campagne en nombre, Microsoft Ads en chaîne :
            // c'est la seule chose que le composant partagé doit savoir de la régie.
            const getChannelTypeName = (channelType) => {
              const typeMapping = {
                0: 'Non spécifié', 1: 'Inconnu', 2: 'Recherche', 3: 'Display', 4: 'Shopping',
                5: 'Hôtel', 6: 'Vidéo', 7: 'Multi-canal', 8: 'Local', 9: 'Intelligente',
                10: 'Performance Max', 11: 'Services locaux', 12: 'Découverte', 13: 'Voyage',
                14: 'DemandGen', 15: 'App (iOS)', 16: 'App (Android)', 17: 'Autre'
              };
              return typeof channelType === 'number'
                ? typeMapping[channelType] || `Nouveau type (${channelType})`
                : 'Non défini';
            };

            // Les mêmes filtres doivent porter sur les deux périodes, sans quoi la ligne
            // TOTAL comparerait un sous-ensemble filtré à un ensemble complet.
            const applyFilters = (list) => {
              let filtered = [...list];
              if (selectedCampaignFilter) {
                filtered = filtered.filter(campaign =>
                  String(campaign.id) === String(selectedCampaignFilter.id) ||
                  String(campaign.campaignId) === String(selectedCampaignFilter.id)
                );
              }
              if (selectedCampaignTypeFilter) {
                filtered = filtered.filter(campaign => {
                  const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
                  return String(campaignType) === String(selectedCampaignTypeFilter.type);
                });
              }
              if (selectedLabelsFilter.length > 0) {
                filtered = filtered.filter(campaign =>
                  campaignMatchesLabelsFilter(campaign.id || campaign.campaignId)
                );
              }
              return filtered;
            };

            return (
              <CampaignTypesSection
                campaigns={applyFilters(allCampaignsData)}
                previousCampaigns={
                  gadsCompareEnabled && previousPeriodData?.campaigns
                    ? applyFilters(previousPeriodData.campaigns)
                    : null
                }
                compareEnabled={gadsCompareEnabled}
                loadingComparison={gadsCompareEnabled && loadingPreviousPeriod}
                getCampaignType={(campaign) => getChannelTypeName(campaign.advertisingChannelType)}
                metrics={campaignTypesMetrics}
                moduleId="campaign_types"
                accountId={selectedAccount?.id || 'global'}
                formatNumber={formatNumber}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                loading={loadingCampaigns}
                onTypeClick={(typeName) => {
                  if (selectedCampaignFilter) setSelectedCampaignFilter(null);
                  const typeMapping = {
                    'Non spécifié': 0, 'Inconnu': 1, 'Recherche': 2, 'Display': 3, 'Shopping': 4,
                    'Hôtel': 5, 'Vidéo': 6, 'Multi-canal': 7, 'Local': 8, 'Intelligente': 9,
                    'Performance Max': 10, 'Services locaux': 11, 'Découverte': 12, 'Voyage': 13,
                    'DemandGen': 14, 'App (iOS)': 15, 'App (Android)': 16, 'Autre': 17
                  };
                  setSelectedCampaignTypeFilter({ type: typeMapping[typeName], name: typeName });
                }}
              />
            );
          })()}

          {/* Section Réseaux */}
          {selectedDashboardModel === 'global' && selectedAccount && resultsData?.charts?.length > 0 && !gadsLoading && (
            <>
            <PartialDataNotice failedSources={networksPreloadData?.failedSources} resolveName={resolveAccountName} />
            <NetworksTableModular
              selectedAccount={selectedAccount}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              formatNumber={formatNumber}
              isVisible={showNetworks}
              onToggleVisibility={toggleNetworks}
              selectedCampaignFilter={selectedCampaignFilter}
              selectedCampaignTypeFilter={selectedCampaignTypeFilter}
              campaignsData={campaignsData?.data?.[0]?.campaigns || []}
              token={token}
              setError={setError}
              selectedLabelsFilter={selectedLabelsFilter}
              campaignMatchesLabelsFilter={campaignMatchesLabelsFilter}
              previousPeriodData={previousPeriodData}
              compareEnabled={gadsCompareEnabled}
              comparisonType={gadsComparisonType}
              loadingComparison={loadingPreviousPeriod}
              userRole={userRole}
              isClientModuleVisible={isClientModuleVisible}
              toggleClientModuleVisibility={toggleClientModuleVisibility}
              preloadedData={networksPreloadData}
            />
            </>
          )}

          {/* Section Campagnes - Utilisation du composant CampaignsTable */}
          {selectedDashboardModel === 'global' && campaignsData?.data && resultsData?.charts?.length > 0 && !gadsLoading && (
            <div className="campaigns-table-container">
              <>
              <PartialDataNotice failedSources={campaignsData?.failedSources} resolveName={resolveAccountName} />
              <CampaignsTable
                rawData={{
                  account: {
                    campaigns: campaignsData.data[0]?.campaigns || []
                  }
                }}
                isVisible={showCampaignDetails}
                onToggleVisibility={toggleCampaignDetails}
                formatNumber={formatNumber}
                selectedCampaignFilter={selectedCampaignFilter}
                setSelectedCampaignFilter={setSelectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                filteredCampaignIds={labelFilteredCampaignIds}
                campagnesAccountId={selectedAccount?.sourceId || selectedAccount?.id}
                campagnesAccountType="GOOGLE_ADS"
                campagnesToken={token}
                selectedAccount={(() => {
                  // Récupérer le premier compte depuis rawData avec son ID
                  const firstAccountData = Object.values(campaignsData.rawData || {})[0];
                  return firstAccountData ? {
                    id: firstAccountData.id || Object.keys(campaignsData.rawData)[0],
                    name: firstAccountData.accountName
                  } : null;
                })()}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                loadingComparison={loadingPreviousPeriod}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
              />
              </>
            </div>
          )}

          {/* Section Groupes d'annonces - Utilisation du composant AdGroupsTable */}
          {selectedDashboardModel === 'global' && adGroupsData?.data && resultsData?.charts?.length > 0 && !gadsLoading && (
            <div className="ad-groups-table-container">
              <>
              <PartialDataNotice failedSources={adGroupsData?.failedSources} resolveName={resolveAccountName} />
              <AdGroupsTable
                rawData={{ account: adGroupsData.data[0] || { adGroups: [] } }}
                campaignsData={campaignsData?.data?.[0]?.campaigns || []}
                isVisible={showAdGroupDetails}
                onToggleVisibility={toggleAdGroupDetails}
                formatNumber={formatNumber}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                selectedLabelsFilter={selectedLabelsFilter}
                campaignMatchesLabelsFilter={campaignMatchesLabelsFilter}
                selectedAccount={(() => {
                  // Récupérer le premier compte depuis rawData avec son ID
                  const firstAccountData = Object.values(adGroupsData.rawData || {})[0];
                  return firstAccountData ? {
                    id: firstAccountData.id || Object.keys(adGroupsData.rawData)[0],
                    name: firstAccountData.accountName
                  } : null;
                })()}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                token={token}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
              />
              </>
            </div>
          )}

          {/* Section Groupes de composants Performance Max */}
          <AssetGroupsTable
            rawData={autoCharts.rawData}
            selectedAccount={selectedAccount}
            isVisible={showAssetGroups}
            onToggleVisibility={toggleAssetGroups}
            formatNumber={formatNumber}
            selectedCampaignFilter={selectedCampaignFilter}
            selectedCampaignTypeFilter={selectedCampaignTypeFilter}
            selectedLabelsFilter={selectedLabelsFilter}
            campaignMatchesLabelsFilter={campaignMatchesLabelsFilter}
            previousPeriodData={previousPeriodData}
            compareEnabled={gadsCompareEnabled}
            comparisonType={gadsComparisonType}
            loadingComparison={loadingPreviousPeriod}
            token={token}
            selectedPeriod={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
          />

          {/* Section Appareils */}
          {(() => {
            console.log('📱 [DEVICES-EXISTING-DEBUG] Vérification devicesData:', {
              hasDevicesData: !!devicesData,
              devicesData: devicesData,
              hasCharts: !!devicesData?.charts,
              chartsLength: devicesData?.charts?.length
            });
            return devicesData?.charts;
          })() && (
            <div className="devices-data-container">
              <>
              <PartialDataNotice failedSources={devicesData?.failedSources} resolveName={resolveAccountName} />
              <DevicesTable
                charts={devicesData.charts}
                campaignsData={campaignsData?.data?.[0]?.campaigns || []}
                isVisible={showDeviceConversions}
                onToggleVisibility={toggleDeviceConversions}
                formatNumber={formatNumber}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                selectedLabelsFilter={selectedLabelsFilter}
                campaignMatchesLabelsFilter={campaignMatchesLabelsFilter}
                selectedAccount={(() => {
                  // Récupérer le premier compte depuis devicesData.rawData avec son ID
                  const firstAccountData = Object.values(devicesData?.rawData || {})[0];
                  return firstAccountData ? {
                    id: firstAccountData.id || Object.keys(devicesData.rawData)[0],
                    name: firstAccountData.accountName
                  } : null;
                })()}
                COLORS={COLORS}
                labelFilteredCampaignIds={labelFilteredCampaignIds}
                token={token}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                loadingComparison={loadingPreviousPeriod}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
              />
              </>
            </div>
          )}

          {/* Section Top & Flop Termes de recherche */}
          {selectedDashboardModel === 'global' && selectedAccount && resultsData?.charts?.length > 0 && !gadsLoading && (
            <TopFlopSearchTermsTable
              selectedAccount={selectedAccount}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              formatNumber={formatNumber}
              isVisible={showTopFlopSearchTerms}
              onToggleVisibility={toggleTopFlopSearchTerms}
              selectedCampaignFilter={selectedCampaignFilter}
              selectedCampaignTypeFilter={selectedCampaignTypeFilter}
              campaignsData={campaignsData?.data?.[0]?.campaigns || []}
              performanceMaxData={performanceMaxData}
              token={token}
              setError={setError}
              selectedLabelsFilter={selectedLabelsFilter}
              campaignMatchesLabelsFilter={campaignMatchesLabelsFilter}
              previousPeriodData={previousPeriodData}
              compareEnabled={gadsCompareEnabled}
              comparisonType={gadsComparisonType}
              userRole={userRole}
              isClientModuleVisible={isClientModuleVisible}
              toggleClientModuleVisibility={toggleClientModuleVisibility}
              loadingComparison={loadingPreviousPeriod}
              preloadedData={searchTermsPreloadData}
            />
          )}

          {/* Section Jours de la semaine */}
          {weekdaysData?.charts && (
            <div className="weekdays-data-container">
              <>
              <PartialDataNotice failedSources={weekdaysData?.failedSources} resolveName={resolveAccountName} />
              <WeekdaysTable
                charts={weekdaysData.charts}
                campaignsData={campaignsData?.data?.[0]?.campaigns || []}
                isVisible={showWeekdayConversions}
                onToggleVisibility={toggleWeekdayConversions}
                formatNumber={formatNumber}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                selectedAccount={(() => {
                  // Récupérer le premier compte depuis weekdaysData.rawData avec son ID
                  const firstAccountData = Object.values(weekdaysData?.rawData || {})[0];
                  return firstAccountData ? {
                    id: firstAccountData.id || Object.keys(weekdaysData.rawData)[0],
                    name: firstAccountData.accountName
                  } : null;
                })()}
                COLORS={COLORS}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                loadingComparison={loadingPreviousPeriod}
                token={token}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
              />
              </>
            </div>
          )}

          {/* Section géographique supprimée - remplacée par GeographyTableModular */}
          {false && (() => {
            // Debug : vérifier les charts disponibles
            
            // Debug: afficher tous les IDs de graphiques disponibles
            console.log('🌍 [GEO] Debug - Graphiques disponibles:', autoCharts.charts.map(chart => chart.id));
            
            // Chercher le graphique des conversions par origine géographique
            const geoChart = autoCharts.charts.find(chart => chart.id === 'real-geo-conversions');
            
            console.log('🌍 [GEO] Debug - geoChart trouvé:', !!geoChart);
            if (geoChart) {
              console.log('🌍 [GEO] Debug - geoChart.data length:', geoChart.data?.length || 0);
              console.log('🌍 [GEO] Debug - geoChart.dataDetailed length:', geoChart.dataDetailed?.length || 0);
              console.log('🌍 [GEO] Debug - selectedCampaignTypeFilter:', !!selectedCampaignTypeFilter);
            }
            
            if (!geoChart || !geoChart.data || geoChart.data.length === 0) {
              console.log('🌍 [GEO] Debug - Pas de données géographiques disponibles');
              return null;
            }

            // Filtrer les données géographiques si un filtre de type de campagne est appliqué
            let filteredGeoData = geoChart.data;
            
            if (selectedCampaignTypeFilter) {
              console.log('🌍 [GEO] Filtrage par type de campagne:', selectedCampaignTypeFilter);
              
              // Obtenir les campagnes du type sélectionné depuis autoCharts (dashboard global)
              const campaignsData = autoCharts.rawData ? 
                Object.values(autoCharts.rawData).find(data => data.campaigns)?.campaigns || [] : [];
              
              console.log('🌍 [GEO] Campagnes disponibles pour lookup:', campaignsData.length);
              
              // Trouver les IDs des campagnes qui correspondent au type sélectionné
              const matchingCampaignIds = campaignsData
                .filter(campaign => {
                  const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
                  const matches = campaignType === selectedCampaignTypeFilter.type ||
                                 String(campaignType) === String(selectedCampaignTypeFilter.type) ||
                                 campaignType === selectedCampaignTypeFilter.name ||
                                 String(campaignType) === String(selectedCampaignTypeFilter.name);
                  
                  if (matches) {
                    console.log('🌍 [GEO] Campagne correspondante:', campaign.name, 'ID:', campaign.id, 'Type:', campaignType);
                  }
                  
                  return matches;
                })
                .map(campaign => campaign.id || campaign.campaignId);
              
              console.log('🌍 [GEO] IDs des campagnes correspondantes:', matchingCampaignIds);
              
              // Vérifier si nous avons des données détaillées avec campaignId
              if (geoChart.dataDetailed && Array.isArray(geoChart.dataDetailed)) {
                console.log('🌍 [GEO] Utilisation des données détaillées pour le filtrage');
                
                // Utiliser les données détaillées pour le filtrage
                const filteredDetailedData = {};
                
                geoChart.dataDetailed.forEach((geoEntry, index) => {
                  // Debug: montrer la structure de la première entrée
                  if (index === 0) {
                    console.log('🌍 [GEO] Structure première entrée détaillée:', geoEntry);
                    console.log('🌍 [GEO] Champs disponibles:', Object.keys(geoEntry));
                  }
                  
                  // Vérifier si cette entrée appartient à une campagne du type filtré
                  const geoCampaignId = geoEntry.campaignId || geoEntry.campaign_id;
                  
                  const matchesCampaignType = matchingCampaignIds.includes(geoCampaignId) ||
                                             matchingCampaignIds.includes(String(geoCampaignId)) ||
                                             matchingCampaignIds.includes(Number(geoCampaignId)) ||
                                             matchingCampaignIds.some(id => String(id) === String(geoCampaignId));
                  
                  if (matchesCampaignType) {
                    console.log('🌍 [GEO] Entrée géographique correspondante:', geoEntry.name, 'Campaign ID:', geoCampaignId);
                    
                    const countryName = geoEntry.name || geoEntry.country || geoEntry.location;
                    
                    if (!filteredDetailedData[countryName]) {
                      filteredDetailedData[countryName] = {
                        name: countryName,
                        impressions: 0,
                        clicks: 0,
                        cost: 0,
                        conversions: 0,
                        value: 0,
                        conversionsValue: 0
                      };
                    }
                    
                    const country = filteredDetailedData[countryName];
                    country.impressions += geoEntry.impressions || 0;
                    country.clicks += geoEntry.clicks || 0;
                    country.cost += geoEntry.cost || 0;
                    country.conversions += geoEntry.conversions || 0;
                    country.value += geoEntry.value || geoEntry.conversions || 0;
                    country.conversionsValue += geoEntry.conversionsValue || 0;
                  }
                });
                
                // Convertir en tableau et calculer les métriques dérivées
                filteredGeoData = Object.values(filteredDetailedData).map(country => ({
                  ...country,
                  ctr: country.impressions > 0 ? (country.clicks / country.impressions * 100) : 0,
                  cpc: country.clicks > 0 ? (country.cost / country.clicks) : 0,
                  conversionRate: country.clicks > 0 ? (country.conversions / country.clicks * 100) : 0,
                  cpa: country.conversions > 0 ? (country.cost / country.conversions) : 0,
                  cos: country.conversionsValue > 0 ? (country.cost / country.conversionsValue * 100) : 0
                }));
                
                console.log('🌍 [GEO] Données après filtrage par type:', filteredGeoData.length, 'pays');
              } else {
                console.log('🌍 [GEO] Pas de données détaillées disponibles, affichage de données vides');
                // Si pas de données détaillées, afficher des données vides plutôt que non filtrées
                filteredGeoData = [];
              }
            }
            
            // Vérifier s'il y a des données valides (pas seulement des données avec des valeurs nulles/zéros)
            const hasValidGeoData = filteredGeoData.length > 0 && filteredGeoData.some(country => 
              (country?.impressions > 0) || 
              (country?.clicks > 0) || 
              (country?.cost > 0) || 
              (country?.conversions > 0)
            );
            
            // Attendre que autoCharts soit chargé et vérifier s'il y a des données valides
            if (!autoCharts.rawData || filteredGeoData.length === 0 || !hasValidGeoData) {
              return null;
            }

            // Fonction de tri pour les données géographiques
            const sortGeoData = (data, config) => {
              const sorted = [...data];
              sorted.sort((a, b) => {
                let aValue, bValue;
                
                // Calcul spécial pour CPA
                if (config.key === 'cpa') {
                  aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
                  bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
                } else if (config.key === 'name') {
                  aValue = a.name.toLowerCase();
                  bValue = b.name.toLowerCase();
                  return config.direction === 'asc' ? 
                    aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                } else {
                  aValue = a[config.key] || 0;
                  bValue = b[config.key] || 0;
                }
                
                if (config.direction === 'asc') {
                  return aValue - bValue;
                } else {
                  return bValue - aValue;
                }
              });
              return sorted;
            };

            const handleGeoSort = (key) => {
              let direction = 'desc';
              if (geoSortConfig.key === key && geoSortConfig.direction === 'desc') {
                direction = 'asc';
              }
              setGeoSortConfig({ key, direction });
            };

            // Composant tableau géographique
            const GeoTable = ({ geoData, sortConfig, onSort }) => {
              const sortedData = sortGeoData(geoData, sortConfig);
              
              // Calculer les totaux
              const totals = geoData.reduce((acc, country) => ({
                impressions: acc.impressions + (country.impressions || 0),
                clicks: acc.clicks + (country.clicks || 0),
                cost: acc.cost + (country.cost || 0),
                conversions: acc.conversions + (country.conversions || 0),
                conversionsValue: acc.conversionsValue + (country.conversionsValue || 0)
              }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });
              
              // Calculer les moyennes pour CTR, CPC, taux de conversion, CPA et COS
              totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
              totals.cpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
              totals.conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
              totals.cpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
              totals.cos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;

              return (
                <div className="campaigns-table-wrapper">
                  <table className="campaigns-table">
                    <thead>
                      <tr>
                        <th onClick={() => onSort('name')} className="sortable">
                          Pays {sortConfig.key === 'name' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('impressions')} className="sortable numeric">
                          Impressions {sortConfig.key === 'impressions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('ctr')} className="sortable numeric">
                          CTR {sortConfig.key === 'ctr' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('clicks')} className="sortable numeric">
                          Clics {sortConfig.key === 'clicks' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('cpc')} className="sortable numeric">
                          CPC {sortConfig.key === 'cpc' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('cost')} className="sortable numeric">
                          Coût {sortConfig.key === 'cost' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('conversionRate')} className="sortable numeric">
                          Taux Conv. {sortConfig.key === 'conversionRate' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('conversions')} className="sortable numeric">
                          Conversions {sortConfig.key === 'conversions' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('cpa')} className="sortable numeric">
                          CPA {sortConfig.key === 'cpa' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('conversionsValue')} className="sortable numeric">
                          Valeur Conv. {sortConfig.key === 'conversionsValue' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                        <th onClick={() => onSort('cos')} className="sortable numeric">
                          COS {sortConfig.key === 'cos' && (sortConfig.direction === 'asc' ? '▲' : '▼')}
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {sortedData.map((country, index) => {
                        const cos = (country.conversionsValue || 0) > 0 ? (country.cost / (country.conversionsValue || 1) * 100) : 0;
                        const conversionRate = country.clicks > 0 ? (country.conversions / country.clicks * 100) : 0;
                        const cpa = country.conversions > 0 ? (country.cost / country.conversions) : 0;
                        
                        return (
                          <tr key={country.name || index}>
                            <td className="campaign-name">{country.name}</td>
                            <td className="numeric">{formatNumber(country.impressions || 0)}</td>
                            <td className="numeric">{formatNumber(country.ctr || 0, 2)}%</td>
                            <td className="numeric">{formatNumber(country.clicks || 0)}</td>
                            <td className="numeric">€{formatNumber(country.cpc || 0, 2)}</td>
                            <td className="numeric">€{formatNumber(country.cost || 0, 2)}</td>
                            <td className="numeric">{formatNumber(conversionRate, 2)}%</td>
                            <td className="numeric">{formatNumber(country.conversions || 0, 1)}</td>
                            <td className="numeric">{cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}</td>
                            <td className="numeric">€{formatNumber(country.conversionsValue || 0, 2)}</td>
                            <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                              {formatNumber(cos, 1)}%
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      <tr className="totals-row">
                        <td className="campaign-name"><strong>TOTAL</strong></td>
                        <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>
                        <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                        <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>
                        <td className="numeric"><strong>€{formatNumber(totals.cpc, 2)}</strong></td>
                        <td className="numeric"><strong>€{formatNumber(totals.cost, 2)}</strong></td>
                        <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                        <td className="numeric"><strong>{formatNumber(totals.conversions, 1)}</strong></td>
                        <td className="numeric"><strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong></td>
                        <td className="numeric"><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></td>
                        <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                          <strong>{formatNumber(totals.cos, 1)}%</strong>
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              );
            };

            // Pour les clients, ne pas afficher si le module est masqué
            if (userRole === 'CLIENT' && !isClientModuleVisible('geography')) {
              return null;
            }

            return (
              <div className="device-conversions-section">
                <div className="section-header">
                  <h4><MapPin size={16} />&nbsp;Origine géographique</h4>
                  <ModuleVisibilityToggle
                    isVisible={isClientModuleVisible('geography')}
                    onToggle={toggleClientModuleVisibility}
                    userRole={userRole}
                    moduleId="geography"
                  />
                </div>
                {(userRole !== 'CLIENT' || isClientModuleVisible('geography')) && (
                  <>
                    <div className="device-chart-container">
                      <div className="chart-card full-width">
                    <div className="chart-preview">
                      <ResponsiveContainer width="100%" height={300}>
                        <PieChart>
                          <Pie
                            data={filteredGeoData}
                            cx="50%"
                            cy="50%"
                            labelLine={false}
                            label={({ name, value, percentage }) => `${name}: ${value} (${percentage}%)`}
                            outerRadius={80}
                            fill="#8884d8"
                            dataKey="value"
                          >
                            {filteredGeoData.map((entry, index) => (
                              <Cell key={`geo-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip formatter={(value, name) => [value + ' clics', name]} />
                          <Legend />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>

                  </div>
                </div>
                
                    {/* Tableau détaillé dans le même bloc */}
                    <div style={{ marginTop: '20px' }}>
                      <GeoTable 
                        geoData={filteredGeoData}
                        sortConfig={geoSortConfig}
                        onSort={setGeoSortConfig}
                      />
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* Section Top & Flop Catégories - Composant modulaire */}
          {selectedDashboardModel === 'global' && resultsData?.charts?.length > 0 && !gadsLoading && (
            <div className="top-flop-categories-container">
              <>
              <PartialDataNotice failedSources={topFlopCategoriesPreloadData?.failedSources} resolveName={resolveAccountName} />
              <TopFlopCategoriesTableModular
                selectedAccount={selectedAccount}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                formatNumber={formatNumber}
                isVisible={showTopFlopCategories}
                onToggleVisibility={toggleTopFlopCategories}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                campaignIds={idsCampagnesFiltrees}
                token={token}
                setError={setError}
                selectedLabelsFilter={selectedLabelsFilter}
                campaignMatchesLabelsFilter={campaignMatchesLabelsFilter}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                loadingComparison={loadingPreviousPeriod}
                preloadedData={topFlopCategoriesPreloadData}
              />
              </>
            </div>
          )}

          {/* Section Top & Flop Groupes Produits - DÉSACTIVÉ TEMPORAIREMENT
          {selectedDashboardModel === 'global' && (
            <div className="top-flop-itemgroups-container">
              <TopFlopItemGroupsTableModular
                selectedAccount={selectedAccount}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                formatNumber={formatNumber}
                isVisible={showTopFlopItemGroups}
                onToggleVisibility={toggleTopFlopItemGroups}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                token={token}
                setError={setError}
                selectedLabelsFilter={selectedLabelsFilter}
                campaignMatchesLabelsFilter={campaignMatchesLabelsFilter}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
              />
            </div>
          )}
          */}

          {/* Section Top & Flop Produits - Composant modulaire */}
          {(() => {
            return selectedDashboardModel === 'global' && resultsData?.charts?.length > 0 && !gadsLoading;
          })() && (
            <div className="top-flop-products-container">
              <>
              <PartialDataNotice failedSources={topFlopProductsData?.failedSources} resolveName={resolveAccountName} />
              <TopFlopProductsTableModular
                selectedAccount={selectedAccount}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                formatNumber={formatNumber}
                isVisible={showTopFlopProducts}
                onToggleVisibility={toggleTopFlopProducts}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                campaignIds={idsCampagnesFiltrees}
                token={token}
                setError={setError}
                preloadedData={topFlopProductsData}
                selectedLabelsFilter={selectedLabelsFilter}
                campaignMatchesLabelsFilter={campaignMatchesLabelsFilter}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                loadingComparison={loadingPreviousPeriod}
              />
              </>
            </div>
          )}

          {/* Section Geography - Données par pays */}
          {selectedDashboardModel === 'global' && resultsData?.charts?.length > 0 && !gadsLoading && (
            <div className="geography-data-container">
              <>
              <PartialDataNotice failedSources={geographyData?.failedSources} resolveName={resolveAccountName} />
              <GeographyTableModular
                selectedAccount={selectedAccount}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                formatNumber={formatNumber}
                isVisible={showGeography}
                onToggleVisibility={toggleGeography}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                token={token}
                setError={setError}
                preloadedData={geographyData}
                selectedLabelsFilter={selectedLabelsFilter}
                labelFilteredCampaignIds={idsCampagnesFiltrees}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                loadingComparison={loadingPreviousPeriod}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
              />
              </>
            </div>
          )}

          {/* Section Age Ranges - Tranches d'âge */}
          {selectedDashboardModel === 'global' && resultsData?.charts?.length > 0 && !gadsLoading && (
            <div className="age-ranges-data-container">
              <>
              <PartialDataNotice failedSources={ageRangesPreloadData?.failedSources} resolveName={resolveAccountName} />
              <AgeRangesTableModular
                selectedAccount={selectedAccount}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                formatNumber={formatNumber}
                isVisible={showAgeRanges}
                onToggleVisibility={toggleAgeRanges}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                token={token}
                setError={setError}
                selectedLabelsFilter={selectedLabelsFilter}
                labelFilteredCampaignIds={labelFilteredCampaignIds}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                loadingComparison={loadingPreviousPeriod}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                preloadedData={ageRangesPreloadData}
              />
              </>
            </div>
          )}

          {/* Section Gender - Genre */}
          {selectedDashboardModel === 'global' && resultsData?.charts?.length > 0 && !gadsLoading && (
            <div className="gender-data-container">
              <>
              <PartialDataNotice failedSources={genderPreloadData?.failedSources} resolveName={resolveAccountName} />
              <GenderTableModular
                selectedAccount={selectedAccount}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                formatNumber={formatNumber}
                isVisible={showGender}
                onToggleVisibility={toggleGender}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                token={token}
                setError={setError}
                selectedLabelsFilter={selectedLabelsFilter}
                labelFilteredCampaignIds={labelFilteredCampaignIds}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                loadingComparison={loadingPreviousPeriod}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                preloadedData={genderPreloadData}
              />
              </>
            </div>
          )}


          {/* Section Audiences */}
          {selectedDashboardModel === 'global' && resultsData?.charts?.length > 0 && !gadsLoading && (
            <div className="audiences-data-container">
              <>
              <PartialDataNotice failedSources={audiencesPreloadData?.failedSources} resolveName={resolveAccountName} />
              <AudiencesTableModular
                selectedAccount={selectedAccount}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                formatNumber={formatNumber}
                isVisible={showAudiences}
                onToggleVisibility={toggleAudiences}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                token={token}
                setError={setError}
                selectedLabelsFilter={selectedLabelsFilter}
                labelFilteredCampaignIds={idsCampagnesFiltrees}
                previousPeriodData={previousPeriodData}
                compareEnabled={gadsCompareEnabled}
                comparisonType={gadsComparisonType}
                globalTotals={(() => {
                  // Calculer les totaux globaux depuis les données de campagnes (hors Performance Max)
                  let filteredCampaigns = campaignsData?.data?.[0]?.campaigns || [];

                  // Filtrer par campagne si une campagne est sélectionnée
                  if (selectedCampaignFilter) {
                    filteredCampaigns = filteredCampaigns.filter(campaign =>
                      String(campaign.id) === String(selectedCampaignFilter.id) ||
                      String(campaign.campaignId) === String(selectedCampaignFilter.id)
                    );
                  }

                  // Restreindre aux filtres par label et par type : sans cela, « Sans audiences »
                  // rapporte le trafic du compte entier sous un en-tête filtré.
                  filteredCampaigns = restreindreAuxCampagnesFiltrees(filteredCampaigns);

                  // Exclure les campagnes Performance Max (type 10) car elles n'ont pas de données d'audiences
                  const nonPmaxCampaigns = filteredCampaigns.filter(campaign => {
                    const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
                    return campaignType !== 10 && String(campaignType) !== '10';
                  });
                  if (!nonPmaxCampaigns.length) return null;
                  return nonPmaxCampaigns.reduce((acc, campaign) => ({
                    impressions: acc.impressions + (campaign.impressions || 0),
                    clicks: acc.clicks + (campaign.clicks || 0),
                    cost: acc.cost + (campaign.cost || 0),
                    conversions: acc.conversions + (campaign.conversions || 0),
                    conversionsValue: acc.conversionsValue + (campaign.conversionsValue || 0),
                    views: (acc.views || 0) + (campaign.views || 0),
                    videoImpressions: (acc.videoImpressions || 0) + (campaign.videoImpressions || 0),
                    viewCost: (acc.viewCost || 0) + (campaign.viewCost || 0)
                  }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, views: 0, videoImpressions: 0, viewCost: 0 });
                })()}
                previousGlobalTotals={(() => {
                  // Calculer les totaux globaux de la période précédente (hors Performance Max)
                  if (!gadsCompareEnabled || !previousPeriodData?.campaigns) return null;
                  // Mêmes restrictions que la période courante, sinon la comparaison oppose un
                  // périmètre filtré à un compte entier et affiche des écarts inventés.
                  let prevCampaigns = previousPeriodData.campaigns || [];
                  if (selectedCampaignFilter) {
                    prevCampaigns = prevCampaigns.filter(campaign =>
                      String(campaign.id) === String(selectedCampaignFilter.id) ||
                      String(campaign.campaignId) === String(selectedCampaignFilter.id)
                    );
                  }
                  prevCampaigns = restreindreAuxCampagnesFiltrees(prevCampaigns);
                  // Exclure les campagnes Performance Max (type 10)
                  const nonPmaxCampaigns = prevCampaigns.filter(campaign => {
                    const campaignType = campaign.advertisingChannelType || campaign.type || campaign.campaignType;
                    return campaignType !== 10 && String(campaignType) !== '10';
                  });
                  if (!nonPmaxCampaigns.length) return null;
                  return nonPmaxCampaigns.reduce((acc, campaign) => ({
                    impressions: acc.impressions + (campaign.impressions || 0),
                    clicks: acc.clicks + (campaign.clicks || 0),
                    cost: acc.cost + (campaign.cost || 0),
                    conversions: acc.conversions + (campaign.conversions || 0),
                    conversionsValue: acc.conversionsValue + (campaign.conversionsValue || 0),
                    views: (acc.views || 0) + (campaign.views || 0),
                    videoImpressions: (acc.videoImpressions || 0) + (campaign.videoImpressions || 0),
                    viewCost: (acc.viewCost || 0) + (campaign.viewCost || 0)
                  }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, views: 0, videoImpressions: 0, viewCost: 0 });
                })()}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                loadingComparison={loadingPreviousPeriod}
                preloadedData={audiencesPreloadData}
              />
              </>
            </div>
          )}

          {/* Section des pages de destination - Composant modulaire */}
          {/* TEMPORAIREMENT DÉSACTIVÉ
          {selectedDashboardModel === 'global' && (
            <LandingPagesTableModular
              selectedAccount={selectedAccount}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              formatNumber={formatNumber}
              isVisible={showLandingPages}
              onToggleVisibility={() => setShowLandingPages(!showLandingPages)}
              selectedCampaignFilter={selectedCampaignFilter}
              token={token}
              setError={setError}
            />
          )}
          */}

            </>
          )}

        </div>
      )}

      {/* Dashboard Mots-clés */}
      {selectedDashboardModel === 'keywords' && selectedAccount && (
        <div className="keywords-dashboard" style={{ marginTop: '10px' }}>

          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingCharts && accountHasNoData && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {/* Contenu du dashboard - affiché uniquement si données disponibles */}
          {!accountHasNoData && (
            <>
          {/* Sticky Campaign Filter Banner - Affiché seulement après le chargement */}
          {!(loadingCharts || loadingPerformanceMax) && selectedAccount && (
            <div style={{
              background: 'linear-gradient(135deg, #FAF9F6 0%, #F5F4F0 100%)',
              padding: '16px 20px',
              borderRadius: '8px',
              marginTop: '-5px',
              marginBottom: '10px',
              position: 'sticky',
              top: '0',
              zIndex: '1000'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '0.95em',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> Filtrer:
                </span>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    placeholder="Rechercher une campagne..."
                    value={selectedCampaignFilter ? (selectedCampaignFilter.name || selectedCampaignFilter.campaignName) : gaCampaignSearchQuery}
                    onChange={(e) => {
                      if (selectedCampaignFilter) {
                        setSelectedCampaignFilter(null);
                      }
                      setGaCampaignSearchQuery(e.target.value);
                      setShowGaCampaignAutocomplete(true);
                    }}
                    onFocus={() => setShowGaCampaignAutocomplete(true)}
                    onBlur={() => {
                      setTimeout(() => setShowGaCampaignAutocomplete(false), 200);
                    }}
                    style={{
                      padding: '4px 12px',
                      paddingRight: (gaCampaignSearchQuery || selectedCampaignFilter) ? '28px' : '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: selectedCampaignFilter ? '#dbeafe' : 'white',
                      color: '#374151',
                      fontSize: '0.9em',
                      width: '400px',
                      height: '35px',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                  {(gaCampaignSearchQuery || selectedCampaignFilter) && (
                    <button
                      onClick={() => {
                        setGaCampaignSearchQuery('');
                        setSelectedCampaignFilter(null);
                        setShowGaCampaignAutocomplete(false);
                      }}
                      style={{
                        position: 'absolute',
                        right: '6px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: selectedCampaignFilter ? '#1a1a1a' : '#9ca3af',
                        padding: '2px',
                        fontSize: '14px',
                        lineHeight: 1,
                        zIndex: 1
                      }}
                      title={selectedCampaignFilter ? "Effacer le filtre campagne" : "Effacer la recherche"}
                    >
                      ✕
                    </button>
                  )}
                  {/* Autocomplete dropdown */}
                  {showGaCampaignAutocomplete && (() => {
                    const searchLower = gaCampaignSearchQuery.toLowerCase().trim();
                    const filteredCampaigns = searchLower
                      ? [...availableKeywordsCampaigns]
                          .filter(c => (c.name || c.campaignName || '').toLowerCase().includes(searchLower))
                          .sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''))
                      : [...availableKeywordsCampaigns].sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''));

                    if (filteredCampaigns.length === 0) {
                      return (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          marginTop: '4px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          zIndex: 1001,
                          padding: '12px',
                          color: '#6b7280',
                          fontSize: '0.9em'
                        }}>
                          Aucune campagne trouvée
                        </div>
                      );
                    }

                    return (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        zIndex: 1001,
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        <div style={{
                          padding: '8px 12px',
                          fontSize: '0.8em',
                          color: '#6b7280',
                          borderBottom: '1px solid #e5e7eb',
                          backgroundColor: '#f9fafb'
                        }}>
                          {filteredCampaigns.length} campagne{filteredCampaigns.length > 1 ? 's' : ''} disponible{filteredCampaigns.length > 1 ? 's' : ''}
                        </div>
                        {filteredCampaigns.map(campaign => (
                          <div
                            key={campaign.id || campaign.campaignId}
                            onClick={() => {
                              setSelectedCampaignFilter(campaign);
                              setGaCampaignSearchQuery('');
                              setShowGaCampaignAutocomplete(false);
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f3f4f6',
                              fontSize: '0.9em',
                              color: '#374151',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            {campaign.name || campaign.campaignName}
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
              </div>
            </div>
          )}

          {!(loadingCharts || loadingPerformanceMax) && (
            <>
              {selectedCpcTierFilter && (
                  <div style={{
                    backgroundColor: '#fff3cd',
                    color: '#856404',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: '1px solid #ffeaa7'
                  }}>
                    📊 CPC: {formatNumber(selectedCpcTierFilter.min, 2)}€ - {formatNumber(selectedCpcTierFilter.max, 2)}€
                    <button
                      onClick={() => setSelectedCpcTierFilter(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#856404',
                        cursor: 'pointer',
                        padding: '0 2px',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        borderRadius: '2px'
                      }}
                      title="Supprimer le filtre palier CPC"
                      onMouseOver={(e) => e.target.style.backgroundColor = '#ffeaa7'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      ×
                    </button>
                  </div>
                )}
                {selectedQualityScoreFilter !== null && selectedQualityScoreFilter !== undefined && (
                  <div style={{
                    backgroundColor: '#f3e5f5',
                    color: '#7b1fa2',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: '1px solid #ce93d8'
                  }}>
                    📊 Score: {selectedQualityScoreFilter === 0 ? 'Non défini' : selectedQualityScoreFilter}
                    <button
                      onClick={() => setSelectedQualityScoreFilter(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#7b1fa2',
                        cursor: 'pointer',
                        padding: '0 2px',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        borderRadius: '2px'
                      }}
                      title="Supprimer le filtre score de qualité"
                      onMouseOver={(e) => e.target.style.backgroundColor = '#ce93d8'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      ×
                    </button>
                  </div>
                )}
                {selectedMatchTypeFilter !== null && selectedMatchTypeFilter !== undefined && (
                  <div style={{
                    backgroundColor: '#e8f5e8',
                    color: '#2e7d32',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: '1px solid #a5d6a7'
                  }}>
                    🔧 {selectedMatchTypeFilter}
                    <button
                      onClick={() => setSelectedMatchTypeFilter(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#2e7d32',
                        cursor: 'pointer',
                        padding: '0 2px',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        borderRadius: '2px'
                      }}
                      title="Supprimer le filtre type de correspondance"
                      onMouseOver={(e) => e.target.style.backgroundColor = '#a5d6a7'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      ×
                    </button>
                  </div>
                )}
            </>
          )}

          {/* Composants du dashboard keywords - Affichés seulement après le chargement */}
          {!(loadingCharts || loadingPerformanceMax) && (
            <>
              <QualityScoreBreakdown
            selectedAccount={selectedAccount}
            selectedPeriod={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            formatNumber={formatNumber}
            isVisible={showQualityScoreBreakdown}
            onToggleVisibility={() => setShowQualityScoreBreakdown(!showQualityScoreBreakdown)}
            token={token}
            onQualityScoreFilter={setSelectedQualityScoreFilter}
            selectedQualityScoreFilter={selectedQualityScoreFilter}
            selectedMatchTypeFilter={selectedMatchTypeFilter}
            selectedCampaignFilter={selectedCampaignFilter}
            selectedCpcTierFilter={selectedCpcTierFilter}
            userRole={userRole}
          />
          
          <MatchTypeBreakdown
            selectedAccount={selectedAccount}
            selectedPeriod={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            formatNumber={formatNumber}
            isVisible={showMatchTypeBreakdown}
            onToggleVisibility={() => setShowMatchTypeBreakdown(!showMatchTypeBreakdown)}
            token={token}
            onMatchTypeFilter={setSelectedMatchTypeFilter}
            selectedMatchTypeFilter={selectedMatchTypeFilter}
            selectedQualityScoreFilter={selectedQualityScoreFilter}
            selectedCampaignFilter={selectedCampaignFilter}
            selectedCpcTierFilter={selectedCpcTierFilter}
            userRole={userRole}
            compareEnabled={gadsCompareEnabled}
            comparisonType={gadsComparisonType}
          />
          
          <CpcTierBreakdown
            selectedAccount={selectedAccount}
            selectedPeriod={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            formatNumber={formatNumber}
            isVisible={showCpcTierBreakdown}
            onToggleVisibility={() => setShowCpcTierBreakdown(!showCpcTierBreakdown)}
            onTierClick={(tierMin, tierMax) => {
              console.log('🎯 [CPC-TIER-FILTER] Tranche sélectionnée:', { tierMin, tierMax });
              setSelectedCpcTierFilter({ min: tierMin, max: tierMax });
            }}
            selectedCpcTierFilter={selectedCpcTierFilter}
            selectedCampaignFilter={selectedCampaignFilter}
            selectedQualityScoreFilter={selectedQualityScoreFilter}
            selectedMatchTypeFilter={selectedMatchTypeFilter}
            token={token}
            userRole={userRole}
            compareEnabled={gadsCompareEnabled}
            comparisonType={gadsComparisonType}
          />
          
          <DetailedQualityScoreTable
            selectedAccount={selectedAccount}
            selectedPeriod={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            formatNumber={formatNumber}
            isVisible={showDetailedQualityScore}
            onToggleVisibility={() => setShowDetailedQualityScore(!showDetailedQualityScore)}
            selectedCampaignFilter={selectedCampaignFilter}
            selectedQualityScoreFilter={selectedQualityScoreFilter}
            selectedMatchTypeFilter={selectedMatchTypeFilter}
            selectedCpcTierFilter={selectedCpcTierFilter}
            token={token}
          />
          
          <KeywordsTable
            selectedAccount={selectedAccount}
            selectedPeriod={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            formatNumber={formatNumber}
            isVisible={showKeywords}
            onToggleVisibility={() => setShowKeywords(!showKeywords)}
            selectedCampaignFilter={selectedCampaignFilter}
            selectedQualityScoreFilter={selectedQualityScoreFilter}
            selectedMatchTypeFilter={selectedMatchTypeFilter}
            selectedCpcTierFilter={selectedCpcTierFilter}
            token={token}
            userRole={userRole}
            compareEnabled={gadsCompareEnabled}
            comparisonType={gadsComparisonType}
            onCampaignsExtracted={(keywordsData) => {
              const campaigns = extractCampaignsFromKeywords(keywordsData);
              setAvailableKeywordsCampaigns(campaigns);
            }}
          />
            </>
          )}
          </>
          )}
        </div>
      )}

      {/* Dashboard Termes de recherche - Affiche la section combinée + analyse n-gram */}
      {selectedDashboardModel === 'search-terms' && selectedAccount && (
        <div className="search-terms-dashboard" style={{ marginTop: '10px' }}>

          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingCharts && accountHasNoData && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {/* Contenu du dashboard - affiché uniquement si données disponibles */}
          {!accountHasNoData && (
            <>
          {(loadingCharts || loadingPerformanceMax) && (
            <DashboardLoadingScreen
              platformName="Google Ads"
              features={[
                { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
                { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
                { icon: <img src={googleAdsLogo} alt="Google Ads" style={{width: '28px', height: '28px'}} />, label: 'Analyse des campagnes' }
              ]}
            />
          )}
          
          {/* Filtre par campagne pour le dashboard termes de recherche */}
          {console.log('🎯 [CAMPAIGN FILTER RENDER] Conditions:', {
            loadingCharts,
            loadingPerformanceMax,
            isLoading: loadingCharts || loadingPerformanceMax,
            selectedAccount: selectedAccount?.name,
            shouldRender: !(loadingCharts || loadingPerformanceMax) && selectedAccount,
            availableSearchTermsCampaigns: availableSearchTermsCampaigns.length,
            loadingSearchTermsCampaigns
          })}
          {!(loadingCharts || loadingPerformanceMax) && selectedAccount && (
            <div style={{
              background: 'linear-gradient(135deg, #FAF9F6 0%, #F5F4F0 100%)',
              padding: '16px 20px',
              borderRadius: '8px',
              marginTop: '-5px',
              marginBottom: '10px',
              position: 'sticky',
              top: '0',
              zIndex: '1000'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                <span style={{
                  fontWeight: '500',
                  color: '#374151',
                  fontSize: '0.95em',
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> Filtrer:
                </span>
                <div style={{ position: 'relative', display: 'inline-block' }}>
                  <input
                    type="text"
                    placeholder={availableSearchTermsCampaigns.length > 0
                      ? `Toutes les campagnes (${availableSearchTermsCampaigns.length})`
                      : 'Rechercher une campagne...'}
                    value={searchTermsCampaignFilter !== 'all' ? (availableSearchTermsCampaigns.find(c => c.id === searchTermsCampaignFilter)?.name || '') : searchTermsCampaignSearchQuery}
                    onChange={(e) => {
                      if (searchTermsCampaignFilter !== 'all') {
                        setSearchTermsCampaignFilter('all');
                      }
                      setSearchTermsCampaignSearchQuery(e.target.value);
                      setShowSearchTermsCampaignAutocomplete(true);
                    }}
                    onFocus={() => setShowSearchTermsCampaignAutocomplete(true)}
                    onBlur={() => {
                      setTimeout(() => setShowSearchTermsCampaignAutocomplete(false), 200);
                    }}
                    style={{
                      padding: '4px 12px',
                      paddingRight: (searchTermsCampaignSearchQuery || searchTermsCampaignFilter !== 'all') ? '28px' : '12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: searchTermsCampaignFilter !== 'all' ? '#dbeafe' : 'white',
                      color: '#374151',
                      fontSize: '0.9em',
                      width: '400px',
                      height: '35px',
                      boxSizing: 'border-box',
                      outline: 'none'
                    }}
                  />
                  {(searchTermsCampaignSearchQuery || searchTermsCampaignFilter !== 'all') && (
                    <button
                      onClick={() => {
                        setSearchTermsCampaignSearchQuery('');
                        setSearchTermsCampaignFilter('all');
                        setShowSearchTermsCampaignAutocomplete(false);
                      }}
                      style={{
                        position: 'absolute',
                        right: '6px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        color: searchTermsCampaignFilter !== 'all' ? '#1a1a1a' : '#9ca3af',
                        padding: '2px',
                        fontSize: '14px',
                        lineHeight: 1,
                        zIndex: 1
                      }}
                      title={searchTermsCampaignFilter !== 'all' ? "Effacer le filtre campagne" : "Effacer la recherche"}
                    >
                      ✕
                    </button>
                  )}
                  {/* Autocomplete dropdown */}
                  {showSearchTermsCampaignAutocomplete && (() => {
                    const searchLower = searchTermsCampaignSearchQuery.toLowerCase().trim();
                    const filteredCampaigns = searchLower
                      ? [...availableSearchTermsCampaigns]
                          .filter(c => (c.name || '').toLowerCase().includes(searchLower))
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                      : [...availableSearchTermsCampaigns].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

                    if (filteredCampaigns.length === 0) {
                      return (
                        <div style={{
                          position: 'absolute',
                          top: '100%',
                          left: 0,
                          right: 0,
                          backgroundColor: 'white',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          marginTop: '4px',
                          boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                          zIndex: 1001,
                          padding: '12px',
                          color: '#6b7280',
                          fontSize: '0.9em'
                        }}>
                          Aucune campagne trouvée
                        </div>
                      );
                    }

                    return (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        zIndex: 1001,
                        maxHeight: '300px',
                        overflowY: 'auto'
                      }}>
                        <div style={{
                          padding: '8px 12px',
                          fontSize: '0.8em',
                          color: '#6b7280',
                          borderBottom: '1px solid #e5e7eb',
                          backgroundColor: '#f9fafb'
                        }}>
                          {filteredCampaigns.length} campagne{filteredCampaigns.length > 1 ? 's' : ''} disponible{filteredCampaigns.length > 1 ? 's' : ''}
                        </div>
                        {filteredCampaigns.map(campaign => (
                          <div
                            key={campaign.id}
                            onClick={() => {
                              setSearchTermsCampaignFilter(campaign.id);
                              setSearchTermsCampaignSearchQuery('');
                              setShowSearchTermsCampaignAutocomplete(false);
                            }}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #f3f4f6',
                              fontSize: '0.9em',
                              color: '#374151',
                              transition: 'background-color 0.15s'
                            }}
                            onMouseOver={(e) => e.currentTarget.style.backgroundColor = '#f3f4f6'}
                            onMouseOut={(e) => e.currentTarget.style.backgroundColor = 'white'}
                          >
                            {campaign.name} - {campaign.searchTermsCount} termes
                          </div>
                        ))}
                      </div>
                    );
                  })()}
                </div>
                {selectedCpcTierFilter && (
                  <div style={{
                    backgroundColor: '#e3f2fd',
                    color: '#1565c0',
                    padding: '4px 8px',
                    borderRadius: '4px',
                    fontSize: '0.8em',
                    fontWeight: '500',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    border: '1px solid #bbdefb'
                  }}>
                    <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> CPC: {formatNumber(selectedCpcTierFilter.min, 2)}€ - {formatNumber(selectedCpcTierFilter.max, 2)}€
                    <button
                      onClick={() => setSelectedCpcTierFilter(null)}
                      style={{
                        background: 'none',
                        border: 'none',
                        color: '#1565c0',
                        cursor: 'pointer',
                        padding: '0 2px',
                        fontSize: '15px',
                        fontWeight: 'bold',
                        borderRadius: '2px'
                      }}
                      title="Supprimer le filtre CPC"
                      onMouseOver={(e) => e.target.style.backgroundColor = '#bbdefb'}
                      onMouseOut={(e) => e.target.style.backgroundColor = 'transparent'}
                    >
                      ×
                    </button>
                  </div>
                )}
                {loadingSearchTermsCampaigns && (
                  <div style={{ 
                    fontSize: '0.8em',
                    color: '#9ca3af',
                    fontStyle: 'italic',
                    marginLeft: '8px'
                  }}>
                    Récupération des campagnes...
                  </div>
                )}
                {!loadingSearchTermsCampaigns && availableSearchTermsCampaigns.length === 0 && (
                  <div style={{ 
                    fontSize: '0.8em',
                    color: '#9ca3af',
                    fontStyle: 'italic',
                    marginLeft: '12px'
                  }}>
                    <div>Aucune campagne avec termes de recherche</div>
                    <div style={{ fontSize: '0.75em', marginTop: '2px' }}>
                      Vérifiez la période sélectionnée
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          
          {/* Section combinée des termes de recherche - SEULEMENT dans le dashboard search-terms */}
          {selectedDashboardModel === 'search-terms' && !(loadingCharts || loadingPerformanceMax || isLoadingSearchTermsRef.current) && (
            <>
            <PartialDataNotice failedSources={searchTermsPreloadData?.failedSources} resolveName={resolveAccountName} />
            <CombinedSearchTermsTable
              autoCharts={autoCharts}
              selectedAccount={selectedAccount}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              performanceMaxData={performanceMaxData}
              campaignFilter={searchTermsCampaignFilter}
              cpcTierFilter={selectedCpcTierFilter}
              isVisible={showCombinedSearchTerms}
              onToggleVisibility={() => setShowCombinedSearchTerms(!showCombinedSearchTerms)}
              formatNumber={formatNumber}
              openSearchTermModal={openSearchTermModal}
              userRole={userRole}
              compareEnabled={gadsCompareEnabled}
              comparisonType={gadsComparisonType}
              token={token}
            />
            </>
          )}
          
          {/* Distribution des paliers CPC des termes de recherche - SEULEMENT dans le dashboard search-terms */}
          {selectedDashboardModel === 'search-terms' && !(loadingCharts || loadingPerformanceMax || isLoadingSearchTermsRef.current) && (
            <>
            <PartialDataNotice failedSources={searchTermsPreloadData?.failedSources} resolveName={resolveAccountName} />
            <SearchTermsCpcTierBreakdown
              autoCharts={autoCharts}
              selectedAccount={selectedAccount}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              performanceMaxData={performanceMaxData}
              campaignFilter={searchTermsCampaignFilter}
              isVisible={showSearchTermsCpcTierBreakdown}
              onToggleVisibility={() => setShowSearchTermsCpcTierBreakdown(!showSearchTermsCpcTierBreakdown)}
              onTierClick={(tierMin, tierMax) => {
                console.log('🎯 [CPC-TIER-FILTER] Tranche sélectionnée depuis Search Terms:', { tierMin, tierMax });
                setSelectedCpcTierFilter({ min: tierMin, max: tierMax });
              }}
              selectedCpcTierFilter={selectedCpcTierFilter}
              formatNumber={formatNumber}
              userRole={userRole}
            />
            </>
          )}
          
          {/* Section d'analyse n-gram - SEULEMENT dans le dashboard search-terms */}
          {selectedDashboardModel === 'search-terms' && !(loadingCharts || loadingPerformanceMax || isLoadingSearchTermsRef.current) && (
            <>
            <PartialDataNotice failedSources={searchTermsPreloadData?.failedSources} resolveName={resolveAccountName} />
            <NgramAnalysisTable
              autoCharts={autoCharts}
              selectedAccount={selectedAccount}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              performanceMaxData={performanceMaxData}
              campaignFilter={searchTermsCampaignFilter}
              cpcTierFilter={selectedCpcTierFilter}
              isVisible={showNgramAnalysis}
              onToggleVisibility={() => setShowNgramAnalysis(!showNgramAnalysis)}
              formatNumber={formatNumber}
              openNgramModal={openNgramModal}
              userRole={userRole}
              compareEnabled={gadsCompareEnabled}
              comparisonType={gadsComparisonType}
              token={token}
            />
            </>
          )}
          </>
          )}
        </div>
      )}

      {/*
        L'analyse en panneau latéral, à côté du dashboard et non par-dessus.
        `sansOverlay` laisse le tableau lisible et manipulable pendant la lecture ; la barre
        latérale se replie à l'ouverture pour rendre la largeur que le panneau prend.
      */}
      <ChatPanel
        isOpen={panneauAnalyseOuvert}
        onClose={() => setPanneauAnalyseOuvert(false)}
        token={token}
        accountId={selectedAccount?.sourceId || selectedAccount?.id || ''}
        accountName={selectedAccount?.name || ''}
        analysisResult={null}
        /*
         * La **fonction**, pas son résultat.
         *
         * La charge était figée à l'ouverture du panneau. Or les composants de l'écran arrivent
         * en deux vagues : campagnes, groupes d'annonces, géographie et produits d'abord ;
         * appareils, jours, réseaux, âges, genre, audiences et termes de recherche ensuite, en
         * arrière-plan et sans être attendus. Un clic rapide sur « Analyse IA » capturait donc
         * un écran à moitié chargé, et l'analyse répondait — à juste titre — que le bloc
         * Appareils n'était pas disponible.
         *
         * Depuis que le panneau attend une question, l'instant qui compte est l'envoi et non
         * l'ouverture : le temps de formuler sa question, la seconde vague est arrivée.
         */
        dashboardStream={construireChargeAnalyse}
        sansOverlay
      />

      {/* Modal pour le brief utilisateur */}
      {showAiModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target.className === 'modal-overlay') {
            setShowAiModal(false);
            setUserBrief('');
          }
        }}>
          <div className="modal-content ai-brief-modal">
            <h3>Analyse IA personnalisée</h3>
            <div className="modal-body">
              <p className="modal-description">
                Apportez des précisions pour obtenir une analyse plus pertinente de vos campagnes Google Ads.
              </p>
              
              <div className="brief-form">
                <label htmlFor="user-brief">Votre contexte et objectifs :</label>
                <textarea
                id="user-brief"
                value={userBrief}
                onChange={(e) => setUserBrief(e.target.value)}
                placeholder="Exemples :
• Mon objectif est d'atteindre 500 conversions/mois avec un CPA < 50€
• Je cherche à améliorer ma rentabilité (COS < 30%)
• Je veux développer ma présence sur mobile
• Mon budget maximum est de 5000€/mois
• Je cible principalement les 25-45 ans en France
• Mes produits phares sont [listez vos produits]
• Ma marge moyenne est de 40%
• Je lance une nouvelle gamme le mois prochain
• Mes concurrents principaux sont..."
                rows={10}
                className="form-textarea"
              />
              <p className="help-text">
                💡 Plus vous donnez de contexte, plus l'analyse sera précise et les recommandations pertinentes.
              </p>
            </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={() => {
                  setShowAiModal(false);
                  setUserBrief('');
                }}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button 
                onClick={submitAIAnalysis}
                className="btn btn-primary"
                disabled={loadingInsights}
              >
                {loadingInsights ? '🧠 Analyse en cours...' : '🚀 Lancer l\'analyse'}
              </button>
            </div>
            
            <div className="modal-info">
              <small>
                💰 Coût estimé : ~0.04€ | 🔢 Tokens : ~300-500 | ⏱️ Durée : ~15-25s
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Écran de chargement pour l'analyse IA */}
      {loadingInsights && (
        <div className="modal-overlay ai-loading-overlay">
          <div className="ai-loading-content">
            <div className="ai-loading-animation">
              <div className="ai-brain-icon">🧠</div>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <h3>Analyse en cours...</h3>
            <p>Kokpit AI analyse vos données Google Ads</p>
            <div className="loading-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
              <div className="loading-details">
                <small>Traitement de {Object.keys(autoCharts?.rawData || {}).length} comptes</small>
                <small>• {autoCharts?.charts?.length || 0} graphiques</small>
                <small>• Estimation : 15-25 secondes</small>
              </div>
            </div>
            <div className="loading-tips">
              <p><Sparkles size={14} />&nbsp;Le saviez-vous ?</p>
              <p className="tip">Kokpit AI analyse vos tendances de performance, identifie les opportunités d'optimisation et fournit des recommandations personnalisées basées sur vos objectifs.</p>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard Pages d'atterrissage */}
      {selectedDashboardModel === 'landing-pages' && selectedAccount && (
        <div className="landing-pages-dashboard" style={{ marginTop: '10px' }}>

          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingLandingPagesCampaigns && accountHasNoData && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {!accountHasNoData && (
            <>
          {loadingLandingPagesCampaigns ? (
            <DashboardLoadingScreen
              platformName="Google Ads"
              features={[
                { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
                { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
                { icon: <FileText size={28} />, label: "Pages d'atterrissage" }
              ]}
            />
          ) : (
            <>
              <div style={{
                background: 'linear-gradient(135deg, #FAF9F6 0%, #F5F4F0 100%)',
                padding: '16px 20px',
                borderRadius: '8px',
                marginTop: '-5px',
                marginBottom: '10px',
                border: '1px solid #E8E6E1',
                position: 'sticky',
                top: '0',
                zIndex: '1000',
                boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                  <span style={{
                    fontWeight: '500',
                    color: '#374151',
                    fontSize: '0.95em',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> Filtrer:
                  </span>
                  <div style={{ position: 'relative', width: '400px' }}>
                    <input
                      type="text"
                      value={landingPagesCampaignSearchQuery}
                      onChange={(e) => {
                        setLandingPagesCampaignSearchQuery(e.target.value);
                        setShowLandingPagesCampaignAutocomplete(true);
                      }}
                      onFocus={() => setShowLandingPagesCampaignAutocomplete(true)}
                      placeholder={landingPagesCampaignFilter === 'all'
                        ? `Toutes les campagnes (${availableLandingPagesCampaigns.length})`
                        : availableLandingPagesCampaigns.find(c => c.id === landingPagesCampaignFilter)?.name || 'Rechercher une campagne...'}
                      disabled={loadingLandingPagesCampaigns}
                      style={{
                        padding: '4px 36px 4px 12px',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        backgroundColor: loadingLandingPagesCampaigns ? '#f3f4f6' : 'white',
                        fontSize: '0.9em',
                        color: '#374151',
                        cursor: loadingLandingPagesCampaigns ? 'not-allowed' : 'text',
                        width: '100%',
                        height: '35px',
                        boxSizing: 'border-box',
                        opacity: loadingLandingPagesCampaigns ? 0.6 : 1
                      }}
                    />
                    {(landingPagesCampaignSearchQuery || landingPagesCampaignFilter !== 'all') && (
                      <button
                        onClick={() => {
                          setLandingPagesCampaignSearchQuery('');
                          setLandingPagesCampaignFilter('all');
                          setShowLandingPagesCampaignAutocomplete(false);
                        }}
                        style={{
                          position: 'absolute',
                          right: '8px',
                          top: '50%',
                          transform: 'translateY(-50%)',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          color: '#9ca3af',
                          fontSize: '1em',
                          padding: '4px'
                        }}
                      >
                        ✕
                      </button>
                    )}
                    {showLandingPagesCampaignAutocomplete && (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        maxHeight: '300px',
                        overflowY: 'auto',
                        zIndex: 1001
                      }}>
                        <div
                          onClick={() => {
                            setLandingPagesCampaignFilter('all');
                            setLandingPagesCampaignSearchQuery('');
                            setShowLandingPagesCampaignAutocomplete(false);
                          }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            backgroundColor: landingPagesCampaignFilter === 'all' ? '#FAF9F5' : 'transparent',
                            borderBottom: '1px solid #e5e7eb'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = landingPagesCampaignFilter === 'all' ? '#FAF9F5' : 'transparent'}
                        >
                          <strong>Toutes les campagnes ({availableLandingPagesCampaigns.length})</strong>
                        </div>
                        {[...availableLandingPagesCampaigns]
                          .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                          .filter(campaign =>
                            !landingPagesCampaignSearchQuery ||
                            (campaign.name || '').toLowerCase().includes(landingPagesCampaignSearchQuery.toLowerCase())
                          )
                          .map(campaign => (
                            <div
                              key={campaign.id}
                              onClick={() => {
                                setLandingPagesCampaignFilter(campaign.id);
                                setLandingPagesCampaignSearchQuery('');
                                setShowLandingPagesCampaignAutocomplete(false);
                              }}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                backgroundColor: landingPagesCampaignFilter === campaign.id ? '#FAF9F5' : 'transparent',
                                borderBottom: '1px solid #f3f4f6'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = landingPagesCampaignFilter === campaign.id ? '#FAF9F5' : 'transparent'}
                            >
                              <div style={{ fontWeight: '500' }}>{campaign.name}</div>
                              <div style={{ fontSize: '0.85em', color: '#6b7280' }}>
                                {campaign.landingPagesCount || 0} pages d'atterrissage
                              </div>
                            </div>
                          ))
                        }
                      </div>
                    )}
                  </div>
                </div>
              </div>
              
              <div className="dashboard-section">
                <LandingPagesTableModular
                  selectedAccount={selectedAccount}
                  selectedPeriod={selectedPeriod}
                  customStartDate={customStartDate}
                  customEndDate={customEndDate}
                  formatNumber={formatNumber}
                  token={token}
                  setError={setError}
                  selectedCampaignFilter={
                    landingPagesCampaignFilter === 'all'
                      ? null
                      : availableLandingPagesCampaigns.find(campaign => campaign.id === landingPagesCampaignFilter)
                  }
                  onCampaignsLoaded={(campaigns) => {
                    console.log('📊 [App] Campagnes reçues du composant LandingPagesTableModular:', { campaigns: campaigns.length });
                    setAvailableLandingPagesCampaigns(campaigns);
                    setLoadingLandingPagesCampaigns(false);
                  }}
                  userRole={userRole}
                  compareEnabled={gadsCompareEnabled}
                  comparisonType={gadsComparisonType}
                />
              </div>
            </>
          )}
          </>
          )}
        </div>
      )}

      {/* Dashboard Performance Max */}
      {selectedDashboardModel === 'performance-max' && selectedAccount && (
        <div className="performance-max-dashboard">

          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingPerformanceMax && accountHasNoData && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {!accountHasNoData && (
            <>
          {console.log('📊 Rendu du dashboard Performance Max - loadingPerformanceMax:', loadingPerformanceMax, 'performanceMaxData:', !!performanceMaxData)}
          {loadingPerformanceMax ? (
            <DashboardLoadingScreen
              platformName="Google Ads"
              features={[
                { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
                { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
                { icon: <img src={googleAdsLogo} alt="Google Ads" style={{width: '28px', height: '28px'}} />, label: 'Analyse des campagnes' }
              ]}
            />
          ) : (() => {
            // Vérifier s'il y a des données à afficher
            const hasPerformanceMaxData = performanceMaxData && Object.keys(performanceMaxData).length > 0;
            
            if (!hasPerformanceMaxData) return false;
            
            // Vérifier s'il y a des impressions ou des campagnes actives
            const hasDataToShow = Object.values(performanceMaxData).some(accountData => {
              // Vérifier s'il y a des impressions totales (même de campagnes en pause)
              if ((accountData.totals?.impressions || 0) > 0) return true;
              
              // Vérifier s'il y a des campagnes actives
              if (accountData.campaigns) {
                const hasActiveCampaigns = accountData.campaigns.some(campaign => {
                  const status = String(campaign.status);
                  const isActive = !(status === '3' || status === '4' || status === 'PAUSED' || status === 'REMOVED');
                  return isActive;
                });
                if (hasActiveCampaigns) return true;
              }
              
              return false;
            });
            
            console.log('🔍 Condition d\'affichage Performance Max:', {
              hasPerformanceMaxData,
              hasDataToShow,
              accounts: Object.keys(performanceMaxData || {}),
              totalImpressions: Object.values(performanceMaxData || {}).map(acc => acc.totals?.impressions || 0)
            });
            
            return hasDataToShow;
          })() ? (
            <div>
              {/* Conteneur de filtrage pour Performance Max */}
              {(() => {
                // Récupérer toutes les campagnes PMAX pour le filtrage
                const allPmaxCampaigns = Object.values(performanceMaxData || {}).flatMap(accountData => accountData.campaigns || []);

                // Récupérer les labels disponibles pour les campagnes PMAX
                const pmaxLabelsSet = new Set();
                allPmaxCampaigns.forEach(campaign => {
                  const campaignId = (campaign.id || campaign.campaignId || '').toString();
                  const labels = campaignLabelsMap[campaignId] || [];
                  labels.forEach(label => pmaxLabelsSet.add(label));
                });
                const pmaxAvailableLabels = Array.from(pmaxLabelsSet).sort();

                if (allPmaxCampaigns.length === 0) return null;

                return (
                  <div style={{
                    background: 'linear-gradient(135deg, #FAF9F6 0%, #F5F4F0 100%)',
                    padding: '16px 20px',
                    borderRadius: '8px',
                    marginTop: '-5px',
                    marginBottom: '10px',
                    border: '1px solid #E8E6E1',
                    position: 'sticky',
                    top: '0',
                    zIndex: '1000',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                      <span style={{
                        fontWeight: '500',
                        color: '#374151',
                        fontSize: '0.95em',
                        display: 'flex',
                        alignItems: 'center'
                      }}>
                        <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> Filtrer:
                      </span>

                      {/* Filtre par campagne */}
                      <div style={{ position: 'relative', display: 'inline-block' }}>
                        <input
                          type="text"
                          placeholder="Rechercher une campagne PMAX..."
                          value={pmaxCampaignFilter ? (pmaxCampaignFilter.name || pmaxCampaignFilter.campaignName) : pmaxCampaignSearchQuery}
                          onChange={(e) => {
                            if (pmaxCampaignFilter) {
                              setPmaxCampaignFilter(null);
                            }
                            setPmaxCampaignSearchQuery(e.target.value);
                            setShowPmaxCampaignAutocomplete(true);
                          }}
                          onFocus={() => setShowPmaxCampaignAutocomplete(true)}
                          onBlur={() => {
                            setTimeout(() => setShowPmaxCampaignAutocomplete(false), 200);
                          }}
                          style={{
                            padding: '4px 12px',
                            paddingRight: (pmaxCampaignSearchQuery || pmaxCampaignFilter) ? '28px' : '12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: pmaxCampaignFilter ? '#dbeafe' : 'white',
                            color: '#374151',
                            fontSize: '0.9em',
                            width: '400px',
                            height: '35px',
                            boxSizing: 'border-box',
                            outline: 'none'
                          }}
                        />
                        {(pmaxCampaignSearchQuery || pmaxCampaignFilter) && (
                          <button
                            onClick={() => {
                              setPmaxCampaignSearchQuery('');
                              setPmaxCampaignFilter(null);
                              setShowPmaxCampaignAutocomplete(false);
                            }}
                            style={{
                              position: 'absolute',
                              right: '6px',
                              top: '50%',
                              transform: 'translateY(-50%)',
                              background: 'none',
                              border: 'none',
                              cursor: 'pointer',
                              color: pmaxCampaignFilter ? '#1a1a1a' : '#9ca3af',
                              padding: '2px',
                              fontSize: '14px',
                              lineHeight: 1,
                              zIndex: 1
                            }}
                            title={pmaxCampaignFilter ? "Effacer le filtre campagne" : "Effacer la recherche"}
                          >
                            ✕
                          </button>
                        )}
                        {/* Autocomplete dropdown */}
                        {showPmaxCampaignAutocomplete && (() => {
                          const searchLower = pmaxCampaignSearchQuery.toLowerCase().trim();
                          let filteredCampaigns = allPmaxCampaigns;

                          // Filtrer par labels si des labels sont sélectionnés
                          if (pmaxLabelsFilter.length > 0) {
                            filteredCampaigns = filteredCampaigns.filter(c => {
                              const campaignId = (c.id || c.campaignId || '').toString();
                              const campaignLabels = campaignLabelsMap[campaignId] || [];
                              return pmaxLabelsFilter.some(label => campaignLabels.includes(label));
                            });
                          }

                          // Filtrer par recherche textuelle
                          if (searchLower) {
                            filteredCampaigns = filteredCampaigns.filter(c =>
                              (c.name || c.campaignName || '').toLowerCase().includes(searchLower)
                            );
                          }

                          filteredCampaigns = filteredCampaigns.sort((a, b) =>
                            (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || '')
                          );

                          if (filteredCampaigns.length === 0) {
                            return (
                              <div style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                right: 0,
                                backgroundColor: 'white',
                                border: '1px solid #d1d5db',
                                borderRadius: '6px',
                                marginTop: '4px',
                                boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                zIndex: 1001,
                                padding: '12px',
                                color: '#6b7280',
                                fontSize: '0.9em'
                              }}>
                                Aucune campagne trouvée
                              </div>
                            );
                          }

                          return (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              marginTop: '4px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                              zIndex: 1001,
                              maxHeight: '300px',
                              overflowY: 'auto'
                            }}>
                              <div style={{
                                padding: '8px 12px',
                                fontSize: '0.8em',
                                color: '#6b7280',
                                borderBottom: '1px solid #e5e7eb',
                                backgroundColor: '#f9fafb'
                              }}>
                                {searchLower
                                  ? `${filteredCampaigns.length} campagne(s) trouvée(s)`
                                  : `Toutes les campagnes PMAX (${filteredCampaigns.length})`}
                              </div>
                              {filteredCampaigns.map((campaign, index) => (
                                <div
                                  key={campaign.id || campaign.campaignId || index}
                                  onClick={() => {
                                    setPmaxCampaignFilter(campaign);
                                    setPmaxCampaignSearchQuery('');
                                    setShowPmaxCampaignAutocomplete(false);
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.9em',
                                    color: '#374151',
                                    borderBottom: index < filteredCampaigns.length - 1 ? '1px solid #f3f4f6' : 'none',
                                    transition: 'background-color 0.15s'
                                  }}
                                  onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                  onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                >
                                  {campaign.name || campaign.campaignName}
                                </div>
                              ))}
                            </div>
                          );
                        })()}
                      </div>

                      {/* Filtre par labels */}
                      {pmaxAvailableLabels.length > 0 && (
                        <div style={{ position: 'relative', marginLeft: '16px' }}>
                          <button
                            onClick={() => setShowPmaxLabelsDropdown(!showPmaxLabelsDropdown)}
                            style={{
                              padding: '4px 12px',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              backgroundColor: pmaxLabelsFilter.length > 0 ? '#dbeafe' : 'white',
                              color: '#374151',
                              fontSize: '0.9em',
                              cursor: 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px',
                              minWidth: '140px',
                              height: '35px',
                              boxSizing: 'border-box'
                            }}
                          >
                            <span><Tag size={14} />&nbsp;Labels</span>
                            {pmaxLabelsFilter.length > 0 && (
                              <span style={{
                                backgroundColor: '#1a1a1a',
                                color: 'white',
                                borderRadius: '10px',
                                padding: '2px 8px',
                                fontSize: '0.8em',
                                fontWeight: '600'
                              }}>
                                {pmaxLabelsFilter.length}
                              </span>
                            )}
                            <span style={{ marginLeft: 'auto' }}>{showPmaxLabelsDropdown ? '▲' : '▼'}</span>
                          </button>

                          {showPmaxLabelsDropdown && (
                            <div
                              style={{
                                position: 'absolute',
                                top: '100%',
                                left: 0,
                                marginTop: '4px',
                                backgroundColor: 'white',
                                border: '1px solid #e5e7eb',
                                borderRadius: '8px',
                                boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                                zIndex: 9999,
                                minWidth: '200px',
                                maxHeight: '300px',
                                overflowY: 'auto'
                              }}
                            >
                              {pmaxLabelsFilter.length > 0 && (
                                <div
                                  onClick={() => setPmaxLabelsFilter([])}
                                  style={{
                                    padding: '8px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.85em',
                                    color: '#ef4444',
                                    borderBottom: '1px solid #e5e7eb',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                  }}
                                >
                                  ✕ Tout effacer
                                </div>
                              )}
                              {pmaxAvailableLabels.map((label) => (
                                <div
                                  key={label}
                                  onClick={() => {
                                    if (pmaxLabelsFilter.includes(label)) {
                                      setPmaxLabelsFilter(pmaxLabelsFilter.filter(l => l !== label));
                                    } else {
                                      setPmaxLabelsFilter([...pmaxLabelsFilter, label]);
                                    }
                                  }}
                                  style={{
                                    padding: '10px 12px',
                                    cursor: 'pointer',
                                    fontSize: '0.9em',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '8px',
                                    backgroundColor: pmaxLabelsFilter.includes(label) ? '#FAF9F5' : 'transparent',
                                    borderBottom: '1px solid #f3f4f6'
                                  }}
                                  onMouseEnter={(e) => {
                                    if (!pmaxLabelsFilter.includes(label)) {
                                      e.currentTarget.style.backgroundColor = '#f9fafb';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor = pmaxLabelsFilter.includes(label) ? '#FAF9F5' : 'transparent';
                                  }}
                                >
                                  <input
                                    type="checkbox"
                                    checked={pmaxLabelsFilter.includes(label)}
                                    onChange={() => {}}
                                    style={{
                                      width: '16px',
                                      height: '16px',
                                      cursor: 'pointer',
                                      accentColor: '#1a1a1a'
                                    }}
                                  />
                                  <span style={{ fontSize: '0.9em' }}>{label}</span>
                                </div>
                              ))}
                              <div
                                onClick={() => setShowPmaxLabelsDropdown(false)}
                                style={{
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  fontSize: '0.9em',
                                  color: 'white',
                                  backgroundColor: '#1a1a1a',
                                  borderTop: '1px solid #e5e7eb',
                                  textAlign: 'center',
                                  fontWeight: '500',
                                  marginTop: '4px'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#333333'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = '#1a1a1a'}
                              >
                                ✓ Valider
                              </div>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Bouton de configuration des métriques */}
                      <button
                        onClick={() => setShowPmaxMetricsConfig(true)}
                        style={{
                          padding: '6px 10px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          display: 'flex',
                          alignItems: 'center',
                          height: '35px',
                          marginLeft: 'auto'
                        }}
                        title="Configurer les métriques affichées"
                      >
                        <Settings size={16} color="#6b7280" />
                      </button>
                    </div>
                  </div>
                );
              })()}

              {console.log('🔍 Rendu des comptes Performance Max:', Object.entries(performanceMaxData))}
              {(() => {
                // Fonction de filtrage des campagnes PMAX
                const filterPmaxCampaign = (campaign) => {
                  const campaignId = (campaign.id || campaign.campaignId || '').toString();
                  const campaignName = campaign.name || campaign.campaignName || '';

                  // Filtre par campagne spécifique
                  if (pmaxCampaignFilter) {
                    const filterId = (pmaxCampaignFilter.id || pmaxCampaignFilter.campaignId || '').toString();
                    const filterName = pmaxCampaignFilter.name || pmaxCampaignFilter.campaignName || '';
                    if (campaignId !== filterId && campaignName !== filterName) {
                      return false;
                    }
                  }

                  // Filtre par labels
                  if (pmaxLabelsFilter.length > 0) {
                    const campaignLabels = campaignLabelsMap[campaignId] || [];
                    if (!pmaxLabelsFilter.some(label => campaignLabels.includes(label))) {
                      return false;
                    }
                  }

                  return true;
                };

                // Filtrer performanceMaxChannelsData pour les graphiques
                const filteredChannelsData = (performanceMaxChannelsData || []).filter(campaign => filterPmaxCampaign(campaign));

                // Note: channelsEvolutionData contient des données agrégées par jour, pas par campagne
                // Le filtrage par campagne ne s'applique pas directement à ces données d'évolution

                // Create render function with access to state
                const renderPerformanceMaxAccount = (accountId, accountData) => {
                  // Filtrer les campagnes de ce compte
                  const filteredCampaigns = (accountData.campaigns || []).filter(filterPmaxCampaign);

                  // Fonction de filtrage pour les searchTerms (filtrer par campaignId)
                  const filterSearchTerm = (term) => {
                    if (!pmaxCampaignFilter && pmaxLabelsFilter.length === 0) return true;

                    const termCampaignId = term.campaignId?.toString() || '';

                    // Filtrer par campagne spécifique
                    if (pmaxCampaignFilter) {
                      const filterId = (pmaxCampaignFilter.id || pmaxCampaignFilter.campaignId || '').toString();
                      if (termCampaignId && termCampaignId !== filterId) return false;
                    }

                    // Filtrer par labels (si le terme a un campaignId, vérifier ses labels)
                    if (pmaxLabelsFilter.length > 0 && termCampaignId) {
                      const campaignLabels = campaignLabelsMap[termCampaignId] || [];
                      if (!pmaxLabelsFilter.some(label => campaignLabels.includes(label))) return false;
                    }

                    return true;
                  };

                  // Fonction de filtrage pour les placements (filtrer par campaigns array)
                  const filterPlacement = (placement) => {
                    if (!pmaxCampaignFilter && pmaxLabelsFilter.length === 0) return true;

                    const placementCampaigns = placement.campaigns || [];

                    // Filtrer par campagne spécifique
                    if (pmaxCampaignFilter) {
                      const filterId = (pmaxCampaignFilter.id || pmaxCampaignFilter.campaignId || '').toString();
                      if (!placementCampaigns.includes(filterId) && !placementCampaigns.map(c => c.toString()).includes(filterId)) {
                        return false;
                      }
                    }

                    // Filtrer par labels
                    if (pmaxLabelsFilter.length > 0) {
                      // Vérifier si au moins une campagne du placement a un des labels sélectionnés
                      const hasMatchingLabel = placementCampaigns.some(campId => {
                        const campaignLabels = campaignLabelsMap[campId.toString()] || [];
                        return pmaxLabelsFilter.some(label => campaignLabels.includes(label));
                      });
                      if (!hasMatchingLabel) return false;
                    }

                    return true;
                  };

                  // Filtrer les données
                  const filteredSearchTerms = (accountData.searchTerms || []).filter(filterSearchTerm);
                  const filteredPlacements = (accountData.placements || []).filter(filterPlacement);

                  const filteredAccountData = {
                    ...accountData,
                    campaigns: filteredCampaigns,
                    searchTerms: filteredSearchTerms,
                    placements: filteredPlacements
                  };

                  return (
                <div key={accountId}>
                  {console.log('🔍 Données du compte:', accountId, filteredAccountData)}

                  {/* Répartition et Évolution des coûts */}
                  {filteredAccountData.channelMetrics && Object.keys(filteredAccountData.channelMetrics).length > 0 && filteredChannelsData.length > 0 && (
                    <div className="campaigns-table-section">
                      <div className="section-header">
                        <h4><Wallet size={16} />&nbsp;Coûts par canal</h4>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '2fr 3fr', gap: '20px' }}>
                        {/* Graphique camembert - Répartition des coûts */}
                        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '10px 20px 15px 20px', height: '380px' }}>
                          <h5 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151', display: 'flex', alignItems: 'center', gap: '6px' }}>
                            <Split size={14} /> Répartition des coûts
                          </h5>
                          <ResponsiveContainer width="100%" height={300}>
                            <PieChart>
                              <Pie
                                data={(() => {
                                  // Utiliser les données de la nouvelle segmentation API
                                  const channelConfig = {
                                    google_search: { name: 'Recherche Google', color: '#4285f4' },
                                    search_partners: { name: 'Partenaires recherche', color: '#5f6368' },
                                    shopping: { name: 'Shopping', color: '#34a853' },
                                    youtube: { name: 'YouTube', color: '#ff0000' },
                                    display: { name: 'Display', color: '#fbbc04' },
                                    gmail: { name: 'Gmail', color: '#ea4335' },
                                    discover: { name: 'Discover', color: '#ff6f00' },
                                    maps: { name: 'Maps', color: '#34a853' },
                                    google_tv: { name: 'Google TV', color: '#9334e6' },
                                    cross_network: { name: 'Cross-network', color: '#9334e6' },
                                    mixed: { name: 'Mixte', color: '#ff6d01' },
                                    unspecified: { name: 'Non spécifié', color: '#dadce0' },
                                    other: { name: 'Autre', color: '#9ca3af' }
                                  };

                                  // Agréger les coûts par canal depuis filteredChannelsData (filtré)
                                  const channelTotals = {};
                                  if (filteredChannelsData && filteredChannelsData.length > 0) {
                                    filteredChannelsData.forEach(campaign => {
                                      if (campaign.channelsArray) {
                                        campaign.channelsArray.forEach(ch => {
                                          if (!channelTotals[ch.channel]) {
                                            channelTotals[ch.channel] = 0;
                                          }
                                          channelTotals[ch.channel] += ch.cost || 0;
                                        });
                                      }
                                    });
                                  }

                                  return Object.entries(channelTotals)
                                    .filter(([_, cost]) => cost > 0)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([channel, cost]) => ({
                                      name: channelConfig[channel]?.name || channel,
                                      value: cost,
                                      channel: channel
                                    }));
                                })()}
                                cx="50%"
                                cy="45%"
                                labelLine={false}
                                label={({ name, percent }) => percent > 0.05 ? `${name} ${(percent * 100).toFixed(1)}%` : ''}
                                outerRadius={70}
                                dataKey="value"
                              >
                                {(() => {
                                  const channelColors = {
                                    google_search: '#4285f4',
                                    search_partners: '#5f6368',
                                    shopping: '#34a853',
                                    youtube: '#ff0000',
                                    display: '#fbbc04',
                                    gmail: '#ea4335',
                                    discover: '#ff6f00',
                                    maps: '#34a853',
                                    google_tv: '#9334e6',
                                    cross_network: '#9334e6',
                                    mixed: '#ff6d01',
                                    unspecified: '#dadce0',
                                    other: '#9ca3af'
                                  };

                                  // Agréger les coûts par canal (filtré)
                                  const channelTotals = {};
                                  if (filteredChannelsData && filteredChannelsData.length > 0) {
                                    filteredChannelsData.forEach(campaign => {
                                      if (campaign.channelsArray) {
                                        campaign.channelsArray.forEach(ch => {
                                          if (!channelTotals[ch.channel]) {
                                            channelTotals[ch.channel] = 0;
                                          }
                                          channelTotals[ch.channel] += ch.cost || 0;
                                        });
                                      }
                                    });
                                  }

                                  return Object.entries(channelTotals)
                                    .filter(([_, cost]) => cost > 0)
                                    .sort((a, b) => b[1] - a[1])
                                    .map(([channel], index) => (
                                      <Cell key={`cell-${index}`} fill={channelColors[channel] || '#6B7280'} />
                                    ));
                                })()}
                              </Pie>
                              <Tooltip formatter={(value) => `${formatNumber(value)} €`} />
                              <Legend
                                layout="horizontal"
                                verticalAlign="bottom"
                                align="center"
                                wrapperStyle={{ fontSize: '11px', paddingTop: '10px' }}
                              />
                            </PieChart>
                          </ResponsiveContainer>
                        </div>

                        {/* Graphique d'évolution des coûts par canal */}
                        <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '10px 20px 15px 20px', height: '380px', display: 'flex', flexDirection: 'column' }}>
                          <h5 style={{ fontSize: '14px', fontWeight: '600', marginBottom: '12px', color: '#374151' }}>
                            📈 Évolution des coûts
                          </h5>
                          {loadingChannelsEvolution ? (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                              Chargement...
                            </div>
                          ) : channelsEvolutionData && channelsEvolutionData.evolution && channelsEvolutionData.evolution.length > 0 ? (
                            <ResponsiveContainer width="100%" height={280}>
                              <AreaChart data={channelsEvolutionData.evolution} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                                <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                                <XAxis
                                  dataKey="date"
                                  tick={{ fontSize: 11 }}
                                  tickFormatter={(date) => {
                                    const d = new Date(date);
                                    return `${d.getDate()}/${d.getMonth() + 1}`;
                                  }}
                                />
                                <YAxis
                                  tick={{ fontSize: 11 }}
                                  tickFormatter={(value) => `${formatNumber(value)}€`}
                                />
                                <Tooltip
                                  formatter={(value, name) => [`${formatNumber(value, 2)}€`, name]}
                                  labelFormatter={(date) => {
                                    const d = new Date(date);
                                    return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
                                  }}
                                />
                                <Legend wrapperStyle={{ fontSize: '12px' }} />
                                {channelsEvolutionData.channels && channelsEvolutionData.channels.map(ch => (
                                  <Area
                                    key={ch.channel}
                                    type="monotone"
                                    dataKey={ch.channel}
                                    name={ch.channelName}
                                    stroke={ch.color}
                                    fill={ch.color}
                                    fillOpacity={0.3}
                                    stackId="1"
                                  />
                                ))}
                              </AreaChart>
                            </ResponsiveContainer>
                          ) : (
                            <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#6b7280' }}>
                              Aucune donnée disponible
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Budget par campagne */}
                  {filteredChannelsData && filteredChannelsData.length > 0 && (
                    <div className="campaigns-table-section">
                      <div className="section-header">
                        <h4><BarChart2 size={16} />&nbsp;Budget par campagne</h4>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                          <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer', fontSize: '14px', color: '#6b7280' }}>
                            <input
                              type="checkbox"
                              checked={showCampaignBreakdownAsPercentage}
                              onChange={(e) => setShowCampaignBreakdownAsPercentage(e.target.checked)}
                              style={{ marginRight: '6px' }}
                            />
                            Vue en %
                          </label>
                        </div>
                      </div>
                      <div className="chart-container" style={{ padding: '20px' }}>
                        {(() => {
                          // Configuration des canaux avec couleurs
                          const channelConfig = {
                            google_search: { name: 'Recherche Google', color: '#4285f4' },
                            search_partners: { name: 'Partenaires recherche', color: '#5f6368' },
                            shopping: { name: 'Shopping', color: '#34a853' },
                            youtube: { name: 'YouTube', color: '#ff0000' },
                            display: { name: 'Display', color: '#fbbc04' },
                            gmail: { name: 'Gmail', color: '#ea4335' },
                            discover: { name: 'Discover', color: '#ff6f00' },
                            maps: { name: 'Maps', color: '#34a853' },
                            google_tv: { name: 'Google TV', color: '#9334e6' },
                            cross_network: { name: 'Cross-network', color: '#9334e6' },
                            mixed: { name: 'Mixte', color: '#ff6d01' },
                            unspecified: { name: 'Non spécifié', color: '#dadce0' },
                            other: { name: 'Autre', color: '#9ca3af' }
                          };

                          // Collecter tous les canaux uniques présents (données filtrées)
                          const allChannelsSet = new Set();
                          filteredChannelsData.forEach(campaign => {
                            if (campaign.channelsArray) {
                              campaign.channelsArray.forEach(ch => {
                                if (ch.cost > 0) allChannelsSet.add(ch.channel);
                              });
                            }
                          });

                          // Trier les canaux par ordre de priorité
                          const priorityOrder = ['google_search', 'search_partners', 'shopping', 'youtube', 'display', 'gmail', 'discover', 'maps', 'google_tv', 'cross_network', 'mixed', 'unspecified', 'other'];
                          const activeChannels = Array.from(allChannelsSet).sort((a, b) => {
                            const indexA = priorityOrder.indexOf(a);
                            const indexB = priorityOrder.indexOf(b);
                            if (indexA === -1) return 1;
                            if (indexB === -1) return -1;
                            return indexA - indexB;
                          });

                          console.log('📊 [CAMPAIGN DISTRIBUTION] Canaux actifs:', activeChannels);

                          // Préparer les données pour le graphique (données filtrées)
                          const campaignData = filteredChannelsData
                            .sort((a, b) => (b.totalCost || 0) - (a.totalCost || 0))
                            .map(campaign => {
                              const totalCost = campaign.totalCost || 0;
                              const result = {
                                name: campaign.campaignName.length > 30 ? campaign.campaignName.substring(0, 30) + '...' : campaign.campaignName,
                                fullName: campaign.campaignName,
                                totalCost: totalCost
                              };

                              // Ajouter les valeurs pour chaque canal
                              activeChannels.forEach(channelKey => {
                                const channelData = campaign.channelsArray?.find(ch => ch.channel === channelKey);
                                const cost = channelData?.cost || 0;

                                if (showCampaignBreakdownAsPercentage && totalCost > 0) {
                                  result[channelKey] = (cost / totalCost) * 100;
                                } else {
                                  result[channelKey] = cost;
                                }
                                // Garder la valeur originale pour le tooltip
                                result[`original_${channelKey}`] = cost;
                              });

                              return result;
                            });

                          return (
                            <ResponsiveContainer width="100%" height={400}>
                              <BarChart
                                data={campaignData}
                                margin={{ top: 20, right: 30, left: 20, bottom: 80 }}
                              >
                                <CartesianGrid strokeDasharray="3 3" />
                                <XAxis
                                  dataKey="name"
                                  angle={-45}
                                  textAnchor="end"
                                  height={100}
                                  interval={0}
                                  fontSize={12}
                                />
                                <YAxis
                                  tickFormatter={(value) => showCampaignBreakdownAsPercentage ? `${formatNumber(value, 1)}%` : `${formatNumber(value)} €`}
                                  domain={showCampaignBreakdownAsPercentage ? [0, 100] : [0, 'dataMax']}
                                />
                                <Tooltip
                                  formatter={(value, name, props) => {
                                    // Trouver la clé du canal à partir du nom affiché
                                    const channelKey = Object.entries(channelConfig).find(([_, cfg]) => cfg.name === name)?.[0];
                                    if (showCampaignBreakdownAsPercentage && channelKey) {
                                      const originalValue = props.payload?.[`original_${channelKey}`] || 0;
                                      return [`${formatNumber(value, 1)}% (${formatNumber(originalValue)} €)`, name];
                                    } else {
                                      return [`${formatNumber(value)} €`, name];
                                    }
                                  }}
                                  labelFormatter={(label) => {
                                    const campaign = campaignData.find(c => c.name === label);
                                    return `${campaign?.fullName || label} (Total: ${formatNumber(campaign?.totalCost || 0)} €)`;
                                  }}
                                />
                                <Legend />
                                {activeChannels.map(channelKey => (
                                  <Bar
                                    key={channelKey}
                                    dataKey={channelKey}
                                    stackId="cost"
                                    fill={channelConfig[channelKey]?.color || '#9ca3af'}
                                    name={channelConfig[channelKey]?.name || channelKey}
                                  />
                                ))}
                              </BarChart>
                            </ResponsiveContainer>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Composant Performance Max - Répartition par canal */}
                  {!loadingPerformanceMax && performanceMaxData && Object.keys(performanceMaxData).length > 0 && (
                    <PerformanceMaxNetworksTable
                      selectedAccount={selectedAccount}
                      selectedPeriod={selectedPeriod}
                      customStartDate={customStartDate}
                      customEndDate={customEndDate}
                      token={token}
                      setError={setError}
                      pmaxCampaignFilter={pmaxCampaignFilter}
                      pmaxLabelsFilter={pmaxLabelsFilter}
                      campaignLabelsMap={campaignLabelsMap}
                      performanceMaxChannelsData={performanceMaxChannelsData}
                      isPmaxMetricVisible={isPmaxMetricVisible}
                    />
                  )}

                  {/* Composant Performance Max - Appareils */}
                  {!loadingPerformanceMax && performanceMaxData && Object.keys(performanceMaxData).length > 0 && (
                    <PerformanceMaxDevicesTableModular
                      selectedAccount={selectedAccount}
                      selectedPeriod={selectedPeriod}
                      customStartDate={customStartDate}
                      customEndDate={customEndDate}
                      formatNumber={formatNumber}
                      isVisible={true}
                      onToggleVisibility={() => {}}
                      selectedCampaignFilter={pmaxCampaignFilter}
                      pmaxLabelsFilter={pmaxLabelsFilter}
                      campaignLabelsMap={campaignLabelsMap}
                      performanceMaxChannelsData={performanceMaxChannelsData}
                      token={token}
                      setError={setError}
                      isPmaxMetricVisible={isPmaxMetricVisible}
                    />
                  )}

                  {/* Campagnes Performance Max */}
                  {filteredAccountData.campaigns && filteredAccountData.campaigns.length > 0 && (
                    <div className="campaigns-table-section">
                      <div className="section-header">
                        <h4>
                          🚀 Campagnes Performance Max ({(() => {
                          // Calculer le nombre de campagnes après filtrage
                          return filteredAccountData.campaigns.length;
                        })()})
                        </h4>
                      </div>
                      <div className="campaigns-table-wrapper">
                        {(() => {
                          // Utiliser les campagnes filtrées
                          const filteredCampaigns = filteredAccountData.campaigns;
                          
                          // Calculer les totaux avec toutes les métriques harmonisées
                          const campaignTotals = filteredCampaigns.reduce((acc, campaign) => ({
                            impressions: acc.impressions + (campaign.impressions || 0),
                            clicks: acc.clicks + (campaign.clicks || 0),
                            cost: acc.cost + (campaign.cost || 0),
                            conversions: acc.conversions + (campaign.conversions || 0),
                            conversionsValue: acc.conversionsValue + (campaign.conversionsValue || 0)
                          }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });
                          
                          // Calculer les métriques dérivées
                          const totalCTR = campaignTotals.impressions > 0 ? (campaignTotals.clicks / campaignTotals.impressions * 100) : 0;
                          const totalCPC = campaignTotals.clicks > 0 ? (campaignTotals.cost / campaignTotals.clicks) : 0;
                          const totalConversionRate = campaignTotals.clicks > 0 ? (campaignTotals.conversions / campaignTotals.clicks * 100) : 0;
                          const totalCPA = campaignTotals.conversions > 0 ? (campaignTotals.cost / campaignTotals.conversions) : 0;
                          const totalCOS = campaignTotals.conversionsValue > 0 ? (campaignTotals.cost / campaignTotals.conversionsValue * 100) : 0;
                          const cosClass = getCosClassName(totalCOS, selectedAccount?.id || 'global', 'campaigns');

                          // Fonction de tri
                          const sortedCampaigns = [...filteredCampaigns].sort((a, b) => {
                            let aValue, bValue;
                            
                            // Calcul spécial pour les métriques calculées
                            switch(performanceMaxCampaignsSortConfig.key) {
                              case 'ctr':
                                aValue = a.impressions > 0 ? (a.clicks / a.impressions * 100) : 0;
                                bValue = b.impressions > 0 ? (b.clicks / b.impressions * 100) : 0;
                                break;
                              case 'cpc':
                                aValue = a.clicks > 0 ? (a.cost / a.clicks) : Number.MAX_VALUE;
                                bValue = b.clicks > 0 ? (b.cost / b.clicks) : Number.MAX_VALUE;
                                break;
                              case 'conversionRate':
                                aValue = a.clicks > 0 ? (a.conversions / a.clicks * 100) : 0;
                                bValue = b.clicks > 0 ? (b.conversions / b.clicks * 100) : 0;
                                break;
                              case 'cpa':
                                aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
                                bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
                                break;
                              case 'cos':
                                aValue = a.conversionsValue > 0 ? (a.cost / a.conversionsValue * 100) : Number.MAX_VALUE;
                                bValue = b.conversionsValue > 0 ? (b.cost / b.conversionsValue * 100) : Number.MAX_VALUE;
                                break;
                              default:
                                aValue = a[performanceMaxCampaignsSortConfig.key] || 0;
                                bValue = b[performanceMaxCampaignsSortConfig.key] || 0;
                            }
                            
                            if (aValue < bValue) return performanceMaxCampaignsSortConfig.direction === 'asc' ? -1 : 1;
                            if (aValue > bValue) return performanceMaxCampaignsSortConfig.direction === 'asc' ? 1 : -1;
                            return 0;
                          });
                          
                          // Configuration de la pagination
                          const itemsPerPage = 15;
                          const totalPages = Math.ceil(filteredCampaigns.length / itemsPerPage);
                          const startIndex = (performanceMaxCampaignsPage - 1) * itemsPerPage;
                          const endIndex = Math.min(startIndex + itemsPerPage, filteredCampaigns.length);
                          const paginatedCampaigns = sortedCampaigns.slice(startIndex, endIndex);
                          
                          // Fonction de gestion du tri
                          const handleCampaignSort = (key) => {
                            let direction = 'desc';
                            if (performanceMaxCampaignsSortConfig.key === key && performanceMaxCampaignsSortConfig.direction === 'desc') {
                              direction = 'asc';
                            }
                            setPerformanceMaxCampaignsSortConfig({ key, direction });
                            setPerformanceMaxCampaignsPage(1); // Réinitialiser à la première page
                          };
                          
                          return (
                            <>
                            <table className="campaigns-table" style={{ width: '100%', fontSize: '14px' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                              <th onClick={() => handleCampaignSort('name')} className="sortable" style={{ textAlign: 'left', padding: '8px', minWidth: '200px', cursor: 'pointer' }}>
                                Campagne {performanceMaxCampaignsSortConfig.key === 'name' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th style={{ textAlign: 'left', padding: '8px' }}>Statut</th>
                              {isPmaxMetricVisible('impressions') && (
                                <th onClick={() => handleCampaignSort('impressions')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Impr. {performanceMaxCampaignsSortConfig.key === 'impressions' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('ctr') && (
                                <th onClick={() => handleCampaignSort('ctr')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  CTR {performanceMaxCampaignsSortConfig.key === 'ctr' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('clics') && (
                                <th onClick={() => handleCampaignSort('clicks')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Clics {performanceMaxCampaignsSortConfig.key === 'clicks' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('cpc') && (
                                <th onClick={() => handleCampaignSort('cpc')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  CPC {performanceMaxCampaignsSortConfig.key === 'cpc' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('cout') && (
                                <th onClick={() => handleCampaignSort('cost')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Coût {performanceMaxCampaignsSortConfig.key === 'cost' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('taux_conv') && (
                                <th onClick={() => handleCampaignSort('conversionRate')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Taux Conv. {performanceMaxCampaignsSortConfig.key === 'conversionRate' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('conv') && (
                                <th onClick={() => handleCampaignSort('conversions')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Conv. {performanceMaxCampaignsSortConfig.key === 'conversions' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('cpa') && (
                                <th onClick={() => handleCampaignSort('cpa')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  CPA {performanceMaxCampaignsSortConfig.key === 'cpa' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('valeur_conv') && (
                                <th onClick={() => handleCampaignSort('conversionsValue')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Valeur Conv. {performanceMaxCampaignsSortConfig.key === 'conversionsValue' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('cos') && (
                                <th onClick={() => handleCampaignSort('cos')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  COS {performanceMaxCampaignsSortConfig.key === 'cos' && (performanceMaxCampaignsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {paginatedCampaigns.map((campaign, index) => {
                                const campaignCos = (campaign.conversionsValue && campaign.conversionsValue > 0) ? 
                                  (campaign.cost / campaign.conversionsValue * 100) : 0;
                                
                                // Debug spécifique pour 📊 Campagnes
                                const accountId = selectedAccount?.id || 'global';
                                if (campaignCos >= 49 && campaignCos <= 50) {
                                  console.log(`🚨 [📊 CAMPAGNES-DEBUG] Campagne "${campaign.name}" COS ${campaignCos}%:`, {
                                    accountId,
                                    selectedAccount,
                                    utiliséPour: 'getCosClassName'
                                  });
                                }
                                
                                const campaignCosClass = getCosClassName(campaignCos, accountId, 'campaigns');
                                
                                return (
                                <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                  <td style={{ padding: '12px 8px', fontWeight: '500' }}>{campaign.name}</td>
                                <td style={{ padding: '12px 8px' }}>
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: (() => {
                                      const status = String(campaign.status);
                                      switch(status) {
                                        case '2':
                                        case 'ENABLED': return '#d1fae5';
                                        case '3':
                                        case 'PAUSED': return '#fef3c7';
                                        case '4':
                                        case 'REMOVED': return '#fee2e2';
                                        case 'PENDING': return '#e0e7ff';
                                        case 'ENDED': return '#f3f4f6';
                                        default: return '#f3f4f6';
                                      }
                                    })(),
                                    color: (() => {
                                      const status = String(campaign.status);
                                      switch(status) {
                                        case '2':
                                        case 'ENABLED': return '#065f46';
                                        case '3':
                                        case 'PAUSED': return '#92400e';
                                        case '4':
                                        case 'REMOVED': return '#991b1b';
                                        case 'PENDING': return '#3730a3';
                                        case 'ENDED': return '#6b7280';
                                        default: return '#6b7280';
                                      }
                                    })()
                                  }}>
                                    {(() => {
                                      // Mapping numérique et textuel des statuts
                                      const statusMap = {
                                        // Valeurs numériques selon l'ordre de l'enum Google Ads
                                        '0': 'Inconnu',
                                        '1': 'Inconnu',
                                        '2': 'Actif',
                                        '3': 'En pause',
                                        '4': 'Supprimé',
                                        // Valeurs textuelles
                                        'ENABLED': 'Actif',
                                        'PAUSED': 'En pause',
                                        'REMOVED': 'Supprimé',
                                        'PENDING': 'En attente',
                                        'ENDED': 'Terminé',
                                        'UNKNOWN': 'Inconnu'
                                      };
                                      const statusValue = String(campaign.status);
                                      console.log('🔍 Statut campagne brut:', campaign.status, 'Type:', typeof campaign.status);
                                      return statusMap[statusValue] || statusValue || 'Inconnu';
                                    })()}
                                  </span>
                                </td>
                                {isPmaxMetricVisible('impressions') && (
                                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.impressions)}</td>
                                )}
                                {isPmaxMetricVisible('ctr') && (
                                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                                    {campaign.impressions > 0 ? formatNumber((campaign.clicks / campaign.impressions) * 100, 2) + '%' : formatNumber(0, 2) + '%'}
                                  </td>
                                )}
                                {isPmaxMetricVisible('clics') && (
                                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.clicks)}</td>
                                )}
                                {isPmaxMetricVisible('cpc') && (
                                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                                    {campaign.clicks > 0 ? formatNumber(campaign.cost / campaign.clicks, 2) + '€' : formatNumber(0, 2) + '€'}
                                  </td>
                                )}
                                {isPmaxMetricVisible('cout') && (
                                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.cost, 2)}€</td>
                                )}
                                {isPmaxMetricVisible('taux_conv') && (
                                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                                    {campaign.clicks > 0 ? formatNumber((campaign.conversions / campaign.clicks) * 100, 2) + '%' : formatNumber(0, 2) + '%'}
                                  </td>
                                )}
                                {isPmaxMetricVisible('conv') && (
                                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.conversions, 1)}</td>
                                )}
                                {isPmaxMetricVisible('cpa') && (
                                  <td className={`numeric ${(() => {
                                    const cpa = campaign.conversions > 0 ? (campaign.cost / campaign.conversions) : 0;
                                    return getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns');
                                  })()}`} style={{ padding: '12px 8px', textAlign: 'right' }}>
                                    {campaign.conversions > 0 ? formatNumber(campaign.cost / campaign.conversions, 2) + '€' : formatNumber(0, 2) + '€'}
                                  </td>
                                )}
                                {isPmaxMetricVisible('valeur_conv') && (
                                  <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.conversionsValue || 0, 2)}€</td>
                                )}
                                {isPmaxMetricVisible('cos') && (
                                  <td className={`numeric ${campaignCosClass}`} style={{ padding: '12px 8px', textAlign: 'right' }}>
                                    {campaign.conversionsValue > 0 ? formatNumber(campaignCos, 1) + '%' : '∞'}
                                  </td>
                                )}
                              </tr>
                                );
                              })}
                          </tbody>
                          <tfoot>
                            <tr className="total-row" style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                              <td style={{ padding: '12px 8px', fontWeight: '600' }}>TOTAL</td>
                              <td style={{ padding: '12px 8px' }}>-</td>
                              {isPmaxMetricVisible('impressions') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.impressions)}</td>
                              )}
                              {isPmaxMetricVisible('ctr') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalCTR, 2)}%</td>
                              )}
                              {isPmaxMetricVisible('clics') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.clicks)}</td>
                              )}
                              {isPmaxMetricVisible('cpc') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalCPC, 2)}€</td>
                              )}
                              {isPmaxMetricVisible('cout') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.cost, 2)}€</td>
                              )}
                              {isPmaxMetricVisible('taux_conv') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalConversionRate, 2)}%</td>
                              )}
                              {isPmaxMetricVisible('conv') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.conversions, 1)}</td>
                              )}
                              {isPmaxMetricVisible('cpa') && (
                                <td className={`numeric ${getCpaClassName(totalCPA, selectedAccount?.id || 'global', 'campaigns')}`} style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                                  {campaignTotals.conversions >= 0 ? formatNumber(totalCPA, 2) + '€' : '-'}
                                </td>
                              )}
                              {isPmaxMetricVisible('valeur_conv') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.conversionsValue, 2)}€</td>
                              )}
                              {isPmaxMetricVisible('cos') && (
                                <td className={`numeric ${cosClass}`} style={{ padding: '12px 8px', fontWeight: '600' }}>
                                  {campaignTotals.conversionsValue > 0 ? formatNumber(totalCOS, 1) + '%' : '∞'}
                                </td>
                              )}
                            </tr>
                          </tfoot>
                        </table>
                        {totalPages > 1 && (
                          <div className="pagination-controls">
                            <div className="pagination-info">
                              <span>
                                Affichage {startIndex + 1}-{endIndex} sur {filteredCampaigns.length} campagnes
                              </span>
                            </div>
                            <div className="pagination-buttons">
                              <button
                                onClick={() => setPerformanceMaxCampaignsPage(prev => Math.max(1, prev - 1))}
                                disabled={performanceMaxCampaignsPage === 1}
                                className="pagination-btn"
                              >
                                ← Précédent
                              </button>
                              <span className="page-numbers">
                                Page {performanceMaxCampaignsPage} sur {totalPages}
                              </span>
                              <button
                                onClick={() => setPerformanceMaxCampaignsPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={performanceMaxCampaignsPage === totalPages}
                                className="pagination-btn"
                              >
                                Suivant →
                              </button>
                            </div>
                          </div>
                        )}
                            </>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Composant Performance Max - Canal Shopping */}
                  {!loadingPerformanceMax && performanceMaxData && Object.keys(performanceMaxData).length > 0 && (
                    <PerformanceMaxProductsTableModular
                      selectedAccount={selectedAccount}
                      selectedPeriod={selectedPeriod}
                      customStartDate={customStartDate}
                      customEndDate={customEndDate}
                      formatNumber={formatNumber}
                      isVisible={true}
                      onToggleVisibility={() => {}}
                      selectedCampaignFilter={pmaxCampaignFilter}
                      selectedCampaignTypeFilter={null}
                      pmaxLabelsFilter={pmaxLabelsFilter}
                      campaignLabelsMap={campaignLabelsMap}
                      performanceMaxChannelsData={performanceMaxChannelsData}
                      token={token}
                      setError={setError}
                      compareEnabled={pmaxCompareEnabled}
                      comparisonType={pmaxComparisonType}
                      isPmaxMetricVisible={isPmaxMetricVisible}
                    />
                  )}

                  {/* Composant Performance Max - Vidéos */}
                  {!loadingPerformanceMax && performanceMaxData && Object.keys(performanceMaxData).length > 0 && (
                    <PerformanceMaxVideosTable
                      selectedAccount={selectedAccount}
                      selectedPeriod={selectedPeriod}
                      customStartDate={customStartDate}
                      customEndDate={customEndDate}
                      formatNumber={formatNumber}
                      isVisible={true}
                      onToggleVisibility={() => {}}
                      selectedVideoFilter={null}
                      setSelectedVideoFilter={() => {}}
                      // Le filtre du dashboard Performance Max, pas celui du dashboard
                      // Google Ads global : ce sont deux états distincts, et le composant
                      // recevait le second, qui ne bouge jamais dans cette vue.
                      selectedCampaignFilter={pmaxCampaignFilter}
                      // Cette vue ne montre que du Performance Max. Transmettre le filtre
                      // de type global viderait le tableau dès qu'un autre type est
                      // sélectionné ailleurs.
                      selectedCampaignTypeFilter={null}
                      token={token}
                      setError={setError}
                      onDataLoaded={handlePerformanceMaxVideoData}
                    />
                  )}


                  {/* Composant Performance Max - Canaux - DÉSACTIVÉ TEMPORAIREMENT */}
                  {/* {!loadingPerformanceMax && performanceMaxData && Object.keys(performanceMaxData).length > 0 && (
                    <PerformanceMaxChannelsTableModular
                      customerId={selectedAccount?.id}
                      period={selectedPeriod}
                      customStartDate={customStartDate}
                      customEndDate={customEndDate}
                    />
                  )} */}

                  {/* TEST: Vérification shopping_performance_view */}
                  {(() => {
                    const testData = shoppingData?.[selectedAccount?.id]?.testShoppingView || shoppingData?.[selectedAccount?.name]?.testShoppingView;
                    
                    if (!testData) {
                      return null;
                    }
                    
                    return (
                      <div style={{ marginBottom: '32px' }}>
                        <h4 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1f2937' }}>
                          🛍️ Résultats Shopping
                        </h4>
                        
                        {!testData.success ? (
                          <div style={{ backgroundColor: '#fee', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                            <p style={{ color: '#dc2626', fontWeight: '600' }}>❌ Erreur: {testData.error}</p>
                            <p style={{ fontSize: '14px', color: '#666', marginTop: '10px' }}>
                              La vue shopping_performance_view n'est pas accessible pour ce compte.
                            </p>
                          </div>
                        ) : (
                          <>

                            {/* Tableau des campagnes trouvées */}
                            {testData.campaignSummary && testData.campaignSummary.length > 0 && (
                              <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                                <table style={{ width: '100%', fontSize: '14px' }}>
                                  <thead>
                                    <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                                      <th style={{ textAlign: 'left', padding: '8px' }}>Campagne</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>Impr.</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>CTR</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>Clics</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>CPC</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>Coût</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>Taux Conv.</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>Conv.</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>CPA</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>Valeur Conv.</th>
                                      <th style={{ textAlign: 'right', padding: '8px' }}>COS</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {testData.campaignSummary.slice(0, 20).map((campaign, index) => {
                                      const ctr = campaign.totalImpressions > 0 ? (campaign.totalClicks / campaign.totalImpressions * 100) : 0;
                                      const cpc = campaign.totalClicks > 0 ? (campaign.totalCost / campaign.totalClicks) : 0;
                                      const conversionRate = campaign.totalClicks > 0 ? (campaign.totalConversions / campaign.totalClicks * 100) : 0;
                                      const cpa = campaign.totalConversions > 0 ? (campaign.totalCost / campaign.totalConversions) : 0;
                                      const cos = (campaign.totalConversionsValue && campaign.totalConversionsValue > 0) ? (campaign.totalCost / campaign.totalConversionsValue * 100) : 0;
                                      
                                      return (
                                        <tr key={campaign.campaignId} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                          <td style={{ padding: '12px 8px', fontWeight: '500' }}>{campaign.campaignName}</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.totalImpressions)}</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(ctr, 2)}%</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.totalClicks)}</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>{cpc >= 0 ? formatNumber(cpc, 2) + '€' : '-'}</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaign.totalCost, 2)}€</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(conversionRate, 2)}%</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.totalConversions, 1)}</td>
                                          <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns')}`} style={{ padding: '12px 8px', textAlign: 'right' }}>{cpa >= 0 ? formatNumber(cpa, 2) + '€' : '-'}</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.totalConversionsValue || 0, 2)}€</td>
                                          <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'campaigns')}`} style={{ padding: '12px 8px', textAlign: 'right' }}>
                                            {cos >= 0 ? formatNumber(cos, 1) + '%' : '-'}
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                  <tfoot>
                                    {(() => {
                                      // Calculer les totaux harmonisés pour les campagnes affichées
                                      const displayedCampaigns = testData.campaignSummary.slice(0, 20);
                                      const campaignTotals = displayedCampaigns.reduce((acc, campaign) => ({
                                        totalImpressions: acc.totalImpressions + (campaign.totalImpressions || 0),
                                        totalClicks: acc.totalClicks + (campaign.totalClicks || 0),
                                        totalCost: acc.totalCost + (campaign.totalCost || 0),
                                        totalConversions: acc.totalConversions + (campaign.totalConversions || 0),
                                        totalConversionsValue: acc.totalConversionsValue + (campaign.totalConversionsValue || 0)
                                      }), { totalImpressions: 0, totalClicks: 0, totalCost: 0, totalConversions: 0, totalConversionsValue: 0 });
                                      
                                      const totalCTR = campaignTotals.totalImpressions > 0 ? (campaignTotals.totalClicks / campaignTotals.totalImpressions * 100) : 0;
                                      const totalCPC = campaignTotals.totalClicks > 0 ? (campaignTotals.totalCost / campaignTotals.totalClicks) : 0;
                                      const totalConversionRate = campaignTotals.totalClicks > 0 ? (campaignTotals.totalConversions / campaignTotals.totalClicks * 100) : 0;
                                      const totalCPA = campaignTotals.totalConversions > 0 ? (campaignTotals.totalCost / campaignTotals.totalConversions) : 0;
                                      const totalCOS = campaignTotals.totalConversionsValue > 0 ? (campaignTotals.totalCost / campaignTotals.totalConversionsValue * 100) : 0;
                                      
                                      return (
                                        <tr className="total-row" style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                                          <td style={{ padding: '12px 8px', fontWeight: '600' }}>TOTAL (20 premières)</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.totalImpressions)}</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalCTR, 2)}%</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.totalClicks)}</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalCPC, 2)}€</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.totalCost, 2)}€</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalConversionRate, 2)}%</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.totalConversions, 1)}</td>
                                          <td className={`numeric ${getCpaClassName(totalCPA, selectedAccount?.id || 'global', 'campaigns')}`} style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{totalCPA >= 0 ? formatNumber(totalCPA, 2) + '€' : '-'}</td>
                                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(campaignTotals.totalConversionsValue, 2)}€</td>
                                          <td style={{
                                            padding: '12px 8px',
                                            textAlign: 'right',
                                            fontWeight: '600',
                                            color: totalCOS === 0 ? '#6b7280' : totalCOS <= getCosThreshold(selectedAccount?.id || 'global') ? '#059669' : '#dc2626'
                                          }}>
                                            {totalCOS >= 0 ? formatNumber(totalCOS, 1) + '%' : '-'}
                                          </td>
                                        </tr>
                                      );
                                    })()}
                                  </tfoot>
                                </table>
                              </div>
                            )}

                          </>
                        )}
                      </div>
                    );
                  })()}

                  {/* Top Produits Shopping */}
                  {accountData.topProducts && accountData.topProducts.length > 0 && (
                    <div style={{ marginBottom: '32px' }}>
                      <h4 style={{ fontSize: '20px', fontWeight: '600', marginBottom: '20px', color: '#1f2937' }}>
                        🛍️ Top Produits Shopping
                      </h4>
                      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
                        {(() => {
                          // Fonction de tri pour les produits
                          const sortedProducts = [...accountData.topProducts].sort((a, b) => {
                            let aValue, bValue;
                            
                            // Calcul spécial pour les métriques calculées
                            switch(topProductsSortConfig.key) {
                              case 'title':
                                aValue = (a.title || 'Sans titre').toLowerCase();
                                bValue = (b.title || 'Sans titre').toLowerCase();
                                return topProductsSortConfig.direction === 'asc' 
                                  ? aValue.localeCompare(bValue)
                                  : bValue.localeCompare(aValue);
                              case 'itemId':
                                aValue = (a.itemId || '').toLowerCase();
                                bValue = (b.itemId || '').toLowerCase();
                                return topProductsSortConfig.direction === 'asc' 
                                  ? aValue.localeCompare(bValue)
                                  : bValue.localeCompare(aValue);
                              case 'ctr':
                                aValue = a.impressions > 0 ? (a.clicks / a.impressions * 100) : 0;
                                bValue = b.impressions > 0 ? (b.clicks / b.impressions * 100) : 0;
                                break;
                              default:
                                aValue = a[topProductsSortConfig.key] || 0;
                                bValue = b[topProductsSortConfig.key] || 0;
                            }
                            
                            if (topProductsSortConfig.key !== 'title' && topProductsSortConfig.key !== 'itemId') {
                              if (aValue < bValue) return topProductsSortConfig.direction === 'asc' ? -1 : 1;
                              if (aValue > bValue) return topProductsSortConfig.direction === 'asc' ? 1 : -1;
                              return 0;
                            }
                            return 0; // Already handled for string comparisons
                          });
                          
                          // Fonction de gestion du tri
                          const handleProductSort = (key) => {
                            let direction = 'desc';
                            if (topProductsSortConfig.key === key && topProductsSortConfig.direction === 'desc') {
                              direction = 'asc';
                            }
                            setTopProductsSortConfig({ key, direction });
                          };
                          
                          // Calculer les totaux (limité aux 10 premiers produits affichés)
                          const displayedProducts = sortedProducts.slice(0, 10);
                          const productTotals = displayedProducts.reduce((acc, product) => ({
                            impressions: acc.impressions + (product.impressions || 0),
                            clicks: acc.clicks + (product.clicks || 0),
                            cost: acc.cost + (product.cost || 0),
                            conversions: acc.conversions + (product.conversions || 0)
                          }), { impressions: 0, clicks: 0, cost: 0, conversions: 0 });
                          
                          // Calculer CTR total
                          const totalCTR = productTotals.impressions > 0 ? (productTotals.clicks / productTotals.impressions * 100) : 0;
                          
                          return (
                            <table style={{ width: '100%', fontSize: '14px' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                              <th onClick={() => handleProductSort('title')} className="sortable" style={{ textAlign: 'left', padding: '8px', minWidth: '300px', cursor: 'pointer' }}>
                                Produit {topProductsSortConfig.key === 'title' && (topProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th onClick={() => handleProductSort('itemId')} className="sortable" style={{ textAlign: 'left', padding: '8px', cursor: 'pointer' }}>
                                ID {topProductsSortConfig.key === 'itemId' && (topProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th onClick={() => handleProductSort('impressions')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                Impressions {topProductsSortConfig.key === 'impressions' && (topProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th onClick={() => handleProductSort('clicks')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                Clics {topProductsSortConfig.key === 'clicks' && (topProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th onClick={() => handleProductSort('ctr')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                CTR {topProductsSortConfig.key === 'ctr' && (topProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th onClick={() => handleProductSort('cost')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                Coût {topProductsSortConfig.key === 'cost' && (topProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th onClick={() => handleProductSort('conversions')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                Conv. {topProductsSortConfig.key === 'conversions' && (topProductsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {displayedProducts
                              .map((product, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '12px 8px', fontWeight: '500' }}>{product.title || 'Sans titre'}</td>
                                <td style={{ padding: '12px 8px', fontSize: '12px', color: '#6b7280' }}>{product.itemId}</td>
                                <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(product.impressions)}</td>
                                <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(product.clicks)}</td>
                                <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                                  {product.impressions > 0 ? ((product.clicks / product.impressions) * 100).toFixed(2) + '%' : (0).toFixed(2) + '%'}
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(product.cost, 2)}€</td>
                                <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(product.conversions, 1)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="total-row" style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                              <td style={{ padding: '12px 8px', fontWeight: '600' }}>TOTAL (10 premiers)</td>
                              <td style={{ padding: '12px 8px' }}>-</td>
                              <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(productTotals.impressions)}</td>
                              <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(productTotals.clicks)}</td>
                              <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalCTR, 2)}%</td>
                              <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(productTotals.cost, 2)}€</td>
                              <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(productTotals.conversions, 1)}</td>
                            </tr>
                          </tfoot>
                        </table>
                          );
                        })()}
                      </div>
                    </div>
                  )}

                  {/* Termes de recherche */}
                  {console.log('🔍 Debug searchTerms:', {
                    hasSearchTerms: !!filteredAccountData.searchTerms,
                    length: filteredAccountData.searchTerms?.length || 0,
                    searchTerms: filteredAccountData.searchTerms
                  })}
                  {filteredAccountData.searchTerms && filteredAccountData.searchTerms.length > 0 ? (
                    <div className="campaigns-table-section">
                      <div className="section-header">
                        <h4>
                          🔍 Termes de recherche Performance Max ({filteredAccountData.searchTerms.length} termes{(pmaxCampaignFilter || pmaxLabelsFilter.length > 0) ? ' filtrés' : ''})
                        </h4>
                      </div>
                      <div className="search-terms-table-wrapper">
                        {(() => {
                          // Calculer les totaux harmonisés sur toutes les données filtrées
                          const searchTermsTotals = filteredAccountData.searchTerms.reduce((acc, term) => ({
                            impressions: acc.impressions + (term.impressions || 0),
                            clicks: acc.clicks + (term.clicks || 0),
                            cost: acc.cost + (term.cost || 0),
                            conversions: acc.conversions + (term.conversions || 0),
                            conversionsValue: acc.conversionsValue + (term.conversionsValue || 0)
                          }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });
                          
                          // Calculer toutes les métriques dérivées
                          const totalCTR = searchTermsTotals.impressions > 0 ? (searchTermsTotals.clicks / searchTermsTotals.impressions * 100) : 0;
                          const totalCPC = searchTermsTotals.clicks > 0 ? (searchTermsTotals.cost / searchTermsTotals.clicks) : 0;
                          const totalConversionRate = searchTermsTotals.clicks > 0 ? (searchTermsTotals.conversions / searchTermsTotals.clicks * 100) : 0;
                          const totalCPA = searchTermsTotals.conversions > 0 ? (searchTermsTotals.cost / searchTermsTotals.conversions) : 0;
                          const totalCOS = searchTermsTotals.conversionsValue > 0 ? (searchTermsTotals.cost / searchTermsTotals.conversionsValue * 100) : 0;
                          const cosClass = getCosClassName(totalCOS, selectedAccount?.id || 'global', 'search_terms');

                          // Fonction de tri pour les termes de recherche
                          const sortedSearchTerms = [...filteredAccountData.searchTerms].sort((a, b) => {
                            let aValue, bValue;
                            
                            // Calcul spécial pour les métriques calculées
                            switch(searchTermsSortConfig.key) {
                              case 'ctr':
                                aValue = a.ctr || 0;
                                bValue = b.ctr || 0;
                                break;
                              case 'cpc':
                                aValue = a.cpc || Number.MAX_VALUE;
                                bValue = b.cpc || Number.MAX_VALUE;
                                break;
                              case 'conversionRate':
                                aValue = a.clicks > 0 ? (a.conversions / a.clicks * 100) : 0;
                                bValue = b.clicks > 0 ? (b.conversions / b.clicks * 100) : 0;
                                break;
                              case 'cpa':
                                aValue = a.conversions > 0 ? (a.cost / a.conversions) : Number.MAX_VALUE;
                                bValue = b.conversions > 0 ? (b.cost / b.conversions) : Number.MAX_VALUE;
                                break;
                              case 'cos':
                                aValue = a.conversionsValue > 0 ? (a.cost / a.conversionsValue * 100) : Number.MAX_VALUE;
                                bValue = b.conversionsValue > 0 ? (b.cost / b.conversionsValue * 100) : Number.MAX_VALUE;
                                break;
                              default:
                                aValue = a[searchTermsSortConfig.key] || 0;
                                bValue = b[searchTermsSortConfig.key] || 0;
                            }
                            
                            if (aValue < bValue) return searchTermsSortConfig.direction === 'asc' ? -1 : 1;
                            if (aValue > bValue) return searchTermsSortConfig.direction === 'asc' ? 1 : -1;
                            return 0;
                          });
                          
                          // Fonction de gestion du tri
                          const handleSearchTermSort = (key) => {
                            let direction = 'desc';
                            if (searchTermsSortConfig.key === key && searchTermsSortConfig.direction === 'desc') {
                              direction = 'asc';
                            }
                            setSearchTermsSortConfig({ key, direction });
                            setSearchTermsPage(1); // Réinitialiser à la première page
                          };
                          
                          return (
                            <table className="search-terms-table" style={{ width: '100%', fontSize: '14px' }}>
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                              <th onClick={() => handleSearchTermSort('searchTerm')} className="sortable" style={{ textAlign: 'left', padding: '8px', minWidth: '300px', cursor: 'pointer' }}>
                                Terme de recherche {searchTermsSortConfig.key === 'searchTerm' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              {isPmaxMetricVisible('impressions') && (
                                <th onClick={() => handleSearchTermSort('impressions')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Impr. {searchTermsSortConfig.key === 'impressions' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('ctr') && (
                                <th onClick={() => handleSearchTermSort('ctr')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  CTR {searchTermsSortConfig.key === 'ctr' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('clics') && (
                                <th onClick={() => handleSearchTermSort('clicks')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Clics {searchTermsSortConfig.key === 'clicks' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('cpc') && (
                                <th onClick={() => handleSearchTermSort('cpc')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  CPC {searchTermsSortConfig.key === 'cpc' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('cout') && (
                                <th onClick={() => handleSearchTermSort('cost')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Coût {searchTermsSortConfig.key === 'cost' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('taux_conv') && (
                                <th onClick={() => handleSearchTermSort('conversionRate')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Taux Conv. {searchTermsSortConfig.key === 'conversionRate' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('conv') && (
                                <th onClick={() => handleSearchTermSort('conversions')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Conv. {searchTermsSortConfig.key === 'conversions' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('cpa') && (
                                <th onClick={() => handleSearchTermSort('cpa')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  CPA {searchTermsSortConfig.key === 'cpa' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('valeur_conv') && (
                                <th onClick={() => handleSearchTermSort('conversionsValue')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  Valeur Conv. {searchTermsSortConfig.key === 'conversionsValue' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                              {isPmaxMetricVisible('cos') && (
                                <th onClick={() => handleSearchTermSort('cos')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                  COS {searchTermsSortConfig.key === 'cos' && (searchTermsSortConfig.direction === 'asc' ? '▲' : '▼')}
                                </th>
                              )}
                            </tr>
                          </thead>
                          <tbody>
                            {sortedSearchTerms
                              .slice((searchTermsPage - 1) * 10, searchTermsPage * 10)
                              .map((term, index) => {
                                // Utiliser le COS calculé côté backend (avec l'agrégation)
                                const termCos = term.cos || 0;
                                const termCosClass = getCosClassName(termCos, selectedAccount?.id || 'global', 'search_terms');
                                
                                return (
                                <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                  <td style={{ padding: '12px 8px', fontWeight: '500' }}>
                                    {term.searchTerm}
                                  </td>
                                  {isPmaxMetricVisible('impressions') && (
                                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(term.impressions)}</td>
                                  )}
                                  {isPmaxMetricVisible('ctr') && (
                                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(term.ctr, 2)}%</td>
                                  )}
                                  {isPmaxMetricVisible('clics') && (
                                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(term.clicks)}</td>
                                  )}
                                  {isPmaxMetricVisible('cpc') && (
                                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(term.cpc, 2)}€</td>
                                  )}
                                  {isPmaxMetricVisible('cout') && (
                                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(term.cost, 2)}€</td>
                                  )}
                                  {isPmaxMetricVisible('taux_conv') && (
                                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>
                                      {formatNumber(term.conversionRate, 2)}%
                                    </td>
                                  )}
                                  {isPmaxMetricVisible('conv') && (
                                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(term.conversions, 1)}</td>
                                  )}
                                  {isPmaxMetricVisible('cpa') && (
                                    <td className={`numeric ${getCpaClassName(term.cpa || 0, selectedAccount?.id || 'global', 'search_terms')}`} style={{ padding: '12px 8px', textAlign: 'right' }}>
                                      {term.conversions >= 0 ? formatNumber(term.cpa, 2) + '€' : '-'}
                                    </td>
                                  )}
                                  {isPmaxMetricVisible('valeur_conv') && (
                                    <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(term.conversionsValue || 0, 2)}€</td>
                                  )}
                                  {isPmaxMetricVisible('cos') && (
                                    <td className={`numeric ${termCosClass}`} style={{ padding: '12px 8px', textAlign: 'right' }}>
                                      {term.conversionsValue > 0 ? formatNumber(termCos, 1) + '%' : '0,0%'}
                                    </td>
                                  )}
                                </tr>
                                );
                              })
                            }
                          </tbody>
                          <tfoot>
                            <tr className="total-row" style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                              <td style={{ padding: '12px 8px', fontWeight: '600' }}>TOTAL</td>
                              {isPmaxMetricVisible('impressions') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(searchTermsTotals.impressions)}</td>
                              )}
                              {isPmaxMetricVisible('ctr') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalCTR, 2)}%</td>
                              )}
                              {isPmaxMetricVisible('clics') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(searchTermsTotals.clicks)}</td>
                              )}
                              {isPmaxMetricVisible('cpc') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalCPC, 2)}€</td>
                              )}
                              {isPmaxMetricVisible('cout') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(searchTermsTotals.cost, 2)}€</td>
                              )}
                              {isPmaxMetricVisible('taux_conv') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalConversionRate, 2)}%</td>
                              )}
                              {isPmaxMetricVisible('conv') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(searchTermsTotals.conversions, 1)}</td>
                              )}
                              {isPmaxMetricVisible('cpa') && (
                                <td className={`numeric ${getCpaClassName(totalCPA, selectedAccount?.id || 'global', 'search_terms')}`} style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                                  {searchTermsTotals.conversions >= 0 ? formatNumber(totalCPA, 2) + '€' : '-'}
                                </td>
                              )}
                              {isPmaxMetricVisible('valeur_conv') && (
                                <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(searchTermsTotals.conversionsValue, 2)}€</td>
                              )}
                              {isPmaxMetricVisible('cos') && (
                                <td className={`numeric ${cosClass}`} style={{ padding: '12px 8px', fontWeight: '600' }}>
                                  {searchTermsTotals.conversionsValue > 0 ? formatNumber(totalCOS, 1) + '%' : '0,0%'}
                                </td>
                              )}
                            </tr>
                          </tfoot>
                        </table>
                          );
                        })()}
                        {filteredAccountData.searchTerms.length > 10 && (
                          <div className="pagination-controls">
                            <div className="pagination-info">
                              <span>
                                Affichage {((searchTermsPage - 1) * 10) + 1}-{Math.min(searchTermsPage * 10, filteredAccountData.searchTerms.length)} sur {filteredAccountData.searchTerms.length} termes
                              </span>
                            </div>
                            <div className="pagination-buttons">
                              <button
                                onClick={() => setSearchTermsPage(p => Math.max(1, p - 1))}
                                disabled={searchTermsPage === 1}
                                className="pagination-btn"
                              >
                                ← Précédent
                              </button>
                              <span className="page-numbers">
                                Page {searchTermsPage} sur {Math.ceil(filteredAccountData.searchTerms.length / 10)}
                              </span>
                              <button
                                onClick={() => setSearchTermsPage(p => Math.min(Math.ceil(filteredAccountData.searchTerms.length / 10), p + 1))}
                                disabled={searchTermsPage >= Math.ceil(filteredAccountData.searchTerms.length / 10)}
                                className="pagination-btn"
                              >
                                Suivant →
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="campaigns-table-section">
                      <div className="section-header">
                        <h4>
                          🔍 Termes de recherche Performance Max
                        </h4>
                      </div>
                      <div style={{ 
                        backgroundColor: '#f8f9fa', 
                        borderRadius: '12px', 
                        padding: '48px', 
                        textAlign: 'center',
                        border: '1px solid #e5e7eb'
                      }}>
                        <div style={{ marginBottom: '16px' }}><Search size={48} color="#6b7280" /></div>
                        <h3 style={{ fontSize: '18px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                          Aucun terme de recherche Performance Max
                        </h3>
                        <p style={{ fontSize: '14px', color: '#6b7280' }}>
                          Les termes de recherche Performance Max apparaîtront ici quand disponibles
                        </p>
                      </div>
                    </div>
                  )}

                  {/* Composant Performance Max - Emplacements */}
                  {!loadingPerformanceMax && performanceMaxData && Object.keys(performanceMaxData).length > 0 && (
                    <div className="campaigns-table-section">
                      <PerformanceMaxPlacementsTableModular
                        selectedAccount={selectedAccount}
                        selectedPeriod={selectedPeriod}
                        customStartDate={customStartDate}
                        customEndDate={customEndDate}
                        formatNumber={formatNumber}
                        isVisible={true}
                        onToggleVisibility={() => {}}
                        selectedCampaignFilter={pmaxCampaignFilter}
                        pmaxLabelsFilter={pmaxLabelsFilter}
                        campaignLabelsMap={campaignLabelsMap}
                        performanceMaxChannelsData={performanceMaxChannelsData}
                        token={token}
                        setError={setError}
                      />
                    </div>
                  )}

                  {/* Emplacements (version inline - données filtrées) */}
                  {filteredAccountData.placements && filteredAccountData.placements.length > 0 && (
                    <div className="campaigns-table-section">
                      <div className="section-header">
                        <h4>
                          📍 Emplacements Performance Max ({filteredAccountData.placements.length} emplacements{(pmaxCampaignFilter || pmaxLabelsFilter.length > 0) ? ' filtrés' : ''}) - Impressions uniquement
                        </h4>
                      </div>

                      {/* Note explicative */}
                      <div style={{
                        backgroundColor: '#fef3c7',
                        borderLeft: '4px solid #f59e0b',
                        padding: '12px 16px',
                        marginBottom: '20px',
                        borderRadius: '8px',
                        fontSize: '15px',
                        color: '#92400e'
                      }}>
                        <strong>Note :</strong> L'API Google Ads ne fournit que les données d'impressions pour les emplacements Performance Max. Les métriques de clics, coût et conversions ne sont pas disponibles au niveau des emplacements.
                      </div>

                      {/* Graphique camembert de ventilation par type */}
                      <div style={{ backgroundColor: 'white', borderRadius: '12px', padding: '20px', boxShadow: '0 1px 3px rgba(0,0,0,0.1)', marginBottom: '20px' }}>
                        <h5 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '16px', color: '#374151' }}>
                          Ventilation des impressions par type d'emplacement
                        </h5>
                        {(() => {
                          // Agrégation des impressions par type
                          const impressionsByType = {};
                          filteredAccountData.placements.forEach(placement => {
                            const typeStr = String(placement.placementType);
                            const typeMap = {
                              '0': 'Non spécifié',
                              '1': 'Inconnu', 
                              '2': 'Site web',
                              '3': 'Application mobile',
                              '4': 'Catégorie d\'app mobile',
                              '5': 'Vidéo YouTube',
                              '6': 'Chaîne YouTube',
                              '7': 'Produit Google',
                              'UNSPECIFIED': 'Non spécifié',
                              'UNKNOWN': 'Inconnu',
                              'WEBSITE': 'Site web',
                              'MOBILE_APP_CATEGORY': 'Catégorie d\'app mobile',
                              'MOBILE_APPLICATION': 'Application mobile',
                              'YOUTUBE_VIDEO': 'Vidéo YouTube',
                              'YOUTUBE_CHANNEL': 'Chaîne YouTube',
                              'GOOGLE_PRODUCT': 'Produit Google'
                            };
                            const typeName = typeMap[typeStr] || typeStr || 'Non spécifié';
                            
                            if (!impressionsByType[typeName]) {
                              impressionsByType[typeName] = 0;
                            }
                            impressionsByType[typeName] += placement.impressions || 0;
                          });
                          
                          // Conversion en données pour le graphique
                          const chartData = Object.entries(impressionsByType)
                            .map(([name, value]) => ({ name, value }))
                            .sort((a, b) => b.value - a.value);
                          
                          const COLORS = ['#1a1a1a', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#ec4899', '#14b8a6', '#f97316'];
                          
                          return (
                            <ResponsiveContainer width="100%" height={300}>
                              <PieChart>
                                <Pie
                                  data={chartData}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ name, percent }) => `${name} ${(percent * 100).toFixed(1)}%`}
                                  outerRadius={80}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {chartData.map((entry, index) => (
                                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value) => formatNumber(value)} />
                              </PieChart>
                            </ResponsiveContainer>
                          );
                        })()}
                      </div>
                      
                      <div className="campaigns-table-wrapper">
                        {(() => {
                          // Calculer le total des impressions sur toutes les données filtrées
                          const totalImpressions = filteredAccountData.placements.reduce((acc, placement) => acc + (placement.impressions || 0), 0);

                          // Fonction de tri pour les emplacements
                          const sortedPlacements = [...filteredAccountData.placements].sort((a, b) => {
                            let aValue, bValue;
                            
                            switch(placementsSortConfig.key) {
                              case 'displayName':
                                aValue = (a.displayName || a.placement || '').toLowerCase();
                                bValue = (b.displayName || b.placement || '').toLowerCase();
                                return placementsSortConfig.direction === 'asc' 
                                  ? aValue.localeCompare(bValue)
                                  : bValue.localeCompare(aValue);
                              case 'placement':
                                aValue = (a.placement || '').toLowerCase();
                                bValue = (b.placement || '').toLowerCase();
                                return placementsSortConfig.direction === 'asc' 
                                  ? aValue.localeCompare(bValue)
                                  : bValue.localeCompare(aValue);
                              case 'placementType':
                                aValue = String(a.placementType || '');
                                bValue = String(b.placementType || '');
                                return placementsSortConfig.direction === 'asc' 
                                  ? aValue.localeCompare(bValue)
                                  : bValue.localeCompare(aValue);
                              default:
                                aValue = a[placementsSortConfig.key] || 0;
                                bValue = b[placementsSortConfig.key] || 0;
                                if (aValue < bValue) return placementsSortConfig.direction === 'asc' ? -1 : 1;
                                if (aValue > bValue) return placementsSortConfig.direction === 'asc' ? 1 : -1;
                                return 0;
                            }
                          });
                          
                          // Fonction de gestion du tri
                          const handlePlacementSort = (key) => {
                            let direction = 'desc';
                            if (placementsSortConfig.key === key && placementsSortConfig.direction === 'desc') {
                              direction = 'asc';
                            }
                            setPlacementsSortConfig({ key, direction });
                            setPlacementsPage(1); // Réinitialiser à la première page
                          };
                          
                          return (
                            <table className="campaigns-table">
                          <thead>
                            <tr style={{ borderBottom: '2px solid #e5e7eb' }}>
                              <th onClick={() => handlePlacementSort('displayName')} className="sortable" style={{ textAlign: 'left', padding: '8px', minWidth: '250px', cursor: 'pointer' }}>
                                Emplacement {placementsSortConfig.key === 'displayName' && (placementsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th onClick={() => handlePlacementSort('placement')} className="sortable" style={{ textAlign: 'left', padding: '8px', minWidth: '200px', cursor: 'pointer' }}>
                                URL {placementsSortConfig.key === 'placement' && (placementsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th onClick={() => handlePlacementSort('placementType')} className="sortable" style={{ textAlign: 'left', padding: '8px', cursor: 'pointer' }}>
                                Type {placementsSortConfig.key === 'placementType' && (placementsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                              <th onClick={() => handlePlacementSort('impressions')} className="sortable numeric" style={{ textAlign: 'right', padding: '8px', cursor: 'pointer' }}>
                                Impressions {placementsSortConfig.key === 'impressions' && (placementsSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {sortedPlacements
                              .slice((placementsPage - 1) * 10, placementsPage * 10)
                              .map((placement, index) => (
                              <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                                <td style={{ padding: '12px 8px', fontWeight: '500' }}>{placement.displayName || placement.placement}</td>
                                <td style={{ padding: '12px 8px' }}>
                                  {placement.placement ? (
                                    <a 
                                      href={placement.placement.startsWith('http') ? placement.placement : `https://${placement.placement}`}
                                      target="_blank" 
                                      rel="noopener noreferrer"
                                      style={{
                                        color: '#1a1a1a',
                                        textDecoration: 'none',
                                        fontSize: '12px',
                                        maxWidth: '180px',
                                        display: 'inline-block',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap'
                                      }}
                                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                                    >
                                      {placement.placement}
                                    </a>
                                  ) : (
                                    <span style={{ color: '#9ca3af', fontSize: '12px' }}>-</span>
                                  )}
                                </td>
                                <td style={{ padding: '12px 8px' }}>
                                  <span style={{
                                    padding: '2px 8px',
                                    borderRadius: '4px',
                                    fontSize: '12px',
                                    backgroundColor: '#e0e7ff',
                                    color: '#3730a3'
                                  }}>
                                    {(() => {
                                      // Mapping des types d'emplacement (enum numérique vers nom)
                                      const placementTypeMap = {
                                        '0': 'Non spécifié',
                                        '1': 'Inconnu', 
                                        '2': 'Site web',
                                        '3': 'Application mobile',
                                        '4': 'Catégorie d\'app mobile',
                                        '5': 'Vidéo YouTube',
                                        '6': 'Chaîne YouTube',
                                        '7': 'Produit Google',
                                        // Mapping textuel aussi au cas où
                                        'UNSPECIFIED': 'Non spécifié',
                                        'UNKNOWN': 'Inconnu',
                                        'WEBSITE': 'Site web',
                                        'MOBILE_APP_CATEGORY': 'Catégorie d\'app mobile',
                                        'MOBILE_APPLICATION': 'Application mobile',
                                        'YOUTUBE_VIDEO': 'Vidéo YouTube',
                                        'YOUTUBE_CHANNEL': 'Chaîne YouTube',
                                        'GOOGLE_PRODUCT': 'Produit Google'
                                      };
                                      const typeValue = String(placement.placementType);
                                      console.log('🔍 Type d\'emplacement brut:', placement.placementType, 'Type:', typeof placement.placementType);
                                      return placementTypeMap[typeValue] || typeValue || 'Non spécifié';
                                    })()}
                                  </span>
                                </td>
                                <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(placement.impressions)}</td>
                              </tr>
                            ))}
                          </tbody>
                          <tfoot>
                            <tr className="total-row" style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                              <td style={{ padding: '12px 8px', fontWeight: '600' }}>TOTAL</td>
                              <td style={{ padding: '12px 8px' }}>-</td>
                              <td style={{ padding: '12px 8px' }}>-</td>
                              <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>{formatNumber(totalImpressions)}</td>
                            </tr>
                          </tfoot>
                        </table>
                          );
                        })()}
                        {filteredAccountData.placements.length > 10 && (
                          <div className="pagination-controls">
                            <div className="pagination-info">
                              <span>
                                Affichage {((placementsPage - 1) * 10) + 1}-{Math.min(placementsPage * 10, filteredAccountData.placements.length)} sur {filteredAccountData.placements.length} emplacements
                              </span>
                            </div>
                            <div className="pagination-buttons">
                              <button
                                onClick={() => setPlacementsPage(p => Math.max(1, p - 1))}
                                disabled={placementsPage === 1}
                                className="pagination-btn"
                              >
                                ← Précédent
                              </button>
                              <span className="page-numbers">
                                Page {placementsPage} sur {Math.ceil(filteredAccountData.placements.length / 10)}
                              </span>
                              <button
                                onClick={() => setPlacementsPage(p => Math.min(Math.ceil(filteredAccountData.placements.length / 10), p + 1))}
                                disabled={placementsPage >= Math.ceil(filteredAccountData.placements.length / 10)}
                                className="pagination-btn"
                              >
                                Suivant →
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
                  );
                };

                // Return the mapped results
                return Object.entries(performanceMaxData).map(([accountId, accountData]) =>
                  renderPerformanceMaxAccount(accountId, accountData)
                );
              })()}
            </div>
          ) : (
            <div style={{ 
              backgroundColor: '#f8f9fa', 
              borderRadius: '12px', 
              padding: '48px', 
              textAlign: 'center',
              border: '1px solid #e5e7eb'
            }}>
              <div style={{ marginBottom: '16px' }}><Zap size={48} color="#6b7280" /></div>
              <h3 style={{ fontSize: '20px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                Aucune campagne Performance Max active dans le compte
              </h3>
              <p style={{ fontSize: '16px', color: '#6b7280' }}>
                Ce compte ne contient pas de campagnes Performance Max actives et n'a enregistré aucune impression sur la période sélectionnée
              </p>
            </div>
          )}
          
          {/* Section Display Performance Max - TEMPORAIREMENT MASQUÉE CAR NON FONCTIONNELLE */}
          {false && (
          <div className="performance-max-display-section" style={{ marginTop: '32px' }}>
            <div style={{
              background: '#ffffff',
              borderRadius: '12px',
              border: '1px solid #e5e7eb',
              padding: '24px',
              marginBottom: '24px'
            }}>
              <div style={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between',
                marginBottom: '20px'
              }}>
                <h3 style={{ 
                  fontSize: '20px', 
                  fontWeight: '600', 
                  color: '#1f2937',
                  margin: 0
                }}>
                  🎯 Display
                </h3>
                {loadingPerformanceMaxDisplay && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <div className="spinner" style={{ width: '16px', height: '16px' }}></div>
                    <span style={{ fontSize: '14px', color: '#6b7280' }}>Chargement...</span>
                  </div>
                )}
              </div>

              {loadingPerformanceMaxDisplay ? (
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  padding: '48px',
                  color: '#6b7280'
                }}>
                  <div className="loading-spinner-container">
                    <div className="spinner"></div>
                  </div>
                  <span style={{ marginLeft: '12px' }}>Chargement des données Display...</span>
                </div>
              ) : performanceMaxDisplayData && performanceMaxDisplayData.campaigns && performanceMaxDisplayData.campaigns.length > 0 ? (
                <div>
                  <div className="display-summary-cards" style={{ 
                    display: 'grid', 
                    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', 
                    gap: '16px',
                    marginBottom: '24px'
                  }}>
                    <div className="summary-card" style={{
                      background: '#f9fafb',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                        {performanceMaxDisplayData.totals ? formatNumber(performanceMaxDisplayData.totals.impressions) : '0'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        Impressions
                      </div>
                    </div>
                    <div className="summary-card" style={{
                      background: '#f9fafb',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                        {performanceMaxDisplayData.totals ? formatNumber(performanceMaxDisplayData.totals.clicks) : '0'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        Clics
                      </div>
                    </div>
                    <div className="summary-card" style={{
                      background: '#f9fafb',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                        {performanceMaxDisplayData.totals ? `€${formatNumber(performanceMaxDisplayData.totals.cost)}` : '€0'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        Coût
                      </div>
                    </div>
                    <div className="summary-card" style={{
                      background: '#f9fafb',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                        {performanceMaxDisplayData.totals ? formatNumber(performanceMaxDisplayData.totals.conversions, 1) : '0'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        Conversions
                      </div>
                    </div>
                    <div className="summary-card" style={{
                      background: '#f9fafb',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                        {performanceMaxDisplayData.totals && performanceMaxDisplayData.totals.impressions > 0 ? 
                          `${formatNumber(performanceMaxDisplayData.totals.ctr)}%` : '0%'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        CTR
                      </div>
                    </div>
                    <div className="summary-card" style={{
                      background: '#f9fafb',
                      borderRadius: '8px',
                      padding: '16px',
                      textAlign: 'center'
                    }}>
                      <div style={{ fontSize: '24px', fontWeight: '600', color: '#1f2937' }}>
                        {performanceMaxDisplayData.totals && performanceMaxDisplayData.totals.cost > 0 ? 
                          `${formatNumber(performanceMaxDisplayData.totals.roas)}` : '0'}
                      </div>
                      <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                        ROAS
                      </div>
                    </div>
                  </div>

                  <div className="display-campaigns-table">
                    <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                      <thead>
                        <tr style={{ backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb' }}>
                          <th style={{ padding: '12px 8px', textAlign: 'left', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                            Campagne
                          </th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                            Impressions
                          </th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                            Clics
                          </th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                            CTR
                          </th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                            Coût
                          </th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                            Conversions
                          </th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                            Valeur Conv.
                          </th>
                          <th style={{ padding: '12px 8px', textAlign: 'right', fontSize: '12px', fontWeight: '600', color: '#374151' }}>
                            ROAS
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {performanceMaxDisplayData.campaigns.slice(0, 10).map((campaign, index) => (
                          <tr key={index} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            <td style={{ padding: '12px 8px' }}>
                              <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {campaign.campaignName}
                              </div>
                              <div style={{ fontSize: '11px', color: '#6b7280' }}>
                                {campaign.account}
                              </div>
                            </td>
                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.impressions)}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.clicks)}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.ctr)}%</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>€{formatNumber(campaign.cost)}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.conversions, 1)}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>€{formatNumber(campaign.conversionsValue)}</td>
                            <td style={{ padding: '12px 8px', textAlign: 'right' }}>{formatNumber(campaign.roas)}</td>
                          </tr>
                        ))}
                      </tbody>
                      <tfoot>
                        <tr className="total-row" style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                          <td style={{ padding: '12px 8px', fontWeight: '600' }}>TOTAL</td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                            {performanceMaxDisplayData.totals ? formatNumber(performanceMaxDisplayData.totals.impressions) : '0'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                            {performanceMaxDisplayData.totals ? formatNumber(performanceMaxDisplayData.totals.clicks) : '0'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                            {performanceMaxDisplayData.totals ? `${formatNumber(performanceMaxDisplayData.totals.ctr)}%` : '0%'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                            €{performanceMaxDisplayData.totals ? formatNumber(performanceMaxDisplayData.totals.cost) : '0'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                            {performanceMaxDisplayData.totals ? formatNumber(performanceMaxDisplayData.totals.conversions, 1) : '0'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                            €{performanceMaxDisplayData.totals ? formatNumber(performanceMaxDisplayData.totals.conversionsValue) : '0'}
                          </td>
                          <td style={{ padding: '12px 8px', textAlign: 'right', fontWeight: '600' }}>
                            {performanceMaxDisplayData.totals ? formatNumber(performanceMaxDisplayData.totals.roas) : '0'}
                          </td>
                        </tr>
                      </tfoot>
                    </table>
                    {performanceMaxDisplayData.campaigns.length > 10 && (
                      <div style={{ marginTop: '16px', textAlign: 'center', fontSize: '14px', color: '#6b7280' }}>
                        ... et {performanceMaxDisplayData.campaigns.length - 10} autres campagnes
                      </div>
                    )}
                  </div>
                </div>
              ) : (
                <div style={{ 
                  backgroundColor: '#f8f9fa', 
                  borderRadius: '8px', 
                  padding: '32px', 
                  textAlign: 'center',
                  border: '1px solid #e5e7eb'
                }}>
                  <div style={{ marginBottom: '12px' }}><Target size={32} color="#6b7280" /></div>
                  <h4 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '8px' }}>
                    Aucune donnée Display trouvée
                  </h4>
                  <p style={{ fontSize: '14px', color: '#6b7280' }}>
                    Aucune impression Display Performance Max sur la période sélectionnée
                  </p>
                </div>
              )}
            </div>
          </div>
          )}
          </>
          )}

        </div>
      )}

      {/* Dashboard Shopping */}
      {selectedDashboardModel === 'shopping' && selectedAccount && (
        <div className="shopping-dashboard" style={{ marginTop: '32px' }}>

          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingShoppingDashboard && accountHasNoData && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {!accountHasNoData && (
            <>
          {loadingShoppingDashboard ? (
            <DashboardLoadingScreen
              platformName="Google Ads"
              progress={shoppingLoadingProgress}
              features={[
                { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
                { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
                { icon: <img src={googleAdsLogo} alt="Google Ads" style={{width: '28px', height: '28px'}} />, label: 'Analyse des campagnes' }
              ]}
            />
          ) : (() => {
            // Vérifier s'il y a des données Shopping à afficher
            const hasShoppingData = shoppingDashboardData && Object.keys(shoppingDashboardData).length > 0;
            
            if (!hasShoppingData) return false;
            
            // Vérifier s'il y a des impressions ou des produits
            const hasDataToShow = Object.values(shoppingDashboardData).some(accountData => {
              // Vérifier s'il y a des impressions totales
              if ((accountData.totalMetrics?.impressions || 0) > 0) return true;
              
              // Vérifier s'il y a des produits
              if (accountData.products && accountData.products.length > 0) return true;
              
              return false;
            });
            
            console.log('🔍 Condition d\'affichage Shopping:', {
              hasShoppingData,
              hasDataToShow,
              accounts: Object.keys(shoppingDashboardData || {}),
              totalImpressions: Object.values(shoppingDashboardData || {}).map(acc => acc.totalMetrics?.impressions || 0),
              totalProducts: Object.values(shoppingDashboardData || {}).map(acc => acc.products?.length || 0)
            });
            
            return hasDataToShow;
          })() ? (
            <div>
              {Object.entries(shoppingDashboardData).map(([accountId, accountData]) => (
                <div key={accountId} className="shopping-account-section" style={{ marginBottom: '32px' }}>

                  {accountData.error ? (
                    <div style={{
                      backgroundColor: '#fee2e2',
                      color: '#dc2626',
                      padding: '16px',
                      borderRadius: '8px',
                      marginBottom: '16px'
                    }}>
                      <AlertCircle size={16} style={{ display: 'inline', marginRight: '8px' }} />Erreur: {accountData.error}
                    </div>
                  ) : (
                    <>
                      {/* Métriques globales */}
                      <div className="performance-metrics-grid" style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                        gap: '16px',
                        marginBottom: '32px'
                      }}>
                        <div className="metric-card" style={{
                          background: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: '#059669' }}>
                            {(() => {
                              // Compter seulement les produits qui ont reçu au moins 1 impression (vraiment diffusés)
                              return filterProducts(accountData.products).filter(product => 
                                (product.impressions || 0) > 0
                              ).length;
                            })()}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                            Produits diffusés
                            {(() => {
                              // Calculer le pourcentage de produits diffusés
                              const diffusedCount = filterProducts(accountData.products).filter(product => 
                                (product.impressions || 0) > 0
                              ).length;
                              
                              // Calculer le total dans les flux selon les filtres
                              let totalInFlux = 0;
                              if (selectedCountryFilter === 'all' && selectedProductTypeFilter === 'all' && selectedLabelFilter === 'all') {
                                const breakdown = accountData.fullCatalogAvailabilityBreakdown || accountData.availabilityBreakdown || {};
                                totalInFlux = Object.values(breakdown).reduce((total, data) => total + (data.count || 0), 0) || accountData.totalProducts || 0;
                              } else if (selectedCountryFilter !== 'all' && selectedLabelFilter === 'all') {
                                if (accountData.fullCatalogCountryBreakdown && accountData.fullCatalogCountryBreakdown[selectedCountryFilter]) {
                                  totalInFlux = accountData.fullCatalogCountryBreakdown[selectedCountryFilter].total;
                                } else {
                                  totalInFlux = filterProducts(accountData.products).length;
                                }
                              } else {
                                totalInFlux = filterProducts(accountData.products).length;
                              }
                              
                              const percentage = totalInFlux > 0 ? ((diffusedCount / totalInFlux) * 100).toFixed(1) : 0;
                              return totalInFlux > 0 ? ` (${percentage}%)` : '';
                            })()}
                          </div>
                        </div>
                        
                        <div className="metric-card" style={{
                          background: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: '#1f2937' }}>
                            {(() => {
                              // Compter tous les produits disponibles dans les flux (données Merchant Center)
                              if (selectedCountryFilter === 'all' && selectedProductTypeFilter === 'all' && selectedLabelFilter === 'all') {
                                // Aucun filtre: utiliser les données complètes Merchant Center
                                const breakdown = accountData.fullCatalogAvailabilityBreakdown || accountData.availabilityBreakdown || {};
                                return Object.values(breakdown).reduce((total, data) => total + (data.count || 0), 0) || accountData.totalProducts || 0;
                              } else if (selectedCountryFilter !== 'all' && selectedLabelFilter === 'all') {
                                // Filtre pays seulement: utiliser les données Merchant Center par pays
                                if (accountData.fullCatalogCountryBreakdown && accountData.fullCatalogCountryBreakdown[selectedCountryFilter]) {
                                  return accountData.fullCatalogCountryBreakdown[selectedCountryFilter].total;
                                } else {
                                  // Fallback: utiliser les données des produits
                                  return filterProducts(accountData.products).length;
                                }
                              } else {
                                // Filtre libellé appliqué: compter sur les produits filtrés
                                return filterProducts(accountData.products).length;
                              }
                            })()}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>Produits dans le(s) flux</div>
                        </div>
                        
                        <div className="metric-card" style={{
                          background: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: '#059669' }}>
                            {(() => {
                              // Helper pour vérifier si un produit est en stock (gère tous les formats)
                              const isInStock = (availability) => {
                                const av = (availability || '').toLowerCase().replace(/[_\s]/g, '');
                                return av === 'instock';
                              };

                              if (selectedCountryFilter === 'all' && selectedProductTypeFilter === 'all' && selectedLabelFilter === 'all') {
                                // Aucun filtre: utiliser les vraies données Merchant Center
                                const breakdown = accountData.fullCatalogAvailabilityBreakdown || accountData.availabilityBreakdown || {};
                                // Chercher la clé en stock (peut être 'in stock', 'in_stock', 'IN_STOCK', etc.)
                                const inStockKey = Object.keys(breakdown).find(k => isInStock(k));
                                return inStockKey ? breakdown[inStockKey]?.count || 0 : 0;
                              } else if (selectedCountryFilter !== 'all' && selectedLabelFilter === 'all') {
                                // Filtre pays seulement: utiliser les données Merchant Center par pays
                                if (accountData.fullCatalogCountryBreakdown && accountData.fullCatalogCountryBreakdown[selectedCountryFilter]) {
                                  return accountData.fullCatalogCountryBreakdown[selectedCountryFilter].inStock;
                                } else {
                                  // Fallback: filtrer tous les produits du flux (pas seulement ceux diffusés)
                                  return filterProducts(accountData.products).filter(product => isInStock(product.availability)).length;
                                }
                              } else {
                                // Filtre libellé appliqué: on doit filtrer sur les produits
                                return filterProducts(accountData.products).filter(product => isInStock(product.availability)).length;
                              }
                            })()}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                            Produits en stock
                            {(() => {
                              // Helper pour vérifier si une clé correspond à "en stock"
                              const isInStock = (availability) => {
                                const av = (availability || '').toLowerCase().replace(/[_\s]/g, '');
                                return av === 'instock';
                              };

                              if (selectedCountryFilter === 'all') {
                                // Pour tous les pays
                                const breakdown = accountData.fullCatalogAvailabilityBreakdown || accountData.availabilityBreakdown || {};
                                const inStockKey = Object.keys(breakdown).find(k => isInStock(k));
                                const inStock = inStockKey ? breakdown[inStockKey]?.count || 0 : 0;
                                const totalInFlux = Object.values(breakdown).reduce((total, data) => total + (data.count || 0), 0) || accountData.totalProducts || 0;
                                const percentage = totalInFlux > 0 ? ((inStock / totalInFlux) * 100).toFixed(1) : 0;
                                return totalInFlux > 0 ? ` (${percentage}%)` : '';
                              } else {
                                // Pour un pays spécifique, utiliser les données Merchant Center par pays
                                if (accountData.fullCatalogCountryBreakdown && accountData.fullCatalogCountryBreakdown[selectedCountryFilter]) {
                                  const countryData = accountData.fullCatalogCountryBreakdown[selectedCountryFilter];
                                  const percentage = countryData.total > 0 ? ((countryData.inStock / countryData.total) * 100).toFixed(1) : 0;
                                  return countryData.total > 0 ? ` (${percentage}%)` : '';
                                } else {
                                  // Fallback
                                  const filteredProducts = (accountData.products || []).filter(product => {
                                    const productCountry = product.feedLabelOrCountry || product.targetCountry || 'FR';
                                    return productCountry === selectedCountryFilter;
                                  });
                                  const inStock = filteredProducts.filter(product => isInStock(product.availability)).length;
                                  const totalInFlux = filteredProducts.length;
                                  const percentage = totalInFlux > 0 ? ((inStock / totalInFlux) * 100).toFixed(1) : 0;
                                  return totalInFlux > 0 ? ` (${percentage}%)` : '';
                                }
                              }
                            })()}
                          </div>
                        </div>
                        
                        <div className="metric-card" style={{
                          background: 'white',
                          padding: '20px',
                          borderRadius: '12px',
                          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                          textAlign: 'center'
                        }}>
                          <div style={{ fontSize: '28px', fontWeight: '700', color: '#dc2626' }}>
                            {(() => {
                              // Helper pour vérifier si un produit est en rupture (gère tous les formats)
                              const isOutOfStock = (availability) => {
                                const av = (availability || '').toLowerCase().replace(/[_\s]/g, '');
                                return av === 'outofstock';
                              };

                              if (selectedCountryFilter === 'all' && selectedProductTypeFilter === 'all' && selectedLabelFilter === 'all') {
                                // Aucun filtre: utiliser les vraies données Merchant Center
                                const breakdown = accountData.fullCatalogAvailabilityBreakdown || accountData.availabilityBreakdown || {};
                                // Chercher la clé en rupture (peut être 'out of stock', 'out_of_stock', 'OUT_OF_STOCK', etc.)
                                const outOfStockKey = Object.keys(breakdown).find(k => isOutOfStock(k));
                                return outOfStockKey ? breakdown[outOfStockKey]?.count || 0 : 0;
                              } else if (selectedCountryFilter !== 'all' && selectedLabelFilter === 'all') {
                                // Filtre pays seulement: utiliser les données Merchant Center par pays
                                if (accountData.fullCatalogCountryBreakdown && accountData.fullCatalogCountryBreakdown[selectedCountryFilter]) {
                                  return accountData.fullCatalogCountryBreakdown[selectedCountryFilter].outOfStock;
                                } else {
                                  // Fallback: filtrer tous les produits du flux (pas seulement ceux diffusés)
                                  return filterProducts(accountData.products).filter(product => isOutOfStock(product.availability)).length;
                                }
                              } else {
                                // Filtre libellé appliqué: on doit filtrer sur les produits
                                return filterProducts(accountData.products).filter(product => isOutOfStock(product.availability)).length;
                              }
                            })()}
                          </div>
                          <div style={{ fontSize: '14px', color: '#6b7280', marginTop: '4px' }}>
                            Produits en rupture
                            {(() => {
                              // Helper pour vérifier si une clé correspond à "en rupture"
                              const isOutOfStock = (availability) => {
                                const av = (availability || '').toLowerCase().replace(/[_\s]/g, '');
                                return av === 'outofstock';
                              };

                              if (selectedCountryFilter === 'all') {
                                // Pour tous les pays
                                const breakdown = accountData.fullCatalogAvailabilityBreakdown || accountData.availabilityBreakdown || {};
                                const outOfStockKey = Object.keys(breakdown).find(k => isOutOfStock(k));
                                const outOfStock = outOfStockKey ? breakdown[outOfStockKey]?.count || 0 : 0;
                                const totalInFlux = Object.values(breakdown).reduce((total, data) => total + (data.count || 0), 0) || accountData.totalProducts || 0;
                                const percentage = totalInFlux > 0 ? ((outOfStock / totalInFlux) * 100).toFixed(1) : 0;
                                return totalInFlux > 0 ? ` (${percentage}%)` : '';
                              } else {
                                // Pour un pays spécifique, utiliser les données Merchant Center par pays
                                if (accountData.fullCatalogCountryBreakdown && accountData.fullCatalogCountryBreakdown[selectedCountryFilter]) {
                                  const countryData = accountData.fullCatalogCountryBreakdown[selectedCountryFilter];
                                  const percentage = countryData.total > 0 ? ((countryData.outOfStock / countryData.total) * 100).toFixed(1) : 0;
                                  return countryData.total > 0 ? ` (${percentage}%)` : '';
                                } else {
                                  // Fallback
                                  const filteredProducts = (accountData.products || []).filter(product => {
                                    const productCountry = product.feedLabelOrCountry || product.targetCountry || 'FR';
                                    return productCountry === selectedCountryFilter;
                                  });
                                  const outOfStock = filteredProducts.filter(product => isOutOfStock(product.availability)).length;
                                  const totalInFlux = filteredProducts.length;
                                  const percentage = totalInFlux > 0 ? ((outOfStock / totalInFlux) * 100).toFixed(1) : 0;
                                  return totalInFlux > 0 ? ` (${percentage}%)` : '';
                                }
                              }
                            })()}
                          </div>
                        </div>
                      </div>

                      {/* Titre et filtre pays sur la même ligne */}
                      <div style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        marginBottom: '24px',
                        marginTop: '12px'
                      }}>
                        <h3 style={{
                          fontSize: '24px',
                          fontWeight: '700',
                          color: '#1f2937',
                          margin: '0',
                          display: 'flex',
                          alignItems: 'center',
                          gap: '12px'
                        }}>
                          <ShoppingCart size={22} /> Performances par produit
                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                            <button style={{
                              fontSize: '15px',
                              fontWeight: '500',
                              color: accountData.hasAdsData ? '#059669' : '#6b7280',
                              backgroundColor: accountData.hasAdsData ? '#d1fae5' : '#f3f4f6',
                              border: 'none',
                              padding: '6px 12px',
                              borderRadius: '6px',
                              cursor: 'pointer'
                            }}>
                              Google Ads
                            </button>
                            <span style={{ color: '#6b7280' }}>+</span>
                            {accountData.hasMerchantData ? (
                              <button style={{
                                fontSize: '15px',
                                fontWeight: '500',
                                color: '#059669',
                                backgroundColor: '#d1fae5',
                                border: 'none',
                                padding: '6px 12px',
                                borderRadius: '6px',
                                cursor: 'pointer'
                              }}>
                                Merchant Center
                              </button>
                            ) : (
                              <button 
                                onClick={() => {
                                  // Ouvrir le modal Merchant API avec les comptes liés suggérés
                                  window.handleMerchantCenterClick && window.handleMerchantCenterClick(accountId);
                                }}
                                style={{
                                  fontSize: '15px',
                                  fontWeight: '500',
                                  color: '#d97706',
                                  backgroundColor: '#fef3c7',
                                  border: 'none',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  cursor: 'pointer'
                                }}
                              >
                                Connecter Merchant Center
                              </button>
                            )}
                          </div>
                        </h3>

                        {/* Bannière de comparaison */}
                        {pmaxCompareEnabled && accountData.comparisonPeriod && (
                          <div style={{
                            backgroundColor: '#dbeafe',
                            color: '#1d4ed8',
                            padding: '10px 16px',
                            borderRadius: '8px',
                            marginTop: '12px',
                            marginBottom: '16px',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px'
                          }}>
                            📊 Comparaison avec {pmaxComparisonType === 'previous_year' ? "l'année précédente" : 'la période précédente'} :
                            <strong>{accountData.comparisonPeriod.startDate}</strong> au <strong>{accountData.comparisonPeriod.endDate}</strong>
                          </div>
                        )}

                        {/* Filtre par pays */}
                        {(() => {
                          // Récupérer la liste unique des pays depuis les données
                          const countries = [...new Set(
                            Object.values(shoppingDashboardData || {})
                              .flatMap(accountData => accountData.products || [])
                              .map(product => {
                                const country = product.feedLabelOrCountry || product.targetCountry || 'FR';
                                console.log('🔍 Pays trouvé:', { title: product.title, feedLabelOrCountry: product.feedLabelOrCountry, targetCountry: product.targetCountry, country });
                                return country;
                              })
                          )].sort();
                          
                          // Récupérer et organiser les types de produits hiérarchiques
                          const productTypes = new Set();
                          Object.values(shoppingDashboardData || {})
                            .flatMap(accountData => accountData.products || [])
                            .forEach(product => {
                              // Récupérer tous les niveaux de types de produits
                              if (product.product_type_l1) productTypes.add(`L1::${product.product_type_l1}`);
                              if (product.product_type_l2) productTypes.add(`L2::${product.product_type_l1} > ${product.product_type_l2}`);
                              if (product.product_type_l3) productTypes.add(`L3::${product.product_type_l1} > ${product.product_type_l2} > ${product.product_type_l3}`);
                              if (product.product_type_l4) productTypes.add(`L4::${product.product_type_l1} > ${product.product_type_l2} > ${product.product_type_l3} > ${product.product_type_l4}`);
                              if (product.product_type_l5) productTypes.add(`L5::${product.product_type_l1} > ${product.product_type_l2} > ${product.product_type_l3} > ${product.product_type_l4} > ${product.product_type_l5}`);
                            });
                          
                          // Convertir en array et trier par niveau puis alphabétiquement
                          const sortedProductTypes = Array.from(productTypes)
                            .sort((a, b) => {
                              const [levelA, pathA] = a.split('::');
                              const [levelB, pathB] = b.split('::');
                              
                              // D'abord trier par niveau
                              if (levelA !== levelB) {
                                return levelA.localeCompare(levelB);
                              }
                              // Puis par chemin alphabétique
                              return pathA.localeCompare(pathB);
                            });
                          
                          // Extraire les libellés personnalisés uniques
                          const customLabels = [...new Set(
                              accountData.products
                                .flatMap(product => {
                                  // Récupérer TOUS les custom labels (0 à 4)
                                  const labels = [];
                                  
                                  // Vérifier customLabel0 à customLabel4
                                  if (product.customLabel0) labels.push(product.customLabel0);
                                  if (product.customLabel1) labels.push(product.customLabel1);
                                  if (product.customLabel2) labels.push(product.customLabel2);
                                  if (product.customLabel3) labels.push(product.customLabel3);
                                  if (product.customLabel4) labels.push(product.customLabel4);
                                  
                                  // Aussi vérifier dans customAttributes si présent
                                  if (product.customAttributes && Array.isArray(product.customAttributes)) {
                                    product.customAttributes.forEach(attr => {
                                      if (attr.name && attr.name.startsWith('custom_label_') && attr.value) {
                                        labels.push(attr.value);
                                      }
                                    });
                                  }
                                  
                                  return labels;
                                })
                                .filter(label => label) // Supprimer les valeurs nulles/vides
                            )].sort();
                          
                          console.log('🏷️ Custom labels extraits:', {
                            customLabels,
                            customLabelsCount: customLabels.length,
                            sampleProducts: accountData.products.slice(0, 5).map(p => ({
                              title: p.title,
                              customAttributes: p.customAttributes,
                              customLabel0: p.customLabel0,
                              customLabel1: p.customLabel1,
                              customLabel2: p.customLabel2,
                              customLabel3: p.customLabel3,
                              customLabel4: p.customLabel4,
                              customLabel: p.customLabel,
                              offerId: p.offerId,
                              itemId: p.itemId
                            })),
                            totalProducts: accountData.products?.length || 0,
                            hasMerchantData: accountData.hasMerchantData,
                            merchantAssociation: accountData.merchantAssociation
                          });
                          
                          return (
                            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap' }}>
                              {/* Filtre de recherche produit */}
                              <div style={{ position: 'relative', display: 'inline-block' }}>
                                <input
                                  type="text"
                                  placeholder="Rechercher un produit..."
                                  value={selectedProductFilter ? selectedProductFilter.title : productSearchQuery}
                                  onChange={(e) => {
                                    if (selectedProductFilter) {
                                      setSelectedProductFilter(null);
                                    }
                                    setProductSearchQuery(e.target.value);
                                    setShowProductAutocomplete(true);
                                  }}
                                  onFocus={() => setShowProductAutocomplete(true)}
                                  onBlur={() => {
                                    setTimeout(() => setShowProductAutocomplete(false), 200);
                                  }}
                                  style={{
                                    padding: '6px 12px',
                                    paddingRight: (productSearchQuery || selectedProductFilter) ? '28px' : '12px',
                                    border: '1px solid #d1d5db',
                                    borderRadius: '6px',
                                    backgroundColor: selectedProductFilter ? '#dbeafe' : 'white',
                                    color: '#374151',
                                    fontSize: '15px',
                                    width: '280px',
                                    height: '35px',
                                    boxSizing: 'border-box',
                                    outline: 'none'
                                  }}
                                />
                                {(productSearchQuery || selectedProductFilter) && (
                                  <button
                                    onClick={() => {
                                      setProductSearchQuery('');
                                      setSelectedProductFilter(null);
                                      setShowProductAutocomplete(false);
                                    }}
                                    style={{
                                      position: 'absolute',
                                      right: '6px',
                                      top: '50%',
                                      transform: 'translateY(-50%)',
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      color: selectedProductFilter ? '#1a1a1a' : '#9ca3af',
                                      padding: '2px',
                                      fontSize: '14px',
                                      lineHeight: 1,
                                      zIndex: 1
                                    }}
                                    title={selectedProductFilter ? "Effacer le filtre produit" : "Effacer la recherche"}
                                  >
                                    ✕
                                  </button>
                                )}
                                {/* Autocomplete dropdown pour produits */}
                                {showProductAutocomplete && (() => {
                                  const searchLower = productSearchQuery.toLowerCase().trim();
                                  const allProducts = accountData.products || [];

                                  // Filtrer et dédupliquer par titre
                                  const uniqueTitles = new Set();
                                  const filteredProducts = allProducts
                                    .filter(p => {
                                      const title = p.title || '';
                                      if (!title || uniqueTitles.has(title.toLowerCase())) return false;
                                      if (searchLower && !title.toLowerCase().includes(searchLower)) return false;
                                      uniqueTitles.add(title.toLowerCase());
                                      return true;
                                    })
                                    .sort((a, b) => (a.title || '').localeCompare(b.title || ''))
                                    .slice(0, 50); // Limiter à 50 résultats

                                  if (filteredProducts.length === 0 && searchLower) {
                                    return (
                                      <div style={{
                                        position: 'absolute',
                                        top: '100%',
                                        left: 0,
                                        right: 0,
                                        backgroundColor: 'white',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        marginTop: '4px',
                                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                        zIndex: 1001,
                                        padding: '12px',
                                        color: '#6b7280',
                                        fontSize: '0.9em'
                                      }}>
                                        Aucun produit trouvé
                                      </div>
                                    );
                                  }

                                  if (!searchLower && filteredProducts.length === 0) {
                                    return null;
                                  }

                                  return (
                                    <div style={{
                                      position: 'absolute',
                                      top: '100%',
                                      left: 0,
                                      width: '400px',
                                      backgroundColor: 'white',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      marginTop: '4px',
                                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                                      zIndex: 1001,
                                      maxHeight: '300px',
                                      overflowY: 'auto'
                                    }}>
                                      <div style={{
                                        padding: '8px 12px',
                                        fontSize: '0.8em',
                                        color: '#6b7280',
                                        borderBottom: '1px solid #e5e7eb',
                                        backgroundColor: '#f9fafb'
                                      }}>
                                        {searchLower
                                          ? `${filteredProducts.length} produit(s) trouvé(s)`
                                          : `Saisissez pour rechercher parmi ${allProducts.length} produits`}
                                      </div>
                                      {searchLower && filteredProducts.map((product, index) => (
                                        <div
                                          key={product.offerId || product.itemId || index}
                                          onClick={() => {
                                            setSelectedProductFilter({ title: product.title });
                                            setProductSearchQuery('');
                                            setShowProductAutocomplete(false);
                                          }}
                                          style={{
                                            padding: '10px 12px',
                                            cursor: 'pointer',
                                            fontSize: '0.9em',
                                            color: '#374151',
                                            borderBottom: index < filteredProducts.length - 1 ? '1px solid #f3f4f6' : 'none',
                                            transition: 'background-color 0.15s',
                                            whiteSpace: 'nowrap',
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis'
                                          }}
                                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                                          title={product.title}
                                        >
                                          {product.title}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                })()}
                              </div>

                              {/* Filtre par pays - Afficher seulement s'il y a plus d'un pays */}
                              {countries.length > 1 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <select
                                    value={selectedCountryFilter}
                                    onChange={(e) => setSelectedCountryFilter(e.target.value)}
                                    style={{
                                      padding: '6px 12px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '15px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer',
                                      minWidth: '120px'
                                    }}
                                  >
                                    <option value="all">Tous les pays</option>
                                    {countries.map(country => (
                                      <option key={country} value={country}>
                                        {country}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              
                              {/* Filtre par type de produit - Afficher seulement s'il y a des types de produits */}
                              {sortedProductTypes.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <select
                                    value={selectedProductTypeFilter}
                                    onChange={(e) => setSelectedProductTypeFilter(e.target.value)}
                                    style={{
                                      padding: '6px 12px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '15px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer',
                                      minWidth: '250px',
                                      maxWidth: '300px'
                                    }}
                                  >
                                    <option value="all">Catégorie</option>
                                    {sortedProductTypes.map(productType => {
                                      const [level, path] = productType.split('::');
                                      const indent = '  '.repeat(parseInt(level.replace('L', '')) - 1);
                                      return (
                                        <option key={productType} value={productType}>
                                          {indent}{path}
                                        </option>
                                      );
                                    })}
                                  </select>
                                </div>
                              )}
                              
                              {/* Filtre par libellé personnalisé */}
                              {customLabels.length > 0 && (
                                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <select
                                    value={selectedLabelFilter}
                                    onChange={(e) => {
                                      console.log('🏷️ Changement de filtre libellé:', e.target.value);
                                      setSelectedLabelFilter(e.target.value);
                                    }}
                                    style={{
                                      padding: '6px 12px',
                                      border: '1px solid #d1d5db',
                                      borderRadius: '6px',
                                      fontSize: '15px',
                                      backgroundColor: 'white',
                                      cursor: 'pointer',
                                      minWidth: '140px'
                                    }}
                                  >
                                    <option value="all">Tous les libellés</option>
                                    {customLabels.map(label => (
                                      <option key={label} value={label}>
                                        {label}
                                      </option>
                                    ))}
                                  </select>
                                </div>
                              )}
                              
                              {/* Filtre par campagne */}
                              {(() => {
                                /**
                                 * Les campagnes qui ont servi au moins un produit, classées
                                 * par dépense décroissante.
                                 *
                                 * Dédoublonnage par une table et non par une chaîne
                                 * « id:nom » redécoupée sur le deux-points : un nom de
                                 * campagne qui en contient un — « [SHOP] : FR » — perdait
                                 * tout ce qui suivait, et le menu affichait un libellé
                                 * tronqué pour une campagne par ailleurs correcte.
                                 *
                                 * Le classement par dépense plutôt qu'alphabétique : sur un
                                 * compte qui en compte trente, celle qu'on vient filtrer est
                                 * presque toujours l'une des premières.
                                 */
                                const parCampagne = new Map();
                                (accountData.products || []).forEach(product => {
                                  (product.allCampaigns || []).forEach(campaign => {
                                    if (!campaign?.id) return;
                                    const cle = String(campaign.id);
                                    const entree = parCampagne.get(cle)
                                      || { id: cle, name: campaign.name || `Campagne ${cle}`, cost: 0 };
                                    entree.cost += campaign.cost || 0;
                                    parCampagne.set(cle, entree);
                                  });
                                });
                                const campaigns = [...parCampagne.values()]
                                  .sort((a, b) => (b.cost - a.cost) || a.name.localeCompare(b.name));
                                
                                return (
                                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                    
                                    {campaigns.length > 0 && (
                                      <select
                                      value={selectedShoppingCampaignFilter}
                                      onChange={(e) => {
                                        console.log('🎯 Changement de filtre campagne:', e.target.value);
                                        setSelectedShoppingCampaignFilter(e.target.value);
                                      }}
                                      style={{
                                        padding: '6px 12px',
                                        border: '1px solid #d1d5db',
                                        borderRadius: '6px',
                                        fontSize: '15px',
                                        backgroundColor: 'white',
                                        cursor: 'pointer',
                                        minWidth: '180px'
                                      }}
                                    >
                                      <option value="all">Toutes les campagnes</option>
                                      {campaigns.map(campaign => (
                                        <option key={campaign.id} value={campaign.id}>
                                          {campaign.name}
                                        </option>
                                      ))}
                                    </select>
                                    )}
                                  </div>
                                );
                              })()}

                              {/* Bouton de configuration des métriques */}
                              <button
                                onClick={() => setShowShoppingMetricsConfig(true)}
                                style={{
                                  padding: '6px 10px',
                                  border: '1px solid #d1d5db',
                                  borderRadius: '6px',
                                  backgroundColor: 'white',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px',
                                  height: '35px'
                                }}
                                title="Configurer les métriques du tableau"
                              >
                                <Settings size={16} />
                              </button>
                            </div>
                          );
                        })()}
                      </div>


                      {/* Tableau des produits */}
                      {accountData.products && accountData.products.length > 0 && (
                        <div style={{ 
                          marginTop: '24px', 
                          width: '100%',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px'
                        }}>

                          <div style={{ overflowX: 'auto', width: '100%' }}>
                            <table className="campaigns-table" style={{ width: '100%' }}>
                              <thead>
                                <tr>
                                  <th 
                                    style={{ textAlign: 'left', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleProductSort('title')}
                                  >
                                    Produit {productsSortConfig.field === 'title' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th 
                                    style={{ textAlign: 'center', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleProductSort('availability')}
                                  >
                                    Disponibilité {productsSortConfig.field === 'availability' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th 
                                    style={{ textAlign: 'center', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleProductSort('status')}
                                  >
                                    Statut {productsSortConfig.field === 'status' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th 
                                    style={{ textAlign: 'center', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                    onClick={() => handleProductSort('feedLabelOrCountry')}
                                  >
                                    Pays {productsSortConfig.field === 'feedLabelOrCountry' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  {isShoppingMetricVisible('impressions') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('impressions')}
                                    >
                                      Impressions {productsSortConfig.field === 'impressions' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('clics') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('clicks')}
                                    >
                                      Clics {productsSortConfig.field === 'clicks' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('ctr') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('ctr')}
                                    >
                                      CTR {productsSortConfig.field === 'ctr' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpc') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('cpc')}
                                    >
                                      CPC {productsSortConfig.field === 'cpc' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cout') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('cost')}
                                    >
                                      Coût {productsSortConfig.field === 'cost' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('taux_conv') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('conversionRate')}
                                    >
                                      Taux conv. {productsSortConfig.field === 'conversionRate' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('conv') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('conversions')}
                                    >
                                      Conv. {productsSortConfig.field === 'conversions' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpa') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('cpa')}
                                    >
                                      CPA {productsSortConfig.field === 'cpa' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('valeur_conv') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('conversionsValue')}
                                    >
                                      Valeur conv. {productsSortConfig.field === 'conversionsValue' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cos') && (
                                    <th
                                      style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }}
                                      onClick={() => handleProductSort('cos')}
                                    >
                                      COS {productsSortConfig.field === 'cos' ? (productsSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // Filtrer les produits par pays et libellé si des filtres sont appliqués
                                  console.log('🔍 Debug filtres:', {
                                    selectedCountryFilter,
                                    selectedLabelFilter,
                                    totalProducts: accountData.products.length,
                                    sampleProducts: accountData.products.slice(0, 3).map(p => ({
                                      feedLabelOrCountry: p.feedLabelOrCountry,
                                      targetCountry: p.targetCountry,
                                      customLabel: p.customAttributes?.find(attr => attr.name === 'custom_label_0')?.value || p.customLabel0 || p.customLabel,
                                      title: p.title
                                    }))
                                  });
                                  
                                  // Appliquer les filtres
                                  const filteredProducts = filterProducts(accountData.products);
                                  
                                  console.log('🔍 Produits après filtrage:', filteredProducts.length);
                                  
                                  // Trier les produits filtrés selon la configuration de tri
                                  const sortedProducts = [...filteredProducts].sort((a, b) => {
                                    const { field, direction } = productsSortConfig;
                                    let valueA, valueB;
                                    
                                    // Déterminer les valeurs à comparer selon le champ
                                    switch (field) {
                                      case 'title':
                                        valueA = (a.title || '').toLowerCase();
                                        valueB = (b.title || '').toLowerCase();
                                        break;
                                      case 'availability':
                                        valueA = a.availability || '';
                                        valueB = b.availability || '';
                                        break;
                                      case 'status':
                                        valueA = a.status || '';
                                        valueB = b.status || '';
                                        break;
                                      case 'feedLabelOrCountry':
                                        valueA = a.feedLabelOrCountry || a.targetCountry || '';
                                        valueB = b.feedLabelOrCountry || b.targetCountry || '';
                                        break;
                                      case 'ctr':
                                        valueA = a.impressions > 0 ? (a.clicks || 0) / a.impressions * 100 : 0;
                                        valueB = b.impressions > 0 ? (b.clicks || 0) / b.impressions * 100 : 0;
                                        break;
                                      case 'cpc':
                                        valueA = a.clicks > 0 ? (a.cost || 0) / a.clicks : 0;
                                        valueB = b.clicks > 0 ? (b.cost || 0) / b.clicks : 0;
                                        break;
                                      case 'conversionRate':
                                        valueA = a.clicks > 0 ? (a.conversions || 0) / a.clicks * 100 : 0;
                                        valueB = b.clicks > 0 ? (b.conversions || 0) / b.clicks * 100 : 0;
                                        break;
                                      case 'cpa':
                                        valueA = a.conversions > 0 ? (a.cost || 0) / a.conversions : 0;
                                        valueB = b.conversions > 0 ? (b.cost || 0) / b.conversions : 0;
                                        break;
                                      case 'cos':
                                        valueA = a.conversionsValue > 0 ? (a.cost || 0) / a.conversionsValue * 100 : 0;
                                        valueB = b.conversionsValue > 0 ? (b.cost || 0) / b.conversionsValue * 100 : 0;
                                        break;
                                      default:
                                        // Pour les champs numériques (impressions, clicks, cost, conversions, conversionsValue)
                                        valueA = a[field] || 0;
                                        valueB = b[field] || 0;
                                        break;
                                    }
                                    
                                    // Comparer les valeurs
                                    let comparison = 0;
                                    if (typeof valueA === 'string') {
                                      comparison = valueA.localeCompare(valueB);
                                    } else {
                                      comparison = valueA - valueB;
                                    }
                                    
                                    // Appliquer la direction du tri
                                    return direction === 'desc' ? -comparison : comparison;
                                  });
                                  
                                  const startIndex = (productsCurrentPage - 1) * productsItemsPerPage;
                                  const endIndex = startIndex + productsItemsPerPage;
                                  const paginatedProducts = sortedProducts.slice(startIndex, endIndex);
                                  
                                  // Fonction utilitaire pour interpréter le statut du produit
                                  const getProductStatus = (product) => {
                                    // Utiliser directement le champ 'status' retourné par l'API
                                    if (product.status) {
                                      switch (product.status) {
                                        case 'approved':
                                          return { status: 'Approuvé', color: '#059669', bgColor: '#d1fae5', icon: '✅' };
                                        case 'rejected':
                                        case 'disapproved':
                                          return { status: 'Refusé', color: '#dc2626', bgColor: '#fee2e2', icon: '❌' };
                                        case 'pending':
                                          return { status: 'En attente', color: '#d97706', bgColor: '#fef3c7', icon: '⏳' };
                                        case 'unknown':
                                          return { status: 'Inconnu', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
                                        default:
                                          return { status: 'Inconnu', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
                                      }
                                    }
                                    
                                    // Fallback: vérifier la nouvelle structure productStatus
                                    if (product.productStatus && product.productStatus.destinationStatuses) {
                                      const shoppingStatus = product.productStatus.destinationStatuses.find(
                                        status => status.destination === 'SHOPPING_ADS'
                                      );
                                      
                                      if (shoppingStatus) {
                                        switch (shoppingStatus.approvalStatus) {
                                          case 'APPROVED':
                                            return { status: 'Approuvé', color: '#059669', bgColor: '#d1fae5', icon: '✅' };
                                          case 'DISAPPROVED':
                                            return { status: 'Refusé', color: '#dc2626', bgColor: '#fee2e2', icon: '❌' };
                                          case 'PENDING':
                                            return { status: 'En attente', color: '#d97706', bgColor: '#fef3c7', icon: '⏳' };
                                          default:
                                            return { status: 'Inconnu', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
                                        }
                                      }
                                    }
                                    
                                    // Fallback: utiliser merchantStatus si disponible
                                    if (product.merchantStatus && product.merchantStatus !== 'no_data') {
                                      switch (product.merchantStatus) {
                                        case 'approved':
                                          return { status: 'Approuvé', color: '#059669', bgColor: '#d1fae5', icon: '✅' };
                                        case 'rejected':
                                        case 'disapproved':
                                          return { status: 'Refusé', color: '#dc2626', bgColor: '#fee2e2', icon: '❌' };
                                        case 'pending':
                                          return { status: 'En attente', color: '#d97706', bgColor: '#fef3c7', icon: '⏳' };
                                        default:
                                          return { status: 'Inconnu', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
                                      }
                                    }
                                    
                                    return { status: 'Retiré du flux', color: '#6b7280', bgColor: '#f3f4f6', icon: '❓' };
                                  };
                                  
                                  return paginatedProducts.map((product, index) => (
                                  <tr key={`${product.itemId}-${index}`} style={{ 
                                    borderBottom: '1px solid #f3f4f6',
                                    backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                                  }}>
                                    <td style={{ padding: '12px 8px', maxWidth: '350px', position: 'relative' }}>
                                      <div
                                        style={{ display: 'flex', alignItems: 'flex-start', gap: '12px' }}
                                      >
                                        {/* Vignette produit */}
                                        <div style={{
                                          width: '48px',
                                          height: '48px',
                                          borderRadius: '6px',
                                          border: '1px solid #e5e7eb',
                                          backgroundColor: '#f9fafb',
                                          flexShrink: 0,
                                          overflow: 'hidden'
                                        }}>
                                          {product.imageLink && (
                                            <img
                                              src={adresseDeVignette(product)}
                                              alt=""
                                              loading="lazy"
                                              decoding="async"
                                              fetchPriority="low"
                                              style={{
                                                width: '100%',
                                                height: '100%',
                                                objectFit: 'contain',
                                                backgroundColor: '#fff'
                                              }}
                                              onError={(e) => {
                                                e.target.style.display = 'none';
                                              }}
                                            />
                                          )}
                                        </div>
                                        <div style={{ flex: 1, minWidth: 0 }}>
                                          <div
                                            className="product-title-container"
                                            onClick={() => openProductModal(product)}
                                            style={{
                                              color: '#333333',
                                              textDecoration: 'none',
                                              cursor: 'pointer',
                                              position: 'relative',
                                              fontWeight: '500',
                                              lineHeight: '1.4',
                                              wordBreak: 'break-word'
                                            }}
                                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                                          >
                                            {product.title || `Produit ${product.itemId}`}
                                          </div>
                                          <div style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                                            ID: {product.itemId}
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                                      <span style={{
                                        fontSize: '12px',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        backgroundColor: (() => {
                                          const av = (product.availability || '').toLowerCase().replace(/[_\s]/g, '');
                                          if (av === 'instock') return '#d1fae5';
                                          if (av === 'outofstock') return '#fee2e2';
                                          if (av === 'preorder') return '#fef3c7';
                                          if (av === 'backorder') return '#dbeafe';
                                          if (!product.availability || product.availability === 'no_data') return '#e5e7eb';
                                          return '#f3f4f6';
                                        })(),
                                        color: (() => {
                                          const av = (product.availability || '').toLowerCase().replace(/[_\s]/g, '');
                                          if (av === 'instock') return '#065f46';
                                          if (av === 'outofstock') return '#dc2626';
                                          if (av === 'preorder') return '#92400e';
                                          if (av === 'backorder') return '#1d4ed8';
                                          if (!product.availability || product.availability === 'no_data') return '#6b7280';
                                          return '#374151';
                                        })()
                                      }}>
                                        {(() => {
                                          const av = (product.availability || '').toLowerCase().replace(/[_\s]/g, '');
                                          if (av === 'instock') return '📦 En stock';
                                          if (av === 'outofstock') return '❌ Rupture';
                                          if (av === 'preorder') return '⏳ Précommande';
                                          if (av === 'backorder') return '🔄 Réappro.';
                                          if (!product.availability || product.availability === 'no_data') return 'Retiré du flux';
                                          return product.availability || '❓ Inconnu';
                                        })()}
                                      </span>
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                                      {(() => {
                                        const statusInfo = getProductStatus(product);
                                        return (
                                          <span style={{
                                            fontSize: '12px',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            backgroundColor: statusInfo.bgColor,
                                            color: statusInfo.color
                                          }}>
                                            {statusInfo.icon} {statusInfo.status}
                                          </span>
                                        );
                                      })()}
                                    </td>
                                    <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                                      <span style={{
                                        fontSize: '12px',
                                        padding: '2px 6px',
                                        borderRadius: '4px',
                                        backgroundColor: '#f3f4f6',
                                        color: '#374151'
                                      }}>
                                        {product.feedLabelOrCountry || 'FR'}
                                      </span>
                                    </td>
                                    {isShoppingMetricVisible('impressions') && (
                                      <td className="numeric">
                                        {formatNumber(product.impressions || 0)}
                                        {pmaxCompareEnabled && product.variations && renderVariation(product.variations.impressions)}
                                      </td>
                                    )}
                                    {isShoppingMetricVisible('clics') && (
                                      <td className="numeric">
                                        {formatNumber(product.clicks || 0)}
                                        {pmaxCompareEnabled && product.variations && renderVariation(product.variations.clicks)}
                                      </td>
                                    )}
                                    {isShoppingMetricVisible('ctr') && (
                                      <td className="numeric">
                                        {formatNumber(product.ctr || 0, 2)}%
                                        {pmaxCompareEnabled && product.variations && renderVariation(
                                          product.previousPeriod ?
                                            ((product.ctr || 0) - ((product.previousPeriod.clicks || 0) / (product.previousPeriod.impressions || 1) * 100)) /
                                            ((product.previousPeriod.clicks || 0) / (product.previousPeriod.impressions || 1) * 100 || 1) * 100 : null
                                        )}
                                      </td>
                                    )}
                                    {isShoppingMetricVisible('cpc') && (
                                      <td className="numeric">
                                        €{formatNumber(product.cpc || 0, 2)}
                                        {pmaxCompareEnabled && product.variations && renderVariation(
                                          product.previousPeriod && product.previousPeriod.clicks > 0 ?
                                            ((product.cpc || 0) - (product.previousPeriod.cost / product.previousPeriod.clicks)) /
                                            ((product.previousPeriod.cost / product.previousPeriod.clicks) || 1) * 100 : null,
                                          true // CPC inversé : baisse = positif
                                        )}
                                      </td>
                                    )}
                                    {isShoppingMetricVisible('cout') && (
                                      <td className="numeric">
                                        €{formatNumber(product.cost || 0, 2)}
                                        {pmaxCompareEnabled && product.variations && renderVariation(product.variations.cost)}
                                      </td>
                                    )}
                                    {isShoppingMetricVisible('taux_conv') && (
                                      <td className="numeric">
                                        {formatNumber(((product.conversions || 0, 1) / (product.clicks || 1)) * 100, 2)}%
                                        {pmaxCompareEnabled && product.variations && product.previousPeriod && renderVariation(
                                          ((product.conversions || 0) / (product.clicks || 1) * 100) > 0 && product.previousPeriod.clicks > 0 ?
                                            (((product.conversions / product.clicks * 100) - (product.previousPeriod.conversions / product.previousPeriod.clicks * 100)) /
                                            (product.previousPeriod.conversions / product.previousPeriod.clicks * 100 || 1) * 100) : null
                                        )}
                                      </td>
                                    )}
                                    {isShoppingMetricVisible('conv') && (
                                      <td className="numeric">
                                        {formatNumber(product.conversions || 0, 1)}
                                        {pmaxCompareEnabled && product.variations && renderVariation(product.variations.conversions)}
                                      </td>
                                    )}
                                    {isShoppingMetricVisible('cpa') && (
                                      <td className={`numeric ${(() => {
                                        const cpa = ((product.conversions || 0) > 0 && (product.cost || 0) > 0) ? (product.cost || 0) / (product.conversions || 1) : 0;
                                        return getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns');
                                      })()}`}>
                                        {((product.conversions || 0) > 0 && (product.cost || 0) > 0) ? `€${formatNumber((product.cost || 0) / (product.conversions || 1), 2)}` : `€${formatNumber(0, 2)}`}
                                        {pmaxCompareEnabled && product.variations && product.previousPeriod && renderVariation(calculateVariation(product.cost / product.conversions, product.previousPeriod.cost / product.previousPeriod.conversions), true)}
                                      </td>
                                    )}
                                    {isShoppingMetricVisible('valeur_conv') && (
                                      <td className="numeric">
                                        €{formatNumber(product.conversionsValue || 0, 2)}
                                        {pmaxCompareEnabled && product.variations && renderVariation(product.variations.conversionsValue)}
                                      </td>
                                    )}
                                    {isShoppingMetricVisible('cos') && (
                                      <td className={`numeric ${calculateCosClassName(product.cost, product.conversionsValue, selectedAccount?.id || 'global')}`}>
                                        {((product.conversionsValue || 0) > 0 && (product.cost || 0) > 0) ? `${formatNumber(((product.cost || 0) / (product.conversionsValue || 1)) * 100, 1)}%` : `${formatNumber(0, 1)}%`}
                                        {pmaxCompareEnabled && product.variations && product.previousPeriod && renderVariation(calculateVariation(product.cost / product.conversionsValue * 100, product.previousPeriod.cost / product.previousPeriod.conversionsValue * 100), true)}
                                      </td>
                                    )}
                                  </tr>
                                  ))
                                })()}
                              </tbody>
                              <tfoot>
                                {(() => {
                                  // Filtrer les produits selon les filtres appliqués puis calculer les totaux
                                  const filteredProducts = filterProducts(accountData.products);
                                  
                                  const sortedProducts = [...filteredProducts].sort((a, b) => {
                                    return (b.cost || 0) - (a.cost || 0);
                                  });
                                  
                                  const totals = sortedProducts.reduce((acc, product) => ({
                                    impressions: acc.impressions + (product.impressions || 0),
                                    clicks: acc.clicks + (product.clicks || 0),
                                    cost: acc.cost + (product.cost || 0),
                                    conversions: acc.conversions + (product.conversions || 0),
                                    conversionsValue: acc.conversionsValue + (product.conversionsValue || 0)
                                  }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

                                  const avgCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
                                  const avgCpc = totals.clicks > 0 ? totals.cost / totals.clicks : 0;
                                  const avgConversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0;
                                  const avgCpa = totals.conversions > 0 ? totals.cost / totals.conversions : 0;
                                  const avgCos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue) * 100 : 0;

                                  return (
                                    <tr style={{ 
                                      backgroundColor: '#f8f9fa',
                                      borderTop: '2px solid #e5e7eb',
                                      fontWeight: '600'
                                    }}>
                                      <td style={{ padding: '12px 8px' }}>
                                        <strong>TOTAL ({sortedProducts.length} produit{sortedProducts.length !== 1 ? 's' : ''})</strong>
                                      </td>
                                      <td style={{ textAlign: 'center', padding: '12px 8px' }}>-</td>
                                      <td style={{ textAlign: 'center', padding: '12px 8px' }}>-</td>
                                      <td style={{ textAlign: 'center', padding: '12px 8px' }}>-</td>
                                      {isShoppingMetricVisible('impressions') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(totals.impressions)}</td>
                                      )}
                                      {isShoppingMetricVisible('clics') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(totals.clicks)}</td>
                                      )}
                                      {isShoppingMetricVisible('ctr') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(avgCtr, 2)}%</td>
                                      )}
                                      {isShoppingMetricVisible('cpc') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>€{formatNumber(avgCpc, 2)}</td>
                                      )}
                                      {isShoppingMetricVisible('cout') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>€{formatNumber(totals.cost, 2)}</td>
                                      )}
                                      {isShoppingMetricVisible('taux_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(avgConversionRate, 2)}%</td>
                                      )}
                                      {isShoppingMetricVisible('conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(totals.conversions, 1)}</td>
                                      )}
                                      {isShoppingMetricVisible('cpa') && (
                                        <td className={`numeric ${getCpaClassName(avgCpa, selectedAccount?.id || 'global', 'campaigns')}`} style={{ padding: '12px 8px' }}>{totals.conversions >= 0 ? `€${formatNumber(avgCpa, 2)}` : '-'}</td>
                                      )}
                                      {isShoppingMetricVisible('valeur_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>€{formatNumber(totals.conversionsValue, 2)}</td>
                                      )}
                                      {isShoppingMetricVisible('cos') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>{totals.conversionsValue >= 0 ? `${formatNumber(avgCos, 1)}%` : '-'}</td>
                                      )}
                                    </tr>
                                  );
                                })()}
                              </tfoot>
                            </table>
                          </div>

                          {/* Contrôles de pagination */}
                          {(() => {
                            // Calculer avec les données filtrées (utiliser la même fonction que pour l'affichage)
                            const filteredProducts = filterProducts(accountData.products);
                            const totalProducts = filteredProducts.length;
                            const totalPages = Math.ceil(totalProducts / productsItemsPerPage);
                            const startIndex = (productsCurrentPage - 1) * productsItemsPerPage;
                            const endIndex = Math.min(startIndex + productsItemsPerPage, totalProducts);
                            
                            return totalPages > 1 && (
                              <div className="pagination-controls">
                                <div className="pagination-info">
                                  <span>
                                    Affichage {startIndex + 1}-{endIndex} sur {totalProducts} produits
                                  </span>
                                </div>
                                <div className="pagination-buttons">
                                  <button 
                                    onClick={() => setProductsCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={productsCurrentPage === 1}
                                    className="pagination-btn"
                                  >
                                    ← Précédent
                                  </button>
                                  <span className="page-numbers">
                                    Page {productsCurrentPage} sur {totalPages}
                                  </span>
                                  <button 
                                    onClick={() => setProductsCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                    disabled={productsCurrentPage === totalPages}
                                    className="pagination-btn"
                                  >
                                    Suivant →
                                  </button>
                                </div>
                              </div>
                            );
                          })()} 
                        </div>
                      )}

                      {/* Tableau des performances par statut */}
                      {accountData.products && accountData.products.length > 0 && (
                        <div style={{ 
                          marginTop: '32px', 
                          width: '100%',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px'
                        }}>
                          
                          <div style={{ 
                            padding: '20px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            <h3 style={{ 
                              fontSize: '20px', 
                              fontWeight: '700', 
                              color: '#1f2937', 
                              margin: '0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <BarChart2 size={18} /> Performances par statut
                            </h3>
                          </div>

                          <div style={{ overflowX: 'auto', width: '100%' }}>
                            <table className="campaigns-table" style={{ width: '100%' }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('status')}>
                                    État {statusSortConfig.field === 'status' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'center', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('country')}>
                                    Pays {statusSortConfig.field === 'country' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('count')}>
                                    Produits {statusSortConfig.field === 'count' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  {isShoppingMetricVisible('impressions') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('impressions')}>
                                      Impressions {statusSortConfig.field === 'impressions' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('clics') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('clicks')}>
                                      Clics {statusSortConfig.field === 'clicks' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('ctr') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('ctr')}>
                                      CTR {statusSortConfig.field === 'ctr' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpc') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('cpc')}>
                                      CPC {statusSortConfig.field === 'cpc' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cout') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('cost')}>
                                      Coût {statusSortConfig.field === 'cost' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('taux_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('conversionRate')}>
                                      Taux conv. {statusSortConfig.field === 'conversionRate' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('conversions')}>
                                      Conv. {statusSortConfig.field === 'conversions' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpa') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('cpa')}>
                                      CPA {statusSortConfig.field === 'cpa' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('valeur_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('conversionsValue')}>
                                      Valeur conv. {statusSortConfig.field === 'conversionsValue' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cos') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleStatusSort('cos')}>
                                      COS {statusSortConfig.field === 'cos' ? (statusSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // Filtrer les produits selon les filtres appliqués
                                  const filteredProducts = filterProducts(accountData.products);

                                  // Fonction pour obtenir le statut d'un produit
                                  const getProductStatus = (product) => {
                                    if (product.status) {
                                      switch (product.status) {
                                        case 'approved': return 'Approuvé';
                                        case 'rejected':
                                        case 'disapproved': return 'Refusé';
                                        case 'pending': return 'En attente';
                                        case 'unknown': return 'Inconnu';
                                        default: return 'Inconnu';
                                      }
                                    }
                                    
                                    if (product.productStatus?.destinationStatuses) {
                                      const shoppingStatus = product.productStatus.destinationStatuses.find(
                                        status => status.destination === 'SHOPPING_ADS'
                                      );
                                      
                                      if (shoppingStatus) {
                                        switch (shoppingStatus.approvalStatus) {
                                          case 'APPROVED': return 'Approuvé';
                                          case 'DISAPPROVED': return 'Refusé';
                                          case 'PENDING': return 'En attente';
                                          default: return 'Inconnu';
                                        }
                                      }
                                    }
                                    
                                    if (product.merchantStatus && product.merchantStatus !== 'no_data') {
                                      switch (product.merchantStatus) {
                                        case 'approved': return 'Approuvé';
                                        case 'rejected':
                                        case 'disapproved': return 'Refusé';
                                        case 'pending': return 'En attente';
                                        default: return 'Inconnu';
                                      }
                                    }
                                    
                                    return 'Retiré du flux';
                                  };

                                  // Fonction pour obtenir l'icône et la couleur du statut
                                  const getStatusInfo = (status) => {
                                    switch (status) {
                                      case 'Approuvé':
                                        return { icon: '✅', color: '#059669', bgColor: '#d1fae5' };
                                      case 'Refusé':
                                        return { icon: '❌', color: '#dc2626', bgColor: '#fee2e2' };
                                      case 'En attente':
                                        return { icon: '⏳', color: '#d97706', bgColor: '#fef3c7' };
                                      case 'Inconnu':
                                      case 'Retiré du flux':
                                      default:
                                        return { icon: '❓', color: '#6b7280', bgColor: '#f3f4f6' };
                                    }
                                  };
                                  
                                  // Agrégation par statut et pays (avec données de comparaison)
                                  const statusCountryAggregation = {};

                                  filteredProducts.forEach(product => {
                                    const status = getProductStatus(product);
                                    const country = product.feedLabelOrCountry || product.targetCountry || 'FR';
                                    const key = `${status}|${country}`;

                                    if (!statusCountryAggregation[key]) {
                                      statusCountryAggregation[key] = {
                                        status,
                                        country,
                                        count: 0,
                                        impressions: 0,
                                        clicks: 0,
                                        cost: 0,
                                        conversions: 0,
                                        conversionsValue: 0,
                                        // Données période précédente pour comparaison
                                        prevImpressions: 0,
                                        prevClicks: 0,
                                        prevCost: 0,
                                        prevConversions: 0,
                                        prevConversionsValue: 0
                                      };
                                    }

                                    const data = statusCountryAggregation[key];
                                    data.count += 1;
                                    data.impressions += product.impressions || 0;
                                    data.clicks += product.clicks || 0;
                                    data.cost += product.cost || 0;
                                    data.conversions += product.conversions || 0;
                                    data.conversionsValue += product.conversionsValue || 0;

                                    // Agréger aussi les données de la période précédente si disponibles
                                    if (product.previousPeriod) {
                                      data.prevImpressions += product.previousPeriod.impressions || 0;
                                      data.prevClicks += product.previousPeriod.clicks || 0;
                                      data.prevCost += product.previousPeriod.cost || 0;
                                      data.prevConversions += product.previousPeriod.conversions || 0;
                                      data.prevConversionsValue += product.previousPeriod.conversionsValue || 0;
                                    }
                                  });
                                  
                                  // Convertir en tableau et trier selon la config
                                  const sortedStatusData = sortAggregatedData(
                                    Object.values(statusCountryAggregation),
                                    statusSortConfig
                                  );
                                  
                                  return sortedStatusData.map((data, index) => {
                                    const statusInfo = getStatusInfo(data.status);
                                    const ctr = data.impressions > 0 ? (data.clicks / data.impressions * 100) : 0;
                                    const cpc = data.clicks > 0 ? (data.cost / data.clicks) : 0;
                                    const conversionRate = data.clicks > 0 ? (data.conversions / data.clicks * 100) : 0;
                                    const cpa = data.conversions > 0 ? (data.cost / data.conversions) : 0;
                                    const cos = data.conversionsValue > 0 ? (data.cost / data.conversionsValue * 100) : 0;
                                    
                                    return (
                                      <tr key={`${data.status}-${data.country}-${index}`} style={{ 
                                        borderBottom: '1px solid #f3f4f6',
                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                                      }}>
                                        <td style={{ padding: '12px 8px' }}>
                                          <span style={{
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: statusInfo.bgColor,
                                            color: statusInfo.color,
                                            fontWeight: '500'
                                          }}>
                                            {statusInfo.icon} {data.status}
                                          </span>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                                          <span style={{
                                            fontSize: '12px',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            backgroundColor: '#f3f4f6',
                                            color: '#374151'
                                          }}>
                                            {data.country}
                                          </span>
                                        </td>
                                        <td className="numeric">{formatNumber(data.count)}</td>
                                        {isShoppingMetricVisible('impressions') && (
                                          <td className="numeric">
                                            {formatNumber(data.impressions)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.impressions, data.prevImpressions))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('clics') && (
                                          <td className="numeric">
                                            {formatNumber(data.clicks)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.clicks, data.prevClicks))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('ctr') && (
                                          <td className="numeric">
                                            {formatNumber(ctr, 2)}%
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(ctr, data.prevClicks / data.prevImpressions * 100))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cpc') && (
                                          <td className="numeric">
                                            €{formatNumber(cpc, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpc, data.prevCost / data.prevClicks), true)}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cout') && (
                                          <td className="numeric">
                                            €{formatNumber(data.cost, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.cost, data.prevCost))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('taux_conv') && (
                                          <td className="numeric">
                                            {formatNumber(conversionRate, 2)}%
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(conversionRate, data.prevConversions / data.prevClicks * 100))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('conv') && (
                                          <td className="numeric">
                                            {formatNumber(data.conversions, 1)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.conversions, data.prevConversions))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cpa') && (
                                          <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns')}`}>
                                            {data.conversions >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpa, data.prevCost / data.prevConversions), true)}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('valeur_conv') && (
                                          <td className="numeric">
                                            €{formatNumber(data.conversionsValue, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.conversionsValue, data.prevConversionsValue))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cos') && (
                                          <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                                            {data.conversionsValue >= 0 && data.cost >= 0 ? `${formatNumber(cos, 1)}%` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cos, data.prevCost / data.prevConversionsValue * 100), true)}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                              <tfoot>
                                {(() => {
                                  // Calculer les totaux pour tous les statuts filtrés
                                  const filteredProducts = filterProducts(accountData.products);

                                  const totals = filteredProducts.reduce((acc, product) => ({
                                    count: acc.count + 1,
                                    impressions: acc.impressions + (product.impressions || 0),
                                    clicks: acc.clicks + (product.clicks || 0),
                                    cost: acc.cost + (product.cost || 0),
                                    conversions: acc.conversions + (product.conversions || 0),
                                    conversionsValue: acc.conversionsValue + (product.conversionsValue || 0),
                                    // Totaux période précédente
                                    prevImpressions: acc.prevImpressions + (product.previousPeriod?.impressions || 0),
                                    prevClicks: acc.prevClicks + (product.previousPeriod?.clicks || 0),
                                    prevCost: acc.prevCost + (product.previousPeriod?.cost || 0),
                                    prevConversions: acc.prevConversions + (product.previousPeriod?.conversions || 0),
                                    prevConversionsValue: acc.prevConversionsValue + (product.previousPeriod?.conversionsValue || 0)
                                  }), { count: 0, impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, prevImpressions: 0, prevClicks: 0, prevCost: 0, prevConversions: 0, prevConversionsValue: 0 });

                                  const avgCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
                                  const avgCpc = totals.clicks > 0 ? totals.cost / totals.clicks : 0;
                                  const avgConversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0;
                                  const avgCpa = totals.conversions > 0 ? totals.cost / totals.conversions : 0;
                                  const avgCos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue) * 100 : 0;

                                  // Métriques période précédente
                                  const prevCtr = totals.prevImpressions > 0 ? (totals.prevClicks / totals.prevImpressions) * 100 : 0;
                                  const prevCpc = totals.prevClicks > 0 ? totals.prevCost / totals.prevClicks : 0;
                                  const prevConversionRate = totals.prevClicks > 0 ? (totals.prevConversions / totals.prevClicks) * 100 : 0;
                                  const prevCpa = totals.prevConversions > 0 ? totals.prevCost / totals.prevConversions : 0;
                                  const prevCos = totals.prevConversionsValue > 0 ? (totals.prevCost / totals.prevConversionsValue) * 100 : 0;

                                  return (
                                    <tr style={{
                                      backgroundColor: '#f8f9fa',
                                      borderTop: '2px solid #e5e7eb',
                                      fontWeight: '600'
                                    }}>
                                      <td style={{ padding: '12px 8px' }}>
                                        <strong>TOTAL</strong>
                                      </td>
                                      <td style={{ textAlign: 'center', padding: '12px 8px' }}>-</td>
                                      <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(totals.count)}</td>
                                      {isShoppingMetricVisible('impressions') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.impressions)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.impressions, totals.prevImpressions))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('clics') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.clicks)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.clicks, totals.prevClicks))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('ctr') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(avgCtr, 2)}%
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCtr, prevCtr))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cpc') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(avgCpc, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpc, prevCpc), true)}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cout') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(totals.cost, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.cost, totals.prevCost))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('taux_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(avgConversionRate, 2)}%
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgConversionRate, prevConversionRate))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.conversions, 1)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversions, totals.prevConversions))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cpa') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {totals.conversions >= 0 ? `€${formatNumber(avgCpa, 2)}` : '-'}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpa, prevCpa), true)}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('valeur_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(totals.conversionsValue, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversionsValue, totals.prevConversionsValue))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cos') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {totals.conversionsValue >= 0 ? `${formatNumber(avgCos, 1)}%` : '-'}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCos, prevCos), true)}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })()}
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Tableau des performances par disponibilité */}
                      {accountData.products && accountData.products.length > 0 && (
                        <div style={{ 
                          marginTop: '32px', 
                          width: '100%',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px'
                        }}>
                          
                          <div style={{ 
                            padding: '20px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            <h3 style={{ 
                              fontSize: '20px', 
                              fontWeight: '700', 
                              color: '#1f2937', 
                              margin: '0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <Package size={18} /> Performances par disponibilité
                            </h3>
                          </div>

                          <div style={{ overflowX: 'auto', width: '100%' }}>
                            <table className="campaigns-table" key={selectedMetrics.join(',')} style={{ width: '100%', tableLayout: 'auto' }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('availability')}>
                                    Disponibilité {availabilitySortConfig.field === 'availability' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'center', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('country')}>
                                    Pays {availabilitySortConfig.field === 'country' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('count')}>
                                    Produits {availabilitySortConfig.field === 'count' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  {isShoppingMetricVisible('impressions') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('impressions')}>
                                      Impressions {availabilitySortConfig.field === 'impressions' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('clics') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('clicks')}>
                                      Clics {availabilitySortConfig.field === 'clicks' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('ctr') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('ctr')}>
                                      CTR {availabilitySortConfig.field === 'ctr' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpc') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('cpc')}>
                                      CPC {availabilitySortConfig.field === 'cpc' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cout') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('cost')}>
                                      Coût {availabilitySortConfig.field === 'cost' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('taux_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('conversionRate')}>
                                      Taux conv. {availabilitySortConfig.field === 'conversionRate' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('conversions')}>
                                      Conv. {availabilitySortConfig.field === 'conversions' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpa') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('cpa')}>
                                      CPA {availabilitySortConfig.field === 'cpa' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('valeur_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('conversionsValue')}>
                                      Valeur conv. {availabilitySortConfig.field === 'conversionsValue' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cos') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleAvailabilitySort('cos')}>
                                      COS {availabilitySortConfig.field === 'cos' ? (availabilitySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // Filtrer les produits selon les filtres appliqués
                                  const filteredProducts = filterProducts(accountData.products);

                                  // Fonction pour obtenir la disponibilité d'un produit (normalise tous les formats)
                                  const getProductAvailability = (product) => {
                                    if (!product.availability || product.availability === 'no_data') {
                                      return 'Retiré du flux';
                                    }
                                    // Normaliser: minuscules, sans espaces ni underscores
                                    const av = product.availability.toLowerCase().replace(/[_\s]/g, '');
                                    switch (av) {
                                      case 'instock':
                                        return 'En stock';
                                      case 'outofstock':
                                        return 'En rupture';
                                      case 'preorder':
                                        return 'Précommande';
                                      case 'backorder':
                                        return 'En réapprovisionnement';
                                      default:
                                        return product.availability;
                                    }
                                  };

                                  // Fonction pour obtenir l'icône et la couleur de la disponibilité
                                  const getAvailabilityInfo = (availability) => {
                                    switch (availability) {
                                      case 'En stock':
                                        return { icon: '📦', color: '#065f46', bgColor: '#d1fae5' };
                                      case 'En rupture':
                                        return { icon: '❌', color: '#dc2626', bgColor: '#fee2e2' };
                                      case 'Précommande':
                                        return { icon: '⏳', color: '#92400e', bgColor: '#fef3c7' };
                                      case 'En réapprovisionnement':
                                        return { icon: '🔄', color: '#1d4ed8', bgColor: '#dbeafe' };
                                      case 'Retiré du flux':
                                      default:
                                        return { icon: '🔗', color: '#6b7280', bgColor: '#e5e7eb' };
                                    }
                                  };
                                  
                                  // Agrégation par disponibilité et pays (avec données de comparaison)
                                  const availabilityCountryAggregation = {};

                                  filteredProducts.forEach(product => {
                                    const availability = getProductAvailability(product);
                                    const country = product.feedLabelOrCountry || product.targetCountry || 'FR';
                                    const key = `${availability}|${country}`;

                                    if (!availabilityCountryAggregation[key]) {
                                      availabilityCountryAggregation[key] = {
                                        availability,
                                        country,
                                        count: 0,
                                        impressions: 0,
                                        clicks: 0,
                                        cost: 0,
                                        conversions: 0,
                                        conversionsValue: 0,
                                        prevImpressions: 0,
                                        prevClicks: 0,
                                        prevCost: 0,
                                        prevConversions: 0,
                                        prevConversionsValue: 0
                                      };
                                    }

                                    const data = availabilityCountryAggregation[key];
                                    data.count += 1;
                                    data.impressions += product.impressions || 0;
                                    data.clicks += product.clicks || 0;
                                    data.cost += product.cost || 0;
                                    data.conversions += product.conversions || 0;
                                    data.conversionsValue += product.conversionsValue || 0;

                                    if (product.previousPeriod) {
                                      data.prevImpressions += product.previousPeriod.impressions || 0;
                                      data.prevClicks += product.previousPeriod.clicks || 0;
                                      data.prevCost += product.previousPeriod.cost || 0;
                                      data.prevConversions += product.previousPeriod.conversions || 0;
                                      data.prevConversionsValue += product.previousPeriod.conversionsValue || 0;
                                    }
                                  });
                                  
                                  // Convertir en tableau et trier selon la config
                                  const sortedAvailabilityData = sortAggregatedData(
                                    Object.values(availabilityCountryAggregation),
                                    availabilitySortConfig
                                  );
                                  
                                  return sortedAvailabilityData.map((data, index) => {
                                    const availabilityInfo = getAvailabilityInfo(data.availability);
                                    const ctr = data.impressions > 0 ? (data.clicks / data.impressions * 100) : 0;
                                    const cpc = data.clicks > 0 ? (data.cost / data.clicks) : 0;
                                    const conversionRate = data.clicks > 0 ? (data.conversions / data.clicks * 100) : 0;
                                    const cpa = data.conversions > 0 ? (data.cost / data.conversions) : 0;
                                    const cos = data.conversionsValue > 0 ? (data.cost / data.conversionsValue * 100) : 0;
                                    
                                    return (
                                      <tr key={`${data.availability}-${data.country}-${index}`} style={{ 
                                        borderBottom: '1px solid #f3f4f6',
                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                                      }}>
                                        <td style={{ padding: '12px 8px' }}>
                                          <span style={{
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: availabilityInfo.bgColor,
                                            color: availabilityInfo.color,
                                            fontWeight: '500'
                                          }}>
                                            {availabilityInfo.icon} {data.availability}
                                          </span>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                                          <span style={{
                                            fontSize: '12px',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            backgroundColor: '#f3f4f6',
                                            color: '#374151'
                                          }}>
                                            {data.country}
                                          </span>
                                        </td>
                                        <td className="numeric">{formatNumber(data.count)}</td>
                                        {isShoppingMetricVisible('impressions') && (
                                          <td className="numeric">
                                            {formatNumber(data.impressions)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.impressions, data.prevImpressions))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('clics') && (
                                          <td className="numeric">
                                            {formatNumber(data.clicks)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.clicks, data.prevClicks))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('ctr') && (
                                          <td className="numeric">
                                            {formatNumber(ctr, 2)}%
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(ctr, data.prevClicks / data.prevImpressions * 100))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cpc') && (
                                          <td className="numeric">
                                            €{formatNumber(cpc, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpc, data.prevCost / data.prevClicks), true)}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cout') && (
                                          <td className="numeric">
                                            €{formatNumber(data.cost, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.cost, data.prevCost))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('taux_conv') && (
                                          <td className="numeric">
                                            {formatNumber(conversionRate, 2)}%
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(conversionRate, data.prevConversions / data.prevClicks * 100))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('conv') && (
                                          <td className="numeric">
                                            {formatNumber(data.conversions, 1)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.conversions, data.prevConversions))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cpa') && (
                                          <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns')}`}>
                                            {data.conversions >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpa, data.prevCost / data.prevConversions), true)}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('valeur_conv') && (
                                          <td className="numeric">
                                            €{formatNumber(data.conversionsValue, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.conversionsValue, data.prevConversionsValue))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cos') && (
                                          <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                                            {data.conversionsValue >= 0 && data.cost >= 0 ? `${formatNumber(cos, 1)}%` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cos, data.prevCost / data.prevConversionsValue * 100), true)}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                              <tfoot>
                                {(() => {
                                  // Calculer les totaux pour toutes les disponibilités filtrées
                                  const filteredProducts = filterProducts(accountData.products);

                                  const totals = filteredProducts.reduce((acc, product) => ({
                                    count: acc.count + 1,
                                    impressions: acc.impressions + (product.impressions || 0),
                                    clicks: acc.clicks + (product.clicks || 0),
                                    cost: acc.cost + (product.cost || 0),
                                    conversions: acc.conversions + (product.conversions || 0),
                                    conversionsValue: acc.conversionsValue + (product.conversionsValue || 0),
                                    // Totaux période précédente
                                    prevImpressions: acc.prevImpressions + (product.previousPeriod?.impressions || 0),
                                    prevClicks: acc.prevClicks + (product.previousPeriod?.clicks || 0),
                                    prevCost: acc.prevCost + (product.previousPeriod?.cost || 0),
                                    prevConversions: acc.prevConversions + (product.previousPeriod?.conversions || 0),
                                    prevConversionsValue: acc.prevConversionsValue + (product.previousPeriod?.conversionsValue || 0)
                                  }), { count: 0, impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, prevImpressions: 0, prevClicks: 0, prevCost: 0, prevConversions: 0, prevConversionsValue: 0 });

                                  const avgCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
                                  const avgCpc = totals.clicks > 0 ? totals.cost / totals.clicks : 0;
                                  const avgConversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0;
                                  const avgCpa = totals.conversions > 0 ? totals.cost / totals.conversions : 0;
                                  const avgCos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue) * 100 : 0;

                                  // Métriques période précédente
                                  const prevCtr = totals.prevImpressions > 0 ? (totals.prevClicks / totals.prevImpressions) * 100 : 0;
                                  const prevCpc = totals.prevClicks > 0 ? totals.prevCost / totals.prevClicks : 0;
                                  const prevConversionRate = totals.prevClicks > 0 ? (totals.prevConversions / totals.prevClicks) * 100 : 0;
                                  const prevCpa = totals.prevConversions > 0 ? totals.prevCost / totals.prevConversions : 0;
                                  const prevCos = totals.prevConversionsValue > 0 ? (totals.prevCost / totals.prevConversionsValue) * 100 : 0;

                                  return (
                                    <tr style={{
                                      backgroundColor: '#f8f9fa',
                                      borderTop: '2px solid #e5e7eb',
                                      fontWeight: '600'
                                    }}>
                                      <td style={{ padding: '12px 8px' }}>
                                        <strong>TOTAL</strong>
                                      </td>
                                      <td style={{ textAlign: 'center', padding: '12px 8px' }}>-</td>
                                      <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(totals.count)}</td>
                                      {isShoppingMetricVisible('impressions') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.impressions)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.impressions, totals.prevImpressions))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('clics') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.clicks)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.clicks, totals.prevClicks))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('ctr') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(avgCtr, 2)}%
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCtr, prevCtr))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cpc') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(avgCpc, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpc, prevCpc), true)}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cout') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(totals.cost, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.cost, totals.prevCost))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('taux_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(avgConversionRate, 2)}%
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgConversionRate, prevConversionRate))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.conversions, 1)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversions, totals.prevConversions))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cpa') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {totals.conversions >= 0 ? `€${formatNumber(avgCpa, 2)}` : '-'}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpa, prevCpa), true)}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('valeur_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(totals.conversionsValue, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversionsValue, totals.prevConversionsValue))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cos') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {totals.conversionsValue >= 0 ? `${formatNumber(avgCos, 1)}%` : '-'}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCos, prevCos), true)}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })()}
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Tableau des performances par type de produit */}
                      {accountData.products && accountData.products.length > 0 && (
                        <div style={{ 
                          marginTop: '32px', 
                          width: '100%',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px'
                        }}>
                          
                          <div style={{ 
                            padding: '20px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            <h3 style={{ 
                              fontSize: '20px', 
                              fontWeight: '700', 
                              color: '#1f2937', 
                              margin: '0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <Tag size={18} /> Performances par catégorie
                            </h3>
                          </div>

                          <div style={{ overflowX: 'auto', width: '100%' }}>
                            <table className="campaigns-table" key={selectedMetrics.join(',')} style={{ width: '100%', tableLayout: 'auto' }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('category')}>
                                    Type de produit {categorySortConfig.field === 'category' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'center', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('country')}>
                                    Pays {categorySortConfig.field === 'country' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('count')}>
                                    Produits {categorySortConfig.field === 'count' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  {isShoppingMetricVisible('impressions') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('impressions')}>
                                      Impressions {categorySortConfig.field === 'impressions' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('clics') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('clicks')}>
                                      Clics {categorySortConfig.field === 'clicks' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('ctr') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('ctr')}>
                                      CTR {categorySortConfig.field === 'ctr' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpc') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('cpc')}>
                                      CPC {categorySortConfig.field === 'cpc' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cout') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('cost')}>
                                      Coût {categorySortConfig.field === 'cost' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('taux_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('conversionRate')}>
                                      Taux conv. {categorySortConfig.field === 'conversionRate' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('conversions')}>
                                      Conv. {categorySortConfig.field === 'conversions' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpa') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('cpa')}>
                                      CPA {categorySortConfig.field === 'cpa' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('valeur_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('conversionsValue')}>
                                      Valeur conv. {categorySortConfig.field === 'conversionsValue' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cos') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleCategorySort('cos')}>
                                      COS {categorySortConfig.field === 'cos' ? (categorySortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // Filtrer les produits selon les filtres appliqués
                                  const filteredProducts = filterProducts(accountData.products);

                                  // Fonction pour obtenir le type de produit et son niveau hiérarchique
                                  const getProductTypeWithLevel = (product) => {
                                    
                                    // Les productTypes viennent de Google Merchant Center
                                    // C'est une hiérarchie séparée par " > "
                                    // Ex: "Meubles > Canapés > Canapés convertibles"
                                    
                                    // Vérifier d'abord productTypes (array ou string)
                                    if (product.productTypes) {
                                      if (Array.isArray(product.productTypes) && product.productTypes.length > 0) {
                                        // Prendre le premier type s'il y en a plusieurs
                                        const typeString = product.productTypes[0].trim();
                                        // Compter le nombre de niveaux basé sur les séparateurs " > "
                                        const levels = typeString.split(' > ');
                                        const level = Math.min(levels.length, 5); // Maximum 5 niveaux
                                        return { type: typeString, level: `L${level}` };
                                      } else if (typeof product.productTypes === 'string' && product.productTypes.trim() !== '') {
                                        const typeString = product.productTypes.trim();
                                        // Compter le nombre de niveaux basé sur les séparateurs " > "
                                        const levels = typeString.split(' > ');
                                        const level = Math.min(levels.length, 5); // Maximum 5 niveaux
                                        return { type: typeString, level: `L${level}` };
                                      }
                                    }
                                    
                                    // Fallback: essayer les segments Google Ads (si jamais implémentés)
                                    if (product.product_type_l5 && product.product_type_l5.trim() !== '') {
                                      return { type: product.product_type_l5.trim(), level: 'L5' };
                                    }
                                    if (product.product_type_l4 && product.product_type_l4.trim() !== '') {
                                      return { type: product.product_type_l4.trim(), level: 'L4' };
                                    }
                                    if (product.product_type_l3 && product.product_type_l3.trim() !== '') {
                                      return { type: product.product_type_l3.trim(), level: 'L3' };
                                    }
                                    if (product.product_type_l2 && product.product_type_l2.trim() !== '') {
                                      return { type: product.product_type_l2.trim(), level: 'L2' };
                                    }
                                    if (product.product_type_l1 && product.product_type_l1.trim() !== '') {
                                      return { type: product.product_type_l1.trim(), level: 'L1' };
                                    }
                                    
                                    return { type: 'Sans type', level: '' };
                                  };

                                  // Fonction pour obtenir seulement le type de produit (pour compatibilité)
                                  const getProductType = (product) => {
                                    return getProductTypeWithLevel(product).type;
                                  };
                                  
                                  // Fonction pour normaliser le type de produit (pour la clé d'agrégation)
                                  const normalizeProductType = (productType) => {
                                    return productType.toLowerCase().trim();
                                  };
                                  
                                  // Créer une structure hiérarchique par niveaux de types de produit
                                  const hierarchicalData = {};
                                  
                                  filteredProducts.forEach(product => {
                                    const productTypeWithLevel = getProductTypeWithLevel(product);
                                    const productType = productTypeWithLevel.type;
                                    const country = product.feedLabelOrCountry || product.targetCountry || 'FR';
                                    
                                    // Déconstruire la hiérarchie (ex: "Meubles > Canapés > Canapés convertibles")
                                    let levels = [];
                                    if (productType === 'Sans type') {
                                      levels = ['Sans type'];
                                    } else if (productType.includes(' > ')) {
                                      levels = productType.split(' > ').map(level => level.trim());
                                    } else {
                                      // Fallback aux segments Google Ads
                                      if (product.product_type_l1) levels.push(product.product_type_l1);
                                      if (product.product_type_l2) levels.push(product.product_type_l2);
                                      if (product.product_type_l3) levels.push(product.product_type_l3);
                                      if (product.product_type_l4) levels.push(product.product_type_l4);
                                      if (product.product_type_l5) levels.push(product.product_type_l5);
                                      
                                      if (levels.length === 0) {
                                        levels = [productType || 'Sans type'];
                                      }
                                    }
                                    
                                    // Construire la hiérarchie pour chaque niveau
                                    for (let i = 0; i < levels.length; i++) {
                                      const levelPath = levels.slice(0, i + 1).join(' > ');
                                      const key = `${levelPath}|${country}|L${i + 1}`;
                                      
                                      if (!hierarchicalData[key]) {
                                        hierarchicalData[key] = {
                                          levelName: levels[i],
                                          fullPath: levelPath,
                                          level: i + 1,
                                          country: country,
                                          productCount: 0,
                                          impressions: 0,
                                          clicks: 0,
                                          cost: 0,
                                          conversions: 0,
                                          conversionsValue: 0,
                                          parentPath: i > 0 ? levels.slice(0, i).join(' > ') : null,
                                          prevImpressions: 0,
                                          prevClicks: 0,
                                          prevCost: 0,
                                          prevConversions: 0,
                                          prevConversionsValue: 0,
                                          products: [] // Stockage des produits au dernier niveau
                                        };
                                      }

                                      // Ajouter les métriques du produit à tous les niveaux de sa hiérarchie
                                      hierarchicalData[key].productCount += 1;

                                      // Stocker le produit uniquement au dernier niveau de sa hiérarchie
                                      if (i === levels.length - 1) {
                                        hierarchicalData[key].products.push(product);
                                      }
                                      hierarchicalData[key].impressions += product.impressions || 0;
                                      hierarchicalData[key].clicks += product.clicks || 0;
                                      hierarchicalData[key].cost += product.cost || 0;
                                      hierarchicalData[key].conversions += product.conversions || 0;
                                      hierarchicalData[key].conversionsValue += product.conversionsValue || 0;

                                      if (product.previousPeriod) {
                                        hierarchicalData[key].prevImpressions += product.previousPeriod.impressions || 0;
                                        hierarchicalData[key].prevClicks += product.previousPeriod.clicks || 0;
                                        hierarchicalData[key].prevCost += product.previousPeriod.cost || 0;
                                        hierarchicalData[key].prevConversions += product.previousPeriod.conversions || 0;
                                        hierarchicalData[key].prevConversionsValue += product.previousPeriod.conversionsValue || 0;
                                      }
                                    }
                                  });
                                  
                                  // Convertir en tableau et organiser par hiérarchie
                                  const allHierarchicalData = Object.values(hierarchicalData);
                                  
                                  // Trier hiérarchiquement pour créer une structure d'arbre correcte
                                  const sortedHierarchicalData = [];
                                  
                                  // Fonction de comparaison basée sur categorySortConfig
                                  const compareByConfig = (a, b) => {
                                    let aValue, bValue;
                                    switch (categorySortConfig.field) {
                                      case 'category': aValue = a.levelName || ''; bValue = b.levelName || ''; break;
                                      case 'ctr':
                                        aValue = a.impressions > 0 ? (a.clicks / a.impressions * 100) : 0;
                                        bValue = b.impressions > 0 ? (b.clicks / b.impressions * 100) : 0;
                                        break;
                                      case 'cpc':
                                        aValue = a.clicks > 0 ? (a.cost / a.clicks) : 0;
                                        bValue = b.clicks > 0 ? (b.cost / b.clicks) : 0;
                                        break;
                                      case 'conversionRate':
                                        aValue = a.clicks > 0 ? (a.conversions / a.clicks * 100) : 0;
                                        bValue = b.clicks > 0 ? (b.conversions / b.clicks * 100) : 0;
                                        break;
                                      case 'cpa':
                                        aValue = a.conversions > 0 ? (a.cost / a.conversions) : 0;
                                        bValue = b.conversions > 0 ? (b.cost / b.conversions) : 0;
                                        break;
                                      case 'cos':
                                        aValue = a.conversionsValue > 0 ? (a.cost / a.conversionsValue * 100) : 0;
                                        bValue = b.conversionsValue > 0 ? (b.cost / b.conversionsValue * 100) : 0;
                                        break;
                                      case 'count': aValue = a.productCount || 0; bValue = b.productCount || 0; break;
                                      default: aValue = a[categorySortConfig.field] || 0; bValue = b[categorySortConfig.field] || 0;
                                    }
                                    if (typeof aValue === 'string') {
                                      return categorySortConfig.direction === 'desc' ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
                                    }
                                    return categorySortConfig.direction === 'desc' ? bValue - aValue : aValue - bValue;
                                  };

                                  // Fonction récursive pour ajouter un élément et ses enfants
                                  const addItemWithChildren = (item) => {
                                    // Ajouter l'élément actuel
                                    sortedHierarchicalData.push(item);

                                    // Trouver les enfants directs (niveau + 1)
                                    const children = allHierarchicalData
                                      .filter(child =>
                                        child.level === item.level + 1 &&
                                        child.fullPath.startsWith(item.fullPath + ' > ') &&
                                        child.country === item.country
                                      )
                                      .sort(compareByConfig);

                                    // Ajouter récursivement chaque enfant et ses descendants
                                    children.forEach(child => {
                                      addItemWithChildren(child);
                                    });
                                  };

                                  // Commencer par tous les éléments de niveau 1, triés selon config
                                  const level1Items = allHierarchicalData
                                    .filter(item => item.level === 1)
                                    .sort(compareByConfig);
                                  
                                  // Construire récursivement l'arbre pour chaque élément de niveau 1
                                  level1Items.forEach(level1Item => {
                                    addItemWithChildren(level1Item);
                                  });
                                  
                                  // Fonctions pour gérer l'expansion/collapsion des niveaux
                                  const toggleExpanded = (itemKey) => {
                                    const newExpanded = new Set(expandedProductTypes);
                                    if (newExpanded.has(itemKey)) {
                                      newExpanded.delete(itemKey);
                                    } else {
                                      newExpanded.add(itemKey);
                                    }
                                    setExpandedProductTypes(newExpanded);
                                  };
                                  
                                  const isExpanded = (itemKey) => {
                                    return expandedProductTypes.has(itemKey);
                                  };

                                  // Fonctions pour gérer l'expansion des produits au dernier niveau
                                  const toggleProductsExpanded = (itemKey) => {
                                    const newExpanded = new Set(expandedCategoryProducts);
                                    if (newExpanded.has(itemKey)) {
                                      newExpanded.delete(itemKey);
                                    } else {
                                      newExpanded.add(itemKey);
                                    }
                                    setExpandedCategoryProducts(newExpanded);
                                  };

                                  const areProductsExpanded = (itemKey) => {
                                    return expandedCategoryProducts.has(itemKey);
                                  };

                                  // Filtrer les éléments visibles selon l'état d'expansion
                                  const getVisibleItems = (items) => {
                                    const visibleItems = [];
                                    
                                    items.forEach((item, index) => {
                                      // Toujours afficher les éléments de niveau 1
                                      if (item.level === 1) {
                                        visibleItems.push(item);
                                        return;
                                      }
                                      
                                      // Pour les niveaux 2+, vérifier si le parent est expansé
                                      let shouldShow = true;
                                      
                                      // Parcourir tous les niveaux parents pour vérifier l'expansion
                                      for (let parentLevel = 1; parentLevel < item.level; parentLevel++) {
                                        const parentPath = item.fullPath.split(' > ').slice(0, parentLevel).join(' > ');
                                        const parentKey = `${parentPath}|${item.country}|L${parentLevel}`;
                                        
                                        if (!isExpanded(parentKey)) {
                                          shouldShow = false;
                                          break;
                                        }
                                      }
                                      
                                      if (shouldShow) {
                                        visibleItems.push(item);
                                      }
                                    });
                                    
                                    return visibleItems;
                                  };
                                  
                                  // Appliquer le filtrage de visibilité (plus de pagination)
                                  const visibleHierarchicalData = getVisibleItems(sortedHierarchicalData);
                                  
                                  if (visibleHierarchicalData.length === 0) {
                                    return (
                                      <tr>
                                        <td colSpan="13" style={{ 
                                          textAlign: 'center', 
                                          padding: '40px', 
                                          color: '#6b7280',
                                          fontStyle: 'italic' 
                                        }}>
                                          Aucune donnée de type de produit disponible pour cette période
                                        </td>
                                      </tr>
                                    );
                                  }
                                  
                                  return visibleHierarchicalData.map((item, index) => {
                                    const ctr = item.impressions > 0 ? (item.clicks / item.impressions * 100) : 0;
                                    const cpc = item.clicks > 0 ? (item.cost / item.clicks) : 0;
                                    const conversionRate = item.clicks > 0 ? (item.conversions / item.clicks * 100) : 0;
                                    const cpa = item.conversions > 0 ? (item.cost / item.conversions) : 0;
                                    const cos = item.conversionsValue > 0 ? (item.cost / item.conversionsValue * 100) : 0;
                                    
                                    // Couleurs par niveau pour différencier visuellement
                                    const getLevelStyle = (level) => {
                                      const styles = {
                                        1: { bgColor: '#dbeafe', color: '#1e40af', icon: '📁' }, // Bleu pour niveau 1
                                        2: { bgColor: '#dcfce7', color: '#166534', icon: '📂' }, // Vert pour niveau 2  
                                        3: { bgColor: '#fef3c7', color: '#92400e', icon: '📄' }, // Jaune pour niveau 3
                                        4: { bgColor: '#fce7f3', color: '#9f1239', icon: '📋' }, // Rose pour niveau 4
                                        5: { bgColor: '#e9d5ff', color: '#6b21a8', icon: '📝' }  // Violet pour niveau 5
                                      };
                                      return styles[level] || { bgColor: '#f3f4f6', color: '#6b7280', icon: '🏷️' };
                                    };
                                    
                                    const levelStyle = getLevelStyle(item.level);
                                    const indentation = (item.level - 1) * 16; // 16px d'indentation par niveau
                                    
                                    // Vérifier si cet élément a des sous-éléments
                                    const hasChildren = sortedHierarchicalData.some(child =>
                                      child.level === item.level + 1 &&
                                      child.fullPath.startsWith(item.fullPath + ' > ') &&
                                      child.country === item.country
                                    );

                                    // Vérifier si cet élément a des produits (dernier niveau)
                                    const hasProducts = item.products && item.products.length > 0;
                                    const canExpand = hasChildren || hasProducts;

                                    const itemKey = `${item.fullPath}|${item.country}|L${item.level}`;
                                    const expanded = isExpanded(itemKey);
                                    const productsExpanded = areProductsExpanded(itemKey);

                                    // Déterminer l'état d'expansion visuel
                                    const isVisuallyExpanded = hasChildren ? expanded : productsExpanded;

                                    return (
                                      <React.Fragment key={`${item.fullPath}_${item.country}_${index}`}>
                                      <tr
                                        style={{
                                          backgroundColor: item.level === 1 ? '#f9fafb' :
                                                           item.level === 2 ? '#f3f4f6' : '#ffffff',
                                          fontWeight: item.level <= 2 ? '600' : '400'
                                        }}
                                      >
                                        <td style={{ padding: '12px 8px' }}>
                                          <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '10px',
                                            marginLeft: `${indentation}px`
                                          }}>
                                            {/* Bouton collapse/expand */}
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (hasChildren) {
                                                  toggleExpanded(itemKey);
                                                } else if (hasProducts) {
                                                  toggleProductsExpanded(itemKey);
                                                }
                                              }}
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                cursor: canExpand ? 'pointer' : 'default',
                                                padding: '2px',
                                                fontSize: '12px',
                                                color: canExpand ? '#6b7280' : 'transparent',
                                                width: '16px',
                                                height: '16px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center'
                                              }}
                                              disabled={!canExpand}
                                            >
                                              {canExpand ? (isVisuallyExpanded ? '▼' : '▶') : ''}
                                            </button>
                                            
                                            <span style={{
                                              padding: '4px 8px',
                                              borderRadius: '6px',
                                              backgroundColor: item.levelName === 'Sans type' ? '#f3f4f6' : levelStyle.bgColor,
                                              color: item.levelName === 'Sans type' ? '#6b7280' : levelStyle.color,
                                              fontSize: '12px',
                                              fontWeight: '500'
                                            }}>
                                              {item.levelName === 'Sans type' ? '🏷️' : levelStyle.icon}
                                            </span>
                                            <button
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                console.log('🖱️ Clic sur nom de catégorie:', item);
                                                console.log('🖱️ Ouverture modal type de produit');
                                                openProductTypeModal(item);
                                              }}
                                              style={{
                                                background: 'none',
                                                border: 'none',
                                                color: '#333333',
                                                textDecoration: 'underline',
                                                cursor: 'pointer',
                                                padding: '0',
                                                fontFamily: 'inherit',
                                                fontSize: 'inherit',
                                                fontWeight: 'inherit',
                                                textAlign: 'left'
                                              }}
                                              onMouseEnter={(e) => e.target.style.color = '#1d4ed8'}
                                              onMouseLeave={(e) => e.target.style.color = '#333333'}
                                            >
                                              {item.levelName}
                                            </button>
                                          </div>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                                          <span style={{
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            backgroundColor: '#f3f4f6',
                                            color: '#374151'
                                          }}>
                                            {item.country}
                                          </span>
                                        </td>
                                        <td className="numeric">{formatNumber(item.productCount)}</td>
                                        {isShoppingMetricVisible('impressions') && (
                                          <td className="numeric">
                                            {formatNumber(item.impressions)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.impressions, item.prevImpressions))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('clics') && (
                                          <td className="numeric">
                                            {formatNumber(item.clicks)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.clicks, item.prevClicks))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('ctr') && (
                                          <td className="numeric">
                                            {formatNumber(ctr, 2)}%
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(ctr, item.prevClicks / item.prevImpressions * 100))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cpc') && (
                                          <td className="numeric">
                                            €{formatNumber(cpc, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpc, item.prevCost / item.prevClicks), true)}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cout') && (
                                          <td className="numeric">
                                            €{formatNumber(item.cost, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.cost, item.prevCost))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('taux_conv') && (
                                          <td className="numeric">
                                            {formatNumber(conversionRate, 2)}%
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(conversionRate, item.prevConversions / item.prevClicks * 100))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('conv') && (
                                          <td className="numeric">
                                            {formatNumber(item.conversions, 1)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.conversions, item.prevConversions))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cpa') && (
                                          <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns')}`}>
                                            {item.conversions >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpa, item.prevCost / item.prevConversions), true)}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('valeur_conv') && (
                                          <td className="numeric">
                                            €{formatNumber(item.conversionsValue, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.conversionsValue, item.prevConversionsValue))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cos') && (
                                          <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                                            {item.conversionsValue >= 0 && item.cost >= 0 ? `${formatNumber(cos, 1)}%` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cos, item.prevCost / item.prevConversionsValue * 100), true)}
                                          </td>
                                        )}
                                      </tr>
                                      {/* Lignes de produits au dernier niveau */}
                                      {productsExpanded && hasProducts && item.products
                                        .sort((a, b) => {
                                          // Trier les produits selon la config de tri actuelle
                                          let aValue, bValue;
                                          switch (categorySortConfig.field) {
                                            case 'ctr':
                                              aValue = a.impressions > 0 ? (a.clicks / a.impressions * 100) : 0;
                                              bValue = b.impressions > 0 ? (b.clicks / b.impressions * 100) : 0;
                                              break;
                                            case 'cpc':
                                              aValue = a.clicks > 0 ? (a.cost / a.clicks) : 0;
                                              bValue = b.clicks > 0 ? (b.cost / b.clicks) : 0;
                                              break;
                                            case 'conversionRate':
                                              aValue = a.clicks > 0 ? (a.conversions / a.clicks * 100) : 0;
                                              bValue = b.clicks > 0 ? (b.conversions / b.clicks * 100) : 0;
                                              break;
                                            case 'cpa':
                                              aValue = a.conversions > 0 ? (a.cost / a.conversions) : 0;
                                              bValue = b.conversions > 0 ? (b.cost / b.conversions) : 0;
                                              break;
                                            case 'cos':
                                              aValue = a.conversionsValue > 0 ? (a.cost / a.conversionsValue * 100) : 0;
                                              bValue = b.conversionsValue > 0 ? (b.cost / b.conversionsValue * 100) : 0;
                                              break;
                                            case 'category':
                                              aValue = a.title || '';
                                              bValue = b.title || '';
                                              break;
                                            default:
                                              aValue = a[categorySortConfig.field] || 0;
                                              bValue = b[categorySortConfig.field] || 0;
                                          }
                                          if (typeof aValue === 'string') {
                                            return categorySortConfig.direction === 'desc' ? bValue.localeCompare(aValue) : aValue.localeCompare(bValue);
                                          }
                                          return categorySortConfig.direction === 'desc' ? bValue - aValue : aValue - bValue;
                                        })
                                        .map((product, pIndex) => {
                                          const pCtr = product.impressions > 0 ? (product.clicks / product.impressions * 100) : 0;
                                          const pCpc = product.clicks > 0 ? (product.cost / product.clicks) : 0;
                                          const pConvRate = product.clicks > 0 ? (product.conversions / product.clicks * 100) : 0;
                                          const pCpa = product.conversions > 0 ? (product.cost / product.conversions) : 0;
                                          const pCos = product.conversionsValue > 0 ? (product.cost / product.conversionsValue * 100) : 0;
                                          const productIndent = item.level * 16 + 16; // Indentation supplémentaire pour les produits

                                          return (
                                            <tr
                                              key={`product_${product.offerId || product.id || pIndex}_${pIndex}`}
                                              style={{
                                                backgroundColor: '#fefefe',
                                                borderLeft: '3px solid #e5e7eb'
                                              }}
                                            >
                                              <td style={{ padding: '8px' }}>
                                                <div style={{
                                                  display: 'flex',
                                                  alignItems: 'flex-start',
                                                  gap: '8px',
                                                  marginLeft: `${productIndent}px`
                                                }}>
                                                  {/* Miniature produit */}
                                                  <div style={{
                                                    width: '32px',
                                                    height: '32px',
                                                    borderRadius: '4px',
                                                    border: '1px solid #e5e7eb',
                                                    backgroundColor: '#f9fafb',
                                                    flexShrink: 0,
                                                    overflow: 'hidden'
                                                  }}>
                                                    {product.imageLink && (
                                                      <img
                                                        src={adresseDeVignette(product)}
                                                        alt=""
                                                        loading="lazy"
                                                        decoding="async"
                                                        fetchPriority="low"
                                                        style={{
                                                          width: '100%',
                                                          height: '100%',
                                                          objectFit: 'cover',
                                                          backgroundColor: '#fff'
                                                        }}
                                                        onError={(e) => {
                                                          e.target.style.display = 'none';
                                                        }}
                                                      />
                                                    )}
                                                  </div>
                                                  <div style={{ maxWidth: '250px' }}>
                                                    <div
                                                      onClick={() => openProductModal(product)}
                                                      style={{
                                                        fontSize: '13px',
                                                        color: '#333333',
                                                        fontWeight: '500',
                                                        wordBreak: 'break-word',
                                                        cursor: 'pointer'
                                                      }}
                                                      title={product.title || product.offerId}
                                                      onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                                                      onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                                                    >
                                                      {product.title || product.offerId || 'Produit sans titre'}
                                                    </div>
                                                    <div style={{ fontSize: '11px', color: '#6b7280', marginTop: '2px' }}>
                                                      ID: {product.itemId || product.offerId}
                                                    </div>
                                                  </div>
                                                </div>
                                              </td>
                                              <td style={{ textAlign: 'center', padding: '8px' }}>
                                                <span style={{
                                                  padding: '2px 4px',
                                                  borderRadius: '3px',
                                                  backgroundColor: '#f9fafb',
                                                  color: '#6b7280',
                                                  fontSize: '11px'
                                                }}>
                                                  {product.feedLabelOrCountry || product.targetCountry || 'FR'}
                                                </span>
                                              </td>
                                              <td className="numeric" style={{ fontSize: '12px', color: '#6b7280' }}>1</td>
                                              {isShoppingMetricVisible('impressions') && (
                                                <td className="numeric" style={{ fontSize: '12px' }}>{formatNumber(product.impressions || 0)}</td>
                                              )}
                                              {isShoppingMetricVisible('clics') && (
                                                <td className="numeric" style={{ fontSize: '12px' }}>{formatNumber(product.clicks || 0)}</td>
                                              )}
                                              {isShoppingMetricVisible('ctr') && (
                                                <td className="numeric" style={{ fontSize: '12px' }}>{formatNumber(pCtr, 2)}%</td>
                                              )}
                                              {isShoppingMetricVisible('cpc') && (
                                                <td className="numeric" style={{ fontSize: '12px' }}>€{formatNumber(pCpc, 2)}</td>
                                              )}
                                              {isShoppingMetricVisible('cout') && (
                                                <td className="numeric" style={{ fontSize: '12px' }}>€{formatNumber(product.cost || 0, 2)}</td>
                                              )}
                                              {isShoppingMetricVisible('taux_conv') && (
                                                <td className="numeric" style={{ fontSize: '12px' }}>{formatNumber(pConvRate, 2)}%</td>
                                              )}
                                              {isShoppingMetricVisible('conv') && (
                                                <td className="numeric" style={{ fontSize: '12px' }}>{formatNumber(product.conversions || 0, 1)}</td>
                                              )}
                                              {isShoppingMetricVisible('cpa') && (
                                                <td className={`numeric ${getCpaClassName(pCpa, selectedAccount?.id || 'global', 'campaigns')}`} style={{ fontSize: '12px' }}>
                                                  {product.conversions >= 0 ? `€${formatNumber(pCpa, 2)}` : '-'}
                                                </td>
                                              )}
                                              {isShoppingMetricVisible('valeur_conv') && (
                                                <td className="numeric" style={{ fontSize: '12px' }}>€{formatNumber(product.conversionsValue || 0, 2)}</td>
                                              )}
                                              {isShoppingMetricVisible('cos') && (
                                                <td className={`numeric ${getCosClassName(pCos, selectedAccount?.id || 'global', 'campaigns')}`} style={{ fontSize: '12px' }}>
                                                  {product.conversionsValue >= 0 && product.cost >= 0 ? `${formatNumber(pCos, 1)}%` : '-'}
                                                </td>
                                              )}
                                            </tr>
                                          );
                                        })
                                      }
                                      </React.Fragment>
                                    );
                                  });
                                })()}
                              </tbody>
                              <tfoot>
                                {(() => {
                                  // Calculer les totaux pour tous les types de produit filtrés
                                  const filteredProducts = filterProducts(accountData.products);

                                  const totals = filteredProducts.reduce((acc, product) => ({
                                    count: acc.count + 1,
                                    impressions: acc.impressions + (product.impressions || 0),
                                    clicks: acc.clicks + (product.clicks || 0),
                                    cost: acc.cost + (product.cost || 0),
                                    conversions: acc.conversions + (product.conversions || 0),
                                    conversionsValue: acc.conversionsValue + (product.conversionsValue || 0),
                                    // Totaux période précédente
                                    prevImpressions: acc.prevImpressions + (product.previousPeriod?.impressions || 0),
                                    prevClicks: acc.prevClicks + (product.previousPeriod?.clicks || 0),
                                    prevCost: acc.prevCost + (product.previousPeriod?.cost || 0),
                                    prevConversions: acc.prevConversions + (product.previousPeriod?.conversions || 0),
                                    prevConversionsValue: acc.prevConversionsValue + (product.previousPeriod?.conversionsValue || 0)
                                  }), { count: 0, impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, prevImpressions: 0, prevClicks: 0, prevCost: 0, prevConversions: 0, prevConversionsValue: 0 });

                                  const avgCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
                                  const avgCpc = totals.clicks > 0 ? totals.cost / totals.clicks : 0;
                                  const avgConversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0;
                                  const avgCpa = totals.conversions > 0 ? totals.cost / totals.conversions : 0;
                                  const avgCos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue) * 100 : 0;

                                  // Métriques période précédente
                                  const prevCtr = totals.prevImpressions > 0 ? (totals.prevClicks / totals.prevImpressions) * 100 : 0;
                                  const prevCpc = totals.prevClicks > 0 ? totals.prevCost / totals.prevClicks : 0;
                                  const prevConversionRate = totals.prevClicks > 0 ? (totals.prevConversions / totals.prevClicks) * 100 : 0;
                                  const prevCpa = totals.prevConversions > 0 ? totals.prevCost / totals.prevConversions : 0;
                                  const prevCos = totals.prevConversionsValue > 0 ? (totals.prevCost / totals.prevConversionsValue) * 100 : 0;

                                  return (
                                    <tr style={{
                                      backgroundColor: '#f8f9fa',
                                      borderTop: '2px solid #e5e7eb',
                                      fontWeight: '600'
                                    }}>
                                      <td style={{ padding: '12px 8px' }}>
                                        <strong>TOTAL</strong>
                                      </td>
                                      <td style={{ textAlign: 'center', padding: '12px 8px' }}>-</td>
                                      <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(totals.count)}</td>
                                      {isShoppingMetricVisible('impressions') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.impressions)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.impressions, totals.prevImpressions))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('clics') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.clicks)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.clicks, totals.prevClicks))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('ctr') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(avgCtr, 2)}%
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCtr, prevCtr))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cpc') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(avgCpc, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpc, prevCpc), true)}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cout') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(totals.cost, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.cost, totals.prevCost))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('taux_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(avgConversionRate, 2)}%
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgConversionRate, prevConversionRate))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.conversions, 1)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversions, totals.prevConversions))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cpa') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {totals.conversions >= 0 ? `€${formatNumber(avgCpa, 2)}` : '-'}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpa, prevCpa), true)}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('valeur_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(totals.conversionsValue, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversionsValue, totals.prevConversionsValue))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cos') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {totals.conversionsValue >= 0 ? `${formatNumber(avgCos, 1)}%` : '-'}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCos, prevCos), true)}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })()}
                              </tfoot>
                            </table>
                          </div>
                        </div>
                      )}

                      {/* Tableau des performances par marques */}
                      {accountData.products && accountData.products.length > 0 && (
                        <div style={{ 
                          marginTop: '32px', 
                          width: '100%',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px'
                        }}>
                          
                          <div style={{ 
                            padding: '20px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            <h3 style={{ 
                              fontSize: '20px', 
                              fontWeight: '700', 
                              color: '#1f2937', 
                              margin: '0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <Store size={18} /> Performances par marques
                            </h3>
                          </div>

                          <div style={{ overflowX: 'auto', width: '100%' }}>
                            <table className="campaigns-table" key={selectedMetrics.join(',')} style={{ width: '100%', tableLayout: 'auto' }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('brand')}>
                                    Marque {brandSortConfig.field === 'brand' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'center', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('country')}>
                                    Pays {brandSortConfig.field === 'country' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('count')}>
                                    Produits {brandSortConfig.field === 'count' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  {isShoppingMetricVisible('impressions') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('impressions')}>
                                      Impressions {brandSortConfig.field === 'impressions' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('clics') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('clicks')}>
                                      Clics {brandSortConfig.field === 'clicks' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('ctr') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('ctr')}>
                                      CTR {brandSortConfig.field === 'ctr' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpc') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('cpc')}>
                                      CPC {brandSortConfig.field === 'cpc' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cout') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('cost')}>
                                      Coût {brandSortConfig.field === 'cost' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('taux_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('conversionRate')}>
                                      Taux conv. {brandSortConfig.field === 'conversionRate' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('conversions')}>
                                      Conv. {brandSortConfig.field === 'conversions' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpa') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('cpa')}>
                                      CPA {brandSortConfig.field === 'cpa' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('valeur_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('conversionsValue')}>
                                      Valeur conv. {brandSortConfig.field === 'conversionsValue' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cos') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleBrandSort('cos')}>
                                      COS {brandSortConfig.field === 'cos' ? (brandSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // Filtrer les produits selon les filtres appliqués
                                  const filteredProducts = filterProducts(accountData.products);
                                  
                                  // Fonction pour obtenir la marque d'un produit
                                  const getProductBrand = (product) => {
                                    if (!product.brand || product.brand === 'no_data' || product.brand.trim() === '') {
                                      return 'Sans marque';
                                    }
                                    return product.brand;
                                  };
                                  
                                  // Fonction pour normaliser la marque (pour la clé d'agrégation)
                                  const normalizeBrand = (brand) => {
                                    return brand.toLowerCase().trim();
                                  };
                                  
                                  // Agrégation par marque et pays
                                  const brandCountryAggregation = {};
                                  const brandDisplayNames = {}; // Pour garder la première occurrence de chaque marque

                                  // Récupérer l'agrégation par marque du backend (calculée directement depuis les données de comparaison)
                                  const backendBrandAggregation = accountData.totalMetricsComparison?.brandAggregation || {};

                                  filteredProducts.forEach(product => {
                                    const brand = getProductBrand(product);
                                    const country = product.feedLabelOrCountry || product.targetCountry || 'FR';

                                    // Utiliser la marque normalisée pour la clé
                                    const normalizedBrand = normalizeBrand(brand);
                                    const key = `${normalizedBrand}|${country}`;

                                    // Garder la première occurrence du nom de marque pour l'affichage
                                    if (!brandDisplayNames[normalizedBrand]) {
                                      brandDisplayNames[normalizedBrand] = brand;
                                    }

                                    if (!brandCountryAggregation[key]) {
                                      // Utiliser les données du backend pour les métriques de comparaison
                                      const backendData = backendBrandAggregation[key];
                                      brandCountryAggregation[key] = {
                                        brand: brandDisplayNames[normalizedBrand],
                                        normalizedBrand: normalizedBrand,
                                        country: country,
                                        productCount: 0,
                                        impressions: 0,
                                        clicks: 0,
                                        cost: 0,
                                        conversions: 0,
                                        conversionsValue: 0,
                                        // Utiliser les vraies données agrégées du backend au lieu de sommer les previousPeriod
                                        prevImpressions: backendData?.impressions || 0,
                                        prevClicks: backendData?.clicks || 0,
                                        prevCost: backendData?.cost || 0,
                                        prevConversions: backendData?.conversions || 0,
                                        prevConversionsValue: backendData?.conversionsValue || 0
                                      };
                                    }

                                    brandCountryAggregation[key].productCount += 1;
                                    brandCountryAggregation[key].impressions += product.impressions || 0;
                                    brandCountryAggregation[key].clicks += product.clicks || 0;
                                    brandCountryAggregation[key].cost += product.cost || 0;
                                    brandCountryAggregation[key].conversions += product.conversions || 0;
                                    brandCountryAggregation[key].conversionsValue += product.conversionsValue || 0;
                                    // Les données de comparaison sont déjà définies depuis backendBrandAggregation
                                  });
                                  
                                  // Convertir en tableau et trier selon la config
                                  const allBrandData = sortAggregatedData(
                                    Object.values(brandCountryAggregation),
                                    brandSortConfig
                                  );
                                  
                                  // Pagination des marques
                                  const brandTotalPages = Math.ceil(allBrandData.length / brandsItemsPerPage);
                                  const brandStartIndex = (brandsCurrentPage - 1) * brandsItemsPerPage;
                                  const brandEndIndex = brandStartIndex + brandsItemsPerPage;
                                  const sortedBrandData = allBrandData.slice(brandStartIndex, brandEndIndex);
                                  
                                  if (sortedBrandData.length === 0) {
                                    return (
                                      <tr>
                                        <td colSpan="13" style={{ 
                                          textAlign: 'center', 
                                          padding: '40px', 
                                          color: '#6b7280',
                                          fontStyle: 'italic' 
                                        }}>
                                          Aucune donnée de marque disponible pour cette période
                                        </td>
                                      </tr>
                                    );
                                  }
                                  
                                  return sortedBrandData.map(item => {
                                    const ctr = item.impressions > 0 ? (item.clicks / item.impressions * 100) : 0;
                                    const cpc = item.clicks > 0 ? (item.cost / item.clicks) : 0;
                                    const conversionRate = item.clicks > 0 ? (item.conversions / item.clicks * 100) : 0;
                                    const cpa = item.conversions > 0 ? (item.cost / item.conversions) : 0;
                                    const cos = item.conversionsValue > 0 ? (item.cost / item.conversionsValue * 100) : 0;
                                    
                                    return (
                                      <tr key={`${item.brand}_${item.country}`}>
                                        <td style={{ padding: '12px 8px' }}>
                                          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                            <span style={{
                                              padding: '4px 8px',
                                              borderRadius: '6px',
                                              backgroundColor: item.brand === 'Sans marque' ? '#f3f4f6' : '#e0f2fe',
                                              color: item.brand === 'Sans marque' ? '#6b7280' : '#0369a1',
                                              fontSize: '12px',
                                              fontWeight: '500'
                                            }}>
                                              {item.brand === 'Sans marque' ? '🏷️' : '🏪'}
                                            </span>
                                            {item.brand}
                                          </div>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                                          <span style={{
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            backgroundColor: '#f3f4f6',
                                            color: '#374151'
                                          }}>
                                            {item.country}
                                          </span>
                                        </td>
                                        <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(item.productCount)}</td>
                                        {isMetricSelected('impressions', selectedAccount?.id || 'global') && (
                                          <td className="numeric" style={{ padding: '12px 8px' }}>
                                            {formatNumber(item.impressions)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.impressions, item.prevImpressions))}
                                          </td>
                                        )}
                                        {isMetricSelected('clics', selectedAccount?.id || 'global') && (
                                          <td className="numeric" style={{ padding: '12px 8px' }}>
                                            {formatNumber(item.clicks)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.clicks, item.prevClicks))}
                                          </td>
                                        )}
                                        {isMetricSelected('ctr', selectedAccount?.id || 'global') && (
                                          <td className="numeric" style={{ padding: '12px 8px' }}>
                                            {ctr >= 0 ? `${formatNumber(ctr, 2)}%` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(ctr, item.prevClicks / item.prevImpressions * 100))}
                                          </td>
                                        )}
                                        {isMetricSelected('cpc', selectedAccount?.id || 'global') && (
                                          <td className="numeric" style={{ padding: '12px 8px' }}>
                                            {cpc >= 0 ? `${formatNumber(cpc, 3)}€` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpc, item.prevCost / item.prevClicks), true)}
                                          </td>
                                        )}
                                        {isMetricSelected('cout', selectedAccount?.id || 'global') && (
                                          <td className="numeric" style={{ padding: '12px 8px', fontWeight: '600', color: '#065f46' }}>
                                            {item.cost >= 0 ? `${formatNumber(item.cost, 2)}€` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.cost, item.prevCost))}
                                          </td>
                                        )}
                                        {isMetricSelected('taux_conv', selectedAccount?.id || 'global') && (
                                          <td className="numeric" style={{ padding: '12px 8px' }}>
                                            {conversionRate >= 0 ? `${formatNumber(conversionRate, 2)}%` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(conversionRate, item.prevConversions / item.prevClicks * 100))}
                                          </td>
                                        )}
                                        {isMetricSelected('conv', selectedAccount?.id || 'global') && (
                                          <td className="numeric" style={{ padding: '12px 8px' }}>
                                            {item.conversions >= 0 ? formatNumber(item.conversions, 2) : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.conversions, item.prevConversions))}
                                          </td>
                                        )}
                                        {isMetricSelected('cpa', selectedAccount?.id || 'global') && (
                                          <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns')}`} style={{ padding: '12px 8px' }}>
                                            {cpa >= 0 ? `${formatNumber(cpa, 2)}€` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpa, item.prevCost / item.prevConversions), true)}
                                          </td>
                                        )}
                                        {isMetricSelected('valeur_conv', selectedAccount?.id || 'global') && (
                                          <td className="numeric" style={{ padding: '12px 8px' }}>
                                            {item.conversionsValue >= 0 ? `${formatNumber(item.conversionsValue, 2)}€` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(item.conversionsValue, item.prevConversionsValue))}
                                          </td>
                                        )}
                                        {isMetricSelected('cos', selectedAccount?.id || 'global') && (
                                          <td className={`numeric ${item.conversionsValue > 0 ? (getCosClassName(cos, selectedAccount?.id || 'global', 'campaigns')) : ''}`} style={{ padding: '12px 8px' }}>
                                            {item.conversionsValue >= 0 ? `${formatNumber(cos, 1)}%` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cos, item.prevCost / item.prevConversionsValue * 100), true)}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                              <tfoot>
                                {(() => {
                                  // Calculer les totaux pour toutes les marques
                                  const filteredProducts = filterProducts(accountData.products);

                                  const totals = filteredProducts.reduce((acc, product) => {
                                    acc.productCount += 1;
                                    acc.impressions += product.impressions || 0;
                                    acc.clicks += product.clicks || 0;
                                    acc.cost += product.cost || 0;
                                    acc.conversions += product.conversions || 0;
                                    acc.conversionsValue += product.conversionsValue || 0;
                                    // Totaux période précédente
                                    acc.prevImpressions += product.previousPeriod?.impressions || 0;
                                    acc.prevClicks += product.previousPeriod?.clicks || 0;
                                    acc.prevCost += product.previousPeriod?.cost || 0;
                                    acc.prevConversions += product.previousPeriod?.conversions || 0;
                                    acc.prevConversionsValue += product.previousPeriod?.conversionsValue || 0;
                                    return acc;
                                  }, {
                                    productCount: 0,
                                    impressions: 0,
                                    clicks: 0,
                                    cost: 0,
                                    conversions: 0,
                                    conversionsValue: 0,
                                    prevImpressions: 0,
                                    prevClicks: 0,
                                    prevCost: 0,
                                    prevConversions: 0,
                                    prevConversionsValue: 0
                                  });

                                  const avgCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
                                  const avgCpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
                                  const avgConversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
                                  const avgCpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
                                  const avgCos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;

                                  // Métriques période précédente
                                  const prevCtr = totals.prevImpressions > 0 ? (totals.prevClicks / totals.prevImpressions * 100) : 0;
                                  const prevCpc = totals.prevClicks > 0 ? (totals.prevCost / totals.prevClicks) : 0;
                                  const prevConversionRate = totals.prevClicks > 0 ? (totals.prevConversions / totals.prevClicks * 100) : 0;
                                  const prevCpa = totals.prevConversions > 0 ? (totals.prevCost / totals.prevConversions) : 0;
                                  const prevCos = totals.prevConversionsValue > 0 ? (totals.prevCost / totals.prevConversionsValue * 100) : 0;

                                  return (
                                    <tr style={{
                                      backgroundColor: '#f9fafb',
                                      fontWeight: '600',
                                      borderTop: '2px solid #d1d5db'
                                    }}>
                                      <td style={{ padding: '12px 8px' }}>
                                        <strong>Total général</strong>
                                      </td>
                                      <td style={{ textAlign: 'center', padding: '12px 8px' }}>-</td>
                                      <td className="numeric" style={{ padding: '12px 8px' }}><strong>{formatNumber(totals.productCount)}</strong></td>
                                      {isMetricSelected('impressions', selectedAccount?.id || 'global') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          <strong>{formatNumber(totals.impressions)}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.impressions, totals.prevImpressions))}
                                        </td>
                                      )}
                                      {isMetricSelected('clics', selectedAccount?.id || 'global') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          <strong>{formatNumber(totals.clicks)}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.clicks, totals.prevClicks))}
                                        </td>
                                      )}
                                      {isMetricSelected('ctr', selectedAccount?.id || 'global') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          <strong>{avgCtr >= 0 ? `${formatNumber(avgCtr, 2)}%` : '-'}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCtr, prevCtr))}
                                        </td>
                                      )}
                                      {isMetricSelected('cpc', selectedAccount?.id || 'global') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          <strong>{avgCpc >= 0 ? `${formatNumber(avgCpc, 3)}€` : '-'}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpc, prevCpc), true)}
                                        </td>
                                      )}
                                      {isMetricSelected('cout', selectedAccount?.id || 'global') && (
                                        <td className="numeric" style={{ padding: '12px 8px', color: '#065f46' }}>
                                          <strong>{totals.cost >= 0 ? `${formatNumber(totals.cost, 2)}€` : '-'}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.cost, totals.prevCost))}
                                        </td>
                                      )}
                                      {isMetricSelected('taux_conv', selectedAccount?.id || 'global') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          <strong>{avgConversionRate >= 0 ? `${formatNumber(avgConversionRate, 2)}%` : '-'}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgConversionRate, prevConversionRate))}
                                        </td>
                                      )}
                                      {isMetricSelected('conv', selectedAccount?.id || 'global') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          <strong>{totals.conversions >= 0 ? formatNumber(totals.conversions, 2) : '-'}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversions, totals.prevConversions))}
                                        </td>
                                      )}
                                      {isMetricSelected('cpa', selectedAccount?.id || 'global') && (
                                        <td className={`numeric ${getCpaClassName(avgCpa, selectedAccount?.id || 'global', 'campaigns')}`} style={{ padding: '12px 8px' }}>
                                          <strong>{avgCpa >= 0 ? `${formatNumber(avgCpa, 2)}€` : '-'}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpa, prevCpa), true)}
                                        </td>
                                      )}
                                      {isMetricSelected('valeur_conv', selectedAccount?.id || 'global') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          <strong>{totals.conversionsValue >= 0 ? `${formatNumber(totals.conversionsValue, 2)}€` : '-'}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversionsValue, totals.prevConversionsValue))}
                                        </td>
                                      )}
                                      {isMetricSelected('cos', selectedAccount?.id || 'global') && (
                                        <td className={`numeric ${totals.conversionsValue > 0 ? (getCosClassName(avgCos, selectedAccount?.id || 'global', 'campaigns')) : ''}`} style={{ padding: '12px 8px' }}>
                                          <strong>{totals.conversionsValue >= 0 ? `${formatNumber(avgCos, 1)}%` : '-'}</strong>
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCos, prevCos), true)}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })()}
                              </tfoot>
                            </table>
                          </div>

                          {/* Contrôles de pagination pour les marques */}
                          {(() => {
                            // Calculer avec les données filtrées
                            const filteredProducts = filterProducts(accountData.products);
                            
                            // Agréger par marque et pays (même logique que dans le tableau)
                            const brandCountryAggregation = {};
                            const brandDisplayNames = {};
                            
                            filteredProducts.forEach(product => {
                              const getProductBrand = (product) => {
                                if (!product.brand || product.brand === 'no_data' || product.brand.trim() === '') {
                                  return 'Sans marque';
                                }
                                return product.brand;
                              };
                              
                              const normalizeBrand = (brand) => {
                                return brand.toLowerCase().trim();
                              };
                              
                              const brand = getProductBrand(product);
                              const country = product.feedLabelOrCountry || product.targetCountry || 'FR';
                              
                              const normalizedBrand = normalizeBrand(brand);
                              const key = `${normalizedBrand}|${country}`;
                              
                              if (!brandDisplayNames[normalizedBrand]) {
                                brandDisplayNames[normalizedBrand] = brand;
                              }
                              
                              if (!brandCountryAggregation[key]) {
                                brandCountryAggregation[key] = {
                                  brand: brandDisplayNames[normalizedBrand],
                                  normalizedBrand: normalizedBrand,
                                  country: country,
                                  productCount: 0,
                                  impressions: 0,
                                  clicks: 0,
                                  cost: 0,
                                  conversions: 0,
                                  conversionsValue: 0
                                };
                              }
                              
                              brandCountryAggregation[key].productCount += 1;
                              brandCountryAggregation[key].impressions += product.impressions || 0;
                              brandCountryAggregation[key].clicks += product.clicks || 0;
                              brandCountryAggregation[key].cost += product.cost || 0;
                              brandCountryAggregation[key].conversions += product.conversions || 0;
                              brandCountryAggregation[key].conversionsValue += product.conversionsValue || 0;
                            });
                            
                            const allBrandData = Object.values(brandCountryAggregation);
                            const totalBrands = allBrandData.length;
                            const brandTotalPages = Math.ceil(totalBrands / brandsItemsPerPage);
                            const brandStartIndex = (brandsCurrentPage - 1) * brandsItemsPerPage;
                            const brandEndIndex = Math.min(brandStartIndex + brandsItemsPerPage, totalBrands);
                            
                            return brandTotalPages > 1 && (
                              <div className="pagination-controls">
                                <div className="pagination-info">
                                  <span>
                                    Affichage {brandStartIndex + 1}-{brandEndIndex} sur {totalBrands} marques
                                  </span>
                                </div>
                                <div className="pagination-buttons">
                                  <button 
                                    onClick={() => setBrandsCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={brandsCurrentPage === 1}
                                    className="pagination-btn"
                                  >
                                    ← Précédent
                                  </button>
                                  <span className="page-numbers">
                                    Page {brandsCurrentPage} sur {brandTotalPages}
                                  </span>
                                  <button 
                                    onClick={() => setBrandsCurrentPage(prev => Math.min(brandTotalPages, prev + 1))}
                                    disabled={brandsCurrentPage === brandTotalPages}
                                    className="pagination-btn"
                                  >
                                    Suivant →
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Tableau des performances par libellé */}
                      {accountData.products && accountData.products.length > 0 && (
                        <div style={{ 
                          marginTop: '32px', 
                          width: '100%',
                          backgroundColor: '#ffffff',
                          border: '1px solid #e5e7eb',
                          borderRadius: '12px'
                        }}>
                          
                          <div style={{ 
                            padding: '20px',
                            borderBottom: '1px solid #e5e7eb'
                          }}>
                            <h3 style={{ 
                              fontSize: '20px', 
                              fontWeight: '700', 
                              color: '#1f2937', 
                              margin: '0',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '12px'
                            }}>
                              <Tag size={18} /> Performances par libellé
                            </h3>
                          </div>

                          <div style={{ overflowX: 'auto', width: '100%' }}>
                            <table className="campaigns-table" key={selectedMetrics.join(',')} style={{ width: '100%', tableLayout: 'auto' }}>
                              <thead>
                                <tr>
                                  <th style={{ textAlign: 'left', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('labelType')}>
                                    Custom Label {labelSortConfig.field === 'labelType' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'left', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('label')}>
                                    Libellé {labelSortConfig.field === 'label' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'center', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('country')}>
                                    Pays {labelSortConfig.field === 'country' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('count')}>
                                    Produits {labelSortConfig.field === 'count' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                  </th>
                                  {isShoppingMetricVisible('impressions') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('impressions')}>
                                      Impressions {labelSortConfig.field === 'impressions' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('clics') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('clicks')}>
                                      Clics {labelSortConfig.field === 'clicks' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('ctr') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('ctr')}>
                                      CTR {labelSortConfig.field === 'ctr' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpc') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('cpc')}>
                                      CPC {labelSortConfig.field === 'cpc' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cout') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('cost')}>
                                      Coût {labelSortConfig.field === 'cost' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('taux_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('conversionRate')}>
                                      Taux conv. {labelSortConfig.field === 'conversionRate' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('conversions')}>
                                      Conv. {labelSortConfig.field === 'conversions' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cpa') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('cpa')}>
                                      CPA {labelSortConfig.field === 'cpa' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('valeur_conv') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('conversionsValue')}>
                                      Valeur conv. {labelSortConfig.field === 'conversionsValue' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                  {isShoppingMetricVisible('cos') && (
                                    <th style={{ textAlign: 'right', padding: '12px 8px', cursor: 'pointer', userSelect: 'none' }} onClick={() => handleLabelSort('cos')}>
                                      COS {labelSortConfig.field === 'cos' ? (labelSortConfig.direction === 'desc' ? '▼' : '▲') : ''}
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {(() => {
                                  // Filtrer les produits selon les filtres appliqués
                                  const filteredProducts = filterProducts(accountData.products);

                                  // Fonction pour obtenir tous les libellés d'un produit avec leur type de custom label
                                  const getProductLabelsWithType = (product) => {
                                    const labelsWithType = [];
                                    
                                    // Vérifier customLabel0 à customLabel4
                                    if (product.customLabel0) labelsWithType.push({ type: 'customLabel0', value: product.customLabel0 });
                                    if (product.customLabel1) labelsWithType.push({ type: 'customLabel1', value: product.customLabel1 });
                                    if (product.customLabel2) labelsWithType.push({ type: 'customLabel2', value: product.customLabel2 });
                                    if (product.customLabel3) labelsWithType.push({ type: 'customLabel3', value: product.customLabel3 });
                                    if (product.customLabel4) labelsWithType.push({ type: 'customLabel4', value: product.customLabel4 });
                                    
                                    // Aussi vérifier dans customAttributes si présent
                                    if (product.customAttributes && Array.isArray(product.customAttributes)) {
                                      product.customAttributes.forEach(attr => {
                                        if (attr.name && attr.name.startsWith('custom_label_') && attr.value) {
                                          labelsWithType.push({ type: attr.name, value: attr.value });
                                        }
                                      });
                                    }
                                    
                                    return labelsWithType;
                                  };
                                  
                                  // Agrégation par type de custom label, libellé et pays
                                  const labelCountryAggregation = {};

                                  filteredProducts.forEach(product => {
                                    const labelsWithType = getProductLabelsWithType(product);
                                    const country = product.feedLabelOrCountry || product.targetCountry || 'FR';

                                    // Si le produit n'a pas de libellé, le compter comme "Sans libellé"
                                    if (labelsWithType.length === 0) {
                                      labelsWithType.push({ type: 'Aucun', value: 'Sans libellé' });
                                    }

                                    // Pour chaque libellé du produit, ajouter ses métriques
                                    labelsWithType.forEach(labelObj => {
                                      const key = `${labelObj.type}|${labelObj.value}|${country}`;

                                      if (!labelCountryAggregation[key]) {
                                        labelCountryAggregation[key] = {
                                          labelType: labelObj.type,
                                          label: labelObj.value,
                                          country,
                                          count: 0,
                                          impressions: 0,
                                          clicks: 0,
                                          cost: 0,
                                          conversions: 0,
                                          conversionsValue: 0,
                                          prevImpressions: 0,
                                          prevClicks: 0,
                                          prevCost: 0,
                                          prevConversions: 0,
                                          prevConversionsValue: 0
                                        };
                                      }

                                      const data = labelCountryAggregation[key];
                                      data.count += 1;
                                      data.impressions += product.impressions || 0;
                                      data.clicks += product.clicks || 0;
                                      data.cost += product.cost || 0;
                                      data.conversions += product.conversions || 0;
                                      data.conversionsValue += product.conversionsValue || 0;

                                      if (product.previousPeriod) {
                                        data.prevImpressions += product.previousPeriod.impressions || 0;
                                        data.prevClicks += product.previousPeriod.clicks || 0;
                                        data.prevCost += product.previousPeriod.cost || 0;
                                        data.prevConversions += product.previousPeriod.conversions || 0;
                                        data.prevConversionsValue += product.previousPeriod.conversionsValue || 0;
                                      }
                                    });
                                  });
                                  
                                  // Convertir en tableau et trier selon la config
                                  const allLabelData = sortAggregatedData(
                                    Object.values(labelCountryAggregation),
                                    labelSortConfig
                                  );
                                  
                                  // Pagination des libellés
                                  const labelTotalPages = Math.ceil(allLabelData.length / labelsItemsPerPage);
                                  const labelStartIndex = (labelsCurrentPage - 1) * labelsItemsPerPage;
                                  const labelEndIndex = labelStartIndex + labelsItemsPerPage;
                                  const sortedLabelData = allLabelData.slice(labelStartIndex, labelEndIndex);
                                  
                                  if (sortedLabelData.length === 0 && allLabelData.length > 0) {
                                    return (
                                      <tr>
                                        <td colSpan="14" style={{ 
                                          textAlign: 'center', 
                                          padding: '40px', 
                                          color: '#6b7280',
                                          fontStyle: 'italic' 
                                        }}>
                                          Aucun résultat sur cette page
                                        </td>
                                      </tr>
                                    );
                                  }
                                  
                                  return sortedLabelData.map((data, index) => {
                                    const ctr = data.impressions > 0 ? (data.clicks / data.impressions * 100) : 0;
                                    const cpc = data.clicks > 0 ? (data.cost / data.clicks) : 0;
                                    const conversionRate = data.clicks > 0 ? (data.conversions / data.clicks * 100) : 0;
                                    const cpa = data.conversions > 0 ? (data.cost / data.conversions) : 0;
                                    const cos = data.conversionsValue > 0 ? (data.cost / data.conversionsValue * 100) : 0;
                                    
                                    // Définir la couleur du badge selon le libellé
                                    const getLabelColor = (label) => {
                                      if (label === 'Sans libellé') {
                                        return { bgColor: '#e5e7eb', color: '#6b7280' };
                                      }
                                      // Utiliser une couleur basée sur un hash simple du libellé pour avoir des couleurs consistantes
                                      const hash = label.split('').reduce((acc, char) => char.charCodeAt(0) + ((acc << 5) - acc), 0);
                                      const colors = [
                                        { bgColor: '#dbeafe', color: '#1e40af' }, // Bleu
                                        { bgColor: '#dcfce7', color: '#166534' }, // Vert
                                        { bgColor: '#fef3c7', color: '#92400e' }, // Jaune
                                        { bgColor: '#fce7f3', color: '#9f1239' }, // Rose
                                        { bgColor: '#e9d5ff', color: '#6b21a8' }, // Violet
                                        { bgColor: '#ccfbf1', color: '#065f46' }, // Turquoise
                                      ];
                                      return colors[Math.abs(hash) % colors.length];
                                    };
                                    
                                    const labelStyle = getLabelColor(data.label);
                                    
                                    return (
                                      <tr key={`${data.label}-${data.country}-${index}`} style={{ 
                                        borderBottom: '1px solid #f3f4f6',
                                        backgroundColor: index % 2 === 0 ? '#ffffff' : '#f9fafb'
                                      }}>
                                        <td style={{ padding: '12px 8px' }}>
                                          <span style={{
                                            fontSize: '12px',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            backgroundColor: '#f3f4f6',
                                            color: '#6b7280',
                                            fontWeight: '500'
                                          }}>
                                            {data.labelType}
                                          </span>
                                        </td>
                                        <td style={{ padding: '12px 8px' }}>
                                          <span style={{
                                            fontSize: '12px',
                                            padding: '4px 8px',
                                            borderRadius: '4px',
                                            backgroundColor: labelStyle.bgColor,
                                            color: labelStyle.color,
                                            fontWeight: '500'
                                          }}>
                                            {data.label}
                                          </span>
                                        </td>
                                        <td style={{ textAlign: 'center', padding: '12px 8px' }}>
                                          <span style={{
                                            fontSize: '12px',
                                            padding: '2px 6px',
                                            borderRadius: '4px',
                                            backgroundColor: '#f3f4f6',
                                            color: '#374151'
                                          }}>
                                            {data.country}
                                          </span>
                                        </td>
                                        <td className="numeric">{formatNumber(data.count)}</td>
                                        {isShoppingMetricVisible('impressions') && (
                                          <td className="numeric">
                                            {formatNumber(data.impressions)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.impressions, data.prevImpressions))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('clics') && (
                                          <td className="numeric">
                                            {formatNumber(data.clicks)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.clicks, data.prevClicks))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('ctr') && (
                                          <td className="numeric">
                                            {formatNumber(ctr, 2)}%
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(ctr, data.prevClicks / data.prevImpressions * 100))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cpc') && (
                                          <td className="numeric">
                                            €{formatNumber(cpc, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpc, data.prevCost / data.prevClicks), true)}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cout') && (
                                          <td className="numeric">
                                            €{formatNumber(data.cost, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.cost, data.prevCost))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('taux_conv') && (
                                          <td className="numeric">
                                            {formatNumber(conversionRate, 2)}%
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(conversionRate, data.prevConversions / data.prevClicks * 100))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('conv') && (
                                          <td className="numeric">
                                            {formatNumber(data.conversions, 1)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.conversions, data.prevConversions))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cpa') && (
                                          <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns')}`}>
                                            {data.conversions >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cpa, data.prevCost / data.prevConversions), true)}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('valeur_conv') && (
                                          <td className="numeric">
                                            €{formatNumber(data.conversionsValue, 2)}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(data.conversionsValue, data.prevConversionsValue))}
                                          </td>
                                        )}
                                        {isShoppingMetricVisible('cos') && (
                                          <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                                            {data.conversionsValue >= 0 && data.cost >= 0 ? `${formatNumber(cos, 1)}%` : '-'}
                                            {pmaxCompareEnabled && renderVariation(calculateVariation(cos, data.prevCost / data.prevConversionsValue * 100), true)}
                                          </td>
                                        )}
                                      </tr>
                                    );
                                  });
                                })()}
                              </tbody>
                              <tfoot>
                                {(() => {
                                  // Calculer les totaux pour tous les libellés filtrés
                                  const filteredProducts = filterProducts(accountData.products);

                                  const totals = filteredProducts.reduce((acc, product) => ({
                                    count: acc.count + 1,
                                    impressions: acc.impressions + (product.impressions || 0),
                                    clicks: acc.clicks + (product.clicks || 0),
                                    cost: acc.cost + (product.cost || 0),
                                    conversions: acc.conversions + (product.conversions || 0),
                                    conversionsValue: acc.conversionsValue + (product.conversionsValue || 0),
                                    // Totaux période précédente
                                    prevImpressions: acc.prevImpressions + (product.previousPeriod?.impressions || 0),
                                    prevClicks: acc.prevClicks + (product.previousPeriod?.clicks || 0),
                                    prevCost: acc.prevCost + (product.previousPeriod?.cost || 0),
                                    prevConversions: acc.prevConversions + (product.previousPeriod?.conversions || 0),
                                    prevConversionsValue: acc.prevConversionsValue + (product.previousPeriod?.conversionsValue || 0)
                                  }), { count: 0, impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, prevImpressions: 0, prevClicks: 0, prevCost: 0, prevConversions: 0, prevConversionsValue: 0 });

                                  const avgCtr = totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0;
                                  const avgCpc = totals.clicks > 0 ? totals.cost / totals.clicks : 0;
                                  const avgConversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0;
                                  const avgCpa = totals.conversions > 0 ? totals.cost / totals.conversions : 0;
                                  const avgCos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue) * 100 : 0;

                                  // Métriques période précédente
                                  const prevCtr = totals.prevImpressions > 0 ? (totals.prevClicks / totals.prevImpressions) * 100 : 0;
                                  const prevCpc = totals.prevClicks > 0 ? totals.prevCost / totals.prevClicks : 0;
                                  const prevConversionRate = totals.prevClicks > 0 ? (totals.prevConversions / totals.prevClicks) * 100 : 0;
                                  const prevCpa = totals.prevConversions > 0 ? totals.prevCost / totals.prevConversions : 0;
                                  const prevCos = totals.prevConversionsValue > 0 ? (totals.prevCost / totals.prevConversionsValue) * 100 : 0;

                                  return (
                                    <tr style={{
                                      backgroundColor: '#f8f9fa',
                                      borderTop: '2px solid #e5e7eb',
                                      fontWeight: '600'
                                    }}>
                                      <td style={{ padding: '12px 8px' }} colSpan="2">
                                        <strong>TOTAL</strong>
                                      </td>
                                      <td style={{ textAlign: 'center', padding: '12px 8px' }}>-</td>
                                      <td className="numeric" style={{ padding: '12px 8px' }}>{formatNumber(totals.count)}</td>
                                      {isShoppingMetricVisible('impressions') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.impressions)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.impressions, totals.prevImpressions))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('clics') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.clicks)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.clicks, totals.prevClicks))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('ctr') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(avgCtr, 2)}%
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCtr, prevCtr))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cpc') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(avgCpc, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpc, prevCpc), true)}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cout') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(totals.cost, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.cost, totals.prevCost))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('taux_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(avgConversionRate, 2)}%
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgConversionRate, prevConversionRate))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {formatNumber(totals.conversions, 1)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversions, totals.prevConversions))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cpa') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {totals.conversions >= 0 ? `€${formatNumber(avgCpa, 2)}` : '-'}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCpa, prevCpa), true)}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('valeur_conv') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          €{formatNumber(totals.conversionsValue, 2)}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(totals.conversionsValue, totals.prevConversionsValue))}
                                        </td>
                                      )}
                                      {isShoppingMetricVisible('cos') && (
                                        <td className="numeric" style={{ padding: '12px 8px' }}>
                                          {totals.conversionsValue >= 0 ? `${formatNumber(avgCos, 1)}%` : '-'}
                                          {pmaxCompareEnabled && renderVariation(calculateVariation(avgCos, prevCos), true)}
                                        </td>
                                      )}
                                    </tr>
                                  );
                                })()}
                              </tfoot>
                            </table>
                          </div>

                          {/* Contrôles de pagination pour les libellés */}
                          {(() => {
                            // Calculer avec les données filtrées
                            const filteredProducts = filterProducts(accountData.products);
                            
                            // Agréger par libellé et pays (même logique que dans le tableau)
                            const labelCountryAggregation = {};
                            
                            filteredProducts.forEach(product => {
                              const getProductLabelsWithType = (product) => {
                                const labelsWithType = [];
                                
                                if (product.customLabel0) labelsWithType.push({ type: 'customLabel0', value: product.customLabel0 });
                                if (product.customLabel1) labelsWithType.push({ type: 'customLabel1', value: product.customLabel1 });
                                if (product.customLabel2) labelsWithType.push({ type: 'customLabel2', value: product.customLabel2 });
                                if (product.customLabel3) labelsWithType.push({ type: 'customLabel3', value: product.customLabel3 });
                                if (product.customLabel4) labelsWithType.push({ type: 'customLabel4', value: product.customLabel4 });
                                
                                if (labelsWithType.length === 0) {
                                  labelsWithType.push({ type: 'Aucun', value: 'Sans libellé' });
                                }
                                
                                return labelsWithType;
                              };
                              
                              const labels = getProductLabelsWithType(product);
                              const country = product.feedLabelOrCountry || product.targetCountry || 'FR';
                              
                              labels.forEach(({ type, value }) => {
                                const key = `${type}|${value}|${country}`;
                                
                                if (!labelCountryAggregation[key]) {
                                  labelCountryAggregation[key] = {
                                    labelType: type,
                                    label: value,
                                    country: country,
                                    count: 0,
                                    impressions: 0,
                                    clicks: 0,
                                    cost: 0,
                                    conversions: 0,
                                    conversionsValue: 0
                                  };
                                }
                                
                                const data = labelCountryAggregation[key];
                                data.count += 1;
                                data.impressions += product.impressions || 0;
                                data.clicks += product.clicks || 0;
                                data.cost += product.cost || 0;
                                data.conversions += product.conversions || 0;
                                data.conversionsValue += product.conversionsValue || 0;
                              });
                            });
                            
                            const allLabelData = Object.values(labelCountryAggregation);
                            const totalLabels = allLabelData.length;
                            const labelTotalPages = Math.ceil(totalLabels / labelsItemsPerPage);
                            const labelStartIndex = (labelsCurrentPage - 1) * labelsItemsPerPage;
                            const labelEndIndex = Math.min(labelStartIndex + labelsItemsPerPage, totalLabels);
                            
                            return labelTotalPages > 1 && (
                              <div className="pagination-controls">
                                <div className="pagination-info">
                                  <span>
                                    Affichage {labelStartIndex + 1}-{labelEndIndex} sur {totalLabels} libellés
                                  </span>
                                </div>
                                <div className="pagination-buttons">
                                  <button 
                                    onClick={() => setLabelsCurrentPage(prev => Math.max(1, prev - 1))}
                                    disabled={labelsCurrentPage === 1}
                                    className="pagination-btn"
                                  >
                                    ← Précédent
                                  </button>
                                  <span className="page-numbers">
                                    Page {labelsCurrentPage} sur {labelTotalPages}
                                  </span>
                                  <button 
                                    onClick={() => setLabelsCurrentPage(prev => Math.min(labelTotalPages, prev + 1))}
                                    disabled={labelsCurrentPage === labelTotalPages}
                                    className="pagination-btn"
                                  >
                                    Suivant →
                                  </button>
                                </div>
                              </div>
                            );
                          })()}
                        </div>
                      )}

                      {/* Conteneur pour les deux graphiques côte à côte */}
                      {accountData.products && accountData.products.length > 0 && (() => {
                        // Calculer le nombre de pays uniques
                        const uniqueCountries = [...new Set(
                          accountData.products.map(product => 
                            product.feedLabelOrCountry || product.targetCountry || 'FR'
                          )
                        )];
                        
                        // Afficher seulement s'il y a au moins 2 pays ET si aucun pays spécifique n'est sélectionné
                        if (uniqueCountries.length < 2 || selectedCountryFilter !== 'all') {
                          return null;
                        }
                        
                        return (
                        <div style={{ 
                          marginTop: '32px',
                          display: 'grid',
                          gridTemplateColumns: '1fr 1fr',
                          gap: '24px'
                        }}>
                          {/* Graphique de ventilation par pays */}
                          <div style={{ 
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '24px'
                          }}>
                            <h5 style={{ 
                              fontSize: '18px', 
                              fontWeight: '600', 
                              color: '#1f2937', 
                              marginBottom: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              🌍 Produits affichés sur la période / pays
                            </h5>
                          
                          {(() => {
                            // Utiliser les données filtrées pour le pie chart
                            const filteredProducts = selectedCountryFilter === 'all' 
                              ? accountData.products
                              : accountData.products.filter(product => 
                                  (product.feedLabelOrCountry || product.targetCountry || 'FR') === selectedCountryFilter
                                );
                            
                            // Calculer la répartition par pays avec les données filtrées
                            const countryBreakdown = filteredProducts.reduce((acc, product) => {
                              const country = product.feedLabelOrCountry || 'FR';
                              if (!acc[country]) {
                                acc[country] = {
                                  count: 0,
                                  cost: 0,
                                  impressions: 0,
                                  clicks: 0,
                                  conversions: 0
                                };
                              }
                              acc[country].count += 1;
                              acc[country].cost += product.cost || 0;
                              acc[country].impressions += product.impressions || 0;
                              acc[country].clicks += product.clicks || 0;
                              acc[country].conversions += product.conversions || 0;
                              return acc;
                            }, {});

                            const totalProducts = filteredProducts.length;
                            const countries = Object.entries(countryBreakdown)
                              .sort(([,a], [,b]) => b.count - a.count);

                            // Couleurs pour les pays (palette harmonieuse)
                            const colors = [
                              '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
                              '#8B5CF6', '#06B6D4', '#F97316', '#84CC16',
                              '#EC4899', '#6366F1', '#14B8A6', '#F87171'
                            ];

                            return (
                              <div style={{ 
                                display: 'grid',
                                gridTemplateColumns: '300px 1fr',
                                gap: '32px',
                                alignItems: 'flex-start'
                              }}>
                                {/* Pie Chart */}
                                <div style={{ position: 'relative' }}>
                                  <svg width="300" height="300" viewBox="0 0 300 300">
                                    {(() => {
                                      let currentAngle = 0;
                                      const centerX = 150;
                                      const centerY = 150;
                                      const radius = 120;

                                      return countries.map(([country, data], index) => {
                                        const percentage = (data.count / totalProducts) * 100;
                                        const angle = (data.count / totalProducts) * 360;
                                        const startAngle = currentAngle;
                                        const endAngle = currentAngle + angle;
                                        
                                        currentAngle += angle;
                                        
                                        const startAngleRad = (startAngle - 90) * (Math.PI / 180);
                                        const endAngleRad = (endAngle - 90) * (Math.PI / 180);
                                        
                                        const x1 = centerX + radius * Math.cos(startAngleRad);
                                        const y1 = centerY + radius * Math.sin(startAngleRad);
                                        const x2 = centerX + radius * Math.cos(endAngleRad);
                                        const y2 = centerY + radius * Math.sin(endAngleRad);
                                        
                                        const largeArcFlag = angle > 180 ? 1 : 0;
                                        
                                        const pathData = [
                                          `M ${centerX} ${centerY}`,
                                          `L ${x1} ${y1}`,
                                          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                                          `Z`
                                        ].join(' ');

                                        // Position du label au milieu du segment
                                        const midAngle = (startAngle + endAngle) / 2;
                                        const midAngleRad = (midAngle - 90) * (Math.PI / 180);
                                        const labelRadius = radius * 0.7; // Position du label
                                        const labelX = centerX + labelRadius * Math.cos(midAngleRad);
                                        const labelY = centerY + labelRadius * Math.sin(midAngleRad);

                                        return (
                                          <g key={country}>
                                            <path
                                              d={pathData}
                                              fill={colors[index % colors.length]}
                                              stroke="#ffffff"
                                              strokeWidth="2"
                                              style={{ 
                                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                                              }}
                                            />
                                            {/* Afficher le label seulement si le segment est assez grand */}
                                            {percentage > 5 && (
                                              <text
                                                x={labelX}
                                                y={labelY}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fill="#ffffff"
                                                fontSize="12"
                                                fontWeight="600"
                                                style={{
                                                  textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                                                  filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.7))'
                                                }}
                                              >
                                                {percentage.toFixed(1)}%
                                              </text>
                                            )}
                                          </g>
                                        );
                                      });
                                    })()}
                                  </svg>
                                </div>

                                {/* Légende et statistiques */}
                                <div>
                                  <div style={{ 
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '12px'
                                  }}>
                                    {countries.map(([country, data], index) => {
                                      const percentage = ((data.count / totalProducts) * 100).toFixed(1);
                                      return (
                                        <div key={country} style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '12px',
                                          padding: '12px 16px',
                                          backgroundColor: '#f8f9fa',
                                          borderRadius: '8px',
                                          border: '1px solid #e5e7eb'
                                        }}>
                                          <div style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%',
                                            backgroundColor: colors[index % colors.length],
                                            flexShrink: 0
                                          }}></div>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ 
                                              fontWeight: '600', 
                                              color: '#1f2937',
                                              fontSize: '14px'
                                            }}>
                                              {country} ({percentage}%)
                                            </div>
                                            <div style={{ 
                                              fontSize: '12px', 
                                              color: '#6b7280',
                                              marginTop: '2px'
                                            }}>
                                              {data.count} produits • €{(data.cost || 0).toLocaleString('fr-FR', { minimumFractionDigits: 2 })}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                            </div>

                          {/* Graphique de conversions par pays */}
                          <div style={{ 
                            backgroundColor: '#ffffff',
                            border: '1px solid #e5e7eb',
                            borderRadius: '12px',
                            padding: '24px'
                          }}>
                            <h5 style={{ 
                              fontSize: '18px', 
                              fontWeight: '600', 
                              color: '#1f2937', 
                              marginBottom: '20px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '8px'
                            }}>
                              <img src={googleAdsLogo} alt="Google Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> Conversions sur la période / pays
                            </h5>
                          
                          {(() => {
                            // Utiliser les données filtrées pour le pie chart
                            const filteredProducts = selectedCountryFilter === 'all' 
                              ? accountData.products
                              : accountData.products.filter(product => 
                                  (product.feedLabelOrCountry || product.targetCountry || 'FR') === selectedCountryFilter
                                );
                            
                            // Calculer la répartition par pays avec les données filtrées
                            const countryBreakdown = filteredProducts.reduce((acc, product) => {
                              const country = product.feedLabelOrCountry || 'FR';
                              if (!acc[country]) {
                                acc[country] = {
                                  count: 0,
                                  cost: 0,
                                  impressions: 0,
                                  clicks: 0,
                                  conversions: 0,
                                  conversionsValue: 0
                                };
                              }
                              acc[country].count += 1;
                              acc[country].cost += product.cost || 0;
                              acc[country].impressions += product.impressions || 0;
                              acc[country].clicks += product.clicks || 0;
                              acc[country].conversions += product.conversions || 0;
                              acc[country].conversionsValue += product.conversionsValue || 0;
                              return acc;
                            }, {});

                            const totalConversions = Object.values(countryBreakdown).reduce((sum, data) => sum + data.conversions, 0);
                            const countries = Object.entries(countryBreakdown)
                              .filter(([country, data]) => data.conversions > 0) // Ne montrer que les pays avec des conversions
                              .sort(([,a], [,b]) => b.conversions - a.conversions);

                            // Couleurs pour les pays (palette harmonieuse)
                            const colors = [
                              '#3B82F6', '#EF4444', '#10B981', '#F59E0B', 
                              '#8B5CF6', '#06B6D4', '#F97316', '#84CC16',
                              '#EC4899', '#6366F1', '#14B8A6', '#F87171'
                            ];

                            if (totalConversions === 0) {
                              return (
                                <div style={{
                                  textAlign: 'center',
                                  padding: '40px',
                                  color: '#6b7280'
                                }}>
                                  <p>Aucune conversion sur la période sélectionnée</p>
                                </div>
                              );
                            }

                            return (
                              <div style={{ 
                                display: 'grid',
                                gridTemplateColumns: '300px 1fr',
                                gap: '32px',
                                alignItems: 'flex-start'
                              }}>
                                {/* Pie Chart */}
                                <div style={{ position: 'relative' }}>
                                  <svg width="300" height="300" viewBox="0 0 300 300">
                                    {(() => {
                                      let currentAngle = 0;
                                      const centerX = 150;
                                      const centerY = 150;
                                      const radius = 120;

                                      return countries.map(([country, data], index) => {
                                        const percentage = (data.conversions / totalConversions) * 100;
                                        const angle = (data.conversions / totalConversions) * 360;
                                        const startAngle = currentAngle;
                                        const endAngle = currentAngle + angle;
                                        
                                        currentAngle += angle;
                                        
                                        const startAngleRad = (startAngle - 90) * (Math.PI / 180);
                                        const endAngleRad = (endAngle - 90) * (Math.PI / 180);
                                        
                                        const x1 = centerX + radius * Math.cos(startAngleRad);
                                        const y1 = centerY + radius * Math.sin(startAngleRad);
                                        const x2 = centerX + radius * Math.cos(endAngleRad);
                                        const y2 = centerY + radius * Math.sin(endAngleRad);
                                        
                                        const largeArcFlag = angle > 180 ? 1 : 0;
                                        
                                        const pathData = [
                                          `M ${centerX} ${centerY}`,
                                          `L ${x1} ${y1}`,
                                          `A ${radius} ${radius} 0 ${largeArcFlag} 1 ${x2} ${y2}`,
                                          `Z`
                                        ].join(' ');

                                        // Position du label au milieu du segment
                                        const midAngle = (startAngle + endAngle) / 2;
                                        const midAngleRad = (midAngle - 90) * (Math.PI / 180);
                                        const labelRadius = radius * 0.7; // Position du label
                                        const labelX = centerX + labelRadius * Math.cos(midAngleRad);
                                        const labelY = centerY + labelRadius * Math.sin(midAngleRad);

                                        return (
                                          <g key={country}>
                                            <path
                                              d={pathData}
                                              fill={colors[index % colors.length]}
                                              stroke="#ffffff"
                                              strokeWidth="2"
                                              style={{ 
                                                filter: 'drop-shadow(0 1px 2px rgba(0,0,0,0.1))'
                                              }}
                                            />
                                            {/* Afficher le label seulement si le segment est assez grand */}
                                            {percentage > 5 && (
                                              <text
                                                x={labelX}
                                                y={labelY}
                                                textAnchor="middle"
                                                dominantBaseline="middle"
                                                fill="#ffffff"
                                                fontSize="12"
                                                fontWeight="600"
                                                style={{
                                                  textShadow: '1px 1px 2px rgba(0,0,0,0.7)',
                                                  filter: 'drop-shadow(1px 1px 2px rgba(0,0,0,0.7))'
                                                }}
                                              >
                                                {percentage.toFixed(1)}%
                                              </text>
                                            )}
                                          </g>
                                        );
                                      });
                                    })()}
                                  </svg>
                                </div>

                                {/* Légende et statistiques */}
                                <div>
                                  <div style={{ 
                                    display: 'grid',
                                    gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
                                    gap: '12px'
                                  }}>
                                    {countries.map(([country, data], index) => {
                                      const percentage = ((data.conversions / totalConversions) * 100).toFixed(1);
                                      const conversionRate = data.clicks > 0 ? ((data.conversions / data.clicks) * 100).toFixed(1) : 0;
                                      return (
                                        <div key={country} style={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: '12px',
                                          padding: '12px 16px',
                                          backgroundColor: '#f8f9fa',
                                          borderRadius: '8px',
                                          border: '1px solid #e5e7eb'
                                        }}>
                                          <div style={{
                                            width: '16px',
                                            height: '16px',
                                            borderRadius: '50%',
                                            backgroundColor: colors[index % colors.length],
                                            flexShrink: 0
                                          }}></div>
                                          <div style={{ flex: 1, minWidth: 0 }}>
                                            <div style={{ 
                                              fontWeight: '600', 
                                              color: '#1f2937',
                                              fontSize: '14px'
                                            }}>
                                              {country} ({percentage}%)
                                            </div>
                                            <div style={{ 
                                              fontSize: '12px', 
                                              color: '#6b7280',
                                              marginTop: '2px'
                                            }}>
                                              {Math.round(data.conversions)} conv. • Taux Conv. {conversionRate}% • €{Math.round(data.conversionsValue || 0).toLocaleString('fr-FR')}
                                            </div>
                                          </div>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </div>
                            );
                          })()}
                          </div>
                        </div>
                        );
                      })()}

                    </>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div style={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '60px 20px',
              minHeight: '400px',
              background: 'white',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)'
            }}>
              <div style={{ marginBottom: '24px', opacity: 0.6 }}><ShoppingCart size={64} color="#6b7280" /></div>
              <h2 style={{
                margin: '0 0 12px 0',
                fontSize: '1.5em',
                color: '#1f2937',
                fontWeight: '600'
              }}>
                Aucun produit actif dans le compte
              </h2>
              <p style={{
                color: '#6b7280',
                fontSize: '1em',
                marginBottom: '24px',
                textAlign: 'center',
                maxWidth: '500px',
                lineHeight: '1.6'
              }}>
                Ce compte ne contient pas de produits et n'a enregistré aucune impression sur la période sélectionnée.
              </p>
              <div style={{
                background: '#f3f4f6',
                borderRadius: '8px',
                padding: '16px 24px',
                fontSize: '0.9em',
                color: '#4b5563'
              }}>
                <strong>Pour utiliser ce dashboard :</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                  <li>Vérifiez que le compte possède des campagnes Shopping actives</li>
                  <li>Assurez-vous que les produits sont synchronisés via Merchant Center</li>
                  <li>Essayez une période plus longue si les campagnes sont récentes</li>
                </ul>
              </div>
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* Modal de détails produit - Portal pour passer au-dessus du header */}
      {showProductModal && selectedProduct && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 100000,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: '40px 20px 20px 20px',
            overflowY: 'auto'
          }}
          onClick={closeProductModal}
        >
          <div
            style={{
              backgroundColor: '#FAF9F5',
              borderRadius: '12px',
              maxWidth: '1400px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              border: '1px solid #E8E6E1'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête de la modal - Charte Kokpit */}
            <div style={{
              background: 'linear-gradient(135deg, #FAF9F5 0%, #F5F4F0 100%)',
              padding: '20px 28px',
              borderBottom: '1px solid #E8E6E1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                margin: '0',
                maxWidth: '600px'
              }}>
                {selectedProduct.title || `Produit ${selectedProduct.itemId}`}
              </h2>
              
              {/* Étiquettes de disponibilité et statut dans le header */}
              <div style={{ display: 'flex', gap: '12px', alignItems: 'center', marginLeft: 'auto', marginRight: '8px' }}>
                {/* Étiquette de disponibilité - Style exact du tableau mais plus grosse */}
                <span style={{
                  fontSize: '15px',
                  fontWeight: '600',
                  padding: '6px 12px',
                  borderRadius: '6px',
                  backgroundColor: (() => {
                    const av = (selectedProduct.availability || '').toLowerCase().replace(/[_\s]/g, '');
                    if (av === 'instock') return '#d1fae5';
                    if (av === 'outofstock') return '#fee2e2';
                    if (av === 'preorder') return '#fef3c7';
                    if (av === 'backorder') return '#dbeafe';
                    if (!selectedProduct.availability || selectedProduct.availability === 'no_data') return '#e5e7eb';
                    return '#f3f4f6';
                  })(),
                  color: (() => {
                    const av = (selectedProduct.availability || '').toLowerCase().replace(/[_\s]/g, '');
                    if (av === 'instock') return '#065f46';
                    if (av === 'outofstock') return '#dc2626';
                    if (av === 'preorder') return '#92400e';
                    if (av === 'backorder') return '#1d4ed8';
                    if (!selectedProduct.availability || selectedProduct.availability === 'no_data') return '#6b7280';
                    return '#374151';
                  })()
                }}>
                  {(() => {
                    const av = (selectedProduct.availability || '').toLowerCase().replace(/[_\s]/g, '');
                    if (av === 'instock') return 'En stock';
                    if (av === 'outofstock') return 'Rupture';
                    if (av === 'preorder') return 'Précommande';
                    if (av === 'backorder') return 'Réappro.';
                    if (!selectedProduct.availability || selectedProduct.availability === 'no_data') return 'Retiré du flux';
                    return selectedProduct.availability || 'Inconnu';
                  })()}
                </span>
                
                {/* Étiquette de statut - Style exact du tableau avec même logique getProductStatus mais plus grosse */}
                {(() => {
                  // Reproduire exactement la logique de getProductStatus du tableau
                  const getProductStatus = (product) => {
                    // Utiliser directement le champ 'status' retourné par l'API
                    if (product.status) {
                      switch (product.status) {
                        case 'approved':
                          return { status: 'Approuvé', color: '#059669', bgColor: '#d1fae5', icon: '' };
                        case 'rejected':
                        case 'disapproved':
                          return { status: 'Refusé', color: '#dc2626', bgColor: '#fee2e2', icon: '' };
                        case 'pending':
                          return { status: 'En attente', color: '#d97706', bgColor: '#fef3c7', icon: '' };
                        case 'unknown':
                          return { status: 'Inconnu', color: '#6b7280', bgColor: '#f3f4f6', icon: '' };
                        default:
                          return { status: 'Inconnu', color: '#6b7280', bgColor: '#f3f4f6', icon: '' };
                      }
                    }

                    // Vérifier les statuts de destination (Google Shopping Ads)
                    if (product.productStatus && product.productStatus.destinationStatuses) {
                      const shoppingStatus = product.productStatus.destinationStatuses.find(dest =>
                        dest.destination === 'Shopping_ads' || dest.destination === 'Shopping ads'
                      );
                      if (shoppingStatus) {
                        switch (shoppingStatus.status) {
                          case 'approved':
                            return { status: 'Approuvé', color: '#059669', bgColor: '#d1fae5', icon: '' };
                          case 'disapproved':
                            return { status: 'Refusé', color: '#dc2626', bgColor: '#fee2e2', icon: '' };
                          case 'pending':
                            return { status: 'En attente', color: '#d97706', bgColor: '#fef3c7', icon: '' };
                        }
                      }
                    }
                    
                    // Fallback sur merchantStatus
                    if (product.merchantStatus) {
                      switch (product.merchantStatus) {
                        case 'approved':
                          return { status: 'Approuvé', color: '#059669', bgColor: '#d1fae5', icon: '' };
                        case 'disapproved':
                          return { status: 'Refusé', color: '#dc2626', bgColor: '#fee2e2', icon: '' };
                        case 'pending':
                          return { status: 'En attente', color: '#d97706', bgColor: '#fef3c7', icon: '' };
                      }
                    }

                    // Statut par défaut si aucun statut trouvé
                    return { status: 'Retiré du flux', color: '#6b7280', bgColor: '#f3f4f6', icon: '' };
                  };

                  const statusInfo = getProductStatus(selectedProduct);
                  return (
                    <span style={{
                      fontSize: '15px',
                      fontWeight: '600',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      backgroundColor: statusInfo.bgColor,
                      color: statusInfo.color
                    }}>
                      {statusInfo.icon} {statusInfo.status}
                    </span>
                  );
                })()}
              </div>
              
              <button
                onClick={closeProductModal}
                style={{
                  background: '#E8E6E1',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: '#6b7280',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => { e.target.style.background = '#d1d5db'; e.target.style.color = '#374151'; }}
                onMouseLeave={(e) => { e.target.style.background = '#E8E6E1'; e.target.style.color = '#6b7280'; }}
              >
                ×
              </button>
            </div>

            {/* Contenu de la modal */}
            <div style={{ padding: '28px', height: 'calc(90vh - 100px)', overflow: 'auto', backgroundColor: 'white' }}>
              {/* Section image et infos principales */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: selectedProduct.imageLink ? '250px 350px 1fr 400px' : '350px 1fr 400px',
                gap: '32px',
                marginBottom: '32px'
              }}>
                {/* Image du produit - PREMIÈRE COLONNE */}
                {selectedProduct.imageLink && (
                  <div style={{
                    backgroundColor: '#FAF9F5',
                    borderRadius: '12px',
                    padding: '16px',
                    textAlign: 'center',
                    border: '1px solid #E8E6E1'
                  }}>
                    <img
                      src={adresseDeVignette(selectedProduct)}
                      alt={selectedProduct.title || 'Produit'}
                      decoding="async"
                      style={{
                        width: '100%',
                        maxWidth: '220px',
                        height: 'auto',
                        maxHeight: '220px',
                        objectFit: 'contain',
                        borderRadius: '8px'
                      }}
                      onError={(e) => {
                        e.target.style.display = 'none';
                      }}
                    />
                  </div>
                )}

                {/* Description du produit - DEUXIÈME COLONNE */}
                <div style={{ marginBottom: '24px' }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <FileText size={18} /> Description
                  </h3>
                  <div style={{ 
                    color: '#6b7280', 
                    fontSize: '15px', 
                    lineHeight: '1.6',
                    maxHeight: '220px',
                    overflowY: 'auto',
                    paddingRight: '8px'
                  }}>
                    {decodeHTMLEntities(selectedProduct.description) || 'Aucune description disponible'}
                  </div>
                </div>

                {/* Informations principales - TROISIÈME COLONNE */}
                <div>
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#1f2937',
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <ClipboardList size={18} /> Informations produit
                    </h3>
                    <div style={{ display: 'grid', gap: '12px' }}>
                      <div>
                        <span style={{ fontWeight: '500', color: '#374151' }}>ID Produit: </span>
                        <span style={{ color: '#6b7280' }}>{selectedProduct.itemId}</span>
                      </div>
                      
                      {selectedProduct.price && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>Prix: </span>
                          <span style={{ color: '#059669', fontWeight: '600' }}>
                            {typeof selectedProduct.price === 'object' && selectedProduct.price.amountMicros
                              ? `${(parseInt(selectedProduct.price.amountMicros) / 1000000).toFixed(2)} ${selectedProduct.price.currencyCode || 'EUR'}`
                              : typeof selectedProduct.price === 'number'
                                ? `${selectedProduct.price.toFixed(2)} ${selectedProduct.currency || 'EUR'}`
                                : null
                            }
                          </span>
                        </div>
                      )}
                      
                      {selectedProduct.brand && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>Marque: </span>
                          <span style={{ color: '#6b7280' }}>{selectedProduct.brand}</span>
                        </div>
                      )}
                      
                      {selectedProduct.condition && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>Condition: </span>
                          <span style={{ color: '#6b7280' }}>
                            {selectedProduct.condition === 'new' ? 'Neuf' :
                             selectedProduct.condition === 'used' ? 'Occasion' :
                             selectedProduct.condition === 'refurbished' ? 'Reconditionné' :
                             selectedProduct.condition}
                          </span>
                        </div>
                      )}
                      
                      {selectedProduct.feedLabelOrCountry && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>Pays/Feed: </span>
                          <span style={{ color: '#6b7280' }}>{selectedProduct.feedLabelOrCountry}</span>
                        </div>
                      )}
                      
                      {selectedProduct.channel && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>Canal: </span>
                          <span style={{ color: '#6b7280' }}>{selectedProduct.channel}</span>
                        </div>
                      )}
                      
                      {selectedProduct.contentLanguage && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>Langue: </span>
                          <span style={{ color: '#6b7280' }}>{selectedProduct.contentLanguage}</span>
                        </div>
                      )}
                      
                      {selectedProduct.gtin && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>GTIN: </span>
                          <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>{selectedProduct.gtin}</span>
                        </div>
                      )}

                      {selectedProduct.mpn && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>MPN: </span>
                          <span style={{ color: '#6b7280', fontFamily: 'monospace' }}>{selectedProduct.mpn}</span>
                        </div>
                      )}

                      {selectedProduct.itemGroupId && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>Groupe d'articles: </span>
                          <span style={{ color: '#6b7280' }}>{selectedProduct.itemGroupId}</span>
                        </div>
                      )}

                      {selectedProduct.googleProductCategory && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>Catégorie Google: </span>
                          <span style={{ color: '#6b7280', fontSize: '13px' }}>{selectedProduct.googleProductCategory}</span>
                        </div>
                      )}

                      {selectedProduct.salePrice && (
                        <div>
                          <span style={{ fontWeight: '500', color: '#374151' }}>Prix soldé: </span>
                          <span style={{ color: '#dc2626', fontWeight: '600' }}>
                            {typeof selectedProduct.salePrice === 'object' && selectedProduct.salePrice.amountMicros
                              ? `${(parseInt(selectedProduct.salePrice.amountMicros) / 1000000).toFixed(2)} ${selectedProduct.salePrice.currencyCode || 'EUR'}`
                              : typeof selectedProduct.salePrice === 'number'
                                ? `${selectedProduct.salePrice.toFixed(2)} ${selectedProduct.currency || 'EUR'}`
                                : null
                            }
                          </span>
                        </div>
                      )}

                      {selectedProduct.link && (
                        <div>
                          <a
                            href={selectedProduct.link}
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              color: '#333333',
                              textDecoration: 'none',
                              fontSize: '14px'
                            }}
                            onMouseOver={(e) => e.target.style.textDecoration = 'underline'}
                            onMouseOut={(e) => e.target.style.textDecoration = 'none'}
                          >
                            <ExternalLink size={14} style={{ marginRight: '4px' }} /> Voir la page produit
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Colonne des libellés personnalisés - QUATRIÈME COLONNE */}
                <div>
                  {/* Section Custom Labels */}
                  {(selectedProduct.customLabel0 || selectedProduct.customLabel1 || selectedProduct.customLabel2 || selectedProduct.customLabel3 || selectedProduct.customLabel4) && (
                    <div style={{ marginBottom: '24px' }}>
                      <h4 style={{
                        fontSize: '16px',
                        fontWeight: '600',
                        color: '#374151',
                        marginBottom: '12px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <Tag size={16} /> Libellés personnalisés
                      </h4>
                      <div style={{ display: 'grid', gap: '8px' }}>
                        {selectedProduct.customLabel0 && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '80px' }}>Label 0:</span>
                            <span style={{ 
                              fontSize: '12px', 
                              padding: '2px 8px', 
                              backgroundColor: '#dbeafe', 
                              color: '#1e40af', 
                              borderRadius: '12px',
                              fontWeight: '500'
                            }}>
                              {selectedProduct.customLabel0}
                            </span>
                          </div>
                        )}
                        {selectedProduct.customLabel1 && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '80px' }}>Label 1:</span>
                            <span style={{ 
                              fontSize: '12px', 
                              padding: '2px 8px', 
                              backgroundColor: '#dcfce7', 
                              color: '#166534', 
                              borderRadius: '12px',
                              fontWeight: '500'
                            }}>
                              {selectedProduct.customLabel1}
                            </span>
                          </div>
                        )}
                        {selectedProduct.customLabel2 && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '80px' }}>Label 2:</span>
                            <span style={{ 
                              fontSize: '12px', 
                              padding: '2px 8px', 
                              backgroundColor: '#fed7d7', 
                              color: '#c53030', 
                              borderRadius: '12px',
                              fontWeight: '500'
                            }}>
                              {selectedProduct.customLabel2}
                            </span>
                          </div>
                        )}
                        {selectedProduct.customLabel3 && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '80px' }}>Label 3:</span>
                            <span style={{ 
                              fontSize: '12px', 
                              padding: '2px 8px', 
                              backgroundColor: '#fef3cd', 
                              color: '#b45309', 
                              borderRadius: '12px',
                              fontWeight: '500'
                            }}>
                              {selectedProduct.customLabel3}
                            </span>
                          </div>
                        )}
                        {selectedProduct.customLabel4 && (
                          <div style={{ display: 'flex', gap: '8px' }}>
                            <span style={{ fontSize: '12px', color: '#6b7280', minWidth: '80px' }}>Label 4:</span>
                            <span style={{ 
                              fontSize: '12px', 
                              padding: '2px 8px', 
                              backgroundColor: '#e9d5ff', 
                              color: '#6b21a8', 
                              borderRadius: '12px',
                              fontWeight: '500'
                            }}>
                              {selectedProduct.customLabel4}
                            </span>
                          </div>
                        )}
                      </div>
                    </div>
                  )}


                  {/* Lien vers Google Merchant Center */}
                  {selectedProduct.merchantCenterUrl && (
                    <div>
                      <a
                        href={selectedProduct.merchantCenterUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '10px',
                          color: '#333333',
                          textDecoration: 'none',
                          fontWeight: '500',
                          padding: '8px 16px',
                          backgroundColor: '#FAF9F5',
                          borderRadius: '8px',
                          border: '1px solid #dbeafe'
                        }}
                        onMouseOver={(e) => e.target.style.backgroundColor = '#dbeafe'}
                        onMouseOut={(e) => e.target.style.backgroundColor = '#FAF9F5'}
                      >
                        <ExternalLink size={14} style={{ marginRight: '4px' }} /> Voir dans Google Merchant Center
                      </a>
                    </div>
                  )}
                </div>
              </div>

              {/* Section performances */}

              {/* Section campagnes - données dynamiques avec filtres appliqués */}
              <div style={{
                marginTop: '24px',
                backgroundColor: '#FAF9F5',
                borderRadius: '12px',
                padding: '24px',
                border: '1px solid #E8E6E1'
              }}>
                <h3 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginTop: '0',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Target size={18} /> Campagnes associées
                  {(selectedCountryFilter !== 'all' || selectedProductTypeFilter !== 'all' || selectedLabelFilter !== 'all') && (
                    <span style={{
                      fontSize: '12px',
                      color: '#6b7280',
                      fontWeight: '400',
                      marginLeft: '8px'
                    }}>
                      (avec filtres appliqués)
                    </span>
                  )}
                </h3>
                
                {loadingProductCampaigns ? (
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '32px',
                    color: '#6b7280'
                  }}>
                    <div style={{ marginRight: '12px' }}><Clock size={16} /></div>
                    Chargement des campagnes...
                  </div>
                ) : productCampaignDetails && productCampaignDetails.campaigns && productCampaignDetails.campaigns.length > 0 ? (
                  <div>

                    {/* Tableau des campagnes - Format identique au dashboard Global */}
                    {(() => {
                      // Calculer les totaux pour le pied de tableau
                      const totals = productCampaignDetails.campaigns.reduce((acc, campaign) => ({
                        impressions: acc.impressions + (campaign.impressions || 0),
                        clicks: acc.clicks + (campaign.clicks || 0),
                        cost: acc.cost + (campaign.cost || 0),
                        conversions: acc.conversions + (campaign.conversions || 0),
                        conversionsValue: acc.conversionsValue + (campaign.conversionsValue || 0)
                      }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });
                      
                      // Calculer les moyennes pour CTR, CPC, taux de conversion, CPA et COS
                      totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
                      totals.cpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
                      totals.conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
                      totals.cpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
                      totals.cos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;
                      
                      return (
                        <div className="campaigns-table-wrapper">
                          <table className="campaigns-table">
                          <thead>
                            <tr>
                              <th className="sortable">
                                Campagne
                              </th>
                              <th className="sortable numeric">
                                Impressions
                              </th>
                              <th className="sortable numeric">
                                CTR
                              </th>
                              <th className="sortable numeric">
                                Clics
                              </th>
                              <th className="sortable numeric">
                                CPC
                              </th>
                              <th className="sortable numeric">
                                Coût
                              </th>
                              <th className="sortable numeric">
                                Taux Conv.
                              </th>
                              <th className="sortable numeric">
                                Conv.
                              </th>
                              <th className="sortable numeric">
                                CPA
                              </th>
                              <th className="sortable numeric">
                                Valeur Conv.
                              </th>
                              <th className="sortable numeric">
                                COS
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {productCampaignDetails.campaigns.map((campaign, index) => {
                              const cos = (campaign.conversionsValue || 0) > 0 ? (campaign.cost / (campaign.conversionsValue || 1) * 100) : 0;
                              const conversionRate = campaign.clicks > 0 ? (campaign.conversions / campaign.clicks * 100) : 0;
                              const cpa = campaign.conversions > 0 ? (campaign.cost / campaign.conversions) : 0;
                              const ctr = campaign.impressions > 0 ? (campaign.clicks / campaign.impressions * 100) : 0;
                              const cpc = campaign.clicks > 0 ? (campaign.cost / campaign.clicks) : 0;
                              
                              return (
                                <tr key={campaign.campaignId || index}>
                                  <td className="campaign-name">{campaign.campaignName || campaign.name}</td>
                                  <td className="numeric">{formatNumber(campaign.impressions || 0)}</td>
                                  <td className="numeric">{formatNumber(ctr, 2)}%</td>
                                  <td className="numeric">{formatNumber(campaign.clicks || 0)}</td>
                                  <td className="numeric">€{formatNumber(cpc, 2)}</td>
                                  <td className="numeric">€{formatNumber(campaign.cost || 0, 2)}</td>
                                  <td className="numeric">{formatNumber(conversionRate, 2)}%</td>
                                  <td className="numeric">{formatNumber(campaign.conversions || 0, 1)}</td>
                                  <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns')}`}>{cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}</td>
                                  <td className="numeric">€{formatNumber(campaign.conversionsValue || 0, 2)}</td>
                                  <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                                    {formatNumber(cos, 1)}%
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                          <tfoot>
                            <tr className="totals-row">
                              <td className="campaign-name"><strong>TOTAL</strong></td>
                              <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>
                              <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                              <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>
                              <td className="numeric"><strong>€{formatNumber(totals.cpc, 2)}</strong></td>
                              <td className="numeric"><strong>€{formatNumber(totals.cost, 2)}</strong></td>
                              <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                              <td className="numeric"><strong>{formatNumber(totals.conversions, 1)}</strong></td>
                              <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', 'campaigns')}`}><strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong></td>
                              <td className="numeric"><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></td>
                              <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                                <strong>{formatNumber(totals.cos, 1)}%</strong>
                              </td>
                            </tr>
                          </tfoot>
                          </table>
                        </div>
                      );
                    })()}
                  </div>
                ) : productCampaignDetails && productCampaignDetails.campaigns && productCampaignDetails.campaigns.length === 0 ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '32px',
                    color: '#6b7280'
                  }}>
                    <div style={{ marginBottom: '12px' }}><ClipboardList size={32} color="#6b7280" /></div>
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                      Aucune campagne trouvée
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      Ce produit n'a pas de données pour les filtres appliqués sur la période sélectionnée
                    </div>
                  </div>
                ) : (
                  <div style={{
                    backgroundColor: '#fef7cd',
                    border: '1px solid #fbbf24',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '14px'
                  }}>
                    <div style={{ color: '#92400e', marginBottom: '4px', fontWeight: '500' }}>
                      <AlertCircle size={14} style={{ display: 'inline', marginRight: '4px' }} />Données non chargées
                    </div>
                    <div style={{ color: '#92400e' }}>
                      Les détails des campagnes n'ont pas pu être chargés. Veuillez réessayer.
                    </div>
                  </div>
                )}
              </div>

              {/* Graphique de performance temporelle - exactement comme dans le dashboard Global */}
              {productCampaignDetails && productCampaignDetails.campaigns && productCampaignDetails.campaigns.length > 0 && (
                <div style={{
                  marginTop: '24px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginTop: '0',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <TrendingUp size={18} /> Évolution temporelle
                  </h3>
                  
                  {loadingProductHistorical ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '60px',
                      color: '#6b7280'
                    }}>
                      <div style={{ marginRight: '12px' }}><Clock size={16} /></div>
                      Chargement des données historiques...
                    </div>
                  ) : productHistoricalData && productHistoricalData.length > 0 ? (
                    <div style={{ 
                      width: '100%', 
                      height: '400px', 
                      background: 'white', 
                      borderRadius: '12px', 
                      padding: '20px', 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      boxSizing: 'border-box',
                      overflow: 'hidden'
                    }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={productHistoricalData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getDate()}/${date.getMonth() + 1}`;
                            }}
                          />
                          
                          {/* Axe Y gauche pour les clics */}
                          <YAxis 
                            yAxisId="left"
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Clics', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                          />
                          
                          {/* Axe Y droit pour les conversions */}
                          <YAxis 
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Conversions', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                          />
                          
                          <Tooltip 
                            formatter={(value, name) => {
                              if (name === 'Clics') return formatNumber(value);
                              if (name === 'Conversions') return formatNumber(value, 2);
                              return value;
                            }}
                            labelFormatter={(label) => {
                              const date = new Date(label);
                              return date.toLocaleDateString('fr-FR', { 
                                weekday: 'short', 
                                day: 'numeric', 
                                month: 'short' 
                              });
                            }}
                          />
                          <Legend />
                          
                          {/* Conversions en histogrammes */}
                          <Bar 
                            yAxisId="right"
                            dataKey="conversions" 
                            fill="#8884d8" 
                            fillOpacity={0.8}
                            name="Conversions"
                            radius={[4, 4, 0, 0]}
                          />
                          
                          {/* Clics en courbe */}
                          <Line 
                            yAxisId="left"
                            type="monotone" 
                            dataKey="clicks" 
                            stroke="#82ca9d" 
                            strokeWidth={3}
                            name="Clics"
                            dot={{ fill: '#82ca9d', strokeWidth: 2, r: 5 }}
                            activeDot={{ r: 7, fill: '#82ca9d' }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#6b7280'
                    }}>
                      Aucune donnée historique disponible pour ce produit
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Product Type Detail - Portal pour passer au-dessus du header */}
      {showProductTypeModal && selectedProductType && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 100000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflowY: 'auto'
          }}
          onClick={closeProductTypeModal}
        >
          <div
            style={{
              backgroundColor: '#FAF9F5',
              borderRadius: '12px',
              maxWidth: '1400px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              border: '1px solid #E8E6E1'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête de la modal - Charte Kokpit */}
            <div style={{
              background: 'linear-gradient(135deg, #FAF9F5 0%, #F5F4F0 100%)',
              padding: '20px 28px',
              borderBottom: '1px solid #E8E6E1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                Détail de la catégorie : "{selectedProductType.fullPath}" ({selectedProductType.country})
              </h2>
              <button
                onClick={closeProductTypeModal}
                style={{
                  background: '#E8E6E1',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: '#6b7280',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#D8D6D1'}
                onMouseLeave={(e) => e.target.style.background = '#E8E6E1'}
              >
                ×
              </button>
            </div>

            {/* Contenu de la modal */}
            <div style={{ padding: '32px', height: 'calc(95vh - 120px)', overflow: 'auto' }}>
              {/* Section Campagnes associées */}
              <div style={{
                backgroundColor: '#ffffff',
                border: '1px solid #e5e7eb',
                borderRadius: '12px',
                padding: '24px'
              }}>
                <h4 style={{
                  fontSize: '18px',
                  fontWeight: '600',
                  color: '#1f2937',
                  marginTop: '0',
                  marginBottom: '16px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px'
                }}>
                  <Target size={18} /> Campagnes associées
                </h4>

                {loadingProductTypeCampaigns ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    padding: '40px'
                  }}>
                    <div>Chargement des campagnes...</div>
                  </div>
                ) : productTypeCampaignsError ? (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#dc2626'
                  }}>
                    ❌ Erreur lors du chargement des campagnes associées
                  </div>
                ) : productTypeCampaignDetails && productTypeCampaignDetails.campaigns ? (
                  (() => {
                    // Calculer les totaux pour le pied de tableau
                    const totals = productTypeCampaignDetails.campaigns.reduce((acc, campaign) => ({
                      impressions: acc.impressions + (campaign.impressions || 0),
                      clicks: acc.clicks + (campaign.clicks || 0),
                      cost: acc.cost + (campaign.cost || 0),
                      conversions: acc.conversions + (campaign.conversions || 0),
                      conversionsValue: acc.conversionsValue + (campaign.conversionsValue || 0)
                    }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });
                    
                    // Calculer les moyennes pour CTR, CPC, taux de conversion, CPA et COS
                    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
                    totals.cpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
                    totals.conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
                    totals.cpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
                    totals.cos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;
                    
                    return (
                      <div className="campaigns-table-wrapper">
                        <table className="campaigns-table">
                        <thead>
                          <tr>
                            <th className="sortable">
                              Campagne
                            </th>
                            <th className="sortable numeric">
                              Impressions
                            </th>
                            <th className="sortable numeric">
                              CTR
                            </th>
                            <th className="sortable numeric">
                              Clics
                            </th>
                            <th className="sortable numeric">
                              CPC
                            </th>
                            <th className="sortable numeric">
                              Coût
                            </th>
                            <th className="sortable numeric">
                              Taux Conv.
                            </th>
                            <th className="sortable numeric">
                              Conv.
                            </th>
                            <th className="sortable numeric">
                              CPA
                            </th>
                            <th className="sortable numeric">
                              Valeur Conv.
                            </th>
                            <th className="sortable numeric">
                              COS
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {productTypeCampaignDetails.campaigns.map((campaign, index) => {
                            const cos = (campaign.conversionsValue || 0) > 0 ? (campaign.cost / (campaign.conversionsValue || 1) * 100) : 0;
                            const conversionRate = campaign.clicks > 0 ? (campaign.conversions / campaign.clicks * 100) : 0;
                            const cpa = campaign.conversions > 0 ? (campaign.cost / campaign.conversions) : 0;
                            const ctr = campaign.impressions > 0 ? (campaign.clicks / campaign.impressions * 100) : 0;
                            const cpc = campaign.clicks > 0 ? (campaign.cost / campaign.clicks) : 0;
                            
                            return (
                              <tr key={campaign.campaignId || campaign.id || index}>
                                <td className="campaign-name">{campaign.campaignName || campaign.name}</td>
                                <td className="numeric">{formatNumber(campaign.impressions || 0)}</td>
                                <td className="numeric">{formatNumber(ctr, 2)}%</td>
                                <td className="numeric">{formatNumber(campaign.clicks || 0)}</td>
                                <td className="numeric">€{formatNumber(cpc, 2)}</td>
                                <td className="numeric">€{formatNumber(campaign.cost || 0, 2)}</td>
                                <td className="numeric">{formatNumber(conversionRate, 2)}%</td>
                                <td className="numeric">{formatNumber(campaign.conversions || 0, 1)}</td>
                                <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'campaigns')}`}>{cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}</td>
                                <td className="numeric">€{formatNumber(campaign.conversionsValue || 0, 2)}</td>
                                <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                                  {formatNumber(cos, 1)}%
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="totals-row">
                            <td className="campaign-name"><strong>TOTAL</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>
                            <td className="numeric"><strong>€{formatNumber(totals.cpc, 2)}</strong></td>
                            <td className="numeric"><strong>€{formatNumber(totals.cost, 2)}</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.conversions, 1)}</strong></td>
                            <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', 'campaigns')}`}><strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong></td>
                            <td className="numeric"><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></td>
                            <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', 'campaigns')}`}>
                              <strong>{formatNumber(totals.cos, 1)}%</strong>
                            </td>
                          </tr>
                        </tfoot>
                        </table>
                      </div>
                    );
                  })()
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    color: '#6b7280',
                    fontStyle: 'italic'
                  }}>
                    {productTypeCampaignDetails?.message || 'Aucune campagne associée trouvée pour cette catégorie'}
                  </div>
                )}
              </div>

              {/* Graphique de performance temporelle - exactement comme dans le dashboard Global */}
              {productTypeCampaignDetails && productTypeCampaignDetails.campaigns && productTypeCampaignDetails.campaigns.length > 0 && (
                <div style={{
                  marginTop: '24px',
                  backgroundColor: '#ffffff',
                  border: '1px solid #e5e7eb',
                  borderRadius: '12px',
                  padding: '24px'
                }}>
                  <h3 style={{
                    fontSize: '18px',
                    fontWeight: '600',
                    color: '#1f2937',
                    marginTop: '0',
                    marginBottom: '16px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <TrendingUp size={18} /> Évolution temporelle
                  </h3>
                  
                  {loadingProductTypeHistorical ? (
                    <div style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '60px',
                      color: '#6b7280'
                    }}>
                      <div style={{ marginRight: '12px' }}><Clock size={16} /></div>
                      Chargement des données historiques...
                    </div>
                  ) : productTypeHistoricalData && productTypeHistoricalData.length > 0 ? (
                    <div style={{ 
                      width: '100%', 
                      height: '400px', 
                      background: 'white', 
                      borderRadius: '12px', 
                      padding: '20px', 
                      boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                      boxSizing: 'border-box',
                      overflow: 'hidden'
                    }}>
                      <ResponsiveContainer width="100%" height="100%">
                        <ComposedChart data={productTypeHistoricalData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis 
                            dataKey="date" 
                            tick={{ fontSize: 12 }}
                            tickFormatter={(value) => {
                              const date = new Date(value);
                              return `${date.getDate()}/${date.getMonth() + 1}`;
                            }}
                          />
                          
                          {/* Axe Y gauche pour les clics */}
                          <YAxis 
                            yAxisId="left"
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Clics', angle: -90, position: 'insideLeft', style: { fontSize: 12 } }}
                          />
                          
                          {/* Axe Y droit pour les conversions */}
                          <YAxis 
                            yAxisId="right"
                            orientation="right"
                            tick={{ fontSize: 12 }}
                            label={{ value: 'Conversions', angle: 90, position: 'insideRight', style: { fontSize: 12 } }}
                          />
                          
                          <Tooltip 
                            formatter={(value, name) => {
                              if (name === 'Clics') return formatNumber(value);
                              if (name === 'Conversions') return formatNumber(value, 2);
                              return value;
                            }}
                            labelFormatter={(label) => {
                              const date = new Date(label);
                              return date.toLocaleDateString('fr-FR', { 
                                weekday: 'short', 
                                day: 'numeric', 
                                month: 'short' 
                              });
                            }}
                          />
                          <Legend />
                          
                          {/* Conversions en histogrammes */}
                          <Bar 
                            yAxisId="right"
                            dataKey="conversions" 
                            fill="#8884d8" 
                            fillOpacity={0.8}
                            name="Conversions"
                            radius={[4, 4, 0, 0]}
                          />
                          
                          {/* Clics en courbe */}
                          <Line 
                            yAxisId="left"
                            type="monotone" 
                            dataKey="clicks" 
                            stroke="#82ca9d" 
                            strokeWidth={3}
                            name="Clics"
                            dot={{ fill: '#82ca9d', strokeWidth: 2, r: 5 }}
                            activeDot={{ r: 7, fill: '#82ca9d' }}
                          />
                        </ComposedChart>
                      </ResponsiveContainer>
                    </div>
                  ) : (
                    <div style={{
                      textAlign: 'center',
                      padding: '40px',
                      color: '#6b7280'
                    }}>
                      Aucune donnée historique disponible pour cette catégorie
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal Search Term Detail */}
      {showSearchTermModal && selectedSearchTerm && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 100000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflowY: 'auto'
          }}
          onClick={closeSearchTermModal}
        >
          <div
            style={{
              backgroundColor: '#FAF9F5',
              borderRadius: '12px',
              maxWidth: '1400px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              border: '1px solid #E8E6E1'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête de la modal - Charte Kokpit */}
            <div style={{
              background: 'linear-gradient(135deg, #FAF9F5 0%, #F5F4F0 100%)',
              padding: '20px 28px',
              borderBottom: '1px solid #E8E6E1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                Détail du terme : "{selectedSearchTerm.searchTerm}"
              </h2>
              <button
                onClick={closeSearchTermModal}
                style={{
                  background: '#E8E6E1',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: '#6b7280',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#D8D6D1'}
                onMouseLeave={(e) => e.target.style.background = '#E8E6E1'}
              >
                ×
              </button>
            </div>
            
            {/* Corps de la modal */}
            <div style={{ 
              padding: '32px',
              maxHeight: 'calc(95vh - 120px)',
              overflowY: 'auto'
            }}>

              {/* Tableau des campagnes */}
              <div>
                <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
                  Performances par campagne
                </h4>
                
                {loadingSearchTermCampaigns ? (
                  <div style={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '200px',
                    background: '#f8f5f0',
                    borderRadius: '8px'
                  }}>
                    <div style={{ textAlign: 'center' }}>
                      <div className="spinner" style={{ margin: '0 auto 12px' }}></div>
                      <div style={{ color: '#64748b', fontSize: '14px' }}>
                        Chargement des détails...
                      </div>
                    </div>
                  </div>
                ) : searchTermCampaignDetails && searchTermCampaignDetails.length > 0 ? (
                  (() => {
                    // Calculer les totaux comme dans la pop-in produit
                    const totals = searchTermCampaignDetails.reduce((acc, campaign) => {
                      acc.impressions += campaign.impressions || 0;
                      acc.clicks += campaign.clicks || 0;
                      acc.cost += campaign.cost || 0;
                      acc.conversions += campaign.conversions || 0;
                      acc.conversionsValue += campaign.conversionsValue || 0;
                      return acc;
                    }, {
                      impressions: 0,
                      clicks: 0,
                      cost: 0,
                      conversions: 0,
                      conversionsValue: 0
                    });

                    // Calculer les métriques dérivées
                    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
                    totals.cpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
                    totals.conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
                    totals.cpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
                    totals.cos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;
                    
                    return (
                      <div className="campaigns-table-wrapper">
                        <table className="campaigns-table">
                        <thead>
                          <tr>
                            <th className="sortable">
                              Campagne
                            </th>
                            <th className="sortable numeric">
                              Impressions
                            </th>
                            <th className="sortable numeric">
                              CTR
                            </th>
                            <th className="sortable numeric">
                              Clics
                            </th>
                            <th className="sortable numeric">
                              CPC
                            </th>
                            <th className="sortable numeric">
                              Coût
                            </th>
                            <th className="sortable numeric">
                              Taux Conv.
                            </th>
                            <th className="sortable numeric">
                              Conv.
                            </th>
                            <th className="sortable numeric">
                              CPA
                            </th>
                            <th className="sortable numeric">
                              Valeur Conv.
                            </th>
                            <th className="sortable numeric">
                              COS
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {searchTermCampaignDetails.map((campaign, index) => {
                            const cos = (campaign.conversionsValue || 0) > 0 ? (campaign.cost / (campaign.conversionsValue || 1) * 100) : 0;
                            const conversionRate = campaign.clicks > 0 ? (campaign.conversions / campaign.clicks * 100) : 0;
                            const cpa = campaign.conversions > 0 ? (campaign.cost / campaign.conversions) : 0;
                            const ctr = campaign.impressions > 0 ? (campaign.clicks / campaign.impressions * 100) : 0;
                            const cpc = campaign.clicks > 0 ? (campaign.cost / campaign.clicks) : 0;
                            
                            return (
                              <tr key={campaign.campaignId || index}>
                                <td className="campaign-name">{campaign.campaignName || campaign.name}</td>
                                <td className="numeric">{formatNumber(campaign.impressions || 0)}</td>
                                <td className="numeric">{formatNumber(ctr, 2)}%</td>
                                <td className="numeric">{formatNumber(campaign.clicks || 0)}</td>
                                <td className="numeric">€{formatNumber(cpc, 2)}</td>
                                <td className="numeric">€{formatNumber(campaign.cost || 0, 2)}</td>
                                <td className="numeric">{formatNumber(conversionRate, 2)}%</td>
                                <td className="numeric">{formatNumber(campaign.conversions || 0, 1)}</td>
                                <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'search_terms')}`}>{cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}</td>
                                <td className="numeric">€{formatNumber(campaign.conversionsValue || 0, 2)}</td>
                                <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'search_terms')}`}>
                                  {formatNumber(cos, 1)}%
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="totals-row">
                            <td className="campaign-name"><strong>TOTAL</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.impressions)}</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.ctr, 2)}%</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.clicks)}</strong></td>
                            <td className="numeric"><strong>€{formatNumber(totals.cpc, 2)}</strong></td>
                            <td className="numeric"><strong>€{formatNumber(totals.cost, 2)}</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.conversionRate, 2)}%</strong></td>
                            <td className="numeric"><strong>{formatNumber(totals.conversions, 1)}</strong></td>
                            <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', 'search_terms')}`}><strong>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</strong></td>
                            <td className="numeric"><strong>€{formatNumber(totals.conversionsValue, 2)}</strong></td>
                            <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', 'search_terms')}`}>
                              <strong>{formatNumber(totals.cos, 1)}%</strong>
                            </td>
                          </tr>
                        </tfoot>
                        </table>
                      </div>
                    );
                  })()
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    background: '#fef3cd',
                    border: '1px solid #f6e05e',
                    borderRadius: '8px',
                    color: '#92400e'
                  }}>
                    <div style={{ marginBottom: '8px' }}><BarChart3 size={24} color="#6b7280" /></div>
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                      Aucune donnée de campagne
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      Les détails par campagne ne sont pas disponibles pour ce terme.
                    </div>
                  </div>
                )}

                {searchTermCampaignsError && (
                  <div style={{
                    background: '#fef2f2',
                    border: '1px solid #fecaca',
                    borderRadius: '8px',
                    padding: '12px',
                    fontSize: '14px'
                  }}>
                    <div style={{ color: '#92400e', marginBottom: '4px', fontWeight: '500' }}>
                      <AlertCircle size={14} style={{ display: 'inline', marginRight: '4px' }} />Données non chargées
                    </div>
                    <div style={{ color: '#92400e' }}>
                      Les détails des campagnes n'ont pas pu être chargés. Veuillez réessayer.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Modal N-gram Detail */}
      {showNgramModal && selectedNgram && ReactDOM.createPortal(
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 100000,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '20px',
            overflowY: 'auto'
          }}
          onClick={closeNgramModal}
        >
          <div
            style={{
              backgroundColor: '#FAF9F5',
              borderRadius: '12px',
              maxWidth: '1400px',
              width: '100%',
              maxHeight: '90vh',
              overflow: 'hidden',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
              border: '1px solid #E8E6E1'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* En-tête de la modal - Charte Kokpit */}
            <div style={{
              background: 'linear-gradient(135deg, #FAF9F5 0%, #F5F4F0 100%)',
              padding: '20px 28px',
              borderBottom: '1px solid #E8E6E1',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h2 style={{
                fontSize: '20px',
                fontWeight: '600',
                color: '#1f2937',
                margin: 0,
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                Termes de recherche contenant : "{selectedNgram.ngram}"
              </h2>
              <button
                onClick={closeNgramModal}
                style={{
                  background: '#E8E6E1',
                  border: 'none',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  color: '#6b7280',
                  fontSize: '20px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => e.target.style.background = '#D8D6D1'}
                onMouseLeave={(e) => e.target.style.background = '#E8E6E1'}
              >
                ×
              </button>
            </div>
            
            {/* Corps de la modal */}
            <div style={{ 
              padding: '32px',
              maxHeight: 'calc(95vh - 120px)',
              overflowY: 'auto'
            }}>

              {/* Info sur le n-gram */}
              <div style={{
                background: '#f8f5f0',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '24px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <div>
                  <h4 style={{ margin: 0, fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
                    {selectedNgram.termCount} termes de recherche trouvés
                  </h4>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
                    Contenant le {selectedNgram.ngram.split(' ').length === 1 ? 'mot' : 
                    selectedNgram.ngram.split(' ').length === 2 ? 'bigramme' : 'trigramme'} : "{selectedNgram.ngram}"
                  </p>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: '20px', fontWeight: '700', color: '#333333' }}>
                    €{formatNumber(selectedNgram.cost, 2)}
                  </div>
                  <div style={{ fontSize: '14px', color: '#6b7280' }}>
                    Coût total
                  </div>
                </div>
              </div>

              {/* Tableau des termes de recherche */}
              <div>
                <h4 style={{ marginBottom: '12px', fontSize: '16px', fontWeight: '600' }}>
                  Détail des termes de recherche
                </h4>
                
                {ngramSearchTerms && ngramSearchTerms.length > 0 ? (
                  (() => {
                    // Fonction de tri pour les termes de recherche de la modal
                    const sortNgramModalData = (data, config) => {
                      if (!data || data.length === 0) return [];
                      
                      return [...data].sort((a, b) => {
                        let aValue, bValue;
                        
                        switch (config.key) {
                          case 'searchTerm':
                            aValue = (a.searchTerm || '').toLowerCase();
                            bValue = (b.searchTerm || '').toLowerCase();
                            break;
                          case 'source':
                            aValue = (a.source || '').toLowerCase();
                            bValue = (b.source || '').toLowerCase();
                            break;
                          case 'ctr':
                            aValue = a.impressions > 0 ? (a.clicks / a.impressions * 100) : 0;
                            bValue = b.impressions > 0 ? (b.clicks / b.impressions * 100) : 0;
                            break;
                          case 'cpc':
                            aValue = a.clicks > 0 ? (a.cost / a.clicks) : 0;
                            bValue = b.clicks > 0 ? (b.cost / b.clicks) : 0;
                            break;
                          case 'conversionRate':
                            aValue = a.clicks > 0 ? (a.conversions / a.clicks * 100) : 0;
                            bValue = b.clicks > 0 ? (b.conversions / b.clicks * 100) : 0;
                            break;
                          case 'cpa':
                            aValue = a.conversions > 0 ? (a.cost / a.conversions) : 0;
                            bValue = b.conversions > 0 ? (b.cost / b.conversions) : 0;
                            break;
                          case 'cos':
                            aValue = a.conversionsValue > 0 ? (a.cost / a.conversionsValue * 100) : 0;
                            bValue = b.conversionsValue > 0 ? (b.cost / b.conversionsValue * 100) : 0;
                            break;
                          default:
                            aValue = a[config.key] || 0;
                            bValue = b[config.key] || 0;
                        }
                        
                        if (config.direction === 'asc') {
                          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
                        } else {
                          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
                        }
                      });
                    };

                    // Appliquer le tri
                    const sortedNgramModalTerms = sortNgramModalData(ngramSearchTerms, ngramModalSortConfig);

                    // Calculer les totaux
                    const totals = sortedNgramModalTerms.reduce((acc, term) => {
                      acc.impressions += term.impressions || 0;
                      acc.clicks += term.clicks || 0;
                      acc.cost += term.cost || 0;
                      acc.conversions += term.conversions || 0;
                      acc.conversionsValue += term.conversionsValue || 0;
                      return acc;
                    }, {
                      impressions: 0,
                      clicks: 0,
                      cost: 0,
                      conversions: 0,
                      conversionsValue: 0
                    });

                    // Calculer les métriques dérivées
                    totals.ctr = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
                    totals.cpc = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
                    totals.conversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
                    totals.cpa = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
                    totals.cos = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;
                    
                    return (
                      <div className="search-terms-table-wrapper">
                        <table className="search-terms-table">
                        <thead>
                          <tr>
                            <th onClick={() => handleNgramModalSort('searchTerm')} className="sortable" style={{ width: '200px', minWidth: '200px', maxWidth: '200px' }}>
                              Terme de recherche {ngramModalSortConfig.key === 'searchTerm' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('impressions')} className="sortable numeric">
                              Impressions {ngramModalSortConfig.key === 'impressions' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('ctr')} className="sortable numeric">
                              CTR {ngramModalSortConfig.key === 'ctr' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('clicks')} className="sortable numeric">
                              Clics {ngramModalSortConfig.key === 'clicks' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('cpc')} className="sortable numeric">
                              CPC {ngramModalSortConfig.key === 'cpc' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('cost')} className="sortable numeric">
                              Coût {ngramModalSortConfig.key === 'cost' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('conversionRate')} className="sortable numeric">
                              Taux Conv. {ngramModalSortConfig.key === 'conversionRate' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('conversions')} className="sortable numeric">
                              Conv. {ngramModalSortConfig.key === 'conversions' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('cpa')} className="sortable numeric">
                              CPA {ngramModalSortConfig.key === 'cpa' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('conversionsValue')} className="sortable numeric">
                              Valeur Conv. {ngramModalSortConfig.key === 'conversionsValue' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            <th onClick={() => handleNgramModalSort('cos')} className="sortable numeric">
                              COS {ngramModalSortConfig.key === 'cos' && (ngramModalSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          </tr>
                        </thead>
                        <tbody>
                          {sortedNgramModalTerms.map((term, index) => {
                            // Utiliser les métriques pré-calculées de openNgramModal
                            const cos = term.cos || 0;
                            const conversionRate = term.conversionRate || 0;
                            const cpa = term.cpa || 0;
                            const ctr = term.ctr || 0;
                            const cpc = term.cpc || 0;
                            
                            return (
                              <tr key={index}>
                                <td className="search-term-name" style={{ 
                                  width: '200px', 
                                  minWidth: '250px', 
                                  maxWidth: '200px',
                                  wordWrap: 'break-word',
                                  whiteSpace: 'normal',
                                  verticalAlign: 'top'
                                }}>
                                  <div>
                                    <div style={{ 
                                      color: '#1a73e8',
                                      fontWeight: '500',
                                      overflow: 'hidden',
                                      textOverflow: 'ellipsis',
                                      whiteSpace: 'nowrap'
                                    }}>
                                      {term.searchTerm}
                                    </div>
                                    <div style={{ marginTop: '2px' }}>
                                      {term.sources.map((source, idx) => (
                                        <span 
                                          key={idx}
                                          style={{
                                            display: 'inline-block',
                                            padding: '1px 4px',
                                            margin: '0 2px 0 0',
                                            borderRadius: '8px',
                                            fontSize: '0.65em',
                                            fontWeight: '600',
                                            textTransform: 'uppercase',
                                            backgroundColor: source === 'Performance Max' ? '#e3f2fd' : '#f3e5f5',
                                            color: source === 'Performance Max' ? '#1565c0' : '#7b1fa2'
                                          }}
                                        >
                                          {source === 'Search/Shopping' ? 'Search' : source === 'Performance Max' ? 'PMax' : source}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                </td>
                                <td className="numeric">{formatNumber(term.impressions || 0)}</td>
                                <td className="numeric">{formatNumber(ctr, 2)}%</td>
                                <td className="numeric">{formatNumber(term.clicks || 0)}</td>
                                <td className="numeric">€{formatNumber(cpc, 2)}</td>
                                <td className="numeric">€{formatNumber(term.cost || 0, 2)}</td>
                                <td className="numeric">{formatNumber(conversionRate, 2)}%</td>
                                <td className="numeric">{formatNumber(term.conversions || 0, 1)}</td>
                                <td className={`numeric ${getCpaClassName(cpa, selectedAccount?.id || 'global', 'search_terms')}`}>{cpa >= 0 ? `€${formatNumber(cpa, 2)}` : '-'}</td>
                                <td className="numeric">€{formatNumber(term.conversionsValue || 0, 2)}</td>
                                <td className={`numeric ${getCosClassName(cos, selectedAccount?.id || 'global', 'search_terms')}`}>
                                  {formatNumber(cos, 1)}%
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          <tr className="total-row" style={{ borderTop: '2px solid #e5e7eb', backgroundColor: '#f9fafb' }}>
                            <td style={{
                              fontWeight: '600',
                              width: '200px',
                              minWidth: '250px',
                              maxWidth: '200px',
                              wordWrap: 'break-word',
                              whiteSpace: 'normal',
                              verticalAlign: 'top'
                            }}>TOTAL ({sortedNgramModalTerms.length} termes)</td>
                            <td className="numeric" style={{ fontWeight: '600' }}>{formatNumber(totals.impressions)}</td>
                            <td className="numeric" style={{ fontWeight: '600' }}>{formatNumber(totals.ctr, 2)}%</td>
                            <td className="numeric" style={{ fontWeight: '600' }}>{formatNumber(totals.clicks)}</td>
                            <td className="numeric" style={{ fontWeight: '600' }}>€{formatNumber(totals.cpc, 2)}</td>
                            <td className="numeric" style={{ fontWeight: '600' }}>€{formatNumber(totals.cost, 2)}</td>
                            <td className="numeric" style={{ fontWeight: '600' }}>{formatNumber(totals.conversionRate, 2)}%</td>
                            <td className="numeric" style={{ fontWeight: '600' }}>{formatNumber(totals.conversions, 1)}</td>
                            <td className={`numeric ${getCpaClassName(totals.cpa, selectedAccount?.id || 'global', 'search_terms')}`} style={{ fontWeight: '600' }}>{totals.cpa >= 0 ? `€${formatNumber(totals.cpa, 2)}` : '-'}</td>
                            <td className="numeric" style={{ fontWeight: '600' }}>€{formatNumber(totals.conversionsValue, 2)}</td>
                            <td className={`numeric ${getCosClassName(totals.cos, selectedAccount?.id || 'global', 'search_terms')}`} style={{ fontWeight: '600' }}>
                              {formatNumber(totals.cos, 1)}%
                            </td>
                          </tr>
                        </tfoot>
                        </table>
                      </div>
                    );
                  })()
                ) : (
                  <div style={{
                    textAlign: 'center',
                    padding: '40px',
                    background: '#fef3cd',
                    border: '1px solid #f6e05e',
                    borderRadius: '8px',
                    color: '#92400e'
                  }}>
                    <div style={{ marginBottom: '8px' }}><BarChart3 size={24} color="#6b7280" /></div>
                    <div style={{ fontWeight: '500', marginBottom: '4px' }}>
                      Aucun terme de recherche trouvé
                    </div>
                    <div style={{ fontSize: '14px' }}>
                      Aucun terme contenant ce n-gram n'a été trouvé.
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {/* Section Jours de la semaine - chargement 100% dédié depuis l'endpoint */}
      {/* Visible seulement avec Dashboard Global ET avec données disponibles */}
      {/* Pour les clients, masqué si le module est invisible */}
      {selectedDashboardModel === 'global' && resultsData?.charts?.length > 0 && !gadsLoading && ((weekdaysData?.data?.weekdayData?.length > 0) || loadingWeekdays) && (userRole !== 'CLIENT' || isClientModuleVisible('weekdays')) && (
        <div className="device-conversions-section">
          <div className="section-header">
            <h4><Calendar size={16} />&nbsp;Jours de la semaine</h4>
            {loadingWeekdays && <span className="loading-indicator"><Zap size={12} />&nbsp;Chargement prioritaire...</span>}
            <ModuleVisibilityToggle
              isVisible={isClientModuleVisible('weekdays')}
              onToggle={toggleClientModuleVisibility}
              userRole={userRole}
              moduleId="weekdays"
            />
          </div>
          {(userRole !== 'CLIENT' || isClientModuleVisible('weekdays')) && (
            <>
              <div className="device-chart-container">
                <div className="chart-card full-width">
                  <div className="chart-preview">
                    <ResponsiveContainer width="100%" height={300}>
                      <BarChart data={(weekdaysData?.data?.weekdayData || []).map(day => ({
                        name: day.dayOfWeek === 'MONDAY' ? 'Lundi' :
                              day.dayOfWeek === 'TUESDAY' ? 'Mardi' :
                              day.dayOfWeek === 'WEDNESDAY' ? 'Mercredi' :
                              day.dayOfWeek === 'THURSDAY' ? 'Jeudi' :
                              day.dayOfWeek === 'FRIDAY' ? 'Vendredi' :
                              day.dayOfWeek === 'SATURDAY' ? 'Samedi' :
                              day.dayOfWeek === 'SUNDAY' ? 'Dimanche' : day.dayOfWeek,
                        conversions: day.conversions || 0,
                        clicks: day.clicks || 0
                      }))}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="name" />
                        <Tooltip />
                        <Legend />
                        <Bar dataKey="conversions" fill="#8884d8" name="Conversions" />
                        <Bar dataKey="clicks" fill="#82ca9d" name="Clics" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </div>
              </div>
              <div className="weekday-table-wrapper">
                <table className="campaigns-table">
                  <thead>
                    <tr>
                      <th>Jour de la semaine</th>
                      <th className="numeric">Impressions</th>
                      <th className="numeric">CTR</th>
                      <th className="numeric">Clics</th>
                      <th className="numeric">CPC</th>
                      <th className="numeric">Coût</th>
                      <th className="numeric">Taux conv.</th>
                      <th className="numeric">Conv.</th>
                      <th className="numeric">CPA</th>
                      <th className="numeric">Valeur conv.</th>
                      <th className="numeric">COS</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(weekdaysData?.data?.weekdayData || []).map((day, index) => {
                      const dayName = day.dayOfWeek === 'MONDAY' ? 'Lundi' :
                                    day.dayOfWeek === 'TUESDAY' ? 'Mardi' :
                                    day.dayOfWeek === 'WEDNESDAY' ? 'Mercredi' :
                                    day.dayOfWeek === 'THURSDAY' ? 'Jeudi' :
                                    day.dayOfWeek === 'FRIDAY' ? 'Vendredi' :
                                    day.dayOfWeek === 'SATURDAY' ? 'Samedi' :
                                    day.dayOfWeek === 'SUNDAY' ? 'Dimanche' : day.dayOfWeek;
                      
                      return (
                        <tr key={index}>
                          <td className="day-name">📅 {dayName}</td>
                          <td className="numeric">{formatNumber(day.impressions || 0)}</td>
                          <td className="numeric">{formatNumber(day.ctr || 0, 2)}%</td>
                          <td className="numeric">{formatNumber(day.clicks || 0)}</td>
                          <td className="numeric">€{formatNumber(day.cpc || 0, 2)}</td>
                          <td className="numeric">€{formatNumber(day.cost || 0, 2)}</td>
                          <td className="numeric">{formatNumber(day.conversionRate || 0, 2)}%</td>
                          <td className="numeric">{formatNumber(day.conversions || 0, 1)}</td>
                          <td className="numeric">{day.cpa >= 0 ? `€${formatNumber(day.cpa, 2)}` : '-'}</td>
                          <td className="numeric">€{formatNumber(day.conversionsValue || 0, 2)}</td>
                          <td className={`numeric ${calculateCosClassName(day.cost, day.conversionsValue, selectedAccount?.id || 'global')}`}>
                            {day.conversionsValue > 0 ? formatNumber((day.cost || 0) / (day.conversionsValue || 1) * 100, 1) + '%' : formatNumber(0, 1) + '%'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  {(weekdaysData?.data?.weekdayData?.length > 0) && (
                    <tfoot>
                      <tr className="totals-row">
                        <td><strong>TOTAL</strong></td>
                        <td className="numeric"><strong>{formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.impressions || 0), 0))}</strong></td>
                        <td className="numeric"><strong>{formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.ctr || 0), 0) / (weekdaysData?.data?.weekdayData || []).length, 2)}%</strong></td>
                        <td className="numeric"><strong>{formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.clicks || 0), 0))}</strong></td>
                        <td className="numeric"><strong>€{formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.cpc || 0), 0) / (weekdaysData?.data?.weekdayData || []).length, 2)}</strong></td>
                        <td className="numeric"><strong>€{formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.cost || 0), 0), 2)}</strong></td>
                        <td className="numeric"><strong>{formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.conversionRate || 0), 0) / (weekdaysData?.data?.weekdayData || []).length, 2)}%</strong></td>
                        <td className="numeric"><strong>{formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.conversions || 0), 0))}</strong></td>
                        <td className="numeric"><strong>€{formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.cpa || 0), 0) / (weekdaysData?.data?.weekdayData || []).length, 2)}</strong></td>
                        <td className="numeric"><strong>€{formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.conversionsValue || 0), 0), 2)}</strong></td>
                        <td className={`numeric ${calculateCosClassName((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.cost || 0), 0), (weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.conversionsValue || 0), 0), selectedAccount?.id || 'global')}`}>
                          <strong>{(weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.conversionsValue || 0), 0) > 0 ? formatNumber((weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.cost || 0), 0) / (weekdaysData?.data?.weekdayData || []).reduce((sum, d) => sum + (d.conversionsValue || 0), 0) * 100, 1) + '%' : '-'}</strong>
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            </>
          )}
        </div>
      )}

      {/* Modal d'ajout de données Google Ads */}
      {showAddModal && (
        <AddDataModal
          token={token}
          onClose={() => setShowAddModal(false)}
          onAdded={() => {
            loadLibraryItems();
            setShowAddModal(false);
          }}
          setSuccess={(msg) => {/* Gérer le succès si nécessaire */}}
          setError={setError}
          initialStep="ads"
          connectors={globalConnectors}
        />
      )}

      {/* Modal de configuration des métriques du tableau Shopping */}
      {showShoppingMetricsConfig && (
        <MetricsConfigModal
          isOpen={showShoppingMetricsConfig}
          onClose={() => setShowShoppingMetricsConfig(false)}
          componentId="shopping_products"
          accountId={selectedAccount?.id || 'global'}
          title="Métriques du tableau Produits"
        />
      )}

      {/* Modal de configuration des métriques Performance Max */}
      {showPmaxMetricsConfig && (
        <MetricsConfigModal
          isOpen={showPmaxMetricsConfig}
          onClose={() => setShowPmaxMetricsConfig(false)}
          componentId="pmax"
          accountId={selectedAccount?.id || 'global'}
          title="Métriques Performance Max"
        />
      )}

      {/* Dashboard Products Insights */}
      {selectedDashboardModel === 'products-insights' && selectedAccount && (
        <div className="products-insights-dashboard-container" style={{ marginTop: '12px' }}>
          <ProductsInsightsDashboard
            token={token}
            sources={[selectedAccount.id]}
            period={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            campaignIds={idsCampagnesFiltrees}
          />
        </div>
      )}

      {/* Dashboard YouTube Ads */}
      {selectedDashboardModel === 'youtube-ads' && selectedAccount && (
        <div className="youtube-ads-dashboard" style={{ marginTop: '32px' }}>

          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingYoutubeVideos && accountHasNoData && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {!accountHasNoData && (
            <>
          {loadingYoutubeVideos ? (
            <DashboardLoadingScreen
              platformName="Google Ads"
              progress={youtubeLoadingProgress}
              features={[
                { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
                { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
                { icon: <Youtube size={28} />, label: 'Analyse des campagnes' }
              ]}
            />
          ) : youtubeVideosData ? (
            <div className="youtube-videos-dashboard-container">
              {/* Composant Résultats pour YouTube */}
              <YoutubeResultsChart
                formatNumber={formatNumber}
                selectedPeriod={selectedPeriod}
                processedYoutubeData={processedYoutubeData}
                isLoading={loadingYoutubeVideos || !isYoutubeDataComplete}
                granularity={youtubeGranularity}
                onGranularityChange={setYoutubeGranularity}
              />
              
              <div className="youtube-videos-table-container">
                <>
                <PartialDataNotice failedSources={youtubeVideosData?.failedSources} resolveName={resolveAccountName} />
                <YoutubeVideosTable
                  rawData={youtubeVideosData}
                  isVisible={true}
                  formatNumber={formatNumber}
                  selectedVideoFilter={null}
                  setSelectedVideoFilter={() => {}}
                  selectedCampaignTypeFilter={null}
                  selectedAccount={selectedAccount}
                  selectedPeriod={selectedPeriod}
                  customStartDate={customStartDate}
                  customEndDate={customEndDate}
                  token={token}
                  setError={setError}
                  dashboardContext="youtube"
                  onDataProcessed={handleYoutubeDataProcessed}
                />
                </>
              </div>
            </div>
          ) : (
            <div style={{
              padding: '48px',
              textAlign: 'center',
              backgroundColor: '#f8f9fa',
              borderRadius: '12px',
              color: '#6b7280'
            }}>
              <div style={{ marginBottom: '16px' }}><Youtube size={48} color="#6b7280" /></div>
              <h3 style={{ margin: '0 0 8px 0', color: '#1f2937' }}>Aucune donnée YouTube Ads</h3>
              <p>Aucune donnée YouTube Ads trouvée pour la période sélectionnée.</p>
            </div>
          )}
          </>
          )}
        </div>
      )}

      {/* Dashboard Concurrence */}
      {selectedDashboardModel === 'competition' && selectedAccount && (
        <div className="competition-dashboard-wrapper">
          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingCharts && accountHasNoData && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {/* Contenu du dashboard - affiché uniquement si données disponibles */}
          {!accountHasNoData && (
            <CompetitionDashboard
              token={token}
              globalConnectors={globalConnectors}
              selectedPeriod={selectedPeriod}
              selectedClientId={selectedClientId}
              selectedAccount={selectedAccount}
            />
          )}
        </div>
      )}

      {config.IS_LOCAL && selectedDashboardModel === 'cross-sales' && selectedAccount && (
        <div className="cross-sales-dashboard-wrapper">
          {/* Message "Pas de données" si aucune donnée sur la période */}
          {!loadingCharts && accountHasNoData && (
            <NoDataMessage
              title="Aucune donnée disponible"
              subtitle="Aucune donnée n'a été trouvée pour ce compte Google Ads sur la période sélectionnée."
            />
          )}

          {/* Contenu du dashboard - affiché uniquement si données disponibles */}
          {!accountHasNoData && (
            <CrossSalesDashboard
              token={token}
              globalConnectors={globalConnectors}
              selectedPeriod={selectedPeriod}
              selectedClientId={selectedClientId}
              selectedAccount={selectedAccount}
            />
          )}
        </div>
      )}

    </div>
  );
}

// ===== COMPOSANT DASHBOARD MICROSOFT ADS =====

function DashboardMicrosoftAds({ token, setError, selectedPeriod, setSelectedPeriod, customStartDate, setCustomStartDate, customEndDate, setCustomEndDate, selectedDashboardModel, setSelectedDashboardModel, preselectedAccountId, activeTab, preselectionUsed, setPreselectionUsed, fetchPreviousPeriodData, globalConnectors, canAccessAnalyzer, selectedClientId, updateUrlWithAccount, dashboardNotes, fetchDashboardNotes, selectedSiteId, userSites, userRole = 'USER', msadsComparisonType = '', msadsCompareEnabled = false, comparisonStartDate = '', comparisonEndDate = '' }) {
  const [selectedSources, setSelectedSources] = useState([]);
  const [autoCharts, setAutoCharts] = useState(null);
  const [granularity, setGranularity] = useState('day');
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [loadingInsights, setLoadingInsights] = useState(false);
  const [libraryItems, setLibraryItems] = useState([]);
  const [allLibraryItems, setAllLibraryItems] = useState([]); // Tous les comptes (non filtrés)
  const [loadingLibraryItems, setLoadingLibraryItems] = useState(true);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const selectedMetrics = useSelectedMetrics(selectedAccount?.id || 'global');
  const [showCustomDates, setShowCustomDates] = useState(false);

  // Ref pour garder la valeur la plus récente de libraryItems (pour les event handlers)
  const libraryItemsRef = React.useRef(libraryItems);
  React.useEffect(() => {
    libraryItemsRef.current = libraryItems;
  }, [libraryItems]);
  const [sortConfig, setSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [msCampaignCurrentPage, setMsCampaignCurrentPage] = useState(1);
  const [msAdGroupSortConfig, setMsAdGroupSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [msAdGroupCurrentPage, setMsAdGroupCurrentPage] = useState(1);
  const [msSearchTermsCurrentPage, setMsSearchTermsCurrentPage] = useState(1);
  const [msAdGroupsData, setMsAdGroupsData] = useState(null);
  const [loadingMsAdGroups, setLoadingMsAdGroups] = useState(false);
  
  // État pour les données de la période précédente
  const [previousPeriodData, setPreviousPeriodData] = useState(null);
  
  const [campaignTypesSortConfig, setCampaignTypesSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [msDevicesSortConfig, setMsDevicesSortConfig] = useState({ key: 'cost', direction: 'desc' });

  // État pour le modal de configuration des métriques (Microsoft Ads)
  const [showMsMetricsConfig, setShowMsMetricsConfig] = useState(null); // null ou componentId ('msads_campaigns', 'msads_adgroups', 'msads_devices')

  // Configuration des métriques pour les composants Microsoft Ads
  const msadsCampaignsMetrics = useComponentMetrics('msads_campaigns', selectedAccount?.id || 'global');
  const msadsAdGroupsMetrics = useComponentMetrics('msads_adgroups', selectedAccount?.id || 'global');
  const msadsDevicesMetrics = useComponentMetrics('msads_devices', selectedAccount?.id || 'global');
  const msadsCampaignTypesMetrics = useComponentMetrics('msads_campaign_types', selectedAccount?.id || 'global');

  // Microsoft Ads renvoie le type de campagne en clair, là où Google Ads l'encode
  // en nombre : c'est la seule chose que la section Types de campagnes doit savoir
  // de la régie, le reste vit dans le composant partagé.
  const getMsadsCampaignTypeName = React.useCallback((campaign) => {
    const channelTypeMapping = {
      'Search': 'Recherche',
      'Search & content': 'Recherche',
      'Shopping': 'Shopping',
      'Display': 'Display',
      'Video': 'Vidéo',
      'Discovery': 'Découverte',
      'Performance Max': 'Performance Max',
      'App': 'Application',
      'Local': 'Local',
      'Smart': 'Intelligente',
      'Hotel': 'Hôtel',
      'DynamicSearchAds': 'Annonces de recherche dynamique',
      'Audience': 'Display',
      'MaxConversions': 'Conversions maximales',
      // Mappings avec préfixe « Type » renvoyés par l'API Microsoft Ads
      'Type Search & content': 'Recherche',
      'Type Shopping': 'Shopping',
      'Type Performance max': 'Performance Max',
      'Type Audience': 'Display'
    };
    const originalType = campaign.advertisingChannelType || campaign.type;
    return channelTypeMapping[originalType] || originalType || 'Non défini';
  }, []);

  // Helpers pour vérifier si une métrique est sélectionnée pour chaque composant Microsoft Ads
  const isMsadsCampaignMetricSelected = useCallback((metricKey) => {
    return msadsCampaignsMetrics.metrics.includes(metricKey);
  }, [msadsCampaignsMetrics.metrics]);

  const isMsadsAdGroupMetricSelected = useCallback((metricKey) => {
    return msadsAdGroupsMetrics.metrics.includes(metricKey);
  }, [msadsAdGroupsMetrics.metrics]);

  const isMsadsDeviceMetricSelected = useCallback((metricKey) => {
    return msadsDevicesMetrics.metrics.includes(metricKey);
  }, [msadsDevicesMetrics.metrics]);

  // État pour le filtre par campagne (Microsoft Ads)
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState(null);
  // Filtre par type de campagne, alimenté par un clic sur une ligne du tableau
  // « Types de campagnes ». Il porte sur ce tableau et sur les tableaux enfants.
  const [selectedCampaignTypeFilter, setSelectedCampaignTypeFilter] = useState(null);
  const [msCampaignSearchQuery, setMsCampaignSearchQuery] = useState('');
  const [showMsCampaignAutocomplete, setShowMsCampaignAutocomplete] = useState(false);

  // État pour le filtre par réseau (Microsoft Ads)
  const [selectedNetworkFilter, setSelectedNetworkFilter] = useState(null);

  // État pour les labels Microsoft Ads : seule la sélection reste locale, les données
  // viennent du hook partagé (voir plus bas).
  const [msSelectedLabelsFilter, setMsSelectedLabelsFilter] = useState([]);
  const [showMsLabelsDropdown, setShowMsLabelsDropdown] = useState(false);

  // Réinitialiser le filtre de campagne et réseau quand on change de compte ou de modèle de dashboard (Microsoft Ads)
  // Note: les filtres persistent lors du changement de période pour permettre la comparaison
  useEffect(() => {
    if (selectedCampaignFilter || msCampaignSearchQuery || msSelectedLabelsFilter.length > 0 || selectedNetworkFilter) {
      console.log('🔄 [MS-CAMPAIGN-FILTER] Réinitialisation des filtres lors du changement de compte/modèle');
      setSelectedCampaignFilter(null);
      setMsCampaignSearchQuery('');
      setShowMsCampaignAutocomplete(false);
      setMsSelectedLabelsFilter([]);
      setShowMsLabelsDropdown(false);
      setSelectedNetworkFilter(null);
    }
  }, [selectedAccount, selectedDashboardModel]);

  /**
   * Les labels des campagnes Microsoft Ads, par le hook partagé.
   *
   * ⚠️ **Le compte est désormais lu par son `sourceId`**, et non plus par son cuid. Ce bloc
   * interrogeait `/api/campaigns` avec `selectedAccount.id` alors que la synchronisation, elle,
   * enregistre sous `sourceId || id` : dès que les deux diffèrent — c'est-à-dire chaque fois que
   * le compte porte un `sourceId` —, la lecture ne trouvait rien et le filtre par label restait
   * vide sans le dire.
   */
  const {
    labelsParCampagne: msCampaignsWithLabels,
    labelsDisponibles: msAvailableLabels
  } = useCampagnesEtiquetees({
    accountId: selectedAccount?.sourceId || selectedAccount?.id,
    accountType: 'MICROSOFT_ADS',
    token
  });

  // Charger les notes du dashboard quand le compte ou les dates changent
  useEffect(() => {
    if (!selectedAccount || !fetchDashboardNotes) return;
    const accountId = selectedAccount?.sourceId || selectedAccount?.id;
    fetchDashboardNotes('MICROSOFT_ADS', accountId, customStartDate, customEndDate);
  }, [selectedAccount, customStartDate, customEndDate, fetchDashboardNotes]);

  // Toggle label filter Microsoft Ads
  const toggleMsLabelFilter = (label) => {
    setMsSelectedLabelsFilter(prev =>
      prev.includes(label)
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  // Calculer les IDs de campagnes filtrées par labels (Microsoft Ads)
  const msFilteredCampaignIds = React.useMemo(() => {
    if (msSelectedLabelsFilter.length === 0) {
      return null;
    }

    const ids = Object.entries(msCampaignsWithLabels)
      .filter(([campaignId, labels]) =>
        msSelectedLabelsFilter.some(label => labels.includes(label))
      )
      .map(([campaignId]) => campaignId);

    console.log('🏷️ [MS-FILTER] IDs filtrés:', ids.length);
    return ids;
  }, [msSelectedLabelsFilter, msCampaignsWithLabels]);
  const [geoSortConfig, setGeoSortConfig] = useState({ key: 'cost', direction: 'desc' });
  const [searchTermsLoading, setSearchTermsLoading] = useState(false);
  const [tempStartDate, setTempStartDate] = useState('');
  const [tempEndDate, setTempEndDate] = useState('');
  
  // États pour contrôler la visibilité des sections
  const [showPerformanceChart, setShowPerformanceChart] = useState(true);
  const [showCampaignDetails, setShowCampaignDetails] = useState(true);
  const [showMsAdGroupDetails, setShowMsAdGroupDetails] = useState(true);
  const [showCampaignTypes, setShowCampaignTypes] = useState(true);
  const [showDeviceConversions, setShowDeviceConversions] = useState(true);
  const [showWeekdayConversions, setShowWeekdayConversions] = useState(true);
  const [showGeoConversions, setShowGeoConversions] = useState(true);
  const [showSearchTerms, setShowSearchTerms] = useState(true);
  const [showKeywords, setShowKeywords] = useState(true);
  const [showAiModal, setShowAiModal] = useState(false);

  // Calculer le clientId effectif à partir du compte Microsoft Ads sélectionné
  // Fallback: utiliser l'ID du compte Microsoft Ads préfixé si pas de site associé
  const effectiveClientId = React.useMemo(() => {
    if (selectedClientId) return selectedClientId;
    if (!selectedAccount) return null;
    const accountId = selectedAccount.id || selectedAccount.sourceId;
    const accountSite = userSites.find(s =>
      s.accountIds?.includes(selectedAccount.id) ||
      s.accountIds?.includes(selectedAccount.sourceId)
    );
    return accountSite?.clientId || (accountId ? `msads_${accountId}` : null);
  }, [selectedClientId, selectedAccount, userSites]);

  // Hook pour la visibilité des modules par client (persistée en base de données)
  const {
    isModuleVisible: isClientModuleVisible,
    toggleModuleVisibility: toggleClientModuleVisibility,
    setModuleVisibility: setClientModuleVisibility,
    hiddenModules: clientHiddenModules
  } = useClientModuleVisibility(effectiveClientId, userRole);

  // State pour le modal des modules masqués (Microsoft Ads)
  const [isHiddenModulesModalOpen, setIsHiddenModulesModalOpen] = useState(false);

  const [userBrief, setUserBrief] = useState('');
  const [showAddModal, setShowAddModal] = useState(false);

  // Réinitialiser les données quand l'onglet change
  React.useEffect(() => {
    if (activeTab === 'microsoft') {
      setAutoCharts(null);
      setLoadingCharts(false);
      setLoadingInsights(false);
    }
  }, [activeTab]);

  // Initialiser showCustomDates selon selectedPeriod
  useEffect(() => {
    setShowCustomDates(selectedPeriod === 'CUSTOM');
  }, [selectedPeriod]);

  // Charger les éléments de la bibliothèque Microsoft Ads
  useEffect(() => {
    loadLibraryItems();
  }, []);

  // Présélectionner le compte si fourni depuis la navigation ou la sidebar
  useEffect(() => {
    const shouldChangeAccount = preselectedAccountId &&
      libraryItems.length > 0 &&
      (!selectedAccount || selectedAccount.id !== preselectedAccountId);

    if (shouldChangeAccount) {
      console.log('🎯 MicrosoftAds - Changement de compte vers:', preselectedAccountId);
      const targetAccount = libraryItems.find(item => item.id === preselectedAccountId);
      if (targetAccount) {
        console.log('✅ MicrosoftAds - Compte trouvé:', targetAccount.name);
        setSelectedAccount(targetAccount);
        setCurrentActiveAccount(targetAccount.id);
        setSelectedSources([targetAccount.id]);
        setPreselectionUsed(true);
        window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: targetAccount.id, accountName: targetAccount.name } }));
        // Mettre à jour l'URL avec le compte sélectionné (pour persistance au refresh)
        window.updateUrlWithAccount?.(targetAccount.id);
      } else {
        console.log('⚠️ MicrosoftAds - Compte non trouvé dans la liste, réinitialisation');
        // Réinitialiser car le compte demandé n'existe pas (changement de client)
        setSelectedAccount(null);
        setCurrentActiveAccount(null);
        setSelectedSources([]);
        setAutoCharts(null);
        setPreselectionUsed(false);
      }
    } else if (!preselectedAccountId && selectedAccount && libraryItems.length > 1) {
      // Ne pas réinitialiser si un seul compte (cas d'auto-sélection)
      console.log('🔄 MicrosoftAds - Réinitialisation (Tous les comptes)');
      setSelectedAccount(null);
      setCurrentActiveAccount(null);
      setSelectedSources([]);
      setAutoCharts(null);
      setPreselectionUsed(false);
    }
  }, [preselectedAccountId, libraryItems]);

  // Générer automatiquement le dashboard lors de la présélection d'un compte
  useEffect(() => {
    if (selectedAccount && preselectedAccountId && selectedAccount.id === preselectedAccountId && !autoCharts) {
      console.log('🚀 MicrosoftAds - Génération automatique du dashboard après présélection');
      generateAutoDashboard();
    }
  }, [selectedAccount, preselectedAccountId, autoCharts]);
  
  // Ref pour savoir si on avait un preselectedAccountId (venant de Synthèse)
  const hadPreselectedAccountRef = useRef(false);
  
  // Tracker quand on reçoit un preselectedAccountId
  useEffect(() => {
    if (preselectedAccountId !== null) {
      hadPreselectedAccountRef.current = true;
    }
  }, [preselectedAccountId]);
  
  // Réinitialiser l'état seulement si on avait un preselectedAccountId et qu'il a été nettoyé
  useEffect(() => {
    if (preselectedAccountId === null && hadPreselectedAccountRef.current && selectedAccount) {
      console.log('🔄 MicrosoftAds - Réinitialisation état après nettoyage preselectedAccountId (retour de Synthèse)');
      setSelectedAccount(null);
      setCurrentActiveAccount(null); // Réinitialiser le compte actif global
      setAutoCharts(null);
      setAiInsights(null);
      hadPreselectedAccountRef.current = false; // Reset du flag
    }
  }, [preselectedAccountId, selectedAccount]);

  // Note: Le gestionnaire accountChange a été supprimé car le useEffect sur preselectedAccountId
  // gère maintenant les changements de compte depuis la sidebar de manière plus fiable

  // Gestionnaire pour la génération automatique depuis la sidebar
  useEffect(() => {
    const handleAutoGenerate = (event) => {
      const { platform } = event.detail;
      if (platform !== 'microsoft') return;

      console.log('🚀 MicrosoftAds - Clic sur plateforme depuis sidebar');

      // Attendre que le filtrage par client soit terminé (le useEffect de filtrage peut ne pas encore avoir été exécuté)
      setTimeout(() => {
        // Utiliser la ref pour avoir la valeur la plus récente de libraryItems
        const currentFilteredItems = libraryItemsRef.current;

        console.log('🔍 MicrosoftAds - Comptes disponibles après filtrage:', currentFilteredItems.length);

        // Si pas de compte sélectionné
        if (!selectedAccount && currentFilteredItems.length > 0) {
          // Si un seul compte disponible, le sélectionner et générer automatiquement
          if (currentFilteredItems.length === 1) {
            const singleAccount = currentFilteredItems[0];
            console.log('📌 MicrosoftAds - Un seul compte disponible, sélection auto:', singleAccount.name);
            setSelectedAccount(singleAccount);
            setCurrentActiveAccount(singleAccount.id);
            setSelectedSources([singleAccount.id]);
            const accountSite = userSites.find(s => s.accountIds?.includes(singleAccount.id) || s.accountIds?.includes(singleAccount.sourceId));
            window.currentNotesContext = {
              platform: 'MICROSOFT_ADS',
              accountId: singleAccount.sourceId || singleAccount.id,
              accountName: singleAccount.name,
              siteId: accountSite?._id || accountSite?.id || null,
              clientId: accountSite?.clientId || null
            };
            // Déclencher la génération
            setTimeout(() => {
              if (window.microsoftDashboardGenerateAuto) {
                window.microsoftDashboardGenerateAuto();
              }
            }, 200);
          } else {
            // Plusieurs comptes disponibles, ne pas sélectionner automatiquement
            console.log('📋 MicrosoftAds - Plusieurs comptes disponibles, attente de sélection manuelle');
          }
        } else if (selectedAccount) {
          // Un compte est déjà sélectionné, générer le dashboard
          setTimeout(() => {
            if (window.microsoftDashboardGenerateAuto) {
              window.microsoftDashboardGenerateAuto();
            }
          }, 200);
        }
      }, 400); // Délai pour permettre au filtrage asynchrone de s'exécuter et à la ref d'être mise à jour
    };

    window.addEventListener('dashboardAutoGenerate', handleAutoGenerate);
    return () => {
      window.removeEventListener('dashboardAutoGenerate', handleAutoGenerate);
    };
  }, [selectedAccount]);

  // Gestionnaire pour la sélection de compte depuis la sidebar (Microsoft Ads)
  useEffect(() => {
    const handleSidebarAccountChange = (event) => {
      const { accountId, accountName } = event.detail;
      console.log('🔄 MicrosoftAds - Changement de compte depuis sidebar:', accountId, accountName);

      if (!accountId) {
        // "Tous les comptes" sélectionné
        setSelectedAccount(null);
        setCurrentActiveAccount(null);
        setSelectedSources([]);
        setAutoCharts(null);
        return;
      }

      // Chercher le compte dans la liste
      const targetAccount = libraryItemsRef.current.find(item =>
        item.id === accountId || item.sourceId === accountId
      );

      if (targetAccount) {
        setSelectedAccount(targetAccount);
        setCurrentActiveAccount(targetAccount.id);
        setSelectedSources([targetAccount.id]);
        const targetSite = userSites.find(s => s.accountIds?.includes(targetAccount.id) || s.accountIds?.includes(targetAccount.sourceId));
        window.currentNotesContext = {
          platform: 'MICROSOFT_ADS',
          accountId: targetAccount.sourceId || targetAccount.id,
          accountName: targetAccount.name,
          siteId: targetSite?._id || targetSite?.id || null,
          clientId: targetSite?.clientId || null
        };
        // Générer automatiquement le dashboard
        setTimeout(() => {
          if (window.microsoftDashboardGenerateAuto) {
            window.microsoftDashboardGenerateAuto();
          }
          // Mettre à jour l'URL après que activeTab soit synchronisé
          window.updateUrlWithAccount?.(targetAccount.id);
        }, 200);
      }
    };

    window.addEventListener('sidebarAccountChange', handleSidebarAccountChange);
    return () => {
      window.removeEventListener('sidebarAccountChange', handleSidebarAccountChange);
    };
  }, []);

  // Filtrer les comptes par client ou par site (Microsoft Ads)
  const filterAccountsByClient = async (accounts, clientId, siteId) => {
    // Si un site spécifique est sélectionné, filtrer par ce site en utilisant userSites
    if (siteId && siteId !== 'all' && accounts.length) {
      const site = userSites.find(s => (s._id || s.id) === siteId);
      if (site && site.accountIds) {
        const allowedAccountIds = ensembleAutorise(site.accountIds);
        const filteredAccounts = accounts.filter(account => compteAutorise(account, allowedAccountIds));
        return filteredAccounts;
      }
    }

    if (!clientId || !accounts.length) {
      return accounts;
    }

    try {
      // Récupérer les sites associés au client
      const response = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { clientId }
      });

      const sites = response.data.sites || [];

      // Extraire tous les accountIds des sites du client
      const allowedAccountIds = ensembleAutorise(
        sites.flatMap(site => (Array.isArray(site.accountIds) ? site.accountIds : []))
      );

      const filteredAccounts = accounts.filter(account => compteAutorise(account, allowedAccountIds));

      return filteredAccounts;
    } catch (error) {
      console.error('Erreur filtrage comptes par client:', error);
      return accounts;
    }
  };

  // Refs pour tracker le site et client précédents (Microsoft Ads)
  const msPrevSiteIdRef = useRef(selectedSiteId);
  const msPrevClientIdRef = useRef(selectedClientId);

  // Recharger la liste des comptes et gérer la sélection quand le site/client change (Microsoft Ads)
  useEffect(() => {
    const siteChanged = msPrevSiteIdRef.current !== selectedSiteId;
    const clientChanged = msPrevClientIdRef.current !== selectedClientId;
    msPrevSiteIdRef.current = selectedSiteId;
    msPrevClientIdRef.current = selectedClientId;

    const filterAndSelectAccount = async () => {
      if (siteChanged || clientChanged) {
        console.log('🔄 [MICROSOFT ADS] Changement de site/client détecté:', { selectedSiteId, selectedClientId });
        // IMPORTANT: Réinitialiser le flag de présélection pour éviter que l'useEffect
        // de réinitialisation ne supprime le compte auto-sélectionné
        hadPreselectedAccountRef.current = false;

        // IMMÉDIAT: Effacer les données du dashboard dès le changement de client
        // pour un feedback visuel instantané (avant le filtrage asynchrone)
        console.log('🔄 [MICROSOFT ADS] Effacement immédiat des données (changement client)');
        setAutoCharts(null);
        setAiInsights(null);
        setMsAdGroupsData(null);
        setError('');
        setSelectedAccount(null);
        setCurrentActiveAccount(null);
        setSelectedSources([]);
      }

      if (allLibraryItems.length > 0) {
        console.log('🔍 [MICROSOFT ADS] Filtrage des comptes pour site:', selectedSiteId);
        const filteredItems = await filterAccountsByClient(allLibraryItems, selectedClientId, selectedSiteId);
        console.log('🔍 [MICROSOFT ADS] Comptes filtrés:', filteredItems.length);
        setLibraryItems(filteredItems);

        // Si un seul compte disponible après filtrage, l'auto-sélectionner
        // (sauf si un compte présélectionné EXISTE dans la liste)
        const preselectedExistsInList = preselectedAccountId && filteredItems.some(item => item.id === preselectedAccountId);
        if (filteredItems.length === 1 && !preselectedExistsInList) {
          const account = filteredItems[0];
          console.log('🎯 [MICROSOFT ADS] Auto-sélection du seul compte disponible:', account.name);
          setSelectedAccount(account);
          setCurrentActiveAccount(account.id);
          setSelectedSources([account.id]);
          // Mettre à jour le contexte des notes
          const accountSite = userSites.find(s => s.accountIds?.includes(account.id) || s.accountIds?.includes(account.sourceId));
          window.currentNotesContext = {
            platform: 'MICROSOFT_ADS',
            accountId: account.sourceId || account.id,
            accountName: account.name,
            siteId: accountSite?._id || accountSite?.id || null,
            clientId: accountSite?.clientId || null
          };
          // Mettre à jour le breadcrumb
          window.dispatchEvent(new CustomEvent('accountSelected', {
            detail: { accountId: account.id, accountName: account.name }
          }));
        }
      }
    };

    filterAndSelectAccount();
  }, [selectedClientId, selectedSiteId, allLibraryItems]);

  // Logique de réinitialisation simplifiée - plus besoin d'événements complexes

  // Refs removed - simplified logic for account selection persistence
  
  // Reset logic removed - keep selected account after navigation from Synthèse

  // Ne pas sélectionner automatiquement de compte
  useEffect(() => {
    // Laisser l'utilisateur choisir un compte
  }, [libraryItems, selectedAccount]);

  const loadLibraryItems = async () => {
    setLoadingLibraryItems(true);
    try {
      const response = await fetch(`${API_URL}/api/library`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success && data.library) {
        // Chercher les comptes Microsoft Ads
        const msAdsItems = data.library.MICROSOFT_ADS || [];
        console.log(`🔷 ${msAdsItems.length} comptes Microsoft Ads trouvés pour le dashboard`);
        
        // Déduplication basée sur l'ID unique
        const uniqueMsAdsItems = msAdsItems.filter((item, index, self) => 
          index === self.findIndex((t) => t.id === item.id)
        );
        console.log(`🔷 Après déduplication: ${uniqueMsAdsItems.length} comptes uniques`);
        
        setAllLibraryItems(uniqueMsAdsItems); // Garder une copie des comptes non filtrés
        setLibraryItems(uniqueMsAdsItems);
      } else {
        console.log('Erreur API:', data.error);
        setError(data.error || 'Erreur lors du chargement des comptes Microsoft Ads');
      }
    } catch (error) {
      console.error('Erreur chargement bibliothèque:', error);
      setError('Erreur lors du chargement des comptes Microsoft Ads');
    } finally {
      setLoadingLibraryItems(false);
    }
  };

  const handleConnectMicrosoftAds = () => {
    window.location.href = `${API_URL}/api/auth/microsoft-ads?token=${token}&returnTo=connectors`;
  };

  // Ref pour accéder à la valeur la plus récente de selectedAccount dans les callbacks async
  const selectedAccountRef = useRef(selectedAccount);
  useEffect(() => {
    selectedAccountRef.current = selectedAccount;
  }, [selectedAccount]);

  // Ref pour éviter les appels concurrents
  const isGeneratingRef = useRef(false);

  // Générer le dashboard automatique
  const generateAutoDashboard = async (accountOverride = null) => {
    const accountToUse = accountOverride || selectedAccountRef.current || selectedAccount;
    if (!accountToUse) {
      setError('Veuillez sélectionner un compte Microsoft Ads');
      return;
    }

    // Éviter les appels concurrents
    if (isGeneratingRef.current) {
      console.log('🔷 Génération déjà en cours, ignoré');
      return;
    }
    isGeneratingRef.current = true;

    setLoadingCharts(true);
    setAutoCharts(null);
    
    try {
      console.log('🔷 Génération dashboard Microsoft Ads pour:', accountToUse?.name || 'compte', 'Période:', selectedPeriod);

      const requestBody = {
        sources: [accountToUse?.id],
        period: selectedPeriod,
        granularity: granularity
      };

      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) {
          setError('Veuillez sélectionner une date de début et une date de fin');
          setLoadingCharts(false);
          isGeneratingRef.current = false;
          return;
        }
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }
      
      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        requestBody.clientId = selectedClientId;
        console.log('🎯 [MS-DASHBOARD] Filtrage par client:', selectedClientId);
      }

      // Ajouter le filtre réseau si un réseau est sélectionné
      if (selectedNetworkFilter) {
        requestBody.networkFilter = selectedNetworkFilter;
        console.log('🌐 [MS-DASHBOARD] Filtrage par réseau:', selectedNetworkFilter.networkName);
      }

      // Filtre par type de campagne : appliqué côté serveur, avant agrégation, faute de
      // référence de campagne dans les réponses réseaux, appareils et jours.
      if (selectedCampaignTypeFilter?.rawTypes?.length) {
        requestBody.campaignTypeFilter = selectedCampaignTypeFilter;
        console.log('🏷️ [MS-DASHBOARD] Filtrage par type de campagne:', selectedCampaignTypeFilter.name);
      }

      /**
       * Les bornes de la période regardée ; le serveur en dérive la précédente.
       *
       * ⚠️ La table des périodes était recopiée ici, et sérialisée par `toISOString()` sur
       * des dates à minuit **local** : « Mois dernier » demandait le 31 août au 29 septembre
       * au lieu du 1er au 30 septembre. `getDateRangeFromPeriod` est celle que le sélecteur
       * affiche.
       */
      const plageRegardee = selectedPeriod === 'CUSTOM' && customStartDate && customEndDate
        ? { startDate: customStartDate, endDate: customEndDate }
        : getDateRangeFromPeriod(selectedPeriod);

      const previousPeriodBody = {
        accountId: accountToUse?.id,
        startDate: plageRegardee.startDate,
        endDate: plageRegardee.endDate,
        comparisonType: msadsComparisonType || 'previous_period',
        comparisonStartDate: comparisonStartDate,
        comparisonEndDate: comparisonEndDate
      };

      // Fonction helper pour fetch le timeline avec retry
      const fetchTimelineWithRetry = async (maxRetries = 2) => {
        for (let attempt = 1; attempt <= maxRetries; attempt++) {
          try {
            const response = await fetch(`${API_URL}/api/dashboard/microsoft-ads/timeline`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`
              },
              body: JSON.stringify(requestBody)
            });
            const data = await response.json();

            // Si on a des données, retourner immédiatement
            if (data.success && data.charts?.length > 0) {
              console.log(`✅ [MS-TIMELINE] Tentative ${attempt}: données reçues`);
              return data;
            }

            // Si pas de données et pas dernier essai, attendre et réessayer
            if (attempt < maxRetries) {
              console.log(`⚠️ [MS-TIMELINE] Tentative ${attempt}: pas de données, retry dans 1s...`);
              await new Promise(resolve => setTimeout(resolve, 1000));
            } else {
              console.log(`⚠️ [MS-TIMELINE] Tentative ${attempt}: pas de données après ${maxRetries} essais`);
              return data;
            }
          } catch (error) {
            console.error(`❌ [MS-TIMELINE] Tentative ${attempt} échouée:`, error.message);
            if (attempt === maxRetries) {
              return { success: false, charts: [], rawData: {} };
            }
            await new Promise(resolve => setTimeout(resolve, 1000));
          }
        }
        return { success: false, charts: [], rawData: {} };
      };

      // Charger les données en parallèle: dashboard principal + timeline (avec retry) + période précédente
      const [dashboardResponse, timelineData, previousPeriodResponse] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/microsoft-ads/charts`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(requestBody)
        }),
        fetchTimelineWithRetry(2),
        fetch(`${API_URL}/api/dashboard/microsoft-ads/previous-period`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify(previousPeriodBody)
        })
      ]);

      const dashboardData = await dashboardResponse.json();
      const previousPeriodResult = await previousPeriodResponse.json();

      console.log('🔷 Réponse dashboard Microsoft Ads:', dashboardData);
      console.log('🔷 Réponse timeline Microsoft Ads:', timelineData);
      console.log('🔷 Réponse période précédente Microsoft Ads:', previousPeriodResult);

      // Traiter la période précédente
      if (previousPeriodResult?.success && previousPeriodResult?.data?.hasData) {
        console.log('✅ Données période précédente Microsoft Ads intégrées');
        console.log('📊 [MS-PREVIOUS] Contenu détaillé:', {
          campaigns: previousPeriodResult.data?.campaigns?.length || 0,
          adGroups: previousPeriodResult.data?.adGroups?.length || 0,
          searchTerms: previousPeriodResult.data?.searchTerms?.length || 0,
          devices: previousPeriodResult.data?.devices?.length || 0,
          geography: previousPeriodResult.data?.geography?.length || 0
        });
        if (previousPeriodResult.data?.adGroups?.length > 0) {
          console.log('📊 [MS-PREVIOUS] Premier adGroup:', previousPeriodResult.data.adGroups[0]);
        }
        setPreviousPeriodData(previousPeriodResult.data);
      } else {
        console.log('⚠️ Période précédente Microsoft Ads non disponible');
        setPreviousPeriodData(null);
      }

      if (dashboardData.success) {
        // Fusionner les données dashboard et timeline en une seule mise à jour d'état
        // pour éviter le flash "Aucune donnée disponible"
        const timelineCharts = timelineData.success ? (timelineData.charts || []) : [];
        const mergedData = {
          ...dashboardData,
          // Fusionner les charts timeline dans le tableau principal pour que PerformanceChart trouve 'real-performance-timeline'
          charts: [...(dashboardData.charts || []), ...timelineCharts],
          timelineCharts: timelineCharts,
          timelineRawData: timelineData.success ? timelineData.rawData : null,
          // Fusionner aussi rawData si disponible
          rawData: timelineData.success && timelineData.rawData
            ? { ...(dashboardData.rawData || {}), ...timelineData.rawData }
            : dashboardData.rawData
        };

        setAutoCharts(mergedData);
        setError('');
        console.log(`✅ Dashboard Microsoft Ads généré: ${dashboardData.charts?.length || 0} graphiques`);

        // Sync campagnes Microsoft Ads en arrière-plan pour labels/objectifs
        const msCampaigns = dashboardData.rawData ?
          Object.values(dashboardData.rawData).find(data => data.campaigns && data.campaigns.length > 0)?.campaigns || [] : [];
        if (msCampaigns.length > 0) {
          console.log('🔄 [MS-SYNC] accountToUse:', accountToUse);
          console.log('🔄 [MS-SYNC] sourceId:', accountToUse?.sourceId, 'id:', accountToUse?.id);
          console.log('🔄 [MS-SYNC] Campagnes à synchroniser:', msCampaigns.length);
          syncCampaignsInBackground(token, accountToUse.sourceId || accountToUse.id, 'MICROSOFT_ADS', msCampaigns);
        }

        if (timelineData.success && timelineData.rawData) {
          console.log(`✅ Timeline Microsoft Ads chargée: données disponibles avec ${Object.keys(timelineData.rawData).length} comptes`);
        } else {
          console.log(`⚠️ Timeline Microsoft Ads: pas de données disponibles`);
        }
      } else {
        setError(dashboardData.error || 'Erreur génération dashboard Microsoft Ads');
      }

    } catch (error) {
      console.error('Erreur génération dashboard:', error);
      setError('Erreur réseau lors de la génération du dashboard');
    } finally {
      setLoadingCharts(false);
      isGeneratingRef.current = false;
    }
  };

  // Fonction pour récupérer les groupes d'annonces Microsoft Ads
  const fetchMicrosoftAdsAdGroups = useCallback(async () => {
    if (!selectedAccount || !token) return;

    setLoadingMsAdGroups(true);
    try {
      console.log('🎯 Récupération des groupes d\'annonces Microsoft Ads...');
      console.log(`🎯 [AD-GROUPS] Période envoyée: ${selectedPeriod}`);
      if (selectedPeriod === 'CUSTOM') {
        console.log(`🎯 [AD-GROUPS] Dates personnalisées: ${customStartDate} → ${customEndDate}`);
      }

      const requestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod
      };

      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) {
          setError('Veuillez sélectionner une date de début et une date de fin');
          setLoadingMsAdGroups(false);
          return;
        }
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      // Ajouter le filtre réseau si un réseau est sélectionné
      if (selectedNetworkFilter) {
        requestBody.networkFilter = selectedNetworkFilter;
        console.log('🌐 [AD-GROUPS] Filtrage par réseau:', selectedNetworkFilter.networkName);
      }

      // Filtre par type de campagne, appliqué côté serveur (les groupes d'annonces ne
      // portent pas le type : la route passe par les noms de campagnes).
      if (selectedCampaignTypeFilter?.rawTypes?.length) {
        requestBody.campaignTypeFilter = selectedCampaignTypeFilter;
      }

      const response = await fetch(`${API_URL}/api/dashboard/microsoft-ads/ad-groups`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log('🎯 Réponse groupes d\'annonces Microsoft Ads:', data);

      if (data.success) {
        setMsAdGroupsData(data.adGroups);
        console.log(`✅ ${data.adGroups?.length || 0} groupes d'annonces récupérés`);
      } else {
        console.error('❌ Erreur récupération groupes d\'annonces:', data.error);
        setMsAdGroupsData([]);
      }
    } catch (error) {
      console.error('❌ Erreur réseau groupes d\'annonces:', error);
      setMsAdGroupsData([]);
    } finally {
      setLoadingMsAdGroups(false);
    }
  }, [selectedAccount, token, selectedPeriod, customStartDate, customEndDate, selectedNetworkFilter, selectedCampaignTypeFilter]);

  // Synchroniser le breadcrumb quand un compte est sélectionné (Microsoft Ads)
  useEffect(() => {
    if (selectedAccount) {
      console.log('🔄 [BREADCRUMB] Synchronisation compte Microsoft Ads:', selectedAccount.name);
      window.dispatchEvent(new CustomEvent('accountSelected', {
        detail: { accountId: selectedAccount.id, accountName: selectedAccount.name }
      }));
    }
  }, [selectedAccount]);

  // Générer automatiquement le dashboard quand le compte, la période ou le client change
  useEffect(() => {
    // Ne pas générer si aucun compte disponible
    if (libraryItems.length === 0) {
      return;
    }
    if (selectedAccount && selectedSources.length > 0) {
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      generateAutoDashboard();
      // Toujours charger les groupes d'annonces pour le dashboard Microsoft Ads quand la période change
      // Note: On lance toujours le rechargement pour s'assurer d'avoir les données de la bonne période
      console.log(`🎯 [DASHBOARD-GENERATION] Chargement groupes d'annonces Microsoft Ads (période: ${selectedPeriod})...`);
      fetchMicrosoftAdsAdGroups();
    }
  }, [selectedAccount, selectedSources, selectedPeriod, customStartDate, customEndDate, selectedNetworkFilter, selectedCampaignTypeFilter, libraryItems.length]);

  // Auto-charger les termes de recherche si la section est ouverte (sauf dans le dashboard Global)
  useEffect(() => {
    // Ne pas charger les termes de recherche dans le dashboard Global
    if (selectedDashboardModel === 'global') {
      return;
    }
    
    console.log(`🔍 [AUTO-LOAD] Vérification conditions auto-chargement...`);
    console.log(`🔍 [AUTO-LOAD] showSearchTerms: ${showSearchTerms}`);
    console.log(`🔍 [AUTO-LOAD] selectedAccount: ${!!selectedAccount}`);
    
    const hasSearchTermsData = autoCharts?.rawData && Object.values(autoCharts.rawData).some(account => 
      account.searchTerms && account.searchTerms.searchTerms && account.searchTerms.searchTerms.length > 0
    );
    
    console.log(`🔍 [AUTO-LOAD] hasSearchTermsData: ${hasSearchTermsData}`);
    
    if (showSearchTerms && selectedAccount && !hasSearchTermsData) {
      console.log(`🚀 [AUTO-LOAD] Conditions remplies - Chargement automatique page 1...`);

      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        console.log(`⏳ [AUTO-LOAD] Attente des dates personnalisées...`);
        return;
      }

      loadSearchTermsPage();
    } else {
      console.log(`ℹ️ [AUTO-LOAD] Conditions non remplies - pas de chargement automatique`);
    }
  }, [showSearchTerms, selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedDashboardModel]);

  // Ref pour tracker le filtre précédent
  const previousCampaignFilterRef = useRef(selectedCampaignFilter);

  // Recharger les termes de recherche quand le filtre campagne change (y compris quand effacé)
  useEffect(() => {
    const hadFilter = previousCampaignFilterRef.current !== null;
    const hasFilter = selectedCampaignFilter !== null;
    const filterChanged = previousCampaignFilterRef.current !== selectedCampaignFilter;

    // Mettre à jour le ref
    previousCampaignFilterRef.current = selectedCampaignFilter;

    // Recharger si le filtre a changé (ajouté, modifié ou supprimé)
    if (showSearchTerms && selectedAccount && filterChanged) {
      if (hasFilter) {
        console.log(`🔍 [CAMPAIGN-FILTER] Filtre campagne appliqué - rechargement des termes...`);
      } else if (hadFilter) {
        console.log(`🔍 [CAMPAIGN-FILTER] Filtre campagne effacé - rechargement de tous les termes...`);
      }
      setMsSearchTermsCurrentPage(1);
      loadSearchTermsPage();
    }
  }, [selectedCampaignFilter]);

  // Recharger les termes de recherche quand la page change
  useEffect(() => {
    if (showSearchTerms && selectedAccount && msSearchTermsCurrentPage > 1) {
      console.log(`📄 [PAGE-CHANGE] Changement de page vers ${msSearchTermsCurrentPage} - rechargement des termes...`);

      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        console.log(`⏳ [PAGE-CHANGE] Attente des dates personnalisées...`);
        return;
      }

      loadSearchTermsPage();
    }
  }, [msSearchTermsCurrentPage]);

  // Ref pour tracker le filtre réseau précédent (pour les termes de recherche)
  const previousNetworkFilterRef = useRef(selectedNetworkFilter);
  const previousCampaignTypeFilterRef = useRef(null);

  // Recharger les termes de recherche quand le filtre réseau ou le filtre par type change
  useEffect(() => {
    const hadFilter = previousNetworkFilterRef.current !== null;
    const hasFilter = selectedNetworkFilter !== null;
    // Le changement doit être évalué sur les deux filtres : ne comparer que le réseau
    // laissait un changement de type sans effet, les termes restaient non filtrés.
    const networkChanged = previousNetworkFilterRef.current !== selectedNetworkFilter;
    const typeChanged = previousCampaignTypeFilterRef.current !== selectedCampaignTypeFilter;
    const filterChanged = networkChanged || typeChanged;

    // Mettre à jour les refs
    previousNetworkFilterRef.current = selectedNetworkFilter;
    previousCampaignTypeFilterRef.current = selectedCampaignTypeFilter;

    // Recharger si le filtre a changé (ajouté, modifié ou supprimé)
    if (showSearchTerms && selectedAccount && filterChanged) {
      if (hasFilter) {
        console.log(`🌐 [NETWORK-FILTER] Filtre réseau appliqué - rechargement des termes...`);
      } else if (hadFilter) {
        console.log(`🌐 [NETWORK-FILTER] Filtre réseau effacé - rechargement de tous les termes...`);
      }
      setMsSearchTermsCurrentPage(1);
      loadSearchTermsPage();
    }
  }, [selectedNetworkFilter, selectedCampaignTypeFilter]);

  // Vider les données des groupes d'annonces quand la période change
  useEffect(() => {
    console.log(`🎯 [PERIOD-CHANGE] Période ou dates changées - vidage des groupes d'annonces...`);
    setMsAdGroupsData(null);
  }, [selectedPeriod, customStartDate, customEndDate]);

  // Réinitialiser la page des termes de recherche quand le compte ou la période change
  useEffect(() => {
    if (msSearchTermsCurrentPage !== 1) {
      console.log(`🔄 [RESET-PAGE] Réinitialisation de la page des termes de recherche à 1`);
      setMsSearchTermsCurrentPage(1);
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate]);

  // Auto-charger les groupes d'annonces si la section est ouverte
  useEffect(() => {
    console.log(`🎯 [AUTO-LOAD] Vérification conditions auto-chargement groupes d'annonces Microsoft Ads...`);
    console.log(`🎯 [AUTO-LOAD] showMsAdGroupDetails: ${showMsAdGroupDetails}`);
    console.log(`🎯 [AUTO-LOAD] selectedAccount: ${!!selectedAccount}`);
    console.log(`🎯 [AUTO-LOAD] msAdGroupsData: ${msAdGroupsData?.length || 0} éléments`);
    console.log(`🎯 [AUTO-LOAD] loadingMsAdGroups: ${loadingMsAdGroups}`);
    
    if (showMsAdGroupDetails && selectedAccount && !msAdGroupsData && !loadingMsAdGroups) {
      console.log(`🚀 [AUTO-LOAD] Conditions remplies - Chargement automatique des groupes d'annonces Microsoft Ads...`);
      
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        console.log(`⏳ [AUTO-LOAD] Attente des dates personnalisées...`);
        return;
      }
      
      fetchMicrosoftAdsAdGroups();
    } else {
      console.log(`ℹ️ [AUTO-LOAD] Conditions non remplies - pas de chargement automatique des groupes d'annonces`);
    }
  }, [showMsAdGroupDetails, selectedAccount, selectedPeriod, customStartDate, customEndDate, msAdGroupsData, loadingMsAdGroups, fetchMicrosoftAdsAdGroups]);

  // Charger les totaux des termes de recherche
  const loadSearchTermsTotals = async () => {
    if (!selectedAccount) {
      console.error('❌ Aucun compte sélectionné pour les totaux');
      return null;
    }

    console.log(`📊 Chargement des totaux des termes de recherche...`);
    
    try {
      const requestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod
      };

      if (selectedPeriod === 'CUSTOM') {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      const response = await fetch(`${API_URL}/api/dashboard/microsoft-ads/search-terms-totals`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();
      console.log(`📊 Totaux reçus:`, data);

      if (data.success && data.totals) {
        // Stocker les totaux
        setAutoCharts(prevCharts => {
          // Trouver la bonne clé pour stocker les données
          const possibleKeys = [
            selectedAccount?.name || selectedAccount?.id,
            selectedAccount.id,
            selectedAccount.sourceName,
            ...Object.keys(prevCharts?.rawData || {}).filter(key => 
              key.includes(selectedAccount?.name || selectedAccount?.id) || 
              key.includes((selectedAccount?.name || selectedAccount?.id).split(' (')[0])
            )
          ];
          
          // Utiliser la première clé qui existe déjà dans rawData, ou selectedAccount.length + ' compte(s)' par défaut
          let accountKey = selectedAccount?.name || selectedAccount?.id;
          for (const key of possibleKeys) {
            if (prevCharts?.rawData?.[key]) {
              accountKey = key;
              break;
            }
          }
          
          const newCharts = {
            ...prevCharts,
            rawData: {
              ...prevCharts?.rawData,
              [accountKey]: {
                ...prevCharts?.rawData?.[accountKey],
                searchTerms: {
                  ...prevCharts?.rawData?.[accountKey]?.searchTerms,
                  totals: data.totals
                }
              }
            }
          };
          return newCharts;
        });
        
        console.log(`✅ Totaux chargés: ${data.totals.totalTerms} termes`);
        return data.totals;
      }
    } catch (error) {
      console.error('❌ Erreur chargement totaux:', error);
    }
    return null;
  };

  // Charger une page spécifique des termes de recherche
  const loadSearchTermsPage = async () => {
    if (!selectedAccount) {
      console.error('❌ [OPTIMIZED] Aucun compte sélectionné pour les termes de recherche');
      return;
    }

    if (searchTermsLoading) {
      console.log('⏳ [OPTIMIZED] Chargement déjà en cours, abandon...');
      return;
    }

    console.log(`🚀 [OPTIMIZED] Début chargement des termes de recherche - Page 1...`);
    setSearchTermsLoading(true);
    
    try {
      const requestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod,
        limit: 20,
        page: msSearchTermsCurrentPage,
        campaignFilter: selectedCampaignFilter?.name || selectedCampaignFilter?.campaignName || null
      };

      if (selectedPeriod === 'CUSTOM') {
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      // Ajouter le filtre réseau si un réseau est sélectionné
      if (selectedNetworkFilter) {
        requestBody.networkFilter = selectedNetworkFilter;
        console.log('🌐 [SEARCH-TERMS] Filtrage par réseau:', selectedNetworkFilter.networkName);
      }

      // Filtre par type de campagne : appliqué côté serveur, avant les totaux et la
      // pagination, qui portent sur l'ensemble des termes et non sur la page affichée.
      if (selectedCampaignTypeFilter?.rawTypes?.length) {
        requestBody.campaignTypeFilter = selectedCampaignTypeFilter;
      }

      const response = await fetch(`${API_URL}/api/dashboard/microsoft-ads/search-terms`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success && data.searchTerms) {
        
        setAutoCharts(prevCharts => {
          // Trouver la bonne clé pour stocker les données
          const possibleKeys = [
            selectedAccount?.name || selectedAccount?.id,
            selectedAccount.id,
            selectedAccount.sourceName,
            ...Object.keys(prevCharts?.rawData || {}).filter(key => 
              key.includes(selectedAccount?.name || selectedAccount?.id) || 
              key.includes((selectedAccount?.name || selectedAccount?.id).split(' (')[0])
            )
          ];
          
          // Utiliser la première clé qui existe déjà dans rawData, ou selectedAccount.length + ' compte(s)' par défaut
          let accountKey = selectedAccount?.name || selectedAccount?.id;
          for (const key of possibleKeys) {
            if (prevCharts?.rawData?.[key]) {
              accountKey = key;
              break;
            }
          }
          
          
          // Extraire les totaux depuis searchTermsResults (summary)
          let extractedTotals = null;
          if (data.searchTermsResults) {
            // Chercher la summary dans les résultats par compte
            const firstKey = Object.keys(data.searchTermsResults)[0];
            if (firstKey && data.searchTermsResults[firstKey]?.summary) {
              const summary = data.searchTermsResults[firstKey].summary;
              extractedTotals = {
                totalImpressions: summary.totalImpressions || 0,
                totalClicks: summary.totalClicks || 0,
                totalCost: summary.totalCost || 0,
                totalConversions: summary.totalConversions || 0,
                totalConversionsValue: summary.totalConversionsValue || 0,
                totalTerms: summary.totalTerms || data.totalCount || 0
              };
              console.log(`📊 [SEARCH-TERMS] Totaux extraits de searchTermsResults:`, extractedTotals);
            }
          }

          const newCharts = {
            ...prevCharts,
            rawData: {
              ...prevCharts?.rawData,
              [accountKey]: {
                ...prevCharts?.rawData?.[accountKey],
                searchTerms: {
                  searchTerms: data.searchTerms,
                  pagination: data.pagination,
                  totalCount: data.totalCount,
                  totals: extractedTotals || prevCharts?.rawData?.[accountKey]?.searchTerms?.totals
                }
              }
            }
          };
          return newCharts;
        });

        console.log(`✅ [OPTIMIZED] Page ${data.pagination?.page || 1} des termes de recherche chargée avec succès`);
      } else {
      }
    } catch (error) {
      console.error('❌ [OPTIMIZED] Erreur chargement termes:', error);
    } finally {
      setSearchTermsLoading(false);
    }
  };

  // Exposer la fonction de génération automatique pour la sidebar
  useEffect(() => {
    window.microsoftDashboardGenerateAuto = generateAutoDashboard;
    return () => {
      delete window.microsoftDashboardGenerateAuto;
    };
  }, [generateAutoDashboard]);

  // Recharger les données de comparaison quand le type de comparaison change
  useEffect(() => {
    const loadMsadsComparisonData = async () => {
      // Si pas de compte sélectionné, on ne peut rien faire
      if (!selectedAccount) {
        return;
      }

      // Si pas de type de comparaison sélectionné, on garde les données existantes
      // (chargées par generateAutoDashboard) - ne pas les effacer !
      if (!msadsComparisonType) {
        return;
      }

      console.log('🔄 [MSADS-COMPARISON] Chargement données comparaison:', msadsComparisonType);

      try {
        // Les bornes de la période regardée. La table vit dans `utils/periodUtils` : celle
        // qui était écrite ici ne connaissait que cinq périodes sur onze, et toute autre
        // retombait en silence sur trente jours.
        const plage = selectedPeriod === 'CUSTOM' && customStartDate && customEndDate
          ? { startDate: customStartDate, endDate: customEndDate }
          : getDateRangeFromPeriod(selectedPeriod);
        const { startDate, endDate } = plage;

        const response = await fetch(`${API_URL}/api/dashboard/microsoft-ads/previous-period`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            accountId: selectedAccount?.id,
            startDate,
            endDate,
            comparisonType: msadsComparisonType,
            comparisonStartDate: comparisonStartDate,
            comparisonEndDate: comparisonEndDate
          })
        });

        const result = await response.json();
        if (result?.success && result?.data?.hasData) {
          console.log('✅ [MSADS-COMPARISON] Données comparaison reçues:', result.data);
          setPreviousPeriodData(result.data);
        } else {
          console.log('⚠️ [MSADS-COMPARISON] Pas de données de comparaison');
          setPreviousPeriodData(null);
        }
      } catch (error) {
        console.error('❌ [MSADS-COMPARISON] Erreur chargement:', error);
        setPreviousPeriodData(null);
      }
    };

    loadMsadsComparisonData();
  }, [selectedAccount, msadsComparisonType, selectedPeriod, customStartDate, customEndDate, token, comparisonStartDate, comparisonEndDate]);

  // Exposer la fonction d'analyse IA et gérer la visibilité du bouton
  useEffect(() => {
    window.microsoftDashboardAIAnalysis = generateAIInsights;

    // Gérer la visibilité du bouton
    if (window.setDashboardAIButton) {
      const shouldShowButton = autoCharts && autoCharts.charts && autoCharts.charts.length > 0;
      window.setDashboardAIButton(shouldShowButton);
    }

    // Gérer l'état d'analyse
    if (window.setDashboardAIAnalyzing) {
      window.setDashboardAIAnalyzing(loadingInsights);
    }
  }, [autoCharts, loadingInsights]);
  
  /*
   * L'analyse s'ouvre en panneau latéral, comme au dashboard Google Ads.
   *
   * Microsoft passait encore par la modale de brief puis une réponse d'un bloc. Les deux
   * dashboards envoient pourtant la même chose à la même route : il n'y avait aucune raison de
   * leur laisser deux expériences différentes, sinon l'ordre dans lequel ils ont été écrits.
   */
  const [panneauAnalyseOuvert, setPanneauAnalyseOuvert] = useState(false);

  useEffect(() => {
    const signaler = window.setAnalyseOuverte;
    if (!signaler) return undefined;
    signaler(panneauAnalyseOuvert);
    return () => signaler(false);
  }, [panneauAnalyseOuvert]);

  useEffect(() => {
    document.body.classList.toggle('panneau-analyse-ouvert', panneauAnalyseOuvert);

    const mesurer = () => {
      const contenu = document.querySelector('.app-layout-with-sidebar');
      const entete = document.querySelector('.app-header');
      const haut = contenu
        ? contenu.getBoundingClientRect().top
        : (entete ? entete.getBoundingClientRect().bottom : 0);
      document.documentElement.style.setProperty('--haut-panneau-analyse', `${Math.max(0, Math.round(haut))}px`);
      if (entete) {
        document.documentElement.style.setProperty('--hauteur-entete-app', `${Math.round(entete.getBoundingClientRect().height)}px`);
      }
    };

    if (!panneauAnalyseOuvert) {
      document.documentElement.style.removeProperty('--haut-panneau-analyse');
      document.documentElement.style.removeProperty('--hauteur-entete-app');
      return () => document.body.classList.remove('panneau-analyse-ouvert');
    }

    mesurer();
    window.addEventListener('resize', mesurer);

    return () => {
      document.body.classList.remove('panneau-analyse-ouvert');
      document.documentElement.style.removeProperty('--haut-panneau-analyse');
      document.documentElement.style.removeProperty('--hauteur-entete-app');
      window.removeEventListener('resize', mesurer);
    };
  }, [panneauAnalyseOuvert]);

  /**
   * Ce qu'on envoie à l'analyse : les données **déjà affichées**.
   *
   * Construite à l'envoi de la question et non à l'ouverture du panneau — les composants du
   * dashboard arrivent en plusieurs temps, et une charge figée trop tôt capture un écran à
   * moitié rempli.
   */
  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    // Les campagnes vivent dans les données brutes, indexées par compte.
    const campagnes = autoCharts?.rawData
      ? Object.values(autoCharts.rawData).find((d) => d?.campaigns?.length)?.campaigns || []
      : [];

    const bruts = {
      "Groupes d'annonces": msAdGroupsData,
      'Période précédente': previousPeriodData
    };
    const blocs = {};
    const absents = [];
    for (const [libelle, bloc] of Object.entries(bruts)) {
      const reduit = bloc ? lignesDuBloc(bloc) : null;
      if (reduit) blocs[libelle] = reduit;
      else absents.push(libelle);
    }

    return {
      dashboardData: {
        sources: selectedSources,
        ...periode,
        charts: autoCharts?.charts,
        campaigns: campagnes,
        composants: { blocs, absents },
        dashboardType: 'microsoft'
      },
      dashboardType: 'microsoft',
      accountId: selectedAccount?.sourceId || selectedAccount?.id || null,
      accountName: selectedAccount?.name || null,
      ...periode
    };
  };

  const generateAIInsights = () => {
    setPanneauAnalyseOuvert(true);
  };

  // Soumettre l'analyse avec le brief utilisateur
  const submitAIAnalysis = async () => {
    // Vérifier l'accès à l'analyse IA
    if (!canAccessAnalyzer) {
      setError('Cette fonctionnalité nécessite un abonnement actif');
      return;
    }
    
    setShowAiModal(false);
    setLoadingInsights(true);
    
    try {
      const response = await fetch(`${API_URL}/api/dashboard/microsoft-ads/insights`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          sources: selectedSources,
          period: selectedPeriod,
          ...(selectedPeriod === 'CUSTOM' && {
            customStartDate: customStartDate,
            customEndDate: customEndDate
          }),
          charts: autoCharts.charts,
          userBrief: userBrief
        })
      });

      const data = await response.json();
      console.log('🔍 Réponse insights reçue:', data);
      console.log('🔍 Type de data.insights:', typeof data.insights);
      console.log('🔍 Contenu data.insights:', data.insights);
      
      if (data.success) {
        console.log('✅ Insights analysés:', {
          hasAnalysis: !!data.insights?.analysis,
          analysisLength: data.insights?.analysis?.length || 0,
          hasRecommendations: !!data.insights?.recommendations
        });
        
        // Utiliser la même logique que le dashboard global qui fonctionne
        setAiInsights(data.insights);
        setError('');
        console.log(`🤖 Insights générés: ${data.insights.tokensUsed} tokens utilisés`);
        console.log('🎯 aiInsights après setAiInsights:', data.insights);
      } else {
        console.error('❌ Erreur insights:', data.error);
        // `message` d'abord : `error` porte un code (`TOO_MANY_ANALYSES`), illisible pour qui
        // le reçoit.
        setError(data.message || data.error || 'Erreur génération insights');
      }
    } catch (error) {
      console.error('Erreur insights IA:', error);
      setError('Erreur réseau lors de la génération des insights');
    } finally {
      setLoadingInsights(false);
      setUserBrief('');
    }
  };

  // Fonctions de tri
  const handleSort = (key) => {
    let direction = 'desc';
    if (sortConfig.key === key && sortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setSortConfig({ key, direction });
  };

  const handleCampaignTypesSort = (key) => {
    let direction = 'desc';
    if (campaignTypesSortConfig.key === key && campaignTypesSortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setCampaignTypesSortConfig({ key, direction });
  };

  const handleMsDevicesSort = (key) => {
    let direction = 'desc';
    if (msDevicesSortConfig.key === key && msDevicesSortConfig.direction === 'desc') {
      direction = 'asc';
    }
    setMsDevicesSortConfig({ key, direction });
  };




  const sortData = (data, config) => {
    return [...data].sort((a, b) => {
      const aValue = a[config.key];
      const bValue = b[config.key];
      
      if (typeof aValue === 'string') {
        return config.direction === 'asc' 
          ? aValue.localeCompare(bValue)
          : bValue.localeCompare(aValue);
      }
      
      return config.direction === 'asc' 
        ? aValue - bValue 
        : bValue - aValue;
    });
  };

  return (
    <div className="view-content">
      {/* Sélection des comptes Microsoft Ads */}
      <div className="dashboard-sources">
        {(() => {
          if (loadingLibraryItems) {
            return (
              <div className="loading-state">
                <Loader className="spin" size={16} />
                <span>Chargement des comptes Microsoft Ads...</span>
              </div>
            );
          }
          
          if (libraryItems.length === 0) {
            // Vérifier si le connecteur Microsoft Ads est activé
            const hasMicrosoftAdsConnector = globalConnectors?.some(c => c.provider === 'MICROSOFT_ADS') || false;
            
            if (!hasMicrosoftAdsConnector) {
              // Cas 1: Connecteur pas activé
              return (
                <div className="no-sources">
                  <div className="empty-state">
                    <p>Aucun connecteur Microsoft Ads activé</p>
                    <button 
                      onClick={handleConnectMicrosoftAds}
                      className="btn btn-primary"
                      style={{
                        fontSize: '16px',
                        padding: '12px 24px'
                      }}
                    >
                      + Connecter
                    </button>
                  </div>
                </div>
              );
            } else {
              // Cas 2: Connecteur activé mais pas de comptes
              return (
                <div className="no-sources">
                  <div className="empty-state">
                    <p>Aucun compte Microsoft Ads connecté</p>
                    <button
                      onClick={() => setShowAddModal(true)}
                      className="btn btn-primary"
                      style={{
                        fontSize: '16px',
                        padding: '12px 24px'
                      }}
                    >
                      + Ajouter un compte
                    </button>
                  </div>
                </div>
              );
            }
          }

          // Cas 3: Plusieurs comptes disponibles mais aucun sélectionné
          if (libraryItems.length > 1 && !selectedAccount) {
            return (
              <div className="account-selection-prompt">
                <div className="account-selection-card">
                  <div className="account-selection-icon">
                    <img src={microsoftAdsLogo} alt="Microsoft Ads" style={{ width: 48, height: 48 }} />
                  </div>
                  <h2>Sélectionnez un compte Microsoft Ads</h2>
                  <p>{libraryItems.length} comptes disponibles</p>
                  <select
                    className="account-selection-dropdown"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        const account = libraryItems.find(item => item.id === e.target.value);
                        if (account) {
                          setSelectedAccount(account);
                          setCurrentActiveAccount(account.id);
                          setSelectedSources([account.id]);
                          window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: account.id, accountName: account.name } }));
                          // Mettre à jour l'URL avec le compte sélectionné
                          setTimeout(() => window.updateUrlWithAccount?.(account.id), 100);
                        }
                      }
                    }}
                  >
                    <option value="">Choisir un compte...</option>
                    {[...libraryItems].sort((a, b) => a.name.localeCompare(b.name)).map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          }

          return (
          <>
            {/* Message de chargement si nécessaire (sauf pour Performance Max qui a son propre écran) */}
            {loadingCharts && selectedDashboardModel !== 'performance-max' && (
              <DashboardLoadingScreen
                platformName="Microsoft Ads"
                features={[
                  { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
                  { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
                  { icon: <img src={microsoftAdsLogo} alt="Microsoft Ads" style={{width: '28px', height: '28px'}} />, label: 'Analyse des campagnes' }
                ]}
              />
            )}
          </>
          );
        })()}
      </div>

      {/* Modal pour le brief utilisateur */}
      {/*
        L'analyse en panneau latéral, à côté du dashboard et non par-dessus — le même composant
        et la même route que le dashboard Google Ads.
      */}
      <ChatPanel
        isOpen={panneauAnalyseOuvert}
        onClose={() => setPanneauAnalyseOuvert(false)}
        token={token}
        accountId={selectedAccount?.sourceId || selectedAccount?.id || ''}
        accountName={selectedAccount?.name || ''}
        analysisResult={null}
        dashboardStream={construireChargeAnalyse}
        sansOverlay
      />

      {showAiModal && (
        <div className="modal-overlay" onClick={(e) => {
          if (e.target.className === 'modal-overlay') {
            setShowAiModal(false);
            setUserBrief('');
          }
        }}>
          <div className="modal-content ai-brief-modal">
            <h3>Analyse IA personnalisée</h3>
            <div className="modal-body">
              <p className="modal-description">
                Apportez des précisions pour obtenir une analyse plus pertinente de vos campagnes Microsoft Ads.
              </p>
              
              <div className="brief-form">
                <label htmlFor="ms-user-brief">Votre contexte et objectifs :</label>
                <textarea
                id="ms-user-brief"
                value={userBrief}
                onChange={(e) => setUserBrief(e.target.value)}
                placeholder="Exemples :
• Mon objectif est d'atteindre 500 conversions/mois avec un CPA < 50€
• Je cherche à améliorer ma rentabilité (COS < 30%)
• Je veux développer ma présence sur mobile
• Mon budget maximum est de 5000€/mois
• Je cible principalement les 25-45 ans en France
• Mes produits phares sont [listez vos produits]
• Ma marge moyenne est de 40%
• Je lance une nouvelle gamme le mois prochain
• Mes concurrents principaux sont..."
                rows={10}
                className="form-textarea"
              />
              <p className="help-text">
                💡 Plus vous donnez de contexte, plus l'analyse sera précise et les recommandations pertinentes.
              </p>
            </div>
            </div>
            
            <div className="modal-footer">
              <button 
                onClick={() => {
                  setShowAiModal(false);
                  setUserBrief('');
                }}
                className="btn btn-secondary"
              >
                Annuler
              </button>
              <button 
                onClick={submitAIAnalysis}
                className="btn btn-primary"
                disabled={loadingInsights}
              >
                {loadingInsights ? '🧠 Analyse en cours...' : '🚀 Lancer l\'analyse'}
              </button>
            </div>
            
            <div className="modal-info">
              <small>
                💰 Coût estimé : ~0.04€ | 🔢 Tokens : ~300-500 | ⏱️ Durée : ~15-25s
              </small>
            </div>
          </div>
        </div>
      )}

      {/* Écran de chargement pour l'analyse IA */}
      {loadingInsights && (
        <div className="modal-overlay ai-loading-overlay">
          <div className="ai-loading-content">
            <div className="ai-loading-animation">
              <div className="ai-brain-icon">🧠</div>
              <div className="loading-dots">
                <span></span>
                <span></span>
                <span></span>
              </div>
            </div>
            <h3>Analyse en cours...</h3>
            <p>Kokpit AI analyse vos données Microsoft Ads</p>
            <div className="loading-progress">
              <div className="progress-bar">
                <div className="progress-fill"></div>
              </div>
              <div className="loading-details">
                <small>Traitement de {Object.keys(autoCharts?.rawData || {}).length} comptes</small>
                <small>• {autoCharts?.charts?.length || 0} graphiques</small>
                <small>• Estimation : 15-25 secondes</small>
              </div>
            </div>
            <div className="loading-tips">
              <p><Sparkles size={14} />&nbsp;Le saviez-vous ?</p>
              <p className="tip">Kokpit AI analyse vos tendances de performance, identifie les opportunités d'optimisation et fournit des recommandations personnalisées basées sur vos objectifs.</p>
            </div>
          </div>
        </div>
      )}

      {/* Section Insights IA - Affichage des résultats */}
      {console.log('🎯 Avant affichage aiInsights (MS Ads):', aiInsights)}
      {aiInsights && (
        <div className="ai-insights-section">
          <h3>🤖 Analyse Intelligence Artificielle</h3>
          <div className="insights-content">
            <div className="insights-header">
              <div className="insights-badges">
                <span className="ai-badge">🤖 CLAUDE AI</span>
              </div>
              <div className="insights-meta">
                <span>Tokens: {aiInsights.tokensUsed}</span>
                <span>Coût: {aiInsights.cost}€</span>
                <span>Analyse: 100% personnalisée</span>
              </div>
            </div>
            
            <div className="insights-analysis">
              <h4><ClipboardList size={16} />&nbsp;Analyse Détaillée de vos Performances</h4>
              <div className="insights-text">
                <div dangerouslySetInnerHTML={{ __html: aiInsights.analysis }}></div>
              </div>
            </div>

            {aiInsights.recommendations && aiInsights.recommendations.length > 0 && (
              <div className="recommendations-section">
                <h4><Sparkles size={16} />&nbsp;Actions Recommandées</h4>
                <div className="recommendations-list">
                  {aiInsights.recommendations
                    .filter(rec => rec && typeof rec === 'string' && rec.trim().length > 0)
                    .map((rec, index) => (
                    <div key={index} className="recommendation-item">
                      <span className="rec-number">{index + 1}</span>
                      <span className="rec-text">{rec}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Section GAQL Debug - FORCE DISPLAY FOR TEST */}
            {(aiInsights.gaqlQuery || aiInsights.approach === 'clean_architecture') && (
              <div className="gaql-section" style={{marginTop: '20px', marginBottom: '20px'}}>
                <h4 style={{fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: '#374151', display: 'flex', alignItems: 'center', gap: '8px'}}><Search size={16} /> Détails de l'analyse</h4>
                <div className="gaql-details" style={{backgroundColor: '#FAF9F5', padding: '16px', borderRadius: '8px', border: '1px solid #e5e7eb'}}>
                  {/* Query utilisateur */}
                  {aiInsights.userQuery && (
                    <div className="user-query" style={{marginBottom: '16px'}}>
                      <strong style={{color: '#6b7280'}}>Question posée :</strong>
                      <div style={{
                        backgroundColor: '#fff',
                        padding: '10px',
                        borderRadius: '4px',
                        marginTop: '6px',
                        fontSize: '14px',
                        fontStyle: 'italic',
                        color: '#374151',
                        border: '1px solid #d1d5db'
                      }}>
                        "{aiInsights.userQuery || 'User query non disponible - DEBUG'}"
                      </div>
                    </div>
                  )}
                  
                  {/* GAQL Query technique */}
                  <div className="gaql-query" style={{marginBottom: '12px'}}>
                    <strong style={{color: '#6b7280'}}>Requête GAQL générée :</strong>
                    <pre className="gaql-code" style={{
                      backgroundColor: '#1f2937',
                      color: '#e5e7eb',
                      padding: '12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      fontFamily: 'Monaco, Consolas, monospace',
                      overflowX: 'auto',
                      marginTop: '8px',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all'
                    }}>{aiInsights.gaqlQuery || 'GAQL query non disponible - DEBUG'}</pre>
                  </div>
                  
                  {/* Métadonnées */}
                  <div className="gaql-metadata" style={{
                    display: 'flex',
                    gap: '16px',
                    flexWrap: 'wrap',
                    fontSize: '13px',
                    color: '#6b7280'
                  }}>
                    <span><strong>Resource:</strong> {aiInsights.resource}</span>
                    <span><strong>Data Points:</strong> {aiInsights.dataPoints}</span>
                    <span><strong>Architecture:</strong> {aiInsights.approach}</span>
                  </div>
                </div>
              </div>
            )}

            <div className="insights-actions">
              <button 
                onClick={() => setAiInsights(null)}
                className="btn btn-secondary"
              >
                🔄 Nouvelle analyse IA
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dashboard automatique - uniquement si des comptes sont disponibles */}
      {libraryItems.length > 0 && autoCharts && autoCharts.charts && (
        <div className="auto-dashboard-section">
          {/* Filtre par campagne Microsoft Ads */}
          {(() => {
            // Extraire les campagnes disponibles depuis rawData
            // Chercher une entrée qui a un tableau campaigns NON-VIDE
            const campaignsData = autoCharts.rawData ?
              Object.values(autoCharts.rawData).find(data => data.campaigns && data.campaigns.length > 0)?.campaigns || [] : [];

            if (campaignsData.length === 0) return null;

            return (
              <div style={{
                position: 'sticky',
                top: '0',
                zIndex: '1000',
                background: '#FAF9F5',
                borderRadius: '8px',
                padding: '16px',
                marginTop: '-5px',
                marginBottom: '10px'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                    <span style={{
                      fontWeight: '500',
                      color: '#374151',
                      fontSize: '0.95em',
                      display: 'flex',
                      alignItems: 'center'
                    }}>
                      <img src={microsoftAdsLogo} alt="Microsoft Ads" style={{width: '24px', height: '24px', marginRight: '8px'}} /> Filtrer:
                    </span>
                    <div style={{ position: 'relative', display: 'inline-block' }}>
                      <input
                        type="text"
                        placeholder="Rechercher une campagne..."
                        value={selectedCampaignFilter ? (selectedCampaignFilter.name || selectedCampaignFilter.campaignName) : msCampaignSearchQuery}
                        onChange={(e) => {
                          if (selectedCampaignFilter) {
                            setSelectedCampaignFilter(null);
                          }
                          setMsCampaignSearchQuery(e.target.value);
                          setShowMsCampaignAutocomplete(true);
                        }}
                        onFocus={() => setShowMsCampaignAutocomplete(true)}
                        onBlur={() => {
                          // Délai pour permettre le clic sur les options
                          setTimeout(() => setShowMsCampaignAutocomplete(false), 200);
                        }}
                        style={{
                          padding: '4px 12px',
                          paddingRight: (msCampaignSearchQuery || selectedCampaignFilter) ? '28px' : '12px',
                          border: '1px solid #d1d5db',
                          borderRadius: '6px',
                          backgroundColor: selectedCampaignFilter ? '#dbeafe' : 'white',
                          color: '#374151',
                          fontSize: '0.9em',
                          width: '400px',
                          height: '35px',
                          boxSizing: 'border-box',
                          outline: 'none'
                        }}
                      />
                      {(msCampaignSearchQuery || selectedCampaignFilter) && (
                        <button
                          onClick={() => {
                            setMsCampaignSearchQuery('');
                            setSelectedCampaignFilter(null);
                            setShowMsCampaignAutocomplete(false);
                          }}
                          style={{
                            position: 'absolute',
                            right: '6px',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            background: 'none',
                            border: 'none',
                            cursor: 'pointer',
                            color: selectedCampaignFilter ? '#1a1a1a' : '#9ca3af',
                            padding: '2px',
                            fontSize: '14px',
                            lineHeight: 1,
                            zIndex: 1
                          }}
                          title={selectedCampaignFilter ? "Effacer le filtre campagne" : "Effacer la recherche"}
                        >
                          ✕
                        </button>
                      )}
                      {/* Autocomplete dropdown */}
                      {showMsCampaignAutocomplete && (() => {
                        const searchLower = msCampaignSearchQuery.toLowerCase().trim();
                        // D'abord filtrer par labels si des labels sont sélectionnés
                        let baseCampaigns = campaignsData || [];
                        if (msSelectedLabelsFilter.length > 0 && Object.keys(msCampaignsWithLabels).length > 0) {
                          baseCampaigns = baseCampaigns.filter(c => {
                            const campaignId = (c.id || c.campaignId || '').toString();
                            const campaignLabels = msCampaignsWithLabels[campaignId] || [];
                            return msSelectedLabelsFilter.some(label => campaignLabels.includes(label));
                          });
                        }
                        // Ensuite filtrer par recherche textuelle
                        const filteredCampaigns = searchLower
                          ? baseCampaigns
                              .filter(c => (c.name || c.campaignName || '').toLowerCase().includes(searchLower))
                              .sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''))
                          : [...baseCampaigns].sort((a, b) => (a.name || a.campaignName || '').localeCompare(b.name || b.campaignName || ''));

                        if (filteredCampaigns.length === 0) {
                          return (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #d1d5db',
                              borderRadius: '6px',
                              marginTop: '4px',
                              boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                              zIndex: 1001,
                              padding: '12px',
                              color: '#6b7280',
                              fontSize: '0.9em'
                            }}>
                              Aucune campagne trouvée
                            </div>
                          );
                        }

                        return (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            right: 0,
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            marginTop: '4px',
                            boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                            zIndex: 1001,
                            maxHeight: '300px',
                            overflowY: 'auto'
                          }}>
                            <div style={{
                              padding: '8px 12px',
                              fontSize: '0.8em',
                              color: '#6b7280',
                              borderBottom: '1px solid #e5e7eb',
                              backgroundColor: '#f9fafb'
                            }}>
                              {searchLower
                                ? `${filteredCampaigns.length} campagne(s) trouvée(s)`
                                : `Toutes les campagnes (${filteredCampaigns.length})`}
                            </div>
                            {filteredCampaigns.map((campaign, index) => (
                              <div
                                key={campaign.id || campaign.campaignId || index}
                                onClick={() => {
                                  setSelectedCampaignFilter(campaign);
                                  setMsCampaignSearchQuery('');
                                  setShowMsCampaignAutocomplete(false);
                                }}
                                style={{
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  fontSize: '0.9em',
                                  color: '#374151',
                                  borderBottom: index < filteredCampaigns.length - 1 ? '1px solid #f3f4f6' : 'none',
                                  transition: 'background-color 0.15s'
                                }}
                                onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                                onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                              >
                                {campaign.name || campaign.campaignName}
                              </div>
                            ))}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Filtre par type de campagne, posé par un clic sur le tableau Types de campagnes */}
                    {selectedCampaignTypeFilter && (
                      <div style={{
                        backgroundColor: '#f3e5f5',
                        color: '#7b1fa2',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.8em',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: '1px solid #ce93d8'
                      }}>
                        🏷️ {selectedCampaignTypeFilter.name}
                        <button
                          onClick={() => setSelectedCampaignTypeFilter(null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#7b1fa2',
                            cursor: 'pointer',
                            padding: '0 2px',
                            fontSize: '15px',
                            fontWeight: 'bold'
                          }}
                          title="Supprimer le filtre type de campagne"
                        >
                          ×
                        </button>
                      </div>
                    )}

                    {/* Filtre réseau, posé par un clic sur une ligne du tableau Réseaux */}
                    {selectedNetworkFilter && (
                      <div style={{
                        backgroundColor: '#e0f2fe',
                        color: '#0369a1',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        fontSize: '0.8em',
                        fontWeight: '500',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '4px',
                        border: '1px solid #7dd3fc'
                      }}>
                        🌐 {selectedNetworkFilter.networkName || selectedNetworkFilter.networkOriginal}
                        <button
                          onClick={() => setSelectedNetworkFilter(null)}
                          style={{
                            background: 'none',
                            border: 'none',
                            color: '#0369a1',
                            cursor: 'pointer',
                            padding: '0 2px',
                            fontSize: '15px',
                            fontWeight: 'bold'
                          }}
                          title="Supprimer le filtre réseau"
                        >
                          ×
                        </button>
                      </div>
                    )}

                    {/* Filtre par labels Microsoft Ads */}
                    {(() => {
                      // Si une campagne est sélectionnée, ne montrer que ses labels
                      let msLabelsToDisplay = msAvailableLabels;
                      if (selectedCampaignFilter) {
                        const campaignId = (selectedCampaignFilter.id || selectedCampaignFilter.campaignId || '').toString();
                        const campaignLabels = msCampaignsWithLabels[campaignId] || [];
                        // Si la campagne n'a pas de labels, masquer le dropdown
                        if (campaignLabels.length === 0) return null;
                        // Sinon, ne montrer que les labels de cette campagne
                        msLabelsToDisplay = campaignLabels.filter(l => msAvailableLabels.includes(l));
                      }
                      if (msLabelsToDisplay.length === 0) return null;

                      return (
                      <div style={{ position: 'relative', marginLeft: '16px' }}>
                        <button
                          onClick={() => setShowMsLabelsDropdown(!showMsLabelsDropdown)}
                          style={{
                            padding: '4px 12px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            backgroundColor: msSelectedLabelsFilter.length > 0 ? '#dbeafe' : 'white',
                            color: '#374151',
                            fontSize: '0.9em',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            minWidth: '140px',
                            height: '35px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <span><Tag size={14} />&nbsp;Labels</span>
                          {msSelectedLabelsFilter.length > 0 && (
                            <span style={{
                              backgroundColor: '#1a1a1a',
                              color: 'white',
                              borderRadius: '10px',
                              padding: '2px 8px',
                              fontSize: '0.8em',
                              fontWeight: '600'
                            }}>
                              {msSelectedLabelsFilter.length}
                            </span>
                          )}
                          <span style={{ marginLeft: 'auto' }}>{showMsLabelsDropdown ? '▲' : '▼'}</span>
                        </button>

                        {showMsLabelsDropdown && (
                          <div style={{
                            position: 'absolute',
                            top: '100%',
                            left: 0,
                            backgroundColor: 'white',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            marginTop: '4px',
                            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                            zIndex: 9999,
                            minWidth: '200px',
                            maxHeight: '250px',
                            overflowY: 'auto'
                          }}>
                            {/* Bouton tout effacer */}
                            {msSelectedLabelsFilter.length > 0 && (
                              <div
                                onClick={() => setMsSelectedLabelsFilter([])}
                                style={{
                                  padding: '8px 12px',
                                  cursor: 'pointer',
                                  fontSize: '0.85em',
                                  color: '#ef4444',
                                  borderBottom: '1px solid #e5e7eb',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '6px'
                                }}
                              >
                                ✕ Tout effacer
                              </div>
                            )}

                            {/* Liste des labels */}
                            {msLabelsToDisplay.map(label => (
                              <div
                                key={label}
                                onClick={() => toggleMsLabelFilter(label)}
                                style={{
                                  padding: '10px 12px',
                                  cursor: 'pointer',
                                  fontSize: '0.9em',
                                  color: '#374151',
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  transition: 'background-color 0.15s',
                                  backgroundColor: msSelectedLabelsFilter.includes(label) ? '#f0f9ff' : 'transparent'
                                }}
                                onMouseEnter={(e) => {
                                  if (!msSelectedLabelsFilter.includes(label)) {
                                    e.target.style.backgroundColor = '#f3f4f6';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  e.target.style.backgroundColor = msSelectedLabelsFilter.includes(label) ? '#f0f9ff' : 'transparent';
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={msSelectedLabelsFilter.includes(label)}
                                  onChange={() => {}}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                    accentColor: '#1a1a1a'
                                  }}
                                />
                                <span>{label}</span>
                              </div>
                            ))}

                            {/* Bouton Valider */}
                            <div
                              onClick={() => setShowMsLabelsDropdown(false)}
                              style={{
                                padding: '10px 12px',
                                cursor: 'pointer',
                                fontSize: '0.9em',
                                color: 'white',
                                backgroundColor: '#1a1a1a',
                                borderTop: '1px solid #e5e7eb',
                                textAlign: 'center',
                                fontWeight: '500',
                                marginTop: '4px'
                              }}
                              onMouseEnter={(e) => e.target.style.backgroundColor = '#333333'}
                              onMouseLeave={(e) => e.target.style.backgroundColor = '#1a1a1a'}
                            >
                              ✓ Valider
                            </div>
                          </div>
                        )}
                      </div>
                      );
                    })()}
                  </div>

                  {/*
                    * Bouton Analyse IA — le même qu'au dashboard Google Ads.
                    *
                    * Il manquait ici : `showAIButton` n'était passé qu'à `DashboardGoogleAds`, et
                    * le bouton vivait dans son JSX. Microsoft exposait pourtant déjà sa fonction
                    * d'analyse (`window.microsoftDashboardAIAnalysis`) et signalait sa
                    * disponibilité — il n'y avait simplement rien pour l'afficher.
                    *
                    * Il s'efface à l'ouverture du panneau, pour la même raison qu'ailleurs : il
                    * n'a plus rien à ouvrir, et le panneau porte sa propre croix.
                    */}
                  {autoCharts?.charts?.length > 0 && canAccessAnalyzer && !panneauAnalyseOuvert && (
                    <button
                      onClick={generateAIInsights}
                      className="nav-btn"
                      style={{
                        padding: '8px 16px',
                        gap: '10px',
                        fontSize: '0.9em',
                        fontWeight: '500',
                        whiteSpace: 'nowrap'
                      }}
                      title="Analyse IA du dashboard Microsoft Ads"
                    >
                      <Sparkles size={16} color="#7c2d12" />
                      Analyse IA
                    </button>
                  )}

                  {/* Lien Modules masqués */}
                  {userRole !== 'CLIENT' && clientHiddenModules.length > 0 && (
                    <div
                      className="hidden-modules-link"
                      onClick={() => setIsHiddenModulesModalOpen(true)}
                      title="Voir les modules masqués pour le client"
                    >
                      <EyeOff size={16} />
                      <span>Modules masqués</span>
                      <span className="hidden-count">{clientHiddenModules.length}</span>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}

          {/* Modal des modules masqués */}
          <HiddenModulesModal
            isOpen={isHiddenModulesModalOpen}
            onClose={() => setIsHiddenModulesModalOpen(false)}
            hiddenModules={clientHiddenModules}
            onRestoreModule={(moduleId) => setClientModuleVisibility(moduleId, true)}
          />

          {/* Section Résultats - avec PerformanceChart et granularité */}
          {(() => {
            // Prioriser les données timeline optimisées si disponibles ET non vides
            // Sinon fallback sur les données du dashboard principal
            let chartSource;
            if (autoCharts.timelineRawData && autoCharts.timelineCharts && autoCharts.timelineCharts.length > 0) {
              chartSource = {
                charts: autoCharts.timelineCharts,
                rawData: autoCharts.timelineRawData
              };
              console.log('📈 [RESULTS] Microsoft Ads - Utilisation des données timeline optimisées');
            } else if (autoCharts.charts && autoCharts.charts.length > 0) {
              chartSource = autoCharts;
              console.log('📈 [RESULTS] Microsoft Ads - Fallback sur données dashboard principal');
            } else {
              console.log('📈 [RESULTS] Microsoft Ads Dashboard automatique - Pas de données - message informatif affiché');
              return <NoDataMessage />;
            }

            // Vérifier qu'il y a bien des données
            if (!chartSource || !chartSource.charts || chartSource.charts.length === 0) {
              console.log('📈 [RESULTS] Microsoft Ads Dashboard automatique - Pas de données après fallback');
              return <NoDataMessage />;
            }
            
            return (
              <PerformanceChart
                autoCharts={chartSource}
                formatNumber={formatNumber}
                KPICard={KPICard}
                previousPeriodData={previousPeriodData}
                compareEnabled={msadsCompareEnabled}
                comparisonType={msadsComparisonType}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                selectedAccount={selectedAccount}
                granularity={granularity}
                onGranularityChange={setGranularity}
                selectedPeriod={selectedPeriod}
                customStartDate={customStartDate}
                customEndDate={customEndDate}
                notes={dashboardNotes}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                moduleId="msads_results"
              />
            );
          })()}

          {/* Section Executive Summary - masqué pour les clients */}
          {userRole !== 'CLIENT' && (() => {
            // Extraire les données depuis les différentes entrées de rawData
            // (campaigns, deviceData et networkData peuvent être dans des entrées différentes)

            // Récupérer les campagnes
            let campaignsData = autoCharts.rawData ?
              Object.values(autoCharts.rawData).find(data => data.campaigns && data.campaigns.length > 0)?.campaigns || [] : [];

            // Récupérer les appareils
            const deviceEntry = autoCharts.rawData ?
              Object.values(autoCharts.rawData).find(data => data.deviceData) : null;
            const deviceData = deviceEntry?.deviceData || [];

            // Récupérer les réseaux
            const networkEntry = autoCharts.rawData ?
              Object.values(autoCharts.rawData).find(data => data.networkData) : null;
            const networkData = networkEntry?.networkData || [];

            // Récupérer les groupes d'annonces
            const adGroupsData = msAdGroupsData || [];

            // Récupérer les termes de recherche
            const searchTermsEntry = autoCharts.rawData ?
              Object.values(autoCharts.rawData).find(data => data.searchTerms) : null;
            const searchTermsData = searchTermsEntry?.searchTerms?.searchTerms || [];

            console.log('🔍 [EXEC-SUMMARY] Données extraites:', {
              campaignsData: campaignsData.length,
              adGroupsData: adGroupsData.length,
              deviceData: deviceData.length,
              networkData: networkData.length,
              searchTermsData: searchTermsData.length
            });

            // Ne pas afficher si pas de données de campagnes
            if (campaignsData.length === 0) {
              return null;
            }

            // Appliquer le filtre par campagne si sélectionné
            if (selectedCampaignFilter && campaignsData.length > 0) {
              const filterId = selectedCampaignFilter.id || selectedCampaignFilter.campaignId || null;
              const filterName = selectedCampaignFilter.name || selectedCampaignFilter.campaignName || '';
              campaignsData = campaignsData.filter(campaign => {
                const campId = campaign.id || campaign.campaignId || null;
                const campName = campaign.name || campaign.campaignName || '';
                if (filterId && campId) {
                  return String(campId) === String(filterId);
                }
                return campName === filterName;
              });
            }

            return (
              <MicrosoftExecutiveSummary
                campaignsData={campaignsData}
                adGroupsData={adGroupsData}
                deviceData={deviceData}
                networkData={networkData}
                searchTermsData={searchTermsData}
                previousPeriodData={previousPeriodData}
                compareEnabled={msadsCompareEnabled}
                comparisonType={msadsComparisonType}
                formatNumber={formatNumber}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                moduleId="msads_insights"
              />
            );
          })()}

          {/* Section Types de campagnes - composant partagé avec le dashboard Google Ads */}
          {(() => {
            const campaignsFromRawData = autoCharts?.rawData
              ? Object.values(autoCharts.rawData).find(d => d.campaigns && d.campaigns.length > 0)?.campaigns || []
              : [];
            if (campaignsFromRawData.length === 0) return null;

            // Les mêmes filtres doivent porter sur les deux périodes, sans quoi la ligne
            // TOTAL comparerait un sous-ensemble filtré à un ensemble complet.
            const applyFilters = (list) => {
              let filtered = [...list];
              if (selectedCampaignFilter) {
                filtered = filtered.filter(campaign => {
                  const campId = campaign.id || campaign.campaignId || null;
                  const filterId = selectedCampaignFilter.id || selectedCampaignFilter.campaignId || null;
                  if (filterId && campId) return String(campId) === String(filterId);
                  const campName = campaign.name || campaign.campaignName || '';
                  const filterName = selectedCampaignFilter.name || selectedCampaignFilter.campaignName || '';
                  return campName === filterName;
                });
              }
              if (selectedCampaignTypeFilter) {
                filtered = filtered.filter(campaign =>
                  getMsadsCampaignTypeName(campaign) === selectedCampaignTypeFilter.name
                );
              }
              return filtered;
            };

            // Le rapport Microsoft de la période précédente ne renvoie pas le type de
            // campagne : sans cette reconstitution par nom, toutes les campagnes précédentes
            // retombaient sur « Non défini » et aucune ligne ne trouvait sa contrepartie,
            // donc aucune variation ne s'affichait.
            const typeByCampaignName = new Map(
              campaignsFromRawData.map(campaign => [
                campaign.name || campaign.campaignName,
                campaign.advertisingChannelType || campaign.type
              ])
            );
            const previousCampaignsWithType = (previousPeriodData?.campaigns || []).map(campaign => ({
              ...campaign,
              type: campaign.advertisingChannelType || campaign.type
                || typeByCampaignName.get(campaign.name || campaign.campaignName)
            }));

            return (
              <CampaignTypesSection
                campaigns={applyFilters(campaignsFromRawData)}
                previousCampaigns={
                  msadsCompareEnabled && previousCampaignsWithType.length > 0
                    ? applyFilters(previousCampaignsWithType)
                    : null
                }
                compareEnabled={msadsCompareEnabled}
                onTypeClick={(typeName) => {
                  if (selectedCampaignFilter) setSelectedCampaignFilter(null);
                  // Les libellés bruts servent au filtrage serveur : réseaux, appareils et jours
                  // sont agrégés sans référence de campagne, la route doit donc restreindre les
                  // lignes avant de les agréger.
                  const rawTypes = [...new Set(
                    campaignsFromRawData
                      .filter(campaign => getMsadsCampaignTypeName(campaign) === typeName)
                      .map(campaign => campaign.advertisingChannelType || campaign.type)
                      .filter(Boolean)
                  )];
                  setSelectedCampaignTypeFilter({ name: typeName, type: typeName, rawTypes });
                }}
                getCampaignType={getMsadsCampaignTypeName}
                metrics={msadsCampaignTypesMetrics}
                moduleId="msads_campaign_types"
                accountId={selectedAccount?.id || 'global'}
                componentTitle="Types de campagnes"
                formatNumber={formatNumber}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
              />
            );
          })()}


          {/* Section Réseaux Microsoft Ads */}
          {selectedAccount && autoCharts?.rawData && (() => {
            // Récupérer les données de réseau depuis rawData (même source que Résultats/Appareils)
            const rawDataEntry = Object.values(autoCharts.rawData).find(data => data.networkData);
            const networkData = rawDataEntry?.networkData || [];
            const networkDataByCampaign = rawDataEntry?.networkDataByCampaign || {};

            return (
              <MicrosoftNetworksTable
                selectedAccount={selectedAccount}
                formatNumber={formatNumber}
                isVisible={true}
                COLORS={COLORS}
                selectedCampaignFilter={selectedCampaignFilter}
                selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                networkData={networkData}
                networkDataByCampaign={networkDataByCampaign}
                onNetworkClick={setSelectedNetworkFilter}
                selectedNetworkFilter={selectedNetworkFilter}
                previousPeriodData={previousPeriodData}
                compareEnabled={msadsCompareEnabled}
                comparisonType={msadsComparisonType}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                moduleId="msads_networks"
              />
            );
          })()}

          {/* Tableau des campagnes Microsoft Ads - utilise le composant CampaignsTable */}
          {(() => {
            // Extraire les données de campagnes Microsoft Ads depuis autoCharts
            const campaignsData = autoCharts.rawData ?
              Object.values(autoCharts.rawData).find(data => data.campaigns && data.campaigns.length > 0)?.campaigns || [] : [];

            if (campaignsData.length === 0) return null;

            return (
              <div className="campaigns-table-container">
                <CampaignsTable
                  rawData={{
                    account: {
                      campaigns: campaignsData
                    }
                  }}
                  isVisible={true}
                  formatNumber={formatNumber}
                  selectedCampaignFilter={selectedCampaignFilter}
                  selectedCampaignTypeFilter={selectedCampaignTypeFilter}
                  setSelectedCampaignFilter={setSelectedCampaignFilter}
                  selectedAccount={selectedAccount}
                  previousPeriodData={previousPeriodData}
                  compareEnabled={msadsCompareEnabled}
                  comparisonType={msadsComparisonType}
                  userRole={userRole}
                  isClientModuleVisible={isClientModuleVisible}
                  toggleClientModuleVisibility={toggleClientModuleVisibility}
                  moduleId="msads_campaigns"
                  campagnesAccountId={selectedAccount?.sourceId || selectedAccount?.id}
                  campagnesAccountType="MICROSOFT_ADS"
                  campagnesToken={token}
                />
              </div>
            );
          })()}

          {/* Section Groupes d'annonces - utilise l'API dédiée */}
          {(() => {
            // Utiliser les données récupérées via l'API dédiée
            let adGroupsData = msAdGroupsData || [];

            // Appliquer le filtre par campagne si sélectionné
            if (selectedCampaignFilter && adGroupsData.length > 0) {
              const filterId = selectedCampaignFilter.id || selectedCampaignFilter.campaignId || null;
              const filterName = selectedCampaignFilter.name || selectedCampaignFilter.campaignName || '';
              adGroupsData = adGroupsData.filter(adGroup => {
                const adGroupCampId = adGroup.campaignId || adGroup.CampaignId || null;
                const adGroupCampName = adGroup.campaignName || adGroup.CampaignName || '';
                // Comparer par ID seulement si les deux ont un ID, sinon comparer par nom
                if (filterId && adGroupCampId) {
                  return String(adGroupCampId) === String(filterId);
                }
                return adGroupCampName === filterName;
              });
            }

            console.log('🎯 Microsoft Ads - Groupes d\'annonces depuis API:', adGroupsData.length, adGroupsData);
            
            if (loadingMsAdGroups) {
              return null; // Ne rien afficher pendant le chargement
            }
            
            if (adGroupsData.length === 0) {
              return null;
            }

            // Helpers pour la comparaison
            const calculateAdGroupVariation = (current, previous) => {
              if (previous === 0 && current === 0) return 0;
              if (previous === 0) return current > 0 ? 100 : -100;
              return ((current - previous) / previous) * 100;
            };

            const renderAdGroupVariation = (variation, isInversed = false) => {
              // Ne pas afficher les variations si msadsCompareEnabled est false
              if (!msadsCompareEnabled) return null;
              if (variation === null || variation === undefined) return null;
              const isPositive = isInversed ? variation < 0 : variation >= 0;
              const color = isPositive ? '#16a34a' : '#dc2626';
              const arrow = variation >= 0 ? '↑' : '↓';
              return (
                <span style={{ display: 'block', fontSize: '0.75em', color, marginTop: '2px' }}>
                  {arrow} {Math.abs(variation).toFixed(1)}%
                </span>
              );
            };

            // Map des données précédentes par groupe d'annonces
            // Toujours afficher les données de comparaison si disponibles (pas de condition msadsCompareEnabled)
            const previousAdGroupsMap = {};
            if (previousPeriodData?.adGroups) {
              previousPeriodData.adGroups.forEach(adGroup => {
                const key = adGroup.name || adGroup.adGroupName;
                if (key) previousAdGroupsMap[key] = adGroup;
              });
            }

            const handleMsAdGroupSort = (key) => {
              setMsAdGroupSortConfig(prevConfig => ({
                key,
                direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
              }));
              // Réinitialiser à la première page lors du changement de tri
              setMsAdGroupCurrentPage(1);
            };

            const sortData = (data, config) => {
              return [...data].sort((a, b) => {
                // Le panier moyen n'est pas un champ des lignes : il se calcule, ici comme à
                // l'affichage. Le trier comme les autres aurait comparé des `undefined`.
                let aValue = config.key === 'panier_moyen' ? panierMoyenPublicitaire(a) : a[config.key];
                let bValue = config.key === 'panier_moyen' ? panierMoyenPublicitaire(b) : b[config.key];

                if (config.key === 'name') {
                  return config.direction === 'asc' ? aValue.localeCompare(bValue) : bValue.localeCompare(aValue);
                }
                
                // Pour les valeurs numériques
                if (typeof aValue === 'string' && !isNaN(aValue)) aValue = parseFloat(aValue);
                if (typeof bValue === 'string' && !isNaN(bValue)) bValue = parseFloat(bValue);
                
                return config.direction === 'asc' ? aValue - bValue : bValue - aValue;
              });
            };

            const sortedAdGroups = sortData(adGroupsData, msAdGroupSortConfig);

            // Pagination
            const itemsPerPage = 10;
            const totalPages = Math.ceil(sortedAdGroups.length / itemsPerPage);
            const startIndex = (msAdGroupCurrentPage - 1) * itemsPerPage;
            const endIndex = Math.min(startIndex + itemsPerPage, sortedAdGroups.length);
            const paginatedAdGroups = sortedAdGroups.slice(startIndex, endIndex);

            // Calculs des totaux (sur toutes les données, pas seulement la page actuelle)
            const totals = adGroupsData.reduce((acc, adGroup) => ({
              impressions: acc.impressions + (adGroup.impressions || 0),
              clicks: acc.clicks + (adGroup.clicks || 0),
              cost: acc.cost + (parseFloat(adGroup.cost) || 0),
              conversions: acc.conversions + (adGroup.conversions || 0),
              conversionsValue: acc.conversionsValue + (parseFloat(adGroup.conversionsValue) || 0)
            }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

            // Calcul des métriques calculées
            const totalCTR = totals.impressions > 0 ? (totals.clicks / totals.impressions * 100) : 0;
            const totalCPC = totals.clicks > 0 ? (totals.cost / totals.clicks) : 0;
            const totalConversionRate = totals.clicks > 0 ? (totals.conversions / totals.clicks * 100) : 0;
            const totalCPA = totals.conversions > 0 ? (totals.cost / totals.conversions) : 0;
            const totalCOS = totals.conversionsValue > 0 ? (totals.cost / totals.conversionsValue * 100) : 0;

            // Pour les clients, ne pas afficher si le module est masqué
            if (userRole === 'CLIENT' && !isClientModuleVisible('ad_groups')) {
              return null;
            }

            return (
              <div className="campaigns-table-section">
                <div className="section-header">
                  <h4><Target size={16} />&nbsp;Groupes d'annonces</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {userRole !== 'CLIENT' && (
                      <button
                        onClick={() => setShowMsMetricsConfig('msads_adgroups')}
                        className="metrics-settings-btn"
                        title="Configurer les métriques"
                      >
                        <Settings size={16} />
                      </button>
                    )}
                    <ModuleVisibilityToggle
                      isVisible={isClientModuleVisible('ad_groups')}
                      onToggle={toggleClientModuleVisibility}
                      userRole={userRole}
                      moduleId="ad_groups"
                    />
                  </div>
                </div>
                {(userRole !== 'CLIENT' || isClientModuleVisible('ad_groups')) && (
                  <>
                  <div className="campaigns-table-wrapper">
                    <table className="campaigns-table">
                      <thead>
                        <tr>
                          <th onClick={() => handleMsAdGroupSort('name')} className="sortable">
                            Groupe d'annonces {msAdGroupSortConfig.key === 'name' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                          </th>
                          {isMsadsAdGroupMetricSelected('impressions') && (
                            <th onClick={() => handleMsAdGroupSort('impressions')} className="sortable numeric">
                              Impressions {msAdGroupSortConfig.key === 'impressions' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('ctr') && (
                            <th onClick={() => handleMsAdGroupSort('ctr')} className="sortable numeric">
                              CTR {msAdGroupSortConfig.key === 'ctr' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('clics') && (
                            <th onClick={() => handleMsAdGroupSort('clicks')} className="sortable numeric">
                              Clics {msAdGroupSortConfig.key === 'clicks' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('cpc') && (
                            <th onClick={() => handleMsAdGroupSort('cpc')} className="sortable numeric">
                              CPC {msAdGroupSortConfig.key === 'cpc' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('cout') && (
                            <th onClick={() => handleMsAdGroupSort('cost')} className="sortable numeric">
                              Coût {msAdGroupSortConfig.key === 'cost' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('taux_conv') && (
                            <th onClick={() => handleMsAdGroupSort('conversionRate')} className="sortable numeric">
                              Taux Conv. {msAdGroupSortConfig.key === 'conversionRate' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('conv') && (
                            <th onClick={() => handleMsAdGroupSort('conversions')} className="sortable numeric">
                              Conv. {msAdGroupSortConfig.key === 'conversions' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('cpa') && (
                            <th onClick={() => handleMsAdGroupSort('cpa')} className="sortable numeric">
                              CPA {msAdGroupSortConfig.key === 'cpa' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('panier_moyen') && (
                            <th onClick={() => handleMsAdGroupSort('panier_moyen')} className="sortable numeric">
                              Panier moyen {msAdGroupSortConfig.key === 'panier_moyen' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('valeur_conv') && (
                            <th onClick={() => handleMsAdGroupSort('conversionsValue')} className="sortable numeric">
                              Valeur Conv. {msAdGroupSortConfig.key === 'conversionsValue' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                          {isMsadsAdGroupMetricSelected('cos') && (
                            <th onClick={() => handleMsAdGroupSort('cos')} className="sortable numeric">
                              COS {msAdGroupSortConfig.key === 'cos' && (msAdGroupSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {paginatedAdGroups.map((adGroup, index) => {
                          const adGroupKey = adGroup.name || adGroup.adGroupName;
                          const prevAdGroup = previousAdGroupsMap[adGroupKey] || {};
                          const prevCtr = prevAdGroup.impressions > 0 ? (prevAdGroup.clicks / prevAdGroup.impressions * 100) : 0;
                          const prevCpc = prevAdGroup.clicks > 0 ? (prevAdGroup.cost / prevAdGroup.clicks) : 0;
                          const prevConvRate = prevAdGroup.clicks > 0 ? (prevAdGroup.conversions / prevAdGroup.clicks * 100) : 0;
                          const prevCpa = prevAdGroup.conversions > 0 ? (prevAdGroup.cost / prevAdGroup.conversions) : 0;
                          const prevCos = prevAdGroup.conversionsValue > 0 ? (prevAdGroup.cost / prevAdGroup.conversionsValue * 100) : 0;

                          return (
                            <tr key={index}>
                              <td className="campaign-name" style={{ display: 'flex', alignItems: 'center' }}>
                                {adGroup.isDelivering !== undefined && (
                                  <span
                                    style={{
                                      display: 'inline-block',
                                      width: '8px',
                                      height: '8px',
                                      borderRadius: '50%',
                                      backgroundColor: adGroup.isDelivering ? '#22c55e' : '#ef4444',
                                      marginRight: '8px',
                                      flexShrink: 0
                                    }}
                                    title={adGroup.isDelivering ? 'Actif' : 'Inactif'}
                                  />
                                )}
                                <span>{adGroup.name || adGroup.adGroupName || '-'}</span>
                              </td>
                              {isMsadsAdGroupMetricSelected('impressions') && (
                                <td className="numeric">
                                  {formatNumber(adGroup.impressions || 0)}
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.impressions || 0, prevAdGroup.impressions || 0))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('ctr') && (
                                <td className="numeric">
                                  {formatNumber(adGroup.ctr || 0, 2)}%
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.ctr || 0, prevCtr))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('clics') && (
                                <td className="numeric">
                                  {formatNumber(adGroup.clicks || 0)}
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.clicks || 0, prevAdGroup.clicks || 0))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('cpc') && (
                                <td className="numeric">
                                  {formatNumber(adGroup.cpc || 0, 2)}€
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.cpc || 0, prevCpc), true)}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('cout') && (
                                <td className="numeric">
                                  {formatNumber(adGroup.cost || 0, 2)}€
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.cost || 0, prevAdGroup.cost || 0), true)}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('taux_conv') && (
                                <td className="numeric">
                                  {formatNumber(adGroup.conversionRate || 0, 2)}%
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.conversionRate || 0, prevConvRate))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('conv') && (
                                <td className="numeric">
                                  {formatNumber(adGroup.conversions || 0, 1)}
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.conversions || 0, prevAdGroup.conversions || 0))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('cpa') && (
                                <td className={`numeric ${getCpaClassName(adGroup.cpa || 0, selectedAccount?.id || 'global', 'ad_groups')}`}>
                                  {formatNumber(adGroup.cpa || 0, 2)}€
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.cpa || 0, prevCpa), true)}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('panier_moyen') && (
                                <td className="numeric">
                                  {formatNumber(panierMoyenPublicitaire(adGroup), 2)}€
                                  {renderAdGroupVariation(calculateAdGroupVariation(panierMoyenPublicitaire(adGroup), panierMoyenPublicitaire(prevAdGroup)))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('valeur_conv') && (
                                <td className="numeric">
                                  {formatNumber(adGroup.conversionsValue || 0, 2)}€
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.conversionsValue || 0, prevAdGroup.conversionsValue || 0))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('cos') && (
                                <td className={`numeric ${getCosClassName(adGroup.cos, selectedAccount?.id || 'global', 'ad_groups')}`}>
                                  {formatNumber(adGroup.cos || 0, 1)}%
                                  {renderAdGroupVariation(calculateAdGroupVariation(adGroup.cos || 0, prevCos), true)}
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                      <tfoot>
                        {(() => {
                          // Calculer les totaux de la période précédente
                          const prevAdGroups = previousPeriodData?.adGroups || [];
                          const prevTotals = prevAdGroups.reduce((acc, adGroup) => ({
                            impressions: acc.impressions + (adGroup.impressions || 0),
                            clicks: acc.clicks + (adGroup.clicks || 0),
                            cost: acc.cost + (parseFloat(adGroup.cost) || 0),
                            conversions: acc.conversions + (adGroup.conversions || 0),
                            conversionsValue: acc.conversionsValue + (parseFloat(adGroup.conversionsValue) || 0)
                          }), { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });

                          const prevTotalCTR = prevTotals.impressions > 0 ? (prevTotals.clicks / prevTotals.impressions * 100) : 0;
                          const prevTotalCPC = prevTotals.clicks > 0 ? (prevTotals.cost / prevTotals.clicks) : 0;
                          const prevTotalConversionRate = prevTotals.clicks > 0 ? (prevTotals.conversions / prevTotals.clicks * 100) : 0;
                          const prevTotalCPA = prevTotals.conversions > 0 ? (prevTotals.cost / prevTotals.conversions) : 0;
                          const prevTotalCOS = prevTotals.conversionsValue > 0 ? (prevTotals.cost / prevTotals.conversionsValue * 100) : 0;

                          return (
                            <tr className="total-row">
                              <td className="campaign-name"><strong>TOTAL</strong></td>
                              {isMsadsAdGroupMetricSelected('impressions') && (
                                <td className="numeric">
                                  <strong>{formatNumber(totals.impressions)}</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totals.impressions, prevTotals.impressions))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('ctr') && (
                                <td className="numeric">
                                  <strong>{formatNumber(totalCTR, 2)}%</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totalCTR, prevTotalCTR))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('clics') && (
                                <td className="numeric">
                                  <strong>{formatNumber(totals.clicks)}</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totals.clicks, prevTotals.clicks))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('cpc') && (
                                <td className="numeric">
                                  <strong>{formatNumber(totalCPC, 2)}€</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totalCPC, prevTotalCPC), true)}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('cout') && (
                                <td className="numeric">
                                  <strong>{formatNumber(totals.cost, 2)}€</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totals.cost, prevTotals.cost), true)}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('taux_conv') && (
                                <td className="numeric">
                                  <strong>{formatNumber(totalConversionRate, 2)}%</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totalConversionRate, prevTotalConversionRate))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('conv') && (
                                <td className="numeric">
                                  <strong>{formatNumber(totals.conversions, 1)}</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totals.conversions, prevTotals.conversions))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('cpa') && (
                                <td className={`numeric ${getCpaClassName(totalCPA, selectedAccount?.id || 'global', 'ad_groups')}`}>
                                  <strong>{formatNumber(totalCPA, 2)}€</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totalCPA, prevTotalCPA), true)}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('panier_moyen') && (
                                <td className="numeric">
                                  <strong>{formatNumber(panierMoyenPublicitaire(totals), 2)}€</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(panierMoyenPublicitaire(totals), panierMoyenPublicitaire(prevTotals)))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('valeur_conv') && (
                                <td className="numeric">
                                  <strong>{formatNumber(totals.conversionsValue, 2)}€</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totals.conversionsValue, prevTotals.conversionsValue))}
                                </td>
                              )}
                              {isMsadsAdGroupMetricSelected('cos') && (
                                <td className={`numeric ${getCosClassName(totalCOS, selectedAccount?.id || 'global', 'ad_groups')}`}>
                                  <strong>{formatNumber(totalCOS, 1)}%</strong>
                                  {renderAdGroupVariation(calculateAdGroupVariation(totalCOS, prevTotalCOS), true)}
                                </td>
                              )}
                            </tr>
                          );
                        })()}
                      </tfoot>
                    </table>
                  </div>

                  {/* Contrôles de pagination */}
                  {totalPages > 1 && (
                    <div className="pagination-controls">
                      <div className="pagination-info">
                        Affichage {startIndex + 1}-{endIndex} sur {sortedAdGroups.length} groupes d'annonces
                      </div>
                      <div className="pagination-buttons">
                        <button 
                          onClick={() => setMsAdGroupCurrentPage(Math.max(1, msAdGroupCurrentPage - 1))}
                          disabled={msAdGroupCurrentPage === 1}
                          className="pagination-btn"
                        >
                          ← Précédent
                        </button>
                        <button 
                          onClick={() => setMsAdGroupCurrentPage(Math.min(totalPages, msAdGroupCurrentPage + 1))}
                          disabled={msAdGroupCurrentPage === totalPages}
                          className="pagination-btn"
                        >
                          Suivant →
                        </button>
                      </div>
                    </div>
                  )}
                  </>
                )}
              </div>
            );
          })()}

          {/* Section Appareils - avec chargement prioritaire */}
          {(() => {
            // Utiliser les données existantes d'autoCharts comme les autres sections
            let deviceDataSource = autoCharts.rawData ?
              Object.values(autoCharts.rawData).find(data => data.deviceData)?.deviceData || [] : [];

            // Appliquer le filtre par campagne si sélectionné
            if (selectedCampaignFilter && deviceDataSource.length > 0) {
              const filterName = selectedCampaignFilter.name || selectedCampaignFilter.campaignName || '';
              const deviceDataByCampaign = autoCharts.rawData ?
                Object.values(autoCharts.rawData).find(data => data.deviceDataByCampaign)?.deviceDataByCampaign || {} : {};

              // Récupérer les données de la campagne sélectionnée
              const campaignDeviceData = deviceDataByCampaign[filterName];
              if (campaignDeviceData) {
                // Convertir l'objet en array et calculer les métriques
                deviceDataSource = Object.values(campaignDeviceData).map(device => ({
                  ...device,
                  ctr: device.impressions > 0 ? (device.clicks / device.impressions * 100) : 0,
                  cpc: device.clicks > 0 ? (device.cost / device.clicks) : 0,
                  conversionRate: device.clicks > 0 ? (device.conversions / device.clicks * 100) : 0,
                  cpa: device.conversions > 0 ? (device.cost / device.conversions) : 0,
                  cos: device.conversionsValue > 0 ? (device.cost / device.conversionsValue * 100) : 0
                }));
              } else {
                deviceDataSource = [];
              }
            }

            if (deviceDataSource.length === 0) return null;

            // Trier les données selon msDevicesSortConfig
            deviceDataSource = [...deviceDataSource].sort((a, b) => {
              let aValue, bValue;
              const key = msDevicesSortConfig.key;

              switch (key) {
                case 'device':
                  aValue = a.device || '';
                  bValue = b.device || '';
                  return msDevicesSortConfig.direction === 'asc'
                    ? aValue.localeCompare(bValue)
                    : bValue.localeCompare(aValue);
                case 'ctr':
                  aValue = a.impressions > 0 ? (a.clicks / a.impressions * 100) : 0;
                  bValue = b.impressions > 0 ? (b.clicks / b.impressions * 100) : 0;
                  break;
                case 'cpc':
                  aValue = a.clicks > 0 ? (a.cost / a.clicks) : 0;
                  bValue = b.clicks > 0 ? (b.cost / b.clicks) : 0;
                  break;
                case 'conversionRate':
                  aValue = a.clicks > 0 ? (a.conversions / a.clicks * 100) : 0;
                  bValue = b.clicks > 0 ? (b.conversions / b.clicks * 100) : 0;
                  break;
                case 'cpa':
                  aValue = a.conversions > 0 ? (a.cost / a.conversions) : 0;
                  bValue = b.conversions > 0 ? (b.cost / b.conversions) : 0;
                  break;
                case 'cos':
                  aValue = a.conversionsValue > 0 ? (a.cost / a.conversionsValue * 100) : 0;
                  bValue = b.conversionsValue > 0 ? (b.cost / b.conversionsValue * 100) : 0;
                  break;
                case 'panier_moyen':
                  aValue = panierMoyenPublicitaire(a);
                  bValue = panierMoyenPublicitaire(b);
                  break;
                default:
                  aValue = a[key] || 0;
                  bValue = b[key] || 0;
              }
              return msDevicesSortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
            });

            // Pour les clients, ne pas afficher si le module est masqué
            if (userRole === 'CLIENT' && !isClientModuleVisible('devices')) {
              return null;
            }

            // Helpers pour la comparaison
            const calculateDeviceVariation = (current, previous) => {
              if (previous === 0 && current === 0) return 0;
              if (previous === 0) return current > 0 ? 100 : -100;
              return ((current - previous) / previous) * 100;
            };

            const renderDeviceVariation = (variation, isInversed = false) => {
              // Ne pas afficher les variations si msadsCompareEnabled est false
              if (!msadsCompareEnabled) return null;
              if (variation === null || variation === undefined) return null;
              const isPositive = isInversed ? variation < 0 : variation >= 0;
              const color = isPositive ? '#16a34a' : '#dc2626';
              const arrow = variation >= 0 ? '↑' : '↓';
              return (
                <span style={{ display: 'block', fontSize: '0.75em', color, marginTop: '2px' }}>
                  {arrow} {Math.abs(variation).toFixed(1)}%
                </span>
              );
            };

            // Map des données précédentes par appareil
            // Toujours afficher les données de comparaison si disponibles (pas de condition msadsCompareEnabled)
            const previousDevicesMap = {};
            if (previousPeriodData?.devices) {
              previousPeriodData.devices.forEach(device => {
                const key = device.device || device.deviceType || device.name;
                if (key) previousDevicesMap[key] = device;
              });
            }

            return (
              <div className="device-conversions-section">
                <div className="section-header">
                  <h4><Smartphone size={16} />&nbsp;Appareils</h4>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {userRole !== 'CLIENT' && (
                      <button
                        onClick={() => setShowMsMetricsConfig('msads_devices')}
                        className="metrics-settings-btn"
                        title="Configurer les métriques"
                      >
                        <Settings size={16} />
                      </button>
                    )}
                    <ModuleVisibilityToggle
                      isVisible={isClientModuleVisible('devices')}
                      onToggle={toggleClientModuleVisibility}
                      userRole={userRole}
                      moduleId="devices"
                    />
                  </div>
                </div>
                {(userRole !== 'CLIENT' || isClientModuleVisible('devices')) && (
                  <>
                    <div style={{ background: 'white', borderRadius: '12px', padding: '20px', marginBottom: '20px' }}>
                      <div style={{ display: 'flex', alignItems: 'center' }}>
                          {/* Pie chart Clics (gauche) */}
                          <div style={{ flex: 1, textAlign: 'center' }}>
                            <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Clics par appareil</h5>
                            <ResponsiveContainer width="100%" height={280}>
                              <PieChart>
                                <Pie
                                  data={deviceDataSource.map(device => ({
                                    name: device.device === 'Computer' ? 'Ordinateur' : 
                                          device.device === 'Smartphone' ? 'Smartphone' : 
                                          device.device === 'Tablet' ? 'Tablette' : device.device,
                                    value: device.clicks || 0,
                                    percentage: deviceDataSource.reduce((sum, d) => sum + (d.clicks || 0), 0) > 0 ? 
                                      ((device.clicks || 0) / deviceDataSource.reduce((sum, d) => sum + (d.clicks || 0), 0) * 100).toFixed(1) : '0.0'
                                  })).filter(device => parseFloat(device.percentage) > 0.0)}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ percentage }) => `${percentage}%`}
                                  outerRadius={70}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {deviceDataSource.map((entry, index) => (
                                    <Cell key={`clicks-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value, name) => [value + ' clics', name]} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                          
                          {/* Légende commune (centre) */}
                          <div style={{ 
                            minWidth: '120px', 
                            padding: '0 20px',
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center'
                          }}>
                            {deviceDataSource.map((device, index) => (
                              <div key={index} style={{ 
                                display: 'flex', 
                                alignItems: 'center', 
                                marginBottom: '8px',
                                fontSize: '13px'
                              }}>
                                <div style={{ 
                                  width: '12px', 
                                  height: '12px', 
                                  backgroundColor: COLORS[index % COLORS.length], 
                                  marginRight: '8px',
                                  borderRadius: '2px'
                                }}></div>
                                <span>{device.device === 'Computer' ? 'Ordinateur' : 
                                       device.device === 'Smartphone' ? 'Smartphone' : 
                                       device.device === 'Tablet' ? 'Tablette' : device.device}</span>
                              </div>
                            ))}
                          </div>
                          
                          {/* Pie chart Conversions (droite) */}
                          <div style={{ flex: 1, textAlign: 'center' }}>
                            <h5 style={{ margin: '0 0 10px 0', color: '#374151', fontSize: '14px' }}>Conversions par appareil</h5>
                            <ResponsiveContainer width="100%" height={280}>
                              <PieChart>
                                <Pie
                                  data={deviceDataSource.map(device => ({
                                    name: device.device === 'Computer' ? 'Ordinateur' : 
                                          device.device === 'Smartphone' ? 'Smartphone' : 
                                          device.device === 'Tablet' ? 'Tablette' : device.device,
                                    value: device.conversions || 0,
                                    percentage: deviceDataSource.reduce((sum, d) => sum + (d.conversions || 0), 0) > 0 ? 
                                      ((device.conversions || 0) / deviceDataSource.reduce((sum, d) => sum + (d.conversions || 0), 0) * 100).toFixed(1) : '0.0'
                                  })).filter(device => parseFloat(device.percentage) > 0.0)}
                                  cx="50%"
                                  cy="50%"
                                  labelLine={false}
                                  label={({ percentage }) => `${percentage}%`}
                                  outerRadius={70}
                                  fill="#8884d8"
                                  dataKey="value"
                                >
                                  {deviceDataSource.map((entry, index) => (
                                    <Cell key={`conversions-cell-${index}`} fill={COLORS[index % COLORS.length]} />
                                  ))}
                                </Pie>
                                <Tooltip formatter={(value, name) => [value + ' conversions', name]} />
                              </PieChart>
                            </ResponsiveContainer>
                          </div>
                        </div>
                      </div>
                    {/* Tableau détaillé des appareils */}
                    <div className="device-table-wrapper">
                      <table className="campaigns-table">
                        <thead>
                          <tr>
                            <th onClick={() => handleMsDevicesSort('device')} className="sortable" style={{ cursor: 'pointer' }}>
                              Appareil {msDevicesSortConfig.key === 'device' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                            </th>
                            {isMsadsDeviceMetricSelected('impressions') && (
                              <th onClick={() => handleMsDevicesSort('impressions')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                Impressions {msDevicesSortConfig.key === 'impressions' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('ctr') && (
                              <th onClick={() => handleMsDevicesSort('ctr')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                CTR {msDevicesSortConfig.key === 'ctr' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('clics') && (
                              <th onClick={() => handleMsDevicesSort('clicks')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                Clics {msDevicesSortConfig.key === 'clicks' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('cpc') && (
                              <th onClick={() => handleMsDevicesSort('cpc')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                CPC {msDevicesSortConfig.key === 'cpc' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('cout') && (
                              <th onClick={() => handleMsDevicesSort('cost')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                Coût {msDevicesSortConfig.key === 'cost' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('taux_conv') && (
                              <th onClick={() => handleMsDevicesSort('conversionRate')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                Taux conv. {msDevicesSortConfig.key === 'conversionRate' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('conv') && (
                              <th onClick={() => handleMsDevicesSort('conversions')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                Conv. {msDevicesSortConfig.key === 'conversions' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('cpa') && (
                              <th onClick={() => handleMsDevicesSort('cpa')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                CPA {msDevicesSortConfig.key === 'cpa' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('panier_moyen') && (
                              <th onClick={() => handleMsDevicesSort('panier_moyen')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                Panier moyen {msDevicesSortConfig.key === 'panier_moyen' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('valeur_conv') && (
                              <th onClick={() => handleMsDevicesSort('conversionsValue')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                Valeur conv. {msDevicesSortConfig.key === 'conversionsValue' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                            {isMsadsDeviceMetricSelected('cos') && (
                              <th onClick={() => handleMsDevicesSort('cos')} className="sortable numeric" style={{ cursor: 'pointer' }}>
                                COS {msDevicesSortConfig.key === 'cos' && (msDevicesSortConfig.direction === 'asc' ? '▲' : '▼')}
                              </th>
                            )}
                          </tr>
                        </thead>
                        <tbody>
                          {deviceDataSource.map((device, index) => {
                            const deviceName = device.device === 'Computer' ? 'Ordinateur' :
                                             device.device === 'Smartphone' ? 'Smartphone' :
                                             device.device === 'Tablet' ? 'Tablette' : device.device;

                            // Récupérer les données précédentes pour cet appareil
                            const prevDevice = previousDevicesMap[device.device] || {};
                            const prevCtr = prevDevice.impressions > 0 ? (prevDevice.clicks / prevDevice.impressions * 100) : 0;
                            const prevCpc = prevDevice.clicks > 0 ? (prevDevice.cost / prevDevice.clicks) : 0;
                            const prevConvRate = prevDevice.clicks > 0 ? (prevDevice.conversions / prevDevice.clicks * 100) : 0;
                            const prevCpa = prevDevice.conversions > 0 ? (prevDevice.cost / prevDevice.conversions) : 0;
                            const prevCos = prevDevice.conversionsValue > 0 ? (prevDevice.cost / prevDevice.conversionsValue * 100) : 0;

                            return (
                              <tr key={index}>
                                <td className="device-name">
                                  <span
                                    className="device-indicator"
                                    style={{ backgroundColor: COLORS[index % COLORS.length] }}
                                  ></span>
                                  {deviceName}
                                </td>
                                {isMsadsDeviceMetricSelected('impressions') && (
                                  <td className="numeric">
                                    {formatNumber(device.impressions || 0)}
                                    {renderDeviceVariation(calculateDeviceVariation(device.impressions || 0, prevDevice.impressions || 0))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('ctr') && (
                                  <td className="numeric">
                                    {formatNumber(device.ctr || 0, 2)}%
                                    {renderDeviceVariation(calculateDeviceVariation(device.ctr || 0, prevCtr))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('clics') && (
                                  <td className="numeric">
                                    {formatNumber(device.clicks || 0)}
                                    {renderDeviceVariation(calculateDeviceVariation(device.clicks || 0, prevDevice.clicks || 0))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('cpc') && (
                                  <td className="numeric">
                                    €{formatNumber(device.cpc || 0, 2)}
                                    {renderDeviceVariation(calculateDeviceVariation(device.cpc || 0, prevCpc), true)}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('cout') && (
                                  <td className="numeric">
                                    €{formatNumber(device.cost || 0, 2)}
                                    {renderDeviceVariation(calculateDeviceVariation(device.cost || 0, prevDevice.cost || 0), true)}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('taux_conv') && (
                                  <td className="numeric">
                                    {formatNumber(device.conversionRate || 0, 2)}%
                                    {renderDeviceVariation(calculateDeviceVariation(device.conversionRate || 0, prevConvRate))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('conv') && (
                                  <td className="numeric">
                                    {formatNumber(device.conversions || 0, 1)}
                                    {renderDeviceVariation(calculateDeviceVariation(device.conversions || 0, prevDevice.conversions || 0))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('cpa') && (
                                  <td className={`numeric ${getCpaClassName(device.cpa || 0, selectedAccount?.id || 'global', 'devices')}`}>
                                    {device.cpa >= 0 ? `€${formatNumber(device.cpa, 2)}` : '-'}
                                    {renderDeviceVariation(calculateDeviceVariation(device.cpa || 0, prevCpa), true)}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('panier_moyen') && (
                                  <td className="numeric">
                                    €{formatNumber(panierMoyenPublicitaire(device), 2)}
                                    {renderDeviceVariation(calculateDeviceVariation(panierMoyenPublicitaire(device), panierMoyenPublicitaire(prevDevice)))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('valeur_conv') && (
                                  <td className="numeric">
                                    €{formatNumber(device.conversionsValue || 0, 2)}
                                    {renderDeviceVariation(calculateDeviceVariation(device.conversionsValue || 0, prevDevice.conversionsValue || 0))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('cos') && (
                                  <td className={`numeric ${calculateCosClassName(device.cost, device.conversionsValue, selectedAccount?.id || 'global')}`}>
                                    {device.conversionsValue > 0 ? formatNumber((device.cost || 0) / (device.conversionsValue || 1) * 100, 1) + '%' : formatNumber(0, 1) + '%'}
                                    {renderDeviceVariation(calculateDeviceVariation((device.cost || 0) / (device.conversionsValue || 1) * 100, prevCos), true)}
                                  </td>
                                )}
                              </tr>
                            );
                          })}
                        </tbody>
                        <tfoot>
                          {(() => {
                            // Calculer les totaux actuels
                            const totalImpressions = deviceDataSource.reduce((sum, d) => sum + (d.impressions || 0), 0);
                            const totalClicks = deviceDataSource.reduce((sum, d) => sum + (d.clicks || 0), 0);
                            const totalCost = deviceDataSource.reduce((sum, d) => sum + (d.cost || 0), 0);
                            const totalConversions = deviceDataSource.reduce((sum, d) => sum + (d.conversions || 0), 0);
                            const totalConversionsValue = deviceDataSource.reduce((sum, d) => sum + (d.conversionsValue || 0), 0);
                            const totalCtr = totalImpressions > 0 ? (totalClicks / totalImpressions * 100) : 0;
                            const totalCpc = totalClicks > 0 ? (totalCost / totalClicks) : 0;
                            const totalConvRate = totalClicks > 0 ? (totalConversions / totalClicks * 100) : 0;
                            const totalCpa = totalConversions > 0 ? (totalCost / totalConversions) : 0;
                            const totalCos = totalConversionsValue > 0 ? (totalCost / totalConversionsValue * 100) : 0;

                            // Calculer les totaux de la période précédente
                            const prevDevices = previousPeriodData?.devices || [];
                            const prevTotalImpressions = prevDevices.reduce((sum, d) => sum + (d.impressions || 0), 0);
                            const prevTotalClicks = prevDevices.reduce((sum, d) => sum + (d.clicks || 0), 0);
                            const prevTotalCost = prevDevices.reduce((sum, d) => sum + (d.cost || 0), 0);
                            const prevTotalConversions = prevDevices.reduce((sum, d) => sum + (d.conversions || 0), 0);
                            const prevTotalConversionsValue = prevDevices.reduce((sum, d) => sum + (d.conversionsValue || 0), 0);
                            const prevTotalCtr = prevTotalImpressions > 0 ? (prevTotalClicks / prevTotalImpressions * 100) : 0;
                            const prevTotalCpc = prevTotalClicks > 0 ? (prevTotalCost / prevTotalClicks) : 0;
                            const prevTotalConvRate = prevTotalClicks > 0 ? (prevTotalConversions / prevTotalClicks * 100) : 0;
                            const prevTotalCpa = prevTotalConversions > 0 ? (prevTotalCost / prevTotalConversions) : 0;
                            const prevTotalCos = prevTotalConversionsValue > 0 ? (prevTotalCost / prevTotalConversionsValue * 100) : 0;

                            return (
                              <tr className="totals-row">
                                <td className="device-name"><strong>TOTAL</strong></td>
                                {isMsadsDeviceMetricSelected('impressions') && (
                                  <td className="numeric">
                                    <strong>{formatNumber(totalImpressions)}</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalImpressions, prevTotalImpressions))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('ctr') && (
                                  <td className="numeric">
                                    <strong>{formatNumber(totalCtr, 2)}%</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalCtr, prevTotalCtr))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('clics') && (
                                  <td className="numeric">
                                    <strong>{formatNumber(totalClicks)}</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalClicks, prevTotalClicks))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('cpc') && (
                                  <td className="numeric">
                                    <strong>€{formatNumber(totalCpc, 2)}</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalCpc, prevTotalCpc), true)}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('cout') && (
                                  <td className="numeric">
                                    <strong>€{formatNumber(totalCost, 2)}</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalCost, prevTotalCost), true)}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('taux_conv') && (
                                  <td className="numeric">
                                    <strong>{formatNumber(totalConvRate, 2)}%</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalConvRate, prevTotalConvRate))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('conv') && (
                                  <td className="numeric">
                                    <strong>{formatNumber(totalConversions)}</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalConversions, prevTotalConversions))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('cpa') && (
                                  <td className={`numeric ${getCpaClassName(totalCpa, selectedAccount?.id || 'global', 'devices')}`}>
                                    <strong>€{formatNumber(totalCpa, 2)}</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalCpa, prevTotalCpa), true)}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('panier_moyen') && (
                                  <td className="numeric">
                                    <strong>€{formatNumber(totalConversions > 0 ? totalConversionsValue / totalConversions : 0, 2)}</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(
                                      totalConversions > 0 ? totalConversionsValue / totalConversions : 0,
                                      prevTotalConversions > 0 ? prevTotalConversionsValue / prevTotalConversions : 0
                                    ))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('valeur_conv') && (
                                  <td className="numeric">
                                    <strong>€{formatNumber(totalConversionsValue, 2)}</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalConversionsValue, prevTotalConversionsValue))}
                                  </td>
                                )}
                                {isMsadsDeviceMetricSelected('cos') && (
                                  <td className={`numeric ${calculateCosClassName(totalCost, totalConversionsValue, selectedAccount?.id || 'global')}`}>
                                    <strong>{totalConversionsValue >= 0 ? formatNumber(totalCos, 1) + '%' : '-'}</strong>
                                    {renderDeviceVariation(calculateDeviceVariation(totalCos, prevTotalCos), true)}
                                  </td>
                                )}
                              </tr>
                            );
                          })()}
                        </tfoot>
                      </table>
                    </div>
                  </>
                )}
              </div>
            );
          })()}

          {/* Section Origine géographique Microsoft Ads - masquée si aucune donnée */}
          {selectedAccount && ((autoCharts.timelineCharts && autoCharts.timelineCharts.length > 0) || (autoCharts.charts && autoCharts.charts.length > 0)) && (
            <GeographyTableModular
              selectedAccount={selectedAccount}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              formatNumber={formatNumber}
              isVisible={true}
              token={token}
              setError={setError}
              compareEnabled={msadsCompareEnabled}
              comparisonType={msadsComparisonType}
              previousPeriodData={previousPeriodData}
              userRole={userRole}
              isClientModuleVisible={isClientModuleVisible}
              toggleClientModuleVisibility={toggleClientModuleVisibility}
              moduleId="msads_geography"
              provider="microsoft"
              selectedCampaignFilter={selectedCampaignFilter}
              selectedCampaignTypeFilter={selectedCampaignTypeFilter}
              selectedNetworkFilter={selectedNetworkFilter}
            />
          )}

          {/* Section Jours de la semaine Microsoft Ads - masquée si aucune donnée */}
          {selectedAccount && ((autoCharts.timelineCharts && autoCharts.timelineCharts.length > 0) || (autoCharts.charts && autoCharts.charts.length > 0)) && (
            <WeekdaysTable
              selectedAccount={selectedAccount}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              formatNumber={formatNumber}
              isVisible={true}
              token={token}
              compareEnabled={msadsCompareEnabled}
              comparisonType={msadsComparisonType}
              userRole={userRole}
              isClientModuleVisible={isClientModuleVisible}
              toggleClientModuleVisibility={toggleClientModuleVisibility}
              moduleId="msads_weekdays"
              provider="microsoft"
              selectedCampaignFilter={selectedCampaignFilter}
              selectedCampaignTypeFilter={selectedCampaignTypeFilter}
              selectedNetworkFilter={selectedNetworkFilter}
            />
          )}

          {/* Section Termes de recherche - identique au dashboard Google Ads */}
          {(() => {
            // Debug: vérifier ce qu'on a dans autoCharts.rawData

            // Vérifier s'il y a des données de termes de recherche 
            const hasSearchTermsData = autoCharts?.rawData && 
              Object.values(autoCharts.rawData).some(data => {
                
                let hasTerms = false;
                if (data?.searchTerms) {
                  if (Array.isArray(data.searchTerms)) {
                    // Structure plate (Microsoft Ads)
                    hasTerms = data.searchTerms.length > 0;
                  } else if (data.searchTerms.searchTerms && Array.isArray(data.searchTerms.searchTerms)) {
                    // Structure imbriquée (Google Ads)
                    hasTerms = data.searchTerms.searchTerms.length > 0;
                  }
                }
                return hasTerms;
              });
            

            // Extraire les données des termes de recherche et pagination
            let searchTermsData = [];
            let searchTermsPagination = null;
            if (autoCharts.rawData && selectedAccount) {
              // Essayer plusieurs clés possibles pour trouver les données
              const possibleKeys = [
                selectedAccount?.name || selectedAccount?.id,
                selectedAccount.id,
                selectedAccount.sourceName,
                // Essayer aussi de trouver par inclusion du nom
                ...Object.keys(autoCharts.rawData).filter(key => 
                  key.includes(selectedAccount?.name || selectedAccount?.id) || 
                  key.includes((selectedAccount?.name || selectedAccount?.id).split(' (')[0])
                )
              ];
              
              
              let accountData = null;
              let foundKey = null;
              
              // Essayer chaque clé jusqu'à trouver des données
              for (const key of possibleKeys) {
                if (autoCharts.rawData[key] && autoCharts.rawData[key].searchTerms) {
                  accountData = autoCharts.rawData[key];
                  foundKey = key;
                  break;
                }
              }
              
              if (accountData && accountData.searchTerms && accountData.searchTerms.searchTerms) {
                searchTermsData = accountData.searchTerms.searchTerms || [];
                searchTermsPagination = accountData.searchTerms.pagination;
              } else {
              }
            }

            // Note: Le filtre par campagne est appliqué côté backend pour une pagination correcte
            
            // Utiliser les informations de pagination du serveur si disponibles
            // IMPORTANT: totalTerms doit refléter le total sur TOUTES les pages, pas seulement la page courante
            
            // Calculer le nombre total de termes à partir de totalPages * limit si totalCount n'est pas disponible
            const itemsPerPage = searchTermsPagination?.limit || searchTermsPagination?.perPage || 20;
            const totalPages = searchTermsPagination?.totalPages || searchTermsPagination?.pages || Math.ceil(searchTermsData.length / itemsPerPage);
            const totalTerms = searchTermsPagination?.totalCount || searchTermsPagination?.total || (totalPages * itemsPerPage);
            const currentPage = msSearchTermsCurrentPage;
            

            // Fonction de tri pour les termes de recherche
            const sortSearchTermsData = (data, sortConfig) => {
              if (!sortConfig.key) return data;
              
              return [...data].sort((a, b) => {
                let aVal = a[sortConfig.key];
                let bVal = b[sortConfig.key];
                
                // Gérer les cas spéciaux pour les valeurs numériques
                if (['cost', 'conversionsValue', 'cpc', 'cpa'].includes(sortConfig.key)) {
                  // Convertir les chaînes avec € en nombres
                  aVal = typeof aVal === 'string' ? parseFloat(aVal.replace('€', '')) || 0 : parseFloat(aVal) || 0;
                  bVal = typeof bVal === 'string' ? parseFloat(bVal.replace('€', '')) || 0 : parseFloat(bVal) || 0;
                } else if (['ctr', 'conversionRate', 'cos'].includes(sortConfig.key)) {
                  // Convertir les pourcentages en nombres
                  aVal = typeof aVal === 'string' ? parseFloat(aVal.replace('%', '')) || 0 : parseFloat(aVal) || 0;
                  bVal = typeof bVal === 'string' ? parseFloat(bVal.replace('%', '')) || 0 : parseFloat(bVal) || 0;
                } else if (typeof aVal === 'number' || typeof bVal === 'number') {
                  // Gérer les autres valeurs numériques
                  aVal = parseFloat(aVal) || 0;
                  bVal = parseFloat(bVal) || 0;
                }
                
                if (aVal < bVal) return sortConfig.direction === 'asc' ? -1 : 1;
                if (aVal > bVal) return sortConfig.direction === 'asc' ? 1 : -1;
                return 0;
              });
            };
            
            // Le serveur fait déjà le tri par coût décroissant et la pagination
            // Pas besoin de re-trier côté client pour éviter de perturber la pagination
            const paginatedSearchTerms = searchTermsData;
            const msSearchTermsTotalPages = totalPages;
            
            // Calculer les totaux : priorité aux totaux de TOUS les termes depuis l'API séparée
            let searchTermsTotals = { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0, ctr: 0, cpc: 0, conversionRate: 0, cpa: 0, cos: 0 };
            
            if (autoCharts.rawData && selectedAccount) {
              // Essayer plusieurs clés possibles pour trouver les données
              const possibleKeys = [
                selectedAccount?.name || selectedAccount?.id,
                selectedAccount.id,
                selectedAccount.sourceName,
                ...Object.keys(autoCharts.rawData).filter(key => 
                  key.includes(selectedAccount?.name || selectedAccount?.id) || 
                  key.includes((selectedAccount?.name || selectedAccount?.id).split(' (')[0])
                )
              ];
              
              let accountDataForTotals = null;
              let foundKey = null;
              
              // Essayer chaque clé jusqu'à trouver des données
              for (const key of possibleKeys) {
                if (autoCharts.rawData[key]) {
                  accountDataForTotals = autoCharts.rawData[key];
                  foundKey = key;
                  break;
                }
              }
              
              if (!accountDataForTotals) {
                console.log(`⚠️ [TOTALS] Aucune donnée trouvée pour les clés:`, possibleKeys);
              } else {
                console.log(`✅ [TOTALS] Données trouvées avec la clé "${foundKey}"`);
              }
              
              // 1. Priorité : totaux précalculés de tous les termes
              if (accountDataForTotals?.searchTerms?.totals) {
                console.log(`✅ [TOTALS] Utilisation des totaux précalculés sur TOUS les termes`);
                searchTermsTotals = {
                  impressions: accountDataForTotals.searchTerms.totals.totalImpressions || 0,
                  clicks: accountDataForTotals.searchTerms.totals.totalClicks || 0,
                  cost: accountDataForTotals.searchTerms.totals.totalCost || 0,
                  conversions: accountDataForTotals.searchTerms.totals.totalConversions || 0,
                  conversionsValue: accountDataForTotals.searchTerms.totals.totalConversionsValue || 0,
                  ctr: 0, // Sera calculé après
                  cpc: 0, 
                  conversionRate: 0,
                  cpa: 0,
                  cos: 0
                };
                console.log(`📊 [TOTALS] Total de ${accountDataForTotals.searchTerms.totals.totalTerms || 'N/A'} termes:`, searchTermsTotals);
                
              // 2. Fallback : calculer sur les termes affichés uniquement
              } else if (searchTermsData.length > 0) {
                console.warn(`⚠️ [FALLBACK] Calcul des totaux sur ${searchTermsData.length} termes affichés seulement`);
                
                const calculated = searchTermsData.reduce((acc, term) => {
                  const cost = typeof term.cost === 'string' ? parseFloat(term.cost.replace('€', '')) || 0 : parseFloat(term.cost) || 0;
                  const conversionsValue = typeof term.conversionsValue === 'string' ? parseFloat(term.conversionsValue.replace('€', '')) || 0 : parseFloat(term.conversionsValue) || 0;
                  
                  return {
                    impressions: acc.impressions + (term.impressions || 0),
                    clicks: acc.clicks + (term.clicks || 0),
                    cost: acc.cost + cost,
                    conversions: acc.conversions + (term.conversions || 0),
                    conversionsValue: acc.conversionsValue + conversionsValue
                  };
                }, { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 });
                
                searchTermsTotals = { ...calculated, ctr: 0, cpc: 0, conversionRate: 0, cpa: 0, cos: 0 };
              }
            }
            
            // Calculer les métriques dérivées
            searchTermsTotals.ctr = searchTermsTotals.impressions > 0 ? (searchTermsTotals.clicks / searchTermsTotals.impressions * 100) : 0;
            searchTermsTotals.cpc = searchTermsTotals.clicks > 0 ? (searchTermsTotals.cost / searchTermsTotals.clicks) : 0;
            searchTermsTotals.conversionRate = searchTermsTotals.clicks > 0 ? (searchTermsTotals.conversions / searchTermsTotals.clicks * 100) : 0;
            searchTermsTotals.cpa = searchTermsTotals.conversions > 0 ? (searchTermsTotals.cost / searchTermsTotals.conversions) : 0;
            searchTermsTotals.cos = searchTermsTotals.conversionsValue > 0 ? (searchTermsTotals.cost / searchTermsTotals.conversionsValue * 100) : 0;

            if (totalTerms === 0) {
              return null;
            }
            
            return (
              <MicrosoftSearchTermsTable
                searchTermsData={searchTermsData}
                pagination={{
                  current: currentPage,
                  total: totalTerms,
                  totalPages: msSearchTermsTotalPages,
                  limit: itemsPerPage
                }}
                isVisible={showSearchTerms}
                onToggleVisibility={() => setShowSearchTerms(!showSearchTerms)}
                formatNumber={formatNumber}
                selectedAccount={selectedAccount}
                itemsPerPage={itemsPerPage}
                onPageChange={setMsSearchTermsCurrentPage}
                totals={searchTermsTotals}
                userRole={userRole}
                isClientModuleVisible={isClientModuleVisible}
                toggleClientModuleVisibility={toggleClientModuleVisibility}
                moduleId="msads_search_terms"
                previousPeriodData={previousPeriodData}
                compareEnabled={msadsCompareEnabled}
                comparisonType={msadsComparisonType}
              />
            );
          })()}

          {/* Message informatif si pas de données Microsoft Ads */}
          {(() => {
            // Vérifier s'il y a des données à afficher
            const hasAdGroups = msAdGroupsData && msAdGroupsData.length > 0;
            const hasSearchTerms = autoCharts && Object.values(autoCharts).some(accountData => {
              if (!accountData?.searchTerms?.searchTerms) return false;
              return accountData.searchTerms.searchTerms.length > 0;
            });
            
            return null;
          })()}

        </div>
      )}


      {/* Message si pas de données */}
      {!loadingCharts && !autoCharts && selectedAccount && (
        <div className="empty-state">
          <p>Sélectionnez un compte et une période pour voir le dashboard</p>
        </div>
      )}
      
      {/* Modal d'ajout de données Microsoft Ads */}
      {showAddModal && (
        <AddDataModal
          token={token}
          onClose={() => setShowAddModal(false)}
          onAdded={() => {
            loadLibraryItems();
            setShowAddModal(false);
          }}
          setSuccess={(msg) => {/* Gérer le succès si nécessaire */}}
          setError={setError}
          initialStep="microsoft-ads"
          connectors={globalConnectors}
        />
      )}

      {/* Modal de configuration des métriques Microsoft Ads */}
      {showMsMetricsConfig && (
        <MetricsConfigModal
          isOpen={!!showMsMetricsConfig}
          onClose={() => setShowMsMetricsConfig(null)}
          componentId={showMsMetricsConfig}
          accountId={selectedAccount?.id || 'global'}
          title={
            showMsMetricsConfig === 'msads_campaigns' ? 'Campagnes Microsoft Ads' :
            showMsMetricsConfig === 'msads_adgroups' ? "Groupes d'annonces Microsoft Ads" :
            showMsMetricsConfig === 'msads_devices' ? 'Appareils Microsoft Ads' :
            'Configuration des métriques'
          }
          hideApplyToAll={true}
        />
      )}

    </div>
  );
}

// Fonction de comparaison pour React.memo - évite les re-rendus inutiles
const DashboardGoogleAdsComparison = (prevProps, nextProps) => {
  // Comparaison des props critiques pour éviter les re-rendus excessifs
  const criticalProps = [
    'selectedPeriod', 'customStartDate', 'customEndDate',
    'selectedDashboardModel', 'preselectedAccountId', 'activeTab', 'selectedClientId',
    'dashboardNotes', 'selectedSiteId',
    'pmaxCompareEnabled', 'pmaxComparisonType',
    'gadsComparisonType', 'gadsCompareEnabled'
  ];

  for (const prop of criticalProps) {
    if (prevProps[prop] !== nextProps[prop]) {
      return false; // Re-render si une prop critique a changé
    }
  }

  return true; // Éviter le re-render si aucune prop critique n'a changé
};

// Mémoriser le composant DashboardGoogleAds pour éviter les re-rendus excessifs
const MemoizedDashboardGoogleAds = memo(DashboardGoogleAds, DashboardGoogleAdsComparison);

// ===== COMPOSANT DASHBOARD FACEBOOK ADS =====
function DashboardFacebookAds({ token, setError, selectedPeriod, setSelectedPeriod, customStartDate, setCustomStartDate, customEndDate, setCustomEndDate, selectedDashboardModel, setSelectedDashboardModel, preselectedAccountId, activeTab, preselectionUsed, setPreselectionUsed, globalConnectors, canAccessAnalyzer, selectedClientId, updateUrlWithAccount, authError, setAuthError, handleApiError, handleReconnect, dashboardNotes, fetchDashboardNotes, selectedSiteId, userSites, compareEnabled = false, comparisonType = '', userRole = 'USER', comparisonStartDate = '', comparisonEndDate = '' }) {
  const [selectedSources, setSelectedSources] = useState([]);
  const [autoCharts, setAutoCharts] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const selectedMetrics = useSelectedMetrics(selectedAccount?.id || 'global');
  const [libraryItems, setLibraryItems] = useState([]);
  const [allLibraryItems, setAllLibraryItems] = useState([]); // Tous les comptes (non filtrés)
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [campaignsData, setCampaignsData] = useState(null);
  const [previousCampaignsData, setPreviousCampaignsData] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [showCampaignDetails, setShowCampaignDetails] = useState(true);
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState(null);
  const [selectedPlatformFilter, setSelectedPlatformFilter] = useState(null);
  const [fbCampaignSearchQuery, setFbCampaignSearchQuery] = useState('');

  // Calculer le clientId effectif à partir du compte Facebook Ads sélectionné
  // Fallback: utiliser l'ID du compte Facebook Ads préfixé si pas de site associé
  const effectiveClientId = React.useMemo(() => {
    if (selectedClientId) return selectedClientId;
    if (!selectedAccount) return null;
    const accountId = selectedAccount.id || selectedAccount.sourceId;
    const accountSite = userSites.find(s =>
      s.accountIds?.includes(selectedAccount.id) ||
      s.accountIds?.includes(selectedAccount.sourceId)
    );
    return accountSite?.clientId || (accountId ? `fbads_${accountId}` : null);
  }, [selectedClientId, selectedAccount, userSites]);

  // Hook pour la visibilité des modules par client (persistée en base de données)
  const {
    isModuleVisible: isClientModuleVisible,
    toggleModuleVisibility: toggleClientModuleVisibility,
    setModuleVisibility: setClientModuleVisibility,
    hiddenModules: clientHiddenModules
  } = useClientModuleVisibility(effectiveClientId, userRole);

  // State pour le modal des modules masqués (Facebook Ads)
  const [isHiddenModulesModalOpen, setIsHiddenModulesModalOpen] = useState(false);

  // Ref pour garder la valeur la plus récente de libraryItems (pour les event handlers)
  const libraryItemsRef = React.useRef(libraryItems);
  React.useEffect(() => {
    libraryItemsRef.current = libraryItems;
  }, [libraryItems]);
  const [showCampaignAutocomplete, setShowCampaignAutocomplete] = useState(false);
  const [granularity, setGranularity] = useState('day');
  const [showFacebookGender, setShowFacebookGender] = useState(true);
  const [showFacebookPlacements, setShowFacebookPlacements] = useState(true);
  const [showFacebookDevices, setShowFacebookDevices] = useState(true);
  const [showFacebookAge, setShowFacebookAge] = useState(true);
  const [showFacebookAdSets, setShowFacebookAdSets] = useState(true);
  const [showFacebookAds, setShowFacebookAds] = useState(true);
  const [showEngagementDetails, setShowEngagementDetails] = useState(true);
  const [showFacebookWeekdays, setShowFacebookWeekdays] = useState(true);
  const [showFacebookPlatform, setShowFacebookPlatform] = useState(true);

  // États pour le filtrage par labels : seule la sélection est locale, les données viennent du
  // hook partagé (voir plus bas).
  const [selectedLabelFilters, setSelectedLabelFilters] = useState([]);
  const [showLabelDropdown, setShowLabelDropdown] = useState(false);
  const labelDropdownRef = React.useRef(null);

  // Fermer le dropdown labels quand on clique en dehors
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (labelDropdownRef.current && !labelDropdownRef.current.contains(event.target)) {
        setShowLabelDropdown(false);
      }
    };

    if (showLabelDropdown) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showLabelDropdown]);

  // État pour les données de la période précédente
  const [previousPeriodData, setPreviousPeriodData] = useState(null);

  // Réinitialiser les données de résultats quand l'onglet change
  React.useEffect(() => {
    if (activeTab === 'facebook-ads') {
      setResultsData(null);
      setLoadingResults(false);
      setCampaignsData(null);
      setLoadingCampaigns(false);
    }
  }, [activeTab]);

  // Réinitialiser les filtres quand on change de compte ou de modèle de dashboard (Facebook Ads)
  // Note: les filtres persistent lors du changement de période pour permettre la comparaison
  React.useEffect(() => {
    if (selectedCampaignFilter || fbCampaignSearchQuery || selectedPlatformFilter || selectedLabelFilters.length > 0) {
      console.log('🔄 [FB-CAMPAIGN-FILTER] Réinitialisation des filtres lors du changement de compte/modèle');
      setSelectedCampaignFilter(null);
      setFbCampaignSearchQuery('');
      setShowCampaignAutocomplete(false);
      setSelectedPlatformFilter(null);
      setSelectedLabelFilters([]);
      setShowLabelDropdown(false);
    }
  }, [selectedAccount, selectedDashboardModel]);

  // Synchroniser le breadcrumb quand un compte est sélectionné (Facebook Ads)
  React.useEffect(() => {
    if (selectedAccount) {
      console.log('🔄 [BREADCRUMB] Synchronisation compte Facebook Ads:', selectedAccount.name);
      window.dispatchEvent(new CustomEvent('accountSelected', {
        detail: { accountId: selectedAccount.id, accountName: selectedAccount.name }
      }));
    }
  }, [selectedAccount]);

  // Exposer la fonction de génération automatique pour la sidebar
  React.useEffect(() => {
    window.facebookDashboardGenerateAuto = generateDashboard;
    return () => {
      delete window.facebookDashboardGenerateAuto;
    };
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedDashboardModel]);

  // Exposer la fonction d'analyse IA globalement pour le bouton du parent
  React.useEffect(() => {
    window.facebookDashboardAIAnalysis = handleAIAnalysis;
    return () => {
      delete window.facebookDashboardAIAnalysis;
    };
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate]);

  /*
   * L'analyse s'ouvre en panneau latéral, comme aux dashboards Google et Microsoft Ads.
   *
   * Meta appelait `/api/dashboard/ai-insights`, qui refait sa propre collecte à partir
   * d'identifiants de comptes : son analyse portait donc sur des chiffres qu'elle allait
   * rechercher, et non sur le tableau affiché. Le panneau envoie l'écran tel quel.
   */
  const [panneauAnalyseOuvert, setPanneauAnalyseOuvert] = useState(false);

  useEffect(() => {
    const signaler = window.setAnalyseOuverte;
    if (!signaler) return undefined;
    signaler(panneauAnalyseOuvert);
    return () => signaler(false);
  }, [panneauAnalyseOuvert]);

  useEffect(() => {
    document.body.classList.toggle('panneau-analyse-ouvert', panneauAnalyseOuvert);

    const mesurer = () => {
      const contenu = document.querySelector('.app-layout-with-sidebar');
      const entete = document.querySelector('.app-header');
      const haut = contenu
        ? contenu.getBoundingClientRect().top
        : (entete ? entete.getBoundingClientRect().bottom : 0);
      document.documentElement.style.setProperty('--haut-panneau-analyse', `${Math.max(0, Math.round(haut))}px`);
      if (entete) {
        document.documentElement.style.setProperty('--hauteur-entete-app', `${Math.round(entete.getBoundingClientRect().height)}px`);
      }
    };

    if (!panneauAnalyseOuvert) {
      document.documentElement.style.removeProperty('--haut-panneau-analyse');
      document.documentElement.style.removeProperty('--hauteur-entete-app');
      return () => document.body.classList.remove('panneau-analyse-ouvert');
    }

    mesurer();
    window.addEventListener('resize', mesurer);

    return () => {
      document.body.classList.remove('panneau-analyse-ouvert');
      document.documentElement.style.removeProperty('--haut-panneau-analyse');
      document.documentElement.style.removeProperty('--hauteur-entete-app');
      window.removeEventListener('resize', mesurer);
    };
  }, [panneauAnalyseOuvert]);

  /** Ce qu'on envoie à l'analyse : les données **déjà affichées**, lues à l'envoi de la question. */
  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    const bruts = {
      'Campagnes': campaignsData,
      'Période précédente': previousCampaignsData,
      'Résultats': resultsData
    };
    const blocs = {};
    const absents = [];
    for (const [libelle, bloc] of Object.entries(bruts)) {
      const reduit = bloc ? lignesDuBloc(bloc) : null;
      if (reduit) blocs[libelle] = reduit;
      else absents.push(libelle);
    }

    return {
      dashboardData: {
        sources: selectedSources,
        ...periode,
        charts: autoCharts?.charts,
        composants: { blocs, absents },
        dashboardType: 'meta'
      },
      dashboardType: 'meta',
      accountId: selectedAccount?.sourceId || selectedAccount?.id || null,
      accountName: selectedAccount?.name || null,
      ...periode
    };
  };

  const handleAIAnalysis = () => {
    if (!canAccessAnalyzer) {
      setError('Cette fonctionnalité nécessite un abonnement actif');
      return;
    }
    if (!selectedAccount) {
      setError('Veuillez sélectionner un compte Meta Ads');
      return;
    }
    setPanneauAnalyseOuvert(true);
  };


  /**
   * Les labels des campagnes Meta, par le hook partagé.
   *
   * Ce bloc faisait **deux** requêtes — `/api/campaigns/labels` puis `/api/campaigns` — là où la
   * seconde suffit : les labels disponibles se déduisent des campagnes rendues. Le hook n'en fait
   * qu'une, et il prévient les autres instances après chaque écriture, ce qui fait apparaître le
   * filtre dès le premier label posé depuis le panneau.
   */
  const {
    labelsParCampagne: campaignsWithLabels,
    labelsDisponibles: availableLabels,
    recharger: loadLabelsForAccount
  } = useCampagnesEtiquetees({
    accountId: selectedAccount?.sourceId || selectedAccount?.id,
    accountType: 'FACEBOOK_ADS',
    token
  });

  // Changer de compte remet le filtre à zéro : les labels d'un compte n'ont pas cours sur l'autre
  React.useEffect(() => {
    setSelectedLabelFilters([]);
  }, [selectedAccount]);


  // Charger les notes du dashboard quand le compte ou les dates changent
  useEffect(() => {
    if (!selectedAccount || !fetchDashboardNotes) return;
    const accountId = selectedAccount?.sourceId || selectedAccount?.id;
    fetchDashboardNotes('FACEBOOK_ADS', accountId, customStartDate, customEndDate);
  }, [selectedAccount, customStartDate, customEndDate, fetchDashboardNotes]);

  // Fonction toggle pour les labels
  const toggleLabelFilter = (label) => {
    // Reset les données de Résultats pour afficher l'état de chargement (comme les autres composants)
    // Note: Campagnes utilise un filtrage client-side donc pas besoin de reset
    setResultsData(null);
    setLoadingResults(true);

    setSelectedLabelFilters(prev =>
      prev.includes(label)
        ? prev.filter(l => l !== label)
        : [...prev, label]
    );
  };

  // Calculer les IDs de campagnes filtrées par labels
  const filteredCampaignIds = React.useMemo(() => {
    if (selectedLabelFilters.length === 0) {
      console.log('🏷️ [FB-FILTER] Aucun label sélectionné, filteredCampaignIds = null');
      return null; // Pas de filtre
    }

    // Retourner les IDs des campagnes ayant AU MOINS UN des labels sélectionnés
    const ids = Object.entries(campaignsWithLabels)
      .filter(([campaignId, labels]) =>
        selectedLabelFilters.some(label => labels.includes(label))
      )
      .map(([campaignId]) => campaignId);

    console.log('🏷️ [FB-FILTER] Labels sélectionnés:', selectedLabelFilters);
    console.log('🏷️ [FB-FILTER] Campagnes avec labels (keys):', Object.keys(campaignsWithLabels));
    console.log('🏷️ [FB-FILTER] Campagnes avec labels (full):', JSON.stringify(campaignsWithLabels, null, 2));
    console.log('🏷️ [FB-FILTER] IDs filtrés:', ids);
    console.log('🏷️ [FB-FILTER] Nombre d\'IDs:', ids.length);

    return ids;
  }, [selectedLabelFilters, campaignsWithLabels]);

  /**
   * Les deux totaux que les tableaux de ventilation ne peuvent pas calculer eux-mêmes.
   *
   * La couverture d'une ligne est juste — Meta la déduplique dans son segment —, mais leur
   * somme ne l'est pas : une personne vue sur Facebook et sur Instagram figure dans les deux
   * lignes. Seul Meta sait déduplier, et il le fait pour l'ensemble qu'on lui demande.
   *
   * **La source est le résumé de la timeline, et non celui des campagnes.** La timeline est
   * interrogée avec les mêmes filtres que les tableaux — campagne, plateforme, labels —, si
   * bien que son total porte toujours exactement sur ce qu'ils affichent. Le résumé des
   * campagnes, lui, ignore ces filtres : s'en servir obligeait à retenir le total dès qu'un
   * filtre était posé, c'est-à-dire à priver l'écran d'un chiffre qui existait pourtant.
   *
   * Un seul compte à la fois sur ce dashboard ; au-delà, aucun total n'existerait, Meta ne
   * sachant pas déduplier entre deux comptes publicitaires.
   */
  const resumesMetaTimeline = resultsData?.rawData ? Object.values(resultsData.rawData) : [];
  const resumeMetaTimeline = resumesMetaTimeline.length === 1 ? resumesMetaTimeline[0]?.metrics : null;

  const totalCouvertureCompte = Number.isFinite(resumeMetaTimeline?.totalReach)
    ? resumeMetaTimeline.totalReach
    : null;
  const totalRepetitionCompte = Number.isFinite(resumeMetaTimeline?.frequency)
    ? resumeMetaTimeline.frequency
    : null;

  /**
   * Ce que compte la métrique Conversions sur ce compte Meta.
   *
   * Même source que la couverture dédupliquée : la timeline, seule à connaître la configuration
   * enregistrée sur le compte. Les tableaux de ventilation interrogent chacun leur propre route
   * et n'en savent rien — elle leur descend en propriété, faute de quoi leur en-tête afficherait
   * « Conversions » là où la courbe affiche « Conversions (Ajout au panier) ».
   *
   * `null` signifie « rien de configuré » : la métrique compte alors les achats.
   */
  const entreeMetaTimeline = resumesMetaTimeline.length === 1 ? resumesMetaTimeline[0] : null;
  const configConversionsMeta = entreeMetaTimeline?.conversionConfig || null;

  // Charger les comptes Facebook Ads de la bibliothèque
  React.useEffect(() => {
    const loadLibraryItems = async () => {
      setLoadingLibrary(true);
      try {
        const response = await fetch(`${API_URL}/api/library`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success && data.library) {
          // Chercher les comptes Facebook Ads
          const facebookAdsItems = data.library.FACEBOOK_ADS || [];
          console.log(`📘 ${facebookAdsItems.length} comptes Facebook Ads trouvés pour le dashboard`);
          
          // Déduplication basée sur l'ID unique
          const uniqueFacebookAdsItems = facebookAdsItems.filter((item, index, self) => 
            index === self.findIndex((t) => t.id === item.id)
          );
          console.log(`📘 Après déduplication: ${uniqueFacebookAdsItems.length} comptes uniques`);
          
          setAllLibraryItems(uniqueFacebookAdsItems); // Garder une copie des comptes non filtrés
          setLibraryItems(uniqueFacebookAdsItems);
        } else {
          console.log('Erreur API:', data.error);
        }
      } catch (error) {
        console.error('Erreur chargement bibliothèque:', error);
      } finally {
        setLoadingLibrary(false);
      }
    };

    loadLibraryItems();
  }, [token]);

  // Filtrer les comptes par client ou par site (Facebook Ads)
  const filterAccountsByClient = async (accounts, clientId, siteId) => {
    // Si un site spécifique est sélectionné, filtrer par ce site en utilisant userSites
    if (siteId && siteId !== 'all' && accounts.length) {
      const site = userSites.find(s => (s._id || s.id) === siteId);
      if (site && site.accountIds) {
        const allowedAccountIds = ensembleAutorise(site.accountIds);
        const filteredAccounts = accounts.filter(account => compteAutorise(account, allowedAccountIds));
        return filteredAccounts;
      }
    }

    if (!clientId || !accounts.length) {
      return accounts;
    }

    try {
      // Récupérer les sites associés au client
      const response = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { clientId }
      });

      const sites = response.data.sites || [];

      // Extraire tous les accountIds des sites du client
      const allowedAccountIds = ensembleAutorise(
        sites.flatMap(site => (Array.isArray(site.accountIds) ? site.accountIds : []))
      );

      const filteredAccounts = accounts.filter(account => compteAutorise(account, allowedAccountIds));

      return filteredAccounts;
    } catch (error) {
      console.error('Erreur filtrage comptes par client:', error);
      return accounts;
    }
  };

  // Refs pour tracker le site et client précédents (Facebook Ads)
  const fbPrevSiteIdRef = React.useRef(selectedSiteId);
  const fbPrevClientIdRef = React.useRef(selectedClientId);

  // Recharger la liste des comptes et gérer la sélection quand le site/client change (Facebook Ads)
  React.useEffect(() => {
    const siteChanged = fbPrevSiteIdRef.current !== selectedSiteId;
    const clientChanged = fbPrevClientIdRef.current !== selectedClientId;
    fbPrevSiteIdRef.current = selectedSiteId;
    fbPrevClientIdRef.current = selectedClientId;

    const filterAndSelectAccount = async () => {
      if (siteChanged || clientChanged) {
        console.log('🔄 [FACEBOOK ADS] Changement de site/client détecté:', { selectedSiteId, selectedClientId });
        // IMPORTANT: Réinitialiser le flag de présélection pour éviter que l'useEffect
        // de réinitialisation ne supprime le compte auto-sélectionné
        hadPreselectedAccountRef.current = false;

        // IMMÉDIAT: Effacer les données du dashboard dès le changement de client
        // pour un feedback visuel instantané (avant le filtrage asynchrone)
        console.log('🔄 [FACEBOOK ADS] Effacement immédiat des données (changement client)');
        setAutoCharts(null);
        setAiInsights(null);
        setResultsData(null);
        setCampaignsData(null);
        setError('');
        setSelectedAccount(null);
        setCurrentActiveAccount(null);
        setSelectedSources([]);
      }

      if (allLibraryItems.length > 0) {
        console.log('🔍 [FACEBOOK ADS] Filtrage des comptes pour site:', selectedSiteId);
        const filteredItems = await filterAccountsByClient(allLibraryItems, selectedClientId, selectedSiteId);
        console.log('🔍 [FACEBOOK ADS] Comptes filtrés:', filteredItems.length);
        setLibraryItems(filteredItems);

        // Si un seul compte disponible après filtrage, l'auto-sélectionner
        // (sauf si un compte présélectionné EXISTE dans la liste)
        const preselectedExistsInList = preselectedAccountId && filteredItems.some(item => item.id === preselectedAccountId);
        if (filteredItems.length === 1 && !preselectedExistsInList) {
          const account = filteredItems[0];
          console.log('🎯 [FACEBOOK ADS] Auto-sélection du seul compte disponible:', account.name);
          setSelectedAccount(account);
          setCurrentActiveAccount(account.id);
          setSelectedSources([account.id]);
          // Mettre à jour le contexte des notes
          const accountSite = userSites.find(s => s.accountIds?.includes(account.id) || s.accountIds?.includes(account.sourceId));
          window.currentNotesContext = {
            platform: 'FACEBOOK_ADS',
            accountId: account.sourceId || account.id,
            accountName: account.name,
            siteId: accountSite?._id || accountSite?.id || null,
            clientId: accountSite?.clientId || null
          };
          // Mettre à jour le breadcrumb
          window.dispatchEvent(new CustomEvent('accountSelected', {
            detail: { accountId: account.id, accountName: account.name }
          }));
        }
      }
    };

    filterAndSelectAccount();
  }, [selectedClientId, selectedSiteId, allLibraryItems]);

  // Fonction pour connecter Facebook Ads
  const handleConnectFacebookAds = () => {
    window.location.href = `${API_URL}/api/auth/facebook-ads?token=${token}&returnTo=connectors`;
  };

  // Fonction pour connecter Pinterest Ads
  const handleConnectPinterestAds = () => {
    window.location.href = `${API_URL}/api/auth/pinterest-ads?token=${token}&returnTo=connectors`;
  };


  // Charger les données de la section Résultats avec l'endpoint Facebook Ads Timeline
  const loadResultsSection = React.useCallback(async (currentFilteredCampaignIds) => {
    if (!selectedAccount) {
      console.log('❌ [RESULTS] Pas de selectedAccount, arrêt');
      return;
    }

    // Réinitialiser les erreurs d'authentification au début du chargement
    setAuthError(null);

    try {
      setLoadingResults(true);
      console.log('⚡ [RESULTS] Chargement prioritaire section 📈 Résultats Facebook Ads...');
      console.log('⚡ [RESULTS] currentFilteredCampaignIds:', currentFilteredCampaignIds);

      const baseRequestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod,
        granularity: granularity
      };

      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) return;
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }

      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        baseRequestBody.clientId = selectedClientId;
        console.log('🎯 [FB-RESULTS] Filtrage par client:', selectedClientId);
      }
      
      // Ajouter le filtre campagne si une campagne est sélectionnée
      if (selectedCampaignFilter) {
        baseRequestBody.campaignFilter = selectedCampaignFilter;
        console.log('🎯 [FB-RESULTS] Filtrage par campagne:', selectedCampaignFilter.name);
      }

      // Ajouter le filtre plateforme si une plateforme est sélectionnée
      if (selectedPlatformFilter) {
        baseRequestBody.platformFilter = selectedPlatformFilter;
        console.log('🎯 [FB-RESULTS] Filtrage par plateforme:', selectedPlatformFilter);
      }

      // Ajouter le filtre par labels si des labels sont sélectionnés
      if (currentFilteredCampaignIds && currentFilteredCampaignIds.length > 0) {
        baseRequestBody.labelCampaignIds = currentFilteredCampaignIds;
        console.log('🎯 [FB-RESULTS] Filtrage par labels:', currentFilteredCampaignIds.length, 'campagnes');
      }

      console.log('⚡ [RESULTS] Requête vers:', `${API_URL}/api/dashboard/facebook-ads/timeline`);
      console.log('⚡ [RESULTS] Payload:', baseRequestBody);

      /**
       * Les bornes de la période regardée ; le serveur en dérive la précédente.
       *
       * ⚠️ Même défaut que chez Microsoft Ads : la table était recopiée ici et sérialisée
       * par `toISOString()` sur des dates à minuit **local**, qui tombe la veille en UTC.
       */
      const plageRegardee = selectedPeriod === 'CUSTOM' && customStartDate && customEndDate
        ? { startDate: customStartDate, endDate: customEndDate }
        : getDateRangeFromPeriod(selectedPeriod);

      const previousPeriodBody = {
        accountId: selectedAccount?.id,
        startDate: plageRegardee.startDate,
        endDate: plageRegardee.endDate,
        campaignFilter: selectedCampaignFilter,
        comparisonType: comparisonType || 'previous_period',
        comparisonStartDate: comparisonStartDate,
        comparisonEndDate: comparisonEndDate
      };

      // Charger les données: timeline + période précédente (si comparaison activée)
      const fetchPromises = [
        fetch(`${API_URL}/api/dashboard/facebook-ads/timeline`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(baseRequestBody)
        })
      ];

      // Toujours ajouter la requête de période précédente pour le module Résultats
      if (previousPeriodBody) {
        fetchPromises.push(
          fetch(`${API_URL}/api/dashboard/facebook-ads/previous-period`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify(previousPeriodBody)
          })
        );
      }

      const responses = await Promise.all(fetchPromises);
      const timelineResponse = responses[0];
      const previousPeriodResponse = responses[1]; // undefined si comparaison désactivée

      if (!timelineResponse.ok) {
        const errorText = await timelineResponse.text();
        console.error('❌ [RESULTS] Erreur HTTP:', timelineResponse.status, errorText);

        // Vérifier si c'est une erreur d'authentification expirée ou permission refusée
        if (timelineResponse.status === 401) {
          try {
            const errorData = JSON.parse(errorText);
            if ((errorData.error === 'auth_expired' || errorData.error === 'permission_denied') && setAuthError) {
              console.log(`🔑 [FB-RESULTS] ${errorData.error === 'permission_denied' ? 'Permission refusée' : 'Token expiré'} détecté dans loadResultsSection`);
              setAuthError({
                platform: 'FACEBOOK_ADS',
                message: errorData.message,
                accountName: errorData.accountName,
                accountId: errorData.accountId,
                requiresReauth: errorData.requiresReauth,
                permissionDenied: errorData.permissionDenied,
                userRole: errorData.userRole
              });
              return; // Ne pas afficher l'erreur brute
            }
          } catch (parseError) {
            // Si le parsing échoue, continuer avec l'erreur normale
          }
        }

        throw new Error(`Erreur HTTP: ${timelineResponse.status} - ${errorText}`);
      }

      const data = await timelineResponse.json();
      setResultsData(data);

      // Toujours traiter la période précédente pour le module Résultats
      if (previousPeriodResponse) {
        const previousPeriodResult = await previousPeriodResponse.json();
        console.log('📘 [FB-RESULTS] Réponse période précédente:', previousPeriodResult);
        if (previousPeriodResult?.success && previousPeriodResult?.data?.hasData) {
          console.log('✅ Données période précédente Facebook Ads intégrées');
          setPreviousPeriodData(previousPeriodResult.data);
        } else {
          console.log('⚠️ Période précédente Facebook Ads non disponible');
          setPreviousPeriodData(null);
        }
      } else {
        setPreviousPeriodData(null);
      }

    } catch (error) {
      console.error('❌ [RESULTS] Erreur chargement section Résultats:', error);
      // Ne pas afficher l'erreur si c'est une erreur d'auth déjà gérée
      if (!error.message?.includes('auth_expired')) {
        setError(`Erreur lors du chargement des résultats: ${error.message}`);
      }
    } finally {
      setLoadingResults(false);
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, granularity, selectedClientId, selectedCampaignFilter, selectedPlatformFilter, token, compareEnabled, comparisonType]);

  // Charger les données de la section Campagnes Facebook Ads
  const loadCampaignsSection = async () => {
    if (!selectedAccount) {
      return;
    }
    
    try {
      setLoadingCampaigns(true);
      console.log('📊 [CAMPAIGNS] Chargement prioritaire section Campagnes Facebook Ads...');
      
      const baseRequestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod,
        model: selectedDashboardModel
      };
      
      // Ajouter les dates personnalisées si période CUSTOM
      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) return;
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }
      
      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        baseRequestBody.clientId = selectedClientId;
        console.log('🎯 [FB-CAMPAIGNS] Filtrage par client:', selectedClientId);
      }

      // Ajouter le filtre plateforme si une plateforme est sélectionnée
      if (selectedPlatformFilter) {
        baseRequestBody.platformFilter = selectedPlatformFilter;
        console.log('🎯 [FB-CAMPAIGNS] Filtrage par plateforme:', selectedPlatformFilter);
      }

      console.log('📊 [CAMPAIGNS] Requête vers:', `${API_URL}/api/dashboard/facebook-ads`);
      console.log('📊 [CAMPAIGNS] Payload:', {...baseRequestBody, model: 'global'});
      
      const response = await fetch(`${API_URL}/api/dashboard/facebook-ads`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(baseRequestBody)
      });
      
      console.log('📊 [CAMPAIGNS] Statut réponse:', response.status, response.statusText);
      
      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [CAMPAIGNS] Erreur HTTP:', response.status, errorText);

        // Vérifier si c'est une erreur d'authentification expirée ou permission refusée
        if (response.status === 401) {
          try {
            const errorData = JSON.parse(errorText);
            if ((errorData.error === 'auth_expired' || errorData.error === 'permission_denied') && setAuthError) {
              console.log(`🔑 [FB-CAMPAIGNS] ${errorData.error === 'permission_denied' ? 'Permission refusée' : 'Token expiré'} détecté dans loadCampaignsSection`);
              setAuthError({
                platform: 'FACEBOOK_ADS',
                message: errorData.message,
                accountName: errorData.accountName,
                accountId: errorData.accountId,
                requiresReauth: errorData.requiresReauth,
                permissionDenied: errorData.permissionDenied,
                userRole: errorData.userRole
              });
              return; // Ne pas afficher l'erreur brute
            }
          } catch (parseError) {
            // Si le parsing échoue, continuer avec l'erreur normale
          }
        }

        throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
      }

      const facebookAdsResult = await response.json();
      console.log('📊 [CAMPAIGNS] Données reçues:', facebookAdsResult);
      
      if (facebookAdsResult.success) {
        // Utiliser les mêmes données que le composant Résultats
        // Les campagnes avec métriques sont dans rawData[sourceId].campaigns ou dans une structure simulée
        const rawData = facebookAdsResult.rawData || {};
        const sourceKeys = Object.keys(rawData);
        
        console.log('📊 [CAMPAIGNS] Sources disponibles:', sourceKeys);
        console.log('📊 [CAMPAIGNS] Structure rawData:', rawData);
        console.log('📊 [CAMPAIGNS] facebookAdsResult keys:', Object.keys(facebookAdsResult));
        
        let allCampaigns = [];
        let totals = {};
        
        if (sourceKeys.length > 0) {
          // Récupérer les données de la première source
          const sourceData = rawData[sourceKeys[0]];
          console.log('📊 [CAMPAIGNS] Source data structure:', Object.keys(sourceData || {}));
          
          // Utiliser les campagnes avec métriques si disponibles
          if (sourceData && sourceData.campaigns) {
            allCampaigns = sourceData.campaigns;
          } else if (facebookAdsResult.campaigns) {
            // Fallback sur les campagnes de base
            allCampaigns = facebookAdsResult.campaigns;
          }
          
          // Utiliser les totaux du summary s'ils sont disponibles
          if (sourceData && sourceData.summary) {
            totals = {
              impressions: sourceData.summary.impressions || 0,
              clicks: sourceData.summary.clicks || 0,
              cost: sourceData.summary.spend || 0,
              conversions: sourceData.summary.conversions || 0,
              conversionsValue: sourceData.summary.conversionValue || 0,
              /**
               * Couverture et répétition reprises **telles quelles**, sans `|| 0`.
               *
               * Le serveur les a demandées à Meta pour la période entière : ce sont les seules
               * valeurs dédupliquées qui existent. `null` veut dire « Meta n'a pas pu les
               * fournir » — un zéro se lirait comme une absence de diffusion.
               */
              reach: sourceData.summary.reach ?? null,
              frequency: sourceData.summary.frequency ?? null
            };
          }
        }
        
        console.log('📊 [CAMPAIGNS] Campaigns avec métriques:', allCampaigns.length, allCampaigns.slice(0, 2));
        console.log('📊 [CAMPAIGNS] Totals depuis summary:', totals);
        console.log('📊 [CAMPAIGNS] Sample campaign structure:', allCampaigns[0]);
        
        const campaignsData = {
          success: true,
          data: [{
            campaigns: allCampaigns,
            totals: totals
          }]
        };
        
        console.log('📊 [CAMPAIGNS] Final campaignsData being set:', campaignsData);
        setCampaignsData(campaignsData);

        // Récupérer les données de la période précédente si comparaison activée
        if (compareEnabled && comparisonType) {
          try {

            // Déterminer les dates de la période actuelle
            let currentStart = customStartDate;
            let currentEnd = customEndDate;
            if (!currentStart || !currentEnd) {
              const dateRange = getDateRangeFromPeriod(selectedPeriod);
              currentStart = dateRange.startDate;
              currentEnd = dateRange.endDate;
            }

            // Les bornes de comparaison, calculées une fois pour toute l'application :
            // `utils/periodUtils` est seul à savoir ce que « la période précédente » veut dire
            // un jour de changement d'heure.
            const { startDate: compStartDate, endDate: compEndDate } =
              calculatePreviousPeriod(currentStart, currentEnd, comparisonType);

            console.log('📊 [CAMPAIGNS-COMPARE] Récupération période précédente:', { compStartDate, compEndDate });

            const prevRequestBody = {
              ...baseRequestBody,
              period: 'CUSTOM',
              customStartDate: compStartDate,
              customEndDate: compEndDate
            };

            const prevResponse = await fetch(`${API_URL}/api/dashboard/facebook-ads`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(prevRequestBody)
            });

            if (prevResponse.ok) {
              const prevResult = await prevResponse.json();
              if (prevResult.success) {
                const prevRawData = prevResult.rawData || {};
                const prevSourceKeys = Object.keys(prevRawData);
                let prevCampaigns = [];

                if (prevSourceKeys.length > 0) {
                  const prevSourceData = prevRawData[prevSourceKeys[0]];
                  if (prevSourceData && prevSourceData.campaigns) {
                    prevCampaigns = prevSourceData.campaigns;
                  } else if (prevResult.campaigns) {
                    prevCampaigns = prevResult.campaigns;
                  }
                }

                const prevCampaignsData = {
                  success: true,
                  data: [{ campaigns: prevCampaigns }]
                };

                console.log('📊 [CAMPAIGNS-COMPARE] Données précédentes récupérées:', prevCampaigns.length);
                setPreviousCampaignsData(prevCampaignsData);
              }
            }
          } catch (compError) {
            console.error('📊 [CAMPAIGNS-COMPARE] Erreur récupération période précédente:', compError);
          }
        } else {
          setPreviousCampaignsData(null);
        }

        // Sync campagnes Facebook Ads en arrière-plan pour labels/objectifs
        if (allCampaigns.length > 0) {
          console.log('🔄 [FB-SYNC] selectedAccount:', selectedAccount);
          console.log('🔄 [FB-SYNC] sourceId:', selectedAccount.sourceId, 'id:', selectedAccount.id);
          console.log('🔄 [FB-SYNC] Exemple campagne FB:', allCampaigns[0]);
          // Attendre que le sync soit terminé avant de charger les labels
          await syncCampaignsInBackground(token, selectedAccount.sourceId || selectedAccount.id, 'FACEBOOK_ADS', allCampaigns);
          // Recharger les labels après le sync
          console.log('🏷️ [FB-SYNC] Rechargement des labels après sync...');
          loadLabelsForAccount();
        }
      }

    } catch (error) {
      console.error('❌ [CAMPAIGNS] Erreur chargement section Campagnes:', error);
      // Ne pas afficher l'erreur si c'est une erreur d'auth déjà gérée
      if (!error.message?.includes('auth_expired')) {
        setError(`Erreur lors du chargement des campagnes: ${error.message}`);
      }
    } finally {
      setLoadingCampaigns(false);
    }
  };

  // Générer le dashboard quand les paramètres changent
  const generateDashboard = async () => {
    if (!selectedAccount) return;

    setLoadingCharts(true);
    setLoadingProgress(0);
    setAutoCharts(null);
    setAuthError(null); // Réinitialiser les erreurs d'authentification lors du changement de compte

    try {
      console.log('📘 Génération dashboard Facebook Ads pour:', selectedAccount?.name || 'compte', 'Période:', selectedPeriod);
      
      const requestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod,
        customStartDate: selectedPeriod === 'CUSTOM' ? customStartDate : null,
        customEndDate: selectedPeriod === 'CUSTOM' ? customEndDate : null,
        model: selectedDashboardModel
      };
      
      // Ajouter le filtre client si un client est sélectionné
      if (selectedClientId) {
        requestBody.clientId = selectedClientId;
        console.log('🎯 [FB-DASHBOARD] Filtrage par client:', selectedClientId);
      }

      // Ajouter le filtre plateforme si une plateforme est sélectionnée
      if (selectedPlatformFilter) {
        requestBody.platformFilter = selectedPlatformFilter;
        console.log('🎯 [FB-DASHBOARD] Filtrage par plateforme:', selectedPlatformFilter);
      }

      console.log('📘 Payload Facebook Ads:', requestBody);
      
      const response = await fetch(`${API_URL}/api/dashboard/facebook-ads`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(requestBody)
      });
      
      const data = await response.json();
      console.log('📘 Réponse dashboard Facebook Ads:', data);
      console.log('📘 Structure des charts:', data.charts);
      console.log('📘 Premier chart:', data.charts?.[0]);

      // Gérer l'erreur d'authentification expirée
      if (response.status === 401 && data.error === 'auth_expired') {
        console.log('🔑 [FB-AUTH] Token Facebook expiré détecté');
        if (setAuthError) {
          setAuthError({
            message: data.message,
            accountName: data.accountName,
            accountId: data.accountId,
            requiresReauth: data.requiresReauth,
            userRole: data.userRole,
            platform: 'FACEBOOK_ADS'
          });
        }
        return;
      }

      if (data.success) {
        setAutoCharts(data);
        
        // Mise à jour synchronisée de la section Campagnes avec les mêmes données
        console.log('📊 [SYNC-CAMPAIGNS] Mise à jour campagnes depuis generateDashboard...');
        const facebookAdsResult = data;
        
        if (facebookAdsResult && facebookAdsResult.rawData && Object.keys(facebookAdsResult.rawData).length > 0) {
          // Extraire et traiter les données de campagnes comme dans loadCampaignsSection
          const allCampaigns = [];
          let totals = { impressions: 0, clicks: 0, cost: 0, conversions: 0, conversionsValue: 0 };
          
          let comptesAvecCouverture = 0;
          Object.values(facebookAdsResult.rawData).forEach(source => {
            if (source && source.campaigns) {
              const sourceCampaigns = source.campaigns.map(campaign => ({
                ...campaign,
                // Calculs pour l'affichage
                cpa: campaign.conversions > 0 ? (campaign.cost / campaign.conversions) : 0,
                ctr: campaign.impressions > 0 ? ((campaign.clicks / campaign.impressions) * 100) : 0,
                cos: campaign.conversionsValue > 0 ? ((campaign.cost / campaign.conversionsValue) * 100) : 0,
                roas: campaign.cost > 0 ? (campaign.conversionsValue / campaign.cost) : 0
              }));
              
              allCampaigns.push(...sourceCampaigns);
              
              // Calculer les totaux depuis summary si disponible
              if (source.summary) {
                totals.impressions += source.summary.impressions || 0;
                totals.clicks += source.summary.clicks || 0;
                totals.cost += source.summary.spend || 0;
                totals.conversions += source.summary.conversions || 0;
                totals.conversionsValue += source.summary.conversionsValue || 0;

                /**
                 * Couverture et répétition : la **première** source seulement, et rien au-delà.
                 *
                 * Meta ne sait pas déduplier une couverture **entre deux comptes publicitaires**
                 * — l'API ne l'expose pas. Dès qu'une seconde source apporte la sienne, aucun
                 * total juste n'existe, et on le dit en remettant les deux à `null` plutôt qu'en
                 * additionnant.
                 */
                if (comptesAvecCouverture === 0) {
                  totals.reach = source.summary.reach ?? null;
                  totals.frequency = source.summary.frequency ?? null;
                } else {
                  totals.reach = null;
                  totals.frequency = null;
                }
                comptesAvecCouverture++;
              }
            }
          });
          
          console.log('📊 [SYNC-CAMPAIGNS] Campagnes extraites:', allCampaigns.length);
          console.log('📊 [SYNC-CAMPAIGNS] Totaux calculés:', totals);

          const campaignsData = {
            success: true,
            data: [{
              campaigns: allCampaigns,
              totals: totals
            }]
          };

          setCampaignsData(campaignsData);

          // Récupérer les données de la période précédente si comparaison activée
          if (compareEnabled && comparisonType && allCampaigns.length > 0) {
            try {

              // Déterminer les dates de la période actuelle
              let currentStart = customStartDate;
              let currentEnd = customEndDate;
              if (!currentStart || !currentEnd) {
                const dateRange = getDateRangeFromPeriod(selectedPeriod);
                currentStart = dateRange.startDate;
                currentEnd = dateRange.endDate;
              }

              // Les bornes de comparaison, calculées une fois pour toute l'application :
              // `utils/periodUtils` est seul à savoir ce que « la période précédente » veut dire
              // un jour de changement d'heure.
              const { startDate: compStartDate, endDate: compEndDate } =
                calculatePreviousPeriod(currentStart, currentEnd, comparisonType);

              console.log('📊 [CAMPAIGNS-COMPARE-GEN] Récupération période précédente:', { compStartDate, compEndDate });

              const prevRequestBody = {
                sources: [selectedAccount?.id],
                period: 'CUSTOM',
                customStartDate: compStartDate,
                customEndDate: compEndDate,
                model: selectedDashboardModel
              };

              if (selectedClientId) {
                prevRequestBody.clientId = selectedClientId;
              }

              if (selectedPlatformFilter) {
                prevRequestBody.platformFilter = selectedPlatformFilter;
              }

              const prevResponse = await fetch(`${API_URL}/api/dashboard/facebook-ads`, {
                method: 'POST',
                headers: {
                  'Authorization': `Bearer ${token}`,
                  'Content-Type': 'application/json'
                },
                body: JSON.stringify(prevRequestBody)
              });

              if (prevResponse.ok) {
                const prevResult = await prevResponse.json();
                if (prevResult.success && prevResult.rawData) {
                  const prevCampaigns = [];
                  Object.values(prevResult.rawData).forEach(source => {
                    if (source && source.campaigns) {
                      prevCampaigns.push(...source.campaigns);
                    }
                  });

                  const prevCampaignsData = {
                    success: true,
                    data: [{ campaigns: prevCampaigns }]
                  };

                  console.log('📊 [CAMPAIGNS-COMPARE-GEN] Données précédentes récupérées:', prevCampaigns.length);
                  setPreviousCampaignsData(prevCampaignsData);
                }
              }
            } catch (compError) {
              console.error('📊 [CAMPAIGNS-COMPARE-GEN] Erreur récupération période précédente:', compError);
            }
          } else {
            setPreviousCampaignsData(null);
          }

          // Sync campagnes Facebook Ads en arrière-plan pour labels/objectifs
          if (allCampaigns.length > 0) {
            console.log('🔄 [FB-SYNC-GEN] selectedAccount:', selectedAccount);
            console.log('🔄 [FB-SYNC-GEN] sourceId:', selectedAccount?.sourceId, 'id:', selectedAccount?.id);
            // Attendre que le sync soit terminé avant de charger les labels
            await syncCampaignsInBackground(token, selectedAccount?.sourceId || selectedAccount?.id, 'FACEBOOK_ADS', allCampaigns);
            // Recharger les labels après le sync
            console.log('🏷️ [FB-SYNC-GEN] Rechargement des labels après sync...');
            loadLabelsForAccount();
          }
        }
      } else {
        setError(`Erreur: ${data.error}`);
      }
    } catch (error) {
      console.error('Erreur génération dashboard Facebook Ads:', error);
      setError('Erreur lors de la génération du dashboard');
    } finally {
      setLoadingCharts(false);
      setLoadingProgress(0);
    }
  };

  // Générer automatiquement le dashboard quand le compte, la période, les dates personnalisées ou la comparaison changent
  React.useEffect(() => {
    if (selectedAccount && selectedSources.length > 0 && selectedDashboardModel === 'global') {
      // Pour période CUSTOM, attendre que les deux dates soient définies
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }

      generateDashboard();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedDashboardModel, selectedPlatformFilter, compareEnabled, comparisonType]);

  // Charger automatiquement la section Résultats (avec debounce pour les filtres)
  const resultsDebounceRef = React.useRef(null);
  React.useEffect(() => {
    if (selectedAccount && selectedDashboardModel === 'global') {
      // Pour période CUSTOM, attendre que les deux dates soient définies
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }

      // Annuler le timeout précédent
      if (resultsDebounceRef.current) {
        clearTimeout(resultsDebounceRef.current);
      }

      // Debounce de 300ms pour les changements de filtres
      resultsDebounceRef.current = setTimeout(() => {
        loadResultsSection(filteredCampaignIds);
      }, 300);
    }

    return () => {
      if (resultsDebounceRef.current) {
        clearTimeout(resultsDebounceRef.current);
      }
    };
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedDashboardModel, granularity, selectedCampaignFilter, selectedPlatformFilter, filteredCampaignIds, selectedLabelFilters, loadResultsSection, compareEnabled, comparisonType]);

  // Note: La section Campagnes est maintenant chargée directement dans generateDashboard() 
  // pour éviter les doublons d'appels API et assurer la synchronisation des périodes

  // Tracker la présélection pour permettre la réinitialisation
  const hadPreselectedAccountRef = React.useRef(false);
  
  // Tracker quand on reçoit un preselectedAccountId
  React.useEffect(() => {
    if (preselectedAccountId !== null) {
      hadPreselectedAccountRef.current = true;
    }
  }, [preselectedAccountId]);
  
  // Réinitialiser l'état seulement si on avait un preselectedAccountId et qu'il a été nettoyé
  React.useEffect(() => {
    if (preselectedAccountId === null && hadPreselectedAccountRef.current && selectedAccount) {
      console.log('🔄 FacebookAds - Réinitialisation état après nettoyage preselectedAccountId (retour de Synthèse)');
      setSelectedAccount(null);
      setCurrentActiveAccount(null); // Réinitialiser le compte actif global
      setAutoCharts(null);
      setAiInsights(null);
      setResultsData(null);
      setCampaignsData(null);
      hadPreselectedAccountRef.current = false; // Reset du flag
    }
  }, [preselectedAccountId, selectedAccount]);

  // Note: Le gestionnaire accountChange a été supprimé car le useEffect sur preselectedAccountId
  // gère maintenant les changements de compte depuis la sidebar de manière plus fiable

  // Gestionnaire pour la génération automatique depuis la sidebar
  React.useEffect(() => {
    const handleAutoGenerate = (event) => {
      const { platform } = event.detail;
      if (platform !== 'facebook-ads') return;

      console.log('🚀 FacebookAds - Clic sur plateforme depuis sidebar');

      // Attendre que le filtrage par client soit terminé
      setTimeout(() => {
        // Utiliser la ref pour avoir la valeur la plus récente de libraryItems
        const currentFilteredItems = libraryItemsRef.current;

        console.log('🔍 FacebookAds - Comptes disponibles après filtrage:', currentFilteredItems.length);

        // Si pas de compte sélectionné
        if (!selectedAccount && currentFilteredItems.length > 0) {
          // Si un seul compte disponible, le sélectionner et générer automatiquement
          if (currentFilteredItems.length === 1) {
            const singleAccount = currentFilteredItems[0];
            console.log('📌 FacebookAds - Un seul compte disponible, sélection auto:', singleAccount.name);
            setSelectedAccount(singleAccount);
            setCurrentActiveAccount(singleAccount.id);
            setSelectedSources([singleAccount.id]);
            const accountSite = userSites.find(s => s.accountIds?.includes(singleAccount.id) || s.accountIds?.includes(singleAccount.sourceId));
            window.currentNotesContext = {
              platform: 'FACEBOOK_ADS',
              accountId: singleAccount.sourceId || singleAccount.id,
              accountName: singleAccount.name,
              siteId: accountSite?._id || accountSite?.id || null,
              clientId: accountSite?.clientId || null
            };
            // Déclencher la génération
            setTimeout(() => {
              if (window.facebookDashboardGenerateAuto) {
                window.facebookDashboardGenerateAuto();
              }
            }, 200);
          } else {
            // Plusieurs comptes disponibles, ne pas sélectionner automatiquement
            console.log('📋 FacebookAds - Plusieurs comptes disponibles, attente de sélection manuelle');
          }
        } else if (selectedAccount) {
          // Un compte est déjà sélectionné, générer le dashboard
          setTimeout(() => {
            if (window.facebookDashboardGenerateAuto) {
              window.facebookDashboardGenerateAuto();
            }
          }, 200);
        }
      }, 400); // Délai pour permettre au filtrage asynchrone de s'exécuter et à la ref d'être mise à jour
    };

    window.addEventListener('dashboardAutoGenerate', handleAutoGenerate);
    return () => {
      window.removeEventListener('dashboardAutoGenerate', handleAutoGenerate);
    };
  }, [selectedAccount]);

  // Gestionnaire pour la sélection de compte depuis la sidebar (Facebook Ads)
  React.useEffect(() => {
    const handleSidebarAccountChange = (event) => {
      const { accountId, accountName } = event.detail;
      console.log('🔄 FacebookAds - Changement de compte depuis sidebar:', accountId, accountName);

      if (!accountId) {
        // "Tous les comptes" sélectionné
        setSelectedAccount(null);
        setCurrentActiveAccount(null);
        setSelectedSources([]);
        setAutoCharts(null);
        return;
      }

      // Chercher le compte dans la liste
      const targetAccount = libraryItemsRef.current.find(item =>
        item.id === accountId || item.sourceId === accountId
      );

      if (targetAccount) {
        setSelectedAccount(targetAccount);
        setCurrentActiveAccount(targetAccount.id);
        setSelectedSources([targetAccount.id]);
        const targetSite = userSites.find(s => s.accountIds?.includes(targetAccount.id) || s.accountIds?.includes(targetAccount.sourceId));
        window.currentNotesContext = {
          platform: 'FACEBOOK_ADS',
          accountId: targetAccount.sourceId || targetAccount.id,
          accountName: targetAccount.name,
          siteId: targetSite?._id || targetSite?.id || null,
          clientId: targetSite?.clientId || null
        };
        // Générer automatiquement le dashboard
        setTimeout(() => {
          if (window.facebookDashboardGenerateAuto) {
            window.facebookDashboardGenerateAuto();
          }
          // Mettre à jour l'URL après que activeTab soit synchronisé
          window.updateUrlWithAccount?.(targetAccount.id);
        }, 200);
      }
    };

    window.addEventListener('sidebarAccountChange', handleSidebarAccountChange);
    return () => {
      window.removeEventListener('sidebarAccountChange', handleSidebarAccountChange);
    };
  }, []);

  // Gestion de la présélection depuis la sidebar ou Synthèse
  React.useEffect(() => {
    const shouldChangeAccount = preselectedAccountId &&
      libraryItems.length > 0 &&
      (!selectedAccount || selectedAccount.id !== preselectedAccountId);

    if (shouldChangeAccount) {
      console.log('🎯 FacebookAds - Changement de compte vers:', preselectedAccountId);
      const preselectedItem = libraryItems.find(item => item.id === preselectedAccountId);
      if (preselectedItem) {
        console.log('✅ FacebookAds - Compte trouvé:', preselectedItem.name);
        setSelectedAccount(preselectedItem);
        setCurrentActiveAccount(preselectedItem.id);
        setSelectedSources([preselectedItem.id]);
        setPreselectionUsed(true);
        window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: preselectedItem.id, accountName: preselectedItem.name } }));
        // Mettre à jour l'URL avec le compte sélectionné (pour persistance au refresh)
        window.updateUrlWithAccount?.(preselectedItem.id);
        // Générer automatiquement le dashboard après sélection
        generateDashboard();
      } else {
        console.log('⚠️ FacebookAds - Compte non trouvé dans la liste, réinitialisation');
        // Réinitialiser car le compte demandé n'existe pas (changement de client)
        setSelectedAccount(null);
        setCurrentActiveAccount(null);
        setSelectedSources([]);
        setAutoCharts(null);
        setPreselectionUsed(false);
      }
    } else if (!preselectedAccountId && selectedAccount && libraryItems.length > 1) {
      // Ne pas réinitialiser si un seul compte (cas d'auto-sélection)
      console.log('🔄 FacebookAds - Réinitialisation (Tous les comptes)');
      setSelectedAccount(null);
      setCurrentActiveAccount(null);
      setSelectedSources([]);
      setAutoCharts(null);
      setPreselectionUsed(false);
    }
  }, [preselectedAccountId, libraryItems]);


  /**
   * Accès Meta perdu : le bandeau de tête porte la nouvelle, les sections s'effacent.
   *
   * Chaque section interroge sa propre route. Quand l'accès au compte est refusé, elles
   * échouent toutes les neuf — et affichaient neuf fois le message brut du serveur sous leur
   * titre (« Internal server error while retrieving platform data »), c'est-à-dire neuf
   * sections qui paraissent en panne alors que la cause est unique, déjà annoncée en haut de
   * l'écran, avec le seul geste possible : gérer sa bibliothèque ou se reconnecter.
   *
   * `accesMeta.js` tenait déjà ce raisonnement, mais au niveau de chaque table et à partir du
   * **texte** de son erreur — ce qui suppose que la route nomme le défaut d'accès. Les routes
   * de section n'en font rien : elles rendent une 500 générique dès que l'erreur de Meta ne
   * porte pas le mot `PERMISSION_DENIED`. C'était donc une liste de messages à deviner.
   *
   * Ici, la cause est lue à la source : `authError`, posé par le chargement du dashboard, qui
   * est aussi ce qui fait apparaître le bandeau. Les deux ne peuvent plus se contredire.
   *
   * ⚠️ `authError` est remis à `null` au début de chaque chargement de compte
   * (`generateDashboard`, chargement des résultats) : changer de compte ne traîne pas l'erreur
   * du précédent. Inutile donc de comparer l'identifiant — et le faire serait fragile, la
   * forme `act_…` n'étant pas garantie des deux côtés.
   *
   * La connexion expirée est logée à la même enseigne que la permission refusée : dans les deux
   * cas, rien à lire et un seul geste, en tête d'écran. C'est déjà ce que fait `accesMeta.js`.
   */
  const accesMetaRefuse = Boolean(authError && (authError.platform || 'GOOGLE_ADS') === 'FACEBOOK_ADS');

  return (
    <div className="view-content">
      {/* Sélection des comptes Facebook Ads */}
      <div className="dashboard-sources">
        {(() => {
        if (loadingLibrary) {
          return (
            <div className="loading-state">
              <Loader className="spin" size={16} />
              <span>Chargement des comptes Meta Ads...</span>
            </div>
          );
        }
        
        if (libraryItems.length === 0) {
          // Vérifier si le connecteur Facebook Ads est activé
          const hasFacebookAdsConnector = globalConnectors?.some(c => c.provider === 'FACEBOOK_ADS') || false;
          
          if (!hasFacebookAdsConnector) {
            // Cas 1: Connecteur pas activé
            return (
              <div className="no-sources">
                <div className="empty-state">
                  <p>Aucun connecteur Meta Ads activé</p>
                  <button 
                    onClick={handleConnectFacebookAds}
                    className="btn btn-primary"
                    style={{
                      fontSize: '16px',
                      padding: '12px 24px'
                    }}
                  >
                    + Connecter
                  </button>
                </div>
              </div>
            );
          } else {
            // Cas 2: Connecteur activé mais pas de comptes
            return (
              <div className="no-sources">
                <div className="empty-state">
                  <p>Aucun compte Meta Ads connecté</p>
                  <button 
                    onClick={() => setShowAddModal(true)}
                    className="btn btn-primary"
                    style={{
                      fontSize: '16px',
                      padding: '12px 24px'
                    }}
                  >
                    + Ajouter un compte
                  </button>
                </div>
              </div>
            );
          }
        }

        // Plusieurs comptes disponibles mais aucun sélectionné
        if (libraryItems.length > 1 && !selectedAccount) {
          return (
            <div className="account-selection-prompt">
              <div className="account-selection-card">
                <div className="account-selection-icon">
                  <img src={metaAdsLogo} alt="Meta Ads" style={{ width: 48, height: 48, objectFit: 'contain' }} />
                </div>
                <h2>Sélectionnez un compte Meta Ads</h2>
                <p className="account-selection-description">
                  {libraryItems.length} comptes disponibles
                </p>
                <select
                  className="account-selection-dropdown"
                  value=""
                  onChange={(e) => {
                    if (e.target.value) {
                      const account = libraryItems.find(item => item.id === e.target.value);
                      if (account) {
                        setSelectedAccount(account);
                        setCurrentActiveAccount(account.id);
                        setSelectedSources([account.id]);
                        window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: account.id, accountName: account.name } }));
                        // Générer automatiquement le dashboard et mettre à jour l'URL
                        setTimeout(() => {
                          generateDashboard();
                          window.updateUrlWithAccount?.(account.id);
                        }, 100);
                      }
                    }
                  }}
                >
                  <option value="">Choisir un compte...</option>
                  {[...libraryItems].sort((a, b) => a.name.localeCompare(b.name)).map(account => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          );
        }

        // Les contrôles (compte, période, dates) sont dans la sidebar
        return null;
      })()}
      </div>

      {/* Écran de chargement */}
      {loadingCharts && (
        <DashboardLoadingScreen
          platformName="Meta Ads"
          features={[
            { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
            { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
            { icon: <img src={metaAdsLogo} alt="Meta Ads" style={{width: '28px', height: '28px', objectFit: 'contain'}} />, label: 'Analyse des campagnes' }
          ]}
        />
      )}

      {/* Section Insights IA pour Dashboard Global */}
      {/*
        L'analyse en panneau latéral, à côté du dashboard — le même composant et la même route
        que les dashboards Google Ads et Microsoft Ads.
      */}
      <ChatPanel
        isOpen={panneauAnalyseOuvert}
        onClose={() => setPanneauAnalyseOuvert(false)}
        token={token}
        accountId={selectedAccount?.sourceId || selectedAccount?.id || ''}
        accountName={selectedAccount?.name || ''}
        analysisResult={null}
        dashboardStream={construireChargeAnalyse}
        sansOverlay
      />

      {selectedDashboardModel === 'global' && aiInsights && (
        <div className="ai-insights-section">
          <h3>🤖 Analyse Intelligence Artificielle</h3>
          <div className="insights-content">
            <div dangerouslySetInnerHTML={{ __html: aiInsights.replace(/\n/g, '<br>') }}></div>
          </div>
        </div>
      )}

      {/* Conteneur de filtres Facebook Ads */}
      {!loadingCharts && selectedDashboardModel === 'global' && campaignsData?.data && campaignsData.data[0]?.campaigns && campaignsData.data[0].campaigns.length > 0 && (
        <div style={{
          position: 'sticky',
          top: '0',
          zIndex: '1000',
          background: '#FAF9F5',
          borderRadius: '8px',
          padding: '16px',
          marginTop: '-5px',
          marginBottom: '10px'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', justifyContent: 'space-between', width: '100%' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <span style={{
                fontWeight: '500',
                color: '#374151',
                fontSize: '0.95em',
                display: 'flex',
                alignItems: 'center'
              }}>
                <img src={metaAdsLogo} alt="Meta Ads" style={{width: '24px', height: '24px', marginRight: '8px', objectFit: 'contain'}} /> Filtrer:
              </span>
              <div style={{ position: 'relative', display: 'inline-block' }}>
                <input
                  type="text"
                  placeholder="Rechercher une campagne..."
                  value={selectedCampaignFilter ? (selectedCampaignFilter.name || selectedCampaignFilter.campaignName) : fbCampaignSearchQuery}
                  onChange={(e) => {
                    if (selectedCampaignFilter) {
                      setSelectedCampaignFilter(null);
                    }
                    setFbCampaignSearchQuery(e.target.value);
                    setShowCampaignAutocomplete(true);
                  }}
                  onFocus={() => setShowCampaignAutocomplete(true)}
                  onBlur={() => {
                    // Délai pour permettre le clic sur les options
                    setTimeout(() => setShowCampaignAutocomplete(false), 200);
                  }}
                  style={{
                    padding: '4px 12px',
                    paddingRight: (fbCampaignSearchQuery || selectedCampaignFilter) ? '28px' : '12px',
                    border: '1px solid #d1d5db',
                    borderRadius: '6px',
                    backgroundColor: selectedCampaignFilter ? '#dbeafe' : 'white',
                    color: '#374151',
                    fontSize: '0.9em',
                    width: '400px',
                    height: '35px',
                    boxSizing: 'border-box',
                    outline: 'none'
                  }}
                />
                {(fbCampaignSearchQuery || selectedCampaignFilter) && (
                  <button
                    onClick={() => {
                      setFbCampaignSearchQuery('');
                      setSelectedCampaignFilter(null);
                      setShowCampaignAutocomplete(false);
                    }}
                    style={{
                      position: 'absolute',
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      color: selectedCampaignFilter ? '#1a1a1a' : '#9ca3af',
                      padding: '2px',
                      fontSize: '14px',
                      lineHeight: 1,
                      zIndex: 1
                    }}
                    title={selectedCampaignFilter ? "Effacer le filtre campagne" : "Effacer la recherche"}
                  >
                    ✕
                  </button>
                )}
                {/* Autocomplete dropdown */}
                {showCampaignAutocomplete && (() => {
                  const searchLower = fbCampaignSearchQuery.toLowerCase().trim();
                  // D'abord filtrer par labels si des labels sont sélectionnés
                  let baseCampaigns = campaignsData.data[0].campaigns;
                  if (selectedLabelFilters.length > 0 && Object.keys(campaignsWithLabels).length > 0) {
                    baseCampaigns = baseCampaigns.filter(c => {
                      const campaignId = (c.id || c.campaignId || '').toString();
                      const campaignLabels = campaignsWithLabels[campaignId] || [];
                      return selectedLabelFilters.some(label => campaignLabels.includes(label));
                    });
                  }
                  // Ensuite filtrer par recherche textuelle
                  const filteredCampaigns = searchLower
                    ? baseCampaigns
                        .filter(c => (c.name || '').toLowerCase().includes(searchLower))
                        .sort((a, b) => (a.name || '').localeCompare(b.name || ''))
                    : [...baseCampaigns].sort((a, b) => (a.name || '').localeCompare(b.name || ''));

                  if (filteredCampaigns.length === 0) {
                    return (
                      <div style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        right: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                        zIndex: 1001,
                        padding: '12px',
                        color: '#6b7280',
                        fontSize: '0.9em'
                      }}>
                        Aucune campagne trouvée
                      </div>
                    );
                  }

                  return (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      backgroundColor: 'white',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      marginTop: '4px',
                      boxShadow: '0 4px 6px rgba(0,0,0,0.1)',
                      zIndex: 1001,
                      maxHeight: '300px',
                      overflowY: 'auto'
                    }}>
                      <div style={{
                        padding: '8px 12px',
                        fontSize: '0.8em',
                        color: '#6b7280',
                        borderBottom: '1px solid #e5e7eb',
                        backgroundColor: '#f9fafb'
                      }}>
                        {searchLower
                          ? `${filteredCampaigns.length} campagne(s) trouvée(s)`
                          : `Toutes les campagnes (${filteredCampaigns.length})`}
                      </div>
                      {filteredCampaigns.map((campaign, index) => (
                        <div
                          key={campaign.id || index}
                          onClick={() => {
                            setSelectedCampaignFilter(campaign);
                            setFbCampaignSearchQuery('');
                            setShowCampaignAutocomplete(false);
                          }}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                            color: '#374151',
                            borderBottom: index < filteredCampaigns.length - 1 ? '1px solid #f3f4f6' : 'none',
                            transition: 'background-color 0.15s'
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#f3f4f6'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = 'transparent'}
                        >
                          {campaign.name}
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>
              
              {/* Liste déroulante multi-sélection pour les labels */}
              {(() => {
                // Si une campagne est sélectionnée, ne montrer que ses labels
                let fbLabelsToDisplay = availableLabels;
                if (selectedCampaignFilter && Object.keys(campaignsWithLabels).length > 0) {
                  const campaignId = (selectedCampaignFilter.id || selectedCampaignFilter.campaignId || '').toString();
                  const campaignLabels = campaignsWithLabels[campaignId] || [];
                  // Si la campagne n'a pas de labels, masquer le dropdown
                  if (campaignLabels.length === 0) return null;
                  // Sinon, ne montrer que les labels de cette campagne
                  fbLabelsToDisplay = campaignLabels.filter(l => availableLabels.includes(l));
                }
                if (fbLabelsToDisplay.length === 0) return null;

                return (
                <div ref={labelDropdownRef} style={{ position: 'relative', marginLeft: '16px' }}>
                  <button
                    onClick={() => setShowLabelDropdown(!showLabelDropdown)}
                    style={{
                      padding: '4px 12px',
                      border: '1px solid #d1d5db',
                      borderRadius: '6px',
                      backgroundColor: selectedLabelFilters.length > 0 ? '#dbeafe' : 'white',
                      color: '#374151',
                      fontSize: '0.9em',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      minWidth: '140px',
                      height: '35px',
                      boxSizing: 'border-box'
                    }}
                  >
                    <span><Tag size={14} />&nbsp;Labels</span>
                    {selectedLabelFilters.length > 0 && (
                      <span style={{
                        backgroundColor: '#1a1a1a',
                        color: 'white',
                        borderRadius: '10px',
                        padding: '2px 8px',
                        fontSize: '0.8em',
                        fontWeight: '600'
                      }}>
                        {selectedLabelFilters.length}
                      </span>
                    )}
                    <span style={{ marginLeft: 'auto' }}>{showLabelDropdown ? '▲' : '▼'}</span>
                  </button>

                  {showLabelDropdown && (
                    <div
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: 0,
                        backgroundColor: 'white',
                        border: '1px solid #d1d5db',
                        borderRadius: '6px',
                        marginTop: '4px',
                        boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
                        zIndex: 9999,
                        minWidth: '200px',
                        maxHeight: '250px',
                        overflowY: 'auto'
                      }}
                    >
                      {/* Bouton tout effacer */}
                      {selectedLabelFilters.length > 0 && (
                        <div
                          onClick={() => {
                            setSelectedLabelFilters([]);
                          }}
                          style={{
                            padding: '8px 12px',
                            cursor: 'pointer',
                            fontSize: '0.85em',
                            color: '#ef4444',
                            borderBottom: '1px solid #e5e7eb',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                          }}
                        >
                          ✕ Tout effacer
                        </div>
                      )}

                      {/* Liste des labels avec checkboxes */}
                      {fbLabelsToDisplay.map(label => (
                        <div
                          key={label}
                          onClick={() => toggleLabelFilter(label)}
                          style={{
                            padding: '10px 12px',
                            cursor: 'pointer',
                            fontSize: '0.9em',
                            color: '#374151',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '10px',
                            transition: 'background-color 0.15s',
                            backgroundColor: selectedLabelFilters.includes(label) ? '#f0f9ff' : 'transparent'
                          }}
                          onMouseEnter={(e) => {
                            if (!selectedLabelFilters.includes(label)) {
                              e.target.style.backgroundColor = '#f3f4f6';
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.target.style.backgroundColor = selectedLabelFilters.includes(label) ? '#f0f9ff' : 'transparent';
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={selectedLabelFilters.includes(label)}
                            onChange={() => {}}
                            style={{
                              width: '16px',
                              height: '16px',
                              cursor: 'pointer',
                              accentColor: '#1a1a1a'
                            }}
                          />
                          <span>{label}</span>
                        </div>
                      ))}

                      {/* Bouton Valider */}
                      <div
                        onClick={() => setShowLabelDropdown(false)}
                        style={{
                          padding: '10px 12px',
                          cursor: 'pointer',
                          fontSize: '0.9em',
                          color: 'white',
                          backgroundColor: '#1a1a1a',
                          borderTop: '1px solid #e5e7eb',
                          textAlign: 'center',
                          fontWeight: '500',
                          marginTop: '4px'
                        }}
                        onMouseEnter={(e) => e.target.style.backgroundColor = '#333333'}
                        onMouseLeave={(e) => e.target.style.backgroundColor = '#1a1a1a'}
                      >
                        ✓ Valider
                      </div>
                    </div>
                  )}
                </div>
                );
              })()}

              {/* Indicateur filtre plateforme actif */}
              {selectedPlatformFilter && (
                <>
                  <span style={{ color: '#6b7280', fontSize: '0.9em', marginLeft: '12px' }}>|</span>
                  <span style={{
                    fontWeight: '500',
                    color: '#374151',
                    fontSize: '0.9em',
                    display: 'flex',
                    alignItems: 'center'
                  }}>
                    Plateforme: <strong style={{ marginLeft: '4px' }}>
                      {selectedPlatformFilter === 'facebook' ? 'Facebook' :
                       selectedPlatformFilter === 'instagram' ? 'Instagram' :
                       selectedPlatformFilter === 'audience_network' ? 'Audience Network' :
                       selectedPlatformFilter === 'messenger' ? 'Messenger' : selectedPlatformFilter}
                    </strong>
                  </span>
                  <button
                    onClick={() => setSelectedPlatformFilter(null)}
                    style={{
                      padding: '6px 10px',
                      backgroundColor: '#ef4444',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      fontSize: '0.85em',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '4px'
                    }}
                    title="Supprimer le filtre plateforme"
                  >
                    ✕ Effacer
                  </button>
                </>
              )}

            </div>

            {/*
              * Bouton Analyse IA — le même qu'aux dashboards Google et Microsoft Ads.
              *
              * Meta exposait sa fonction d'analyse sans que rien ne l'affiche : `showAIButton`
              * n'était passé qu'à `DashboardGoogleAds`, où vivait le bouton.
              */}
            {autoCharts?.charts?.length > 0 && canAccessAnalyzer && !panneauAnalyseOuvert && (
              <button
                onClick={handleAIAnalysis}
                className="nav-btn"
                style={{
                  padding: '8px 16px',
                  gap: '10px',
                  fontSize: '0.9em',
                  fontWeight: '500',
                  whiteSpace: 'nowrap'
                }}
                title="Analyse IA du dashboard Meta Ads"
              >
                <Sparkles size={16} color="#7c2d12" />
                Analyse IA
              </button>
            )}

            {/* Lien Modules masqués - à droite */}
            {userRole !== 'CLIENT' && clientHiddenModules.length > 0 && (
              <div
                className="hidden-modules-link"
                onClick={() => setIsHiddenModulesModalOpen(true)}
                title="Voir les modules masqués pour le client"
              >
                <EyeOff size={16} />
                <span>Modules masqués</span>
                <span className="hidden-count">{clientHiddenModules.length}</span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal des modules masqués */}
      <HiddenModulesModal
        isOpen={isHiddenModulesModalOpen}
        onClose={() => setIsHiddenModulesModalOpen(false)}
        hiddenModules={clientHiddenModules}
        onRestoreModule={(moduleId) => setClientModuleVisibility(moduleId, true)}
      />

      {/* Section Résultats - Composant modulaire avec chargement prioritaire */}
      {!loadingCharts && selectedAccount && (() => {
        // Utiliser UNIQUEMENT resultsData du nouvel endpoint timeline
        const chartSource = resultsData;
        console.log('📈 [RESULTS] Données timeline:', resultsData?.charts?.length || 0, 'charts');

        // Afficher le chargement si on attend encore les données (première fois)
        if (loadingResults && !chartSource) {
          return (
            <div className="performance-chart-section">
              <div className="section-header">
                <h4><TrendingUp size={16} />&nbsp;Résultats</h4>
              </div>
              <div className="loading-placeholder" style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                height: '200px',
                backgroundColor: '#FAF9F5',
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                color: '#6b7280'
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <Loader size={18} className="spin" style={{ color: '#6b7280' }} />
                  Chargement des résultats...
                </div>
              </div>
            </div>
          );
        }

        // Ne pas afficher la section si aucun compte n'est sélectionné
        if (!selectedAccount) {
          return null;
        }

        // Ne pas afficher de message si erreur d'auth (la bannière suffit)
        if (accesMetaRefuse) {
          return null;
        }

        // Afficher un message informatif s'il n'y a pas de données
        if (!chartSource || !chartSource.charts || chartSource.charts.length === 0) {
          console.log('📈 [RESULTS] Pas de données - message informatif affiché');
          return <NoDataMessage />;
        }
        
        console.log('📘 [FB-RESULTS] Props PerformanceChart:', {
          compareEnabled,
          comparisonType,
          hasPreviousPeriodData: !!previousPeriodData,
          previousPeriodDataKeys: previousPeriodData ? Object.keys(previousPeriodData) : []
        });
        return (
            <PerformanceChart
              autoCharts={chartSource}
              formatNumber={formatNumber}
              KPICard={KPICard}
              previousPeriodData={previousPeriodData}
              selectedCampaignFilter={selectedCampaignFilter}
              selectedCampaignTypeFilter={null}
              selectedAccount={selectedAccount}
              granularity={granularity}
              onGranularityChange={setGranularity}
              compareEnabled={compareEnabled}
              comparisonType={comparisonType}
              selectedPeriod={selectedPeriod}
              customStartDate={customStartDate}
              customEndDate={customEndDate}
              labelCampaignIds={filteredCampaignIds}
              notes={dashboardNotes}
              userRole={userRole}
              isClientModuleVisible={isClientModuleVisible}
              toggleClientModuleVisibility={toggleClientModuleVisibility}
              moduleId="fbads_results"
              conversionConfig={configConversionsMeta}
            token={token}
            />
        );
      })()}

      {/*
        Le total dédupliqué du compte, pour les tableaux qui ne peuvent pas le calculer.

        Dans un tableau de ventilation, la couverture d'une ligne est juste — Meta la déduplique
        dans son segment — mais leur somme ne l'est pas : une personne vue sur Facebook et sur
        Instagram figure dans les deux lignes. Le seul total qui existe est celui du compte, que
        Meta calcule pour la période entière, et c'est celui-ci.

        Il n'est passé que si **rien ne restreint le périmètre**. Un filtre de campagne, de
        plateforme ou de label rend le tableau plus étroit que le compte, et le total ne lui
        correspondrait plus : les tableaux affichent alors un tiret.
      */}

      {/* Section PlatformTable - Affichage des performances par plateforme */}
      {!accesMetaRefuse && !loadingCharts && !loadingResults && selectedAccount && selectedDashboardModel === 'global' &&
       resultsData && resultsData.charts && resultsData.charts.length > 0 && (
        <PlatformTable
          token={token}
          selectedAccount={selectedAccount?.id}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          isVisible={showFacebookPlatform}
          onToggleVisibility={() => setShowFacebookPlatform(!showFacebookPlatform)}
          formatNumber={formatNumber}
          selectedAccountProp={selectedAccount}
          selectedCampaignFilter={selectedCampaignFilter}
          selectedPlatformFilter={selectedPlatformFilter}
          setSelectedPlatformFilter={setSelectedPlatformFilter}
          labelCampaignIds={filteredCampaignIds}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_platforms"
          conversionConfig={configConversionsMeta}
          totalCouverture={totalCouvertureCompte}
          totalRepetition={totalRepetitionCompte}
        />
      )}

      {/* Section Campagnes - identique au dashboard Google Ads */}
      {!accesMetaRefuse && !loadingCharts && selectedDashboardModel === 'global' && campaignsData?.data && (
        <CampaignsTable
          rawData={{ account: campaignsData.data[0] || { campaigns: [] } }}
          previousRawData={previousCampaignsData ? { account: previousCampaignsData.data[0] || { campaigns: [] } } : null}
          isVisible={showCampaignDetails}
          onToggleVisibility={() => setShowCampaignDetails(!showCampaignDetails)}
          formatNumber={formatNumber}
          selectedCampaignFilter={selectedCampaignFilter}
          setSelectedCampaignFilter={setSelectedCampaignFilter}
          selectedAccount={selectedAccount}
          filteredCampaignIds={filteredCampaignIds}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_campaigns"
          totalCouverture={totalCouvertureCompte}
          totalRepetition={totalRepetitionCompte}
          campagnesAccountId={selectedAccount?.sourceId || selectedAccount?.id}
          campagnesAccountType="FACEBOOK_ADS"
          campagnesToken={token}
          conversionConfig={configConversionsMeta}
        />
      )}

      {/* Section AdSets Table - Affichage des ensembles de publicités */}
      {/* Note: AdSetTable gère son propre état de chargement indépendamment,
          on ne doit pas le conditionner à resultsData pour éviter le démontage lors des changements de période */}
      {!accesMetaRefuse && !loadingCharts && selectedAccount && selectedDashboardModel === 'global' && (
        <AdSetTable
          token={token}
          selectedAccount={selectedAccount?.id}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          isVisible={showFacebookAdSets}
          onToggleVisibility={() => setShowFacebookAdSets(!showFacebookAdSets)}
          formatNumber={formatNumber}
          selectedAccountProp={selectedAccount}
          selectedCampaignFilter={selectedCampaignFilter}
          selectedPlatformFilter={selectedPlatformFilter}
          labelCampaignIds={filteredCampaignIds}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_adsets"
          conversionConfig={configConversionsMeta}
          totalCouverture={totalCouvertureCompte}
          totalRepetition={totalRepetitionCompte}
        />
      )}

      {/* Section Annonces — l'échelon sous les ensembles, et le seul tableau qui porte un
          visuel : c'est au niveau annonce que se juge la création. */}
      {!accesMetaRefuse && !loadingCharts && selectedAccount && selectedDashboardModel === 'global' && (
        <AdsTable
          token={token}
          selectedAccount={selectedAccount?.id}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          isVisible={showFacebookAds}
          onToggleVisibility={() => setShowFacebookAds(!showFacebookAds)}
          formatNumber={formatNumber}
          selectedAccountProp={selectedAccount}
          selectedCampaignFilter={selectedCampaignFilter}
          selectedPlatformFilter={selectedPlatformFilter}
          labelCampaignIds={filteredCampaignIds}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_ads"
          conversionConfig={configConversionsMeta}
          totalCouverture={totalCouvertureCompte}
          totalRepetition={totalRepetitionCompte}
        />
      )}

      {/* Section Engagement Details - Détail des Interactions et Réactions */}
      {!accesMetaRefuse && !loadingCharts && selectedAccount && selectedDashboardModel === 'global' && (
        <EngagementDetailsTable
          token={token}
          selectedAccount={selectedAccount?.id}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          isVisible={showEngagementDetails}
          onToggleVisibility={() => setShowEngagementDetails(!showEngagementDetails)}
          formatNumber={formatNumber}
          selectedCampaignFilter={selectedCampaignFilter}
          labelCampaignIds={filteredCampaignIds}
          selectedClientId={selectedClientId}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_engagement"
        />
      )}

      {/* Loading Campagnes */}
      {!accesMetaRefuse && !loadingCharts && loadingCampaigns && selectedDashboardModel === 'global' && (
        <div className="campaigns-table-section">
          <div className="section-header">
            <h4><BarChart2 size={16} />&nbsp;Campagnes</h4>
          </div>
          <div className="loading-placeholder" style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            height: '200px',
            backgroundColor: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: '8px',
            color: '#6c757d'
          }}>
            <div>
              <div className="spinner-border" role="status" style={{marginRight: '10px'}}>
                <span className="visually-hidden">Chargement...</span>
              </div>
              📊 Chargement des campagnes...
            </div>
          </div>
        </div>
      )}

      {/* Section Device Table - Affichage des performances par appareil */}
      {!accesMetaRefuse && !loadingCharts && !loadingResults && selectedAccount && selectedDashboardModel === 'global' && 
       resultsData && resultsData.charts && resultsData.charts.length > 0 && (
        <DeviceTable
          token={token}
          selectedAccount={selectedAccount?.id}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          isVisible={showFacebookDevices}
          onToggleVisibility={() => setShowFacebookDevices(!showFacebookDevices)}
          formatNumber={formatNumber}
          selectedAccountProp={selectedAccount}
          selectedCampaignFilter={selectedCampaignFilter}
          selectedPlatformFilter={selectedPlatformFilter}
          labelCampaignIds={filteredCampaignIds}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_devices"
          conversionConfig={configConversionsMeta}
          totalCouverture={totalCouvertureCompte}
          totalRepetition={totalRepetitionCompte}
        />
      )}

      {/* Section WeekdaysTable - Affichage des performances par jours de la semaine */}
      {!accesMetaRefuse && !loadingCharts && !loadingResults && selectedAccount && selectedDashboardModel === 'global' &&
       resultsData && resultsData.charts && resultsData.charts.length > 0 && (
        <FacebookWeekdaysTable
          token={token}
          selectedAccount={selectedAccount?.id}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          isVisible={showFacebookWeekdays}
          onToggleVisibility={() => setShowFacebookWeekdays(!showFacebookWeekdays)}
          formatNumber={formatNumber}
          selectedAccountProp={selectedAccount}
          selectedCampaignFilter={selectedCampaignFilter}
          selectedPlatformFilter={selectedPlatformFilter}
          labelCampaignIds={filteredCampaignIds}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_weekdays"
          conversionConfig={configConversionsMeta}
        />
      )}

      {/* Section AgeTable - Affichage des performances par tranche d'âge */}
      {!accesMetaRefuse && !loadingCharts && !loadingResults && selectedAccount && selectedDashboardModel === 'global' &&
       resultsData && resultsData.charts && resultsData.charts.length > 0 && (
        <AgeTable
          token={token}
          selectedAccount={selectedAccount?.id}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          isVisible={showFacebookAge}
          onToggleVisibility={() => setShowFacebookAge(!showFacebookAge)}
          formatNumber={formatNumber}
          selectedAccountProp={selectedAccount}
          selectedCampaignFilter={selectedCampaignFilter}
          selectedPlatformFilter={selectedPlatformFilter}
          labelCampaignIds={filteredCampaignIds}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_age"
          conversionConfig={configConversionsMeta}
          totalCouverture={totalCouvertureCompte}
          totalRepetition={totalRepetitionCompte}
        />
      )}

      {/* Section Gender Table - Affichage des performances par genre */}
      {!accesMetaRefuse && !loadingCharts && !loadingResults && selectedAccount && selectedDashboardModel === 'global' &&
       resultsData && resultsData.charts && resultsData.charts.length > 0 && (
        <GenderTable
          token={token}
          selectedAccount={selectedAccount?.id}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          isVisible={showFacebookGender}
          onToggleVisibility={() => setShowFacebookGender(!showFacebookGender)}
          formatNumber={formatNumber}
          selectedAccountProp={selectedAccount}
          selectedCampaignFilter={selectedCampaignFilter}
          setSelectedCampaignFilter={setSelectedCampaignFilter}
          selectedPlatformFilter={selectedPlatformFilter}
          labelCampaignIds={filteredCampaignIds}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_gender"
          conversionConfig={configConversionsMeta}
          totalCouverture={totalCouvertureCompte}
          totalRepetition={totalRepetitionCompte}
        />
      )}

      {/* Section Placement Table - Affichage des performances par placement */}
      {!accesMetaRefuse && !loadingCharts && !loadingResults && selectedAccount && selectedDashboardModel === 'global' &&
       resultsData && resultsData.charts && resultsData.charts.length > 0 && (
        <PlacementTable
          token={token}
          selectedAccount={selectedAccount?.id}
          period={selectedPeriod}
          customStartDate={customStartDate}
          customEndDate={customEndDate}
          isVisible={showFacebookPlacements}
          onToggleVisibility={() => setShowFacebookPlacements(!showFacebookPlacements)}
          formatNumber={formatNumber}
          selectedAccountProp={selectedAccount}
          selectedCampaignFilter={selectedCampaignFilter}
          setSelectedCampaignFilter={setSelectedCampaignFilter}
          selectedPlatformFilter={selectedPlatformFilter}
          labelCampaignIds={filteredCampaignIds}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="fbads_placements"
          conversionConfig={configConversionsMeta}
          totalCouverture={totalCouvertureCompte}
          totalRepetition={totalRepetitionCompte}
        />
      )}
      
      {/* Message si pas de données - masqué si erreur d'auth */}
      {!loadingCharts && !autoCharts && selectedAccount && !accesMetaRefuse && (
        <div className="empty-state">
          <p>Sélectionnez un compte et une période pour voir le dashboard</p>
        </div>
      )}

      {/* Modal d'ajout de données Facebook Ads */}
      {showAddModal && (
        <AddDataModal 
          token={token}
          onClose={() => setShowAddModal(false)}
          onAdded={async () => {
            // Recharger les comptes après ajout
            setLoadingLibrary(true);
            try {
              const response = await fetch(`${API_URL}/api/library`, {
                method: 'GET',
                headers: {
                  'Authorization': `Bearer ${token}`
                }
              });
              const data = await response.json();
              if (data.success && data.library) {
                const facebookAdsItems = data.library.FACEBOOK_ADS || [];
                
                // Déduplication basée sur l'ID unique
                const uniqueFacebookAdsItems = facebookAdsItems.filter((item, index, self) => 
                  index === self.findIndex((t) => t.id === item.id)
                );
                
                setAllLibraryItems(uniqueFacebookAdsItems); // Garder une copie des comptes non filtrés
                setLibraryItems(uniqueFacebookAdsItems);
              }
            } catch (error) {
              console.error('Erreur chargement bibliothèque:', error);
            } finally {
              setLoadingLibrary(false);
            }
            setShowAddModal(false);
          }}
          setSuccess={() => {}}
          setError={setError}
          initialStep="facebook-ads"
          connectors={globalConnectors}
        />
      )}
      
    </div>
  );
}

// ===== COMPOSANT DASHBOARD PINTEREST ADS =====
function DashboardPinterestAds({ token, setError, selectedPeriod, setSelectedPeriod, customStartDate, setCustomStartDate, customEndDate, setCustomEndDate, selectedDashboardModel, setSelectedDashboardModel, preselectedAccountId, activeTab, preselectionUsed, setPreselectionUsed, globalConnectors, canAccessAnalyzer, selectedClientId, updateUrlWithAccount, authError, setAuthError, handleApiError, handleReconnect, dashboardNotes, fetchDashboardNotes, selectedSiteId, userSites, compareEnabled = false, comparisonType = '', userRole = 'USER', comparisonStartDate = '', comparisonEndDate = '' }) {
  const [selectedSources, setSelectedSources] = useState([]);
  const [autoCharts, setAutoCharts] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [libraryItems, setLibraryItems] = useState([]);
  const [allLibraryItems, setAllLibraryItems] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [granularity, setGranularity] = useState('day');
  const [previousPeriodData, setPreviousPeriodData] = useState(null);

  // Calculer le clientId effectif
  const effectiveClientId = React.useMemo(() => {
    if (selectedClientId) return selectedClientId;
    if (!selectedAccount) return null;
    const accountId = selectedAccount.id || selectedAccount.sourceId;
    const accountSite = userSites.find(s =>
      s.accountIds?.includes(selectedAccount.id) ||
      s.accountIds?.includes(selectedAccount.sourceId)
    );
    return accountSite?.clientId || (accountId ? `pntrst_${accountId}` : null);
  }, [selectedClientId, selectedAccount, userSites]);

  // Hook pour la visibilité des modules par client
  const {
    isModuleVisible: isClientModuleVisible,
    toggleModuleVisibility: toggleClientModuleVisibility,
    setModuleVisibility: setClientModuleVisibility,
    hiddenModules: clientHiddenModules
  } = useClientModuleVisibility(effectiveClientId, userRole);

  const [isHiddenModulesModalOpen, setIsHiddenModulesModalOpen] = useState(false);

  // Ref pour garder la valeur la plus récente de libraryItems
  const libraryItemsRef = React.useRef(libraryItems);
  React.useEffect(() => {
    libraryItemsRef.current = libraryItems;
  }, [libraryItems]);

  // Tracker la présélection
  const hadPreselectedAccountRef = React.useRef(false);

  // Réinitialiser les données quand l'onglet change
  React.useEffect(() => {
    if (activeTab === 'pinterest-ads') {
      setResultsData(null);
      setLoadingResults(false);
    }
  }, [activeTab]);

  // Charger les comptes Pinterest Ads de la bibliothèque
  React.useEffect(() => {
    const loadLibraryItems = async () => {
      setLoadingLibrary(true);
      try {
        const response = await fetch(`${API_URL}/api/library`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success && data.library) {
          const pinterestAdsItems = data.library.PINTEREST_ADS || [];
          console.log(`📌 ${pinterestAdsItems.length} comptes Pinterest Ads trouvés`);

          const uniquePinterestAdsItems = pinterestAdsItems.filter((item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
          );
          console.log(`📌 Après déduplication: ${uniquePinterestAdsItems.length} comptes uniques`);

          setAllLibraryItems(uniquePinterestAdsItems);
          setLibraryItems(uniquePinterestAdsItems);
        }
      } catch (error) {
        console.error('Erreur chargement bibliothèque:', error);
      } finally {
        setLoadingLibrary(false);
      }
    };

    loadLibraryItems();
  }, [token]);

  // Filtrer les comptes par client ou site
  const filterAccountsByClient = async (accounts, clientId, siteId) => {
    if (siteId && siteId !== 'all' && accounts.length) {
      const site = userSites.find(s => (s._id || s.id) === siteId);
      if (site && site.accountIds) {
        const allowedAccountIds = ensembleAutorise(site.accountIds);
        const filteredAccounts = accounts.filter(account => compteAutorise(account, allowedAccountIds));
        return filteredAccounts;
      }
    }

    if (!clientId || !accounts.length) {
      return accounts;
    }

    try {
      const response = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { clientId }
      });

      const sites = response.data.sites || [];
      const allowedAccountIds = ensembleAutorise(
        sites.flatMap(site => (Array.isArray(site.accountIds) ? site.accountIds : []))
      );

      return accounts.filter(account => compteAutorise(account, allowedAccountIds));
    } catch (error) {
      console.error('Erreur filtrage comptes par client:', error);
      return accounts;
    }
  };

  // Refs pour tracker le site et client précédents
  const pnPrevSiteIdRef = React.useRef(selectedSiteId);
  const pnPrevClientIdRef = React.useRef(selectedClientId);

  // Recharger la liste des comptes quand le site/client change
  React.useEffect(() => {
    const siteChanged = pnPrevSiteIdRef.current !== selectedSiteId;
    const clientChanged = pnPrevClientIdRef.current !== selectedClientId;
    pnPrevSiteIdRef.current = selectedSiteId;
    pnPrevClientIdRef.current = selectedClientId;

    const filterAndSelectAccount = async () => {
      if (siteChanged || clientChanged) {
        console.log('🔄 [PINTEREST ADS] Changement de site/client détecté');
        hadPreselectedAccountRef.current = false;
        setAutoCharts(null);
        setResultsData(null);
        setError('');
        setSelectedAccount(null);
        setSelectedSources([]);
      }

      if (allLibraryItems.length > 0) {
        const filteredItems = await filterAccountsByClient(allLibraryItems, selectedClientId, selectedSiteId);
        setLibraryItems(filteredItems);

        // Auto-sélectionner si un seul compte
        const preselectedExistsInList = preselectedAccountId && filteredItems.some(item => item.id === preselectedAccountId);
        if (filteredItems.length === 1 && !preselectedExistsInList) {
          const account = filteredItems[0];
          console.log('🎯 [PINTEREST ADS] Auto-sélection du seul compte:', account.name);
          setSelectedAccount(account);
          setSelectedSources([account.id]);
          const accountSite = userSites.find(s => s.accountIds?.includes(account.id) || s.accountIds?.includes(account.sourceId));
          window.currentNotesContext = {
            platform: 'PINTEREST_ADS',
            accountId: account.sourceId || account.id,
            accountName: account.name,
            siteId: accountSite?._id || accountSite?.id || null,
            clientId: accountSite?.clientId || null
          };
          window.dispatchEvent(new CustomEvent('accountSelected', {
            detail: { accountId: account.id, accountName: account.name }
          }));
        }
      }
    };

    filterAndSelectAccount();
  }, [selectedClientId, selectedSiteId, allLibraryItems]);

  // Fonction pour connecter Pinterest Ads
  const handleConnectPinterestAds = () => {
    window.location.href = `${API_URL}/api/auth/pinterest-ads?token=${token}&returnTo=connectors`;
  };


  // Charger les données de la section Résultats
  const loadResultsSection = React.useCallback(async () => {
    if (!selectedAccount) return;

    setAuthError && setAuthError(null);

    try {
      setLoadingResults(true);
      console.log('📌 [RESULTS] Chargement section Résultats Pinterest Ads...');

      const baseRequestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod,
        granularity: granularity
      };

      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) return;
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }

      if (selectedClientId) {
        baseRequestBody.clientId = selectedClientId;
      }

      console.log('📌 [RESULTS] Requête vers:', `${API_URL}/api/dashboard/pinterest-ads/timeline`);

      const response = await fetch(`${API_URL}/api/dashboard/pinterest-ads/timeline`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(baseRequestBody)
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('❌ [RESULTS] Erreur HTTP:', response.status, errorText);

        if (response.status === 401) {
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.error === 'auth_expired' && setAuthError) {
              setAuthError({
                platform: 'PINTEREST_ADS',
                message: errorData.message,
                accountName: errorData.accountName,
                accountId: errorData.accountId,
                requiresReauth: errorData.requiresReauth,
                userRole: errorData.userRole
              });
              return;
            }
          } catch (parseError) {}
        }

        throw new Error(`Erreur HTTP: ${response.status} - ${errorText}`);
      }

      const data = await response.json();
      setResultsData(data);

    } catch (error) {
      console.error('❌ [RESULTS] Erreur chargement section Résultats:', error);
      if (!error.message?.includes('auth_expired')) {
        setError(`Erreur lors du chargement des résultats: ${error.message}`);
      }
    } finally {
      setLoadingResults(false);
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, granularity, selectedClientId, token]);

  // Charger automatiquement la section Résultats
  React.useEffect(() => {
    if (selectedAccount && selectedDashboardModel === 'global') {
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      loadResultsSection();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedDashboardModel, granularity, loadResultsSection]);

  // Charger les notes du dashboard
  useEffect(() => {
    if (!selectedAccount || !fetchDashboardNotes) return;
    const accountId = selectedAccount?.sourceId || selectedAccount?.id;
    fetchDashboardNotes('PINTEREST_ADS', accountId, customStartDate, customEndDate);
  }, [selectedAccount, customStartDate, customEndDate, fetchDashboardNotes]);

  // Gestion de la présélection
  React.useEffect(() => {
    if (preselectedAccountId !== null) {
      hadPreselectedAccountRef.current = true;
    }
  }, [preselectedAccountId]);

  React.useEffect(() => {
    // « Voir » depuis l'accueil transmet le `sourceId` du compte — l'identifiant de la régie —
    // et non l'identifiant interne du `LibraryItem` : chercher sur le seul `id` ne trouvait
    // rien, et le dashboard s'ouvrait sur son sélecteur alors qu'un compte avait été demandé.
    // Awin et ChatGPT Ads acceptaient déjà les deux formes.
    const preselectedItem = preselectedAccountId && libraryItems.length > 0
      ? libraryItems.find(item => item.id === preselectedAccountId || item.sourceId === preselectedAccountId)
      : null;

    if (preselectedItem && (!selectedAccount || selectedAccount.id !== preselectedItem.id)) {
      setSelectedAccount(preselectedItem);
      setSelectedSources([preselectedItem.id]);
      setPreselectionUsed(true);
      window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: preselectedItem.id, accountName: preselectedItem.name } }));
    }
  }, [preselectedAccountId, libraryItems]);

  // Gestionnaire pour la génération automatique depuis la sidebar
  React.useEffect(() => {
    const handleAutoGenerate = (event) => {
      const { platform } = event.detail;
      if (platform !== 'pinterest-ads') return;

      console.log('🚀 PinterestAds - Clic sur plateforme depuis sidebar');

      setTimeout(() => {
        const currentFilteredItems = libraryItemsRef.current;

        if (!selectedAccount && currentFilteredItems.length > 0) {
          if (currentFilteredItems.length === 1) {
            const singleAccount = currentFilteredItems[0];
            setSelectedAccount(singleAccount);
            setSelectedSources([singleAccount.id]);
            const accountSite = userSites.find(s => s.accountIds?.includes(singleAccount.id) || s.accountIds?.includes(singleAccount.sourceId));
            window.currentNotesContext = {
              platform: 'PINTEREST_ADS',
              accountId: singleAccount.sourceId || singleAccount.id,
              accountName: singleAccount.name,
              siteId: accountSite?._id || accountSite?.id || null,
              clientId: accountSite?.clientId || null
            };
          }
        }
      }, 400);
    };

    window.addEventListener('dashboardAutoGenerate', handleAutoGenerate);
    return () => {
      window.removeEventListener('dashboardAutoGenerate', handleAutoGenerate);
    };
  }, [selectedAccount]);

  // Gestionnaire pour la sélection de compte depuis la sidebar (Pinterest Ads)
  React.useEffect(() => {
    const handleSidebarAccountChange = (event) => {
      const { accountId, accountName } = event.detail;
      console.log('🔄 PinterestAds - Changement de compte depuis sidebar:', accountId, accountName);

      if (!accountId) {
        // "Tous les comptes" sélectionné
        setSelectedAccount(null);
        setSelectedSources([]);
        setAutoCharts(null);
        setResultsData(null);
        return;
      }

      // Chercher le compte dans la liste
      const targetAccount = libraryItemsRef.current.find(item =>
        item.id === accountId || item.sourceId === accountId
      );

      if (targetAccount) {
        setSelectedAccount(targetAccount);
        setSelectedSources([targetAccount.id]);
        const targetSite = userSites.find(s => s.accountIds?.includes(targetAccount.id) || s.accountIds?.includes(targetAccount.sourceId));
        window.currentNotesContext = {
          platform: 'PINTEREST_ADS',
          accountId: targetAccount.sourceId || targetAccount.id,
          accountName: targetAccount.name,
          siteId: targetSite?._id || targetSite?.id || null,
          clientId: targetSite?.clientId || null
        };
        window.dispatchEvent(new CustomEvent('accountSelected', {
          detail: { accountId: targetAccount.id, accountName: targetAccount.name }
        }));
        // Mettre à jour l'URL après que activeTab soit synchronisé
        setTimeout(() => {
          window.updateUrlWithAccount?.(targetAccount.id);
        }, 200);
      }
    };

    window.addEventListener('sidebarAccountChange', handleSidebarAccountChange);
    return () => {
      window.removeEventListener('sidebarAccountChange', handleSidebarAccountChange);
    };
  }, []);

  /**
   * Ce qu'on envoie à l'analyse : les données **déjà affichées**.
   *
   * Appelée à l'envoi de la question et non à l'ouverture du panneau : les blocs arrivent en
   * plusieurs temps, et une charge figée trop tôt capture un écran incomplet.
   */
  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    const bruts = { 'Résultats': resultsData, 'Période précédente': previousPeriodData };
    const blocs = {};
    const absents = [];
    for (const [libelle, bloc] of Object.entries(bruts)) {
      const reduit = bloc ? lignesDuBloc(bloc) : null;
      if (reduit) blocs[libelle] = reduit;
      else absents.push(libelle);
    }

    return {
      dashboardData: {
        sources: selectedSources,
        ...periode,
        charts: autoCharts?.charts,
        composants: { blocs, absents },
        dashboardType: 'pinterest'
      },
      dashboardType: 'pinterest',
      accountId: selectedAccount?.sourceId || selectedAccount?.id || null,
      accountName: selectedAccount?.name || null,
      ...periode
    };
  };

  return (
    <div className="view-content">
      {/*
        L'analyse IA. Le composant rend le bouton *et* le panneau, celui-ci passant par un
        portail — sa place dans l'arbre n'a aucune incidence sur son placement à l'écran.
      */}
      {selectedAccount && autoCharts?.charts?.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <AnalyseIA
            token={token}
            compte={selectedAccount}
            autorise={canAccessAnalyzer}
            construireCharge={construireChargeAnalyse}
          />
        </div>
      )}

      {/* Sélection des comptes Pinterest Ads */}
      <div className="dashboard-sources">
        {(() => {
          if (loadingLibrary) {
            return (
              <div className="loading-state">
                <Loader className="spin" size={16} />
                <span>Chargement des comptes Pinterest Ads...</span>
              </div>
            );
          }

          if (libraryItems.length === 0) {
            const hasPinterestAdsConnector = globalConnectors?.some(c => c.provider === 'PINTEREST_ADS') || false;

            if (!hasPinterestAdsConnector) {
              return (
                <div className="no-sources">
                  <div className="empty-state">
                    <p>Aucun connecteur Pinterest Ads activé</p>
                    <button onClick={handleConnectPinterestAds} className="btn btn-primary" style={{ fontSize: '16px', padding: '12px 24px' }}>
                      + Connecter
                    </button>
                  </div>
                </div>
              );
            } else {
              return (
                <div className="no-sources">
                  <div className="empty-state">
                    <p>Aucun compte Pinterest Ads connecté</p>
                    <button onClick={() => setShowAddModal(true)} className="btn btn-primary" style={{ fontSize: '16px', padding: '12px 24px' }}>
                      + Ajouter un compte
                    </button>
                  </div>
                </div>
              );
            }
          }

          // Plusieurs comptes disponibles mais aucun sélectionné
          if (libraryItems.length > 1 && !selectedAccount) {
            return (
              <div className="account-selection">
                <div className="empty-state">
                  <p style={{ marginBottom: '16px', color: '#64748b' }}>Sélectionnez un compte Pinterest Ads</p>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', justifyContent: 'center' }}>
                    {libraryItems.map(item => (
                      <button
                        key={item.id}
                        onClick={() => {
                          setSelectedAccount(item);
                          setSelectedSources([item.id]);
                          const accountSite = userSites.find(s => s.accountIds?.includes(item.id) || s.accountIds?.includes(item.sourceId));
                          window.currentNotesContext = {
                            platform: 'PINTEREST_ADS',
                            accountId: item.sourceId || item.id,
                            accountName: item.name,
                            siteId: accountSite?._id || accountSite?.id || null,
                            clientId: accountSite?.clientId || null
                          };
                          window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: item.id, accountName: item.name } }));
                          // Mettre à jour l'URL avec le compte sélectionné
                          setTimeout(() => window.updateUrlWithAccount?.(item.id), 100);
                        }}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          padding: '12px 20px',
                          borderRadius: '8px',
                          border: '1px solid #e5e7eb',
                          backgroundColor: 'white',
                          cursor: 'pointer',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#E60023';
                          e.currentTarget.style.backgroundColor = '#fef2f2';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = '#e5e7eb';
                          e.currentTarget.style.backgroundColor = 'white';
                        }}
                      >
                        <img src={pinterestAdsLogo} alt="Pinterest Ads" style={{ width: '24px', height: '24px', objectFit: 'contain' }} />
                        <span style={{ fontWeight: '500', color: '#1e293b' }}>{item.name}</span>
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            );
          }

          // Un compte sélectionné - pas de header (même mise en page que Google Ads)
          return null;
        })()}
      </div>

      {/* Modal des modules masqués */}
      <HiddenModulesModal
        isOpen={isHiddenModulesModalOpen}
        onClose={() => setIsHiddenModulesModalOpen(false)}
        hiddenModules={clientHiddenModules}
        onRestoreModule={(moduleId) => setClientModuleVisibility(moduleId, true)}
      />

      {/* Section Résultats Pinterest Ads */}
      {!loadingCharts && selectedAccount && (() => {
        const chartSource = resultsData;

        /*
         * ⚠️ **Pendant un rechargement, ne rien laisser de la période précédente à l'écran.**
         * La condition était `loadingResults && !chartSource` : elle ne couvrait que le tout
         * premier chargement. Dès le second, `resultsData` portait encore la réponse précédente,
         * et l'écran affichait « Aucune donnée du 01/01 au 13/10 » à qui venait de demander
         * autre chose — d'autant plus visible que le chemin `/reports` prend une dizaine de
         * secondes.
         *
         * C'est `DashboardLoadingScreen` qui couvre l'attente, comme sur Google Ads, Meta Ads
         * et les autres régies : Pinterest était le seul dashboard à ne pas l'employer.
         */
        if (loadingResults) {
          return (
            <DashboardLoadingScreen
              platformName="Pinterest Ads"
              features={[
                { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
                { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
                { icon: <img src={pinterestAdsLogo} alt="Pinterest Ads" style={{width: '28px', height: '28px', objectFit: 'contain'}} />, label: 'Analyse des campagnes' }
              ]}
            />
          );
        }

        if (!selectedAccount) {
          return null;
        }

        if (authError && authError.platform === 'PINTEREST_ADS') {
          return null;
        }

        const fenetre = chartSource?.fenetre;
        const enDate = (iso) => iso ? new Date(iso + 'T12:00:00').toLocaleDateString('fr-FR') : '';

        /*
         * Trois états distincts, et les confondre est précisément ce qui rendait l'écran
         * trompeur : une période hors de portée n'est pas une absence de données, et un refus
         * de l'API n'en est pas une non plus.
         */
        if (!chartSource || !chartSource.charts || chartSource.charts.length === 0) {
          if (fenetre?.statut === 'hors_portee') {
            return (
              <NoDataMessage
                title="Période hors de portée"
                subtitle={`L'API Pinterest ne remonte pas au-delà de 914 jours, soit environ deux ans et demi. La période demandée (${enDate(fenetre.demandee.debut)} – ${enDate(fenetre.demandee.fin)}) est entièrement antérieure : elle n'a pas été interrogée.`}
              />
            );
          }
          const refus = chartSource?.refusApi?.[0];
          if (refus) {
            return (
              <NoDataMessage
                title="Pinterest n'a pas répondu"
                subtitle={`Les données de « ${refus.accountName} » n'ont pas pu être récupérées : ${refus.raison}`}
              />
            );
          }
          /*
           * ⚠️ Le bandeau d'écrêtage ne s'affiche qu'au-dessus des graphiques : sans données, il
           * ne s'affiche pas, et l'état vide restituait alors des bornes sorties de nulle part —
           * « du 16/07 au 13/10 » à qui avait demandé l'année en cours. L'explication doit donc
           * être répétée ici, et non supposée lue ailleurs.
           */
          if (fenetre?.statut === 'ecretee') {
            return (
              <NoDataMessage
                title="Aucune donnée sur la partie consultable"
                subtitle={`Vous avez demandé du ${enDate(fenetre.demandee.debut)} au ${enDate(fenetre.demandee.fin)}, mais l'API Pinterest ne remonte pas au-delà de 914 jours : seule la période du ${enDate(fenetre.servie.debut)} au ${enDate(fenetre.servie.fin)} a pu être interrogée, et elle ne contient aucune donnée.`}
              />
            );
          }
          return (
            <NoDataMessage
              subtitle={fenetre?.servie
                ? `Aucune donnée du ${enDate(fenetre.servie.debut)} au ${enDate(fenetre.servie.fin)} pour le compte sélectionné.`
                : undefined}
            />
          );
        }

        return (
          <>
          {/* La période servie n'est pas toujours celle demandée : nommer les deux, plutôt que
              d'étiqueter un trimestre « Année en cours ». */}
          {fenetre?.statut === 'ecretee' && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: '8px',
              padding: '10px 14px', marginBottom: '12px',
              background: '#fffbeb', border: '1px solid #fde68a',
              borderRadius: '8px', fontSize: '13px', color: '#92400e'
            }}>
              <AlertTriangle size={16} style={{ flexShrink: 0 }} />
              <span>
                Chiffres du <b>{enDate(fenetre.servie.debut)}</b> au <b>{enDate(fenetre.servie.fin)}</b>,
                et non du {enDate(fenetre.demandee.debut)} au {enDate(fenetre.demandee.fin)} :
                l'API Pinterest ne remonte pas au-delà de 914 jours.
              </span>
            </div>
          )}
          <PerformanceChart
            autoCharts={chartSource}
            formatNumber={formatNumber}
            KPICard={KPICard}
            previousPeriodData={previousPeriodData}
            selectedCampaignFilter={null}
            selectedCampaignTypeFilter={null}
            selectedAccount={selectedAccount}
            granularity={granularity}
            onGranularityChange={setGranularity}
            compareEnabled={compareEnabled}
            comparisonType={comparisonType}
            selectedPeriod={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            labelCampaignIds={null}
            notes={dashboardNotes}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="pntrst_results"
          />
          </>
        );
      })()}

      {/* Message si pas de données */}
      {!loadingCharts && !loadingResults && !autoCharts && !resultsData && selectedAccount && !(authError && authError.platform === 'PINTEREST_ADS') && (
        <div className="empty-state">
          <p>Sélectionnez une période pour voir le dashboard</p>
        </div>
      )}

      {/* Modal d'ajout de données Pinterest Ads */}
      {showAddModal && (
        <AddDataModal
          token={token}
          onClose={() => setShowAddModal(false)}
          onAdded={async () => {
            setLoadingLibrary(true);
            try {
              const response = await fetch(`${API_URL}/api/library`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const data = await response.json();
              if (data.success && data.library) {
                const pinterestAdsItems = data.library.PINTEREST_ADS || [];
                const uniquePinterestAdsItems = pinterestAdsItems.filter((item, index, self) =>
                  index === self.findIndex((t) => t.id === item.id)
                );
                setAllLibraryItems(uniquePinterestAdsItems);
                setLibraryItems(uniquePinterestAdsItems);
              }
            } catch (error) {
              console.error('Erreur chargement bibliothèque:', error);
            } finally {
              setLoadingLibrary(false);
            }
            setShowAddModal(false);
          }}
          setSuccess={() => {}}
          setError={setError}
          initialStep="pinterest-ads"
          connectors={globalConnectors}
        />
      )}

    </div>
  );
}

// Composant: Dashboard TikTok Ads
function DashboardTikTokAds({ token, setError, selectedPeriod, setSelectedPeriod, customStartDate, setCustomStartDate, customEndDate, setCustomEndDate, selectedDashboardModel, setSelectedDashboardModel, preselectedAccountId, activeTab, preselectionUsed, setPreselectionUsed, globalConnectors, canAccessAnalyzer, selectedClientId, updateUrlWithAccount, authError, setAuthError, handleApiError, handleReconnect, dashboardNotes, fetchDashboardNotes, selectedSiteId, userSites, compareEnabled = false, comparisonType = '', userRole = 'USER', comparisonStartDate = '', comparisonEndDate = '' }) {
  const [selectedSources, setSelectedSources] = useState([]);
  const [autoCharts, setAutoCharts] = useState(null);
  const [loadingCharts, setLoadingCharts] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const selectedMetrics = useSelectedMetrics(selectedAccount?.id || 'global');
  const [libraryItems, setLibraryItems] = useState([]);
  const [allLibraryItems, setAllLibraryItems] = useState([]);
  const [loadingLibrary, setLoadingLibrary] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [resultsData, setResultsData] = useState(null);
  const [loadingResults, setLoadingResults] = useState(false);
  const [granularity, setGranularity] = useState('day');
  const prevGranularityRef = React.useRef(granularity);
  const hasResultsDataRef = React.useRef(false);
  const [previousPeriodData, setPreviousPeriodData] = useState(null);
  const [campaignsData, setCampaignsData] = useState(null);
  const [loadingCampaigns, setLoadingCampaigns] = useState(false);
  const [showCampaignsSection, setShowCampaignsSection] = useState(true);
  const [selectedCampaignFilter, setSelectedCampaignFilter] = useState(null);
  const [previousCampaignsData, setPreviousCampaignsData] = useState(null);

  // Calculer le clientId effectif
  const effectiveClientId = React.useMemo(() => {
    if (selectedClientId) return selectedClientId;
    if (!selectedAccount) return null;
    const accountId = selectedAccount.id || selectedAccount.sourceId;
    const accountSite = userSites.find(s =>
      s.accountIds?.includes(selectedAccount.id) ||
      s.accountIds?.includes(selectedAccount.sourceId)
    );
    return accountSite?.clientId || (accountId ? `tiktok_${accountId}` : null);
  }, [selectedClientId, selectedAccount, userSites]);

  // Hook pour la visibilité des modules par client
  const {
    isModuleVisible: isClientModuleVisible,
    toggleModuleVisibility: toggleClientModuleVisibility,
    setModuleVisibility: setClientModuleVisibility,
    hiddenModules: clientHiddenModules
  } = useClientModuleVisibility(effectiveClientId, userRole);

  const [isHiddenModulesModalOpen, setIsHiddenModulesModalOpen] = useState(false);

  // Ref pour garder la valeur la plus récente de libraryItems
  const libraryItemsRef = React.useRef(libraryItems);
  React.useEffect(() => {
    libraryItemsRef.current = libraryItems;
  }, [libraryItems]);

  // Tracker la présélection
  const hadPreselectedAccountRef = React.useRef(false);

  // Réinitialiser les données quand l'onglet change
  React.useEffect(() => {
    if (activeTab === 'tiktok-ads') {
      setResultsData(null);
      setLoadingResults(false);
      hasResultsDataRef.current = false;
    }
  }, [activeTab]);

  // Charger les comptes TikTok Ads de la bibliothèque
  React.useEffect(() => {
    const loadLibraryItems = async () => {
      setLoadingLibrary(true);
      try {
        const response = await fetch(`${API_URL}/api/library`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });
        const data = await response.json();
        if (data.success && data.library) {
          const tiktokAdsItems = data.library.TIKTOK_ADS || [];
          console.log(`📱 ${tiktokAdsItems.length} comptes TikTok Ads trouvés`);

          const uniqueTikTokAdsItems = tiktokAdsItems.filter((item, index, self) =>
            index === self.findIndex((t) => t.id === item.id)
          );
          console.log(`📱 Après déduplication: ${uniqueTikTokAdsItems.length} comptes uniques`);

          setAllLibraryItems(uniqueTikTokAdsItems);
          setLibraryItems(uniqueTikTokAdsItems);
        }
      } catch (error) {
        console.error('Erreur chargement bibliothèque:', error);
      } finally {
        setLoadingLibrary(false);
      }
    };

    loadLibraryItems();
  }, [token]);

  // Filtrer les comptes par client ou site
  const filterAccountsByClient = async (accounts, clientId, siteId) => {
    if (siteId && siteId !== 'all' && accounts.length) {
      const site = userSites.find(s => (s._id || s.id) === siteId);
      if (site && site.accountIds) {
        const allowedAccountIds = ensembleAutorise(site.accountIds);
        const filteredAccounts = accounts.filter(account => compteAutorise(account, allowedAccountIds));
        return filteredAccounts;
      }
    }

    if (!clientId || !accounts.length) {
      return accounts;
    }

    try {
      const response = await axios.get(`${API_URL}/api/sites`, {
        headers: { Authorization: `Bearer ${token}` },
        params: { clientId }
      });

      const sites = response.data.sites || [];
      const allowedAccountIds = ensembleAutorise(
        sites.flatMap(site => (Array.isArray(site.accountIds) ? site.accountIds : []))
      );

      return accounts.filter(account => compteAutorise(account, allowedAccountIds));
    } catch (error) {
      console.error('Erreur filtrage comptes par client:', error);
      return accounts;
    }
  };

  // Refs pour tracker le site et client précédents
  const tkPrevSiteIdRef = React.useRef(selectedSiteId);
  const tkPrevClientIdRef = React.useRef(selectedClientId);

  // Recharger la liste des comptes quand le site/client change
  React.useEffect(() => {
    const siteChanged = tkPrevSiteIdRef.current !== selectedSiteId;
    const clientChanged = tkPrevClientIdRef.current !== selectedClientId;
    tkPrevSiteIdRef.current = selectedSiteId;
    tkPrevClientIdRef.current = selectedClientId;

    const filterAndSelectAccount = async () => {
      if (siteChanged || clientChanged) {
        console.log('🔄 [TIKTOK ADS] Changement de site/client détecté');
        hadPreselectedAccountRef.current = false;
        setAutoCharts(null);
        setResultsData(null);
        hasResultsDataRef.current = false;
        setError('');
        setSelectedAccount(null);
        setSelectedSources([]);
      }

      if (allLibraryItems.length > 0) {
        const filteredItems = await filterAccountsByClient(allLibraryItems, selectedClientId, selectedSiteId);
        setLibraryItems(filteredItems);

        // Auto-sélectionner si un seul compte
        const preselectedExistsInList = preselectedAccountId && filteredItems.some(item => item.id === preselectedAccountId);
        if (filteredItems.length === 1 && !preselectedExistsInList) {
          const account = filteredItems[0];
          console.log('🎯 [TIKTOK ADS] Auto-sélection du seul compte:', account.name);
          setSelectedAccount(account);
          setSelectedSources([account.id]);
          const accountSite = userSites.find(s => s.accountIds?.includes(account.id) || s.accountIds?.includes(account.sourceId));
          window.currentNotesContext = {
            platform: 'TIKTOK_ADS',
            accountId: account.sourceId || account.id,
            accountName: account.name,
            siteId: accountSite?._id || accountSite?.id || null,
            clientId: accountSite?.clientId || null
          };
          window.dispatchEvent(new CustomEvent('accountSelected', {
            detail: { accountId: account.id, accountName: account.name }
          }));
        }
      }
    };

    filterAndSelectAccount();
  }, [selectedClientId, selectedSiteId, allLibraryItems]);

  // Fonction pour connecter TikTok Ads
  const handleConnectTikTokAds = () => {
    window.location.href = `${API_URL}/api/auth/tiktok-ads?token=${token}&returnTo=connectors`;
  };


  // Charger les données de la section Résultats
  const loadResultsSection = React.useCallback(async () => {
    if (!selectedAccount) return;

    setAuthError && setAuthError(null);

    // Ne pas afficher le loading si seule la granularité a changé
    const onlyGranularityChanged = prevGranularityRef.current !== granularity && hasResultsDataRef.current;
    prevGranularityRef.current = granularity;

    try {
      if (!onlyGranularityChanged) {
        setLoadingResults(true);
      }
      console.log('📱 [RESULTS] Chargement section Résultats TikTok Ads...');

      const baseRequestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod,
        granularity: granularity
      };

      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) return;
        baseRequestBody.customStartDate = customStartDate;
        baseRequestBody.customEndDate = customEndDate;
      }

      if (selectedClientId) {
        baseRequestBody.clientId = selectedClientId;
      }

      console.log('📱 [RESULTS] Requête vers:', `${API_URL}/api/dashboard/tiktok-ads/timeline`);

      // Les bornes de la période regardée ; le serveur en dérive la précédente. La table
      // était recopiée ici, et sérialisée en UTC sur des dates à minuit local.
      const { startDate, endDate } = selectedPeriod === 'CUSTOM'
        ? { startDate: customStartDate, endDate: customEndDate }
        : getDateRangeFromPeriod(selectedPeriod);

      // Préparer le body pour la période précédente
      const previousPeriodBody = {
        accountId: selectedAccount.id,
        startDate,
        endDate,
        comparisonType: comparisonType || 'previous_period',
        comparisonStartDate: comparisonStartDate,
        comparisonEndDate: comparisonEndDate
      };

      // Appeler les deux endpoints en parallèle
      const [timelineResponse, previousPeriodResponse] = await Promise.all([
        fetch(`${API_URL}/api/dashboard/tiktok-ads/timeline`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(baseRequestBody)
        }),
        fetch(`${API_URL}/api/dashboard/tiktok-ads/previous-period`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(previousPeriodBody)
        })
      ]);

      // Gérer la réponse timeline
      if (!timelineResponse.ok) {
        const errorText = await timelineResponse.text();
        console.error('❌ [RESULTS] Erreur HTTP:', timelineResponse.status, errorText);

        if (timelineResponse.status === 401) {
          try {
            const errorData = JSON.parse(errorText);
            if (errorData.error === 'auth_expired' && setAuthError) {
              setAuthError({
                platform: 'TIKTOK_ADS',
                message: errorData.message,
                accountName: errorData.accountName,
                accountId: errorData.accountId,
                requiresReauth: errorData.requiresReauth,
                userRole: errorData.userRole
              });
              return;
            }
          } catch (parseError) {}
        }

        throw new Error(`Erreur HTTP: ${timelineResponse.status} - ${errorText}`);
      }

      const data = await timelineResponse.json();
      setResultsData(data);
      hasResultsDataRef.current = true;

      // Gérer la réponse période précédente
      if (previousPeriodResponse.ok) {
        const previousData = await previousPeriodResponse.json();
        if (previousData.success && previousData.data) {
          console.log('📊 [RESULTS] Données période précédente TikTok:', previousData.data);
          setPreviousPeriodData(previousData.data);
        } else {
          setPreviousPeriodData(null);
        }
      } else {
        console.error('❌ [RESULTS] Erreur période précédente TikTok');
        setPreviousPeriodData(null);
      }

    } catch (error) {
      console.error('❌ [RESULTS] Erreur chargement section Résultats:', error);
      if (!error.message?.includes('auth_expired')) {
        setError(`Erreur lors du chargement des résultats: ${error.message}`);
      }
    } finally {
      setLoadingResults(false);
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, granularity, selectedClientId, token, comparisonType]);

  // Charger automatiquement la section Résultats
  React.useEffect(() => {
    if (selectedAccount && selectedDashboardModel === 'global') {
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        return;
      }
      loadResultsSection();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedDashboardModel, granularity, loadResultsSection, comparisonType]);

  // Charger les données des campagnes TikTok Ads
  const loadCampaignsSection = React.useCallback(async () => {
    console.log('🎵 [TIKTOK-CAMPAIGNS] loadCampaignsSection appelé, selectedAccount:', selectedAccount?.id);
    if (!selectedAccount) {
      console.log('🎵 [TIKTOK-CAMPAIGNS] Pas de compte sélectionné, abandon');
      return;
    }

    try {
      setLoadingCampaigns(true);
      console.log('🎵 [TIKTOK-CAMPAIGNS] Chargement section Campagnes TikTok Ads...');

      const requestBody = {
        sources: [selectedAccount?.id],
        period: selectedPeriod
      };

      if (selectedPeriod === 'CUSTOM') {
        if (!customStartDate || !customEndDate) return;
        requestBody.customStartDate = customStartDate;
        requestBody.customEndDate = customEndDate;
      }

      const response = await fetch(`${API_URL}/api/dashboard/tiktok-ads/campaigns`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        throw new Error(`Erreur HTTP: ${response.status}`);
      }

      const data = await response.json();
      console.log('🎵 [TIKTOK-CAMPAIGNS] Réponse API:', data);
      if (data.success && data.rawData) {
        console.log('✅ [TIKTOK-CAMPAIGNS] Données campagnes TikTok chargées:', Object.keys(data.rawData));
        setCampaignsData(data.rawData);

        /**
         * Enregistrer les campagnes en base, comme le font Google, Microsoft et Meta.
         *
         * C'est ce qui permet de leur poser des labels et des objectifs : ceux-ci sont attachés à
         * une ligne `Campaign`, pas aux données de la régie. Sans cet appel, le panneau s'ouvrirait
         * sur une table vide — ce qui ressemble à une panne plutôt qu'à une absence de donnée.
         */
        const campagnesTikTok = Object.values(data.rawData).flatMap(compte => compte.campaigns || []);
        syncCampaignsInBackground(
          token,
          selectedAccount?.sourceId || selectedAccount?.id,
          'TIKTOK_ADS',
          campagnesTikTok
        );

        // Charger les données de comparaison si activée
        if (compareEnabled && comparisonType) {
          try {
            console.log('🎵 [TIKTOK-CAMPAIGNS-COMPARE] Chargement période précédente...');

            // Calculer les dates de la période actuelle
            let currentStartDate = customStartDate;
            let currentEndDate = customEndDate;

            if (!currentStartDate || !currentEndDate) {
              const plage = getDateRangeFromPeriod(selectedPeriod);
              currentStartDate = plage.startDate;
              currentEndDate = plage.endDate;
            }

            /**
             * Les bornes de comparaison. Seule différence avec les autres écrans : ici la base
             * « Personnalisée » a ses dates, puisque le composant les reçoit — on les emploie
             * plutôt que de retomber sur la période précédente.
             */
            let prevStartDate, prevEndDate;
            if (comparisonType === 'custom' && comparisonStartDate && comparisonEndDate) {
              prevStartDate = comparisonStartDate;
              prevEndDate = comparisonEndDate;
            } else {
              ({ startDate: prevStartDate, endDate: prevEndDate } =
                calculatePreviousPeriod(currentStartDate, currentEndDate, comparisonType));
            }

            console.log('🎵 [TIKTOK-CAMPAIGNS-COMPARE] Période précédente:', prevStartDate, 'à', prevEndDate);

            const prevRequestBody = {
              sources: [selectedAccount?.id],
              period: 'CUSTOM',
              customStartDate: prevStartDate,
              customEndDate: prevEndDate
            };

            const prevResponse = await fetch(`${API_URL}/api/dashboard/tiktok-ads/campaigns`, {
              method: 'POST',
              headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
              },
              body: JSON.stringify(prevRequestBody)
            });

            if (prevResponse.ok) {
              const prevData = await prevResponse.json();
              if (prevData.success && prevData.rawData) {
                const prevCampaigns = Object.values(prevData.rawData).flatMap(account => account.campaigns || []);
                const prevCampaignsData = {
                  success: true,
                  data: [{ campaigns: prevCampaigns }]
                };
                console.log('🎵 [TIKTOK-CAMPAIGNS-COMPARE] Données précédentes récupérées:', prevCampaigns.length);
                setPreviousCampaignsData(prevCampaignsData);
              }
            }
          } catch (compError) {
            console.error('🎵 [TIKTOK-CAMPAIGNS-COMPARE] Erreur récupération période précédente:', compError);
          }
        } else {
          setPreviousCampaignsData(null);
        }
      } else {
        console.log('⚠️ [TIKTOK-CAMPAIGNS] Pas de rawData dans la réponse');
      }
    } catch (error) {
      console.error('❌ [TIKTOK-CAMPAIGNS] Erreur chargement campagnes TikTok:', error);
    } finally {
      setLoadingCampaigns(false);
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, token, compareEnabled, comparisonType, comparisonStartDate, comparisonEndDate]);

  // Charger automatiquement les campagnes
  React.useEffect(() => {
    console.log('🎵 [TIKTOK-CAMPAIGNS] useEffect - selectedAccount:', selectedAccount?.id, 'model:', selectedDashboardModel);
    if (selectedAccount && selectedDashboardModel === 'global') {
      if (selectedPeriod === 'CUSTOM' && (!customStartDate || !customEndDate)) {
        console.log('🎵 [TIKTOK-CAMPAIGNS] Période CUSTOM mais dates manquantes');
        return;
      }
      console.log('🎵 [TIKTOK-CAMPAIGNS] Déclenchement loadCampaignsSection');
      loadCampaignsSection();
    }
  }, [selectedAccount, selectedPeriod, customStartDate, customEndDate, selectedDashboardModel, loadCampaignsSection]);

  // Charger les notes du dashboard
  useEffect(() => {
    if (!selectedAccount || !fetchDashboardNotes) return;
    const accountId = selectedAccount?.sourceId || selectedAccount?.id;
    fetchDashboardNotes('TIKTOK_ADS', accountId, customStartDate, customEndDate);
  }, [selectedAccount, customStartDate, customEndDate, fetchDashboardNotes]);

  // Gestion de la présélection
  React.useEffect(() => {
    if (preselectedAccountId !== null) {
      hadPreselectedAccountRef.current = true;
    }
  }, [preselectedAccountId]);

  React.useEffect(() => {
    // « Voir » depuis l'accueil transmet le `sourceId` du compte — l'identifiant de la régie —
    // et non l'identifiant interne du `LibraryItem` : chercher sur le seul `id` ne trouvait
    // rien, et le dashboard s'ouvrait sur son sélecteur alors qu'un compte avait été demandé.
    // Awin et ChatGPT Ads acceptaient déjà les deux formes.
    const preselectedItem = preselectedAccountId && libraryItems.length > 0
      ? libraryItems.find(item => item.id === preselectedAccountId || item.sourceId === preselectedAccountId)
      : null;

    if (preselectedItem && (!selectedAccount || selectedAccount.id !== preselectedItem.id)) {
      setSelectedAccount(preselectedItem);
      setSelectedSources([preselectedItem.id]);
      setPreselectionUsed(true);
      window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: preselectedItem.id, accountName: preselectedItem.name } }));
    }
  }, [preselectedAccountId, libraryItems]);

  // Gestionnaire pour la génération automatique depuis la sidebar
  React.useEffect(() => {
    const handleAutoGenerate = (event) => {
      const { platform } = event.detail;
      if (platform !== 'tiktok-ads') return;

      console.log('🚀 TikTokAds - Clic sur plateforme depuis sidebar');

      setTimeout(() => {
        const currentFilteredItems = libraryItemsRef.current;

        if (!selectedAccount && currentFilteredItems.length > 0) {
          if (currentFilteredItems.length === 1) {
            const singleAccount = currentFilteredItems[0];
            setSelectedAccount(singleAccount);
            setSelectedSources([singleAccount.id]);
            const accountSite = userSites.find(s => s.accountIds?.includes(singleAccount.id) || s.accountIds?.includes(singleAccount.sourceId));
            window.currentNotesContext = {
              platform: 'TIKTOK_ADS',
              accountId: singleAccount.sourceId || singleAccount.id,
              accountName: singleAccount.name,
              siteId: accountSite?._id || accountSite?.id || null,
              clientId: accountSite?.clientId || null
            };
          }
        }
      }, 400);
    };

    window.addEventListener('dashboardAutoGenerate', handleAutoGenerate);
    return () => {
      window.removeEventListener('dashboardAutoGenerate', handleAutoGenerate);
    };
  }, [selectedAccount]);

  // Gestionnaire pour la sélection de compte depuis la sidebar (TikTok Ads)
  React.useEffect(() => {
    const handleSidebarAccountChange = (event) => {
      const { accountId, accountName } = event.detail;
      console.log('🔄 TikTokAds - Changement de compte depuis sidebar:', accountId, accountName);

      if (!accountId) {
        // "Tous les comptes" sélectionné
        setSelectedAccount(null);
        setSelectedSources([]);
        setAutoCharts(null);
        setResultsData(null);
        hasResultsDataRef.current = false;
        return;
      }

      // Chercher le compte dans la liste
      const targetAccount = libraryItemsRef.current.find(item =>
        item.id === accountId || item.sourceId === accountId
      );

      if (targetAccount) {
        setSelectedAccount(targetAccount);
        setSelectedSources([targetAccount.id]);
        const targetSite = userSites.find(s => s.accountIds?.includes(targetAccount.id) || s.accountIds?.includes(targetAccount.sourceId));
        window.currentNotesContext = {
          platform: 'TIKTOK_ADS',
          accountId: targetAccount.sourceId || targetAccount.id,
          accountName: targetAccount.name,
          siteId: targetSite?._id || targetSite?.id || null,
          clientId: targetSite?.clientId || null
        };
        window.dispatchEvent(new CustomEvent('accountSelected', {
          detail: { accountId: targetAccount.id, accountName: targetAccount.name }
        }));
        // Mettre à jour l'URL après que activeTab soit synchronisé
        setTimeout(() => {
          window.updateUrlWithAccount?.(targetAccount.id);
        }, 200);
      }
    };

    window.addEventListener('sidebarAccountChange', handleSidebarAccountChange);
    return () => {
      window.removeEventListener('sidebarAccountChange', handleSidebarAccountChange);
    };
  }, []);

  /**
   * Ce qu'on envoie à l'analyse : les données **déjà affichées**.
   *
   * Appelée à l'envoi de la question et non à l'ouverture du panneau : les blocs arrivent en
   * plusieurs temps, et une charge figée trop tôt capture un écran incomplet.
   */
  const construireChargeAnalyse = () => {
    const periode = {
      period: selectedPeriod,
      ...(selectedPeriod === 'CUSTOM' && { customStartDate, customEndDate })
    };

    const bruts = { 'Campagnes': campaignsData };
    const blocs = {};
    const absents = [];
    for (const [libelle, bloc] of Object.entries(bruts)) {
      const reduit = bloc ? lignesDuBloc(bloc) : null;
      if (reduit) blocs[libelle] = reduit;
      else absents.push(libelle);
    }

    return {
      dashboardData: {
        sources: selectedSources,
        ...periode,
        charts: autoCharts?.charts,
        composants: { blocs, absents },
        dashboardType: 'tiktok'
      },
      dashboardType: 'tiktok',
      accountId: selectedAccount?.sourceId || selectedAccount?.id || null,
      accountName: selectedAccount?.name || null,
      ...periode
    };
  };

  return (
    <div className="view-content">
      {/*
        L'analyse IA. Le composant rend le bouton *et* le panneau, celui-ci passant par un
        portail — sa place dans l'arbre n'a aucune incidence sur son placement à l'écran.
      */}
      {selectedAccount && autoCharts?.charts?.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
          <AnalyseIA
            token={token}
            compte={selectedAccount}
            autorise={canAccessAnalyzer}
            construireCharge={construireChargeAnalyse}
          />
        </div>
      )}

      {/* Sélection des comptes TikTok Ads */}
      <div className="dashboard-sources">
        {(() => {
          if (loadingLibrary) {
            return (
              <div className="loading-state">
                <Loader className="spin" size={16} />
                <span>Chargement des comptes TikTok Ads...</span>
              </div>
            );
          }

          if (libraryItems.length === 0) {
            const hasTikTokAdsConnector = globalConnectors?.some(c => c.provider === 'TIKTOK_ADS') || false;

            if (!hasTikTokAdsConnector) {
              return (
                <div className="no-sources">
                  <div className="empty-state">
                    <p>Aucun connecteur TikTok Ads activé</p>
                    <button onClick={handleConnectTikTokAds} className="btn btn-primary" style={{ fontSize: '16px', padding: '12px 24px' }}>
                      + Connecter
                    </button>
                  </div>
                </div>
              );
            } else {
              return (
                <div className="no-sources">
                  <div className="empty-state">
                    <p>Aucun compte TikTok Ads connecté</p>
                    <button onClick={() => setShowAddModal(true)} className="btn btn-primary" style={{ fontSize: '16px', padding: '12px 24px' }}>
                      + Ajouter un compte
                    </button>
                  </div>
                </div>
              );
            }
          }

          // Plusieurs comptes disponibles mais aucun sélectionné
          if (libraryItems.length > 1 && !selectedAccount) {
            return (
              <div className="account-selection-prompt">
                <div className="account-selection-card">
                  <div className="account-selection-icon">
                    <img src={tiktokAdsLogo} alt="TikTok Ads" style={{ width: 48, height: 48, objectFit: 'contain' }} />
                  </div>
                  <h2>Sélectionnez un compte TikTok Ads</h2>
                  <p className="account-selection-description">
                    {libraryItems.length} comptes disponibles
                  </p>
                  <select
                    className="account-selection-dropdown"
                    value=""
                    onChange={(e) => {
                      if (e.target.value) {
                        const account = libraryItems.find(item => item.id === e.target.value);
                        if (account) {
                          setSelectedAccount(account);
                          setSelectedSources([account.id]);
                          const accountSite = userSites.find(s => s.accountIds?.includes(account.id) || s.accountIds?.includes(account.sourceId));
                          window.currentNotesContext = {
                            platform: 'TIKTOK_ADS',
                            accountId: account.sourceId || account.id,
                            accountName: account.name,
                            siteId: accountSite?._id || accountSite?.id || null,
                            clientId: accountSite?.clientId || null
                          };
                          window.dispatchEvent(new CustomEvent('accountSelected', { detail: { accountId: account.id, accountName: account.name } }));
                          // Mettre à jour l'URL avec le compte sélectionné
                          setTimeout(() => window.updateUrlWithAccount?.(account.id), 100);
                        }
                      }
                    }}
                  >
                    <option value="">Choisir un compte...</option>
                    {[...libraryItems].sort((a, b) => a.name.localeCompare(b.name)).map(account => (
                      <option key={account.id} value={account.id}>
                        {account.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            );
          }

          // Un compte sélectionné - pas de header (même mise en page que Google Ads)
          return null;
        })()}
      </div>

      {/* Modal des modules masqués */}
      <HiddenModulesModal
        isOpen={isHiddenModulesModalOpen}
        onClose={() => setIsHiddenModulesModalOpen(false)}
        hiddenModules={clientHiddenModules}
        onRestoreModule={(moduleId) => setClientModuleVisibility(moduleId, true)}
      />

      {/* Écran de chargement TikTok Ads */}
      {loadingResults && selectedAccount && (
        <DashboardLoadingScreen
          platformName="TikTok Ads"
          features={[
            { icon: <BarChart3 size={28} />, label: 'Métriques de performance' },
            { icon: <TrendingUp size={28} />, label: 'Tendances et graphiques' },
            { icon: <img src={tiktokAdsLogo} alt="TikTok Ads" style={{width: '28px', height: '28px', objectFit: 'contain'}} />, label: 'Analyse des campagnes' }
          ]}
        />
      )}

      {/* Section Résultats TikTok Ads */}
      {!loadingResults && selectedAccount && (() => {
        const chartSource = resultsData;

        if (!selectedAccount) {
          return null;
        }

        if (authError && authError.platform === 'TIKTOK_ADS') {
          return null;
        }

        if (!chartSource || !chartSource.charts || chartSource.charts.length === 0) {
          return <NoDataMessage />;
        }

        return (
          <PerformanceChart
            autoCharts={chartSource}
            formatNumber={formatNumber}
            KPICard={KPICard}
            previousPeriodData={previousPeriodData}
            selectedCampaignFilter={selectedCampaignFilter}
            setSelectedCampaignFilter={setSelectedCampaignFilter}
            selectedCampaignTypeFilter={null}
            selectedAccount={selectedAccount}
            granularity={granularity}
            onGranularityChange={setGranularity}
            compareEnabled={compareEnabled}
            comparisonType={comparisonType}
            selectedPeriod={selectedPeriod}
            customStartDate={customStartDate}
            customEndDate={customEndDate}
            labelCampaignIds={null}
            notes={dashboardNotes}
            userRole={userRole}
            isClientModuleVisible={isClientModuleVisible}
            toggleClientModuleVisibility={toggleClientModuleVisibility}
            moduleId="tiktok_results"
          />
        );
      })()}

      {/* Section Campagnes TikTok Ads */}
      {!loadingCampaigns && selectedAccount && campaignsData && Object.keys(campaignsData).length > 0 && Object.values(campaignsData).some(account => account.campaigns?.some(c => c.impressions > 0 || c.clicks > 0)) && (
        <CampaignsTable
          rawData={campaignsData}
          previousRawData={previousCampaignsData ? { account: previousCampaignsData.data[0] || { campaigns: [] } } : null}
          previousPeriodData={previousPeriodData}
          isVisible={showCampaignsSection}
          onToggleVisibility={() => setShowCampaignsSection(!showCampaignsSection)}
          formatNumber={formatNumber}
          selectedCampaignFilter={selectedCampaignFilter}
          setSelectedCampaignFilter={setSelectedCampaignFilter}
          selectedCampaignTypeFilter={null}
          selectedAccount={selectedAccount}
          filteredCampaignIds={null}
          compareEnabled={compareEnabled}
          comparisonType={comparisonType}
          loadingComparison={false}
          userRole={userRole}
          isClientModuleVisible={isClientModuleVisible}
          toggleClientModuleVisibility={toggleClientModuleVisibility}
          moduleId="tiktok_results"
          campagnesAccountId={selectedAccount?.sourceId || selectedAccount?.id}
          campagnesAccountType="TIKTOK_ADS"
          campagnesToken={token}
        />
      )}


      {/* Message si pas de données */}
      {!loadingCharts && !loadingResults && !autoCharts && !resultsData && selectedAccount && !(authError && authError.platform === 'TIKTOK_ADS') && (
        <div className="empty-state">
          <p>Sélectionnez une période pour voir le dashboard</p>
        </div>
      )}

      {/* Modal d'ajout de données TikTok Ads */}
      {showAddModal && (
        <AddDataModal
          token={token}
          onClose={() => setShowAddModal(false)}
          onAdded={async () => {
            setLoadingLibrary(true);
            try {
              const response = await fetch(`${API_URL}/api/library`, {
                method: 'GET',
                headers: { 'Authorization': `Bearer ${token}` }
              });
              const data = await response.json();
              if (data.success && data.library) {
                const tiktokAdsItems = data.library.TIKTOK_ADS || [];
                const uniqueTikTokAdsItems = tiktokAdsItems.filter((item, index, self) =>
                  index === self.findIndex((t) => t.id === item.id)
                );
                setAllLibraryItems(uniqueTikTokAdsItems);
                setLibraryItems(uniqueTikTokAdsItems);
              }
            } catch (error) {
              console.error('Erreur chargement bibliothèque:', error);
            } finally {
              setLoadingLibrary(false);
            }
            setShowAddModal(false);
          }}
          setSuccess={() => {}}
          setError={setError}
          initialStep="tiktok-ads"
          connectors={globalConnectors}
        />
      )}

    </div>
  );
}

// Composant: Prompt d'upgrade pour les fonctionnalités premium
function UpgradePromptView({ userPlan, feature, description }) {
  return (
    <div className="step-content">
      <div className="upgrade-prompt-container" style={{
        textAlign: 'center',
        padding: '60px 20px',
        maxWidth: '600px',
        margin: '0 auto'
      }}>
        <div className="upgrade-icon" style={{ marginBottom: '24px' }}>
          <Bot size={64} color="#8884d8" />
        </div>
        
        <h2 style={{ marginBottom: '16px', color: '#333' }}>
          Fonctionnalité {feature} Premium
        </h2>
        
        <p style={{ 
          fontSize: '18px', 
          color: '#666', 
          marginBottom: '32px',
          lineHeight: '1.5'
        }}>
          {description}
        </p>
        
        <div className="current-plan" style={{
          background: '#f8f9fa',
          padding: '16px 24px',
          borderRadius: '8px',
          marginBottom: '32px',
          border: '1px solid #e5e7eb'
        }}>
          <p style={{ margin: '0', color: '#666' }}>
            <strong>Plan actuel :</strong> {userPlan}
          </p>
          <p style={{ margin: '8px 0 0 0', fontSize: '14px', color: '#888' }}>
            Cette fonctionnalité nécessite un abonnement actif
          </p>
        </div>

        <div className="upgrade-options" style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '20px',
          marginBottom: '32px'
        }}>
          <div className="plan-card" style={{
            background: 'white',
            border: '2px solid #c2410c',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#c2410c' }}>Plan Solo</h3>
            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
              19€<span style={{ fontSize: '14px', fontWeight: 'normal' }}>/mois</span>
            </div>
            <ul style={{
              textAlign: 'left',
              margin: '16px 0',
              paddingLeft: '20px',
              fontSize: '14px',
              color: '#666'
            }}>
              <li>1 utilisateur</li>
              <li>5 comptes connectés</li>
              <li>Clients illimités</li>
            </ul>
          </div>

          <div className="plan-card" style={{
            background: 'white',
            border: '2px solid #059669',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#059669' }}>Plan Team</h3>
            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
              69€<span style={{ fontSize: '14px', fontWeight: 'normal' }}>/mois</span>
            </div>
            <ul style={{
              textAlign: 'left',
              margin: '16px 0',
              paddingLeft: '20px',
              fontSize: '14px',
              color: '#666'
            }}>
              <li>3 utilisateurs</li>
              <li>25 comptes connectés</li>
              <li>Clients illimités</li>
            </ul>
          </div>

          <div className="plan-card" style={{
            background: 'white',
            border: '2px solid #2563eb',
            borderRadius: '12px',
            padding: '24px',
            textAlign: 'center'
          }}>
            <h3 style={{ margin: '0 0 12px 0', color: '#2563eb' }}>Plan Agency</h3>
            <div style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '8px' }}>
              179€<span style={{ fontSize: '14px', fontWeight: 'normal' }}>/mois</span>
            </div>
            <ul style={{
              textAlign: 'left',
              margin: '16px 0',
              paddingLeft: '20px',
              fontSize: '14px',
              color: '#666'
            }}>
              <li>10 utilisateurs</li>
              <li>100 comptes connectés</li>
              <li>Clients illimités</li>
            </ul>
          </div>
        </div>
        
        <div className="upgrade-actions">
          <button 
            className="btn btn-primary"
            style={{
              background: '#8884d8',
              border: 'none',
              padding: '12px 32px',
              fontSize: '16px',
              fontWeight: '600'
            }}
            onClick={() => {
              // Rediriger vers la page de facturation ou d'upgrade
              window.location.href = '/pricing'; // À adapter selon votre routing
            }}
          >
            🚀 Upgrader maintenant
          </button>
        </div>
        
        <p style={{ 
          fontSize: '12px', 
          color: '#888', 
          marginTop: '24px' 
        }}>
          Besoin d'aide ? Contactez notre support à hello@kokpit.io
        </p>
      </div>
    </div>
  );
}

// Composant: Vue Résultats d'analyse
function ResultsView({ analysisResult, setCurrentView, token, setAnalysisResult }) {
  // Ouvrir automatiquement le chat en mode streaming
  const [chatOpen, setChatOpen] = useState(analysisResult?.streamingMode === true);
  const [localResult, setLocalResult] = useState(analysisResult);

  // Extraire les infos du compte depuis les métadonnées de l'analyse
  const accountId = localResult?.accountId ||
                    localResult?.metadata?.summary?.account_id ||
                    localResult?.metadata?.customerId ||
                    localResult?.customerId || '';
  const accountName = localResult?.accountName ||
                      localResult?.metadata?.summary?.account_name ||
                      localResult?.metadata?.accountName || 'Compte Google Ads';
  const initialQuery = localResult?.query ||
                       localResult?.userQuery ||
                       localResult?.metadata?.query || '';

  // Callback quand le streaming est terminé
  const handleStreamComplete = (streamResult) => {
    const updatedResult = {
      ...localResult,
      analysis: streamResult.analysis,
      artifacts: streamResult.artifacts,
      sampleData: streamResult.data,
      gaqlQuery: streamResult.gaqlQuery,
      tokensUsed: streamResult.tokensUsed,
      pending: false,
      streamingMode: false
    };
    setLocalResult(updatedResult);
    if (setAnalysisResult) {
      setAnalysisResult(updatedResult);
    }
  };

  // En mode streaming, afficher uniquement le chat panel
  if (localResult?.streamingMode || localResult?.pending) {
    return (
      <div className="step-content" style={{ maxWidth: 'none', padding: '20px' }}>
        <div className="analysis-results">
          <div className="results-header">
            <h2><BarChart3 size={20} />&nbsp;Analyse en cours</h2>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                onClick={() => setCurrentView('analyze')}
                className="btn btn-secondary"
              >
                ← Nouvelle analyse
              </button>
            </div>
          </div>

          <div className="results-content" style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            padding: '80px 20px',
            textAlign: 'center',
            color: '#6b7280'
          }}>
            <MessageSquare size={48} style={{ marginBottom: '16px', color: '#667eea' }} />
            <p style={{ fontSize: '18px', marginBottom: '8px', fontWeight: '500' }}>
              L'analyse est en cours dans le panneau de conversation
            </p>
            <p style={{ fontSize: '14px', color: '#9ca3af', marginBottom: '24px' }}>
              Suivez la réponse de Claude en temps réel
            </p>
            {!chatOpen && (
              <button
                onClick={() => setChatOpen(true)}
                className="btn btn-primary"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  border: 'none',
                  padding: '12px 24px',
                  fontSize: '16px'
                }}
              >
                <MessageSquare size={18} />
                Ouvrir le panneau
              </button>
            )}
          </div>
        </div>

        {/* Chat Panel avec support du streaming */}
        <ChatPanel
          isOpen={chatOpen}
          onClose={() => setChatOpen(false)}
          analysisResult={null}
          accountId={accountId}
          accountName={accountName}
          token={token}
          initialQuery={initialQuery}
          streamingMode={true}
          libraryItemIds={localResult?.libraryItemIds || []}
          onStreamComplete={handleStreamComplete}
        />
      </div>
    );
  }

  return (
    <div className="step-content" style={{ maxWidth: 'none', padding: '20px' }}>
      <div className="analysis-results">
        <div className="results-header">
          <h2><BarChart3 size={20} />&nbsp;Résultats de l'analyse</h2>
          <div style={{ display: 'flex', gap: '10px' }}>
            {/* Bouton Continuer la conversation */}
            <button
              onClick={() => setChatOpen(true)}
              className="btn btn-primary"
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                border: 'none'
              }}
            >
              <MessageSquare size={16} />
              Continuer la conversation
            </button>
            <button
              onClick={() => setCurrentView('analyze')}
              className="btn btn-secondary"
            >
              ← Nouvelle analyse
            </button>
            <button
              onClick={() => setCurrentView('history')}
              className="btn btn-secondary"
            >
              📋 Historique
            </button>
          </div>
        </div>

        <div className="results-content">
          {/* Affichage de l'analyse avec rendu HTML - partie avant les artifacts */}
          <div className="analysis-text">
            <div dangerouslySetInnerHTML={{ __html: localResult?.analysis?.split('<!-- ARTIFACTS_PLACEHOLDER -->')[0] || localResult?.analysis || '' }}></div>
          </div>

          {/* Artifacts - Graphiques dynamiques (entre analyse et données) */}
          {!localResult?.pending && localResult?.artifacts && localResult.artifacts.length > 0 && (
            <div className="artifacts-section" style={{ marginTop: '24px', marginBottom: '24px' }}>
              <h3 style={{ marginBottom: '16px', fontSize: '18px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '8px' }}><BarChart3 size={18} /> Visualisations</h3>
              {localResult.artifacts.map((artifact, index) => (
                <ArtifactRenderer key={index} artifact={artifact} />
              ))}
            </div>
          )}

          {/* Affichage des données et sources (partie après les artifacts) */}
          {!localResult?.pending && localResult?.analysis?.includes('<!-- ARTIFACTS_PLACEHOLDER -->') && (
            <div className="data-section">
              <div dangerouslySetInnerHTML={{ __html: localResult.analysis.split('<!-- ARTIFACTS_PLACEHOLDER -->')[1] || '' }}></div>
            </div>
          )}

          {/* Métadonnées de l'analyse */}
          {!localResult?.pending && localResult?.tokensUsed && (
            <div className="analysis-metadata">
              <small style={{ color: '#666', marginTop: '24px', display: 'block', fontSize: '14px' }}>
                📈 {localResult.tokensUsed} tokens utilisés
                {localResult.estimatedCost && ` • Coût estimé: €${localResult.estimatedCost.toFixed(4)}`}
                {localResult.metadata?.totalRows && ` • ${localResult.metadata.totalRows} lignes analysées`}
              </small>
            </div>
          )}

          {/* Requêtes GAQL exécutées si disponibles */}
          {!localResult?.pending && localResult?.executedQueries && localResult.executedQueries.length > 0 && (
            <details style={{ marginTop: '24px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>
                🔍 Requêtes GAQL exécutées ({localResult.executedQueries.length})
              </summary>
              <div style={{ marginTop: '12px' }}>
                {localResult.executedQueries.map((queryInfo, index) => (
                  <div key={index} style={{ marginBottom: '16px' }}>
                    <div style={{ fontSize: '14px', fontWeight: '600', marginBottom: '8px', color: '#495057' }}>
                      {queryInfo.explanation}
                    </div>
                    <pre style={{
                      background: '#f8f9fa',
                      padding: '16px',
                      borderRadius: '8px',
                      overflow: 'auto',
                      maxHeight: '200px',
                      fontSize: '13px',
                      border: '1px solid #e9ecef',
                      fontFamily: 'Monaco, Consolas, "Lucida Console", monospace'
                    }}>
                      {queryInfo.query}
                    </pre>
                  </div>
                ))}
              </div>
            </details>
          )}

          {/* Section GAQL pour analyses avec données */}
          {!localResult?.pending && localResult?.gaqlQuery && (
            <details style={{ marginTop: '24px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>
                🔍 Détails de l'analyse
              </summary>
              <div style={{
                background: '#f8f5f0',
                padding: '16px',
                borderRadius: '8px',
                border: '1px solid #e5e7eb',
                marginTop: '12px'
              }}>
                {/* Query utilisateur */}
                {localResult.userQuery && (
                  <div style={{ marginBottom: '16px' }}>
                    <strong style={{ color: '#6b7280' }}>Question posée :</strong>
                    <div style={{
                      backgroundColor: '#fff',
                      padding: '10px',
                      borderRadius: '4px',
                      marginTop: '6px',
                      fontSize: '14px',
                      fontStyle: 'italic',
                      color: '#374151',
                      border: '1px solid #d1d5db'
                    }}>
                      "{localResult.userQuery}"
                    </div>
                  </div>
                )}

                {/* GAQL Query technique */}
                <div style={{ marginBottom: '12px' }}>
                  <strong style={{ color: '#6b7280' }}>Requête GAQL générée :</strong>
                  <pre style={{
                    backgroundColor: '#1f2937',
                    color: '#e5e7eb',
                    padding: '12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    fontFamily: 'Monaco, Consolas, monospace',
                    overflowX: 'auto',
                    marginTop: '8px',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all'
                  }}>{localResult.gaqlQuery}</pre>
                </div>

                {/* Métadonnées */}
                <div style={{
                  display: 'flex',
                  gap: '16px',
                  flexWrap: 'wrap',
                  fontSize: '13px',
                  color: '#6b7280'
                }}>
                  {localResult.resource && <span><strong>Resource:</strong> {localResult.resource}</span>}
                  {localResult.dataPoints && <span><strong>Data Points:</strong> {localResult.dataPoints}</span>}
                  {localResult.approach && <span><strong>Architecture:</strong> {localResult.approach}</span>}
                </div>
              </div>
            </details>
          )}

          {/* Données échantillon si disponibles */}
          {!localResult?.pending && localResult?.sampleData && localResult.sampleData.length > 0 && (
            <details style={{ marginTop: '24px' }}>
              <summary style={{ cursor: 'pointer', fontWeight: 'bold', fontSize: '16px' }}>
                📋 Données échantillon ({localResult.sampleData.length} entrées)
              </summary>
              <pre style={{
                background: '#f8f9fa',
                padding: '16px',
                borderRadius: '8px',
                overflow: 'auto',
                maxHeight: '400px',
                fontSize: '13px',
                border: '1px solid #e9ecef',
                marginTop: '12px'
              }}>
                {JSON.stringify(localResult.sampleData.slice(0, 10), null, 2)}
              </pre>
            </details>
          )}
        </div>
      </div>

      {/* Chat Panel avec support du streaming */}
      <ChatPanel
        isOpen={chatOpen}
        onClose={() => setChatOpen(false)}
        analysisResult={localResult?.pending ? null : localResult}
        accountId={accountId}
        accountName={accountName}
        token={token}
        initialQuery={initialQuery}
        streamingMode={localResult?.streamingMode === true}
        libraryItemIds={localResult?.libraryItemIds || []}
        onStreamComplete={handleStreamComplete}
      />
    </div>
  );
}


export default App;